<template>
  <div class="ci-wrap jcs-portal-card jcs-portal-reports" :class="{ 'ci-pcfull-mode': pcFull || plFull }" :style="accentColor ? { '--ci-accent': accentColor } : null">
    <div class="container">
      <!-- MOBILE right-edge toggle STACK — one flex column of red tabs (CHAT · FILTERS · EMAIL) pinned just
           above the global TOOLS handle. A flex column with a fixed gap guarantees EQUAL spacing between
           whichever tabs are docked (and to TOOLS below). Each tab shows only while its panel is collapsed.
           On DESKTOP these reopen from the .floating-pill-rail pills instead (see the report-*-pill portals). -->
      <div v-if="isMobile" class="ci-edge-stack">
        <button v-if="chatDocked" type="button" class="ci-email-edge-tab" aria-label="Show the chat" @click="restoreChat">
          <span class="ci-email-edge-arrow" aria-hidden="true"></span>
          <span class="ci-email-edge-txt">CHAT</span>
        </button>
        <button v-if="filtersDocked" type="button" class="ci-email-edge-tab" aria-label="Show the filters" @click="restoreFilters">
          <span class="ci-email-edge-arrow" aria-hidden="true"></span>
          <span class="ci-email-edge-txt">FILTERS</span>
        </button>
        <button v-if="emailDocked" type="button" class="ci-email-edge-tab" aria-label="Show Email Insights and Orders In Process" @click="restoreEmailInsights">
          <span class="ci-email-edge-arrow" aria-hidden="true"></span>
          <span class="ci-email-edge-txt">EMAIL</span>
        </button>
      </div>

      <!-- MOBILE: favorites strip pinned ABOVE the hero Ask (the favorites grid card was removed from
           MOBILE_LAYOUT — desktop keeps the draggable card). Same state/methods as the card; tap a star
           to apply, tap ★+ to save the current filters. Keep the two renders in sync. -->
      <!-- THIS is the favorites render a PHONE sees (MOBILE_LAYOUT excludes the `favorites` grid card), so the
           touch long-press drag has to be wired here as well as on the card — the card still matters because an
           iPad in landscape is >=992px and therefore gets the card with a touch-only input device.
           ci-fav--horz is not cosmetic: the red insertion line (.fav-drop-before/after::before) is
           orientation-gated on .ci-fav--horz / .ci-fav--vert, which normally come from .ci-fav-cardbody — a
           class this strip does not have. Without it a touch drag would show no drop feedback at all.
           (.ci-fav--horz .ci-fav-bar is a DESCENDANT selector, so a bar carrying the class cannot self-match.) -->
      <div v-if="isMobile" class="ci-fav-bar ci-fav-bar--hero ci-fav--horz" :class="{ 'is-favdrag': !!favDragId, 'is-favtouch': favTouchDrag }">
        <button type="button" class="ci-fav-col-btn" :disabled="!activeChips.length" @click.stop="activeChips.length && saveCurrentAsFavorite()" :title="activeChips.length ? 'Save the current filters as a favorite' : 'Apply filters first, then save them as a favorite'"><span class="ci-fav-star">★</span><span class="ci-fav-col-plus">+</span></button>
        <div v-for="g in favoriteGroups" :key="'mfg' + g.id" class="ci-favwrap" :data-fav-group="g.id"
             :class="{ 'fav-drop-group': !!favDragId && favDropGroupId === g.id }"
             @mouseenter="openFavPop(g.id, 'group', $event)" @mouseleave="closeFavPop()">
          <button type="button" class="ci-fav ci-fav--group" :class="{ 'is-active': favPopId === g.id }" :title="g.name">
            <span class="ci-fav-star">★</span><span class="ci-fav-badge">{{ groupFavorites(g.id).length }}</span>
          </button>
        </div>
        <div v-for="(fav, fi) in ungroupedFavorites" :key="'mff' + fav.id" class="ci-favwrap" :data-fav-id="fav.id"
             :class="[favDragId && favDragId !== fav.id && favDropIdx === fi ? 'fav-drop-' + favDropMode : '', favDragId === fav.id && favTouchDrag ? 'is-touchsrc' : '']"
             @mouseenter="openFavPop(fav.id, 'fav', $event)" @mouseleave="closeFavPop()">
          <button type="button" class="ci-fav" :class="{ 'is-active': favPopId === fav.id, 'is-chat': !!(fav.ask && fav.ask.question), 'is-dragging': favDragId === fav.id, 'is-pressing': favPressId === fav.id }"
                  @touchstart="favTouchStart(fav, $event)" @contextmenu.prevent
                  @click="favTap(fav)" :aria-label="fav.name"
                  :title="'Tap to apply · press and hold to move · ' + fav.name">
            <span class="ci-fav-star">★</span>
          </button>
        </div>
        <span v-if="!favoriteGroups.length && !ungroupedFavorites.length" class="ci-fav-hero-empty">★ favorites appear here</span>
      </div>

      <!-- ===== HERO ASK — "Tell me what you want to see." Centered at the very top of the report, for EVERY
           report role (server-side _scope means a rep can only ever ask about their own territory's data).
           Applying an AI-proposed view chains into: ⭐ save as favorite → 📧 schedule the existing email digest. -->
      <div v-if="!chatDocked" class="ci-ask ci-ask-hero">
        <div class="ci-ask-row">
          <div class="ci-ask-sugwrap">
            <textarea ref="heroAskInput" v-model="aiQuestion" class="ci-ask-input" rows="1"
              placeholder="Tell me what you want to see.  (Enter to ask · Shift+Enter for a new line)"
              :disabled="aiAskBusy" @input="heroSugRecalc" @keydown="heroSugKey" @blur="heroSugBlur"
              @paste="heroPaste"></textarea>
            <!-- Typeahead: completes real values from the loaded cards (reps, customers, parts, …). -->
            <div v-if="heroSug.open && heroSug.items.length" class="ci-ask-sug">
              <button v-for="(s, i) in heroSug.items" :key="'hsg' + i" type="button"
                class="ci-ask-sug-item" :class="{ 'is-sel': i === heroSug.idx }"
                @mousedown.prevent="heroSugPick(s)">
                <span class="ci-ask-sug-txt">{{ s.text }}</span><span class="ci-ask-sug-lbl">{{ s.label }}</span>
              </button>
            </div>
          </div>
          <!-- Attach a quote: PDF / DOCX / XLSX / PNG / JPG. Or just paste a screenshot into the box. -->
          <button type="button" class="ci-ask-clip" :class="{ 'is-on': heroFiles.length > 0 }"
                  :title="heroFiles.length ? heroFiles.length + ' attached — click to add more' : 'Attach a quote (PDF, DOCX, XLSX, PNG, JPG) — or paste a screenshot'"
                  aria-label="Attach a file" @click="heroPickFiles">
            <!-- A drawn gem clip, not the 📎 emoji. The emoji rendered as 15px text beside the mic's 18px
                 SVG and was unreadable without zooming in.
                 The geometry is how a paperclip is actually bent: THREE parallel straight runs joined by
                 TWO U-turns at opposite ends. The outer arms (x 7.5 and 14.5) are joined by the wide bend
                 at the bottom; the narrow bend at the top folds the right arm back to the inner run at
                 x 9.5, which therefore sits BETWEEN the outer two. Getting that nesting wrong is what
                 makes a clip icon read as a zigzag — spacing the three lines evenly does exactly that.
                 Rotated -40° for the conventional diagonal, 18px to match .ci-ask-mic, stroked in
                 currentColor so it inherits the hover and is-on states. -->
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false"
                 fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
              <path transform="rotate(-40 11 11)"
                    d="M7.5 6.5 L7.5 17.5 A3.5 3.5 0 0 0 14.5 17.5 L14.5 4.5 A2.5 2.5 0 0 0 9.5 4.5 L9.5 16" />
            </svg>
            <span v-if="heroFiles.length" class="ci-ask-clip-n">{{ heroFiles.length }}</span>
          </button>
          <input ref="heroFileInput" type="file" class="ci-ask-file" multiple
                 accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                 @change="heroFilesChosen" />
          <button v-if="voiceSupported" type="button" class="ci-ask-mic" :class="{ 'is-on': heroVoice.on }"
            :aria-pressed="heroVoice.on ? 'true' : 'false'"
            :title="heroVoice.on ? 'Stop listening' : 'Speak your question'"
            :aria-label="heroVoice.on ? 'Stop listening' : 'Speak your question'" @click="heroVoiceToggle"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="M10.1 1.6 L13.9 1.6 C15.72 1.6 17.2 3.08 17.2 4.9 L17.2 10 C17.2 11.99 15.79 13.6 14.05 13.6 L9.95 13.6 C8.21 13.6 6.8 11.99 6.8 10 L6.8 4.9 C6.8 3.08 8.28 1.6 10.1 1.6 Z M9.6 3.9 L14.4 3.9 A 0.95 0.95 0 0 1 14.4 5.8 L9.6 5.8 A 0.95 0.95 0 0 1 9.6 3.9 Z M9.6 7.15 L14.4 7.15 A 0.95 0.95 0 0 1 14.4 9.05 L9.6 9.05 A 0.95 0.95 0 0 1 9.6 7.15 Z M9.6 10.4 L14.4 10.4 A 0.95 0.95 0 0 1 14.4 12.3 L9.6 12.3 A 0.95 0.95 0 0 1 9.6 10.4 Z"/><path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M4 6.4 L4 11.4 C4 14.2 7.6 15.9 12 15.9 C16.4 15.9 20 14.2 20 11.4 L20 6.4"/><path fill="currentColor" d="M10.9 13 L13.1 13 L13.1 18.4 L14.7 20 L9.3 20 L10.9 18.4 Z"/><path fill="currentColor" d="M8.6 19.9 L15.4 19.9 A 1.1 1.1 0 0 1 15.4 22.1 L8.6 22.1 A 1.1 1.1 0 0 1 8.6 19.9 Z"/></svg></button>
          <button type="button" class="ci-ask-go" :disabled="aiAskBusy || (!aiQuestion.trim() && !heroFiles.length)" @click="askQuestion">
            <span v-if="aiAskBusy" class="ci-ai-spin" aria-hidden="true"></span>{{ aiAskBusy ? '…' : 'Ask' }}
          </button>
          <button type="button" class="ci-ai-histbtn" :class="{ 'is-on': aiAskHistOpen }" title="Past questions" @click="toggleAskHist">🕘</button>
          <button type="button" class="ci-ask-clear" title="Clear the search, filters, and results" aria-label="Clear search, filters, and results" @click="clearHero">✕</button>
          <button type="button" class="ci-ask-collapse" title="Collapse — reopen from the CHAT tab on the right edge"
                  aria-label="Collapse the chat" @click="collapseChat">▶</button>
        </div>
        <!-- Attached files. Thumbnails for images so a rep can see WHICH screenshot they grabbed. -->
        <div v-if="heroFiles.length" class="ci-ask-files">
          <div v-for="(f, i) in heroFiles" :key="'haf' + i" class="ci-ask-file-chip" :title="f.name">
            <img v-if="f.thumb" :src="f.thumb" class="ci-ask-file-thumb" alt="" />
            <span v-else class="ci-ask-file-ico">{{ f.icon }}</span>
            <span class="ci-ask-file-name">{{ f.name }}</span>
            <span class="ci-ask-file-size">{{ f.sizeLabel }}</span>
            <button type="button" class="ci-ask-file-x" :disabled="aiAskBusy"
                    :aria-label="'Remove ' + f.name" @click="heroRemoveFile(i)">✕</button>
          </div>
          <div class="ci-ask-file-hint">Ask what you want compared — e.g. “compare this quote to what XI199 has ordered”.</div>
        </div>
        <!-- What the server managed to READ. A file it could not open must say so; silence would
             let an answer look like it had considered a quote it never saw. -->
        <div v-if="heroFileNotes.length" class="ci-ask-files-read">
          <span v-for="(n, i) in heroFileNotes" :key="'hfn' + i"
                class="ci-ask-file-note" :class="{ 'is-bad': !n.ok }">{{ n.ok ? '✓' : '⚠' }} {{ n.name }} — {{ n.note }}</span>
        </div>
        <div v-if="aiAskHistOpen" class="ci-ask-mob-hist">
          <div v-if="avgAskLabel" class="ci-ask-hist-avg" title="Average time these questions took to answer">⏱ Avg ask time: <strong>{{ avgAskLabel }}</strong></div>
          <div v-if="!qaHistory.length" class="ci-ai-hist-empty">No questions asked yet.</div>
          <!-- Always GROUPED BY SALES REP: a rep sees their own group; Sales All sees every Sales rep;
               Master sees everyone. × (delete) only appears on the caller's own questions. -->
          <div v-else v-for="g in qaHistoryGroups" :key="'hg' + g.name" class="ci-ask-hist-grp">
            <div class="ci-ask-hist-grp-h">👤 {{ g.name }}<span v-if="g.own" class="ci-ask-hist-you">you</span>
              <!-- Master moderation: wipe this user's entire hero-chat history in one click. -->
              <button v-if="isRealMaster && !g.own && g.ownerId" type="button" class="ci-ask-hist-grpdel"
                      :title="'Delete ALL of ' + g.name + '\'s chat history'" @click="removeUserHistory(g)">🗑 Delete all</button>
            </div>
            <ul class="ci-ask-hist-list">
              <li v-for="h in g.items.slice(0, 10)" :key="'hh' + h.id" class="ci-ask-hist-item">
                <span class="ci-ask-hist-ic" aria-hidden="true">🕘</span>
                <button type="button" class="ci-ask-hist-q" :title="(h.a || '').slice(0, 300)" @click="replayQuestion(h); aiAskHistOpen = false">{{ h.q }}</button>
                <span v-if="h.ms != null" class="ci-ask-hist-ms" title="How long this question took to answer">{{ fmtAskMs(h.ms) }}</span>
                <!-- Fixed, aligned action slots: ★ favorite · ↻ re-apply · × delete. All three always occupy a
                     column (inapplicable ones are hidden but reserve their space) so the icons line up row-to-row. -->
                <button type="button" class="ci-ask-hist-act ci-ask-hist-fav" :class="{ 'is-faved': isQuestionFavorited(h) }" :title="isQuestionFavorited(h) ? 'Already in your ★ favorites' : 'Save this question to your ★ favorites'" @click="favoriteFromHistory(h)"><span class="ci-ask-hist-fav-star">★</span></button>
                <button type="button" class="ci-ask-hist-act ci-ask-hist-view" :class="{ 'is-hidden': !h.applied }" title="Re-apply the view this set" @click="h.applied && reapplyHistoryView(h)">↻</button>
                <button type="button" class="ci-ask-hist-act ci-ask-hist-del" :class="{ 'is-hidden': !(h.own || isRealMaster) }" :title="h.own ? 'Delete this question from your history' : 'Delete this question (Master)'" @click="(h.own || isRealMaster) && removeQuestion(h)">×</button>
              </li>
            </ul>
          </div>
        </div>
        <div v-if="aiAnswer" class="ci-ask-answer" :class="{ 'is-collapsed': aiAnswerCollapsed }">
          <!-- The answer body scrolls INSIDE the card. The hero card is pinned by translateY (sticky is
               dead site-wide here), so it travels with the scroll — a 22-line quote comparison would run
               past the bottom of the viewport with no way to reach it, because scrolling the page moves
               the card too. The collapse handle deliberately sits OUTSIDE this box so it can never scroll
               out of reach. -->
          <div class="ci-ask-answer-scroll">
          <!-- Collapsed: one line of context so the card never becomes a mystery stub. -->
          <div v-if="aiAnswerCollapsed" class="ci-ask-answer-stub">
            <span class="ci-ask-answer-stub-t">Answer hidden</span>
            <span class="ci-ask-answer-stub-n">{{ aiAnswerLines.length }} line(s) — click ⌄ to show</span>
          </div>
          <!-- ===== QUOTE vs PAST ORDERS ===== -->
          <div v-if="qrLines.length" v-show="!aiAnswerCollapsed" class="qr-wrap">
            <div class="qr-head">
              <span class="qr-title">Quote vs past orders</span>
              <span class="qr-sub">{{ qrCoverageLabel }} · click a row for the past orders behind the average</span>
              <!-- Never let a narrowed comparison pass for an all-time one. Taken from the SERVER's reply,
                   so this is the window the query actually ran with, not the one the dialog offered. -->
              <span v-if="qrRead && qrRead.historyLabel" class="qr-win" :title="qrWindowTip">
                📅 vs {{ qrRead.historyLabel }}</span>
              <button type="button" class="qr-present qr-doc-btn ci-noprint" v-if="qrRead && qrRead.documentText"
                      @click="openQrDoc('')" title="Show the quote exactly as the reader saw it">📄 What we read</button>
              <button type="button" class="qr-present ci-noprint" @click="openQrPrintPick('print')"
                      title="Print this comparison on letterhead — choose which lines to include">🖨 Print</button>
              <button type="button" class="qr-present qr-xlsx ci-noprint" @click="openQrPrintPick('excel')"
                      title="Download as Excel — a sheet per part with its past-order statistics">⬇ Excel</button>
            </div>
            <div v-if="qrRead && qrRead.truncated" class="qr-warn">
              This quote was long enough that the reader ran out of room — later lines may be missing.
            </div>
            <!-- Rows that were NOT treated as quote lines. Named and countable: dropping a row from a
                 comparison silently is the failure this whole feature keeps guarding against. -->
            <!-- Removals live ABOVE the folded-rows note: this one is the rep's own doing and they need
                 to see it, especially that the written summary predates it. -->
            <div v-if="qrRemoved.length" class="qr-note qr-note--rm ci-noprint">
              <b>{{ qrRemoved.length }} line(s) removed</b> from this comparison —
              {{ qrRemovedLabel }}. They are excluded from the table, the printed sheet and the Excel
              workbook. The written summary above was produced before the removal, so it may still
              mention them.
              <button type="button" class="qr-fixbtn" @click="undoQrRemove()">↩ Undo the last one</button>
              <button type="button" class="qr-fixbtn" v-if="qrRemoved.length > 1"
                      @click="restoreAllQrRemoved()">Restore all</button>
            </div>
            <div v-if="qrRead && qrRead.pricesNotPrintedInDocument" class="qr-warn">
              <b>{{ qrRead.pricesNotPrintedInDocument }} price(s)</b> on this comparison do not appear
              anywhere in the document — they were not read off the quote. Those lines are marked; check
              them against the paper, correct them with “View / Fix this line”, or remove them.
            </div>
            <div v-if="qrRead && qrRead.descriptionRowsFolded" class="qr-note">
              <b>{{ qrRead.descriptionRowsFolded }} row(s)</b> were treated as part of the line above them
              rather than as lines of their own: they carry no line number, and either no price at all —
              a component list printed inside another line's description — or a price that appears nowhere
              in the document. Anything with a number, a price on the paper, or a row the document scan
              found stays a line of its own, freight and discounts and credits included.
              <button type="button" class="qr-fixbtn ci-noprint" @click="qrFoldOpen = !qrFoldOpen">
                {{ qrFoldOpen ? "Hide them" : "Show them" }}</button>
              <ul v-if="qrFoldOpen" class="qr-note-list">
                <li v-for="(t, i) in (qrRead.descriptionRowsFoldedSample || qrRead.descriptionRowsSample || [])"
                    :key="'qfd' + i">{{ t }}</li>
              </ul>
            </div>
            <div v-if="qrRead && qrRead.partsCapped" class="qr-warn">
              This customer buys more parts than we compare in one pass, so only their
              <b>{{ qrRead.partsSearched }}</b> most recently ordered parts were searched. A part missing
              from the comparison is not necessarily one they have never bought.
            </div>
            <!-- Everything that produced no comparison, named. A line left out of the answer is
                 indistinguishable from a line that was fine, which is the failure this whole feature
                 keeps running into. -->
            <div v-if="qrNotCompared.length" class="qr-gap">
              <div class="qr-gap-h">{{ qrNotCompared.length }} line(s) were NOT compared:</div>
              <div v-if="qrRead && qrRead.historyFrom" class="qr-gap-win">Only orders {{ qrRead.historyLabel }}
                were searched — a part missing here may have been bought before that.</div>
              <ul>
                <li v-for="l in qrNotCompared" :key="'qrg' + l.line">
                  <b v-if="l.quoteLine" class="qr-lineno">#{{ l.quoteLine }}</b>
                  <b>{{ l.partQuoted || l.partMatched || ('line ' + l.line) }}</b>
                  <span v-if="l.qty"> ×{{ fmtNum(l.qty) }}</span>
                  <span v-if="l.quotedUnit != null"> at {{ fmtMoney(l.quotedUnit) }}</span>
                  — {{ l.notComparedReason }}
                  <!-- The rep can see the row we read and correct it. A line the reader could not resolve
                       is not a dead end: they have the quote in front of them. -->
                  <button type="button" class="qr-fixbtn ci-noprint" @click="openQrFix(l)">🔍 View / Fix</button>
                </li>
              </ul>
            </div>
            <table class="qr-tbl">
              <thead>
                <tr>
                  <th class="qr-x"></th>
                  <th>Quoted Part Number</th>
                  <th class="qr-n">Quoted Price</th>
                  <th class="qr-n">Avg of Past Orders</th>
                  <th class="qr-n">Margin</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="l in qrLines">
                  <tr :key="'qr' + l.line" class="qr-row" :class="'qr-' + (l.verdict || 'none')"
                      @click="qrToggle(l.line)">
                    <td class="qr-x">{{ qrOpen[l.line] ? '−' : '+' }}</td>
                    <td>
                      <!-- The number the QUOTE prints, so the row can be read against the paper. Absent when
                           the census could not pair the row with an ordinal — better blank than wrong. -->
                      <span v-if="l.quoteLine" class="qr-lineno" title="Line number on the quote">#{{ l.quoteLine }}</span>
                      <span class="qr-part">{{ l.partQuoted || l.partMatched || '(no part number)' }}</span>
                      <span v-if="l.partMatched && l.partQuoted && l.partMatched !== l.partQuoted"
                            class="qr-matched">→ {{ l.partMatched }}</span>
                      <!-- "never bought" is a claim a rep may repeat to a customer, so it is only shown
                           when a part number was actually READ and looked up. A line whose part could not
                           be read is a gap, and says so. -->
                      <span v-if="l.source === 'rep'" class="qr-chip qr-chip--rep" title="Part, quantity or price supplied by you — the comparison itself was recomputed on the server">your correction</span>
                      <span v-if="l.matchState === 'notInHistory'" class="qr-chip qr-chip--new">{{ qrNewChipText }}</span>
                      <span v-else-if="l.matchState === 'partNotIdentified'" class="qr-chip qr-chip--gap">part not identified — not checked</span>
                      <span v-else-if="l.matchState === 'priceNotRead'" class="qr-chip qr-chip--gap">price not read</span>
                      <span v-else-if="l.matchState === 'notAPricedLine'" class="qr-chip qr-chip--muted">not a priced line</span>
                      <span v-if="l.qty" class="qr-qty">×{{ fmtNum(l.qty) }}{{ l.qtyUom ? ' ' + l.qtyUom : '' }}</span>
                      <!-- An assembly quoted as ONE line with its components listed underneath. The count is
                           on the row so the fold is visible where it happened, not only in a footnote. -->
                      <span v-if="l.descriptionRows" class="qr-chip qr-chip--comp"
                            :title="l.descriptionRows + ' component part(s) are listed in this line\'s description. They carry no price of their own and are not quoted separately, so they are not compared.'">
                        +{{ l.descriptionRows }} components</span>
                      <div v-if="l.description" class="qr-desc">{{ l.description }}</div>
                      <!-- The reading is part of the answer: a price the arithmetic could not prove must
                           never look like one it did. -->
                      <span v-for="f in qrKeyFlags(l)" :key="'qf' + l.line + f" class="qr-chip qr-chip--warn">{{ f }}</span>
                    </td>
                    <td class="qr-n">
                      <template v-if="l.quotedUnit != null">{{ fmtMoney(l.quotedUnit) }}</template>
                      <span v-else class="qr-unread">not readable</span>
                      <div v-if="l.quotedPerPiece != null && l.quotedPerPiece !== l.quotedUnit" class="qr-sub2">
                        {{ fmtMoney(l.quotedPerPiece) }} each
                      </div>
                    </td>
                    <td class="qr-n">
                      <template v-if="l.pastAvg != null">{{ fmtMoney(l.pastAvg) }}</template>
                      <span v-else class="qr-unread">—</span>
                      <div v-if="l.pctVsAvg != null" class="qr-sub2" :class="l.pctVsAvg > 0 ? 'is-up' : 'is-dn'">
                        {{ l.pctVsAvg > 0 ? '+' : '' }}{{ l.pctVsAvg }}%
                      </div>
                    </td>
                    <td class="qr-n">
                      <template v-if="l.marginPct != null">{{ l.marginPct }}%</template>
                      <span v-else class="qr-unread" :title="qrCostIncluded ? 'No cost on file for this part' : 'Margin needs cost access'">—</span>
                    </td>
                  </tr>
                  <!-- Expanded: the past orders behind the average. -->
                  <tr v-if="qrOpen[l.line]" :key="'qrd' + l.line" class="qr-detail">
                    <td colspan="5">
                      <div class="qr-note">{{ l.note }}</div>
                      <ul v-if="(l.flagsExplained || []).length" class="qr-flags">
                        <li v-for="(f, fi) in l.flagsExplained" :key="'qfe' + l.line + fi">{{ f }}</li>
                      </ul>
                      <ul v-if="(l.alternateReadings || []).length" class="qr-flags">
                        <li v-for="(a, ai) in l.alternateReadings" :key="'qa' + l.line + ai">Also reads as: {{ a }}</li>
                      </ul>
                      <!-- An ambiguous line shows BOTH readings rather than picking one. -->
                      <div v-if="l.ambiguous" class="qr-both">
                        <div class="qr-both-h">The quote does not settle which reading is right:</div>
                        <div v-if="l.vsIfUnit">As a UNIT price: {{ fmtMoney(l.vsIfUnit.unitUsed) }}<span v-if="l.vsIfUnit.pctVsLastPaid != null"> — {{ l.vsIfUnit.pctVsLastPaid > 0 ? '+' : '' }}{{ l.vsIfUnit.pctVsLastPaid }}% vs last paid ({{ l.vsIfUnit.verdict }})</span></div>
                        <div v-if="l.vsIfExtended">As a LINE TOTAL: unit would be {{ fmtMoney(l.vsIfExtended.unitUsed) }}<span v-if="l.vsIfExtended.pctVsLastPaid != null"> — {{ l.vsIfExtended.pctVsLastPaid > 0 ? '+' : '' }}{{ l.vsIfExtended.pctVsLastPaid }}% vs last paid ({{ l.vsIfExtended.verdict }})</span></div>
                      </div>
                      <div v-if="l.past" class="qr-past">
                        <div class="qr-past-h">Past orders — {{ l.partMatched }}<span v-if="l.past.customerId"> · {{ l.past.customerId }}</span></div>
                        <div class="qr-past-grid">
                          <div><span>Orders</span>{{ fmtNum(l.past.orders) }}</div>
                          <div><span>Total qty</span>{{ fmtNum(l.past.totalQty) }}</div>
                          <div><span>Last paid</span>{{ fmtMoney(l.past.lastPaid) }}</div>
                          <div><span>Average</span>{{ fmtMoney(l.past.avgPaid) }}</div>
                          <div><span>Lowest</span>{{ fmtMoney(l.past.minPaid) }}</div>
                          <div><span>Highest</span>{{ fmtMoney(l.past.maxPaid) }}</div>
                          <div v-if="l.past.spreadRatio != null"><span>Spread</span>{{ l.past.spreadRatio }}×</div>
                          <div v-if="l.past.firstOrder"><span>First order</span>{{ l.past.firstOrder }}</div>
                          <div v-if="l.past.lastOrder"><span>Last order</span>{{ l.past.lastOrder }}</div>
                          <div v-if="l.past.everyoneElseAvg != null"><span>Everyone else</span>{{ fmtMoney(l.past.everyoneElseAvg) }}<em v-if="l.past.everyoneElseCustomers"> ({{ l.past.everyoneElseCustomers }} cust.)</em></div>
                          <div v-if="l.past.unitCost != null"><span>Unit cost</span>{{ fmtMoney(l.past.unitCost) }}</div>
                          <div v-if="l.past.listPrice != null"><span>List price</span>{{ fmtMoney(l.past.listPrice) }}</div>
                        </div>
                        <div v-if="l.past.withinTheirRange === false" class="qr-outside">
                          The quoted price sits outside everything they have paid for this part<template v-if="qrRead && qrRead.historyFrom"> {{ qrRead.historyLabel }}</template>.
                        </div>
                      </div>
                      <!-- The chip above may read "none since Jan 1, 2026" — this line used to answer the
                           wider question in absolute terms right underneath it. Reuses the server's own
                           sentence, which already covers window AND truncated-lookup. -->
                      <div v-else class="qr-past qr-past--none">{{ l.notComparedReason || "No past orders for this part under this customer." }}</div>
                      <div class="qr-rawline">
                        <div class="qr-rawline-h">The row as we read it</div>
                        <code v-if="l.raw">{{ l.raw }}</code>
                        <em v-else>No row text was captured for this line.</em>
                        <div class="qr-rawline-acts ci-noprint">
                          <button type="button" class="qr-fixbtn" @click.stop="openQrFix(l)">🔍 View / Fix this line</button>
                          <button type="button" class="qr-fixbtn" v-if="qrRead && qrRead.documentText"
                                  @click.stop="openQrDoc(l.raw || l.partQuoted || '')">📄 Find it in the document</button>
                          <!-- The rep's escape hatch (user 2026-08-12). Whatever the reader and the census
                               between them decide, the person holding the quote can say "this is not a line
                               on my quote" and be right. Reversible, and never silent -- a banner names what
                               was taken out. -->
                          <button type="button" class="qr-fixbtn qr-fixbtn--rm"
                                  title="Take this row out of the comparison, the printed sheet and the workbook. You can put it back."
                                  @click.stop="removeQrLine(l)">✕ Remove this line</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <!-- The written review, BELOW the table it describes. -->
          <ul v-show="!aiAnswerCollapsed" class="ci-ai-text"><li v-for="(ln, i) in aiAnswerLines" :key="'hal' + i" :class="{ 'is-bullet': ln.bullet }">{{ ln.text }}</li></ul>
          <!-- MOBILE talk-back: speak the answer in an Azure neural voice (POST /report/tts). The voice picker
               persists per browser. Deliberately NO browser-voice fallback — if Azure isn't available we say so. -->
          <div v-if="isMobile" class="ci-ask-tts">
            <button type="button" class="ci-ask-tts-btn" :disabled="ttsBusy" @click="speakAnswer">
              {{ ttsBusy ? '…' : (ttsPlaying ? '⏹ Stop' : '🔊 Listen') }}
            </button>
            <select class="ci-ask-tts-voice" v-model="ttsVoice" @change="onTtsVoiceChange" aria-label="Voice">
              <option v-for="v in ttsVoices" :key="v.id" :value="v.id">{{ v.label }}</option>
            </select>
            <span v-if="ttsError" class="ci-ask-tts-err">{{ ttsError }}</span>
          </div>
          <!-- Structured RESULT CARD: when the answer carries a ranking/breakdown, render the data as a mini
               card — sticky-header table + magnitude bars on the numeric column — with an Excel export. -->
          <!-- ci-noprint while a SUBSET presentation is building: captureDashboardShot() photographs the live
               .ci-wrap, and this card always renders EVERY group — without the class the excluded groups
               reappeared on the deck's dashboard-snapshot page. -->
          </div><!-- /.ci-ask-answer-scroll -->

          <!-- Collapse / reopen handle. Straddles the bottom border of the card. ci-noprint:
               a presentation capture must not photograph a UI control. -->
          <button type="button" class="ci-ask-answer-toggle ci-noprint"
                  :class="{ 'is-collapsed': aiAnswerCollapsed }"
                  :title="aiAnswerCollapsed ? 'Show the answer' : 'Hide the answer'"
                  :aria-label="aiAnswerCollapsed ? 'Show the answer' : 'Hide the answer'"
                  :aria-expanded="aiAnswerCollapsed ? 'false' : 'true'"
                  @click="aiAnswerCollapsed = !aiAnswerCollapsed; $nextTick(sizeAnswerScroll)">
            <span aria-hidden="true">{{ aiAnswerCollapsed ? '⌄' : '⌃' }}</span>
          </button>
          <div v-if="aiTable" v-show="!aiAnswerCollapsed" class="ci-ask-tbl" :class="{ 'ci-noprint': aiPresentFiltered }">
            <div class="ci-ask-tbl-h">
              <span class="ci-ask-tbl-title">📊 {{ aiTable.title || 'Results' }}</span>
              <span class="ci-ask-tbl-hbtns">
                <!-- (removed) redundant "☆ Favorite" button — favoriting is offered by the "Save this view as a
                     favorite?" bar below the answer (aiFollow), which is the richer path (dupe-check + email digest). -->
                <button type="button" class="ci-ask-tbl-act ci-ask-tbl-gen" :disabled="aiInsightsBusy || printBusy" @click="cardGenerateInsights" title="Generate Sales Insights that open by answering this question, and build a matching presentation">{{ aiInsightsBusy ? '⏳ Generating…' : '✨ Generate Insights' }}</button>
                <!-- `|| printBusy`: a subset build blanks aiInsights to force a re-generate, which would
                     otherwise un-render this button (and its "⏳ Preparing…" state) mid-print. -->
                <button v-if="aiInsights || printBusy" type="button" class="ci-ask-tbl-act ci-ask-tbl-present" :disabled="printBusy || aiInsightsBusy" @click="cardPresentation" title="Open a print-ready presentation that includes this question and its results">{{ printBusy ? '⏳ Preparing…' : '📊 Presentation' }}</button>
                <button v-if="aiTableGroups" type="button" class="ci-ask-tbl-groupall" @click="toggleAllAiGroups" :title="allAiGroupsOpen ? 'Collapse every group' : 'Expand every group'">{{ allAiGroupsOpen ? '− Collapse all' : '+ Expand all' }}</button>
                <button type="button" class="ci-ask-tbl-export" @click="exportAiTable" title="Download these results as a spreadsheet (opens in Excel)">⬇ Excel</button>
              </span>
            </div>
            <!-- Confirms the exact window/scope the card's numbers cover (e.g. "📅 Jan 1, 2021 → present"),
                 which the ask auto-applied to the whole dashboard. -->
            <div v-if="aiTable.caption" class="ci-ask-tbl-cap">{{ aiTable.caption }}</div>
            <div class="ci-ask-tbl-scroll">
              <table>
                <thead>
                  <tr>
                    <th v-for="(c, i) in aiTable.columns" :key="'ath' + i" class="ci-ask-sortable" :class="{ 'is-num': i === aiTableNumIdx, 'is-sorted': aiSort && aiSort.col === i }" @click="sortAiCol(i)" :title="'Sort by ' + c">{{ c }}<span v-if="aiSort && aiSort.col === i" class="ci-ask-sort">{{ aiSort.dir === 1 ? ' ▲' : ' ▼' }}</span></th>
                    <th v-if="aiTableNumIdx >= 0" class="ci-ask-tbl-barth" aria-hidden="true"></th>
                  </tr>
                </thead>
                <!-- Grouped breakdown (first column repeats — per rep / territory / customer …):
                     one collapsible summary row per group, "+" reveals its detail rows. -->
                <tbody v-if="aiTableGroups">
                  <template v-for="(g, gi) in aiTableGroups">
                    <tr :key="'atg' + gi" class="ci-ask-tbl-grp" @click="toggleAiGroup(gi)">
                      <td v-for="(c, ci) in aiTable.columns" :key="'atgc' + gi + '-' + ci" :class="{ 'is-num': ci === aiTableNumIdx }">
                        <template v-if="ci === 0"><span class="ci-ask-tbl-tgl">{{ aiTableOpen[gi] ? '−' : '+' }}</span>{{ g.key || '(blank)' }} <span class="ci-ask-tbl-grp-n">· {{ g.rows.length }}</span></template>
                        <template v-else>{{ g.sums[ci] == null ? '' : fmtAiCell(g.sums[ci], ci) }}</template>
                      </td>
                      <td v-if="aiTableNumIdx >= 0" class="ci-ask-tbl-barcell"><span class="ci-ask-tbl-bar" :style="{ width: aiGroupBarPct(g) + '%', background: aiBarColor(g.rows[0]) }"></span></td>
                    </tr>
                    <template v-if="aiTableOpen[gi]">
                      <tr v-for="(r, ri) in g.rows" :key="'atgr' + gi + '-' + ri" class="ci-ask-tbl-sub">
                        <td v-for="(c, ci) in r" :key="'atgd' + gi + '-' + ri + '-' + ci" :class="{ 'is-num': ci === aiTableNumIdx }">{{ ci === 0 ? '' : fmtAiCell(c, ci) }}</td>
                        <td v-if="aiTableNumIdx >= 0" class="ci-ask-tbl-barcell"><span class="ci-ask-tbl-bar" :style="{ width: aiBarPct(r) + '%', background: aiBarColor(r) }"></span></td>
                      </tr>
                    </template>
                  </template>
                </tbody>
                <tbody v-else>
                  <tr v-for="(r, ri) in aiFlatRows" :key="'atr' + ri">
                    <td v-for="(c, ci) in r" :key="'atd' + ri + '-' + ci" :class="{ 'is-num': ci === aiTableNumIdx }">{{ fmtAiCell(c, ci) }}</td>
                    <td v-if="aiTableNumIdx >= 0" class="ci-ask-tbl-barcell"><span class="ci-ask-tbl-bar" :style="{ width: aiBarPct(r) + '%', background: aiBarColor(r) }"></span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-if="aiProposed && aiProposedChips.length" class="ci-ask-apply">
            <div v-if="aiProposedNote" class="ci-ask-apply-note">{{ aiProposedNote }}</div>
            <div class="ci-ask-apply-chips"><span v-for="(ch, i) in aiProposedChips" :key="'hpc' + i" class="ci-ask-chip">{{ ch.label }}</span></div>
            <div class="ci-ask-apply-actions">
              <button type="button" class="ci-ask-apply-btn" @click="reapplyHistoryView({ applied: aiProposed })">✓ Apply this view</button>
              <button type="button" class="ci-ask-dismiss" @click="aiProposed = null">Dismiss</button>
            </div>
          </div>
          <!-- Fresh asks auto-apply their filters; this banner tells the rep what the view is now scoped to.
               (The Apply-this-view block above only appears for replayed history questions.) -->
          <div v-else-if="aiApplied" class="ci-ask-applied">
            <span class="ci-ask-applied-check">✓ View updated</span>
            <span class="ci-ask-applied-syn">{{ filterSynopsis }}</span>
            <button type="button" class="ci-ask-dismiss" @click="aiUndoApplied" title="Restore the filters you had before this question">↩ Undo</button>
          </div>
        </div>
        <div v-if="aiAskError" class="ci-ask-error">{{ aiAskError }}
          <!-- One-tap mic fix (user 2026-08-01, mobile): a page can never flip the permission itself, but a
               getUserMedia call from THIS tap makes the browser re-raise its native Allow prompt whenever
               it's still willing — allowed → dictation starts immediately. Hard-blocked → device-specific
               steps below. -->
          <button v-if="micFixAvail" type="button" class="ci-mic-fixbtn" :disabled="micFixBusy"
                  @click="fixMicrophone">{{ micFixBusy ? "Asking…" : "🎙 Fix microphone" }}</button>
        </div>
        <div v-if="micHelp" class="ci-mic-help">
          <div class="ci-mic-help-h">
            <span>{{ micHelp.title || ("The microphone is blocked for this site — here's how to allow it on " + micHelp.device + ":") }}</span>
            <button type="button" class="ci-mic-help-x" @click="micHelp = null" aria-label="Close">✕</button>
          </div>
          <ol class="ci-mic-help-steps">
            <li v-for="(s, si) in micHelp.steps" :key="'mh' + si">{{ s }}</li>
          </ol>
          <a v-if="micHelp.deepLink && (micHelp.deepLink.indexOf('https://') === 0 || micHelp.deepLink.indexOf('app-settings:') === 0)"
             :href="micHelp.deepLink" class="ci-mic-fixbtn ci-mic-deeplink" target="_blank" rel="noopener">{{ micHelp.deepLinkLabel || "Open settings" }}</a>
          <button v-if="micHelp.copyLink" type="button" class="ci-mic-fixbtn" @click="copyPageLink">{{ micLinkCopied ? "✓ Copied — paste it in Safari" : "⧉ Copy page link" }}</button>
          <button v-else type="button" class="ci-mic-fixbtn" :disabled="micFixBusy" @click="fixMicrophone">{{ micFixBusy ? "Asking…" : "I've allowed it — try again" }}</button>
          <span v-if="micHelp.ai" class="ci-mic-ai-tag">✨ Steps tailored to your device</span>
        </div>
        <!-- ⭐→📧 follow-up chain after applying an AI view -->
        <div v-if="aiFollow.stage === 'fav'" class="ci-ask-follow">
          <span class="ci-ask-follow-q"><span class="ci-ask-follow-star" aria-hidden="true">★</span> Save this view as a favorite?</span>
          <input v-model="aiFollow.name" class="ci-ask-follow-name" maxlength="60" aria-label="Favorite name" />
          <button type="button" class="ci-ask-apply-btn" @click="aiFollowSaveFavorite">Save favorite</button>
          <button type="button" class="ci-ask-dismiss" @click="aiFollow = { stage: '', name: '', favId: '' }">No thanks</button>
        </div>
        <div v-else-if="aiFollow.stage === 'dupe'" class="ci-ask-follow ci-ask-follow-dupe">
          <span class="ci-ask-follow-q"><span class="ci-ask-follow-star" aria-hidden="true">★</span> A favorite <strong>“{{ aiFollow.dupeName }}”</strong> already matches this — overwrite it?</span>
          <button type="button" class="ci-ask-apply-btn" @click="aiFollowOverwrite">Overwrite</button>
          <button type="button" class="ci-ask-apply-btn ci-ask-follow-alt" @click="aiFollowSaveNew">Save as new</button>
          <button type="button" class="ci-ask-dismiss" @click="aiFollow = { stage: '', name: '', favId: '' }">Cancel</button>
        </div>
        <div v-else-if="aiFollow.stage === 'notify'" class="ci-ask-follow">
          <span class="ci-ask-follow-q">📧 Saved! Want this view emailed to you on a schedule?</span>
          <button type="button" class="ci-ask-apply-btn" @click="aiFollowOpenNotify">Set up email updates</button>
          <button type="button" class="ci-ask-dismiss" @click="aiFollow = { stage: '', name: '', favId: '' }">Not now</button>
        </div>
        <!-- Notations / Specifications for the SELECTED customer — hover a chip to see the VMFG note/spec in the
             same popover style used across the report. Only shown when a single customer is selected AND it has
             the content (heroCustomerNotes). -->
        <div v-if="heroCustomerNotes" class="ci-ask-notes">
          <button v-if="heroCustomerNotes.notation" type="button" class="ci-ask-note-chip"
                  @mouseenter="heroNoteHover('note', $event)" @mouseleave="heroNoteLeave"
                  @focus="heroNoteHover('note', $event)" @blur="heroNoteLeave">
            <span aria-hidden="true">📝</span> Notations
          </button>
          <button v-if="heroCustomerNotes.specs" type="button" class="ci-ask-note-chip"
                  @mouseenter="heroNoteHover('spec', $event)" @mouseleave="heroNoteLeave"
                  @focus="heroNoteHover('spec', $event)" @blur="heroNoteLeave">
            <span aria-hidden="true">📋</span> Specifications
          </button>
        </div>
      </div>

      <!-- (removed 2026-07-26) The red "Updating the report" loading banner lived here. It was REDUNDANT, not
           merely unwanted: isProcessing = (loading || aiAskBusy) drives the centered processing overlay below,
           so every refresh that showed this bar was already showing the overlay on top of it. The overlay is
           the one the user kept — it also carries the elapsed/ETA line, so no information was lost.
           The _startLoadTimer/_tickLoadTimer estimator is intentionally left in place: it only runs during a
           load, and it is what feeds the self-calibrating per-range estimate. -->

      <!-- Global PROCESSING overlay — a centered circular spinner + "Loading" whenever an Ask (aiAskBusy) or a
           filter/data refresh (loading) is in flight, so the user always sees the system is working. Shown via
           a short-delay flag (showProcessing) so instant loads don't flash. Sits above everything (z 100090). -->
      <div v-if="showProcessing || askConfirmAllTime" class="ci-proc-overlay" role="status" aria-live="polite" @click.self="onProcBackdrop">
        <div class="ci-proc-box">
          <!-- Range line only when the window is KNOWN: a locked re-ask (shortcut/favorite), the paused gate,
               or a plain data refresh. Hidden mid-ask otherwise — the backend resolves an un-dated question's
               window from the question itself, so echoing the reset YTD baseline here would be a lie. -->
          <div v-if="procDateLabel && (!aiAskBusy || procLockDates)" class="ci-proc-range" :title="'Date range being searched'">{{ procDateLabel }}</div>
          <!-- PAUSED confirm state: an un-dated (all-time / back-to-1990) query does NOT auto-run — the circle
               is an ASK button; clicking it runs the full-history search, a shortcut narrows + runs instead. -->
          <button v-if="procPaused" type="button" class="ci-proc-askbtn" @click="confirmAllTimeAsk" aria-label="Run this all-time search">
            <span class="ci-proc-ask-label" aria-hidden="true">ASK</span>
          </button>
          <!-- ACTIVE asking/loading: spinner with a ✕ in its center to cancel. -->
          <button v-else type="button" class="ci-proc-spinbtn" @click="cancelProcessing" :aria-label="procStopLabel" :title="procStopLabel">
            <span class="ci-proc-spin" aria-hidden="true"></span>
            <span class="ci-proc-x" aria-hidden="true">✕</span>
          </button>
          <span class="ci-proc-txt">{{ procPaused ? 'Ready when you are' : (aiAskBusy ? 'Asking…' : (aiInsightsBusy ? 'Generating insights…' : 'Loading…')) }}</span>
          <div v-if="!procPaused" class="ci-proc-eta">{{ procRemainLabel }}</div>
          <!-- All-time note + one-row narrower-range shortcuts. Shown in the PAUSED confirm state, or while an
               all-time reload is actually running. procQuickRange narrows + re-asks locked to the window. -->
          <div v-if="procPaused || (procIsAllTime && !aiAskBusy)" class="ci-proc-allnote">
            <div class="ci-proc-allnote-txt">
              Searching all the way back to <strong>1990</strong> — this can take longer than normal.<template v-if="procRangeAvg"> Average time for this range is <strong>{{ procRangeAvg }}</strong>.</template><template v-if="procPaused"><br>Click <strong>ASK</strong> to run it, or narrow first:</template>
            </div>
            <div class="ci-proc-presets">
              <span class="ci-proc-presets-lbl">Narrow:</span>
              <button type="button" @click="procQuickRange('ytd')" :title="shortcutTip('ytd', 'Year to date (Jan 1 → today)')">YTD</button>
              <button type="button" @click="procQuickRange('r1')" :title="shortcutTip('r1', 'The last 12 months (through today)')">1 Yr</button>
              <button type="button" @click="procQuickRange('r3')" :title="shortcutTip('r3', 'The last 3 years (through today)')">3 Yr</button>
              <button type="button" @click="procQuickRange('r5')" :title="shortcutTip('r5', 'The last 5 years (through today)')">5 Yr</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ⬇ Excel export group-picker — choose which grouped items (reps / territories / customers) go into
           the workbook. All checked by default; unchecking one leaves its rows AND its sheet out. -->
      <!-- Costing for Parts → Excel. Scope picker reusing the hero result-card export chrome
           (.ci-xlsx-*) and its _xlsxSheet/_xlsxSheetName helpers, so there is ONE xlsx mechanism. -->
      <div v-if="pcExcelModal.open" class="ci-xlsx-overlay" role="dialog" aria-modal="true" @click.self="pcExcelModal.open = false">
        <div class="ci-xlsx-modal">
          <div class="ci-xlsx-head">
            <span>Export part costs</span>
            <button type="button" class="ci-xlsx-x" @click="pcExcelModal.open = false" aria-label="Close">✕</button>
          </div>
          <div class="ci-xlsx-sub">Choose how much to include. Every option respects the dashboard filter and your territory access — this only changes how many rows are written.</div>
          <ul class="ci-xlsx-list">
            <li><label class="ci-xlsx-item"><input type="radio" value="page" v-model="pcExcelModal.scope"><span>This page — the {{ fmtNum(partCostRows.length) }} highest-cost parts already loaded</span></label></li>
            <li v-if="partCostTermList.length"><label class="ci-xlsx-item"><input type="radio" value="search" v-model="pcExcelModal.scope"><span>Search results only — {{ fmtNum(partCostView.length) }} matching “{{ partCostSearchLabel }}”</span></label></li>
            <li><label class="ci-xlsx-item"><input type="radio" value="all" v-model="pcExcelModal.scope"><span>All parts in view — {{ fmtNum(partCostTotals ? partCostTotals.parts : 0) }} (re-queries the server)</span></label></li>
          </ul>
          <div v-if="pcExcelModal.busy" class="ci-xlsx-sub">Fetching all parts…</div>
          <div v-if="pcExcelModal.error" class="ci-xlsx-sub">{{ pcExcelModal.error }}</div>
          <div class="ci-xlsx-actions">
            <button type="button" class="ci-xlsx-cancel" @click="pcExcelModal.open = false">Cancel</button>
            <button type="button" class="ci-xlsx-go" :disabled="pcExcelModal.busy" @click="confirmPartCostExcel">⬇ Export</button>
          </div>
        </div>
      </div>

      <!-- Parts Sold at a Loss → Excel. Same scope-picker chrome as the cost card's export above. -->
      <div v-if="plExcelModal.open" class="ci-xlsx-overlay" role="dialog" aria-modal="true" @click.self="plExcelModal.open = false">
        <div class="ci-xlsx-modal">
          <div class="ci-xlsx-head">
            <span>Export loss lines</span>
            <button type="button" class="ci-xlsx-x" @click="plExcelModal.open = false" aria-label="Close">✕</button>
          </div>
          <div class="ci-xlsx-sub">Choose how much to include. Every option respects the date window, the selected tab and your search chips — this only changes how many rows are written.</div>
          <ul class="ci-xlsx-list">
            <li><label class="ci-xlsx-item"><input type="radio" value="page" v-model="plExcelModal.scope"><span>This page — the {{ fmtNum(plRows.length) }} most recent lines already loaded</span></label></li>
            <li v-if="plSearchLabel"><label class="ci-xlsx-item"><input type="radio" value="search" v-model="plExcelModal.scope"><span>Search results only — {{ fmtNum(plView.length) }} matching “{{ plSearchLabel }}”</span></label></li>
            <li><label class="ci-xlsx-item"><input type="radio" value="all" v-model="plExcelModal.scope"><span>All lines in the window — {{ fmtNum(plWindowRows) }} (re-queries the server)</span></label></li>
          </ul>
          <div v-if="plExcelModal.busy" class="ci-xlsx-sub">Working…</div>
          <div v-if="plExcelModal.error" class="ci-xlsx-sub">{{ plExcelModal.error }}</div>
          <div class="ci-xlsx-actions">
            <button type="button" class="ci-xlsx-cancel" @click="plExcelModal.open = false">Cancel</button>
            <button type="button" class="ci-xlsx-go" :disabled="plExcelModal.busy" @click="confirmPartLossExcel">⬇ Export</button>
          </div>
        </div>
      </div>
      <!-- "Returning to NC999" confirm — a rep acting AS a customer just filtered on MORE than one
           customer. The header follows a SINGLE selected customer only, so continuing homes the company
           selector to NC999; backing out removes the just-added customers and stays on the current
           company (user 2026-08-01: don't blindside the rep — let them back out). Backdrop/✕ = back out
           (the safe choice: nothing changes). -->
      <div v-if="chipHomePrompt" class="ci-xlsx-overlay" role="dialog" aria-modal="true" @click.self="chipHomeBack">
        <div class="ci-xlsx-modal ci-homeprompt">
          <div class="ci-xlsx-head ci-homeprompt-head">
            <span>Returning to JC Steele (NC999)</span>
            <button type="button" class="ci-xlsx-x" @click="chipHomeBack" aria-label="Close">✕</button>
          </div>
          <div class="ci-xlsx-sub">
            Your filters now include <b>more than one customer</b>. The company selector follows a single
            selected customer only, so it will switch back to <b>JC Steele &amp; Sons (NC999)</b> for
            reporting purposes. Your customer filters stay applied to the report.
          </div>
          <div class="ci-xlsx-actions">
            <button type="button" class="ci-xlsx-cancel" @click="chipHomeBack">Back out</button>
            <button type="button" class="ci-xlsx-go ci-homeprompt-go" @click="chipHomeContinue">Continue to NC999</button>
          </div>
        </div>
      </div>

      <div v-if="aiExcelModal.open" class="ci-xlsx-overlay" role="dialog" aria-modal="true" @click.self="aiExcelModal.open = false">
        <div class="ci-xlsx-modal">
          <div class="ci-xlsx-head">
            <span>⬇ Export to Excel</span>
            <button type="button" class="ci-xlsx-x" @click="aiExcelModal.open = false" aria-label="Close">✕</button>
          </div>
          <div class="ci-xlsx-sub">Choose which groups to include — all are selected by default; uncheck any to leave it out (its rows and its sheet are excluded).</div>
          <div class="ci-xlsx-tools">
            <button type="button" @click="setAiExcelAll(true)">Select all</button>
            <button type="button" @click="setAiExcelAll(false)">Clear all</button>
            <span class="ci-xlsx-count">{{ aiExcelCheckedCount }} of {{ aiExcelModal.groups.length }} selected</span>
          </div>
          <ul class="ci-xlsx-list">
            <li v-for="g in aiExcelModal.groups" :key="'xg' + g.key">
              <label class="ci-xlsx-item"><input type="checkbox" v-model="g.checked"><span>{{ g.key }}</span></label>
            </li>
          </ul>
          <div class="ci-xlsx-actions">
            <button type="button" class="ci-xlsx-cancel" @click="aiExcelModal.open = false">Cancel</button>
            <button type="button" class="ci-xlsx-go" :disabled="aiExcelCheckedCount === 0" @click="confirmExportAiTable">⬇ Export {{ aiExcelCheckedCount }} group<template v-if="aiExcelCheckedCount !== 1">s</template></button>
          </div>
        </div>
      </div>

      <!-- 🔍 VIEW / FIX one line. Left: what the reader had. Right: what the rep can put right. The
           recompute goes to the SERVER (POST /report/quote-line-fix) rather than being worked out here —
           a corrected line has to be arithmetically identical to one the reader got right, and this file
           does not do pricing arithmetic. -->
      <div v-if="qrFix.open" class="ci-xlsx-overlay qr-below-nav" role="dialog" aria-modal="true" @click.self="qrFix.open = false">
        <div class="ci-xlsx-modal qr-fix-modal">
          <div class="ci-xlsx-head">
            <span>🔍 Line {{ qrFix.line }} — what we read, and what it should be</span>
            <button type="button" class="ci-xlsx-x" @click="qrFix.open = false" aria-label="Close">✕</button>
          </div>

          <div class="qr-fix-read">
            <div class="qr-fix-lbl">The row, exactly as it came off the page</div>
            <code v-if="qrFix.raw" class="qr-fix-raw">{{ qrFix.raw }}</code>
            <em v-else class="qr-fix-none">No row text was captured — the reader never saw a row here. Type the
              numbers off the quote and this line will be compared like any other.</em>
            <div v-if="qrFix.reason" class="qr-fix-why"><b>Why it was not compared:</b> {{ qrFix.reason }}</div>
            <div v-if="qrFix.note" class="qr-fix-why">{{ qrFix.note }}</div>
            <div v-if="(qrFix.candidates || []).length" class="qr-fix-why">
              <b>Money-shaped tokens on this row:</b> {{ qrFix.candidates.join(", ") }}
            </div>
            <button type="button" class="qr-fixbtn" v-if="qrRead && qrRead.documentText"
                    @click="openQrDoc(qrFix.raw || qrFix.part || '')">📄 Show this row in the document</button>
          </div>

          <div class="qr-fix-form">
            <label class="qr-fix-f"><span>Part number</span>
              <input type="text" v-model="qrFix.part" placeholder="e.g. NDR D 3540 CB"></label>
            <label class="qr-fix-f qr-fix-f--sm"><span>Quantity</span>
              <input type="text" v-model="qrFix.qty" placeholder="1"></label>
            <label class="qr-fix-f qr-fix-f--sm"><span>Price as printed</span>
              <input type="text" v-model="qrFix.amount" placeholder="371.90"></label>
            <label class="qr-fix-chk"><input type="checkbox" v-model="qrFix.isExt">
              <span>That amount is the <b>line total</b>, not the unit price</span></label>
            <div class="qr-fix-hint">Type the figures exactly as the quote prints them — the server does the
              arithmetic, the same way it does for every other line, and tells you what it concluded.</div>
            <div v-if="qrFix.error" class="qr-fix-err">{{ qrFix.error }}</div>
          </div>

          <div class="ci-xlsx-actions">
            <button type="button" class="ci-xlsx-cancel" @click="qrFix.open = false">Cancel</button>
            <button type="button" class="ci-xlsx-go" :disabled="qrFix.busy || !(qrFix.amount || '').trim()"
                    @click="submitQrFix">{{ qrFix.busy ? "Recomputing…" : "Recompute this line" }}</button>
          </div>
        </div>
      </div>

      <!-- 📄 THE DOCUMENT AS READ. Not a preview of the PDF — the text the reader actually worked from,
           which is the thing worth arguing with when a line comes back wrong. Plus the row census, so the
           rep can see which rows were treated as priced items and which were not. -->
      <div v-if="qrDoc.open" class="ci-xlsx-overlay qr-below-nav" role="dialog" aria-modal="true" @click.self="qrDoc.open = false">
        <div class="ci-xlsx-modal qr-doc-modal">
          <div class="ci-xlsx-head">
            <span>📄 The quote as the reader saw it</span>
            <button type="button" class="ci-xlsx-x" @click="qrDoc.open = false" aria-label="Close">✕</button>
          </div>
          <div class="qr-doc-tools">
            <input type="text" v-model="qrDoc.find" placeholder="Find on the page — part number, price…">
            <button type="button" :class="{ 'is-on': qrDoc.tab === 'text' }" @click="qrDoc.tab = 'text'">Text</button>
            <button type="button" :class="{ 'is-on': qrDoc.tab === 'rows' }" @click="qrDoc.tab = 'rows'">Rows ({{ (qrRead && qrRead.rows || []).length }})</button>
          </div>
          <!-- v-html is safe here: qrDocHtml escapes the document first and only then inserts the marks. -->
          <pre v-if="qrDoc.tab === 'text'" class="qr-doc-text" v-html="qrDocHtml"></pre>
          <div v-else class="qr-doc-rows">
            <table>
              <thead><tr><th>#</th><th>Row</th><th>Treated as</th><th class="qr-n">Qty</th><th class="qr-n">Unit</th><th class="qr-n">Line total</th></tr></thead>
              <tbody>
                <tr v-for="r in qrDocRows" :key="'qdr' + r.id" :class="{ 'is-item': r.kind === 'Item' }">
                  <td>{{ r.ordinal }}</td>
                  <td class="qr-doc-rowtext">{{ r.text }}</td>
                  <td>{{ r.kind }}</td>
                  <td class="qr-n">{{ r.qty || "—" }}</td>
                  <td class="qr-n">{{ r.provenUnit != null ? fmtMoney(r.provenUnit) : "—" }}</td>
                  <td class="qr-n">{{ r.provenExtended != null ? fmtMoney(r.provenExtended) : "—" }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="!qrDocRows.length" class="qr-fix-none">No rows matched that search.</div>
          </div>
          <div class="ci-xlsx-sub">Nothing here is stored — this is the text from the file you attached, held
            for this answer only.</div>
        </div>
      </div>

      <!-- 🖨 Print row-picker for the quote comparison — same chrome and same idea as the Excel group
           picker above (user 2026-08-12). Lines whose price could not be read start UNCHECKED: a sheet
           going to a customer should not carry rows with an empty comparison unless the rep decides it
           should. Nothing is hidden by this — the card still shows every line, and the unchecked rows are
           listed here by name, so leaving one out is a choice rather than an omission. -->
      <div v-if="qrPrintPick.open" class="ci-xlsx-overlay" role="dialog" aria-modal="true" @click.self="qrPrintPick.open = false">
        <div class="ci-xlsx-modal">
          <div class="ci-xlsx-head">
            <span>{{ qrPrintPick.mode === "excel" ? "⬇ Export quote comparison" : "🖨 Print quote comparison" }}</span>
            <button type="button" class="ci-xlsx-x" @click="qrPrintPick.open = false" aria-label="Close">✕</button>
          </div>
          <div v-if="qrPrintPick.mode === 'excel'" class="ci-xlsx-sub">Choose which lines to export — all are
            selected except any whose price could not be read, which start unchecked. The workbook gets a
            comparison sheet plus <b>one sheet per part</b>, each carrying that part's past-order statistics
            for the window this answer used.</div>
          <div v-else class="ci-xlsx-sub">Choose which lines to print — all are selected except any whose price
            could not be read, which start unchecked. The printed sheet, its “not compared” list and its
            line count all follow this selection.</div>
          <div class="ci-xlsx-tools">
            <button type="button" @click="setQrPrintAll(true)">Select all</button>
            <button type="button" @click="setQrPrintAll(false)">Clear all</button>
            <span class="ci-xlsx-count">{{ qrPrintPickCount }} of {{ qrPrintPick.rows.length }} selected</span>
          </div>
          <ul class="ci-xlsx-list">
            <li v-for="r in qrPrintPick.rows" :key="'qpp' + r.line">
              <label class="ci-xlsx-item" :class="{ 'is-unreadable': !r.readable }">
                <input type="checkbox" v-model="r.checked">
                <span>
                  {{ r.label }}
                  <em v-if="r.qty" class="qpp-qty">×{{ r.qty }}</em>
                  <b v-if="r.price" class="qpp-price">{{ r.price }}</b>
                  <i v-else class="qpp-none">price not readable</i>
                </span>
              </label>
            </li>
          </ul>
          <div class="ci-xlsx-actions">
            <button type="button" class="ci-xlsx-cancel" @click="qrPrintPick.open = false">Cancel</button>
            <button type="button" class="ci-xlsx-go" :disabled="qrPrintPickCount === 0" @click="confirmQrPrint">
              {{ qrPrintPick.mode === "excel" ? "⬇ Export" : "🖨 Print" }} {{ qrPrintPickCount }} line<template v-if="qrPrintPickCount !== 1">s</template>
            </button>
          </div>
        </div>
      </div>

      <!-- 📊 Presentation group-picker — mirrors the Excel picker above (user 2026-08-02): on a GROUPED
           result table, choose which groups the presentation covers before it is built. -->
      <div v-if="aiPresentModal.open" class="ci-xlsx-overlay" role="dialog" aria-modal="true" @click.self="aiPresentModal.open = false">
        <div class="ci-xlsx-modal ci-present-modal">
          <div class="ci-xlsx-head ci-present-head">
            <span>📊 Build presentation</span>
            <button type="button" class="ci-xlsx-x" @click="aiPresentModal.open = false" aria-label="Close">✕</button>
          </div>
          <div class="ci-xlsx-sub">Choose which groups the results table and the AI write-up cover — all are selected by default; uncheck any to leave it out. The cover totals, Overview charts and Saved Orders pages always reflect the full filtered dashboard.</div>
          <div class="ci-xlsx-tools">
            <button type="button" @click="setAiPresentAll(true)">Select all</button>
            <button type="button" @click="setAiPresentAll(false)">Clear all</button>
            <span class="ci-xlsx-count">{{ aiPresentCheckedCount }} of {{ aiPresentModal.groups.length }} selected</span>
          </div>
          <ul class="ci-xlsx-list">
            <li v-for="g in aiPresentModal.groups" :key="'pg' + g.key">
              <label class="ci-xlsx-item"><input type="checkbox" v-model="g.checked"><span>{{ g.key }}</span></label>
            </li>
          </ul>
          <div class="ci-xlsx-actions">
            <button type="button" class="ci-xlsx-cancel" @click="aiPresentModal.open = false">Cancel</button>
            <button type="button" class="ci-xlsx-go ci-present-go" :disabled="aiPresentCheckedCount === 0 || printBusy" @click="confirmCardPresentation">{{ printBusy ? "⏳ Preparing…" : "📊 Build " + aiPresentCheckedCount + " group" + (aiPresentCheckedCount === 1 ? "" : "s") }}</button>
          </div>
        </div>
      </div>

      <!-- Not-synced notice -->
      <div v-if="notReady" class="ci-notready">
        ⏳ The report data hasn’t been synced yet. Once the VMFG sync has populated
        <code>rpt_CustomerOrders</code>, this page will fill in automatically.
      </div>

      <!-- Active filters no longer sit across the top of the page; they live in the Filter Drawer (sourced from
           every card/map selection). This compact pill summarizes the active-filter count and opens the drawer. -->
      <!-- The old .ci-fpill-bar (Filters: label + chips + ⚙ More + favorites row) was folded into the unified
           .ci-fbar below: the "All customers…" hint was dropped, ⚙ More moved to the right of the date presets,
           favorites moved beside the Status dropdown, and active-filter chips render in .ci-fbar-active.
           (Layout 2 is retired, so nothing else depended on the old bar.) -->

      <!-- Shared favorites popover — appended to <body> in mounted() so it escapes the report card's isolated
           stacking context (a descendant's z-index gets trapped under the grid, same as the drawers). It's
           fixed-positioned at the hovered star and kept open by its own mouseenter + a short close delay. -->
      <!-- Shared active-filters popover: hovering ANY card's filter chip opens this (body-appended in mounted so it
           escapes the cards' clipped overflow). Same content/analysis as the "See active filters" popup; the hovered
           chip is highlighted + pulled to the top. Reuses the seeall-pop styles (box); .ci-chippop makes it fixed. -->
      <!-- Professor-style "show your work" breakdown of a KPI card's % vs prior period — body-appended (fixed) so it
           escapes the card clip, shown on CLICK of the "?" help button. HTML rebuilt per KPI in toggleKpiMath. -->
      <div ref="kpiMathPanel" v-show="kpiMathOpen" class="ci-kpi-math"
           :style="{ top: kpiMathPos.top + 'px', left: kpiMathPos.left + 'px' }"
           v-html="kpiMathBody"></div>
      <div ref="terrInsPanel" v-show="terrIns.open" class="ci-terr-ins"
           :style="{ top: terrIns.top + 'px', left: terrIns.left + 'px' }"
           v-html="terrIns.body"></div>
      <!-- Customer notation popover (📝 in the Customer ID(s) card). Body-appended so it escapes the card's
           overflow/stacking context; click-toggle with an outside-click close (mirrors the KPI-math popover). -->
      <div ref="notePopPanel" v-show="notePop.open" class="ci-notepop"
           :style="{ top: notePop.top + 'px', left: notePop.left + 'px' }">
        <div class="ci-notepop-h">
          <span class="ci-notepop-title">📝 {{ notePop.name }}</span>
          <button type="button" class="ci-notepop-x" @click="closeNotePop" aria-label="Close">×</button>
        </div>
        <div class="ci-notepop-body">{{ notePop.text }}</div>
      </div>
      <!-- Customer SPECIFICATIONS popover (📋), sibling to the notation popover (body-appended, click-toggled). -->
      <div ref="specPopPanel" v-show="specPop.open" class="ci-notepop ci-specpop"
           :style="{ top: specPop.top + 'px', left: specPop.left + 'px' }">
        <div class="ci-notepop-h">
          <span class="ci-notepop-title">📋 {{ specPop.name }}</span>
          <button type="button" class="ci-notepop-x" @click="closeSpecPop" aria-label="Close">×</button>
        </div>
        <div class="ci-notepop-body">{{ specPop.text }}</div>
      </div>
      <!-- Hero Notations/Specifications HOVER popover (body-appended, reuses .ci-notepop styling). Kept open while
           the pointer is over it so long notes can be read/scrolled; closes on leave with a short grace. -->
      <div ref="heroNotePanel" v-show="heroNote.open" class="ci-notepop ci-heronote"
           :style="{ top: heroNote.top + 'px', left: heroNote.left + 'px' }"
           @mouseenter="heroNoteKeep" @mouseleave="heroNoteLeave">
        <div class="ci-notepop-h">
          <span class="ci-notepop-title">{{ heroNote.kind === 'spec' ? '📋' : '📝' }} {{ heroNote.name }}</span>
        </div>
        <div class="ci-notepop-body">{{ heroNote.text }}</div>
      </div>
      <!-- 💡 AI sales-PERSPECTIVE on a customer's note + specs (on-demand fetch; body-appended, click-toggled). -->
      <div ref="perspPopPanel" v-show="perspPop.open" class="ci-notepop ci-persppop"
           :style="{ top: perspPop.top + 'px', left: perspPop.left + 'px' }">
        <div class="ci-notepop-h">
          <span class="ci-notepop-title">💡 {{ perspPop.name }}</span>
          <button type="button" class="ci-notepop-x" @click="closePerspPop" aria-label="Close">×</button>
        </div>
        <div v-if="perspPop.busy" class="ci-persp-loading"><span class="ci-persp-spin" aria-hidden="true"></span> Thinking…</div>
        <template v-else>
          <div v-if="perspPop.headline" class="ci-persp-headline">{{ perspPop.headline }}</div>
          <div class="ci-notepop-body">{{ perspPop.body }}</div>
          <ul v-if="perspPop.actions && perspPop.actions.length" class="ci-persp-actions">
            <li v-for="(a, ai) in perspPop.actions" :key="ai">{{ a }}</li>
          </ul>
        </template>
      </div>
      <!-- 💡 "Why is this customer here?" — on a Customer Order Details row whose ID-prefix home territory isn't in
           the active filter (its orders followed a rep into a filtered territory). Body-appended, click-toggled. -->
      <div ref="codWhyPopPanel" v-show="codWhyPop.open" class="ci-notepop ci-whypop"
           :style="{ top: codWhyPop.top + 'px', left: codWhyPop.left + 'px' }">
        <div class="ci-notepop-h">
          <span class="ci-notepop-title">💡 Rep history</span>
          <button type="button" class="ci-notepop-x" @click="closeCodWhyPop" aria-label="Close">×</button>
        </div>
        <div class="ci-notepop-body ci-why-body">
          <p class="ci-why-cust"><b>{{ codWhyPop.name }}</b></p>
          <template v-if="codWhyPop.attribution.length">
            <p class="ci-why-lbl" v-if="codWhyPop.attribution.length > 1">This customer's orders are <b>split across more than one rep / territory</b> — per the Sales Territories map:</p>
            <p class="ci-why-lbl" v-else>Current attribution — per the Sales Territories map:</p>
            <div class="ci-why-hist">
              <div v-for="(a, ai) in codWhyPop.attribution" :key="'a' + ai" class="ci-why-hrow is-cur">
                <span class="ci-why-hrep">{{ a.rep }}</span>
                <span class="ci-why-hterr">{{ a.territory }}</span>
                <span class="ci-why-hnum">{{ fmtNum(a.lines) }} · {{ fmtMoney(a.sales) }}</span>
              </div>
            </div>
          </template>
          <p v-else class="ci-why-lbl">Currently attributed to <b>{{ codWhyPop.currentRep }}</b><span v-if="codWhyPop.currentTerr"> ({{ codWhyPop.currentTerr }})</span> — per the Sales Territories map.</p>
          <template v-if="codWhyHistoryRows.length">
            <p class="ci-why-lbl">How VISUAL originally credited these orders (before any alias / rep change):</p>
            <div class="ci-why-hist">
              <div v-for="(h, hi) in codWhyHistoryRows" :key="'h' + hi" class="ci-why-hrow">
                <span class="ci-why-hrep">{{ h.rep || "(no rep in VISUAL)" }}</span>
                <span class="ci-why-hterr">{{ h.territory || "— no territory" }}</span>
                <span class="ci-why-hnum">{{ fmtNum(h.lines) }} · {{ fmtMoney(h.sales) }}</span>
              </div>
            </div>
          </template>
          <p class="ci-why-note">Each order keeps its VISUAL sales rep; the map only fills in that rep's territory. Orders with no rep in VISUAL are auto-assigned by the customer-ID prefix rule (toggle in Sales Territories).</p>
        </div>
      </div>
      <!-- 💡 "Why unassigned" diagnostic — body-appended; who + why (no rep vs unmapped rep) + suggested territory/rep. -->
      <div ref="unassignedPopPanel" v-show="unassignedPop.open" class="ci-notepop ci-unpop"
           :style="{ top: unassignedPop.top + 'px', left: unassignedPop.left + 'px' }">
        <div class="ci-notepop-h">
          <span class="ci-notepop-title">💡 Why these are unassigned</span>
          <button type="button" class="ci-notepop-x" @click="closeUnassignedPop" aria-label="Close">×</button>
        </div>
        <div v-if="unassignedPop.busy" class="ci-persp-loading"><span class="ci-persp-spin" aria-hidden="true"></span> Analyzing…</div>
        <template v-else-if="unassignedPop.data && unassignedPop.data.ok">
          <div class="ci-un-sum">{{ fmtMoney(unassignedPop.data.totalSales) }} · {{ unassignedPop.data.customerCount }} customers · {{ fmtNum(unassignedPop.data.totalOrders) }} orders</div>
          <div class="ci-un-causes">
            <template v-for="bc in unassignedPop.data.byCause"><span v-if="bc.customers" :key="bc.cause" class="ci-un-cause" :class="'is-' + bc.cause">{{ bc.label }}: {{ fmtMoney(bc.sales) }} ({{ bc.customers }})</span></template>
          </div>
          <div v-if="unassignedPop.data.autoAssignOn === false" class="ci-un-offnote">⚠ Prefix auto-assign is <strong>OFF</strong> (Sales Territories) — no-rep orders won't be assigned automatically. Turn it on to auto-assign these, or set the rep in VISUAL.</div>
          <div v-if="unassignedPop.data.unmappedReps && unassignedPop.data.unmappedReps.length" class="ci-un-block">
            <div class="ci-un-h">Reps on orders but not in the territory map — click to add in Account Setup → Sales Territories</div>
            <div class="ci-un-reps"><button v-for="ur in unassignedPop.data.unmappedReps" :key="ur.rep" type="button" class="ci-un-rep ci-un-rep--btn" @click.stop="addRepToTerritoryMap(ur.rep)" :title="'Add ' + ur.rep + ' to the territory map (Account Setup → Sales Territories)'">{{ ur.rep }} · {{ fmtMoney(ur.sales) }} →</button></div>
          </div>
          <div class="ci-un-h">Top customers to assign (biggest first)</div>
          <ul class="ci-un-list">
            <li v-for="(c, ci) in (unassignedPop.showAll ? unassignedPop.data.customers : unassignedPop.data.customers.slice(0, 12))" :key="ci" class="ci-un-item" :class="'is-' + c.cause">
              <div class="ci-un-item-top"><b>{{ c.customerId }}</b> {{ c.customerName }}<span v-if="c.state" class="ci-un-st">{{ c.state }}</span><span v-if="c.country" class="ci-un-ctry">{{ c.country }}</span><span class="ci-un-amt">{{ fmtMoney(c.sales) }}</span></div>
              <div class="ci-un-fix">{{ c.fix }}</div>
            </li>
          </ul>
          <button v-if="unassignedPop.data.customers.length > 12" type="button" class="ci-un-more" @click.stop="toggleUnassignedAll">
            {{ unassignedPop.showAll ? 'Show fewer' : ('+ ' + (unassignedPop.data.customers.length - 12) + ' more customers…') }}
          </button>
          <div class="ci-un-emailrow">
            <button v-if="unPartsCusts.length" type="button" class="ci-un-email ci-un-email--parts" @click.stop="openUnassignedEmail('parts')">📧 Email Parts team ({{ unPartsCusts.length }}) — set rep in VISUAL</button>
            <button v-if="unItCusts.length" type="button" class="ci-un-email" @click.stop="openUnassignedEmail('it')">📧 Email IT ({{ unItCusts.length }}) — fix the territory map</button>
          </div>
          <div class="ci-un-foot"><b class="ci-un-foot-ok">✓ Mapped</b> rows are already assigned for reporting — they drop off once the rep is set in VISUAL at the source. Blank-rep orders need a rep in VISUAL; unmapped reps &amp; states are fixed in Account Setup → Sales Territories.</div>
        </template>
        <div v-else class="ci-notepop-body">{{ (unassignedPop.data && unassignedPop.data.message) || 'No unassigned orders in the current view.' }}</div>
      </div>
      <!-- Email-the-parts-team modal (from the 💡 unassigned diagnostic). Body-appended; z above the popover. -->
      <div ref="unEmailPanel" v-show="unEmail.open" class="ci-unemail-back" @click.self="closeUnassignedEmail">
        <div class="ci-unemail" role="dialog" aria-label="Email the parts team">
          <div class="ci-unemail-h"><span>{{ unEmail.target === 'parts' ? '📧 Email Parts team' : '📧 Email IT (Help Desk)' }}</span><button type="button" class="ci-unemail-x" @click="closeUnassignedEmail" aria-label="Close">×</button></div>
          <div v-if="unEmail.sent" class="ci-unemail-sent">✓ Sent to {{ unEmail.target === 'parts' ? 'the Parts team' : 'IT' }}.</div>
          <template v-else>
            <div class="ci-unemail-body">
              <p v-if="unEmail.target === 'parts'" class="ci-unemail-lead">Sends <b>parts@jcsteele.com</b> (JC Steele Parts) the accounts below — each is auto-mapped for reporting but still needs its <b>rep set in VISUAL</b> at the source. The full list is in the email. Replies come back to you.</p>
              <p v-else class="ci-unemail-lead">Sends <b>helpdesk@jcsteele.com</b> (JC Steele IT) the reps / Customer-ID prefixes below that <b>aren't in the territory map</b> yet, so IT can add them in Account Setup → Sales Territories. The full list is in the email. Replies come back to you.</p>
              <div v-if="unEmailCusts.length" class="ci-unemail-preview">
                <div class="ci-unemail-preview-h">{{ unEmailCusts.length }} account{{ unEmailCusts.length === 1 ? '' : 's' }} — exactly what's included:</div>
                <ul class="ci-unemail-preview-list">
                  <li v-for="(c, ci) in unEmailCusts" :key="ci"><b>{{ c.customerId }}</b> {{ c.customerName }}<span v-if="c.state" class="ci-unemail-preview-st"> · {{ c.state }}</span><span v-if="c.country" class="ci-unemail-preview-st"> · {{ c.country }}</span><span class="ci-unemail-preview-amt">{{ fmtMoney(c.sales) }}</span></li>
                </ul>
              </div>
              <label class="ci-unemail-lbl">Add a note (optional)</label>
              <textarea v-model="unEmail.note" class="ci-unemail-note" rows="4" placeholder="e.g. Please confirm who should own the NC customers…"></textarea>
              <div v-if="unEmail.error" class="ci-unemail-err">{{ unEmail.error }}</div>
            </div>
            <div class="ci-unemail-foot">
              <button type="button" class="ci-unemail-cancel" @click="closeUnassignedEmail" :disabled="unEmail.busy">Cancel</button>
              <button type="button" class="ci-unemail-send" @click="sendUnassignedEmail" :disabled="unEmail.busy">{{ unEmail.busy ? 'Sending…' : (unEmail.target === 'parts' ? 'Send to Parts' : 'Send to IT') }}</button>
            </div>
          </template>
        </div>
      </div>
      <div ref="chipHoverPanel" v-show="chipHoverOpen && activeChips.length" class="ci-fbar-seeall-pop ci-chippop"
           :style="{ top: chipHoverPos.top + 'px', left: chipHoverPos.left + 'px' }"
           @mouseenter="openChipHoverPop()" @mouseleave="closeChipHoverPop()">
        <div class="ci-fbar-seeall-h">Active filters ({{ activeChips.length }})</div>
        <ul class="ci-fbar-seeall-list">
          <li v-for="c in chipHoverSorted" :key="'chp' + c.key + (c.val || '')" class="ci-seeall-li"
              :class="{ 'is-dead': chipIrrelevant(c), 'is-culprit': chipIsCulprit(c), 'is-hovered': isChipHoverActive(c) }"
              @mouseenter="hoverChip(c)" @mouseleave="unhoverChip">
            <div class="ci-seeall-row">
              <span class="ci-fbar-seeall-k">{{ c.label }}:</span>
              <span class="ci-fbar-seeall-v">{{ c.text }}</span>
              <span v-if="chipIsCulprit(c)" class="ci-fbar-seeall-cause" aria-label="likely cause of the struck-out filters">likely cause</span>
              <button type="button" class="ci-fbar-seeall-x" @click="removeChip(c.key, c.val)" title="Remove filter">×</button>
            </div>
            <div v-show="isChipHovered(c) && (chipIrrelevant(c) || chipIsCulprit(c))" class="ci-tip" v-html="chipTipHtml(c)"></div>
          </li>
        </ul>
        <button type="button" class="ci-fbar-seeall-clear" @click="resetFilters">Clear all filters</button>
      </div>
      <!-- Rep-basis help popup — top-level (NOT inside a vue-grid-item, whose transform would trap position:fixed);
           body-appended in mounted() like favPopPanel below. Opens on hover of the "Attribute by" ⓘ. -->
      <div ref="repBasisHelpPanel" v-show="repBasisHelp.open" class="ci-rbhelp-pop" :style="{ top: repBasisHelp.top + 'px', left: repBasisHelp.left + 'px' }" @mouseenter="keepRepBasisHelp" @mouseleave="hideRepBasisHelp">
        <div class="ci-rbhelp-h">Attribute by — who gets credit for each order</div>
        <div class="ci-rbhelp-row"><span class="ci-rbhelp-n">Owned By</span> <span class="ci-rbhelp-sub">customer's rep</span><span class="ci-rbhelp-def">default</span><p>Your book of business. Credits every order to the rep who owns the account <em>today</em> — so a customer reassigned to you shows their whole history under you. Best for “what are my accounts buying, and how are they trending?”</p></div>
        <div class="ci-rbhelp-row"><span class="ci-rbhelp-n">Ordered By</span> <span class="ci-rbhelp-sub">on the order</span><p>Who worked the deal. Credits each order to the rep named on the sales order — the one who actually took it — regardless of who owns the account. Best for “which orders did I personally handle?” or when you covered for another rep.</p></div>
        <div class="ci-rbhelp-row"><span class="ci-rbhelp-n">Sold By</span> <span class="ci-rbhelp-sub">on the invoice</span><p>Who got the sale credit. Credits each order to the rep on the invoice — the name that carries the sale when it ships/bills. Best for commission-style questions: “who was credited for this sale?”</p></div>
        <div class="ci-rbhelp-row ci-rbhelp-toggle"><span class="ci-rbhelp-n">Current vs Original</span><p><b>Current</b> rolls a departed rep's history onto their replacement (e.g. Josh McClain → David Canavan) — use it for “who handles this now.” <b>Original</b> keeps the rep exactly as recorded at the time of the sale — use it for history or an audit trail.</p></div>
      </div>
      <div ref="favPopPanel" v-show="favPopId && currentPopItem" class="ci-favpop"
           :style="{ top: favPopPos.top + 'px', left: favPopPos.left + 'px' }"
           @mouseenter="clearFavPopTimer" @mouseleave="closeFavPop()">
        <template v-if="currentPopItem">
          <div class="ci-favpop-h">
            <input v-if="favRenaming" ref="favRenameInput" v-model="favDraft.name" class="ci-favpop-nameinput" @keyup.enter="favRenaming = false" @keyup.esc="favRenaming = false" />
            <span v-else class="ci-favpop-title" @click="startFavRename" title="Click to rename">{{ favPopName }}</span>
            <button v-if="favDirty" type="button" class="ci-favpop-save" @click="saveFavEdit" title="Save changes">✓</button>
          </div>
          <!-- At-a-glance: the email schedule for this favorite/group, if one exists -->
          <div v-if="currentPopSchedule" class="ci-favpop-sched" :class="{ 'is-paused': currentPopSchedule.active === false }" title="Email schedule — open 📧 Email to change">📧 {{ schedLabel(currentPopSchedule) }}</div>
          <!-- GROUP: the favorites inside it -->
          <template v-if="favPopKind === 'group'">
            <div class="ci-favpop-sub">{{ groupFavorites(favPopId).length }} favorite(s) in this group</div>
            <button v-for="fav in groupFavorites(favPopId)" :key="fav.id" type="button" class="ci-favpop-item" @click="applyFavorite(fav)" :title="'Apply ' + fav.name">
              <span class="ci-fav-star">★</span><span class="ci-favpop-itemname">{{ fav.name }}</span>
              <span class="ci-fav-eject" @click.stop="removeFromGroup(fav)" title="Remove from this group (keeps the favorite)">⤴</span>
              <span class="ci-fav-x" @click.stop="deleteFavorite(fav)" title="Delete favorite">×</span>
            </button>
            <div class="ci-favpop-foot">
              <button type="button" class="ci-favpop-email" @click="openNotify(currentPopItem, 'group')" title="Schedule an email digest of this group">📧 Email</button>
              <button type="button" class="ci-favpop-del" @click="deleteFavGroup(currentPopItem)" title="Ungroup (keeps the favorites)">Ungroup</button>
            </div>
          </template>
          <!-- LOOSE favorite: its configured filters (or, for a hero result-card favorite, the question it re-runs) -->
          <template v-else>
            <div v-if="currentPopItem && currentPopItem.ask && currentPopItem.ask.question" class="ci-favpop-ask">
              <span class="ci-favpop-ask-ic" aria-hidden="true">💬</span>{{ currentPopItem.ask.question }}
              <div class="ci-favpop-ask-hint">Apply re-runs this question and rebuilds the card.</div>
            </div>
            <div v-else class="ci-favpop-filters">
              <span v-for="(chip, i) in favPopChips" :key="i" class="ci-favpop-chip">
                <b>{{ chip.label }}:</b> {{ chip.text }}
                <span class="ci-favpop-chipx" @click.stop="removeFavFilter(chip)" title="Remove this filter">×</span>
              </span>
              <span v-if="!favPopChips.length" class="ci-favpop-empty">No filters in this favorite.</span>
            </div>
            <div class="ci-favpop-foot">
              <button type="button" class="ci-favpop-apply" @click="applyFavorite(currentPopItem)">Apply</button>
              <button type="button" class="ci-favpop-email" @click="openNotify(currentPopItem, 'favorite')" title="Schedule an email digest of this favorite">📧 Email</button>
              <button type="button" class="ci-favpop-del" @click="deleteFavorite(currentPopItem)" title="Delete favorite">Delete</button>
            </div>
          </template>
        </template>
      </div>
      <!-- while editing, a click-outside backdrop pins the popover; clicking it discards the unsaved edit -->
      <div v-if="favDirty" class="ci-fav-popback" @click="cancelFavEdit"></div>

      <!-- Schedule-email modal: a favorite/group's digest cadence + recipients (sent from parts@jcsteele.com). -->
      <div v-if="notifyOpen" class="ci-notify-back" @click="closeNotify"></div>
      <div v-if="notifyOpen" class="ci-notify-modal" role="dialog" aria-label="Schedule email digest">
        <div class="ci-notify-head">
          <span>📧 Email this {{ notifyKind === 'group' ? 'group' : 'favorite' }}</span>
          <button type="button" class="ci-notify-x" @click="closeNotify" aria-label="Close">×</button>
        </div>
        <div class="ci-notify-body">
          <div class="ci-notify-name">{{ notifyItem && notifyItem.name }}</div>
          <p class="ci-notify-intro">A full report‑detail digest (KPIs, top customers &amp; parts, territory, AI insights, saved carts) is emailed from <b>parts@jcsteele.com</b> on your schedule.</p>

          <label class="ci-notify-lbl">Frequency</label>
          <div class="ci-notify-freq">
            <label v-for="f in ['daily','weekly','monthly','quarterly']" :key="f"><input type="radio" :value="f" v-model="notifyForm.frequency"> {{ f.charAt(0).toUpperCase() + f.slice(1) }}</label>
          </div>

          <div v-if="notifyForm.frequency === 'weekly'" class="ci-notify-row">
            <label class="ci-notify-lbl">Day of week</label>
            <select v-model.number="notifyForm.dayOfWeek"><option v-for="(d, i) in weekDays" :key="i" :value="i">{{ d }}</option></select>
          </div>
          <div v-if="notifyForm.frequency === 'monthly' || notifyForm.frequency === 'quarterly'" class="ci-notify-row">
            <label class="ci-notify-lbl">Day of month</label>
            <select v-model.number="notifyForm.dayOfMonth"><option v-for="n in 28" :key="n" :value="n">{{ n }}</option></select>
          </div>
          <div class="ci-notify-row">
            <label class="ci-notify-lbl">Send time</label>
            <select v-model.number="notifyForm.hourLocal"><option v-for="h in 24" :key="h - 1" :value="h - 1">{{ fmtHour(h - 1) }}</option></select>
            <span class="ci-notify-tz">Eastern</span>
          </div>

          <label class="ci-notify-lbl">Recipients</label>
          <div class="ci-notify-to">Sends to <b>you</b><span v-if="myEmail"> ({{ myEmail }})</span>. Add others, comma‑separated:</div>
          <input type="text" class="ci-notify-recip" v-model="notifyForm.recipients" placeholder="manager@example.com, ..." />

          <label class="ci-notify-chk"><input type="checkbox" v-model="notifyForm.active"> Active <span class="ci-notify-chk-note">(emails send only while checked)</span></label>

          <div v-if="notifyExisting" class="ci-notify-status">Scheduled — next: {{ fmtHistDate(notifyExisting.nextRunAt) }}<span v-if="notifyExisting.lastSentAt"> · last sent {{ fmtHistDate(notifyExisting.lastSentAt) }}</span></div>
          <div v-if="notifyMsg" class="ci-notify-msg">{{ notifyMsg }}</div>
        </div>
        <div class="ci-notify-foot">
          <button type="button" class="ci-notify-closebtn" @click="closeNotify" :disabled="!!notifyBusy">Close</button>
          <button type="button" class="ci-notify-del" v-if="notifyExisting" @click="cancelNotification" :disabled="!!notifyBusy">Cancel schedule</button>
          <span class="ci-notify-spacer"></span>
          <button type="button" class="ci-notify-test" @click="sendTestNotification" :disabled="!!notifyBusy">{{ notifyBusy === 'test' ? 'Sending…' : 'Send test now' }}</button>
          <button type="button" class="ci-notify-save" @click="saveNotification" :disabled="!!notifyBusy">{{ notifyBusy === 'save' ? 'Saving…' : 'Save' }}</button>
        </div>
      </div>

      <!-- No-overlap banner: filters active but nothing matches (e.g. a customer + a territory that don't intersect). -->
      <div v-if="loadError" class="ci-noresults">⚠ {{ loadErrorMsg || "The report couldn't load for this selection (the request errored — it may be too broad a range). Try a narrower date range or reload." }}</div>
      <div v-else-if="noResults" class="ci-noresults">No matching orders for this filter combination — your selections don't overlap. Remove a chip to widen the results.</div>

      <!-- ===================== LAYOUT 1 (default) ===================== -->
      <template v-if="layout !== '2'">
      <!-- (The Filters + Favorites bar is now the first DRAGGABLE card inside the grid below — see the
           it.i === 'filters' branch in the grid-item loop. It was moved out of this static spot so it can be
           rearranged with the other cards.) -->
      <!-- 3-column snap-to-grid dashboard. Cards relocate/resize and persist per account. Controls live in the right-rail "Cards" toggle. -->
      <!-- Desktop: [collapsible FILTER rail] · [FAVORITES ★ dropdown] · [dashboard grid]. Mobile: grid only (filters/favorites are the inline mobile card). -->
      <!-- Filters & Favorites are draggable/resizable grid cards below (the it.i==='filters' / it.i==='favorites' branches). -->
      <grid-layout :layout="effectiveLayout" :col-num="24" :row-height="25" :margin="[12, 12]"
                   :is-draggable="!isLayoutLocked && !isMobile" :is-resizable="!isLayoutLocked && !isMobile" :vertical-compact="true"
                   :use-css-transforms="true" @layout-updated="onDashLayoutUpdated">
        <grid-item v-for="it in effectiveLayout" :key="it.i" :x="it.x" :y="it.y" :w="it.w" :h="it.h" :i="it.i"
                   :min-w="1" :min-h="2" drag-allow-from=".dash-handle" :class="[(it.i === 'filters' || it.i === 'favorites') ? 'gi-fbar' : '', pcFull ? (it.i === 'partCosts' ? 'gi-pcfull' : 'gi-pchide') : (plFull ? (it.i === 'partLoss' ? 'gi-pcfull' : 'gi-pchide') : '')]"
                   @move="suppressSticky" @moved="resumeSticky" @resize="suppressSticky" @resized="resumeSticky">
          <!-- Filters + Favorites card — BARE wrapper (overflow visible so the dropdown pop-outs aren't clipped
               by the card box); .gi-fbar (on the grid-item) raises z-index so those pop-outs float above the
               neighboring cards. Drag it by its handle; on a custom layout you can shrink it and move e.g. the
               Map up beside it. -->
          <!-- FILTERS card — date range, presets, Status/Invoiced + the active-filter chips. Drag/resize like any card. -->
          <div v-if="it.i === 'filters'" class="dash-card" :data-card-id="it.i">
            <div class="dash-handle"><span class="dash-grip" aria-hidden="true">⋮⋮</span><span class="dash-handle-title">Filters</span><button type="button" class="ci-card-collapse" title="Collapse the filters to the right edge" aria-label="Collapse the filters" @click.stop="collapseFilters" @mousedown.stop>▶</button></div>
            <div class="dash-body ci-frl-body">
              <div class="ci-frl-dates">
                <label class="ci-frl-lbl">From</label>
                <input type="text" inputmode="numeric" maxlength="10" class="ci-datebox" :value="dateDisplay(filters.from)" @focus="$event.target.select()" @input="onDateTypeMask($event)" @change="commitTypedDate('from', $event)" @keyup.enter="$event.target.blur()" placeholder="MM/DD/YYYY" title="Type a date as MM/DD/YYYY" />
                <label class="ci-frl-lbl">To</label>
                <input type="text" inputmode="numeric" maxlength="10" class="ci-datebox" :value="dateDisplay(filters.to)" @focus="$event.target.select()" @input="onDateTypeMask($event)" @change="commitTypedDate('to', $event)" @keyup.enter="$event.target.blur()" placeholder="MM/DD/YYYY" title="Type a date as MM/DD/YYYY" />
              </div>
              <!-- Both preset groups wrap in .ci-frl-presetrow — display:contents on desktop (no layout change),
                   but on mobile it becomes one flex row so Today/Yest/1W/YTD/QTR/Year/Trailing sit on a single line. -->
              <div class="ci-frl-presetrow">
              <div class="ci-frl-presets">
                <button type="button" :class="{ 'is-on': activePreset === 'today' }" @click="setDateShortcut('today')">Today</button>
                <button type="button" :class="{ 'is-on': activePreset === 'yesterday' }" @click="setDateShortcut('yesterday')" title="Yesterday">Yest</button>
                <button type="button" :class="{ 'is-on': activePreset === 'week' }" @click="setDateShortcut('week')" title="Last 7 days">1W</button>
                <button type="button" :class="{ 'is-on': activePreset === 'ytd' }" @click="setDateShortcut('ytd')" title="Year to date">YTD</button>
              </div>
              <div class="ci-frl-presets">
                <span class="ci-preset-pop-wrap">
                  <button type="button" :class="{ 'is-on': qtrOpen || qtrActive }" @click.stop="qtrOpen = !qtrOpen; yearOpen = false; rollOpen = false" title="Pick a calendar quarter">QTR ▾</button>
                  <span v-if="qtrOpen" class="ci-preset-pop">
                    <button v-for="q in 4" :key="'qp' + q" type="button" :class="{ 'is-on': activePreset === 'q' + q }" @click="setDateShortcut('q' + q); qtrOpen = false" :title="['Jan – Mar','Apr – Jun','Jul – Sep','Oct – Dec'][q - 1]">Q{{ q }}</button>
                  </span>
                </span>
                <span class="ci-preset-pop-wrap">
                  <button type="button" :class="{ 'is-on': yearOpen || calYearActive }" @click.stop="yearOpen = !yearOpen; qtrOpen = false; rollOpen = false" title="Calendar year(s): Jan 1 – Dec 31">YEAR{{ calYearActive ? ' (' + activePreset + ')' : '' }} ▾</button>
                  <span v-if="yearOpen" class="ci-preset-pop ci-preset-pop--year">
                    <button v-for="n in 20" :key="'yp' + n" type="button" :class="{ 'is-on': activePreset === String(n) }" @click="setDateShortcut(n); yearOpen = false" :title="n === 1 ? 'This calendar year (Jan 1 – Dec 31)' : ('Last ' + n + ' calendar years (Jan 1 – Dec 31)')">{{ n }}</button>
                  </span>
                </span>
                <span class="ci-preset-pop-wrap">
                  <button type="button" :class="{ 'is-on': rollOpen || rollActive }" @click.stop="rollOpen = !rollOpen; qtrOpen = false; yearOpen = false" title="Trailing window ending today — the last N year(s) up to today's date">TRAILING{{ rollActive && activePreset ? ' (' + String(activePreset).slice(1) + ')' : '' }} ▾</button>
                  <span v-if="rollOpen" class="ci-preset-pop ci-preset-pop--year">
                    <button v-for="n in 20" :key="'rp' + n" type="button" :class="{ 'is-on': activePreset === 'r' + n }" @click="setDateShortcut('r' + n); rollOpen = false" :title="n === 1 ? 'The last 12 months (one year ago → today)' : ('The last ' + n + ' years (→ today)')">{{ n }}yr</button>
                  </span>
                </span>
              </div>
              </div>
              <div class="ci-frl-field"><filter-slicer label="Status" :options="opts.statuses" :selected="filters.status" :tips="statusTips" @toggle="toggleFilter('status', $event)" @clear="clearFilter('status')" /></div>
              <div class="ci-frl-field"><div class="l2-f"><span>Invoiced</span><select class="ci-fbar-nativesel" v-model="filters.invoiced" @change="applyFilters"><option value="all">All</option><option value="invoiced">Invoiced</option><option value="not">Not invoiced</option></select></div></div>
              <!-- Attribute-by (rep basis) — privileged users only (Master / Sales All). Chooses WHICH VISUAL rep
                   the WHOLE report attributes sales to, and whether departed reps are remapped (Current) or shown
                   raw (Original). Default Owned By / Current = the standard synced attribution (no re-base). -->
              <div v-if="isPrivileged" class="ci-frl-field ci-frl-repbasis">
                <div class="l2-f">
                  <span class="ci-rbhelp-lbl">Attribute by<span class="ci-rbhelp-ic" tabindex="0" role="button" aria-label="What do these attribution options mean?" @mouseenter="showRepBasisHelp($event)" @mouseleave="hideRepBasisHelp" @focus="showRepBasisHelp($event)" @blur="hideRepBasisHelp">&#9432;</span></span>
                  <select class="ci-fbar-nativesel" v-model="repBasis" @change="applyRepBasis">
                    <option value="owned">Owned By (customer's rep)</option>
                    <option value="ordered">Ordered By (on the order)</option>
                    <option value="sold">Sold By (on the invoice)</option>
                  </select>
                </div>
                <div class="ci-repview" role="group" aria-label="Rep view">
                  <button type="button" :class="{ 'is-on': repView === 'current' }" @click="setRepView('current')">Current</button>
                  <button type="button" :class="{ 'is-on': repView === 'original' }" @click="setRepView('original')">Original</button>
                </div>
              </div>
              <div v-if="activeChips.length" class="ci-frl-chips">
                <!-- Header doubles as a collapse toggle: on MOBILE the chip list starts collapsed and this tap
                     shows/hides it (labeled "Filters"); on desktop it's a static header and the list always shows. -->
                <button type="button" class="ci-frl-chips-h ci-frl-chips-toggle" :class="{ 'is-open': mFiltersOpen }"
                        :aria-expanded="mFiltersOpen ? 'true' : 'false'" @click="mFiltersOpen = !mFiltersOpen">
                  {{ isMobile ? 'Filters' : 'Active filters' }} <em>{{ activeChips.length }}</em>
                  <span class="ci-frl-chips-caret" aria-hidden="true">{{ mFiltersOpen ? '▴' : '▾' }}</span>
                </button>
                <div class="ci-frl-chips-list" v-show="!isMobile || mFiltersOpen">
                <!-- Chips grouped by dimension so a multi-value filter (e.g. Status = 3) is ONE compact row: a
                     dropdown to unselect individual values, instead of one chip per value stacking the rail. -->
                <template v-for="g in activeChipGroups">
                  <span v-if="g.vals.length === 1" :key="'frc:' + g.key" class="ci-fbar-chip ci-frl-chip" :class="{ 'ci-fbar-chip--dead': chipIrrelevant(g.vals[0]) }" :title="chipTitle(g.vals[0])">
                    <span class="ci-fbar-chip-k">{{ g.label }}:</span> {{ g.vals[0].text }}
                    <button type="button" class="ci-fbar-chip-x" :style="chipColor(g.vals[0]) ? { background: chipColor(g.vals[0]), color: '#fff' } : null" @click="removeChip(g.vals[0].key, g.vals[0].val)" title="Remove">×</button>
                  </span>
                  <span v-else :key="'frg:' + g.key" class="ci-frl-chipgrp" :class="{ 'is-open': openChipGroupKey === g.key }">
                    <button type="button" class="ci-fbar-chip ci-frl-chip ci-frl-chipgrp-btn" @click.stop="toggleChipGroup(g.key)" :title="g.label + ': ' + g.summary + ' — click to unselect'">
                      <span class="ci-fbar-chip-k">{{ g.label }}:</span> {{ g.vals.length }} selected <span class="ci-frl-caret">▾</span>
                    </button>
                    <span v-show="openChipGroupKey === g.key" v-for="c in g.vals" :key="'frgv:' + c.key + ':' + (c.val || '')" class="ci-fbar-chip ci-frl-chip ci-frl-chipgrp-val" :class="{ 'ci-fbar-chip--dead': chipIrrelevant(c) }" :title="'Unselect ' + c.text">
                      {{ c.text }}
                      <button type="button" class="ci-fbar-chip-x" @click.stop="removeChip(c.key, c.val)" title="Unselect">×</button>
                    </span>
                  </span>
                </template>
                </div>
              </div>
              <button type="button" class="ci-fbar-clearall ci-frl-clear" @click="resetFilters" title="Remove all filters and chips">Clear all</button>
            </div>
          </div>
          <!-- FAVORITES card — ★ icons (hover for the saved filters + Apply/Delete) and the ★+ save. The grid only
               drags from .dash-handle, so the same slim handle bar the Filters card has makes this draggable too
               (the sticky/auto-scroll pinning in refreshStickyCards is untouched — it pins the whole card). -->
          <div v-else-if="it.i === 'favorites'" class="dash-card" :data-card-id="it.i">
            <div class="dash-handle" title="Drag to move"><span class="dash-grip" aria-hidden="true">⋮⋮</span></div>
            <div class="dash-body ci-fav-cardbody" :class="favIsVertical(it) ? 'ci-fav--vert' : 'ci-fav--horz'">
              <!-- Drag a star onto another star; WHERE on it decides what happens, and the target
                   shows you which BEFORE you release:
                     • over its leading/trailing EDGE → REORDER — a red insertion line appears there
                     • over its CENTRE               → GROUP  — the target rings red and shows ＋
                     • over a red GROUP chip         → join that group (whole chip, rings red)
                   Shift forces GROUP anywhere on a star, for when the chips are tight. -->
              <div class="ci-fav-bar" :class="{ 'is-favdrag': !!favDragId, 'is-favtouch': favTouchDrag }">
                <!-- ★+ Save-current-filters — relocated here from the old drag-handle bar (that gray strip was removed). -->
                <button type="button" class="ci-fav-col-btn" :disabled="!activeChips.length" @click.stop="activeChips.length && saveCurrentAsFavorite()" :title="activeChips.length ? 'Save the current filters as a favorite' : 'Apply filters first, then save them as a favorite'"><span class="ci-fav-star">★</span><span class="ci-fav-col-plus">+</span></button>
                <div v-for="g in favoriteGroups" :key="g.id" class="ci-favwrap" :data-fav-group="g.id"
                     :class="{ 'fav-drop-group': !!favDragId && favDropGroupId === g.id }"
                     @mouseenter="openFavPop(g.id, 'group', $event)" @mouseleave="closeFavPop()"
                     @dragover.prevent="favGroupOver(g.id)" @dragleave="favGroupLeave(g.id)"
                     @drop.prevent="favDropOnGroup(g)">
                  <button type="button" class="ci-fav ci-fav--group" :class="{ 'is-active': favPopId === g.id }" :title="'Drop a ★ here to add it to ' + g.name">
                    <span class="ci-fav-star">★</span><span class="ci-fav-badge">{{ groupFavorites(g.id).length }}</span>
                  </button>
                </div>
                <div v-for="(fav, fi) in ungroupedFavorites" :key="fav.id" class="ci-favwrap" :data-fav-id="fav.id"
                     :class="[favDragId && favDragId !== fav.id && favDropIdx === fi ? 'fav-drop-' + favDropMode : '', favDragId === fav.id && favTouchDrag ? 'is-touchsrc' : '']"
                     @mouseenter="openFavPop(fav.id, 'fav', $event)" @mouseleave="closeFavPop()">
                  <button type="button" class="ci-fav"
                          :class="{ 'is-active': favPopId === fav.id, 'is-chat': !!(fav.ask && fav.ask.question), 'is-dragging': favDragId === fav.id, 'is-pressing': favPressId === fav.id }"
                          draggable="true" @dragstart="favDragStart(fav, $event)" @dragend="favDragEnd()"
                          @touchstart="favTouchStart(fav, $event)" @contextmenu.prevent
                          @dragover.prevent="favChipOver(fi, $event)" @dragleave="favChipLeave(fi)"
                          @drop.prevent="favDropOnFavorite(fav, $event)"
                          @mousedown.left="favDown($event)" @mouseup.left="favUp(fav, $event)"
                          @keydown.enter.space.prevent="applyFavorite(fav)"
                          @keydown.alt.up.prevent="favNudge(fav, -1)" @keydown.alt.left.prevent="favNudge(fav, -1)"
                          @keydown.alt.down.prevent="favNudge(fav, 1)" @keydown.alt.right.prevent="favNudge(fav, 1)"
                          :title="'Drag onto a ★: its EDGE reorders (red line), its CENTRE groups (＋) · Alt+Arrow to move · ' + fav.name"
                          :aria-label="fav.name">
                    <span class="ci-fav-star">★</span>
                  </button>
                </div>
              </div>
              <div v-if="!favoriteGroups.length && !ungroupedFavorites.length" class="ci-fav-panel-empty">Apply filters, then click ★ to save a favorite.</div>
            </div>
          </div>
          <div v-else class="dash-card" :data-card-id="it.i">
            <div class="dash-handle"><span class="dash-grip" aria-hidden="true">⋮⋮</span><span v-if="kpiIcon(it.i)" class="dash-handle-ico" aria-hidden="true">{{ kpiIcon(it.i) }}</span><span class="dash-handle-title" :style="titleStyle(it.i)" :class="{ 'is-clickable': masterEdit }" @click.stop="onTitleClick(it.i)" :title="masterEdit ? 'Click to style this title' : ''">{{ dashTitle(it.i) }}</span><button v-if="it.i === 'emailInsights' && isEmailFolderUser" type="button" class="dash-collapse-handle dash-email-inbox" title="Inbox (un-filed emails)" @click.stop="$root.$emit('report-email-view', 'inbox')">📥</button><button v-if="it.i === 'emailInsights' && isMaster" type="button" class="dash-collapse-handle dash-email-settings" title="Auto-scan settings" @click.stop="$root.$emit('report-email-view', 'settings')">⚙</button><button v-if="it.i === 'emailInsights' && isEmailFolderUser" type="button" class="dash-collapse-handle dash-email-folders" title="Saved emails (folders)" @click.stop="$root.$emit('report-email-view', 'saved')">📁</button><button v-if="it.i === 'emailInsights' && isEmailFolderUser" type="button" class="dash-collapse-handle dash-email-done" title="Completed emails" @click.stop="$root.$emit('report-email-view', 'completed')">✅</button><button v-if="it.i === 'emailInsights' && isEmailFolderUser" type="button" class="dash-collapse-handle dash-email-trash" title="Deleted emails (trash)" @click.stop="$root.$emit('report-email-view', 'deleted')">🗑</button><button v-if="it.i === 'emailInsights'" type="button" class="dash-collapse-handle" :title="emailDocked ? 'Restore Email Insights' : 'Collapse Email Insights to the rail'" @click.stop="emailDocked ? restoreEmailInsights() : collapseEmailInsights()">{{ emailDocked ? '◀' : '▶' }}</button><button v-if="masterEdit && it.i !== 'emailInsights' && it.i !== 'ordersInProcess'" type="button" class="dash-cc-edit" title="Duplicate this card" @click.stop="duplicateCard(it.i)">⧉</button><button v-if="isAiCard(it.i) && masterEdit" type="button" class="dash-cc-edit" title="Edit this card — converts it to a fully editable custom card" @click="editBuiltinCard(it.i)">✎</button><button v-if="isAiCard(it.i) && !isLayoutLocked" type="button" class="dash-ai-remove" title="Remove this card" @click="removeAiCard(it.i)">×</button><button v-if="isCustomCard(it.i) && masterEdit && cardOrigins[it.i]" type="button" class="dash-cc-edit" title="Restore the original built-in card (removes this edited copy)" @click.stop="restoreOriginalForCustom(it.i)">↺</button><button v-if="isCustomCard(it.i) && masterEdit" type="button" class="dash-cc-edit" title="Edit this card" @click="editCustomCard(it.i)">✎</button><button v-if="isCustomCard(it.i) && masterEdit" type="button" class="dash-ai-remove" title="Delete this card" @click="deleteCustomCard(it.i)">×</button><button v-if="masterEdit && isBuiltinCard(it.i)" type="button" class="dash-cc-edit" title="Restore this card to the original (undo edits, reset size &amp; styling)" @click.stop="restoreBuiltinCard(it.i)">↺</button><button v-if="masterEdit && isBuiltinCard(it.i) && it.i !== 'emailInsights' && it.i !== 'ordersInProcess'" type="button" class="dash-cc-edit" title="Edit this card — converts it to a fully editable custom card" @click="editBuiltinCard(it.i)">✎</button><button v-if="masterEdit && isBuiltinCard(it.i)" type="button" class="dash-ai-remove" title="Hide this card (Reset Layout or Restore hidden brings it back)" @click="hideCard(it.i)">×</button></div>
            <div v-if="titleEditGid === it.i" class="dash-titlebar" @mousedown.stop>
              <span class="dash-tb-lbl">Title</span>
              <button type="button" class="dash-tb-btn" :class="{ active: (titleStyles[it.i] || {}).bold }" title="Bold" @click="toggleTitleBold(it.i)"><b>B</b></button>
              <input type="color" class="dash-tb-color" :value="(titleStyles[it.i] || {}).color || '#6a6a72'" title="Title color" @input="setTitleStyle(it.i, 'color', $event.target.value)" />
              <select class="dash-tb-size" :value="(titleStyles[it.i] || {}).size || ''" title="Title size" @change="setTitleStyle(it.i, 'size', $event.target.value)">
                <option value="">Size</option><option value="11">S</option><option value="13">M</option><option value="16">L</option><option value="20">XL</option>
              </select>
              <template v-if="it.i === 'kpiSales' || it.i === 'kpiAvg'">
                <span class="dash-tb-lbl">Decimals</span>
                <select class="dash-tb-size" :value="kpiDec(it.i)" title="Decimal places on this card's dollar value" @change="setTitleStyle(it.i, 'decimals', Number($event.target.value))">
                  <option value="0">0</option><option value="1">1</option><option value="2">2</option>
                </select>
              </template>
              <span class="dash-tb-sp"></span>
              <button type="button" class="dash-tb-txt" title="Clear title styling" @click="clearTitleStyle(it.i)">Reset</button>
              <button type="button" class="dash-tb-txt" @click="titleEditGid = null">Done</button>
            </div>
            <div class="dash-body" @mouseenter="onHeavyEnter(it.i)" @mouseleave="onHeavyLeave(it.i)" @wheel="onHeavyWheel($event, it.i)">
              <!-- Total Sales KPI -->
              <template v-if="it.i === 'kpiSales'">
                <div class="ci-kpi ci-kpi--v2">
                  <div class="ci-kpi-main">
                    <div class="ci-kpi-label">Total Sales</div>
                    <div class="ci-kpi-value">{{ fmtMoney(kpis.totalSales, kpiDec('kpiSales')) }}</div>
                    <div class="ci-kpi-foot"><span v-if="filters.invoiced !== 'not'" class="ci-kpi-seg" :class="{ 'is-active': filters.invoiced === 'invoiced' }" @click="toggleInvoiced('invoiced')" title="Show invoiced only (click again to clear)">Invoiced {{ fmtMoney(kpis.invoicedSales, kpiDec('kpiSales')) }}</span><span v-if="filters.invoiced === 'all'" class="ci-kpi-sep"> · </span><span v-if="filters.invoiced !== 'invoiced'" class="ci-kpi-seg" :class="{ 'is-active': filters.invoiced === 'not' }" @click="toggleInvoiced('not')" title="Show not-invoiced only (click again to clear)">Not {{ fmtMoney(kpis.notInvoicedSales, kpiDec('kpiSales')) }}</span></div>
                    <div v-if="dSales" class="ci-kpi-delta" :class="dSales.up ? 'is-up' : 'is-down'">{{ dSales.txt }} <span class="ci-kpi-delta-lbl">vs prior period</span><button type="button" class="ci-kpi-help" @click.stop="toggleKpiMath('sales', $event)" :aria-expanded="kpiMathOpen && kpiMathKind === 'sales' ? 'true' : 'false'" title="How was this calculated?">?</button></div>
                  </div>
                </div>
              </template>
              <!-- Orders KPI -->
              <template v-else-if="it.i === 'kpiOrders'">
                <div class="ci-kpi ci-kpi--v2">
                  <div class="ci-kpi-main">
                    <div class="ci-kpi-label">Orders</div>
                    <div class="ci-kpi-value">{{ fmtNum(kpis.orderCount) }}</div>
                    <div class="ci-kpi-foot"><span v-if="filters.invoiced !== 'not'" class="ci-kpi-seg" :class="{ 'is-active': filters.invoiced === 'invoiced' }" @click="toggleInvoiced('invoiced')" title="Show invoiced only (click again to clear)">Invoiced {{ fmtNum(kpis.invoicedOrderCount) }}</span><span v-if="filters.invoiced === 'all'" class="ci-kpi-sep"> · </span><span v-if="filters.invoiced !== 'invoiced'" class="ci-kpi-seg" :class="{ 'is-active': filters.invoiced === 'not' }" @click="toggleInvoiced('not')" title="Show not-invoiced only (click again to clear)">Not {{ fmtNum(kpis.notInvoicedOrderCount) }}</span></div>
                    <div v-if="dOrders" class="ci-kpi-delta" :class="dOrders.up ? 'is-up' : 'is-down'">{{ dOrders.txt }} <span class="ci-kpi-delta-lbl">vs prior period</span><button type="button" class="ci-kpi-help" @click.stop="toggleKpiMath('orders', $event)" :aria-expanded="kpiMathOpen && kpiMathKind === 'orders' ? 'true' : 'false'" title="How was this calculated?">?</button></div>
                  </div>
                </div>
              </template>
              <!-- Avg / Order KPI -->
              <template v-else-if="it.i === 'kpiAvg'">
                <div class="ci-kpi ci-kpi--v2">
                  <div class="ci-kpi-main">
                    <div class="ci-kpi-label">Avg / Order</div>
                    <div class="ci-kpi-value">{{ fmtMoney(kpis.orderCount ? kpis.totalSales / kpis.orderCount : 0, kpiDec('kpiAvg')) }}</div>
                    <div class="ci-kpi-foot">total sales ÷ orders</div>
                    <div v-if="dAvg" class="ci-kpi-delta" :class="dAvg.up ? 'is-up' : 'is-down'">{{ dAvg.txt }} <span class="ci-kpi-delta-lbl">vs prior period</span><button type="button" class="ci-kpi-help" @click.stop="toggleKpiMath('avg', $event)" :aria-expanded="kpiMathOpen && kpiMathKind === 'avg' ? 'true' : 'false'" title="How was this calculated?">?</button></div>
                  </div>
                </div>
              </template>
              <!-- Saved Carts KPI — open (un-submitted) saved Orders & Quotes for the selected customer(s). -->
              <template v-else-if="it.i === 'savedCarts'">
                <div class="ci-kpi ci-kpi--v2">
                  <div class="ci-kpi-main">
                    <div class="ci-savedcart-figs">
                      <div class="ci-savedcart-fig ci-sc-click" title="Click for the customer breakdown" @click="openSavedCarts('order')">
                        <div class="ci-kpi-label">Saved Orders</div>
                        <div class="ci-kpi-value">{{ fmtNum(savedCarts.savedOrders) }}</div>
                      </div>
                      <div class="ci-savedcart-fig ci-sc-click" title="Click for the customer breakdown" @click="openSavedCarts('quote')">
                        <div class="ci-kpi-label">Saved Quotes</div>
                        <div class="ci-kpi-value">{{ fmtNum(savedCarts.savedQuotes) }}</div>
                      </div>
                    </div>
                    <div class="ci-kpi-foot">click a count for the breakdown</div>
                  </div>
                </div>
              </template>
              <!-- Orders In Process — recently-placed submitted orders not yet complete (Customer ID · Total ·
                   Status · 👁). The eyeball opens the full order detail in a modal. Scoped like the report. -->
              <template v-else-if="it.i === 'ordersInProcess'">
                <div class="ci-oip">
                  <div v-if="oip.busy" class="ci-oip-empty">Loading…</div>
                  <div v-else-if="!oip.orders.length" class="ci-oip-empty">✓ No orders currently in process.</div>
                  <ul v-else class="ci-oip-list">
                    <!-- Header row. Sticky, because the list scrolls inside the card and a header that
                         scrolls away is worse than none. -->
                    <li class="ci-oip-row ci-oip-head" aria-hidden="true">
                      <span>Customer</span><span>Company</span><span>Total</span><span>As of</span><span>Status</span><span></span>
                    </li>
                    <li v-for="o in oip.orders" :key="o.orderId" class="ci-oip-row" @dblclick="openOrderDetail(o.orderId)">
                      <span class="ci-oip-code">{{ o.customerId }}</span>
                      <!-- The "· 6d ago" used to ride in here; it moved to its own As-of column. -->
                      <span class="ci-oip-name">{{ o.customerName }}</span>
                      <span class="ci-oip-total">{{ fmtMoney(o.total) }}</span>
                      <span class="ci-oip-asof" :title="o.dateCreated ? fmtAgo(o.dateCreated) : ''">{{ o.dateCreated ? String(o.dateCreated).slice(0, 10) : "—" }}</span>
                      <span class="ci-oip-status" :class="oipStatusClass(o.status)">{{ o.status }}</span>
                      <button type="button" class="ci-oip-eye" title="View order details" aria-label="View order details" @click.stop="openOrderDetail(o.orderId)">👁</button>
                    </li>
                  </ul>
                </div>
              </template>
              <!-- Ask about your data — the Q&A field now lives in the HERO at the top of the report (.ci-ask-hero,
                   all report roles). This card key stays valid for saved layouts and simply points there. -->
              <template v-else-if="it.i === 'ask'">
                <div class="ci-ask ci-ask--mob">
                  <div class="ci-ask-mob-h"><span>💬 Ask about your data</span></div>
                  <p class="ci-ask-moved">The Ask box moved to the top of the report — scroll up and tell it what you want to see.</p>
                </div>
              </template>
              <!-- Email Insights — opt-in read of the rep's Microsoft 365 inbox + AI part match → new quote. -->
              <template v-else-if="it.i === 'emailInsights'">
                <!-- MOBILE dock note: when docked, this card (and Orders In Process) is filtered OUT of
                     the mobile stack entirely (effectiveLayout); the red EMAIL edge tab restores both. -->
                <email-insights :linked="emailLinked" :is-master="isEmailTeamView" :can-auto-scan="isMaster" :own-grouped="isSalesOwnEmail" @set-linked="onEmailSetLinked" />
              </template>
              <!-- Territory map — full US colored by the admin-managed sales territories. -->
              <template v-else-if="it.i === 'map'">
                <div class="ci-mappanel">
                  <div class="ci-maptoggle">
                    <button type="button" :class="{ 'is-on': mapMode === 'us' }" @click="mapMode = 'us'" title="US states">US</button>
                    <button type="button" :class="{ 'is-on': mapMode === 'world' }" @click="mapMode = 'world'" title="World — international accounts by country">World</button>
                  </div>
                  <div v-if="mapMode === 'world'" class="ci-mapbody">
                    <world-map :by-country="byCountry" :color="terrColor('International')" :selected="mapSelectedCountry" :zoom="worldZoom" @select-country="onSelectCountry" @show-us="showUsFromWorld" @zoom-change="onWorldZoom" @hover-country="mapHoverCountry = $event" />
                    <div class="ci-mapflag" v-if="worldFlagCountry && worldFlagCountry.name && worldFlagCountry.present">
                      <img v-if="countryFlagIso(worldFlagCountry.name)" class="ci-mapflag-img" :src="'https://flagcdn.com/w160/' + countryFlagIso(worldFlagCountry.name) + '.png'" :alt="worldFlagCountry.name + ' flag'" loading="lazy" @error="$event.target.style.visibility = 'hidden'" />
                      <div class="ci-mapflag-info">
                        <div class="ci-mapflag-name">{{ worldFlagCountry.name }}<span v-if="!mapHoverCountry && !(filters.country && filters.country.length)" class="ci-mapflag-hintnote"> · hover a country</span></div>
                        <div class="ci-mapflag-sub" v-if="worldFlagCountry.present">{{ fmtMoney(worldFlagCountry.sales) }} · {{ worldFlagCountry.customers }} customer{{ worldFlagCountry.customers === 1 ? '' : 's' }} · {{ worldFlagCountry.orders }} order{{ worldFlagCountry.orders === 1 ? '' : 's' }}</div>
                        <div class="ci-mapflag-sub ci-mapflag-sub--none" v-else>No accounts here</div>
                      </div>
                    </div>
                    <div v-else class="ci-mapflag ci-mapflag-hint">Hover a country to see total sales, how many customers, and how many orders</div>
                  </div>
                  <div v-else-if="mapStates.length" class="ci-mapbody">
                    <territory-map :by-state="mapStates" :color-for="terrColor" :legend-items="mapLegend" :selected-state="mapSelectedState" :selected-territory="mapSelectedTerritory" :zoom="mapZoom" @select-state="onSelectState" @zoom-change="onMapZoom" @hover-state="mapHoverState = $event" />
                    <div class="ci-mapflag">
                      <template v-if="mapFlagState">
                        <img class="ci-mapflag-img" :src="stateFlagUrl(mapFlagState.postal)" :alt="mapFlagState.name + ' flag'" loading="lazy" />
                        <div class="ci-mapflag-info">
                          <div class="ci-mapflag-name">{{ mapFlagState.name }}</div>
                          <div class="ci-mapflag-sub" v-if="mapFlagState.hasData">{{ fmtMoney(mapFlagState.sales) }} · {{ mapFlagState.customers }} customer{{ mapFlagState.customers === 1 ? '' : 's' }} · {{ mapFlagState.orders }} order{{ mapFlagState.orders === 1 ? '' : 's' }}</div>
                          <div class="ci-mapflag-sub ci-mapflag-sub--none" v-else>No sales in this range</div>
                        </div>
                      </template>
                      <div v-else class="ci-mapflag-hint">Hover a state to see total sales, how many customers, and how many orders</div>
                    </div>
                  </div>
                  <div v-else class="ci-mapempty">No regional data for the current filters.</div>
                </div>
              </template>
              <!-- Customer ID(s) breakdown -->
              <template v-else-if="it.i === 'customers'">
                <div class="ci-chart">
                  <div v-if="filters.customer.length" class="ci-cust-chips ci-chips-inset">
                    <span v-for="id in filters.customer" :key="'cch' + id" class="ci-cust-chip" title="Hover for all active filters · click × to remove" @mouseenter="openChipHoverAt('customer', id, $event)" @mouseleave="closeChipHoverPop()">{{ id }}<button type="button" @click="removeChip('customer', id)" aria-label="Remove filter">×</button></span>
                  </div>
                  <customers-by-id :customers="byCustomer" :selected="filters.customer" :rep-color-map="repTerritoryColors" :customer-color-map="customerColorMap" :segments-map="customerSegments" :territory-color-map="territoryColorMap" :loading="loading" @select="(v) => toggleCustomerFilter(v)" @notation="onCustomerNotation" @specs="onCustomerSpecs" @perspective="onCustomerPerspective" />
                </div>
              </template>
              <!-- Part(s) Ordered breakdown -->
              <template v-else-if="it.i === 'parts'">
                <div class="ci-chart ci-chart--fill">
                  <div v-if="filters.part.length" class="ci-cust-chips ci-chips-inset">
                    <span v-for="v in filters.part" :key="'pch' + v" class="ci-cust-chip" title="Hover for all active filters · click × to remove" @mouseenter="openChipHoverAt('part', v, $event)" @mouseleave="closeChipHoverPop()">{{ v }}<button type="button" @click="removeChip('part', v)" aria-label="Remove filter">×</button></span>
                  </div>
                  <parts-ordered :parts="byPart" :selected="filters.part" :bar-color="activeTerritoryColor" :part-reps="byPartRep" :rep-color-map="repTerritoryColors" :loading="loading" :remote="poSearch.rows" :remote-term="poSearch.term" :remote-busy="poSearch.busy" :remote-capped="poSearch.capped" :remote-failed="poSearch.failed" :remote-description-searched="poSearch.descriptionSearched" @search="poPartSearch" @select="(v) => toggleFilter('part', v)" @select-rep="(r) => toggleRepName(r)" />
                </div>
              </template>
              <!-- Sales by Territory breakdown — reflects the Territory filter: shows ONLY the selected
                   territories when any are chosen (territoryCardRows), else all. -->
              <template v-else-if="it.i === 'territory'">
                <div class="ci-chart" :class="{ 'ci-bars-loading': loading }">
                  <div v-if="filters.territory.length" class="ci-cust-chips ci-chips-inset">
                    <span v-for="t in filters.territory" :key="'tch' + t" class="ci-cust-chip" title="Hover for all active filters · click × to remove" @mouseenter="openChipHoverAt('territory', t, $event)" @mouseleave="closeChipHoverPop()">{{ t }}<button type="button" @click="removeChip('territory', t)" aria-label="Remove filter">×</button></span>
                  </div>
                  <div v-for="t in territoryCardRows" :key="'tt' + t.territory" class="ci-bar-row ci-bar-row--click"
                       :class="{ 'is-sel': filters.territory.indexOf(t.territory) >= 0 }"
                       :title="t.territory === '(Unassigned)' ? 'These orders have no territory because the Customer_Country field is empty in Visual — populate it there to classify them. Click to view these orders.' : ((filters.territory.indexOf(t.territory) >= 0 ? '✓ filtering by ' : 'Click to filter by ') + t.territory)"
                       @click="toggleTerritory(t.territory)">
                    <div class="ci-bar-label"><span class="ci-terr-dot" :style="{ background: terrColor(t.territory) }"></span>{{ t.territory }}<button v-if="territoryInsightInfo(t.territory).show" type="button" class="ci-terr-insbtn" @click.stop="onTerritoryInsight(t, $event)" title="Who rolls up into this territory — and why">💡</button></div>
                    <div class="ci-bar-track"><div class="ci-bar" :style="{ width: barPct(t.sales, maxTerritoryCardSales), background: terrColor(t.territory) }"></div></div>
                    <div class="ci-bar-val">{{ fmtMoney(t.sales) }}<span class="ci-bar-pct">{{ pctOfTotal(t.sales) }}</span></div>
                  </div>
                  <div v-if="!territoryCardRows.length" class="ci-aiempty">
                    <template v-if="crossFacetLimiters.length">
                      <div class="ci-empty-lead">These filter(s) are limiting this card. Click to remove:</div>
                      <div class="ci-empty-chips"><button v-for="c in crossFacetLimiters" :key="'tl' + c.key + (c.val || '')" type="button" class="ci-empty-chip" @click.stop="removeChip(c.key, c.val)">{{ c.label }}: {{ c.text }} ✕</button></div>
                    </template>
                    <template v-else>No territory data for the current filters.</template>
                  </div>
                  <div class="ci-card-addrow" v-if="activeChips.length">
                    <select class="ci-card-add" @change="addFromPicker('territory', $event)" :disabled="!addableTerritoryOptions.length" :title="addableTerritoryOptions.length ? 'Add a territory not already on the card' : 'Every territory is already on the card'">
                      <option value="">＋ Add territory…</option>
                      <option v-for="t in addableTerritoryOptions" :key="'at' + t" :value="t">{{ t }}</option>
                    </select>
                  </div>
                </div>
              </template>
              <!-- Sales Reps leaderboard — ranked by sales; click a rep to filter that rep AND its mapped territory. -->
              <template v-else-if="it.i === 'salesReps'">
                <div class="ci-chart ci-reps" :class="{ 'ci-bars-loading': loading }">
                  <div v-if="filters.salesRep.length" class="ci-cust-chips ci-chips-inset">
                    <span v-for="r in filters.salesRep" :key="'rch' + r" class="ci-cust-chip" :title="r" @mouseenter="openChipHoverAt('salesRep', r, $event)" @mouseleave="closeChipHoverPop()">{{ shortRepName(r) }}<button type="button" @click="removeChip('salesRep', r)" aria-label="Remove filter">×</button></span>
                  </div>
                  <div v-for="r in repCardRows" :key="'rep' + r.salesRep" class="ci-bar-row ci-bar-row--click"
                       :class="{ 'is-sel': filters.salesRep.indexOf(r.salesRep) >= 0 }"
                       :title="r.salesRep === '(Unassigned)' ? 'These orders have no sales rep assigned in VISUAL — click to view them.' : ((filters.salesRep.indexOf(r.salesRep) >= 0 ? '✓ filtering by ' : 'Click to filter by ') + r.salesRep + (r.territory ? ' (' + r.territory + ')' : ''))"
                       @click="toggleRep(r)">
                    <div class="ci-bar-label"><span class="ci-terr-dot" :style="{ background: terrColor(r.territory) }"></span>{{ r.salesRep }}<button v-if="r.salesRep === '(Unassigned)' && canSeeUnassignedDiag" type="button" class="ci-unassigned-diag" @click.stop="onUnassignedDiag(r, $event)" title="Why are these unassigned? See the causes + how to fix.">💡</button><button v-if="repAutoPct(r) >= 1" type="button" class="ci-rep-auto" :title="repAutoTitle(r)">⚙ {{ repAutoPct(r) }}%</button><span v-if="r.territory && r.territory !== '(Unassigned)'" class="ci-rep-terr">{{ r.territory }}</span></div>
                    <div class="ci-bar-track"><div class="ci-bar" :style="{ width: barPct(r.sales, maxRepCardSales), background: terrColor(r.territory) }"></div></div>
                    <div class="ci-bar-val">{{ fmtMoney(r.sales) }}<span class="ci-bar-pct">{{ pctOfTotal(r.sales) }}</span></div>
                  </div>
                  <div v-if="!repCardRows.length" class="ci-aiempty">
                    <template v-if="crossFacetLimiters.length">
                      <div class="ci-empty-lead">These filter(s) are limiting this card. Click to remove:</div>
                      <div class="ci-empty-chips"><button v-for="c in crossFacetLimiters" :key="'rl' + c.key + (c.val || '')" type="button" class="ci-empty-chip" @click.stop="removeChip(c.key, c.val)">{{ c.label }}: {{ c.text }} ✕</button></div>
                    </template>
                    <template v-else>No sales-rep data for the current filters.</template>
                  </div>
                  <div class="ci-card-addrow" v-if="activeChips.length">
                    <select class="ci-card-add" @change="addFromPicker('salesRep', $event)" :disabled="!addableRepOptions.length" :title="addableRepOptions.length ? 'Add a sales rep not already on the card' : 'Every sales rep is already on the card'">
                      <option value="">＋ Add sales rep…</option>
                      <option v-for="r in addableRepOptions" :key="'ar' + r" :value="r">{{ r }}</option>
                    </select>
                  </div>
                </div>
              </template>
              <!-- Costing for Parts — SENSITIVE (Master + All Insights). The v-if is the last of three
                   guards: ensureBuiltinCards keeps the card out of an unauthorized layout, this hides the
                   content if a SAVED layout still carries it after a role change, and [Authorize] on the
                   endpoint is the actual boundary. -->
              <template v-else-if="it.i === 'partCosts'">
                <div v-if="!canSeeCost" class="ci-aiempty">
                  Part costing isn’t available for your role.
                  <div class="ci-pcost-detail">{{ costRoleDebug }}</div>
                </div>
                <div v-else class="ci-pcost" :class="{ 'ci-bars-loading': loading || partCostBusy }">
                  <div v-if="partCostDenied" class="ci-aiempty">
                    Your account isn’t authorized for part costing yet. If you were just added to
                    <strong>All Insights</strong>, sign out and back in — role permissions are held in your
                    sign-in token for up to 24 hours.
                  </div>
                  <div v-else-if="!partCostAvailable" class="ci-aiempty">
                    Part costing isn’t available on the database this server is using.
                    <div v-if="partCostDetail" class="ci-pcost-detail">{{ partCostDetail }}</div>
                  </div>
                  <div v-else-if="partCostQueryError" class="ci-aiempty">
                    The part-cost query failed — most often a timeout on a very wide date range. Narrow the
                    range or add a filter and try again.
                    <div v-if="partCostDetail" class="ci-pcost-detail">{{ partCostDetail }}</div>
                  </div>
                  <template v-else>
                    <!-- Counts only. NO MARGIN and NO REVENUE on this card, by decision: rpt_PartCost
                         holds only CURRENT unit cost, so it cannot be matched to the price a part sold at
                         historically, and any margin derived from it would be invented. -->
                    <div v-if="partCostTotals" class="ci-pcost-sum">
                      <span class="ci-pcost-sum-fig"><em>Parts in view</em> {{ fmtNum(partCostTotals.parts) }}</span>
                      <span class="ci-pcost-sum-fig"><em>With cost on file</em> {{ fmtNum(partCostTotals.partsCosted) }}</span>
                      <span class="ci-pcost-sum-fig"><em>No cost on file</em> {{ fmtNum(partCostTotals.partsNoCost) }}</span>
                      <span v-if="partCostTotals.lastChange" class="ci-pcost-sum-fig"><em>Newest cost change</em> {{ String(partCostTotals.lastChange).slice(0,10) }}</span>
                      <!-- The figures above are SERVER totals for the whole selection and do not move when
                           you type in the search box — but the table, the charts and the Excel default all
                           narrow to the match. Without this the header silently disagrees with everything
                           below it. -->
                      <!-- A COUNT, not an average. An average of per-part margin percentages weights a $1.20
                           cotter pin the same as a $232,000 machine, and a ratio of summed UNIT figures is not
                           money at all. "How many are priced below cost" is unambiguous and actionable. -->
                      <!-- CLICKABLE: filters the card to only the below-cost parts (Melissa's ask #1).
                           Rendered from partCostRows, not partCostView, so the count doesn't collapse to
                           itself while the filter is on. -->
                      <button v-if="pcBelowCostAll || pcBelowOnly" type="button"
                              class="ci-pcost-sum-fig ci-pcost-sum-below ci-pcost-belowbtn" :class="{ on: pcBelowOnly }"
                              @click="pcBelowOnly = !pcBelowOnly"
                              :title="pcBelowOnly ? 'Show all parts again' : 'Show ONLY the parts priced below today\'s cost'">
                        <em>Priced below cost</em> {{ fmtNum(pcBelowCostAll) }}{{ pcBelowOnly ? " ✕" : "" }}
                      </button>
                      <span v-if="partCostTermList.length" class="ci-pcost-sum-fig ci-pcost-sum-match"><em>Matching your search</em> {{ fmtNum(partCostView.length) }}</span>
                      <!-- Chart toggle. One button that CYCLES table → horizontal bars → vertical bars → pie →
                           table, rather than four controls competing for a row that is already crowded. -->
                      <button type="button" class="ci-pcost-fullbtn" @click="pcFull = !pcFull"
                              :title="pcFull ? 'Restore the other cards' : 'Fill the report with this card (the chat bar stays)'">{{ pcFull ? "Exit full view" : "Full view" }}</button>
                      <button type="button" class="ci-pcost-chartbtn" :class="{ on: pcChart }" @click="cyclePartCostChart"
                              :title="pcChartTitle">{{ pcChartIcon }}</button>
                      <button type="button" class="ci-pcost-xls" :disabled="partCostBusy" @click="openPartCostExcel"
                              title="Download these part costs as an Excel workbook">⬇ Excel</button>
                      <!-- The row list is capped but the counts above are not. Without this label the header
                           and the visible list silently disagree. -->
                      <span v-if="partCostTotals.truncated" class="ci-pcost-cover"
                            :title="'Showing the highest-cost ' + partCostTotals.shown + ' parts. The search box filters THIS page only — narrow the dashboard filter to reach the rest.'">
                        showing top {{ fmtNum(partCostTotals.shown) }} of {{ fmtNum(partCostTotals.parts) }}
                      </span>
                    </div>
                    <!-- Search + Generate Insights share one row: the search box gives up the width it was
                         wasting so the AI button sits beside it instead of costing the table another line. -->
                    <div class="ci-pcost-toolrow">
                      <div class="ci-codes-searchwrap ci-pcost-searchwrap">
                        <input class="ci-codes-search" type="text" v-model="partCostSearch"
                               @input="pcSuggest" @keydown="pcSugKey" @focus="pcSugOpen = pcSug.length > 0"
                               @blur="pcSugBlur"
                               :placeholder="partCostTerms.length ? 'Add another part or description…' : 'Search part, description, or who changed a cost…'" />
                        <!-- Suggestions come from the SERVER across every part in the selection — the card
                             only holds the highest-cost 500, so a local list would hide the other 1,500. -->
                        <ul v-if="pcSugOpen && pcSug.length" class="ci-pcost-sug">
                          <li v-for="(x, i) in pcSug" :key="'sg' + i" :class="{ on: i === pcSugIdx }"
                              @mousedown.prevent="pcPickSuggest(i)" @mouseenter="pcSugIdx = i">
                            <span class="ci-pcost-sug-v">{{ x.value }}</span>
                            <span v-if="x.label" class="ci-pcost-sug-l">{{ x.label }}</span>
                            <span v-if="x.kind === 'user'" class="ci-pcost-sug-k">changed a cost</span>
                          </li>
                        </ul>
                        <button v-if="partCostSearch" type="button" class="ci-codes-clear" @click="partCostSearch = ''" title="Clear">×</button>
                      </div>
                      <!-- ＋ keeps the typed term and empties the box for the next one. Enter does the same.
                           Terms are OR'd — see partCostView. -->
                      <button type="button" class="ci-pcost-add" :disabled="!partCostSearch.trim()"
                              @click="addPartCostTerm"
                              title="Add this as another search term — results show parts matching ANY term">＋</button>
                      <button type="button" class="ci-pcost-gen" :disabled="pcAiBusy || !partCostRows.length"
                              @click="generatePartCostInsights"
                              title="Ask OpenAI to read these cost figures and summarize what stands out">
                        {{ pcAiBusy ? "⏳ Generating…" : "✨ Generate Insights" }}
                      </button>
                      <button type="button" class="ci-pcost-pres" :disabled="pcPrintBusy || pcAiBusy || !partCostRows.length"
                              @click="partCostPresentation"
                              title="Open a print-ready presentation: the card, all three charts, the insights, and the filtered data">
                        {{ pcPrintBusy ? "⏳ Preparing…" : "📊 Presentation" }}
                      </button>
                    </div>
                    <!-- Committed terms. Results match ANY of these (plus whatever is still in the box). -->
                    <div v-if="partCostTerms.length" class="ci-pcost-terms">
                      <span class="ci-pcost-terms-lbl">Matching any of</span>
                      <span v-for="(t, i) in partCostTerms" :key="'pct' + i" class="ci-pcost-term">{{ t }}<button
                            type="button" @click="removePartCostTerm(i)" :aria-label="'Remove ' + t">×</button></span>
                      <button type="button" class="ci-pcost-terms-clr" @click="clearPartCostSearch">clear all</button>
                    </div>
                    <!-- The AI briefing, inline above the data it describes. Dismissible: it is an aid, and it
                         should never be the thing occupying the card when someone wants the numbers. -->
                    <div v-if="pcAiBusy || pcAi || pcAiError" class="ci-pcost-ai">
                      <div class="ci-pcost-ai-head">
                        <span class="ci-pcost-ai-title">{{ pcAiTitle || "Cost insights" }}</span>
                        <button type="button" class="ci-pcost-ai-x" @click="pcAi = ''; pcAiError = ''; pcAiTitle = ''" title="Dismiss">×</button>
                      </div>
                      <div v-if="pcAiBusy" class="ci-pcost-ai-body">Reading the cost figures…</div>
                      <div v-else-if="pcAiError" class="ci-pcost-ai-body ci-pcost-ai-err">{{ pcAiError }}</div>
                      <div v-else class="ci-pcost-ai-body">
                        <div v-for="(ln, i) in pcAiLines" :key="'pcai' + i" class="ci-pcost-ai-ln">{{ ln }}</div>
                        <div v-if="pcAiBasis" class="ci-pcost-ai-basis">{{ pcAiBasis }}</div>
                      </div>
                    </div>
                    <div v-if="!pcChart" class="ci-pcost-scroll">
                      <table class="ci-pcost-tbl">
                        <thead>
                          <tr>
                            <th class="ci-pcost-th-x" title="Expand for description and cost history"></th>
                            <!-- v-for over pcCols so a header and its sort key can never disagree. Column
                                 ORDER is unchanged, which matters: the widths are nth-child rules. -->
                            <th v-for="c in pcCols" :key="c.k" :class="[c.num ? 'num' : '', 'ci-pcost-th', pcSort.col === c.k ? 'is-sorted' : '']"
                                @click="pcSortBy(c.k)" :title="'Sort by ' + c.t">{{ c.t }}<span class="ci-pcost-sortarrow">{{ pcSort.col === c.k ? (pcSort.dir > 0 ? "▲" : "▼") : "" }}</span></th>
                          </tr>
                        </thead>
                        <tbody>
                          <template v-for="r in partCostView">
                          <tr :key="'pc' + r.partNumber" class="ci-pcost-row" :class="{ open: !!pcOpen[r.partNumber] }"
                              @click="pcToggleRow(r.partNumber)"
                              @mouseenter="pcRowHover(r, $event)" @mousemove="pcRowHover(r, $event)" @mouseleave="pcRowLeave">
                            <td class="ci-pcost-x"><span class="ci-pcost-caret">{{ pcOpen[r.partNumber] ? "▾" : "▸" }}</span></td>
                            <td class="ci-pcost-pn">{{ r.partNumber }}</td>
                            <!-- A MISSING component renders blank, never $0.00 — absent and free are different. -->
                            <td class="num">{{ r.unitMaterialCost == null ? '—' : fmtMoney(r.unitMaterialCost) }}</td>
                            <td class="num">{{ r.unitLaborCost == null ? '—' : fmtMoney(r.unitLaborCost) }}</td>
                            <td class="num">{{ r.unitBurdenCost == null ? '—' : fmtMoney(r.unitBurdenCost) }}</td>
                            <td class="num">{{ r.unitServiceCost == null ? '—' : fmtMoney(r.unitServiceCost) }}</td>
                            <td class="num ci-pcost-total">
                              <span v-if="r.unitCostTotal > 0">{{ fmtMoney(r.unitCostTotal) }}</span>
                              <span v-else class="ci-pcost-nocost" title="No cost on file in VMFG — the four components are absent or all zero">no cost on file</span>
                            </td>
                            <!-- A price of 0 is NO PRICE, not free: the highest-COST part in the data carries
                                 price 0. Same absent-vs-zero rule as cost. -->
                            <td class="num ci-pcost-price">
                              <span v-if="r.unitPrice > 0">{{ fmtMoney(r.unitPrice) }}</span>
                              <span v-else class="ci-pcost-nocost" title="No selling price on file in VMFG">no price on file</span>
                            </td>
                            <!-- CURRENT margin. Blank unless BOTH cost and price are on file — a missing side
                                 makes the figure meaningless, not zero. Red when the part is priced below
                                 today's cost, which is the whole point of showing it. -->
                            <td class="num ci-pcost-marg" :class="{ neg: pcMargin(r) && pcMargin(r).amt < 0 }">
                              <template v-if="pcMargin(r)">{{ fmtMoney(pcMargin(r).amt) }}<em>{{ pcMargin(r).pct.toFixed(1) }}%</em></template>
                              <span v-else class="ci-pcost-nocost" :title="r.unitPrice > 0 ? 'No cost on file' : 'No price on file'">&mdash;</span>
                            </td>
                            <td class="num">{{ fmtNum(r.qty) }}</td>
                          </tr>
                          <!-- The expanded panel: what the two removed columns used to show. -->
                          <tr v-if="pcOpen[r.partNumber]" :key="'pe' + r.partNumber" class="ci-pcost-exp">
                            <td :colspan="pcCols.length + 1">
                              <div class="ci-pcost-exp-row"><em>Description</em><span>{{ r.partDescription || "—" }}</span></div>
                              <div class="ci-pcost-exp-row"><em>Last cost change</em><span>
                                <template v-if="partCostLastChange(r)">{{ String(partCostLastChange(r).date).slice(0, 10) }}
                                  &middot; {{ partCostLastChange(r).area }}{{ partCostLastChange(r).user ? " · " + partCostLastChange(r).user : "" }}
                                  <template v-if="partCostLastChange(r).oldV != null"> &middot; {{ fmtMoney(partCostLastChange(r).oldV) }} &rarr; {{ fmtMoney(partCostLastChange(r).newV) }}</template>
                                </template>
                                <template v-else>&mdash;</template>
                              </span></div>
                              <div class="ci-pcost-exp-row"><em>Last inventory trans</em><span>{{ r.lastInventoryTransDate ? String(r.lastInventoryTransDate).slice(0, 10) : "none on record" }}</span></div>
                            </td>
                          </tr>
                          </template>
                        </tbody>
                      </table>
                      <div v-if="partCostBusy && !partCostView.length" class="ci-aiempty">Loading Costing for Parts…</div>
                      <div v-else-if="!partCostView.length" class="ci-aiempty">No parts match your search.</div>
                    </div>
                    <!-- Hover preview of the expanded panel. position:fixed at the cursor, because a
                         popover inside .ci-pcost-scroll would be clipped by its own overflow. Suppressed
                         when the row is already expanded — the same content twice is just noise. -->
                    <div v-if="pcHover" class="ci-pcost-peek" :style="{ left: pcHover.x + 'px', top: pcHover.y + 'px' }">
                      <div class="ci-pcost-peek-pn">{{ pcHover.pn }}</div>
                      <div class="ci-pcost-peek-d">{{ pcHover.desc || "No description on file" }}</div>
                      <div class="ci-pcost-peek-l">{{ pcHover.lc || "No cost changes recorded" }}</div>
                      <div v-if="pcHover.inv" class="ci-pcost-peek-l">Last inventory trans: {{ pcHover.inv }}</div>
                      <div class="ci-pcost-peek-h">click to keep this open</div>
                    </div>
                    <!-- CHART MODES. Hand-rolled SVG: this project carries no charting library (only d3-geo,
                         for the territory map), and three small static charts are not worth adding one.
                         All geometry is precomputed in JS because a Vue 2 template has no `Math` in scope.
                         The bars chart the parts currently VISIBLE (search filter included) so the picture
                         always matches the table it replaces. -->
                    <div v-else class="ci-pcost-chart">
                      <div class="ci-pcost-chart-cap">{{ pcChartCaption }}</div>
                      <div v-if="pcChartEmpty" class="ci-aiempty">{{ pcChartEmptyText }}</div>
                      <!-- The chart IS the generated SVG (see _pcChartSvg), the same function the print
                           doc and the Excel PNGs use. The template used to hand-write its own copy of
                           this markup, which meant two versions free to drift. v-html is safe here: the
                           only external values are part numbers and descriptions, and the generator
                           escapes them. No inline <style> in live mode, so the external .ci-pcc-* rules
                           and their dark-mode variants still apply. -->
                      <div v-else class="ci-pcc-host" v-html="pcChartSvgLive"></div>
                    </div>
                  </template>
                </div>
              </template>
              <!-- Parts Sold at a Loss (Accounting report) — one row per customer shipment line whose net price
                   was at-or-below the ACTUAL cost booked on the shipment date (dbo.rpt_PartLoss). The category
                   split IS the report: Below cost = the real losses; No charge (82% of qualifying rows —
                   warranty/service/samples) and Credits are counted but kept out of the loss total. -->
              <template v-else-if="it.i === 'partLoss'">
                <div v-if="!canSeeLoss" class="ci-aiempty">
                  Parts Sold at a Loss isn&rsquo;t available for your role.
                  <div class="ci-pcost-detail">{{ costRoleDebug }}</div>
                </div>
                <div v-else class="ci-ploss" :class="{ 'ci-bars-loading': plBusy }">
                  <div v-if="!plAvailable" class="ci-aiempty">
                    Loss reporting isn&rsquo;t available here yet.
                    <div v-if="plDetail" class="ci-pcost-detail">{{ plDetail }}</div>
                  </div>
                  <template v-else>
                    <!-- Summary strip + toolrow — the cost card's grammar, reusing its .ci-pcost-* chrome
                         so the two cards read as siblings in every theme. In-view figures move with the
                         search; the "showing top N of M" pill states the window they were cut from. -->
                    <div class="ci-pcost-sum">
                      <span class="ci-pcost-sum-fig"><em>Lines in view</em> {{ fmtNum(plView.length) }}</span>
                      <span class="ci-pcost-sum-fig"><em>Parts</em> {{ fmtNum(plPartsInView) }}</span>
                      <span class="ci-pcost-sum-fig"><em>Customers</em> {{ fmtNum(plCustomersInView) }}</span>
                      <span v-if="plNewestShip" class="ci-pcost-sum-fig"><em>Newest shipment</em> {{ plNewestShip }}</span>
                      <span v-if="plCat === 'BelowCost'" class="ci-pcost-sum-fig ci-ploss-sum-loss"><em>Loss in view</em> {{ plMoney(plLossInView) }}</span>
                      <span v-if="plSearchLabel" class="ci-pcost-sum-fig ci-pcost-sum-match"><em>Matching your search</em> {{ fmtNum(plView.length) }}</span>
                      <button type="button" class="ci-pcost-fullbtn" @click="togglePlFull"
                              :title="plFull ? 'Restore the other cards' : 'Fill the report with this card (the chat bar stays)'">{{ plFull ? "Exit full view" : "Full view" }}</button>
                      <button type="button" class="ci-pcost-chartbtn" :class="{ on: plChart }" @click="cyclePlChart"
                              :title="plChartTitle">{{ plChartIcon }}</button>
                      <button type="button" class="ci-pcost-xls" :disabled="plBusy" @click="openPartLossExcel"
                              title="Download these loss lines as an Excel workbook">⬇ Excel</button>
                      <span v-if="plTruncated" class="ci-pcost-cover"
                            :title="'Showing the ' + fmtNum(plRows.length) + ' most recent lines for this tab. Narrow the date range (Filters) or add a search chip to reach the rest.'">
                        showing top {{ fmtNum(plRows.length) }} of {{ fmtNum(plWindowRows) }}
                      </span>
                    </div>
                    <div class="ci-pcost-toolrow">
                      <div class="ci-codes-searchwrap ci-pcost-searchwrap">
                        <input class="ci-codes-search" type="text" v-model="plSearch"
                               @input="plSuggest" @keydown="plSugKeydown" @focus="plSugOpen = plSug.length > 0"
                               @blur="plSugBlur"
                               :placeholder="plTerms.length ? 'Add another part, customer or description…' : 'Search part, description, customer, or order…'" />
                        <!-- Suggestions come from the SERVER across the whole window — the card only holds
                             the newest 500 lines, so a local list would hide everything older. -->
                        <ul v-if="plSugOpen && plSug.length" class="ci-pcost-sug">
                          <li v-for="(x, i) in plSug" :key="'plsg' + i" :class="{ on: i === plSugIdx }"
                              @mousedown.prevent="plPickSuggest(i)" @mouseenter="plSugIdx = i">
                            <span class="ci-pcost-sug-v">{{ x.value }}</span>
                            <span v-if="x.label" class="ci-pcost-sug-l">{{ x.label }}</span>
                            <span v-if="x.kind === 'customer'" class="ci-pcost-sug-k">customer</span>
                          </li>
                        </ul>
                        <button v-if="plSearch" type="button" class="ci-codes-clear" @click="plSearch = ''" title="Clear">×</button>
                      </div>
                      <button type="button" class="ci-pcost-add" :disabled="!plSearch.trim()"
                              @click="addPlTerm"
                              title="Add this as another search term — results show lines matching ANY term">＋</button>
                      <button type="button" class="ci-pcost-gen" :disabled="plAiBusy || !plRows.length"
                              @click="generatePartLossInsights"
                              title="Ask OpenAI to read these loss figures and summarize what stands out">
                        {{ plAiBusy ? "⏳ Generating…" : "✨ Generate Insights" }}
                      </button>
                      <button type="button" class="ci-pcost-pres" :disabled="plPrintBusy || plAiBusy || !plRows.length"
                              @click="partLossPresentation"
                              title="Open a print-ready presentation: the card, all three charts, the insights, and the filtered data">
                        {{ plPrintBusy ? "⏳ Preparing…" : "📊 Presentation" }}
                      </button>
                    </div>
                    <!-- Committed terms. Results match ANY of these (plus whatever is still in the box). -->
                    <div v-if="plTerms.length" class="ci-pcost-terms">
                      <span class="ci-pcost-terms-lbl">Matching any of</span>
                      <span v-for="(t, i) in plTerms" :key="'plt' + i" class="ci-pcost-term">{{ t }}<button
                            type="button" @click="removePlTerm(i)" :aria-label="'Remove ' + t">×</button></span>
                      <button type="button" class="ci-pcost-terms-clr" @click="clearPlSearch">clear all</button>
                    </div>
                    <!-- The AI briefing, inline above the data it describes. Dismissible. -->
                    <div v-if="plAiBusy || plAi || plAiError" class="ci-pcost-ai">
                      <div class="ci-pcost-ai-head">
                        <span class="ci-pcost-ai-title">{{ plAiTitle || "Loss insights" }}</span>
                        <button type="button" class="ci-pcost-ai-x" @click="plAi = ''; plAiError = ''; plAiTitle = ''" title="Dismiss">×</button>
                      </div>
                      <div v-if="plAiBusy" class="ci-pcost-ai-body">Reading the loss figures…</div>
                      <div v-else-if="plAiError" class="ci-pcost-ai-body ci-pcost-ai-err">{{ plAiError }}</div>
                      <div v-else class="ci-pcost-ai-body">
                        <div v-for="(ln, i) in plAiLines" :key="'plai' + i" class="ci-pcost-ai-ln">{{ ln }}</div>
                        <div v-if="plAiBasis" class="ci-pcost-ai-basis">{{ plAiBasis }}</div>
                      </div>
                    </div>
                    <div class="ci-ploss-tabs">
                      <button v-for="c in plTabs" :key="c.key" type="button" class="ci-ploss-tab" :class="{ on: plCat === c.key }" @click="setPlCat(c.key)">
                        {{ c.label }} <em>{{ fmtNum(c.rows) }}</em><em v-if="c.key === 'BelowCost' && c.totalLoss != null" class="ci-ploss-tab-loss">{{ plMoney(c.totalLoss) }}</em>
                      </button>
                    </div>
                    <div class="ci-ploss-note">{{ plNote }}</div>
                    <div v-if="plQueryError" class="ci-pcost-cover">The loss query failed — <span>{{ plDetail }}</span></div>
                    <div v-if="!plChart" class="ci-ploss-scroll">
                      <table class="ci-pcost-tbl ci-ploss-tbl">
                        <thead>
                          <tr>
                            <th @click="setPlSort('transactionDate')" title="Sort by shipment date">Date{{ plSortMark("transactionDate") }}</th>
                            <th @click="setPlSort('partNumber')" title="Sort by part">Part{{ plSortMark("partNumber") }}</th>
                            <th @click="setPlSort('partDescription')" title="Sort by description">Description{{ plSortMark("partDescription") }}</th>
                            <th @click="setPlSort('customerId')" title="Sort by customer">Customer{{ plSortMark("customerId") }}</th>
                            <th @click="setPlSort('custOrderId')" title="Sort by order">Order{{ plSortMark("custOrderId") }}</th>
                            <th class="num" @click="setPlSort('qty')" title="Sort by quantity">Qty{{ plSortMark("qty") }}</th>
                            <th class="num" @click="setPlSort('netUnitPrice')" title="Unit price net of trade discount (negative on credits)">Net unit price{{ plSortMark("netUnitPrice") }}</th>
                            <th class="num" @click="setPlSort('actUnitCost')" title="The ACTUAL cost booked on the shipment transaction on its date">Actual unit cost{{ plSortMark("actUnitCost") }}</th>
                            <th class="num" @click="setPlSort('lineLoss')" title="(net price − actual cost) × qty — first click puts the biggest loss on top">Loss{{ plSortMark("lineLoss") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(r, ri) in plView" :key="r.transactionId || (r.custOrderId + '-' + r.orderLineNo + '-' + ri)">
                            <td>{{ fmtDate(r.transactionDate) }}</td>
                            <td class="ci-ploss-part">{{ r.partNumber }}</td>
                            <td class="ci-ploss-desc" :title="r.partDescription">{{ r.partDescription }}</td>
                            <td>{{ r.customerId }}</td>
                            <td>{{ r.custOrderId }}<span v-if="r.orderLineNo" class="ci-ploss-line"> · {{ r.orderLineNo }}</span></td>
                            <td class="num">{{ fmtNum(r.qty) }}</td>
                            <td class="num">{{ plMoney(r.netUnitPrice) }}</td>
                            <td class="num">{{ plMoney(r.actUnitCost) }}</td>
                            <td class="num ci-ploss-loss" :class="{ neg: r.lineLoss < 0 }">{{ plMoney(r.lineLoss) }}</td>
                          </tr>
                        </tbody>
                      </table>
                      <div v-if="!plBusy && !plView.length && !plQueryError" class="ci-aiempty">No {{ plCatLabel }} lines in this date range.</div>
                      <div v-if="plTruncated" class="ci-ploss-trunc">Showing the {{ fmtNum(plRows.length) }} most recent lines — narrow the date range (Filters) for the full set. The tab counts above cover the whole window.</div>
                    </div>
                    <div v-else class="ci-pcost-chart">
                      <div class="ci-pcost-chart-cap">{{ plChartCaption }}</div>
                      <div v-if="plChartEmpty" class="ci-aiempty">{{ plChartEmptyText }}</div>
                      <!-- The chart IS the generated SVG (see _plChartSvg) — the same generator the print
                           doc and the Excel PNGs use. v-html is safe here: the only external values are
                           part numbers, customers and descriptions, and the generator escapes them. -->
                      <div v-else class="ci-pcc-host" v-html="plChartSvgLive"></div>
                    </div>
                  </template>
                </div>
              </template>
              <!-- Machine & Part Codes — one searchable card; columns = Category / Prefix / Description / Part-Desc.
                   Click any value to add it as a cross-filter (it then moves up to a chip). -->
              <template v-else-if="it.i === 'codes'">
                <div v-if="codesDeferred" class="ci-details-defer">
                  <button type="button" class="ci-details-loadbtn" @click="codesDeferred = false; loadCodes()">🔤 Load Machine &amp; Part Codes</button>
                  <div class="ci-details-defer-hint">Loaded on request — this is the report's heaviest query. Add a filter (customer, part, rep, …) and it loads automatically.</div>
                </div>
                <div v-else class="ci-codes" :class="{ 'ci-bars-loading': loading || codesBusy }">
                  <div v-if="codeChips.length" class="ci-cust-chips ci-chips-inset">
                    <span v-for="c in codeChips" :key="'kch' + c.dim + '|' + c.val" class="ci-cust-chip" title="Hover for all active filters · click × to remove" @mouseenter="openChipHoverAt(c.dim, c.val, $event)" @mouseleave="closeChipHoverPop()">{{ c.val }}<button type="button" @click="removeChip(c.dim, c.val)" aria-label="Remove filter">×</button></span>
                  </div>
                  <div class="ci-codes-searchwrap">
                    <input class="ci-codes-search" type="text" v-model="codeSearch"
                           placeholder="Search machine category, prefix, description, or part description…" />
                    <button v-if="codeSearch" type="button" class="ci-codes-clear" @click="codeSearch = ''" title="Clear">×</button>
                  </div>
                  <div class="ci-codes-cols">
                    <div v-for="g in codeGroups" :key="g.key" class="ci-codes-group"
                         :class="{ 'ci-codes-group--narrow': g.key === 'machineCodePrefix' || g.key === 'machineCodeCategory' }">
                      <div class="ci-codes-gh">{{ g.label }}</div>
                      <div class="ci-codes-rows">
                        <div v-for="c in g.items" :key="g.key + '|' + c.val" class="ci-codes-row"
                             :title="'Click to filter by ' + c.val + ' — ' + fmtNum(c.orderCount) + ' orders'"
                             @click="toggleFilter(g.key, c.val)">
                          <div class="ci-codes-rowtop">
                            <span class="ci-codes-val">{{ c.val }}</span>
                            <span class="ci-codes-cnt">{{ fmtNum(c.orderCount) }}</span>
                          </div>
                          <span class="ci-codes-track">
                            <span class="ci-codes-fill" :style="{ width: barPct(c.orderCount, g.max) }">
                              <span v-for="seg in (codeRepSegments[g.key + '|' + c.val] || [])" :key="seg.rep" class="ci-codes-seg"
                                    :style="{ width: seg.pct, background: seg.color }"
                                    :title="seg.rep + ' — ' + fmtNum(seg.orderCount) + ' orders'"></span>
                              <span v-if="!(codeRepSegments[g.key + '|' + c.val] || []).length" class="ci-codes-solid"></span>
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div v-if="codesBusy && !codeGroups.length" class="ci-aiempty">Loading Machine &amp; Part Codes…</div>
                    <div v-else-if="!codeGroups.length" class="ci-aiempty">No codes match your search.</div>
                  </div>
                </div>
              </template>
              <!-- Customer Order Details (nested drill-down) as a draggable card. HEAVY (full-table LOB scan), so for a
                   broad/date-only view it's deferred behind a button; a narrowing filter (customer/part/rep…) auto-loads it. -->
              <template v-else-if="it.i === 'details'">
                <div class="ci-detailbody">
                  <div v-if="detailsDeferred" class="ci-details-defer">
                    <button type="button" class="ci-details-loadbtn" @click="fetchDetails()">📋 Load order details</button>
                    <div class="ci-details-defer-hint">Loaded on request to keep the report fast on broad views. Add a filter (customer, part, rep, …) and it loads automatically.</div>
                  </div>
                  <div v-else-if="detailsBusy" class="ci-details-loading">Loading order details…</div>
                  <customer-order-details v-else :customers="details.customers" :message="details.message" :color-for="terrColor" :customer-color-map="customerColorMap" @perspective="onCustomerPerspective" @whyhere="onCodWhyHere" @printdoc="printCodDoc" />
                </div>
              </template>
              <!-- Flat order-lines table as a draggable card -->
              <template v-else-if="it.i === 'orders'">
                <order-lines-table :columns="columns" :rows="rows" :loading="loading" :total-rows="totalRows"
                                   :page="page" :page-count="pageCount" :page-size="pageSize" :sort="sort" :dir="dir" :fill="true"
                                   @sort="changeSort" @page="goPage" @page-size="changePageSize" />
              </template>
              <!-- AI-built card — rendered by its viz type from the backend-assembled data. -->
              <template v-else-if="isAiCard(it.i) && aiCards[it.i]">
                <div class="ci-aicard">
                  <!-- kpi: one big figure -->
                  <template v-if="aiCards[it.i].viz === 'kpi'">
                    <div class="ci-kpi">
                      <div class="ci-kpi-label">{{ aiCards[it.i].data.label }}</div>
                      <div class="ci-kpi-value">{{ aiCards[it.i].data.format === 'money' ? fmtMoney(aiCards[it.i].data.value) : fmtNum(aiCards[it.i].data.value) }}</div>
                    </div>
                  </template>
                  <!-- kpiPair: two figures side by side -->
                  <template v-else-if="aiCards[it.i].viz === 'kpiPair'">
                    <div class="ci-kpi ci-kpi-savedcarts">
                      <div v-for="(pi, pidx) in aiCards[it.i].data.items" :key="'aip' + pidx" class="ci-savedcart-fig" :class="{ 'ci-sc-click': aiCards[it.i].cardId === 'savedCarts' }" :title="aiCards[it.i].cardId === 'savedCarts' ? 'Click for the customer breakdown' : ''" @click="aiCards[it.i].cardId === 'savedCarts' && openSavedCarts(pidx === 0 ? 'order' : 'quote')">
                        <div class="ci-kpi-label">{{ pi.label }}</div>
                        <div class="ci-kpi-value">{{ pi.format === 'money' ? fmtMoney(pi.value) : fmtNum(pi.value) }}</div>
                      </div>
                    </div>
                  </template>
                  <!-- barlist: ranked horizontal bars -->
                  <template v-else-if="aiCards[it.i].viz === 'barlist'">
                    <div class="ci-aibars">
                      <div v-for="(bi, bidx) in aiCards[it.i].data.items" :key="'aib' + bidx" class="ci-bar-row">
                        <div class="ci-bar-label" :title="bi.label">{{ bi.label }}</div>
                        <div class="ci-bar-track"><div class="ci-bar ci-bar-ai" :style="{ width: barPct(bi.value, aiBarMax(aiCards[it.i].data.items)) }"></div></div>
                        <div class="ci-bar-val">{{ aiCards[it.i].data.format === 'money' ? fmtMoney(bi.value) : fmtNum(bi.value) }}</div>
                      </div>
                      <div v-if="!aiCards[it.i].data.items || !aiCards[it.i].data.items.length" class="ci-aiempty">No data.</div>
                    </div>
                  </template>
                </div>
              </template>
              <!-- Custom (Master-authored) card — rich-text content, or a bound-data viz -->
              <template v-else-if="isCustomCard(it.i) && customCards[it.i]">
                <!-- Content: sanitized HTML (server-cleaned on save) -->
                <div v-if="customCards[it.i].type === 'content'" class="ci-cc-content" @click="onContentClick" v-html="renderTokens(customCards[it.i].contentHtml)"></div>
                <!-- Data: rendered by the saved viz from /dashboard-cards/{id}/data -->
                <template v-else-if="customCards[it.i].data">
                  <div v-if="customCards[it.i].data.viz === 'kpi'" class="ci-kpi">
                    <div class="ci-kpi-label">{{ customCards[it.i].title }}</div>
                    <div class="ci-kpi-value">{{ ccFmt(customCards[it.i].data.data.value, customCards[it.i].data.data.format) }}</div>
                  </div>
                  <div v-else-if="customCards[it.i].data.viz === 'barlist'" class="ci-aibars">
                    <div v-for="(bi, bidx) in (customCards[it.i].data.data.items || [])" :key="'ccb' + bidx" class="ci-bar-row">
                      <div class="ci-bar-label" :title="bi.label">{{ bi.label }}</div>
                      <div class="ci-bar-track"><div class="ci-bar ci-bar-ai" :style="{ width: barPct(bi.value, ccBarMax(customCards[it.i].data.data.items)) }"></div></div>
                      <div class="ci-bar-val">{{ ccFmt(bi.value, customCards[it.i].data.data.format) }}</div>
                    </div>
                    <div v-if="!(customCards[it.i].data.data.items || []).length" class="ci-aiempty">No data.</div>
                  </div>
                  <div v-else class="ci-cc-tablewrap">
                    <table class="ci-cc-table">
                      <thead><tr><th v-for="(c, ci) in (customCards[it.i].data.data.columns || [])" :key="'cch' + ci">{{ c }}</th></tr></thead>
                      <tbody>
                        <tr v-for="(r, ri) in (customCards[it.i].data.data.rows || [])" :key="'ccr' + ri">
                          <td v-for="(cell, cci) in r" :key="'ccc' + cci">{{ cell }}</td>
                        </tr>
                        <tr v-if="!(customCards[it.i].data.data.rows || []).length"><td :colspan="(customCards[it.i].data.data.columns || []).length || 1" class="ci-aiempty">No rows.</td></tr>
                      </tbody>
                    </table>
                  </div>
                </template>
                <div v-else class="ci-aiempty">Loading…</div>
              </template>
            </div>
          </div>
        </grid-item>
      </grid-layout>

      </template>

      <!-- ===================== LAYOUT 2 (redesigned) ===================== -->
      <template v-else>
        <!-- KPI cards -->
        <div class="l2-kpis">
          <div class="l2-kpi">
            <div class="l2-kpi-val">{{ fmtMoney(kpis.totalSales) }}</div>
            <div class="l2-kpi-lbl">Total sales</div>
          </div>
          <div class="l2-kpi">
            <div class="l2-kpi-val">{{ fmtNum(kpis.orderCount) }}</div>
            <div class="l2-kpi-lbl">Orders</div>
          </div>
          <div class="l2-kpi">
            <div class="l2-kpi-val">{{ fmtMoney(kpis.orderCount ? kpis.totalSales / kpis.orderCount : 0) }}</div>
            <div class="l2-kpi-lbl">Avg. order</div>
          </div>
          <div class="l2-kpi">
            <div class="l2-kpi-val">{{ fmtNum(kpis.invoicedOrderCount) }} <span class="l2-kpi-sub">of {{ fmtNum(kpis.orderCount) }}</span></div>
            <div class="l2-kpi-lbl">Invoiced</div>
          </div>
        </div>

        <!-- Dominant map -->
        <div class="l2-card l2-mapcard" v-if="mapStates.length">
          <div class="l2-card-h">Sales by Territory</div>
          <div class="l2-mapbody">
            <territory-map :by-state="mapStates" :color-for="terrColor" :legend-items="mapLegend" :selected-state="mapSelectedState" :selected-territory="mapSelectedTerritory" :zoom="mapZoom" @select-state="onSelectState" @zoom-change="onMapZoom" @hover-state="mapHoverState = $event" />
            <div class="ci-mapflag">
              <template v-if="mapFlagState">
                <img class="ci-mapflag-img" :src="stateFlagUrl(mapFlagState.postal)" :alt="mapFlagState.name + ' flag'" loading="lazy" />
                <div class="ci-mapflag-info">
                  <div class="ci-mapflag-name">{{ mapFlagState.name }}</div>
                  <div class="ci-mapflag-sub" v-if="mapFlagState.hasData">{{ fmtMoney(mapFlagState.sales) }} · {{ mapFlagState.customers }} customer{{ mapFlagState.customers === 1 ? '' : 's' }} · {{ mapFlagState.orders }} order{{ mapFlagState.orders === 1 ? '' : 's' }}</div>
                  <div class="ci-mapflag-sub ci-mapflag-sub--none" v-else>No sales in this range</div>
                </div>
              </template>
              <div v-else class="ci-mapflag-hint">Hover a state to see total sales, how many customers, and how many orders</div>
            </div>
          </div>
        </div>

        <!-- Ranked insight lists -->
        <div class="l2-ranks">
          <div class="l2-card">
            <div class="l2-card-h">Top Customers</div>
            <div v-for="c in topCustomers" :key="'l2tc' + c.customerId" class="l2-rankrow"
                 :class="{ 'is-sel': filters.customer.indexOf(c.customerId) >= 0 }"
                 :title="'Click to filter by ' + c.customerId" @click="toggleCustomerFilter(c.customerId)">
              <span class="l2-rank-lbl">{{ c.customerName || c.customerId }}</span>
              <span class="l2-rank-track"><span class="l2-rank-bar" :style="{ width: l2pct(c.sales, maxL2Customer) }"></span></span>
              <span class="l2-rank-val">{{ fmtMoney(c.sales) }}</span>
            </div>
            <div v-if="!topCustomers.length" class="l2-rank-empty">No data.</div>
            <button v-if="byCustomer.length > 5" type="button" class="l2-viewall" @click="l2ShowAllCustomers = !l2ShowAllCustomers">
              {{ l2ShowAllCustomers ? 'Show top 5' : 'View all (' + byCustomer.length + ')' }}
            </button>
          </div>

          <div class="l2-card">
            <div class="l2-card-h">Top Parts</div>
            <div v-for="p in topParts" :key="'l2tp' + p.partNumber" class="l2-rankrow"
                 :class="{ 'is-sel': filters.part.indexOf(p.partNumber) >= 0 }"
                 :title="p.partDescription || p.partNumber" @click="toggleFilter('part', p.partNumber)">
              <span class="l2-rank-lbl">{{ p.partNumber }}</span>
              <span class="l2-rank-track"><span class="l2-rank-bar l2-bar-teal" :style="{ width: l2pct(p.qty, maxL2Part) }"></span></span>
              <span class="l2-rank-val">{{ fmtNum(p.qty) }}</span>
            </div>
            <div v-if="!topParts.length" class="l2-rank-empty">No data.</div>
            <button v-if="byPart.length > 5" type="button" class="l2-viewall" @click="l2ShowAllParts = !l2ShowAllParts">
              {{ l2ShowAllParts ? 'Show top 5' : 'View all (' + byPart.length + ')' }}
            </button>
          </div>

          <div class="l2-card">
            <div class="l2-card-h">Territory Mix</div>
            <div v-for="t in topTerritories" :key="'l2tt' + t.territory" class="l2-rankrow"
                 :class="{ 'is-sel': filters.territory.indexOf(t.territory) >= 0 }"
                 :title="'Click to add ' + t.territory" @click="toggleTerritory(t.territory)">
              <span class="l2-rank-lbl"><span class="l2-dot" :style="{ background: terrColor(t.territory) }"></span>{{ t.territory }}</span>
              <span class="l2-rank-track"><span class="l2-rank-bar" :style="{ width: l2pct(t.sales, maxL2Terr), background: terrColor(t.territory) }"></span></span>
              <span class="l2-rank-val">{{ fmtMoney(t.sales) }}</span>
            </div>
            <div v-if="!topTerritories.length" class="l2-rank-empty">No data.</div>
          </div>
        </div>

      </template>

      <!-- ===== PER-ORDER PRINT SHEET (Customer Order Details 🖨) =====
           ONE document per print, on the same letterhead the Order Details page prints: logo left, customer
           center, doc # + Parts Department right, red rule, parts table, bottom-line total. Screen-invisible
           by design (`.codp-doc { display: none }`) — printing is its only job.
           These rows are VMFG documents (invoice or open order) from rpt_CustomerOrders, NOT portal orders, so
           the sheet is built from the row the card already holds rather than re-fetched: no Sold-To/Ship-To
           street address exists for them (ship-to is country + postal only), and there are no PO groupings to
           page-break on. Everything the row DOES carry — notations, specs, part types — prints.
           The wrapper renders unconditionally so mounted() can move it to <body>; only its contents are v-if'd. -->
      <!-- "This looks like a quote" — offered on attach, never forced. -->
      <div v-if="quotePrompt.open" class="qp-backdrop" @click.self="quotePromptNo">
        <div class="qp-box" role="dialog" aria-labelledby="qpTitle">
          <div class="qp-h" id="qpTitle">{{ quotePrompt.step === 2 ? "📅 How far back should I compare?" : "📄 That looks like a quote" }}</div>
          <div class="qp-file">{{ quotePrompt.name }}</div>

          <!-- STEP 2 — the window. Asked here rather than assumed, for the same reason the un-dated Ask
               pauses at the all-time gate: an average over fifteen years of prices and an average over
               last year are different numbers, and the rep is the one who knows which conversation this
               quote belongs to. -->
          <template v-if="quotePrompt.step === 2">
            <p class="qp-p">I compare each quoted price against what
              <b v-if="quotePrompt.peek && quotePrompt.peek.customerId">{{ quotePrompt.peek.customerId }}</b>
              <b v-else>this customer</b> actually paid. How far back should that history reach?</p>
            <div class="qp-winrow">
              <button type="button" v-for="w in quoteWindowChoices" :key="'qpw' + w.kind"
                      class="qp-win" @click="quoteWindowPick(w.kind)" :title="w.tip">{{ w.label }}</button>
            </div>
            <button type="button" class="qp-yes qp-win-all" @click="quoteWindowPick('')">
              All history — everything back to 1990
            </button>
            <div class="qp-winnote">A narrow window reflects what they pay <em>now</em>; all history covers
              every part they have ever bought, but old prices pull the average. Whatever you pick is
              printed on the comparison.</div>
            <div class="qp-btns qp-btns--back">
              <button type="button" class="qp-no" @click="quotePromptBack">← Back</button>
            </div>
          </template>
          <template v-else>

          <!-- WHO and HOW MUCH, read from the document, confirmed before anything runs. Comparing a
               quote against the wrong customer's history is a silent, expensive kind of wrong. -->
          <div v-if="quotePrompt.busy" class="qp-peek qp-peek--busy">Reading the quote…</div>
          <div v-else-if="quotePrompt.peek && quotePrompt.peek.customerId" class="qp-peek qp-peek--ok">
            <div class="qp-peek-l">This quote is for</div>
            <div class="qp-peek-cust">
              <b>{{ quotePrompt.peek.customerId }}</b>
              <span v-if="quotePrompt.peek.customerName"> — {{ quotePrompt.peek.customerName }}</span>
            </div>
            <div v-if="quotePrompt.peek.pricedLines" class="qp-peek-n">
              {{ quotePrompt.peek.pricedLines }} priced line(s) found
            </div>
            <div class="qp-peek-hint">Not the right customer? Choose “No” and name the one you want in your question.</div>
          </div>
          <div v-else-if="quotePrompt.peek && quotePrompt.peek.isImage" class="qp-peek qp-peek--warn">
            This is an image, so I can only read it when you ask the question — I cannot confirm the
            customer yet. Name the customer id in your question if you can.
          </div>
          <div v-else class="qp-peek qp-peek--warn">
            <b>I could not find a customer in this file.</b>
            <span v-if="quotePrompt.peek && quotePrompt.peek.pricedLines"> ({{ quotePrompt.peek.pricedLines }} priced line(s) found.)</span>
            Name the customer id in your question — e.g. “compare this to XI199” — or select the customer
            in the report first.
          </div>

          <p class="qp-p">Want me to read its line items and compare each one against what this customer
             has actually paid?</p>
          <div class="qp-btns">
            <button type="button" class="qp-yes" :disabled="quotePrompt.busy" @click="quotePromptYes">
              {{ quotePrompt.peek && quotePrompt.peek.customerId
                 ? "Yes — compare it to " + quotePrompt.peek.customerId + "'s orders"
                 : "Yes — compare it to past orders" }}
            </button>
            <button type="button" class="qp-no" @click="quotePromptNo">No, I'll ask my own question</button>
          </div>
          <div class="qp-sug-h">Or start from one of these — the file stays attached either way:</div>
          <ul class="qp-sug">
            <li v-for="(q, i) in quoteSuggestions" :key="'qps' + i">
              <button type="button" @click="quotePromptUse(q)">{{ q }}</button>
            </li>
          </ul>
          </template>
        </div>
      </div>

      <!-- ===== QUOTE REVIEW PRESENTATION =====
           The same comparison on the Steele letterhead, for printing or saving as a PDF. Screen-invisible;
           printing is its only purpose. Every row is expanded here — paper has the room a card does not. -->
      <div ref="qrPrintDoc" class="qrp-doc">
        <template v-if="qrPrintOn">
          <div class="qrp-head">
            <!-- OWN failure flag, not the order sheet's: one sheet's image error must not silently
                 replace the other sheet's logo with text. ref = what quotePresentation waits on. -->
            <div class="qrp-head-logo">
              <img v-if="!qrPrintLogoFailed" ref="qrPrintLogo" src="@/assets/img/steele-logo.svg" alt="JC Steele"
                   class="qrp-logo-img" @error="qrPrintLogoFailed = true" />
              <div v-else class="qrp-logo-text">STEELE</div>
            </div>
            <div class="qrp-head-mid">
              <div class="qrp-head-t">Quote Review<span v-if="qrPrintPreparedBy"> — {{ qrPrintPreparedBy }}</span></div>
              <div class="qrp-head-s">{{ qrPrintSubtitle }}</div>
            </div>
            <div class="qrp-head-addr">
              <div class="qrp-head-addr-name">Parts Department</div>
              <div>145 National Dr.</div>
              <div>Statesville, NC 28625</div>
              <div>P. (800) 278-3353</div>
            </div>
          </div>

          <table class="qrp-tbl">
            <thead>
              <tr>
                <th class="qrp-ln">Line</th>
                <th>Quoted Part Number</th><th class="qrp-n">Qty</th><th class="qrp-n">Quoted Price</th>
                <th class="qrp-n">Avg of Past Orders</th><th class="qrp-n">vs Avg</th>
                <th class="qrp-n">Margin</th><th>Reading</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="l in qrPrintLines">
                <tr :key="'qp' + l.line">
                  <td class="qrp-ln">{{ l.quoteLine || "" }}</td>
                  <td>
                    <b>{{ l.partQuoted || l.partMatched || "(no part number)" }}</b>
                    <div v-if="l.description" class="qrp-desc">{{ l.description }}</div>
                    <div v-if="l.descriptionRows" class="qrp-desc">Includes {{ l.descriptionRows }} component
                      part(s) listed under this line; they are not priced separately.</div>
                  </td>
                  <td class="qrp-n">{{ l.qty != null ? fmtNum(l.qty) : "—" }}</td>
                  <td class="qrp-n">{{ l.quotedUnit != null ? fmtMoney(l.quotedUnit) : "not readable" }}</td>
                  <td class="qrp-n">{{ l.pastAvg != null ? fmtMoney(l.pastAvg) : "—" }}</td>
                  <td class="qrp-n">{{ l.pctVsAvg != null ? (l.pctVsAvg > 0 ? "+" : "") + l.pctVsAvg + "%" : "—" }}</td>
                  <td class="qrp-n">{{ l.marginPct != null ? l.marginPct + "%" : "—" }}</td>
                  <td class="qrp-read">{{ l.verdict || (l.partMatched ? "—" : qrNewChipText) }}</td>
                </tr>
                <tr v-if="l.note || (l.past && l.past.orders)" :key="'qpn' + l.line" class="qrp-sub">
                  <td colspan="8">
                    <span v-if="l.past && l.past.orders">
                      {{ fmtNum(l.past.orders) }} past order(s), {{ fmtNum(l.past.totalQty) }} pieces;
                      last paid {{ fmtMoney(l.past.lastPaid) }}, range {{ fmtMoney(l.past.minPaid) }}–{{ fmtMoney(l.past.maxPaid) }}<span v-if="l.past.lastOrder">, last order {{ l.past.lastOrder }}</span>.
                    </span>
                    <span v-if="l.note"> {{ l.note }}</span>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>

          <div v-if="qrPrintNotCompared.length" class="qrp-narr">
            <div class="qrp-narr-h">Not compared ({{ qrPrintNotCompared.length }})</div>
            <ul>
              <li v-for="l in qrPrintNotCompared" :key="'qpg' + l.line">
                <b v-if="l.quoteLine">#{{ l.quoteLine }} </b>
                <b>{{ l.partQuoted || l.partMatched || ('line ' + l.line) }}</b> — {{ l.notComparedReason }}
              </li>
            </ul>
          </div>
          <div v-if="aiAnswerLines.length" class="qrp-narr">
            <div class="qrp-narr-h">Review</div>
            <ul><li v-for="(ln, i) in aiAnswerLines" :key="'qpl' + i">{{ ln.text }}</li></ul>
          </div>
          <div class="qrp-foot">
            Prices compared per unit against this customer's own order history. A line marked
            &ldquo;not readable&rdquo; or &ldquo;uncertain&rdquo; was not confirmed by the quote's own arithmetic.
          </div>
        </template>
      </div>

      <div ref="codPrintDoc" class="codp-doc">
        <template v-if="codPrint.doc">
          <div class="codp-head">
            <div class="codp-head-logo">
              <img v-if="!codPrintLogoFailed" src="@/assets/img/steele-logo.svg" alt="JC Steele"
                   class="codp-logo-img" @error="codPrintLogoFailed = true" />
              <div v-else class="codp-logo-text">STEELE</div>
            </div>
            <div class="codp-head-cust">
              <div class="codp-head-cust-name">{{ codPrint.customer.customerName }}</div>
              <div v-if="codPrint.customer.customerId" class="codp-head-cust-id">Customer ID: {{ codPrint.customer.customerId }}</div>
            </div>
            <div class="codp-head-addr">
              <div class="codp-head-docid">{{ codPrintKind }} #: {{ codPrint.doc.docId }}</div>
              <div class="codp-head-addr-name">Parts Department</div>
              <div>145 National Dr.</div>
              <div>Statesville, NC 28625</div>
              <div>P. (800) 278-3353</div>
            </div>
          </div>

          <div class="codp-meta">
            <div><span class="codp-meta-l">Date</span>{{ codPrintDate }}</div>
            <div v-if="codPrint.doc.territory"><span class="codp-meta-l">Territory</span>{{ codPrint.doc.territory }}</div>
            <div v-if="codPrint.doc.shipTo"><span class="codp-meta-l">Ship To</span>{{ codPrint.doc.shipTo }}</div>
          </div>

          <div v-if="codPrint.doc.orderNotation" class="codp-note">
            <span class="codp-note-l">Order Notation</span>{{ codPrint.doc.orderNotation }}
          </div>
          <div v-if="codPrint.doc.orderSpecs" class="codp-note codp-note--spec">
            <span class="codp-note-l">Order Specifications</span>{{ codPrint.doc.orderSpecs }}
          </div>

          <table class="codp-parts">
            <thead>
              <tr>
                <th>Part ID</th><th>Part Description</th><th>Part Type</th>
                <th class="codp-n">Unit Price</th><th class="codp-n">Qty</th><th class="codp-n">Line Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, pi) in (codPrint.doc.parts || [])" :key="'codp-' + pi">
                <td>{{ p.partNumber }}</td>
                <td>{{ p.partDescription }}
                  <div v-if="p.partNotation" class="codp-pnote">Note — {{ p.partNotation }}</div>
                  <div v-if="p.partSpecs" class="codp-pnote">Spec — {{ p.partSpecs }}</div>
                </td>
                <td>{{ p.partType }}</td>
                <td class="codp-n">{{ fmtMoney(p.unitPrice) }}</td>
                <td class="codp-n">{{ fmtNum(p.qty) }}</td>
                <td class="codp-n">{{ fmtMoney(p.lineTotal) }}</td>
              </tr>
            </tbody>
          </table>

          <div class="codp-total">
            <div class="codp-total-l">{{ codPrintKind }} Total:</div>
            <div class="codp-total-v">{{ fmtNum(codPrintQty) }} Parts at {{ fmtMoney(codPrint.doc.total) }}</div>
          </div>
        </template>
      </div>

      <!-- Filters + Tokens drawers — wrapped and appended to <body> in mounted() so they escape the report
           card's stacking context and render ABOVE the fixed #globalBreadcrumbs bar (z 998), which was
           overlapping the drawer's top (the From/To date fields) and stealing their clicks. -->
      <div ref="reportDrawers">
      <!-- Email Insights team-inbox detail/reply modals land here — reportDrawers is moved to document.body on
           mount, so the modals escape the .container `isolation:isolate` stacking context (which capped their
           z-index below the grid cards). -->
      <portal-target name="report-email-modal"></portal-target>
      <!-- Filters slide-out drawer REMOVED — replaced by the inline filter bar above (chips + "More" panel).
           Disabled with v-if="false" (left in place so the sibling Tokens drawer/markup below is untouched). -->
      <aside v-if="false" class="l2-drawer" :class="{ open: filtersDrawerOpen }" aria-label="Filters">
        <!-- Close handle: a circle with a left arrow on the right border (halfway down) instead of a top-right ×.
             No backdrop — the drawer stays open while you work the page until you click this handle. -->
        <button type="button" class="l2-drawer-tab l2-drawer-tab--right" @click="filtersDrawerOpen = false" title="Close filters" aria-label="Close filters">◀</button>
        <div class="l2-drawer-h"><span>Filters</span></div>
        <div class="l2-drawer-body">
          <!-- Active filters: every selection made from the cards, map, or this drawer shows here as a
               removable chip. This is the single home for the filter state that used to sit atop the page. -->
          <div class="l2-f l2-activef" v-if="activeChips.length">
            <span>Active filters <em class="l2-activef-n">{{ activeChips.length }}</em></span>
            <div class="l2-chips">
              <span v-for="c in activeChips" :key="'df' + c.key + (c.val || '')" class="ci-fbar-chip" :class="{ 'ci-fbar-chip--dead': chipIrrelevant(c) }" :title="chipTitle(c)">
                <span class="ci-fbar-chip-k">{{ c.label }}:</span> {{ c.text }}
                <button type="button" class="ci-fbar-chip-x" :style="chipColor(c) ? { background: chipColor(c), color: '#fff' } : null" @click="removeChip(c.key, c.val)" title="Remove">×</button>
              </span>
            </div>
          </div>
          <div v-else class="l2-activef-empty">No filters yet — click any card, bar, or map area to filter, or use the controls below.</div>
          <div class="l2-f"><span>Quick range</span>
            <div class="l2-quickwrap">
              <div class="l2-qr-row">
                <button type="button" @click="setDateShortcut('today')">Today</button>
                <button type="button" @click="setDateShortcut('yesterday')">Yesterday</button>
                <button type="button" @click="setDateShortcut('week')" title="Last 7 days">1 Week</button>
                <button type="button" @click="setDateShortcut('month')" title="Last month">1 Month</button>
              </div>
              <div class="l2-qr-row">
                <button type="button" @click="setDateShortcut('q1')" title="Jan – Mar">Qtr 1</button>
                <button type="button" @click="setDateShortcut('q2')" title="Apr – Jun">Qtr 2</button>
                <button type="button" @click="setDateShortcut('q3')" title="Jul – Sep">Qtr 3</button>
                <button type="button" @click="setDateShortcut('q4')" title="Oct – Dec">Qtr 4</button>
              </div>
              <div class="l2-qr-row">
                <button type="button" @click="setDateShortcut('ytd')" title="Year to date">YTD</button>
                <button type="button" @click="setDateShortcut(1)" title="This calendar year (Jan 1 – Dec 31)">1</button>
                <button type="button" @click="setDateShortcut(2)" title="Last 2 calendar years">2</button>
                <button type="button" @click="setDateShortcut(3)" title="Last 3 calendar years">3</button>
                <button type="button" @click="setDateShortcut(4)" title="Last 4 calendar years">4</button>
                <button type="button" @click="setDateShortcut(5)" title="Last 5 calendar years">5</button>
              </div>
            </div>
          </div>
          <div class="l2-f"><span>From</span><input type="text" inputmode="numeric" maxlength="10" class="ci-datebox" :value="dateDisplay(filters.from)" @focus="$event.target.select()" @input="onDateTypeMask($event)" @change="commitTypedDate('from', $event)" @keyup.enter="$event.target.blur()" placeholder="MM/DD/YYYY" title="Type a date as MM/DD/YYYY" /></div>
          <div class="l2-f"><span>To</span><input type="text" inputmode="numeric" maxlength="10" class="ci-datebox" :value="dateDisplay(filters.to)" @focus="$event.target.select()" @input="onDateTypeMask($event)" @change="commitTypedDate('to', $event)" @keyup.enter="$event.target.blur()" placeholder="MM/DD/YYYY" title="Type a date as MM/DD/YYYY" /></div>
          <label class="l2-f"><span>Date basis</span><select v-model="filters.dateBasis" @change="applyFilters"><option value="order">Order date</option><option value="invoice">Invoice date</option></select></label>
          <label class="l2-f"><span>Invoiced</span><select v-model="filters.invoiced" @change="applyFilters"><option value="all">All</option><option value="invoiced">Invoiced</option><option value="not">Not invoiced</option></select></label>
          <filter-slicer label="Sales Rep" :options="opts.salesReps" :selected="filters.salesRep" @toggle="toggleRepName($event)" @clear="clearFilter('salesRep')" />
          <filter-slicer label="Territory" :options="opts.territories" :selected="filters.territory" @toggle="toggleTerritory($event)" @clear="clearFilter('territory')" />
          <filter-slicer label="Order Status" :options="opts.statuses" :selected="filters.status" :tips="statusTips" @toggle="toggleFilter('status', $event)" @clear="clearFilter('status')" />
          <filter-slicer label="Machine Category" :options="opts.machineCodeCategories" :selected="filters.machineCodeCategory" @toggle="toggleFilter('machineCodeCategory', $event)" @clear="clearFilter('machineCodeCategory')" />
          <filter-slicer label="Machine Prefix" :options="opts.machineCodePrefixes" :selected="filters.machineCodePrefix" @toggle="toggleFilter('machineCodePrefix', $event)" @clear="clearFilter('machineCodePrefix')" />
          <label class="l2-f"><span>Customer (pick from view)</span>
            <select :value="''" @change="pickCustomer($event.target.value)">
              <option value="">All in view…</option>
              <option v-for="c in byCustomer" :key="'dco' + c.customerId" :value="c.customerId">{{ c.customerId }} — {{ c.customerName }}</option>
            </select>
          </label>
          <label class="l2-f"><span>Order #</span><input type="text" v-model="filters.orderNo" @keyup.enter="applyFilters" /></label>
          <label class="l2-f"><span>Part (exact #; Enter to add)</span><input type="text" placeholder="e.g. NDR S 1511 PC" @keyup.enter="addFilter('part', $event.target.value); $event.target.value = ''" /></label>
        </div>
        <div class="l2-drawer-foot">
          <button type="button" class="l2-btn l2-btn-primary" @click="applyFilters()">Apply filters</button>
          <button type="button" class="l2-btn" @click="resetFilters">Clear all</button>
        </div>
      </aside>

      <!-- Tokens reference drawer (Master) — live values for Text/Content cards + all tables/fields for Data cards. -->
      <div v-if="tokensDrawerOpen" class="l2-drawer-backdrop" @click="tokensDrawerOpen = false"></div>
      <aside class="l2-drawer ci-tokens-drawer" :class="{ open: tokensDrawerOpen }" aria-label="Tokens">
        <!-- Close handle: circle + left arrow on the right border, matching the Filters drawer (replaces the old ×). -->
        <button type="button" class="l2-drawer-tab l2-drawer-tab--right" @click="tokensDrawerOpen = false" title="Close tokens" aria-label="Close tokens">◀</button>
        <div class="l2-drawer-h"><span>Tokens</span></div>
        <div class="l2-drawer-body">
          <input type="text" v-model="tokenSearch" class="ci-tok-search" placeholder="Search tokens &amp; fields…" />
          <div class="ci-tok-sec">
            <div class="ci-tok-sec-h">Live values — type into a Text / Content card</div>
            <p class="ci-tok-note">These fill with live data and update as the filters change. Click to copy.</p>
            <div v-for="tk in filteredLiveTokens" :key="tk.token" class="ci-tok-row" :title="'Copy ' + tk.token" @click="copyToken(tk.token)">
              <code class="ci-tok-name">{{ tk.token }}</code>
              <span class="ci-tok-label">{{ tk.label }}</span>
              <span class="ci-tok-val">{{ tk.value }}</span>
            </div>
            <div v-if="!filteredLiveTokens.length" class="ci-tok-empty">No matching tokens.</div>
          </div>
          <div class="ci-tok-sec">
            <div class="ci-tok-sec-h">All tables &amp; fields — use in a “Data from a table” card</div>
            <p class="ci-tok-note">Every table/view + column the data-mapper can read (credential columns are hidden). Build a Data card and pick these.</p>
            <div v-if="schemaBusy" class="ci-tok-empty">Loading…</div>
            <div v-for="t in filteredSchemaTables" :key="t.name" class="ci-tok-table">
              <div class="ci-tok-table-h">{{ t.name }} <span class="ci-tok-count">{{ t.columns.length }}</span></div>
              <div class="ci-tok-fields">
                <code v-for="c in t.columns" :key="c.name" class="ci-tok-field" :class="{ 'is-num': c.numeric }" :title="c.type + (c.numeric ? ' (numeric)' : '')">{{ c.name }}</code>
              </div>
            </div>
            <div v-if="!schemaBusy && !filteredSchemaTables.length" class="ci-tok-empty">No matching tables/fields.</div>
          </div>
        </div>
      </aside>

      <!-- Saved-carts click-through breakdown popup (inside reportDrawers → body-appended → escapes the card stacking context above the breadcrumb bar) -->
      <div v-if="scDetail.open" class="sc-backdrop" @click.self="scDetail.open = false">
        <div class="sc-modal">
          <div class="sc-head">
            <span>🛒 Saved {{ scDetail.type === 'quote' ? 'Quotes' : 'Orders' }} — customer breakdown</span>
            <button type="button" class="sc-x" @click="scDetail.open = false" aria-label="Close">×</button>
          </div>
          <div class="sc-body">
            <div v-if="scDetail.busy" class="sc-empty">Loading…</div>
            <template v-else>
              <div v-if="filteredScCarts.length && scInsightBusy" class="sc-ins-loading">✨ Analyzing parts for sales insights…</div>
              <div v-if="!filteredScCarts.length" class="sc-empty">No saved {{ scDetail.type === 'quote' ? 'quotes' : 'orders' }} for the current customer filter.</div>
              <template v-for="row in scDisplayRows">
                <!-- Rep group header — shown only when the view spans 2+ sales reps. Click to expand/collapse that
                     rep's carts (collapsed by default; carts + their AI insights load on expand). -->
                <div v-if="row.type === 'rep'" :key="row.key" class="sc-repgroup" :class="{ 'sc-repgroup--open': row.expanded }" :style="{ '--sc-rep-rail': terrColor(row.territory) }" role="button" tabindex="0" :aria-expanded="row.expanded ? 'true' : 'false'" :title="row.expanded ? 'Click to collapse' : 'Click to expand this rep\'s carts'" @click="toggleScRep(row.rep)" @keydown.enter.prevent="toggleScRep(row.rep)" @keydown.space.prevent="toggleScRep(row.rep)">
                  <span class="sc-repgroup-caret">{{ row.expanded ? '▾' : '▸' }}</span>
                  <span class="sc-repgroup-rep">{{ row.rep }}</span>
                  <span v-if="row.territory && row.territory !== '(Unassigned)'" class="sc-repgroup-terr">{{ row.territory }}</span>
                  <span class="sc-repgroup-meta">{{ row.count }} {{ scDetail.type === 'quote' ? 'quote' : 'order' }}{{ row.count === 1 ? '' : 's' }} · {{ fmtMoney(row.total) }}</span>
                </div>
                <div v-else :key="row.key" class="sc-cart">
                  <div class="sc-cart-h">
                    <span v-if="row.cart.customerName && row.cart.customerId" class="sc-cust-id">{{ row.cart.customerId }}</span>
                    <span class="sc-cust">{{ row.cart.customerName || row.cart.customerId }}</span>
                    <span v-if="row.cart.poNumbers" class="sc-po">{{ row.cart.type === 'Quote' ? 'Quote ref' : 'PO #' }}: {{ row.cart.poNumbers }}</span>
                    <span class="sc-meta">{{ row.cart.lineCount }} item{{ row.cart.lineCount === 1 ? '' : 's' }} · {{ fmtMoney(row.cart.total) }} · {{ fmtAgo(row.cart.modifiedAt) }}</span>
                  </div>
                  <!-- Customer notation (VMFG) — powerful context for the rep; shown once per customer in this breakdown. -->
                  <div v-if="row.showNote && (row.cart.customerNotation || row.cart.customerSpecs)" class="sc-notewrap">
                    <div v-if="row.cart.customerNotation" class="sc-note">
                      <button type="button" class="sc-note-lbl sc-note-toggle" @click.stop="toggleScNote(row.cart.cartId + '-note')" :aria-expanded="scNoteOpen(row.cart.cartId + '-note') ? 'true' : 'false'">
                        <span class="sc-note-caret">{{ scNoteOpen(row.cart.cartId + '-note') ? '▾' : '▸' }}</span>📝 Customer Notation
                      </button>
                      <span v-if="scNoteOpen(row.cart.cartId + '-note')" class="sc-note-txt">{{ row.cart.customerNotation }}</span>
                    </div>
                    <div v-if="row.cart.customerSpecs" class="sc-note sc-spec">
                      <button type="button" class="sc-note-lbl sc-note-toggle" @click.stop="toggleScNote(row.cart.cartId + '-spec')" :aria-expanded="scNoteOpen(row.cart.cartId + '-spec') ? 'true' : 'false'">
                        <span class="sc-note-caret">{{ scNoteOpen(row.cart.cartId + '-spec') ? '▾' : '▸' }}</span>📋 Customer Specifications
                      </button>
                      <span v-if="scNoteOpen(row.cart.cartId + '-spec')" class="sc-note-txt">{{ row.cart.customerSpecs }}</span>
                    </div>
                    <button type="button" class="sc-persp-btn"
                            @click.stop="onCustomerPerspective({ customer: { customerId: row.cart.customerId, customerName: row.cart.customerName }, event: $event })"
                            title="AI sales perspective on this customer's notes &amp; specs">💡 AI sales perspective</button>
                  </div>
                  <ul class="sc-items">
                    <li v-for="(it, i) in row.cart.items" :key="i">
                      <span class="sc-pn">{{ it.partNumber }}</span>
                      <span class="sc-pname">{{ it.partName }}</span>
                      <!-- 💡 AI sales-insight (cross-sell, reorder cadence, trend, companions) — hover to reveal. -->
                      <button v-if="scInsightFor(row.cart, it)" type="button" class="sc-insight-btn"
                              @mouseenter="showPartInsight(row.cart, it, $event)" @mouseleave="scheduleHidePartInsight()"
                              @focus="showPartInsight(row.cart, it, $event)" @blur="scheduleHidePartInsight()"
                              @click.stop="showPartInsight(row.cart, it, $event)"
                              aria-label="AI sales insight for this part">💡</button>
                      <span v-else class="sc-ins-slot" aria-hidden="true"></span>
                      <span class="sc-qty">×{{ it.qty }}</span>
                      <span class="sc-price">{{ fmtMoney(it.unitPrice) }}</span>
                    </li>
                    <li v-if="!row.cart.items.length" class="sc-noitems">No line items on this cart.</li>
                  </ul>
                </div>
              </template>
            </template>
          </div>
        </div>
      </div>

      <!-- Per-part AI sales-insight popover (the 💡 hover in the saved-carts breakdown). Fixed-positioned + body-
           appended (via reportDrawers); stays open while the cursor is over it so the rep can click a cross-sell /
           companion part to start a quote. -->
      <div v-if="scPop.open && scPop.data" class="sc-ins-pop" :style="{ left: scPop.x + 'px', top: scPop.y + 'px' }"
           @mouseenter="cancelHidePartInsight()" @mouseleave="scheduleHidePartInsight()">
        <div class="sc-ins-head">
          <span class="sc-ins-ico" aria-hidden="true">💡</span>
          <span class="sc-ins-title">{{ scPop.data.partNumber }}</span>
        </div>
        <div v-if="scPop.data.headline" class="sc-ins-headline">{{ scPop.data.headline }}</div>
        <div v-if="scPop.data.pitch" class="sc-ins-pitch">{{ scPop.data.pitch }}</div>

        <div v-if="scPop.data.alsoBought && scPop.data.alsoBought.length" class="sc-ins-sec">
          <div class="sc-ins-sec-h">🛒 Frequently bought together</div>
          <button v-for="ab in scPop.data.alsoBought" :key="'ab' + ab.partNumber" type="button" class="sc-ins-chip"
                  @click.stop="openInsightPart(scPop.cart, ab)" :title="'Start a quote for ' + ab.partNumber">
            <span v-if="ab.pct" class="sc-ins-pct">{{ ab.pct }}%</span>
            <span class="sc-ins-chip-pn">{{ ab.partNumber }}</span>
            <span v-if="ab.partName" class="sc-ins-chip-nm">{{ ab.partName }}</span>
          </button>
        </div>

        <div v-if="scPop.data.companions && scPop.data.companions.length" class="sc-ins-sec">
          <div class="sc-ins-sec-h">🔧 Companion wear parts (same machine)</div>
          <button v-for="cp in scPop.data.companions" :key="'cp' + cp.partNumber" type="button" class="sc-ins-chip"
                  @click.stop="openInsightPart(scPop.cart, cp)" :title="'Start a quote for ' + cp.partNumber">
            <span class="sc-ins-chip-pn">{{ cp.partNumber }}</span>
            <span v-if="cp.partName" class="sc-ins-chip-nm">{{ cp.partName }}</span>
          </button>
        </div>

        <div class="sc-ins-stats">
          <span v-if="scPop.data.reorder && scPop.data.reorder.orders" class="sc-ins-stat" :class="{ 'is-due': scPop.data.reorder.due }">
            🔁 {{ scPop.data.reorder.orders }}× ordered<template v-if="scPop.data.reorder.due"> · likely due</template>
          </span>
          <span v-if="scPop.data.trend && scPop.data.trend.yoyPct != null" class="sc-ins-stat" :class="scPop.data.trend.yoyPct >= 0 ? 'is-up' : 'is-down'">
            {{ scPop.data.trend.yoyPct >= 0 ? '↑' : '↓' }} {{ Math.abs(scPop.data.trend.yoyPct) }}% YoY
          </span>
          <span v-if="scPop.data.popularity && scPop.data.popularity.customerTopPart" class="sc-ins-stat">⭐ Top part for this customer</span>
        </div>
        <div class="sc-ins-foot">AI insight · click a part to start a quote</div>
      </div>

      <!-- Orders In Process → order-detail modal. Body-appended (via reportDrawers) so it overlays the whole report;
           renders the SAME OrderDetails.vue body in embedded mode from the report-scoped payload. `:key` forces a
           fresh mount per order so the injected orderData re-renders. -->
      <div v-if="oipDetail.open" class="oipd-backdrop" @click.self="closeOrderDetail">
        <div class="oipd-modal">
          <button type="button" class="oipd-x" @click="closeOrderDetail" aria-label="Close">×</button>
          <div class="oipd-body">
            <div v-if="oipDetail.busy" class="oipd-msg">Loading order…</div>
            <div v-else-if="!oipDetail.data" class="oipd-msg">Order not found, or it's outside your territory.</div>
            <order-details v-else :order-data="oipDetail.data" :embedded="true" :key="oipDetail.orderId" />
          </div>
        </div>
      </div>

      <!-- Dashboard tools panel — body-appended via reportDrawers (escapes the card stacking context), opened by the right-rail "Cards" pill. -->
      <div v-if="dashToolsOpen" class="dash-tools">
        <!-- Close handle: circle + right arrow on the left border, matching the Sales Insights drawer (replaces the old ×). -->
        <button type="button" class="l2-drawer-tab l2-drawer-tab--story" @click="dashToolsOpen = false" title="Close dashboard tools" aria-label="Close dashboard tools">▶</button>
        <div class="dash-tools-h"><span>Dashboard</span></div>
        <div class="dash-tools-body">
          <!-- Layouts picker: Layout 1 = locked Default; 2+ = the user's own named layouts -->
          <div class="dash-tools-layouts">
            <span class="dash-tools-lbl">Reports</span>
            <!-- SHARED standard reports, driven by SHARED_REPORTS. Was three hardcoded blocks with
                 hand-numbered labels; a v-for means adding a report is one registry entry, and the numbers
                 stay positional so a viewer who can't see one never sees a gap. ✎ = rename (publisher only;
                 the server re-checks the helpdesk gate). -->
            <button v-for="(R, ri) in visibleSharedReports" :key="R.id" type="button" class="dash-lt-row"
                    :class="{ active: activeLayout === R.id }" @click="selectLayout(R.id)">
              <span class="dash-lt-name">{{ ri + 1 }} · {{ reportNameOf(R.id) }}</span>
              <span class="dash-lt-actions">
                <span v-if="canPublishSharedLayout" class="dash-lt-ic" :title="'Rename ' + reportNameOf(R.id)" @click.stop="renameReport(R.id)">✎</span>
                <span v-if="isMaster" class="dash-lt-ic" title="Export this layout's JSON" @click.stop="exportLayout(R.id)">⤓</span>
                <span v-if="canPublishSharedLayout" class="dash-lt-ic" :title="'Import a layout file → set the shared ' + reportNameOf(R.id) + ' report for everyone with access'" @click.stop="triggerImportFor(R.roleKey)">⤒</span>
                <span class="dash-lt-lock" title="Standard report — locked">🔒</span>
              </span>
            </button>
            <!-- STANDALONE reports: their own pages, so the drawer lists them to launch, not to arrange.
                 They are hand-built with a fixed section order (no card grid, nothing draggable, no layout
                 persistence), so there is deliberately no layout control here rather than a button that
                 would do nothing. -->
            <button v-for="LR in launcherReports" :key="LR.key" type="button" class="dash-lt-row"
                    @click="openLauncherReport(LR.path)">
              <span class="dash-lt-name">{{ LR.name }}</span>
              <span class="dash-lt-actions">
                <span class="dash-lt-ic" title="Open this report — it has its own page (no layout to arrange)">↗</span>
              </span>
            </button>
            <button v-for="(L, idx) in customLayouts" :key="L.id" type="button" class="dash-lt-row" :class="{ active: activeLayout === L.id }" @click="selectLayout(L.id)">
              <span class="dash-lt-name">{{ visibleSharedReports.length + idx + 1 }} · {{ L.name }}</span>
              <span class="dash-lt-actions">
                <span v-if="isMaster" class="dash-lt-ic" title="Export this layout's JSON" @click.stop="exportLayout(L.id)">⤓</span>
                <span class="dash-lt-ic" title="Rename" @click.stop="renameLayout(L.id)">✎</span>
                <span class="dash-lt-ic" title="Delete" @click.stop="deleteLayout(L.id)">×</span>
              </span>
            </button>
            <button v-if="canCreateLayout" type="button" class="dash-lt-new" @click="newLayout">＋ New layout</button>
            <input type="file" ref="layoutImportInput" accept="application/json,.json" style="display:none" @change="onImportFile" />
            <input type="file" ref="layoutImportInputShared2" accept="application/json,.json" style="display:none" @change="onImportFile($event, 'SHARED2')" />
            <input type="file" ref="layoutImportInputShared3" accept="application/json,.json" style="display:none" @change="onImportFile($event, 'SHARED3')" />
          </div>
          <!-- Why the tools are missing, for a Master who used to have them. Rendered OUTSIDE the locked
               block, or it could never appear for the very people who need the explanation. -->
          <p v-if="isMaster && !canPublishSharedLayout && (activeLayout === 'default' || activeLayout === 'shared2' || activeLayout === 'shared3')"
             class="dash-tools-note" style="margin:0 0 8px;">Standard report &mdash; read-only. It is published from
             <b>helpdesk@jcsteele.com</b> (Master, company NC999). Use <b>＋ New layout</b> below to arrange cards for yourself.</p>
          <!-- Editing tools: only on an editable (custom) layout. Layout 1 (Default) is read-only. -->
          <template v-if="!isLayoutLocked">
            <p v-if="activeLayout === 'shared2'" class="dash-tools-note" style="margin:0 0 8px;">Editing the standard <b>Manufacturing Insights</b> report — your changes publish to everyone with access to it.</p>
            <p v-if="activeLayout === 'shared3'" class="dash-tools-note" style="margin:0 0 8px;">Editing the standard <b>Accounting</b> report — your changes publish to everyone with access to it.</p>
            <!-- Automatic layout version history — step back/forward through every arrangement change this session. -->
            <div class="dash-tools-undo">
              <button type="button" class="dash-tools-btn dash-tools-undobtn" :disabled="!canUndo" @click="undoLayout" title="Undo the last layout change (Ctrl+Z)">↶ Undo</button>
              <button type="button" class="dash-tools-btn dash-tools-undobtn" :disabled="!canRedo" @click="redoLayout" title="Redo the layout change (Ctrl+Y)">↷ Redo</button>
            </div>
            <div v-if="layoutHistory.length > 1" class="dash-tools-histnote">Layout version {{ layoutHistoryIndex + 1 }} / {{ layoutHistory.length }} · ⌨ Ctrl+Z / Ctrl+Y</div>
            <button v-if="isMaster" type="button" class="dash-tools-btn" @click="addCustomCard(); dashToolsOpen = false">＋ Add card</button>
            <button type="button" class="dash-tools-btn" @click="aiOpen = !aiOpen">✨ Add a card with AI</button>
            <div v-if="aiOpen" class="dash-tools-ai">
              <input type="text" v-model="aiPrompt" :disabled="aiBusy" placeholder="e.g. how many saved carts and quotes" @keyup.enter="createAiCard" />
              <button type="button" :disabled="aiBusy" @click="createAiCard">Create card</button>
              <span v-if="aiError" class="dash-ai-error">{{ aiError }}</span>
            </div>
            <button type="button" class="dash-tools-btn" @click="saveLayoutNow">{{ dashSaved ? '✓ Saved' : '💾 Save layout' }}</button>
            <!-- Any shared report, from the registry. Accounting (shared3) was MISSING from the old hardcoded
                 pair even though publishSharedLayout() has always handled it, so it could only ever be
                 published by the silent drag autosave — never deliberately. -->
            <button v-if="canPublishSharedLayout && sharedReport" type="button"
                    class="dash-tools-btn dash-tools-publish" @click="publishSharedLayout"
                    title="Make THIS arrangement the standard report every user sees (read-only for them)">📤 Publish to everyone</button>
            <button type="button" class="dash-tools-btn" @click="resetDashLayout">↺ Reset this layout</button>
            <!-- Named layout versions — a reliable save/restore point (per-user blob), independent of the shared
                 Layout-1 default. Save the current arrangement under a name; restore any saved version in one click. -->
            <div class="dash-tools-versions">
              <div class="dash-ver-save">
                <input type="text" v-model="versionName" maxlength="60" placeholder="Name a version…" @keyup.enter="saveLayoutVersion" />
                <button type="button" class="dash-tools-btn" @click="saveLayoutVersion" title="Save the CURRENT arrangement as a named version you can restore anytime">💾 Save version</button>
              </div>
              <div v-if="layoutVersions.length" class="dash-ver-list">
                <div v-for="v in layoutVersions" :key="v.id" class="dash-ver-item">
                  <button type="button" class="dash-ver-restore" :title="'Restore this version (saved ' + fmtVerDate(v.savedAt) + ')'" @click="restoreLayoutVersion(v)">↩ {{ v.name }}</button>
                  <button type="button" class="dash-ver-del" title="Delete this version" @click="deleteLayoutVersion(v)">×</button>
                </div>
              </div>
            </div>
            <!-- Count only USER-hidden cards — the Email DOCK marker also lives in dashHidden (so the grid
                 can't re-append the docked card) but it isn't a hidden card and shouldn't advertise one. -->
            <button v-if="userHiddenCount" type="button" class="dash-tools-btn" @click="restoreHiddenCards">⊕ Restore hidden ({{ userHiddenCount }})</button>
            <!-- DOCKED-layout scenarios: while anything is docked (Filters / Email Insights / both), the current
                 arrangement can be pinned as THAT scenario's layout — re-entering the same dock combination
                 re-applies it exactly. Edits made while docked also autosave into the current scenario. -->
            <button v-if="canPublishSharedLayout && dockScope && dockKey" type="button" class="dash-tools-btn" @click="saveDockPreset" :title="(dockPresetHere ? 'Update' : 'Save') + ' the arrangement applied whenever ' + dockLabel + ' is docked on ' + activeReportName">📌 {{ dockPresetHere ? 'Update' : 'Set' }} Docked layout — {{ dockLabel }}</button>
            <button v-if="canPublishSharedLayout && dockScope && dockKey && dockPresetHere" type="button" class="dash-tools-btn" @click="clearDockPreset" :title="'Forget ' + activeReportName + '&apos;s saved arrangement for the ' + dockLabel + ' docked scenario — docking will then just hide the card(s)'">🗑 Clear Docked layout — {{ dockLabel }}</button>
          </template>
          <p v-else class="dash-tools-note">
            <template v-if="activeLayout === 'shared2'">This is the standard, locked <b>Manufacturing Insights</b> report, curated by a Master.<template v-if="canCreateLayout"> Use <b>＋ New layout</b> above to build your own personal layout.</template></template>
            <template v-else>This is the standard, locked <b>Sales Insights</b> layout.<template v-if="canCreateLayout"> Use <b>＋ New layout</b> above to build your own — drag, resize, and add cards there.</template></template>
          </p>
        </div>
      </div>
      </div>

      <!-- Layout 2 only: flat order-lines table standalone (Layout 1 shows it as a draggable grid card). -->
      <template v-if="layout === '2'">
        <div class="l2-tableheading">Order Details</div>
        <order-lines-table :columns="columns" :rows="rows" :loading="loading" :total-rows="totalRows"
                           :page="page" :page-count="pageCount" :page-size="pageSize" :sort="sort" :dir="dir"
                           @sort="changeSort" @page="goPage" @page-size="changePageSize" />
      </template>

      <!-- Floating "filter story" — appears as filters narrow the data; movable + collapsible. -->
      <!-- Edge toggles: Tokens (left, Master) · Sales Insights (right). The Filters edge toggle was removed
           with the Filters drawer — filters are now the inline bar above the dashboard. -->
      <!-- Tokens toggle now lives on the RIGHT rail (under SETUP) via the report-tokens-pill portal-target. -->
      <portal key="pill-tokens" v-if="layout !== '2' && isMaster" to="report-tokens-pill">
        <div class="ci-rail-pill" role="button" tabindex="0" :class="{ 'is-open': tokensDrawerOpen }" title="Tokens &amp; fields reference"
             @click="openTokens" @keydown.enter.space.prevent="openTokens">
          <span class="ci-rail-pill-icon" aria-hidden="true">🔖</span>
          <span class="ci-rail-pill-label">Tokens</span>
        </div>
      </portal>
      <!-- DESKTOP: the collapsed hero ("Chat") reopens from a rail pill (mirrors Email), placed FIRST in the
           report pill group so it sits above the others. On MOBILE the .ci-chat-edge-tab handles this instead.
           The portal is ALWAYS mounted (v-show, not v-if): chatDocked defaults FALSE (hero open), so a v-if
           portal would leave this slim target EMPTY at mount — and portal-vue doesn't render the first
           projected child into an empty slim target until a re-render, so the very first dock's pill never
           appeared (a refresh "fixed" it because the target then mounted already-primed). Priming the target
           with a display:none pill makes docking a pure CSS toggle. display:none children don't take flex gap. -->
      <portal key="pill-chat" to="report-chat-pill">
        <div v-show="chatDocked" class="ci-rail-pill" role="button" tabindex="0" title="Show the chat" @click="restoreChat" @keydown.enter.space.prevent="restoreChat">
          <span class="ci-rail-pill-icon" aria-hidden="true">💬</span>
          <span class="ci-rail-pill-label">Chat</span>
        </div>
      </portal>
      <!-- DESKTOP: collapsed Filters reopens from a rail pill (between Chat and Email). On MOBILE the FILTERS
           edge tab in .ci-edge-stack handles this instead. -->
      <portal key="pill-filters" v-if="filtersDocked" to="report-filters-pill">
        <div class="ci-rail-pill" role="button" tabindex="0" title="Show the filters" @click="restoreFilters" @keydown.enter.space.prevent="restoreFilters">
          <span class="ci-rail-pill-icon" aria-hidden="true">▤</span>
          <span class="ci-rail-pill-label">Filters</span>
        </div>
      </portal>
      <portal key="pill-email" v-if="layout !== '2' && (emailDocked || (dashHidden && dashHidden.indexOf('emailInsights') >= 0))" to="report-email-pill">
        <div class="ci-rail-pill" role="button" tabindex="0" title="Show Email Insights" @click="restoreEmailInsights" @keydown.enter.space.prevent="restoreEmailInsights">
          <span class="ci-rail-pill-icon" aria-hidden="true">@</span>
          <span class="ci-rail-pill-label">Email</span>
        </div>
      </portal>
      <!-- LAYOUTS drawer pill. Renamed from "Reports" and restricted to helpdesk@jcsteele.com + Master
           (2026-08-05): it is a layout-administration tool, not a report navigator, so it should not be
           in every rep's rail. Visibility is isLayoutsAdmin (NOT canPublishSharedLayout) so the pill
           does not disappear while helpdesk is browsing a customer company — the NC999 interlock still
           gates the PUBLISH and docked-preset buttons inside the drawer. -->
      <portal key="pill-tools" v-if="layout !== '2' && isLayoutsAdmin" to="report-tools-pill">
        <div class="ci-rail-pill" role="button" tabindex="0" title="Layouts — pick a report, arrange its cards, publish and rename"
             @click="dashToolsOpen = !dashToolsOpen" @keydown.enter.space.prevent="dashToolsOpen = !dashToolsOpen">
          <span class="ci-rail-pill-icon" aria-hidden="true">🗂</span>
          <span class="ci-rail-pill-label">Layouts</span>
        </div>
      </portal>
      <portal key="pill-insights" v-if="storyHidden" to="sales-insights-pill">
        <div class="si-pill" role="button" tabindex="0" title="Show Sales Insights"
             @click="openStory" @keydown.enter.space.prevent="openStory"
             style="display:flex;flex-direction:column;align-items:center;gap:6px;background:#fff;color:#9e2436;border:1px solid #ececec;border-left:3px solid #9e2436;border-radius:0 8px 8px 0;box-shadow:2px 2px 8px rgba(0,0,0,.15);padding:10px 8px;min-width:56px;width:56px;cursor:pointer;user-select:none;">
          <span style="font-size:18px;line-height:1;" aria-hidden="true">📊</span>
          <span style="font-size:10px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;line-height:1.1;text-align:center;color:#9e2436;">Insights</span>
        </div>
      </portal>

      <div ref="storyPanel" class="ci-story" :class="{ 'is-open': story.summary && !storyHidden }" :style="{ width: storyWidth + 'px' }">
        <!-- Widen handle (above the close handle): each click opens the panel ~2" wider; resets to default when maxed. -->
        <button type="button" class="l2-drawer-tab ci-story-widen" @click="widenStory" title="Widen the panel (click again for wider; resets when maxed)" aria-label="Widen panel">↔</button>
        <!-- Close handle: a circle with a right arrow on the LEFT border (mirrors the Filters drawer's left-arrow
             handle). The panel stays open until you click it. -->
        <button type="button" class="l2-drawer-tab l2-drawer-tab--story" @click="storyHidden = true" title="Close Sales Insights" aria-label="Close Sales Insights">▶</button>
        <div class="ci-story-head">
          <span class="ci-story-title">📊 Sales Insights</span>
          <span class="ci-story-tools">
            <button type="button" class="ci-story-tool" :disabled="exporting || !totalRows" title="Export the filtered order lines to CSV" @click="exportCsv">{{ exporting ? '…' : '⬇' }}</button>
          </span>
        </div>
        <div class="ci-story-body">
          <!-- ===== AI insights narrative (MANUAL — never auto-called) ===== -->
          <div class="ci-ai">
            <!-- 🕘 Past-insights history — shown at the TOP (above the Presentation button) when the 🕘 toggle is on.
                 Kept outside the generate/result v-if/v-else so it works whether or not a narrative exists yet. -->
            <div v-if="aiHistOpen" class="ci-ai-hist">
              <div class="ci-ai-hist-h"><span>🕘 Insights history</span><button type="button" class="ci-ai-hist-x" @click="aiHistOpen = false" aria-label="Close">×</button></div>
              <div v-if="!aiInsightHistory.length" class="ci-ai-hist-empty">No saved insights yet — click Generate Insights.</div>
              <div v-else class="ci-ai-hist-wrap">
                <!-- Grouped by sales rep, same rules as the Ask history (own → Sales All team → Master all). -->
                <div class="ci-ai-hist-groups">
                  <div v-for="g in aiInsightHistoryGroups" :key="'ihg' + g.name" class="ci-ask-hist-grp">
                    <div class="ci-ask-hist-grp-h">👤 {{ g.name }}<span v-if="g.own" class="ci-ask-hist-you">you</span></div>
                    <ul class="ci-ai-hist-list">
                      <li v-for="h in g.items" :key="h.id" class="ci-ai-hist-item" :class="{ 'is-hover': aiHistHover === h }" @mouseenter="aiHistHover = h">
                        <span class="ci-ai-hist-line" @click="loadInsightFromHistory(h)"><b>{{ fmtHistDate(h.createdAt) }}</b><span v-if="h.title" class="ci-ai-hist-ttl"> — {{ h.title }}</span></span>
                        <button type="button" class="ci-ai-hist-present" @click.stop="printPastReport(h)" title="Open / print this presentation">Presentation</button>
                      </li>
                    </ul>
                  </div>
                </div>
                <div class="ci-ai-hist-prev">
                  <template v-if="aiHistHover">
                    <div class="ci-ai-hist-prev-d">{{ fmtHistDate(aiHistHover.createdAt) }}<span v-if="aiHistHover.title"> — {{ aiHistHover.title }}</span></div>
                    <ul class="ci-ai-text">
                      <li v-for="(ln, i) in histLines(aiHistHover.insights)" :key="'hpi' + i" :class="{ 'is-bullet': ln.bullet }">{{ ln.text }}</li>
                    </ul>
                    <div v-if="aiHistHover.coaching" class="ci-ai-hist-tips">
                      <div class="ci-ai-coach-h">💡 Tips</div>
                      <ul class="ci-ai-text">
                        <li v-for="(ln, i) in histLines(aiHistHover.coaching)" :key="'hpc' + i" :class="{ 'is-bullet': ln.bullet }">{{ ln.text }}</li>
                      </ul>
                    </div>
                  </template>
                  <div v-else class="ci-ai-hist-hint">Hover a date to preview · click to load it</div>
                </div>
              </div>
            </div>
            <!-- Generate (until a narrative exists) + 🕘 history -->
            <div v-if="!aiInsights" class="ci-ai-genrow">
              <button type="button" class="ci-ai-gen" :disabled="aiInsightsBusy" @click="panelGenerateInsights">
                <span v-if="aiInsightsBusy" class="ci-ai-spin" aria-hidden="true"></span>
                {{ aiInsightsBusy ? 'Generating…' : '✨ Generate Insights' }}
              </button>
              <button type="button" class="ci-ai-histbtn" :class="{ 'is-on': aiHistOpen }" title="Past insights" @click="toggleAiHist">🕘</button>
            </div>
            <!-- Generated narrative + Regenerate + the same print-style report data -->
            <div v-else class="ci-ai-result">
              <div class="ci-ai-bar">
                <span class="ci-ai-bar-title">✨ Sales Insights</span>
                <span class="ci-ai-bar-tools">
                  <button type="button" class="ci-ai-histbtn" :class="{ 'is-on': aiHistOpen }" title="Past insights" @click="toggleAiHist">🕘</button>
                  <button type="button" class="ci-ai-regen" :disabled="aiInsightsBusy" @click="panelGenerateInsights">
                    <span v-if="aiInsightsBusy" class="ci-ai-spin" aria-hidden="true"></span>
                    {{ aiInsightsBusy ? 'Regenerating…' : 'Regenerate' }}
                  </button>
                </span>
              </div>
              <!-- Open / print the presentation that was archived with this insight (exact copy incl. screenshot). -->
              <button type="button" class="ci-ai-present" :disabled="printBusy || aiInsightsBusy" @click="openCurrentPresentation">
                <span v-if="printBusy" class="ci-ai-spin" aria-hidden="true"></span>
                🖨 {{ printBusy ? 'Preparing presentation…' : 'Presentation' }}
              </button>
              <div v-if="aiInsightsStale" class="ci-ai-stale">Filters changed — regenerate to refresh.</div>
              <ul class="ci-ai-text">
                <li v-for="(ln, i) in aiInsightsLines" :key="'ail' + i" :class="{ 'is-bullet': ln.bullet }">{{ ln.text }}</li>
              </ul>
              <div v-if="aiCoaching" class="ci-ai-coach">
                <div class="ci-ai-coach-h">💡 Tips &amp; Opportunities</div>
                <ul class="ci-ai-text">
                  <li v-for="(ln, i) in aiCoachingLines" :key="'acl' + i" :class="{ 'is-bullet': ln.bullet }">{{ ln.text }}</li>
                </ul>
              </div>
              <!-- The same data the print presentation shows (KPIs, top customers/parts, territory, saved carts) -->
              <div class="ci-ai-data">
                <div class="ci-ai-data-h">📊 Report data</div>
                <div class="ci-ai-kpis">
                  <span><b>{{ fmtMoney(kpis.totalSales) }}</b> sales</span>
                  <span><b>{{ fmtNum(kpis.orderCount) }}</b> orders</span>
                  <span><b>{{ fmtNum(kpis.invoicedOrderCount) }}</b> invoiced</span>
                </div>
                <div class="ci-ai-data-sub">Top customers</div>
                <div v-for="c in aiDataCustomers" :key="'aidc' + c.customerId" class="ci-ai-brow">
                  <span class="ci-ai-brow-l" :title="c.customerName || c.customerId">{{ c.customerName || c.customerId }}</span>
                  <span class="ci-ai-brow-t"><span class="ci-ai-brow-b" :style="{ width: barPct(c.sales, aiDataMaxCust), background: (customerColorMap[c.customerId] || '#8E44AD') }"></span></span>
                  <span class="ci-ai-brow-v">{{ fmtMoney(c.sales) }}</span>
                </div>
                <div class="ci-ai-data-sub">Top parts (by qty)</div>
                <div v-for="p in aiDataParts" :key="'aidp' + p.partNumber" class="ci-ai-pwrap">
                  <div class="ci-ai-brow">
                    <span class="ci-ai-brow-l" :title="p.partNumber">{{ p.partNumber }}</span>
                    <span class="ci-ai-brow-t"><span class="ci-ai-brow-b" :style="{ width: barPct(p.qty, aiDataMaxPart), background: (partTerritorySegMap[p.partNumber] && partTerritorySegMap[p.partNumber].length) ? 'transparent' : '#16a085' }"><span v-for="(s, si) in (partTerritorySegMap[p.partNumber] || [])" :key="'aps' + si" class="ci-ai-seg" :style="{ width: segShare(s.val, partTerritorySegMap[p.partNumber]), background: s.color }" :title="s.name + ': ' + fmtNum(s.val)"></span></span></span>
                    <span class="ci-ai-brow-v">{{ fmtNum(p.qty) }}</span>
                  </div>
                  <div v-if="(partTerritorySegMap[p.partNumber] || []).length > 1" class="ci-ai-pbreak">
                    <span v-for="(s, si) in partTerritorySegMap[p.partNumber]" :key="'apb' + si" class="ci-ai-pchip"><span class="ci-ai-pdot" :style="{ background: s.color }"></span>{{ s.name }} {{ fmtNum(s.val) }}</span>
                  </div>
                </div>
                <div class="ci-ai-data-sub">Sales by territory</div>
                <div v-for="t in byTerritory" :key="'aidt' + t.territory" class="ci-ai-brow">
                  <span class="ci-ai-brow-l" :title="t.territory"><span class="ci-terr-dot" :style="{ background: terrColor(t.territory) }"></span>{{ t.territory }}</span>
                  <span class="ci-ai-brow-t"><span class="ci-ai-brow-b" :style="{ width: barPct(t.sales, maxTerritorySales), background: terrColor(t.territory) }"></span></span>
                  <span class="ci-ai-brow-v">{{ fmtMoney(t.sales) }}</span>
                </div>
                <div class="ci-ai-data-sub">Saved carts</div>
                <div class="ci-ai-kpis"><span><b>{{ fmtNum(savedCarts.savedOrders) }}</b> saved orders</span><span><b>{{ fmtNum(savedCarts.savedQuotes) }}</b> saved quotes</span></div>
              </div>
            </div>
            <div v-if="aiInsightsError" class="ci-ai-error">{{ aiInsightsError }}</div>
            <p class="ci-ai-note">AI-generated from your current filters — sends the aggregated figures (customer names + totals) to OpenAI.</p>
          </div>

          <!-- The Ask box moved to the HERO at the top of the report (all report roles, not just Master) —
               see .ci-ask-hero. Its full chat history lives behind the hero's 🕘 button. -->

          <!-- ===== Summative: ALL CUSTOMERS ===== -->
          <div v-if="story.summary" class="ci-story-block">
            <div class="ci-story-block-h">All Customers<template v-if="filters.territory.length"> · {{ filters.territory.join(', ') }}</template></div>
            <div class="ci-story-metrics">
              <span class="ci-story-metric"><b>{{ fmtNum(story.summary.orders) }}</b> orders</span>
              <span class="ci-story-metric"><b>{{ fmtMoney(story.summary.sales) }}</b> sales</span>
            </div>
            <div class="ci-story-line">
              <span class="ci-story-dot is-inv"></span><b>{{ fmtNum(story.summary.invoicedOrders) }}</b>&nbsp;invoiced
              <span class="ci-story-sep">·</span>
              <span class="ci-story-dot is-open"></span><b>{{ fmtNum(story.summary.openOrders) }}</b>&nbsp;open
            </div>
            <div v-if="summaryTopParts" class="ci-story-line">🔧&nbsp;{{ summaryTopParts }}</div>
            <div v-if="yoyEligible && story.summary.yoy && story.summary.yoy.length >= 2" class="ci-story-yoy">
              <div class="ci-yoy-label">Year over year{{ yoyDelta(story.summary.yoy) }} <span class="ci-yoy-hint">(bars colored by territory · hover a segment for detail)</span></div>
              <div v-for="(y, i) in story.summary.yoy" :key="'syoy' + y.year" class="ci-yoy-row" :title="yoyTooltip(story.summary.yoy, i)">
                <span class="ci-yoy-year" :class="{ 'is-cur': i === 0 }">{{ y.year }}</span>
                <span class="ci-yoy-track"><span class="ci-yoy-bar ci-q-bar" :style="{ width: yoyPct(y.sales, story.summary.yoy) }"><span v-for="(seg, si) in (y.territories || [])" :key="'ys' + si" class="ci-q-seg" :style="{ width: segPct(seg.sales, y.sales), background: terrColor(seg.name) }" :title="seg.name + ': ' + fmtMoney(seg.sales)"></span></span></span>
                <span class="ci-yoy-val">{{ fmtMoney(y.sales) }}</span>
              </div>
            </div>
            <div v-if="story.summary.quarters && story.summary.quarters.length" class="ci-story-yoy">
              <div class="ci-yoy-label">Avg Quarterly Sales <span class="ci-yoy-hint">(per year · colored by territory)</span></div>
              <div v-for="qq in story.summary.quarters" :key="'sq' + qq.quarter" class="ci-yoy-row">
                <span class="ci-yoy-year">Q{{ qq.quarter }}</span>
                <span class="ci-yoy-track"><span class="ci-yoy-bar ci-q-bar" :style="{ width: yoyPct(qq.sales, story.summary.quarters) }"><span v-for="(seg, si) in (qq.territories || [])" :key="'qs' + si" class="ci-q-seg" :style="{ width: segPct(seg.sales, qq.sales), background: terrColor(seg.name) }" :title="seg.name + ': ' + fmtMoney(seg.sales)"></span></span></span>
                <span class="ci-yoy-val">{{ fmtMoney(qq.sales) }}</span>
              </div>
            </div>
          </div>

          <!-- ===== Customer Story (selected customer) ===== -->
          <div v-if="story.customer" class="ci-story-block ci-story-cust">
            <div class="ci-story-block-h">{{ story.customer.customerName || story.customer.customerId }}</div>
            <p class="ci-story-sentence ci-story-rep">
              Rep <b>{{ story.customer.assignedRep || story.customer.salesRep || '—' }}</b><template v-if="story.customer.territory"> · {{ story.customer.territory }} territory</template>.
            </p>
            <p v-if="story.customer.rank" class="ci-story-sentence">
              <b>#{{ story.customer.rank }}</b> of {{ story.customer.repCustomerCount }} of
              {{ story.customer.salesRep }}'s customers —
              <b>{{ (story.customer.pctOfRepSales || 0).toFixed(1) }}%</b>
              of their {{ fmtMoney(story.customer.repTotalSales) }}.
            </p>
            <div class="ci-story-line">
              <span class="ci-story-dot is-inv"></span><b>{{ fmtNum(story.customer.invoicedOrders) }}</b>&nbsp;invoiced
              <span class="ci-story-sep">·</span>
              <span class="ci-story-dot is-open"></span><b>{{ fmtNum(story.customer.openOrders) }}</b>&nbsp;open
            </div>
            <div v-if="storyParts" class="ci-story-line">🔧&nbsp;{{ storyParts }}</div>
            <div v-if="yoyEligible && story.customer.yoy && story.customer.yoy.length >= 2" class="ci-story-yoy">
              <div class="ci-yoy-label">Year over year{{ yoyDelta(story.customer.yoy) }} <span class="ci-yoy-hint">(bars colored by territory · hover a segment for detail)</span></div>
              <div v-for="(y, i) in story.customer.yoy" :key="'cyoy' + y.year" class="ci-yoy-row" :title="yoyTooltip(story.customer.yoy, i)">
                <span class="ci-yoy-year" :class="{ 'is-cur': i === 0 }">{{ y.year }}</span>
                <span class="ci-yoy-track"><span class="ci-yoy-bar ci-q-bar" :style="{ width: yoyPct(y.sales, story.customer.yoy) }"><span v-for="(seg, si) in (y.territories || [])" :key="'cys' + si" class="ci-q-seg" :style="{ width: segPct(seg.sales, y.sales), background: terrColor(seg.name) }" :title="seg.name + ': ' + fmtMoney(seg.sales)"></span></span></span>
                <span class="ci-yoy-val">{{ fmtMoney(y.sales) }}</span>
              </div>
            </div>
            <div v-if="story.customer.quarters && story.customer.quarters.length" class="ci-story-yoy">
              <div class="ci-yoy-label">Avg Quarterly Sales <span class="ci-yoy-hint">(per year · colored by territory)</span></div>
              <div v-for="qq in story.customer.quarters" :key="'cq' + qq.quarter" class="ci-yoy-row">
                <span class="ci-yoy-year">Q{{ qq.quarter }}</span>
                <span class="ci-yoy-track"><span class="ci-yoy-bar ci-q-bar" :style="{ width: yoyPct(qq.sales, story.customer.quarters) }"><span v-for="(seg, si) in (qq.territories || [])" :key="'cqs' + si" class="ci-q-seg" :style="{ width: segPct(seg.sales, qq.sales), background: terrColor(seg.name) }" :title="seg.name + ': ' + fmtMoney(seg.sales)"></span></span></span>
                <span class="ci-yoy-val">{{ fmtMoney(qq.sales) }}</span>
              </div>
            </div>
          </div>

          <!-- saved carts: what the customer is looking to buy (live portal data) -->
          <div v-if="story.savedCarts && story.savedCarts.length" class="ci-story-block">
            <div class="ci-story-block-h">🛒 Looking to buy — saved Orders &amp; Quotes ({{ story.savedCarts.length }})</div>
            <div v-for="sc in story.savedCarts" :key="'sc' + sc.cartId" class="ci-cart">
              <div class="ci-cart-h">
                <span class="ci-cart-type" :class="sc.type === 'Quote' ? 'is-quote' : 'is-order'">{{ sc.type }}</span>
                <span class="ci-cart-meta">{{ sc.lineCount }} item{{ sc.lineCount === 1 ? '' : 's' }} · {{ fmtMoney(sc.total) }}</span>
                <span class="ci-cart-when">{{ fmtAgo(sc.modifiedAt) }}</span>
              </div>
              <ul class="ci-cart-items">
                <li v-for="(it, idx) in sc.items.slice(0, 6)" :key="idx">
                  <span class="ci-cart-pn">{{ it.partNumber }}</span>
                  <span class="ci-cart-name">{{ it.partName }}</span>
                  <span class="ci-cart-qty">×{{ it.qty }}</span>
                  <span class="ci-cart-price">{{ fmtMoney(it.unitPrice) }}</span>
                </li>
                <li v-if="sc.items.length > 6" class="ci-cart-more">+{{ sc.items.length - 6 }} more line item{{ sc.items.length - 6 === 1 ? '' : 's' }}…</li>
                <li v-if="!sc.items.length" class="ci-cart-more">No line items on this cart.</li>
              </ul>
            </div>
          </div>
          <div v-else-if="story.customer" class="ci-story-line ci-story-muted">🛒 No open saved carts for this customer right now.</div>

        </div>
      </div>

      <!-- Master card designer modal — mounted OUTSIDE the grid so its fixed overlay isn't trapped by grid-item CSS transforms. -->
      <report-card-editor v-if="editorOpen" :card="editingCard" :tokens="tokenList" :scope="currentCardScope()" :templates="buildCardTemplates()" @saved="onCardSaved" @place="placeCardFromLayout1" @restore="restoreFromPicker" @close="closeEditor" />
      <!-- Autosave confirmation — fixed, visible regardless of which panel is open. -->
      <div v-if="dashSaved" class="ci-saved-toast" role="status">✓ Saved</div>
    </div>
  </div>
</template>

<script>
import httpClient from "@/assets/js/HttpClient";
import { recordSearch, recordAction } from "@/assets/js/activity";
import { GridLayout, GridItem } from "vue-grid-layout";
import TerritoryMap from "./TerritoryMap";
import WorldMap from "./WorldMap";
import CustomerOrderDetails from "./CustomerOrderDetails";
import PartsOrdered from "./PartsOrdered";
import EmailInsights from "./EmailInsights";
import CustomersById from "./CustomersById";
import FilterSearch from "./FilterSearch";
import FilterSlicer from "./FilterSlicer";
import ReportCardEditor from "./ReportCardEditor";
import OrderLinesTable from "./OrderLinesTable";
import OrderDetails from "../Account/OrderDetails";
import html2canvas from "html2canvas";

// ---- Costing-for-Parts chart geometry (pure) --------------------------------------------------
// Module-level and side-effect free so the CARD (via computeds) and the EXCEL EXPORT (via the row set
// actually being exported) share one implementation. A Vue 2 template has no `Math` in scope, which is
// why every pixel is resolved here rather than in the markup.
function pcBarsFrom(src) {
    var rows = (src || []).filter(function (r) { return r.unitCostTotal > 0; }).slice(0, 12);
    // Scale to the larger of cost and price across the set, or a price bar would overflow its row
    // whenever a part is priced above its cost — which is most of them.
    var max = 0;
    rows.forEach(function (r) {
      var c = Number(r.unitCostTotal) || 0; if (c > max) max = c;
      var p = Number(r.unitPrice) || 0;     if (p > max) max = p;
    });
    if (max <= 0) max = 1;
    // Vertical mode reserves x < 50 for the value axis, so its plot area is narrower than horizontal's.
    var step = rows.length ? (344 / rows.length) : 344;
    var bw = step > 28 ? 24 : step * 0.7;
    return rows.map(function (r, i) {
      var v = Number(r.unitCostTotal) || 0;
      var frac = v / max;
      // HORIZONTAL bars get a hairline floor so the cheapest part is still visibly present — safe there,
      // because every bar is labelled with its own figure and cannot be misread. VERTICAL bars are left
      // geometrically true: they are measured against a value axis, and inflating a $1.20 part to 1.2%
      // of a $4,820 axis would be the axis telling a small lie. A near-zero bar simply shows as nothing
      // above the baseline, which is the honest reading, and it keeps its label and hover value.
      var hfrac = frac < 0.012 ? 0.012 : frac;
      // Price rides alongside. 0 means NO PRICE ON FILE, so it draws nothing rather than a zero-length
      // bar that would read as "priced at nothing".
      var pv = Number(r.unitPrice) || 0;
      var pfrac = pv > 0 ? pv / max : 0;
      var hpfrac = (pv > 0 && pfrac < 0.012) ? 0.012 : pfrac;
      var half = bw / 2 - 1;
      return {
        label: r.partNumber,
        desc: r.partDescription || "",
        value: v,
        price: pv,
        hasPrice: pv > 0,
        // HORIZONTAL, paired: 24-unit pitch, cost above price. viewBox is 300 tall for this mode.
        hy: i * 24 + 3, hpy: i * 24 + 13, hty: i * 24 + 11,
        hw: hfrac * 232, hpw: hpfrac * 232,
        // VERTICAL, paired: two bars sharing the slot, cost then price.
        vx: 50 + i * step + (step - bw) / 2, vw: half, vh: frac * 150, vy: 170 - frac * 150,
        vpx: 50 + i * step + (step - bw) / 2 + half + 2, vpw: half,
        vph: pfrac * 150, vpy: 170 - pfrac * 150,
        vcx: 50 + i * step + step / 2
      };
    });
}
function pcTicksFrom(bars) {
    if (!bars.length) return [];
    // The axis must span BOTH series or a price bar would run past the top gridline.
    var max = 0;
    bars.forEach(function (b) { if (b.value > max) max = b.value; if (b.price > max) max = b.price; });
    if (!(max > 0)) return [];
    var out = [];
    for (var i = 0; i <= 4; i++) {
      var v = max * i / 4;
      out.push({
        i: i,
        y: 170 - (i / 4) * 150,
        // Compact, because the axis gutter is 44 units wide: "$4.8k" fits where "$4,820.55" does not.
        label: v >= 1000 ? "$" + (v / 1000).toFixed(1) + "k" : "$" + v.toFixed(v >= 100 ? 0 : 2)
      });
    }
    return out;
}
// Price MINUS cost per part, worst first. Its own function because it needs its own sort, its own
// symmetric scale, and it must EXCLUDE parts with no price on file — a missing price is not a zero
// margin, and averaging it in would be the absent-vs-zero error all over again. The count of what was
// excluded is returned so the caption can say so rather than quietly showing a shorter list.
// Layout: part label 0..66 | gap value right-aligned at 126 | plot 134..392, zero at 263, span 129.
// Fixed zones mean a long number can never overflow the viewBox, which end-of-bar labels do.
function pcGapFrom(src) {
  var withCost = (src || []).filter(function (r) { return r.unitCostTotal > 0; });
  var priced = withCost.filter(function (r) { return r.unitPrice > 0; });
  var rows = priced.map(function (r) {
    var c = Number(r.unitCostTotal) || 0, p = Number(r.unitPrice) || 0;
    // pct is margin as % of PRICE — price > 0 is guaranteed here (unpriced parts were filtered out).
    return { label: r.partNumber, desc: r.partDescription || "", cost: c, price: p, gap: p - c, pct: (p - c) / p * 100 };
  }).sort(function (a, b) { return a.gap - b.gap; }).slice(0, 12);
  var maxAbs = 0;
  rows.forEach(function (r) { var a = r.gap < 0 ? -r.gap : r.gap; if (a > maxAbs) maxAbs = a; });
  if (!(maxAbs > 0)) maxAbs = 1;
  var ZERO = 263, SPAN = 129;
  return {
    skipped: withCost.length - priced.length,
    bars: rows.map(function (r, i) {
      var w = (r.gap < 0 ? -r.gap : r.gap) / maxAbs * SPAN;
      if (w < 1) w = 1;                       // a hairline, so a near-zero gap is still visible
      return {
        label: r.label, desc: r.desc, cost: r.cost, price: r.price, gap: r.gap, pct: r.pct, neg: r.gap < 0,
        y: i * 24 + 4, ty: i * 24 + 13,
        x: r.gap < 0 ? ZERO - w : ZERO, w: w
      };
    })
  };
}
function pcPieFrom(src) {
    var sums = { material: 0, labor: 0, burden: 0, service: 0 };
    (src || []).forEach(function (r) {
      if (!(r.unitCostTotal > 0)) return;
      sums.material += Number(r.unitMaterialCost) || 0;
      sums.labor    += Number(r.unitLaborCost) || 0;
      sums.burden   += Number(r.unitBurdenCost) || 0;
      sums.service  += Number(r.unitServiceCost) || 0;
    });
    var defs = [
      { key: "material", label: "Material", color: "#c0392b" },
      { key: "labor",    label: "Labor",    color: "#2980b9" },
      { key: "burden",   label: "Burden",   color: "#e67e22" },
      { key: "service",  label: "Service",  color: "#16a085" }
    ];
    var total = sums.material + sums.labor + sums.burden + sums.service;
    if (!(total > 0)) return [];
    var cx = 108, cy = 122, rad = 84, a = -Math.PI / 2, out = [];
    defs.forEach(function (d) {
      var v = sums[d.key];
      if (!(v > 0)) return;
      var frac = v / total;
      var a1 = a + frac * Math.PI * 2;
      var x0 = cx + rad * Math.cos(a),  y0 = cy + rad * Math.sin(a);
      var x1 = cx + rad * Math.cos(a1), y1 = cy + rad * Math.sin(a1);
      // A component that accounts for everything would draw start == end, i.e. an invisible slice.
      // Two half-arcs make the full circle instead.
      var path = frac > 0.9999
        ? "M " + (cx - rad) + " " + cy + " a " + rad + " " + rad + " 0 1 0 " + (rad * 2) + " 0 a " + rad + " " + rad + " 0 1 0 " + (-rad * 2) + " 0"
        : "M " + cx + " " + cy + " L " + x0.toFixed(2) + " " + y0.toFixed(2)
          + " A " + rad + " " + rad + " 0 " + ((a1 - a) > Math.PI ? 1 : 0) + " 1 " + x1.toFixed(2) + " " + y1.toFixed(2) + " Z";
      out.push({ key: d.key, label: d.label, color: d.color, pct: frac * 100, d: path, ly: 0 });
      a = a1;
    });
    out.forEach(function (s, i) { s.ly = 78 + i * 26; });
    return out;
}
import * as XLSX from "xlsx";   // hero result-card → real .xlsx workbook (summary sheet + one sheet per group)

// ---- Parts Sold at a Loss: chart geometry ------------------------------------------------------
// Same discipline as the pc* functions above: every pixel precomputed (a Vue 2 template has no Math
// in scope), pure functions of the rows so the card, the print doc and the Excel PNGs cannot drift.
function plPartsAgg(rows) {
  var by = {};
  (rows || []).forEach(function (r) {
    var k = r.partNumber || "(none)";
    var o = by[k] || (by[k] = { label: k, desc: r.partDescription || "", loss: 0, lines: 0, qty: 0 });
    o.loss += Number(r.lineLoss) || 0;
    o.lines += 1;
    o.qty += Number(r.qty) || 0;
  });
  // Loss is NEGATIVE, so ascending = biggest loss first.
  return Object.keys(by).map(function (k) { return by[k]; })
    .sort(function (a, b) { return a.loss - b.loss; });
}
// HORIZONTAL bars: the 12 parts carrying the biggest total loss. Single series (|loss|), 24-unit
// pitch — the same layout grammar as pcBarsFrom's horizontal mode. viewBox is 300 tall for this mode.
function plLossBarsFrom(rows) {
  var parts = plPartsAgg(rows).slice(0, 12);
  var max = 0;
  parts.forEach(function (p) { var a = p.loss < 0 ? -p.loss : p.loss; if (a > max) max = a; });
  if (!(max > 0)) return [];
  return parts.map(function (p, i) {
    var a = p.loss < 0 ? -p.loss : p.loss;
    var frac = a / max;
    if (frac < 0.012) frac = 0.012;   // hairline floor — safe here, every bar carries its own figure
    return {
      label: p.label, desc: p.desc, loss: p.loss, lines: p.lines, qty: p.qty,
      hy: i * 24 + 4, hty: i * 24 + 12, hw: frac * 232
    };
  });
}
// VERTICAL bars: loss by month. The server already applied the window; more than 24 months collapses
// to the NEWEST 24 and reports how many were clipped, so the caption can say so.
function plMonthAgg(rows) {
  var by = {};
  (rows || []).forEach(function (r) {
    var ym = String(r.transactionDate || "").slice(0, 7);
    if (ym.length !== 7) return;
    var o = by[ym] || (by[ym] = { ym: ym, loss: 0, lines: 0 });
    o.loss += Number(r.lineLoss) || 0;
    o.lines += 1;
  });
  var months = Object.keys(by).sort().map(function (k) { return by[k]; });
  var clipped = months.length > 24 ? months.length - 24 : 0;
  if (clipped) months = months.slice(months.length - 24);
  return { months: months, clipped: clipped };
}
function plMonthBarsFrom(rows) {
  var agg = plMonthAgg(rows);
  var months = agg.months;
  var max = 0;
  months.forEach(function (m) { var a = m.loss < 0 ? -m.loss : m.loss; if (a > max) max = a; });
  if (!(max > 0)) return { bars: [], clipped: agg.clipped, max: 0 };
  var step = months.length ? (344 / months.length) : 344;
  var bw = step > 28 ? 24 : step * 0.7;
  return {
    clipped: agg.clipped,
    max: max,
    bars: months.map(function (m, i) {
      var a = m.loss < 0 ? -m.loss : m.loss;
      // Geometrically true against the value axis — no hairline floor in vertical mode (the same
      // honesty rule pcBarsFrom documents for its vertical bars).
      var frac = a / max;
      return {
        label: m.ym, loss: m.loss, lines: m.lines,
        vx: 50 + i * step + (step - bw) / 2, vw: bw, vh: frac * 150, vy: 170 - frac * 150,
        vcx: 50 + i * step + step / 2
      };
    })
  };
}
function plTicksFrom(max) {
  if (!(max > 0)) return [];
  var out = [];
  for (var i = 0; i <= 4; i++) {
    var v = max * i / 4;
    out.push({
      i: i,
      y: 170 - (i / 4) * 150,
      label: v >= 1000 ? "$" + (v / 1000).toFixed(1) + "k" : "$" + v.toFixed(v >= 100 ? 0 : 2)
    });
  }
  return out;
}
// PIE: which customers the loss went to — top 7 + Other, share of the summed |loss|. Same centre,
// radius and legend column as pcPieFrom so the two cards' pies read as siblings.
var PL_PIE_COLORS = ["#c0392b", "#2980b9", "#e67e22", "#16a085", "#8e44ad", "#d35400", "#2c3e50", "#7f8c8d"];
function plPieFrom(rows) {
  var by = {};
  (rows || []).forEach(function (r) {
    var a = Number(r.lineLoss) || 0;
    if (a >= 0) return;   // the pie is about LOSS share; a non-negative line adds nothing
    var k = r.customerId || "(none)";
    by[k] = (by[k] || 0) + (-a);
  });
  var list = Object.keys(by).map(function (k) { return { label: k, v: by[k] }; })
    .sort(function (a, b) { return b.v - a.v; });
  var total = list.reduce(function (s, x) { return s + x.v; }, 0);
  if (!(total > 0)) return [];
  var top = list.slice(0, 7);
  var other = list.slice(7).reduce(function (s, x) { return s + x.v; }, 0);
  if (other > 0) top.push({ label: "Other", v: other });
  var cx = 108, cy = 122, rad = 84, a0 = -Math.PI / 2, out = [];
  top.forEach(function (d, i) {
    var frac = d.v / total;
    if (!(frac > 0)) return;
    var a1 = a0 + frac * Math.PI * 2;
    var x0 = cx + rad * Math.cos(a0), y0 = cy + rad * Math.sin(a0);
    var x1 = cx + rad * Math.cos(a1), y1 = cy + rad * Math.sin(a1);
    // A single customer carrying everything would draw start == end — an invisible slice. Two
    // half-arcs make the full circle instead (same guard as pcPieFrom).
    var path = frac > 0.9999
      ? "M " + (cx - rad) + " " + cy + " a " + rad + " " + rad + " 0 1 0 " + (rad * 2) + " 0 a " + rad + " " + rad + " 0 1 0 " + (-rad * 2) + " 0"
      : "M " + cx + " " + cy + " L " + x0.toFixed(2) + " " + y0.toFixed(2)
        + " A " + rad + " " + rad + " 0 " + ((a1 - a0) > Math.PI ? 1 : 0) + " 1 " + x1.toFixed(2) + " " + y1.toFixed(2) + " Z";
    out.push({ label: d.label, color: PL_PIE_COLORS[i % PL_PIE_COLORS.length], pct: frac * 100, v: d.v, d: path, ly: 0 });
    a0 = a1;
  });
  out.forEach(function (p, i) { p.ly = 34 + i * 20; });   // legend rows down the right side
  return out;
}

// Postal -> full state name, for the map's flag panel (the hovered state arrives with its name from the map;
// this resolves the name when the flag falls back to a single SELECTED state, which only carries its postal).
const US_STATE_NAMES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", CO: "Colorado", CT: "Connecticut",
  DE: "Delaware", DC: "District of Columbia", FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri", MT: "Montana",
  NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York",
  NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah",
  VT: "Vermont", VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

// Country name/code -> ISO alpha-2 for the world-map hover flag (flagcdn). Mirrors the map in CustomersById
// (the Customer-ID flag column); keep the two in sync if a new country shows up in the data.
const COUNTRY_ISO = {
  "UNITED STATES": "us", "UNITED STATES OF AMERICA": "us", "USA": "us", "US": "us", "U.S.A.": "us", "U.S.": "us",
  "CANADA": "ca", "CAN": "ca", "MEXICO": "mx", "MEX": "mx",
  "BRAZIL": "br", "BRASIL": "br", "ARGENTINA": "ar", "CHILE": "cl", "PERU": "pe", "COLOMBIA": "co",
  "BOLIVIA": "bo", "ECUADOR": "ec", "VENEZUELA": "ve", "URUGUAY": "uy", "PARAGUAY": "py",
  "AUSTRALIA": "au", "NEW ZEALAND": "nz",
  "UNITED KINGDOM": "gb", "UK": "gb", "ENGLAND": "gb", "IRELAND": "ie", "FRANCE": "fr", "GERMANY": "de",
  "SPAIN": "es", "PORTUGAL": "pt", "ITALY": "it", "GREECE": "gr", "CYPRUS": "cy", "NETHERLANDS": "nl",
  "BELGIUM": "be", "SWITZERLAND": "ch", "AUSTRIA": "at", "SWEDEN": "se", "NORWAY": "no", "DENMARK": "dk",
  "FINLAND": "fi", "POLAND": "pl", "TURKEY": "tr", "RUSSIA": "ru",
  "INDIA": "in", "CHINA": "cn", "JAPAN": "jp", "SOUTH KOREA": "kr", "KOREA": "kr", "INDONESIA": "id",
  "THAILAND": "th", "VIETNAM": "vn", "PHILIPPINES": "ph", "MALAYSIA": "my", "SINGAPORE": "sg",
  "SOUTH AFRICA": "za", "EGYPT": "eg", "NIGERIA": "ng", "KENYA": "ke", "GHANA": "gh", "MOROCCO": "ma",
  "SAUDI ARABIA": "sa", "UNITED ARAB EMIRATES": "ae", "UAE": "ae", "ISRAEL": "il", "QATAR": "qa", "KUWAIT": "kw",
  "GUATEMALA": "gt", "HONDURAS": "hn", "COSTA RICA": "cr", "PANAMA": "pa", "NICARAGUA": "ni",
  "EL SALVADOR": "sv", "DOMINICAN REPUBLIC": "do", "JAMAICA": "jm", "TRINIDAD AND TOBAGO": "tt", "CUBA": "cu",
};

// ===== SHARED STANDARD REPORTS hosted in THIS component, keyed by activeLayout id (2026-08-05) =====
// Before this, the picker rows, the docked-preset gates and the publish button all hardcoded
// "default" / "shared2" / "shared3" in ~15 separate places, so a new report meant editing every one
// and only Sales Insights ever got docked layouts. Everything now derives from this list.
//   id      = activeLayout value              roleKey = the rpt_RoleDefaultLayout row it publishes to
//   name    = built-in label, overridden by that row's LayoutJson.name (see reportNameOf / renameReport)
//   access  = the canSee* computed that gates visibility ("" = everyone with report access)
// ⚠ "Manufacturing Insights" here is the shared LAYOUT in this host — NOT the standalone
// /reports/manufacturing-insights page (a separate component). Same words, different artifact.
//   listed  = shown as a layout-controlled row in the Reports drawer. shared2 is listed:false because the
//             name "Manufacturing Insights" belongs to the STANDALONE /reports/manufacturing-insights page
//             (see LAUNCHER_REPORTS) — listing both produced two identical names, one of which nothing on
//             the Reports dashboard ever routes to. The layout itself is NOT deleted: ?report=shared2 still
//             reaches it and its dock/publish code paths are unchanged, so nothing is lost.
const SHARED_REPORTS = [
  { id: "default", roleKey: "ALL",     name: "Sales Insights",         access: "",                   listed: true },
  { id: "shared2", roleKey: "SHARED2", name: "Manufacturing Insights", access: "canSeeManufacturing", listed: false },
  { id: "shared3", roleKey: "SHARED3", name: "Accounting",             access: "canSeeAccounting",   listed: true },
];

// Reports that live in their OWN page rather than as a layout in this host. They appear in the drawer as
// LAUNCHERS (click = navigate) so it lists every report on the Reports dashboard, but they get no layout
// controls: these pages are hand-built with a fixed section order — no card grid, nothing draggable and no
// layout persistence — so there is genuinely nothing to arrange. Keys match Reports_Dashboard.vue's REPORTS
// so access is gated by the SAME authorizedReports list the dashboard tiles use.
const LAUNCHER_REPORTS = [
  { key: "manufacturing-insights", name: "Manufacturing Insights", path: "/reports/manufacturing-insights" },
  { key: "foundry-insights",       name: "Foundry Insights",       path: "/reports/foundry-insights" },
  { key: "work-order-insights",    name: "Work Order Insights",    path: "/reports/work-order-insights" },
];

// Default dashboard arrangement for the draggable/resizable Layout-1 pads. The grid is 12 columns wide with
// 25px rows, so cards can be placed/resized very finely (1/12 width, half-row height). The default keeps the
// original 3-up look: each "column" spans 4 of the 12 grid columns (x in 0/4/8, w:4). Full-width cards span 12.
// Saved layouts authored on the older 3-col or 6-col grids are auto-normalized on load (see normalizeGridTo24).
const DASH_DEFAULT = [
  // Default report layout for EVERY user — captured from the Master's arranged sandbox layout (2026-06-26).
  // Filters + Favorites card at the very top, full width. It's a draggable/resizable grid card like the rest
  // (on a custom layout) so a Master can shrink it and drag e.g. the Map up beside it. h≈9 fits favorites +
  // the dropdown row + the date row + a wrapped chip row.
  { i: "filters", x: 0, y: 0, w: 20, h: 9 },
  { i: "favorites", x: 20, y: 0, w: 4, h: 9 },
  // 24-column grid (columns 0–8 / 8–16 / 16–24). Left column: KPIs stacked, then Saved Carts.
  { i: "kpiSales", x: 0, y: 9, w: 8, h: 4 },
  { i: "kpiOrders", x: 0, y: 13, w: 8, h: 4 },
  { i: "kpiAvg", x: 0, y: 17, w: 8, h: 4 },
  { i: "savedCarts", x: 0, y: 21, w: 8, h: 4 },
  // Middle column (x:8–16): Sales by Territory over Customer ID(s).
  { i: "territory", x: 8, y: 9, w: 8, h: 8 },
  { i: "customers", x: 8, y: 17, w: 8, h: 8 },
  // Right column (x:16–24): Territory Map over Part(s) Ordered.
  { i: "map", x: 16, y: 9, w: 8, h: 8 },
  { i: "parts", x: 16, y: 17, w: 8, h: 8 },
  // Full-width "Orders In Process" — recently-placed submitted orders not yet complete; 👁 opens the order detail.
  // Kept SHORT (h:3 ≈ header + a row + a couple line-breaks): in-process orders are few (90-day window), so a tall
  // card was mostly dead space. The list scrolls (.ci-oip overflow:auto) in the rare case there are many.
  { i: "ordersInProcess", x: 0, y: 25, w: 24, h: 3 },
  // Full-width Sales Reps leaderboard — ranked bars; click a rep to filter that rep + its mapped territory.
  { i: "salesReps", x: 0, y: 33, w: 24, h: 8 },
  // Full-width Machine & Part Codes — one searchable card spanning Category / Prefix / Description / Part-Desc facets.
  { i: "codes", x: 0, y: 41, w: 24, h: 12 },
  // Costing for Parts — SENSITIVE (Master + All Insights). ensureBuiltinCards SKIPS this entry for
  // anyone else, so it never enters their layout at all; see canSeeCost.
  { i: "partCosts", x: 0, y: 53, w: 24, h: 12 },
  // Full-width Customer Order Details. NOTE: the "orders" (Order Details) card is intentionally NOT in the default
  // (the Master hid it). To show it by default again, add e.g. { i: "orders", x: 0, y: 85, w: 24, h: 20 } here.
  { i: "details", x: 0, y: 53, w: 24, h: 32 },
  // Email Insights (Sales-rep opt-in): recent customer emails from the rep's linked M365 inbox + AI part match.
  { i: "emailInsights", x: 0, y: 85, w: 24, h: 14 },
];
// Human titles shown on each pad's drag handle.
const DASH_TITLES = {
  kpiSales: "Total Sales", kpiOrders: "Orders", kpiAvg: "Avg / Order", savedCarts: "Saved Carts",
  map: "Territory Map", customers: "Customer ID(s)", parts: "Part(s) Ordered", territory: "Sales by Territory",
  details: "Customer Order Details", orders: "Order Details", salesReps: "Sales Reps", codes: "Machine & Part Codes",
  ask: "Ask About Your Data",
  favorites: "Favorites",
  emailInsights: "Email Insights",
  ordersInProcess: "Orders In Process",
  partCosts: "Costing for Parts",
  partLoss: "Parts Sold at a Loss",
};

// The ten Azure NEURAL voices offered for the mobile hero talk-back — long-stable, reliable GA voices (the same
// family the Guides narration uses). MUST match the backend whitelist (ReportController.HeroTtsVoices); anything
// else is coerced server-side. Deliberately NO browser-voice fallback anywhere in this flow (user decision).
const TTS_VOICES = [
  { id: "en-US-AriaNeural", label: "Aria (female)" },
  { id: "en-US-JennyNeural", label: "Jenny (female)" },
  { id: "en-US-GuyNeural", label: "Guy (male)" },
  { id: "en-US-DavisNeural", label: "Davis (male)" },
  { id: "en-US-JaneNeural", label: "Jane (female)" },
  { id: "en-US-JasonNeural", label: "Jason (male)" },
  { id: "en-US-SaraNeural", label: "Sara (female)" },
  { id: "en-US-TonyNeural", label: "Tony (male)" },
  { id: "en-US-NancyNeural", label: "Nancy (female)" },
  { id: "en-US-AmberNeural", label: "Amber (female)" },
];

// Mobile stack (viewport < 992px): the SAME cards reflowed into ONE FULL-WIDTH column, in the user's order. Fed to
// the grid instead of dashLayout on mobile (FIXED, never persisted; desktop + saved layouts untouched). On mobile a
// CSS reflow (@media, in <style>) overrides vue-grid-layout so every card FLOWS full-width + AUTO-HEIGHT (sizes to
// its content — no white space, no clipping; count cards get full width so their $ values don't squish). So the
// w/h/x/y below effectively just set the ORDER; only the array order matters once the CSS reflow is active.
// ── Touch long-press drag tuning (favorites bar) ────────────────────────────────────────────────
// 500ms is the platform long-press on both targets we care about (Android's
// ViewConfiguration.getLongPressTimeout() and iOS's callout delay are each ~500ms), so the gesture
// is already trained. A 2s hold reads as "broken" — the finger lifts at ~700ms and nothing happens;
// the .is-pressing swell (same 500ms) is what answers "did it take?", not a longer timer.
// 8px is Chrome's own touch slop: below it a resting finger's tremor aborts a legitimate press,
// above it the browser has already committed the gesture to a scroll and a later preventDefault()
// is ignored (touchmove arrives with cancelable:false).
const FAV_PRESS_MS = 500;
const FAV_SLOP_PX = 8;
// Hold the dragged star OVER a target this long → the drop flips from REORDER to GROUP. This is the
// iOS "drop an icon on an icon to make a folder" gesture; see _favModeFromEvent for why three-zone
// hit testing can't do grouping on a ~30px chip.
const FAV_DWELL_MS = 600;

const MOBILE_LAYOUT = [
  // 'ask' + 'favorites' are NOT in the mobile stack: the hero Ask renders above the grid for
  // every viewport, and on mobile a favorites strip renders directly ABOVE the hero (see
  // .ci-fav-bar--hero in the template) — so neither needs a stacked card.
  { i: "filters",    x: 0, y: 9,   w: 24, h: 14 },
  { i: "emailInsights", x: 0, y: 29, w: 24, h: 16 },
  { i: "kpiSales",   x: 0, y: 23,  w: 24, h: 5 },
  { i: "kpiOrders",  x: 0, y: 28,  w: 24, h: 5 },
  { i: "kpiAvg",     x: 0, y: 33,  w: 24, h: 5 },
  { i: "savedCarts", x: 0, y: 38,  w: 24, h: 5 },
  { i: "ordersInProcess", x: 0, y: 43, w: 24, h: 3 },
  { i: "map",        x: 0, y: 51,  w: 24, h: 13 },
  { i: "territory",  x: 0, y: 56,  w: 24, h: 10 },
  { i: "customers",  x: 0, y: 66,  w: 24, h: 13 },
  { i: "salesReps",  x: 0, y: 79,  w: 24, h: 13 },
  { i: "parts",      x: 0, y: 92,  w: 24, h: 13 },
  { i: "codes",      x: 0, y: 105, w: 24, h: 13 },
  { i: "details",    x: 0, y: 118, w: 24, h: 24 },
];

// When a Master edits a built-in card we seed the editor with a TOKEN template — it shows the live
// VALUES as {tokens} (interpolated by renderTokens at render time), NOT frozen output, so the
// converted card keeps updating with the filters. KPI cards map 1:1; collection cards (lists / tables /
// map) get a token SUMMARY starting point (a full live table is better built as a "Data from a table" card).
function ciGenericTemplate(title) {
  return '<div style="padding:12px 14px;">' +
    '<h3 style="margin:0 0 8px;color:#9e2436;font-size:15px;">' + title + '</h3>' +
    '<p style="margin:0 0 4px;font-size:13px;color:#444;">Sales <b>{totalSales}</b> &middot; Orders <b>{orderCount}</b> &middot; Avg <b>{avgOrder}</b></p>' +
    '<p style="margin:0;font-size:12px;color:#777;">Top customer {topCustomer} &middot; Top territory {topTerritory} &middot; {dateFrom}&ndash;{dateTo}</p>' +
    '</div>';
}
// gid → category for the "From a Layout 1 card" picker tabs.
const CATEGORY_MAP = {
  kpiSales: "KPIs", kpiOrders: "KPIs", kpiAvg: "KPIs", savedCarts: "KPIs",
  territory: "Charts & Maps", salesReps: "Charts & Maps", customers: "Charts & Maps", parts: "Charts & Maps", map: "Charts & Maps",
  codes: "Tables", details: "Tables", orders: "Tables", ordersInProcess: "Tables", partCosts: "Tables", partLoss: "Tables",
  emailInsights: "Insights", ask: "Insights",
};
const BUILTIN_TEMPLATES = {
  kpiSales: '<div class="ci-kpi"><div class="ci-kpi-label">Total Sales</div><div class="ci-kpi-value">{totalSales}</div><div class="ci-kpi-foot">Invoiced {invoicedSales} &middot; Not {notInvoicedSales}</div></div>',
  kpiOrders: '<div class="ci-kpi"><div class="ci-kpi-label">Orders</div><div class="ci-kpi-value">{orderCount}</div><div class="ci-kpi-foot">Invoiced {invoicedOrders} &middot; Not {openOrders}</div></div>',
  kpiAvg: '<div class="ci-kpi"><div class="ci-kpi-label">Avg / Order</div><div class="ci-kpi-value">{avgOrder}</div><div class="ci-kpi-foot">total sales &divide; orders</div></div>',
  savedCarts: '<div class="ci-kpi ci-kpi-savedcarts"><div class="ci-savedcart-fig"><div class="ci-kpi-label">Saved Orders</div><div class="ci-kpi-value">{savedOrders}</div></div><div class="ci-savedcart-fig"><div class="ci-kpi-label">Saved Quotes</div><div class="ci-kpi-value">{savedQuotes}</div></div><div class="ci-kpi-foot">for the selected customer(s)</div></div>',
};

// US state postal → display name, for the filter "story" sentence.
const STATE_NAMES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", CO: "Colorado",
  CT: "Connecticut", DE: "Delaware", DC: "District of Columbia", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky",
  LA: "Louisiana", ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota",
  MS: "Mississippi", MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire",
  NJ: "New Jersey", NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota",
  OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

export default {
  name: "CustomerInsights",
  components: { GridLayout, GridItem, TerritoryMap, WorldMap, CustomerOrderDetails, PartsOrdered, CustomersById, FilterSearch, FilterSlicer, ReportCardEditor, OrderLinesTable, EmailInsights, OrderDetails },
  data() {
    const now = new Date();
    const ytd = now.getFullYear() + "-01-01";
    const today = now.toISOString().slice(0, 10);
    return {
      // Per-rep saved filter sets ("favorites"), persisted in the same per-user dashboard blob as the layouts
      // (so they follow the rep across devices, separate from the Master's shared Layout 1). A favorite snapshots
      // the filter CHIPS (not the date range, which the rep adjusts freely). Favorites can be dragged together
      // into named groups.
      favorites: [],          // [{ id, name, filters:{snapshot}, groupId }]
      emailLinked: false,     // Email Insights opt-in (persisted in the per-user report-dashboard blob)
      favoriteGroups: [],     // [{ id, name }]
      favDragId: "",          // id of the favorite currently being dragged (drag-to-group AND drag-to-reorder)
      favDropIdx: -1,         // index of the loose favorite currently hovered during a drag (-1 = none)
      favDropMode: "",        // what releasing NOW would do: 'before' | 'after' (reorder) | 'group'
      favDropGroupId: "",     // id of the GROUP chip currently hovered during a drag ("" = none)
      favPressId: "",         // TOUCH: id of the star whose long-press timer is armed (drives the .is-pressing swell)
      favTouchDrag: false,    // TOUCH: a long-press drag is live (drives .is-favtouch + pointer-events:none on the source)
      favPopId: "",           // id of the favorite/group whose hover popover is open
      favPopKind: "",         // "fav" | "group"
      favDraft: null,         // staged popover edits ({name[, filters]}); non-null => unsaved (shows ✓ Save + pins the popover open)
      favRenaming: false,     // title is in inline-rename mode
      favPopPos: { top: 0, left: 0 },   // viewport coords for the body-appended popover (positioned at the hovered star)
      chipHoverOpen: false,             // shared active-filters popover, opened by hovering ANY card's filter chip
      chipHoverKey: "",                 // "key|val" of the hovered card chip → highlighted + sorted to the top of the popover
      chipHoverPos: { top: 0, left: 0 },// viewport coords for the body-appended chip popover
      kpiMathOpen: false,               // KPI "% vs prior period" show-your-work breakdown popover
      kpiMathKind: "",                  // which KPI (sales|orders|avg) the popover shows (for the click-toggle)
      kpiMathBody: "",                  // its HTML (rebuilt when a KPI's "?" is clicked)
      kpiMathPos: { top: 0, left: 0 },
      terrIns: { open: false, body: "", top: 0, left: 0, key: "" },   // 💡 "who rolls up into this territory + why" popover (Sales by Territory card)
      // Customer notation popover (📝 in the Customer ID(s) card): body-appended, click-toggled.
      notePop: { open: false, id: "", name: "", text: "", top: 0, left: 0 },
      // Customer specifications popover (📋) + AI sales-perspective popover (💡), siblings to the 📝 popover.
      specPop: { open: false, id: "", name: "", text: "", top: 0, left: 0 },
      // Hero Notations/Specifications HOVER popover — its own state (separate from the click-toggled notePop/
      // specPop so the Customer-card flow is untouched) but reuses the .ci-notepop styling. kind = "note"|"spec".
      heroNote: { open: false, kind: "", name: "", text: "", top: 0, left: 0 },
      perspPop: { open: false, id: "", name: "", headline: "", body: "", actions: [], busy: false, top: 0, left: 0 },
      codWhyPop: { open: false, name: "", currentRep: "", currentTerr: "", attribution: [], history: [], top: 0, left: 0 },   // 💡 rep history (Customer Order Details)
      unassignedPop: { open: false, busy: false, showAll: false, top: 0, left: 0, data: null },   // 💡 "why unassigned" diagnostic (Sales Reps card)
      unEmail: { open: false, busy: false, sent: false, note: "", error: "", target: "it" },   // unassigned-orders email modal; target 'parts' (VISUAL rep) or 'it' (territory map)
      // ===== Scheduled email digests for a favorite / group (📧 Email) =====
      notifications: [],      // the rep's saved schedules (rpt_FavoriteNotification rows)
      notifyOpen: false,      // schedule modal open
      notifyItem: null,       // the favorite/group being scheduled ({ id, name, filters? })
      notifyKind: "",         // "favorite" | "group"
      notifyExisting: null,   // the existing schedule row for this item (if any)
      notifyBusy: "",         // "" | "save" | "test" | "del"
      notifyMsg: "",          // status line in the modal
      notifyForm: { frequency: "weekly", dayOfWeek: 1, dayOfMonth: 1, hourLocal: 7, recipients: "", active: false },
      weekDays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      // ===== AI "Ask about your data" — Q&A over the SAME scoped dashboard summary, with an optional
      // AI-proposed filter selection the rep applies with one click (propose-then-apply). =====
      aiQuestion: "",        // the rep's free-text question
      aiAskBusy: false,      // ai-ask request in flight
      // Quote attachments for the hero chat (user 2026-08-11). Held in memory only — the
      // backend never stores them either, by design.
      heroFiles: [],         // [{ name, sizeLabel, icon, thumb, dataUri, size }]
      heroFileNotes: [],     // per-file "read / could not read" notes from the last answer
      aiAnswer: "",          // the AI's plain-English answer (bullet-parsed like the narrative)
      // ===== QUOTE REVIEW COMPARISON TABLE =====
      // One row per quote line, from the server's resolved arithmetic: quoted part, quoted price, the
      // average of their past orders, and margin. Expanding a row shows the past orders behind that
      // average, because an average with no history under it is just another number to take on trust.
      qrLines: [],
      qrOpen: {},                 // line number -> expanded
      qrCostIncluded: false,      // margin is only meaningful for callers with cost access
      qrRead: null,               // { linesFound, truncated }
      qrPrintOn: false,           // the presentation sheet is armed for the next print
      // 🖨 row-picker: rows:[{line,label,qty,price,readable,checked}]. Rebuilt from the answer each time
      // the dialog opens, so it can never print a selection made against a previous quote.
      qrPrintPick: { open: false, mode: "print", rows: [] },
      qrPrintLogoFailed: false,   // the letterhead image genuinely 404'd — fall back to the wordmark
      // 🔍 per-line View/Fix, and 📄 the document viewer behind it.
      qrFix: { open: false, line: 0, raw: "", reason: "", note: "", candidates: [],
               part: "", qty: "", amount: "", isExt: false, busy: false, error: "" },
      qrDoc: { open: false, tab: "text", find: "" },
      qrFoldOpen: false,   // the folded component rows are listed on demand, not by default
      // Lines the rep took out by hand: { at: original index, line: the row }. Kept so the removal is
      // reversible and so the banner can name what went.
      qrRemoved: [],
      // Attaching a file called "…Quote…" almost always means one thing. Rather than make the rep type
      // the comparison question every time, offer it — and offer a way OUT that still leaves the file
      // attached, because sometimes they want to ask something else entirely about the same document.
      // busy: the peek is in flight. peek: what the server found IN the document — the customer and
      // the priced-line count — so the rep confirms who this is for before anything is compared.
      // step 1 = what to do with the attachment, step 2 = how far back to compare. pending holds the
      // question step 1 composed, because the ask does not run until the window is chosen.
      quotePrompt: { open: false, name: "", busy: false, peek: null, dataUri: null, step: 1,
                     pending: { mode: "", question: "" } },
      // The window the comparison runs over. Reset to all-history by every new attachment, so a narrow
      // choice made for one quote can never silently apply to the next one.
      quoteHistory: { from: "", kind: "", label: "all history" },
      // Collapsed = kept but hidden. A long quote review is tall enough to follow you down
      // the page, so the ⌃ handle on the bottom border tucks it away without discarding it.
      aiAnswerCollapsed: false,
      aiAskError: "",        // error message
      aiProposed: null,      // validated filter spec the AI proposes ({salesRep?,territory?,country?,…}); null = none
      aiProposedNote: "",    // one-line description of the proposed view
      aiApplied: false,      // a fresh ask AUTO-applied its filters → show the ✓ synopsis banner (with ↩ Undo)
      aiTable: null,         // optional structured RESULT TABLE {title, columns[], rows[][]} → mini card + Excel export
      aiExcelModal: { open: false, groups: [] },   // ⬇ Excel group-picker: {open, groups:[{key,checked}]} — all checked by default
      aiPresentModal: { open: false, groups: [] }, // 📊 Presentation group-picker — same shape/behaviour as the Excel one (user 2026-08-02)
      aiPresentFiltered: false,  // true while building a SUBSET deck: keeps the all-groups result card out of the dashboard screenshot
      aiTableOpen: {},       // grouped result card: { groupIndex: true } = expanded (all collapsed by default)
      aiSort: null,          // result card sort: { col, dir:1|-1 } (click a header: asc → desc → off)
      aiAskCtx: null,        // { question, table } the current insights/presentation were built to answer (card buttons)
      aiInsightsAskKey: "",  // the question string the current aiInsights reflect ("" = filters-only panel generate)
      qaHistory: [],         // [{ id, q, a, applied, note, ts }] — rides in the per-user dashboard blob
      filters: {
        // customer/part/territory/state + salesRep/status/machineCode* are MULTI-SELECT (arrays -> backend IN()).
        // Status DEFAULTS to real orders (CLOSED/HOLD/RELEASED) so CANCELLED/VOID + blank-status rows never count
        // unless deliberately included. resetFilters() ("Clear all") restores this same trio — keep the two in sync.
        from: ytd, to: today, dateBasis: "order", invoiced: "all",
        salesRep: [], territory: [], status: ["CLOSED", "HOLD", "RELEASED"],
        machineCodeCategory: [], machineCodePrefix: [], machineCodeDescription: [], partDescription: [],
        customer: [], orderNo: "", part: [], state: [], country: [],
      },
      // "Attribute by" rep selector (privileged only) — kept OUT of `filters` so it doesn't render as a chip or
      // count toward active filters; it's an attribution LENS, not a data filter. Default Owned By / Current =
      // the standard synced attribution (backend runs the base table, no re-base). Not persisted → resets to the
      // default each session. See applyRepBasis()/setRepView() + buildParams().
      repBasis: "owned",   // owned (CUST_SALES_REP) | ordered (CO_SALES_REP) | sold (INVOICE_SALES_REP)
      repView: "current",  // current (remap departed reps to successor) | original (raw historical rep)
      openChipGroupKey: null,   // which active-filter group's unselect dropdown is open in the rail (one at a time)
      repBasisHelp: { open: false, top: 0, left: 0 },   // hover popup explaining the "Attribute by" options (body-appended)
      opts: { salesReps: [], territories: [], statuses: [], machineCodeCategories: [], machineCodePrefixes: [] },
      // Hover explanations for the Order Status slicer (keyed by the exact OrderStatus value).
      statusTips: {
        "CANCELLED/VOID": "Order stopped before shipment. Useful for spotting trends, price loss to competitors, or a steady customer suddenly going quiet, so you know when to follow up.",
        "CLOSED": "Orders that have shipped out of Visual and accounting is either preparing an invoice or has already sent it to the customer.",
        "FIRMED": "Confirmed orders are ready for release to fulfillment.",
        "HOLD": "Stationary orders that the system cannot process for fulfillment, and are used while Customer Service reviews the order or has open questions.",
        "RELEASED": "In-process orders to be fulfilled by stock or manufacturing. The system treats these as active orders.",
      },
      kpis: {},
      kpisPrior: {},   // prior equal-length window (KPI card deltas)
      loadError: false, // last /customer-insights request errored (distinct from a genuine no-match)
      loadErrorMsg: "", // the backend's specific reason (e.g. a timeout on too-broad a range), if any
      byCustomer: [],
      byCustomerTerritory: [], // per-customer × territory order counts (segments the Customer ID(s) bars by territory)
      byPart: [],
      // PART(S) ORDERED search results from the server. The card is fed the top 500 parts by quantity,
      // so its own box could only ever search those -- see poPartSearch.
      poSearch: { term: "", rows: [], busy: false, capped: false, failed: false, descriptionSearched: true },
      byTerritory: [],
      byState: [],
      bySalesRep: [],
      byPartRep: [],
      byCountry: [],
      byCodes: [],
      byCodeRep: [],
      codesBusy: false,   // Machine & Part Codes load lazily via /customer-insights/codes (separate from the main batch)
      // ---- Parts Sold at a Loss (SENSITIVE — Master + Accounting/All Insights; see canSeeLoss) ----
      // The Accounting report card. Rows come from GET /report/accounting/part-loss (dbo.rpt_PartLoss,
      // synced nightly from VMFG INVENTORY_TRANS): one row per customer shipment line whose net price
      // was at-or-below the ACTUAL cost booked on the shipment date.
      plRows: [],                // current tab's rows (server-capped; plTruncated says if more exist)
      plCats: [],                // [{ category, rows, totalLoss }] for the SAME window — feeds the tabs
      plCat: "BelowCost",        // selected tab. BelowCost is the comptroller's actual question;
                                 // NoCharge (82% of qualifying rows) and Credit are kept separate on purpose.
      plBusy: false,
      plAvailable: true,         // false = rpt_PartLoss missing on this DB (available:false from the server)
      plQueryError: false,
      plDetail: "",
      plTruncated: false,
      plSort: "transactionDate", // client-side column sort of the fetched rows
      plDir: "desc",
      // The Costing-for-Parts toolset, cloned onto this card (user request 2026-07-30). One deliberate
      // difference: committed chips reload the SERVER (loadPartLoss sends them) — these are line-level
      // rows capped at 500, so a local-only chip filter would miss every match beyond the cap. The text
      // still being typed filters locally, so the box stays live between keystrokes.
      plSearch: "",
      plTerms: [],
      plSug: [], plSugOpen: false, plSugIdx: -1, plSugBusy: false,
      plChart: "",               // "" = table → hbar (biggest losses) → vbar (by month) → pie (by customer)
      plFull: false,             // hides the OTHER cards via grid-item classes — never filter effectiveLayout
      plAiBusy: false, plAi: "", plAiError: "", plAiTitle: "", plAiBasis: "",
      plPrintBusy: false,
      plExcelModal: { open: false, scope: "page", busy: false, error: "" },
      // ---- Costing for Parts (SENSITIVE — Master + All Insights only; see canSeeCost) ----------
      // Deferred like the codes card: the endpoint joins the ~1M-row order table to rpt_PartCost.
      partCostRows: [],          // [{ partNumber, qty, revenue, unitMaterialCost, ..., unitCostTotal, costComponentCount, extendedCost, latest* }]
      partCostTotals: null,      // { parts, partsCosted, revenueAll, revenueCosted, costCosted, marginCosted, costCoverage }
      partCostBusy: false,
      partCostSearch: "",
      partCostTerms: [],         // committed search terms (chips); the live box text counts as one more
      // Column sort. col "" = the server's own order (highest cost first), which is a meaningful default and
      // not the same as sorting by unit cost — parts with NO cost on file are deliberately pushed last there.
      pcSort: { col: "", dir: -1 },
      // Typeahead. Suggestions come from the SERVER so they cover every part in the selection, not just
      // the 500 rows this card holds — see /report/customer-insights/part-cost-suggest.
      pcSug: [], pcSugOpen: false, pcSugIdx: -1, pcSugBusy: false,
      // Row expansion. Description and Last cost change are no longer columns — they live in the
      // expanded panel, which is what makes room for Unit price without a horizontal scrollbar.
      pcOpen: {},                // { partNumber: true } — expanded rows
      pcHover: null,             // { pn, desc, lc, x, y } — the hover preview of the expanded panel
      // Full view. The OTHER grid cards are hidden by a CSS class on each grid-item, never by filtering
      // effectiveLayout — filtering the array vue-grid-layout consumes is what caused the documented
      // infinite-render freeze. The hero chat is unaffected for free: it lives above the grid, not in it.
      pcFull: false,
      // Comptroller ask #1: one click to see ONLY the parts priced below today's cost. The charts,
      // the Excel default scope and the counts all read partCostView, so they follow for free.
      pcBelowOnly: false,
      // Chart mode for the cost card. "" = the table; one button cycles through the three chart shapes.
      pcChart: "",               // "" | "hbar" | "vbar" | "pie"
      // The card's own AI briefing. Kept separate from aiInsights (the dashboard-wide narrative) so
      // generating one never clobbers the other.
      pcAi: "",
      pcAiTitle: "",
      pcAiError: "",
      pcAiBasis: "",
      pcAiBusy: false,
      pcPrintBusy: false,        // own flag — the panel's printBusy drives the dashboard presentation
      partCostDenied: false,     // 403 — the role was granted but this browser's 24h JWT predates it
      // false ONLY when the server says rpt_PartCost is absent on this database. Kept distinct from
      // "no rows" so the card can say "not available here" instead of rendering a table of zeros,
      // which would read as "every part is free".
      partCostAvailable: true,
      partCostDetail: "",        // the server's ACTUAL SQL error (number + database + message)
      partCostQueryError: false, // a non-207/208 SQL failure, e.g. a timeout
      pcExcelModal: { open: false, scope: "page", busy: false, error: "" },
      codesDeferred: false, // broad/date-only view → the (heavy) codes query waits behind a button (like Customer Order Details)
      codeSearch: "",
      presentValues: [],
      // Admin-managed State → Territory → Sales Rep rows (drives the map).
      territoryAssignments: [],
      // Floating "filter story" panel.
      storyCollapsed: false,
      storyCorner: "tr",   // default top-right; drag overrides
      storyPos: null,      // { left, top } once dragged
      storyDragging: false,
      storyHidden: true,   // start CLOSED — only opens when the Insights pill/edge is clicked or swiped
      // Story "extras" from /customer-insights/story (summative + customer standing + saved carts).
      story: { summary: null, customer: null, savedCarts: [] },
      // Defined KPI: saved (un-submitted) cart counts split into Orders vs Quotes (/customer-insights/saved-carts).
      savedCarts: { savedOrders: 0, savedQuotes: 0 },
      // AI insights narrative (Sales Insights drawer) — MANUAL only (Generate button); never auto-called.
      aiInsights: "",
      aiInsightsBusy: false,
      aiInsightsError: "",
      aiInsightsStale: false,
      aiCoaching: "",        // tips/best-practices/industry advice from Generate Insights (own section; print page 5)
      printBusy: false,      // building the multi-page presentation (screenshot + insights take a moment)
      aiInsightHistory: [],  // [{id, createdAt, insights, coaching}] from rpt_AiHistory (DB-backed, newest first)
      aiHistOpen: false,     // Generate-Insights 🕘 history popup open
      aiHistHover: null,     // hovered history entry (drives the preview)
      storyWidth: 910,       // Sales Insights drawer width (px) — opens at "3 clicks" (340 + 3×190) so it's roomy by default; the ↔ widen button bumps it ~2" each click
      aiAskHistOpen: false,  // chat (Ask) history popup open
      // ⭐→📧 post-apply follow-up chain for the hero Ask: "" | "fav" (offer favorite) | "notify" (offer digest)
      aiFollow: { stage: "", name: "", favId: "" },
      // Hero Ask typeahead — completes real values from the loaded card facets while typing
      // ("Mark" → "Mark Warren" from the Sales Reps card). frag = the typed fragment being replaced.
      heroSug: { open: false, items: [], idx: 0, frag: "" },
      // Hero Ask voice input (🎤) — browser Web Speech API dictation into the question field.
      heroVoice: { on: false },
      micFixAvail: false,   // show "🎙 Fix microphone" on the blocked-mic error
      micFixBusy: false,
      micHelp: null,        // { device|title, steps[], copyLink? } — per-device unblock/carry-to-Safari panel
      micLinkCopied: false, // "Copy page link" flash (carry-to-Safari flow)
      askHistHover: null,    // hovered chat-history entry (drives the preview)
      // Layout 2 (redesigned, account-selectable) state.
      layout: "1",
      // Layout-1 draggable/resizable dashboard pads (seeded from default; saved layout overrides on mount).
      // partCosts is stripped from the INITIAL layout so an unauthorized user never sees the card frame
      // and title flash on first paint (canSeeCost is a computed, unavailable this early).
      // ensureBuiltinCards adds it back only when the role allows.
      dashLayout: JSON.parse(JSON.stringify(DASH_DEFAULT)).filter(function (it) { return it.i !== "partCosts"; }),
      mobileLayout: JSON.parse(JSON.stringify(MOBILE_LAYOUT)),                 // fixed single-column mobile stack (see MOBILE_LAYOUT)
      isMobile: typeof window !== "undefined" && window.innerWidth < 992,      // < 992px → mobile reflow (matches App.vue's breakpoint)
      mapZoom: 0.64,      // default map zoom (from the Master's default layout); overridden per-user once they zoom
      mapMode: "world",   // map card mode: "world" (country-level, the default view) | "us" (states); auto-switches on filter
      worldZoom: 1,       // zoom for the world map (ephemeral, not persisted)
      mapHoverState: null, // US map: the hovered state (postal/name/territory/sales) — drives the flag panel below the map
      mapHoverCountry: null, // World map: the hovered country (name/present/sales/customers/orders) — drives the flag panel below it
      dashSaved: false,   // brief "Saved" feedback after an explicit Save
      filterRailOpen: true,   // left filter rail (desktop) — open by default, collapsible
      stickyTop: 160,         // sticky offset for the left panels — measured from the fixed header (measureStickyTop)
      favColOpen: false,      // favorites hover-dropdown (top-left, desktop)
      favRailOpen: true,      // left favorites rail (desktop) — open by default, collapsible (mirrors filterRailOpen)
      dashHidden: [],     // built-in card ids the Master hid (persisted; restore via "Restore hidden" or Reset)
      titleStyles: {},    // per-card title styling { gid: { bold, color, size } } (persisted)
      layoutHistory: [],       // automatic undo/redo snapshots of the current layout (session-scoped; see recordLayoutHistory)
      layoutHistoryIndex: -1,  // pointer into layoutHistory (undo = back, redo = forward)
      emailHiddenLayout: null, // LEGACY single Email-docked arrangement (now only a migration seed for dockLayouts.email)
      emailVisibleLayout: null, // arrangement to restore when Email Insights is UN-collapsed — persisted so undock survives a refresh
      // ===== DOCKED-layout scenarios (2026-07-22) — replaces the single "Email-hidden layout". With TWO
      // dockable cards there are three docked scenarios, each with its OWN saved arrangement (per-user
      // blob, applied on the Default layout): key = "email" | "filters" | "email+filters" (fixed order;
      // "" = nothing docked = the main layout itself). Every layout edit made while docked autosaves into
      // the CURRENT scenario's slot, and entering a scenario re-applies its saved arrangement. =====
      dockLayouts: {},
      // Docked presets for every shared report OTHER than Sales Insights: { roleKey: { dockKey: preset } }.
      // Sales Insights deliberately keeps using the FLAT dockLayouts above — same keys, same blob shape,
      // same backend helpdesk-blob bridge — so Layout 1 cannot regress. A NEW property name is also what
      // keeps the stale Sales-shaped presets already sitting in the SHARED2/SHARED3 rows' `dockLayouts`
      // invisible: saveRoleDefault used to stamp the flat Sales map into every row it wrote, so SHARED2
      // carries three dock scenarios for a layout that holds one card. Verified in the sandbox DB.
      dockLayoutsByReport: {},
      // Which LAYOUT emailVisibleLayout / _emailLayoutSnap were captured on. Without it a snapshot taken
      // on Sales Insights was replayed onto Accounting when Email was un-docked there, swapping the
      // Accounting report for the sales dashboard (a live bug). Absent in an old blob => "default".
      emailSnapLayout: "",
      emailDocked: true,       // Email Insights starts COLLAPSED every session (user decision 2026-07-21) — opening it
                               // is a per-session choice (the stored flag is no longer read back; see loadDash note).
                               // While true, layout edits refine the
                               // DOCKED preset (emailHiddenLayout) and NEVER the shared main layout, so docking can't wipe it.
      // Hero Ask ("Chat") collapse — an arrow docks the whole hero strip to a right-edge CHAT tab (mirrors the
      // Email edge tab). Per-user UI preference in localStorage (not the layout blob) so it's low-risk + instant.
      // Default OPEN (chat always available); the edge tab is always there to reopen once docked.
      chatDocked: (typeof localStorage !== "undefined" && localStorage.ciChatDocked === "1"),
      // Filters card docked-to-the-right — DEFAULT COLLAPSED (both desktop pill + mobile edge tab). localStorage
      // "0" = the user explicitly reopened it; anything else (incl. first visit) = collapsed. Applied imperatively
      // in ensureBuiltinCards/applyLayout (desktop, never a dashLayout render-filter → grid-loop trap) and in
      // effectiveLayout's static mobileLayout branch (safe).
      filtersDocked: (typeof localStorage === "undefined" || localStorage.ciFiltersDocked !== "0"),
      mFiltersOpen: false,   // MOBILE: the active-filter chip list ("Filters" toggle) starts collapsed each session
      // Hero talk-back (mobile): Azure neural TTS via POST /report/tts — NEVER the browser voice. The
      // picked voice persists per browser; ttsPlaying/ttsBusy drive the 🔊 Listen / ⏹ Stop button.
      ttsVoice: (typeof localStorage !== "undefined" && localStorage.ciTtsVoice) || "en-US-AriaNeural",
      ttsBusy: false,
      ttsPlaying: false,
      ttsError: "",

      layoutVersions: [],      // named layout snapshots the user saved (per-user blob → reliable restore point)
      versionName: "",         // the "name this version" input
      titleEditGid: null, // which card's title-style toolbar is currently open
      tokensDrawerOpen: false,
      schemaTables: [],   // /report/schema tables (Master) — powers the Tokens reference drawer
      schemaBusy: false,
      tokenSearch: "",
      // Saved-carts click-through breakdown popup.
      scDetail: { open: false, type: "order", busy: false, carts: [] },
      scNoteExpand: {},   // per-block collapse state for Customer Notation / Specifications (collapsed by default)
      scExpanded: {},     // which rep GROUPS are expanded in a multi-rep breakdown (collapsed by default). Rep name -> true.
      scInsightCusts: {}, // customers whose per-part AI insights have already been requested (so a re-expand doesn't refetch)
      // Per-part AI sales insights (the 💡 hover in the breakdown). Cached by "customerId|partNumber".
      scInsights: {},
      scInsightBusy: false,
      scPop: { open: false, x: 0, y: 0, key: "", data: null, cart: null },   // the hover popover
      scPopHideTimer: null,   // NB: no leading underscore — Vue 2 wouldn't proxy a _-prefixed data prop
      // 🖨 on a Customer Order Details row → the one document being printed (customer + doc row, straight off
      // the loaded card data). Cleared on afterprint so a later Ctrl+P prints the report, not a stale order.
      codPrint: { customer: {}, doc: null },
      codPrintLogoFailed: false,   // SVG <img> failed in the print engine → text "STEELE" fallback
      // Orders In Process card (recently-placed orders not yet complete) + its order-detail modal.
      oip: { busy: false, orders: [] },
      oipDetail: { open: false, busy: false, orderId: null, data: null },
      activeLayout: "default",   // "default" = locked Layout 1 (DASH_DEFAULT, read-only); otherwise a custom layout id
      customLayouts: [],         // per-user named layouts (2+): { id, name, layout, mapZoom, aiCards, hidden, titleStyles }
      roleDefaults: {},          // shared, Master-set default Layout 1 PER ROLE: { "Sales": {layout,mapZoom,hidden,titleStyles}, ... }
      authorizedReports: [],     // report keys the signed-in user may see (from /report/access/mine); Master = all
      authorizedReportsOk: false,// true only after a SUCCESSFUL access fetch — else gating fails open (pre-deploy safety)
      dashToolsOpen: false,
      // AI card builder — user-described cards rendered on the Layout-1 dashboard.
      aiCards: {},        // map of gridId -> card { cardId, title, viz, data }
      aiPrompt: "",
      aiBusy: false,
      aiError: "",
      aiOpen: false,
      // Master card designer — shared custom cards keyed by grid id "cust-<dbId>".
      customCards: {},
      cardOrigins: {},    // custGid -> builtin gid it was converted FROM (this session) → one-click "restore original"
      editorOpen: false,
      editingCard: null,
      filtersDrawerOpen: false,
      fmoreOpen: false,        // inline "More" panel (Status / Invoiced / Date basis) — replaces the old Filters drawer
      qtrOpen: false,          // QTR date-preset popup (Q1–Q4)
      yearOpen: false,         // YEAR date-preset popup (1–20 calendar years)
      rollOpen: false,         // TRAILING date-preset popup (1–20 rolling years ending today)
      activePreset: "ytd",     // the applied date-preset key (today/yesterday/week/ytd/qN/N/rN) → keeps its button highlighted; DEFAULT range is YTD (Jan 1 → today), so YTD starts highlighted
      hoverChipKey: "",        // which filter row's custom HTML "comment" tooltip is showing (key|val)
      l2ShowAllCustomers: false,
      l2ShowAllParts: false,
      details: { customers: [], message: "" },
      detailsDeferred: false,   // broad view → details load behind a button (heavy full-table LOB scan)
      detailsBusy: false,
      rows: [],
      totalRows: 0,
      page: 1,
      pageSize: 50,
      sort: "orderDate",
      dir: "desc",
      loading: false,
      chipHomePrompt: false,   // "Returning to NC999" confirm — filters just crossed to 2+ customers while acting AS a customer (see the filters.customer watcher)
      showProcessing: false,   // global "Loading…" overlay — true shortly after an Ask/filter refresh starts (see watch:isProcessing)
      procElapsedMs: 0,        // live elapsed time while the processing overlay is up (drives the ETA line)
      procEstimateSec: 12,     // rough estimate for "time remaining" (recomputed per run: ask ≈ fixed, refresh scales w/ range)
      _procSeq: 0,             // request token — cancelProcessing() bumps it so a pending ask/refresh result is ignored
      procLockDates: false,    // the in-flight ask carries an explicit window (overlay shortcut / favorite) — only then is the range KNOWN before the response
      askConfirmAllTime: false,// an un-dated (all-time) ask PAUSES before the heavy dashboard reload — the overlay shows an ASK button until the user confirms or narrows
      loadElapsedMs: 0,        // TRUE elapsed since the refresh started (counts up — the honest number)
      loadEstMs: 0,            // current self-calibrated estimate (ms) for this selection, for "…out of an estimated X"
      loadPastEstimate: false, // elapsed passed the estimate → drop "N left", say "finishing up…" (elapsed keeps rising)
      loadProgressPct: 0,      // refresh progress bar width % (asymptotic — never 100 until actually done)
      exporting: false,
      notReady: false,
      columns: [
        { key: "orderNo", label: "Order #", sortable: true },
        { key: "orderDate", label: "Date", sortable: true },
        { key: "customer", label: "Customer", sortable: true },
        { key: "part", label: "Part #", sortable: true },
        { key: "", label: "Description" },
        { key: "qty", label: "Qty", sortable: true, num: true },
        { key: "", label: "Unit Price", num: true },
        { key: "lineTotal", label: "Line Total", sortable: true, num: true },
        { key: "", label: "Status" },
        { key: "salesRep", label: "Sales Rep", sortable: true },
        { key: "territory", label: "Territory", sortable: true },
      ],
    };
  },
  computed: {
    // ===== PRINT SHEET (Customer Order Details 🖨) =====
    // An invoiced document prints as an Invoice, an open one as an Order — the same distinction the row's
    // "order" tag makes. All three guard on codPrint.doc: the sheet's wrapper is always rendered.
    codPrintKind() { return (this.codPrint.doc && this.codPrint.doc.isInvoice) ? "Invoice" : "Order"; },
    // Starting points for someone who does NOT want the standard comparison. Each is a question the
    // pipeline can actually answer from the quote plus this customer's history.
    quoteSuggestions() {
      return [
        "Which lines on this quote are parts they have never bought before?",
        "Which lines are priced furthest above what they usually pay?",
        "Summarise this quote — total value, number of lines, and the biggest items.",
        "Are any quantities on this quote unusual compared with how they normally order?",
        "What should I be ready to defend if they push back on price?",
      ];
    },
    // Who and what the presentation is about, from the lines themselves.
    // The four narrow windows, with the date each one resolves to in its tooltip so "3 Yr" is never a
    // guess. Same vocabulary (ytd / rN) the all-time gate's Narrow row uses.
    quoteWindowChoices() {
      var self = this;
      return [
        { kind: "ytd", label: "This year" },
        { kind: "r1", label: "1 Yr" },
        { kind: "r3", label: "3 Yr" },
        { kind: "r5", label: "5 Yr" },
      ].map(function (w) {
        var from = "";
        try { from = self._shortcutRange(w.kind).from; } catch (e) { /* label alone still works */ }
        return { kind: w.kind, label: w.label, tip: from ? "Orders on or after " + from : w.label };
      });
    },
    // The window chip's tooltip — the actual cut-off date, not a label.
    qrWindowTip() {
      return (this.qrRead && this.qrRead.historyFrom)
        ? "Past orders dated on or after " + this.qrRead.historyFrom
        : "Every order on file for this customer";
    },
    // "never bought" is a claim about ALL of history. Under a window the honest wording is narrower.
    qrNewChipText() {
      var r = this.qrRead || {};
      // Three different claims, and only the last one is absolute:
      //  * the part LOOKUP was truncated  → we did not search their whole catalogue
      //  * a window was applied           → we searched part of their history
      //  * neither                        → they have genuinely never bought it
      if (r.partsCapped) return "not among the parts searched";
      if (r.historyFrom) return "none " + (r.historyLabel || "in this window");
      return "never bought";
    },
    // Lines with no comparison beside them. Kept IN the table too — this list is a summary, not a
    // substitute, so nothing is only visible in one place.
    // "PFB S 1211 CB, DOA S 1750 SP" — so the banner says WHAT went, not just how many.
    qrRemovedLabel() {
      var names = (this.qrRemoved || []).map(function (r) {
        var l = r.line || {};
        return (l.quoteLine ? "#" + l.quoteLine + " " : "") +
               (l.partQuoted || l.partMatched || "unnamed line");
      });
      return names.length > 4 ? names.slice(0, 4).join(", ") + " and " + (names.length - 4) + " more"
                             : names.join(", ");
    },
    qrNotCompared() {
      return (this.qrLines || []).filter(function (l) { return l.matchState && l.matchState !== "matched"; });
    },
    // "30 lines · 26 compared" beats a bare count: the gap is the part worth seeing.
    qrCoverageLabel() {
      var total = (this.qrLines || []).length;
      var compared = total - this.qrNotCompared.length;
      var inDoc = this.qrRead && this.qrRead.pricedRowsInDocument;
      var head = (inDoc && inDoc > total ? inDoc + " priced line(s) in the quote, " + total + " read"
                                         : total + " line(s)");
      var tail = compared === total ? head + " · all compared"
                                    : head + " · " + compared + " compared, " + this.qrNotCompared.length + " not";
      // A count that quietly shrinks when a rep removes a row would misreport the quote.
      if (this.qrRemoved.length) tail += " · " + this.qrRemoved.length + " removed";
      return tail;
    },
    // The person who generated the sheet, printed after the title (user 2026-08-12). Read from the REAL
    // signed-in identity, not the previewed one — a Master previewing a rep still generated this. When a
    // preview IS active both names are shown, because a sheet that credits the wrong person is worse than
    // a slightly longer title.
    qrPrintPreparedBy() {
      var g = (this.$store && this.$store.getters) || {};
      var ru = g.realUser || null;
      if (!ru) return "";
      var me = String(ru.firstName || "").trim() || String(ru.email || "").split("@")[0] || "";
      var as = this.viewingAsUser;
      var asName = as ? String(as.name || "").trim() : "";
      if (me && asName && asName.toLowerCase() !== me.toLowerCase()) return me + " (viewing as " + asName + ")";
      return me;
    },
    // The document text with every occurrence of the search term marked. ESCAPED FIRST: this is customer
    // document content going through v-html, so it is escaped and only then are the <mark>s inserted.
    qrDocHtml() {
      var txt = (this.qrRead && this.qrRead.documentText) || "";
      var esc = function (t) {
        return String(t).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      };
      var find = (this.qrDoc.find || "").trim();
      if (!find) return esc(txt);
      // Split on the term case-insensitively, keeping the matches, so every hit is marked.
      var safe = find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      var parts = txt.split(new RegExp("(" + safe + ")", "ig"));
      return parts.map(function (p, i) {
        return (i % 2 === 1) ? "<mark>" + esc(p) + "</mark>" : esc(p);
      }).join("");
    },
    // Census rows, filtered by the same search box.
    qrDocRows() {
      var rows = (this.qrRead && this.qrRead.rows) || [];
      var find = (this.qrDoc.find || "").trim().toLowerCase();
      if (!find) return rows;
      return rows.filter(function (r) { return String(r.text || "").toLowerCase().indexOf(find) >= 0; });
    },
    qrPrintPickCount() {
      return (this.qrPrintPick.rows || []).filter(function (r) { return r.checked; }).length;
    },
    // The lines the SHEET renders. Falls back to every line when nothing was picked (a direct print, or a
    // picker that was never opened) — the print must never come out empty because of this filter.
    qrPrintLines() {
      var rows = this.qrPrintPick.rows || [];
      if (!rows.length) return this.qrLines || [];
      var keep = {};
      rows.forEach(function (r) { if (r.checked) keep[r.line] = true; });
      var picked = (this.qrLines || []).filter(function (l) { return keep[l.line]; });
      return picked.length ? picked : (this.qrLines || []);
    },
    // The printed "not compared" list covers the printed lines only — listing a line that is not on the
    // page would read as an omission from the comparison rather than from the print.
    qrPrintNotCompared() {
      var lines = this.qrPrintLines;
      return (this.qrNotCompared || []).filter(function (g) {
        return lines.some(function (l) { return l.line === g.line; });
      });
    },
    qrPrintSubtitle() {
      var cust = (this.qrLines || []).map(function (l) { return l.past && l.past.customerId; })
                     .filter(Boolean);
      var uniq = cust.filter(function (c, i) { return cust.indexOf(c) === i; });
      var who = uniq.length ? uniq.join(", ") : "";
      var files = (this.heroFileNotes || []).filter(function (n) { return n && n.ok; })
                      .map(function (n) { return n.name; });
      var win = (this.qrRead && this.qrRead.historyLabel) ? "compared vs " + this.qrRead.historyLabel : "";
      var total = (this.qrLines || []).length, shown = this.qrPrintLines.length;
      var count = (shown < total) ? shown + " of " + total + " quote line(s)" : total + " quote line(s)";
      return [who, count, win, files.length ? files.join(", ") : ""]
             .filter(Boolean).join("  ·  ");
    },
    codPrintDate() { return this.codPrint.doc ? this.fmtLongDate(this.codPrint.doc.docDate) : ""; },
    codPrintQty() {
      var n = 0;
      ((this.codPrint.doc || {}).parts || []).forEach(function (p) { n += Number(p.qty) || 0; });
      return n;
    },
    // Refresh-banner labels (counts up): elapsed so far, the estimate, and the remaining vs that estimate.
    loadElapsedLabel() { return this._fmtDur(this.loadElapsedMs); },
    loadEstimateLabel() { return this._fmtDur(this.loadEstMs); },
    loadRemainLabel() { return this._fmtDur(Math.max(0, (this.loadEstMs || 0) - (this.loadElapsedMs || 0))); },
    // 'Sales' role is locked to NC999 in the header and switches companies only
    // by selecting a customer here (preview-aware via effectiveRoleName).
    isSalesLocked() {
      return !!(this.$store && this.$store.getters && this.$store.getters.effectiveRoleName === "Sales");
    },
    // Master / Sales / Sales All may pick a report layout (saved per account).
    canSwitchLayout() {
      var r = (this.$store && this.$store.getters) ? this.$store.getters.effectiveRoleName : "";
      return ["Master", "Sales", "Sales All"].indexOf(r) >= 0;
    },
    // Shared "Layout 2": an always-present team layout for Master + Sales All (curated by a Master, read-only
    // for Sales All). Preview-aware, so a Master previewing "Sales All" sees exactly what that role sees.
    // Report visibility is access-list governed now (rpt_UserReportAccess via /report/access/mine) — a non-Master
    // user sees a report only if listed; Master sees all. If the access endpoint hasn't answered yet (or the
    // backend isn't deployed — authorizedReportsOk false), fail open to the PRIOR role rule so nobody is locked
    // out during the FE-first / BE-after deploy window.
    canSeeSalesInsights() {
      if (this.isMaster) return true;
      if (!this.authorizedReportsOk) return true;   // pre-deploy fallback: everyone used to see Sales Insights
      return (this.authorizedReports || []).indexOf("sales-insights") >= 0;
    },
    canSeeManufacturing() {
      if (this.isMaster) return true;
      if (!this.authorizedReportsOk) {
        var r = (this.$store && this.$store.getters) ? this.$store.getters.effectiveRoleName : "";
        return r === "Sales All";   // pre-deploy fallback: the old Master+Sales All rule
      }
      return (this.authorizedReports || []).indexOf("manufacturing-insights") >= 0;
    },
    // Back-compat alias: the shared "Layout 2" IS the Manufacturing Insights report. Everything that gated on
    // canSeeSharedLayout2 (picker row, numbering, loadDashLayout guard, notes) now follows Report Access.
    canSeeSharedLayout2() {
      return this.canSeeManufacturing;
    },
    // Accounting report ("shared3") — REPORT visibility, distinct from the DATA gates below. Unlike its
    // fail-open siblings above there is NO pre-existing population to protect: the report is brand new,
    // so an unresolved access fetch means NO. The JWT roles are checked too because /report/access/mine
    // returns raw list keys — a user granted the covering "all-insights" (or provisioned the role by an
    // admin directly) is authorized without an "accounting-insights" row of their own.
    canSeeAccounting() {
      if (this.isMaster) return true;
      var ar = this.authorizedReports || [];
      if (this.authorizedReportsOk && (ar.indexOf("accounting-insights") >= 0 || ar.indexOf("all-insights") >= 0)) return true;
      var g = (this.$store && this.$store.getters) ? this.$store.getters : {};
      var names = g.effectiveRoleNames;
      if (!Array.isArray(names) || !names.length) return false;   // unknown identity => NO (fail closed)
      return names.some(function (n) {
        var k = String(n || "").trim().toLowerCase();
        return k === "accounting insights" || k === "all insights";
      });
    },
    // Parts Sold at a Loss — may this user see the DATA? Same construction as canSeeCost directly above
    // (read its warnings — they all apply here): fail closed, no reveal under role preview, isMaster for
    // the Master signal, PLURAL role names. One role wider than cost: Accounting Insights qualifies.
    // UX only — the boundary is [Authorize] on GET /report/accounting/part-loss.
    canSeeLoss() {
      var g = (this.$store && this.$store.getters) ? this.$store.getters : {};
      if (g.viewingAsRole) return false;              // role preview must never reveal cost-class data
      if (this.isMaster) return true;
      var names = g.effectiveRoleNames;
      if (!Array.isArray(names) || !names.length) return false;   // unknown identity => NO
      return names.some(function (n) {
        var k = String(n || "").trim().toLowerCase();
        return k === "master" || k === "all insights" || k === "accounting insights";
      });
    },
    // The three category tabs, built from the ALWAYS-returned per-category counts so each tab shows its
    // size no matter which one is selected. The loss $ total goes on Below cost ONLY — 82% of qualifying
    // rows are no-charge shipments (warranty/service/samples) and adding them in would turn noise into a
    // headline number.
    plTabs() {
      var byCat = {};
      (this.plCats || []).forEach(function (c) { if (c && c.category) byCat[c.category] = c; });
      return [
        { key: "BelowCost", label: "Below cost", rows: (byCat.BelowCost || {}).rows || 0, totalLoss: byCat.BelowCost ? byCat.BelowCost.totalLoss : null },
        { key: "NoCharge", label: "No charge", rows: (byCat.NoCharge || {}).rows || 0, totalLoss: null },
        { key: "Credit", label: "Credits", rows: (byCat.Credit || {}).rows || 0, totalLoss: null }
      ];
    },
    // Client-side sort of the fetched rows (the server returns newest-first). Dates sort as their ISO
    // strings; text case-insensitively; everything else numerically.
    plView() {
      var rows = (this.plRows || []).slice();
      // Committed chips already narrowed the SERVER query (loadPartLoss sends them) — only the text
      // still being typed filters here, so the box works live without a round-trip per keystroke.
      var live = String(this.plSearch || "").trim().toLowerCase();
      if (live) {
        rows = rows.filter(function (r) {
          return [r.partNumber, r.partDescription, r.customerId, r.custOrderId]
            .map(function (v) { return String(v || "").toLowerCase(); }).join("  ").indexOf(live) >= 0;
        });
      }
      return this.plApplySort(rows);
    },
    plCatLabel() { return { BelowCost: "below-cost", NoCharge: "no-charge", Credit: "credit" }[this.plCat] || ""; },
    plNote() {
      if (this.plCat === "BelowCost") return "Customer shipments invoiced below the ACTUAL cost booked on the shipment date — the real losses. Never today's cost projected backwards.";
      if (this.plCat === "NoCharge") return "Lines shipped at $0 — warranty, service and sample shipments. Counted separately; not in the loss total.";
      return "Credit / return lines (negative price). Counted separately; not in the loss total.";
    },
    plSearchLabel() {
      // Chips are OR'd (server-side); the still-typed text then NARROWS those rows locally — an AND.
      // The label says so, or the strip, the captions and the export scope line would all describe an
      // OR the card is not performing.
      var live = String(this.plSearch || "").trim();
      var chips = (this.plTerms || []).join(" or ");
      if (chips && live) return chips + " — narrowed to “" + live + "”";
      return chips || live;
    },
    // The current tab's WINDOW total (all matching lines, not just the 500 loaded) — feeds the
    // "showing top N of M" pill and the export modal's "All lines" count.
    plWindowRows() {
      var self = this;
      var c = (this.plCats || []).find(function (x) { return x && x.category === self.plCat; });
      return c ? (c.rows || 0) : 0;
    },
    plPartsInView() { var s = {}; (this.plView || []).forEach(function (r) { s[r.partNumber] = 1; }); return Object.keys(s).length; },
    plCustomersInView() { var s = {}; (this.plView || []).forEach(function (r) { s[r.customerId] = 1; }); return Object.keys(s).length; },
    plNewestShip() {
      var m = "";
      (this.plRows || []).forEach(function (r) { var d = String(r.transactionDate || "").slice(0, 10); if (d > m) m = d; });
      return m;
    },
    plLossInView() { return (this.plView || []).reduce(function (s, r) { return s + (Number(r.lineLoss) || 0); }, 0); },
    // One button cycles the shape, same grammar as the cost card: icon and tooltip name the NEXT state.
    plChartIcon() {
      return { "": "📊", hbar: "📅", vbar: "🥧", pie: "📋" }[this.plChart] || "📊";
    },
    plChartTitle() {
      return {
        "": "Chart the biggest losses — horizontal bars",
        hbar: "Switch to loss by month",
        vbar: "Switch to a pie of loss by customer",
        pie: "Back to the table"
      }[this.plChart] || "Chart these losses";
    },
    plChartCaption() {
      if (this.plChart === "vbar") {
        var mb = this.plMonthBars;
        return "Loss by month across the lines in view"
          + (mb.clipped ? " — clipped to the newest 24 months (" + this.fmtNum(mb.clipped) + " older month(s) not drawn; the Excel export holds them all)" : "")
          + ". Taller = more lost that month.";
      }
      if (this.plChart === "pie") return "Share of the loss in view by customer — the top 7 plus Other.";
      return "The " + this.fmtNum(this.plChartBars.length) + " parts carrying the biggest total loss in view"
        + (this.plSearchLabel ? " (matching “" + this.plSearchLabel + "”)" : "") + ".";
    },
    plChartBars() { return plLossBarsFrom(this.plView); },
    plMonthBars() { return plMonthBarsFrom(this.plView); },
    plMonthTicks() { return plTicksFrom(this.plMonthBars.max); },
    plPie() { return plPieFrom(this.plView); },
    // The on-screen chart IS the generated SVG — same single-generator rule as the cost card, so the
    // card, the print doc and the Excel PNGs can never drift.
    plChartSvgLive() { return this._plChartSvg(this.plChart); },
    plChartEmpty() {
      if (this.plChart === "vbar") return !this.plMonthBars.bars.length;
      if (this.plChart === "pie") return !this.plPie.length;
      return !this.plChartBars.length;
    },
    plChartEmptyText() {
      if (this.plChart === "pie") return "Nothing to chart — no loss dollars in this view.";
      return "Nothing to chart in this view.";
    },
    plAiLines() {
      return String(this.plAi || "").split("\n")
        .map(function (s) { return s.replace(/^\s*[-•]\s*/, "").trim(); })
        .filter(function (s) { return s.length > 0; });
    },
    // Costing for Parts — may this user see PART COST and MARGIN at all?
    //
    // ⚠️ THIS FAILS CLOSED, which is the OPPOSITE of canSeeSalesInsights/canSeeManufacturing directly
    // above. Those deliberately return TRUE when the access fetch hasn't succeeded, so nobody is locked
    // out of a report they always had. Cost is commercial data: an unknown answer must mean NO. Do not
    // "fix" this for consistency with its neighbours.
    //
    // Roles come from the JWT via the store, NOT from the report-access list, so this deliberately does
    // NOT gate on authorizedReportsOk — that flag is about which report KEYS a user is on, and letting a
    // failed access fetch hide cost from a legitimate Master would be a different bug.
    //
    // PLURAL effectiveRoleNames on purpose: the singular effectiveRoleName collapses a multi-role user to
    // one role (store/auth.js realRoleNameOf → find(master) || r[0]), and ["Sales","All Insights"] is a
    // real shape — gating on the singular would hide the card from exactly the people meant to have it.
    //
    // AND REMEMBER: this is UX only. The real boundary is [Authorize] on the part-costs action. The role
    // preview cannot be used to TEST it — HttpClient always sends the real token, so a Master previewing
    // "Sales" still gets HTTP 200 from the endpoint. Verify with a real Sales-Insights-only login.
    canSeeCost() {
      var g = (this.$store && this.$store.getters) ? this.$store.getters : {};
      if (g.viewingAsRole) return false;              // role preview must never reveal cost
      // MASTER: use the SAME signal every other gate in this component uses — isMaster, which reads the
      // SINGULAR effectiveRoleName via realRoleNameOf. Testing ONLY the plural effectiveRoleNames refused a
      // genuine Master on production (helpdesk@jcsteele.com, header showing Role: Master): the two getters
      // resolve a user's role differently, and the singular is the one the rest of this file trusts.
      // Second time today the same trap bit — MainNav had to stop deriving Master from the role SET for
      // exactly this reason. If a future gate needs Master, reuse isMaster; do not re-derive it.
      if (this.isMaster) return true;
      var names = g.effectiveRoleNames;
      if (!Array.isArray(names) || !names.length) return false;   // unknown identity => NO
      return names.some(function (n) {
        var k = String(n || "").trim().toLowerCase();
        return k === "master" || k === "all insights";
      });
    },
    // What the gate actually SAW, shown on the refusal. Guessing why a gate said no has cost several
    // round trips today; the card should just tell us.
    costRoleDebug() {
      var g = (this.$store && this.$store.getters) ? this.$store.getters : {};
      var names = g.effectiveRoleNames;
      return "roles seen: " + (Array.isArray(names) && names.length ? names.join(", ") : "(none)")
           + " · primary: " + (g.effectiveRoleName || "(none)")
           + (g.viewingAsRole ? " · previewing as " + g.viewingAsRole : "");
    },
    // Rows after the card's own search box. Matches part number, description and the cost-changer names,
    // so "who last changed a burden cost" is findable.
    // Every active term: the committed chips PLUS whatever is currently typed. Including the live text
    // means the box still filters as you type — the ＋ only makes a term STICK so the next one can be
    // added beside it, rather than replacing it.
    partCostTermList() {
      var out = [];
      (this.partCostTerms || []).forEach(function (t) {
        var k = String(t || "").trim().toLowerCase();
        if (k && out.indexOf(k) < 0) out.push(k);
      });
      var live = String(this.partCostSearch || "").trim().toLowerCase();
      if (live && out.indexOf(live) < 0) out.push(live);
      return out;
    },
    // Human rendering of the active terms, for captions, the Excel scope picker and the workbook's
    // provenance sheet — so an exported file records exactly which search produced it.
    partCostSearchLabel() { return this.partCostTermList.join(" or "); },
    // UNION, not intersection. "Add another search term in addition to the first" is additive: DLM plus
    // PUG should show both families. ANDing two part-number fragments almost always yields nothing, which
    // would make the ＋ button look broken. Change .some to .every here to make it narrowing instead.
    // Column definitions drive BOTH the header and the sort, so a header can never sort by the wrong field.
    pcCols() {
      return [
        // Description and Last cost change are deliberately NOT here: they moved into the expanded
        // panel. Sorting therefore covers only what is on screen, which is the honest pairing — a
        // header you cannot see should not be silently deciding the order.
        { k: "partNumber", t: "Part" },
        { k: "unitMaterialCost", t: "Material", num: true }, { k: "unitLaborCost", t: "Labor", num: true },
        { k: "unitBurdenCost", t: "Burden", num: true }, { k: "unitServiceCost", t: "Service", num: true },
        { k: "unitCostTotal", t: "Unit cost", num: true }, { k: "unitPrice", t: "Unit price", num: true },
        { k: "margin", t: "Margin", num: true },
        { k: "qty", t: "Qty in view", num: true }
      ];
    },
    partCostView() {
      var self = this;
      var terms = this.partCostTermList;
      var rows = this.partCostRows || [];
      // Below-cost first, then search, then sort. Requires a real negative margin — a part with no
      // price is UNKNOWN, not a loss, so the filter must not sweep it in.
      if (this.pcBelowOnly) {
        rows = rows.filter(function (r) { var m = self.pcMargin(r); return m && m.amt < 0; });
      }
      if (!terms.length) return this.pcApplySort(rows);
      return this.pcApplySort(rows.filter(function (r) {
        var hay = [r.partNumber, r.partDescription,
                   r.latestMaterialCostUser, r.latestLaborCostUser,
                   r.latestBurdenCostUser, r.latestServiceCostUser]
          .map(function (v) { return String(v || "").toLowerCase(); }).join("  ");
        return terms.some(function (q) { return hay.indexOf(q) >= 0; });
      }));
    },
    // ---- Costing for Parts: chart modes ----------------------------------------------------------
    // One button cycles the shape. Icon and tooltip name the NEXT state, so the control says what it
    // will do rather than what it currently is.
    // "▤" (U+25A4) had no glyph in the portal's font stack and rendered as a blank box, so the
    // back-to-table state looked like an empty white button. Emoji render reliably; plain box-drawing
    // characters do not.
    pcChartIcon() {
      return { "": "📊", hbar: "📶", vbar: "🥧", pie: "📉", gap: "📋" }[this.pcChart] || "📊";
    },
    pcChartTitle() {
      return {
        "": "Chart cost vs price — horizontal bars",
        hbar: "Switch to vertical bars",
        vbar: "Switch to a pie of the cost mix",
        pie: "Switch to price-minus-cost",
        gap: "Back to the table"
      }[this.pcChart] || "Chart these costs";
    },
    pcChartCaption() {
      if (this.pcChart === "pie") {
        return "Cost mix across the " + this.fmtNum(this.pcCostedInView) + " parts in view that have a cost on file"
             + (this.partCostTermList.length ? " matching “" + this.partCostSearchLabel + "”" : "")
             + " — the share of a typical part's unit cost, not money spent.";
      }
      if (this.pcChart === "gap") {
        var g = this.pcChartGap;
        return "Current margin (price − cost), worst first"
          + (g.skipped ? " — " + this.fmtNum(g.skipped) + " part(s) omitted for having no price on file" : "")
          + ". Red is priced BELOW today's cost. Both figures are today's, so this is not margin at the time of sale.";
      }
      return (this.pcBelowOnly ? "Below-cost parts only — " : "") + "Unit cost vs current unit price — the " + this.fmtNum(this.pcChartBars.length)
           + " highest-cost parts in view. The gap between the pair is today's margin"
           + (this.partCostTermList.length ? " (matching “" + this.partCostSearchLabel + "”)" : "") + ".";
    },
    // Counted over ALL loaded rows, not the filtered view — the button's number must not change when
    // the button itself is pressed.
    pcBelowCostAll() {
      var self = this;
      return (this.partCostRows || []).filter(function (r) {
        var m = self.pcMargin(r);
        return m && m.amt < 0;
      }).length;
    },
    pcCostedInView() {
      return (this.partCostView || []).filter(function (r) { return r.unitCostTotal > 0; }).length;
    },
    // Top 12 by unit cost, with EVERY pixel precomputed — a Vue 2 template has no `Math` in scope, so
    // width/height/rotation arithmetic cannot live in the markup.
    pcChartBars() { return pcBarsFrom(this.partCostView); },
    pcChartGap() { return pcGapFrom(this.partCostView); },
    // The on-screen chart IS the generated SVG — see _pcChartSvg. No inline <style>, so the external
    // .ci-pcc-* rules (and their dark-mode variants) still apply.
    pcChartSvgLive() { return this._pcChartSvg(this.pcChart); },
    // Each mode runs dry for a DIFFERENT reason, and saying which one beats a single generic message:
    // the pie needs a cost mix, the bars need any costed part, and the gap needs a part that has BOTH a
    // cost and a price — a set can easily satisfy the first two and not the third.
    pcChartEmpty() {
      if (this.pcChart === "pie") return !this.pcPie.length;
      if (this.pcChart === "gap") return !this.pcChartGap.bars.length;
      return !this.pcChartBars.length;
    },
    pcChartEmptyText() {
      if (this.pcChart === "gap") {
        return this.pcChartBars.length
          ? "No part in this view has BOTH a cost and a price on file, so price-minus-cost can't be computed."
          : "Nothing to chart — no parts in this view have a cost on file.";
      }
      return "Nothing to chart — no parts in this view have a cost on file.";
    },
    // Value axis for the vertical bars. Five gridlines at quarters of the tallest bar — this replaces the
    // per-bar figures, which collided badly once more than about eight parts were on screen.
    pcChartTicks() { return pcTicksFrom(this.pcChartBars); },
    // Pie = the four cost COMPONENTS summed across the costed parts in view. Deliberately a different
    // series from the bars: a pie of "each part's share of the summed unit cost" would be a real-looking
    // number that means nothing, whereas the material/labor/burden/service split is a genuine composition.
    // The caption states which one is on screen.
    pcPie() { return pcPieFrom(this.partCostView); },
    // The AI briefing as lines. The model is asked for "- " bullets; the dash is stripped for rendering
    // and a non-bulleted reply still shows as paragraphs rather than one unbroken block.
    pcAiLines() {
      return String(this.pcAi || "").split("\n")
        .map(function (s) { return s.replace(/^\s*[-•]\s*/, "").trim(); })
        .filter(function (s) { return s.length > 0; });
    },
    // A plain 'Sales' / 'Sales All' user can no longer CREATE their own layouts — they get the two
    // standard curated layouts (Sales Insights + Manufacturing Insights) only; Master (or any other
    // role) still can. Preview-aware via effectiveRoleName. Gates the ＋ New layout button + newLayout().
    canCreateLayout() {
      var r = (this.$store && this.$store.getters) ? this.$store.getters.effectiveRoleName : "";
      return r !== "Sales" && r !== "Sales All";
    },
    // Only Master / Sales All see the 💡 "Why unassigned" diagnostic on the Sales Rep card's (Unassigned) row —
    // it surfaces cross-territory accounts + the fix-it email (Parts/IT). Preview-aware; a regular Sales rep won't see it.
    canSeeUnassignedDiag() {
      var r = (this.$store && this.$store.getters) ? this.$store.getters.effectiveRoleName : "";
      return r === "Master" || r === "Sales All";
    },
    // Master-only: the card designer (add/edit/delete shared custom cards) + per-card edit affordances.
    isMaster() {
      var r = (this.$store && this.$store.getters) ? this.$store.getters.effectiveRoleName : "";
      return r === "Master";
    },
    // Exactly ONE identity publishes the shared standard layouts: helpdesk@jcsteele.com, as Master, with
    // NC999 selected. Everyone else — including other Masters — reads them.
    //  * isMaster is REUSED, never re-derived: it reads the SINGULAR effectiveRoleName, and testing the
    //    plural effectiveRoleNames refused a genuine Master in production earlier today.
    //  * the email comes from realUser, so an admin previewing AS someone cannot borrow the publisher
    //    identity; isMaster is preview-aware, which correctly disables publishing during any preview.
    //  * this is UX only. POST /report/role-default-layout re-checks the email server-side — that is the
    //    boundary. NC999 is an interlock (it stops an accidental publish while browsing a customer);
    //    the server cannot observe it, so it is not security.
    // WHO SEES the Layouts drawer: helpdesk@jcsteele.com holding Master, not previewing another user.
    // Deliberately WITHOUT the NC999 company interlock that canPublishSharedLayout adds — that guards the
    // act of publishing, not the right to open the drawer and look.
    isLayoutsAdmin() {
      if (!this.isMaster || this.viewingAsUser) return false;
      var ru = (this.$store && this.$store.getters) ? this.$store.getters.realUser : null;
      return String((ru && ru.email) || "").trim().toLowerCase() === "helpdesk@jcsteele.com";
    },
    canPublishSharedLayout() {
      if (!this.isMaster || this.viewingAsUser) return false;
      var ru = (this.$store && this.$store.getters) ? this.$store.getters.realUser : null;
      var em = String((ru && ru.email) || "").trim().toLowerCase();
      if (em !== "helpdesk@jcsteele.com") return false;
      var cc = (typeof localStorage !== "undefined") ? String(localStorage.companyCode || "").trim() : "";
      return /^NC999$/i.test(cc);
    },
    // Privileged = Master OR Sales All (preview-aware via effectiveRoleName, so a Master previewing AS a Sales rep
    // is correctly treated as non-privileged here — matching the backend). Gates the "Attribute by" rep selector:
    // only privileged users have no rep-visibility boundary, so re-basing the rep dimension can't change what data
    // they're allowed to see. (Same role test as isEmailTeamView.)
    isPrivileged() {
      var r = (this.$store && this.$store.getters) ? this.$store.getters.effectiveRoleName : "";
      return r === "Master" || r === "Sales All";
    },
    // Email Insights TEAM view: Master AND Sales All both see every rep's scanned emails (team inbox) with
    // their own private folders/completed/trash overlay. Regular "Sales" gets only their own inbox (handled
    // inside EmailInsights). The ⚙ auto-scan control stays Master-only (see :can-auto-scan on <email-insights>).
    isEmailTeamView() {
      var r = (this.$store && this.$store.getters) ? this.$store.getters.effectiveRoleName : "";
      return r === "Master" || r === "Sales All";
    },
    // A regular 'Sales' rep gets the SAME grouped view + folder buttons, but scoped to their OWN emails
    // (server-enforced). Master / Sales All get the whole team (isEmailTeamView).
    isSalesOwnEmail() {
      var r = (this.$store && this.$store.getters) ? this.$store.getters.effectiveRoleName : "";
      return r === "Sales";
    },
    // Anyone who gets the grouped/folder Email Insights view — shows the 📥 📁 ✅ 🗑 header buttons.
    isEmailFolderUser() { return this.isEmailTeamView || this.isSalesOwnEmail; },
    // Layout 1 ("default") is the locked standard layout — read-only for everyone (it changes only by re-baking DASH_DEFAULT).
    // The specific user an admin is previewing as (null normally). When set, the whole report is READ-ONLY (a preview).
    viewingAsUser() { return (this.$store && this.$store.getters) ? this.$store.getters.viewingAsUser : null; },
    // Layout 1 (Default) is read-only for everyone EXCEPT the Master viewing AS Master. Previewing another user locks everything.
    // Layout 1 and the shared Layout 2 are now read-only for EVERY account except the publisher — a peer
    // Master included. They keep their own named layouts (canCreateLayout / ＋ New layout sit outside this).
    isLayoutLocked() { return !!this.viewingAsUser || ((this.activeLayout === "default" || this.activeLayout === "shared2" || this.activeLayout === "shared3") && !this.canPublishSharedLayout); },
    // Per-card edit affordances: Master, on an editable layout.
    masterEdit() { return this.isMaster && !this.isLayoutLocked; },
    // Layout undo/redo availability (automatic version history).
    canUndo() { return this.layoutHistoryIndex > 0; },
    canRedo() { return this.layoutHistoryIndex >= 0 && this.layoutHistoryIndex < this.layoutHistory.length - 1; },
    // Sales / Sales All can IMPORT a layout file (a Master's export) → it's added as a personal layout.
    isSalesRole() {
      var r = (this.$store && this.$store.getters) ? this.$store.getters.effectiveRoleName : "";
      return r === "Sales" || r === "Sales All";
    },
    // The REAL account is a Master — true whether they're viewing as Master OR previewing another role
    // (role-preview overrides effectiveRoleName/currentUser, but viewingAsRole is only set for a previewing Master).
    isRealMaster() {
      if (!this.$store || !this.$store.getters) return false;
      var g = this.$store.getters;
      return g.effectiveRoleName === "Master" || !!g.viewingAsRole;
    },
    // Master / Sales / Sales All may act on a customer — place an order on their behalf from the
    // Saved Carts popup (clicking a cart switches into that company).
    canActForCustomer() {
      var r = (this.$store && this.$store.getters) ? this.$store.getters.effectiveRoleName : "";
      return ["Master", "Sales", "Sales All"].indexOf(r) >= 0;
    },
    // Cap the From/To date pickers at today (local) — future dates have no data.
    maxDateStr() {
      var d = new Date();
      return d.getFullYear() + "-" + ("0" + (d.getMonth() + 1)).slice(-2) + "-" + ("0" + d.getDate()).slice(-2);
    },
    // Live values content-card {tokens} resolve to (recomputed as the filters/data change).
    reportTokens() {
      var k = this.kpis || {};
      var sc = this.savedCarts || {};
      var avg = k.orderCount ? (k.totalSales / k.orderCount) : 0;
      var topC = (this.byCustomer || []).slice().sort(function (a, b) { return (Number(b.sales) || 0) - (Number(a.sales) || 0); })[0] || null;
      var topP = (this.byPart || [])[0] || null;          // byPart is qty-sorted desc
      var topT = (this.byTerritory || [])[0] || null;     // byTerritory is sales-sorted desc
      return {
        totalSales: this.fmtMoney(k.totalSales),
        invoicedSales: this.fmtMoney(k.invoicedSales),
        notInvoicedSales: this.fmtMoney(k.notInvoicedSales),
        orderCount: this.fmtNum(k.orderCount),
        invoicedOrders: this.fmtNum(k.invoicedOrderCount),
        openOrders: this.fmtNum(k.notInvoicedOrderCount),
        avgOrder: this.fmtMoney(avg),
        savedOrders: this.fmtNum(sc.savedOrders),
        savedQuotes: this.fmtNum(sc.savedQuotes),
        customerCount: this.fmtNum((this.byCustomer || []).length),
        partCount: this.fmtNum((this.byPart || []).length),
        topCustomer: topC ? (topC.customerName || topC.customerId) : "—",
        topCustomerSales: topC ? this.fmtMoney(topC.sales) : "—",
        topPart: topP ? topP.partNumber : "—",
        topTerritory: topT ? topT.territory : "—",
        dateFrom: this.fmtShort(this.filters.from),
        dateTo: this.fmtShort(this.filters.to),
      };
    },
    // The token palette shown in the editor (token + friendly label + current value).
    tokenList() {
      var t = this.reportTokens;
      return [
        { token: "{totalSales}", label: "Total sales", value: t.totalSales },
        { token: "{orderCount}", label: "Order count", value: t.orderCount },
        { token: "{avgOrder}", label: "Avg / order", value: t.avgOrder },
        { token: "{invoicedSales}", label: "Invoiced sales", value: t.invoicedSales },
        { token: "{notInvoicedSales}", label: "Not-invoiced sales", value: t.notInvoicedSales },
        { token: "{invoicedOrders}", label: "Invoiced orders", value: t.invoicedOrders },
        { token: "{openOrders}", label: "Open orders", value: t.openOrders },
        { token: "{savedOrders}", label: "Saved orders", value: t.savedOrders },
        { token: "{savedQuotes}", label: "Saved quotes", value: t.savedQuotes },
        { token: "{customerCount}", label: "# customers", value: t.customerCount },
        { token: "{partCount}", label: "# parts", value: t.partCount },
        { token: "{topCustomer}", label: "Top customer", value: t.topCustomer },
        { token: "{topCustomerSales}", label: "Top customer $", value: t.topCustomerSales },
        { token: "{topPart}", label: "Top part", value: t.topPart },
        { token: "{topTerritory}", label: "Top territory", value: t.topTerritory },
        { token: "{dateFrom}", label: "From date", value: t.dateFrom },
        { token: "{dateTo}", label: "To date", value: t.dateTo },
      ];
    },
    // Saved-carts popup — the loaded carts filtered to the clicked type (Order vs Quote).
    filteredScCarts() {
      var want = this.scDetail.type === "quote" ? "Quote" : "Order";
      // Info-only popup, sorted by Customer ID (numeric-aware so AL105 < AL120 < AL170 …).
      return (this.scDetail.carts || [])
        .filter(function (c) { return c.type === want; })
        .sort(function (a, b) {
          return String(a.customerId || "").localeCompare(String(b.customerId || ""), undefined, { numeric: true, sensitivity: "base" });
        });
    },
    // Rows to render in the saved-carts popup. When the view spans 2+ distinct sales reps (i.e. multiple
    // territories/reps are selected), segment the carts into rep GROUPS — a rep header then that rep's carts,
    // groups ordered by total $. Otherwise a flat list. Precomputes the "show customer notation once" flag so the
    // template needs no index math. Each row is { type:'rep', key, rep, territory, count, total } or { type:'cart', key, cart, showNote }.
    scDisplayRows() {
      var carts = this.filteredScCarts || [];
      if (!carts.length) return [];
      var repOf = function (c) { var r = (c.salesRep || "").trim(); return r || "(Unassigned)"; };
      var distinct = {};
      carts.forEach(function (c) { distinct[repOf(c)] = 1; });
      var seen = {};
      var rows = [];
      if (Object.keys(distinct).length <= 1) {
        // One rep (or unknown) → flat list, notation once per customer.
        carts.forEach(function (c) {
          var first = !seen[c.customerId]; seen[c.customerId] = 1;
          rows.push({ type: "cart", key: "c-" + c.cartId, cart: c, showNote: first });
        });
        return rows;
      }
      // Segment by rep. Group, then order groups by total $ desc; carts within a group keep the Customer-ID order.
      // Multi-rep breakdowns open COLLAPSED (rep header only) — a rep's carts render only when its header is
      // expanded (scExpanded[rep]). This keeps a many-rep view (a Master's) compact and lets the heavy per-part
      // AI insights load per-rep on demand rather than all up front. `seen` tracks only RENDERED carts, so the
      // "customer notation once" flag lands on the first VISIBLE cart of each customer.
      var groups = {};
      carts.forEach(function (c) {
        var r = repOf(c);
        var g = groups[r] || (groups[r] = { rep: r, territory: "", carts: [], total: 0 });
        g.carts.push(c);
        g.total += (Number(c.total) || 0);
        if (!g.territory && c.territory) g.territory = c.territory;
      });
      var exp = this.scExpanded || {};
      Object.keys(groups).map(function (k) { return groups[k]; })
        .sort(function (a, b) { return b.total - a.total; })
        .forEach(function (g) {
          var isOpen = !!exp[g.rep];
          rows.push({ type: "rep", key: "rh-" + g.rep, rep: g.rep, territory: g.territory, count: g.carts.length, total: g.total, expanded: isOpen });
          if (!isOpen) return;   // collapsed → header only
          g.carts.forEach(function (c) {
            var first = !seen[c.customerId]; seen[c.customerId] = 1;
            rows.push({ type: "cart", key: "c-" + c.cartId, cart: c, showNote: first });
          });
        });
      return rows;
    },
    // Tokens drawer — live tokens filtered by the search box.
    filteredLiveTokens() {
      var s = (this.tokenSearch || "").trim().toLowerCase();
      var list = this.tokenList;
      if (!s) return list;
      return list.filter(function (tk) { return tk.token.toLowerCase().indexOf(s) >= 0 || (tk.label || "").toLowerCase().indexOf(s) >= 0; });
    },
    // Tokens drawer — all tables/fields filtered by the search box (matches table name or column).
    filteredSchemaTables() {
      var s = (this.tokenSearch || "").trim().toLowerCase();
      var tables = this.schemaTables || [];
      if (!s) return tables;
      var out = [];
      tables.forEach(function (t) {
        var nameMatch = t.name.toLowerCase().indexOf(s) >= 0;
        var cols = nameMatch ? t.columns : (t.columns || []).filter(function (c) { return c.name.toLowerCase().indexOf(s) >= 0; });
        if (nameMatch || cols.length) out.push({ name: t.name, schema: t.schema, columns: cols });
      });
      return out;
    },
    topCustomers() {
      var list = (this.byCustomer || []).slice().sort(function (a, b) { return (Number(b.sales) || 0) - (Number(a.sales) || 0); });
      return this.l2ShowAllCustomers ? list : list.slice(0, 5);
    },
    maxL2Customer() { return (this.byCustomer || []).reduce(function (m, c) { return Math.max(m, Number(c.sales) || 0); }, 0); },
    topParts() {
      var list = this.byPart || [];   // already qty-sorted desc from the backend
      return this.l2ShowAllParts ? list : list.slice(0, 5);
    },
    maxL2Part() { return (this.byPart || []).reduce(function (m, p) { return Math.max(m, Number(p.qty) || 0); }, 0); },
    topTerritories() {
      return (this.byTerritory || []).slice().sort(function (a, b) { return (Number(b.sales) || 0) - (Number(a.sales) || 0); }).slice(0, 8);
    },
    maxL2Terr() { return (this.byTerritory || []).reduce(function (m, t) { return Math.max(m, Number(t.sales) || 0); }, 0); },
    pageCount() { return Math.max(1, Math.ceil(this.totalRows / this.pageSize)); },
    maxCustomerSales() { return this.byCustomer.reduce((m, c) => Math.max(m, Number(c.sales) || 0), 0); },
    maxPartSales() { return this.byPart.reduce((m, p) => Math.max(m, Number(p.sales) || 0), 0); },
    maxTerritorySales() { return this.byTerritory.reduce((m, t) => Math.max(m, Number(t.sales) || 0), 0); },
    totalTerritorySales() { return this.byTerritory.reduce((s, t) => s + (Number(t.sales) || 0), 0); },
    maxRepSales() { return (this.bySalesRep || []).reduce((m, r) => Math.max(m, Number(r.sales) || 0), 0); },
    // KPI card deltas vs the prior equal-length window (null => no prior baseline, so no delta is shown).
    dSales() { return this.kpiDelta((this.kpis || {}).totalSales, (this.kpisPrior || {}).totalSales); },
    dOrders() { return this.kpiDelta((this.kpis || {}).orderCount, (this.kpisPrior || {}).orderCount); },
    dAvg() {
      var k = this.kpis || {}, p = this.kpisPrior || {};
      var cur = k.orderCount ? k.totalSales / k.orderCount : 0;
      var pri = p.orderCount ? p.totalSales / p.orderCount : 0;
      return this.kpiDelta(cur, pri);
    },
    // Customer options for the top filter bar's searchable dropdown (from the faceted byCustomer list).
    // {value,label}: value is the Customer ID (what the filter stores/matches); label shows "ID — Name".
    customerOptions() {
      var seen = {}, out = [];
      (this.byCustomer || []).forEach(function (c) {
        if (c && c.customerId && !seen[c.customerId]) { seen[c.customerId] = 1; out.push({ value: c.customerId, label: c.customerName ? (c.customerId + " — " + c.customerName) : c.customerId }); }
      });
      // Keep every selected customer listed even if it's outside the current scope's byCustomer list.
      (this.filters.customer || []).forEach(function (id) {
        if (id && !seen[id]) { seen[id] = 1; out.push({ value: id, label: String(id) }); }
      });
      return out;
    },
    // Per-customer territory segments for the Customer ID(s) bars: customerId -> [{territory, salesRep, orderCount}]
    // (desc by orderCount). From byCustomerTerritory (scoped to the current date + filters). A customer whose orders
    // span >1 territory IN SCOPE → a multi-color segmented bar; a single territory (e.g. date range narrowed to one
    // rep) → one solid color. This is how a customer like NE150 (Midwest + Northern&Canada history) reads at a glance.
    customerSegments() {
      var map = {};
      (this.byCustomerTerritory || []).forEach(function (r) {
        if (!r || r.customerId == null) return;
        (map[r.customerId] || (map[r.customerId] = [])).push({ territory: r.territory || "(Unassigned)", salesRep: r.salesRep || "", orderCount: Number(r.orderCount) || 0 });
      });
      Object.keys(map).forEach(function (k) { map[k].sort(function (a, b) { return b.orderCount - a.orderCount; }); });
      return map;
    },
    // territory -> color, so each bar segment matches the map / Sales-by-Territory colors.
    territoryColorMap() {
      var self = this, m = {}, terrs = {};
      (this.byCustomerTerritory || []).forEach(function (r) { if (r && r.territory) terrs[r.territory] = 1; });
      (this.byTerritory || []).forEach(function (t) { if (t && t.territory) terrs[t.territory] = 1; });
      Object.keys(terrs).forEach(function (t) { m[t] = self.terrColor(t); });
      return m;
    },
    // Territory options for the top filter bar — derived from the loaded, SCOPED byTerritory aggregate (the
    // same data behind the Sales-by-Territory card and customerOptions). This keeps all the data-driven
    // dropdowns honest: they only offer values that actually exist for the active company scope, so you can't
    // pick a territory that yields "no matching orders". (Earlier this unioned in the unscoped /filters list,
    // which offered every territory system-wide even for a company with none — that was the bug.) byTerritory
    // loads reliably with the main report, so no fallback list is needed.
    territoryOptions() {
      var seen = {}, out = [];
      (this.byTerritory || []).forEach(function (t) { var v = t && t.territory; if (v && !seen[v]) { seen[v] = 1; out.push(v); } });
      // ALWAYS include the currently-selected territories so the multi-select shows (and can un-check) every
      // chosen value — even one with no data under the current filters, which would otherwise vanish here.
      (this.filters.territory || []).forEach(function (v) { if (v && !seen[v]) { seen[v] = 1; out.push(v); } });
      return out.sort(function (a, b) { return String(a).localeCompare(String(b)); });
    },
    // Sales-rep options — same approach: the loaded SCOPED bySalesRep leaderboard PLUS the currently-selected
    // reps (so all selected reps — including cross-added ones with no data in scope — stay listed).
    repOptions() {
      var seen = {}, out = [];
      (this.bySalesRep || []).forEach(function (r) { var v = r && r.salesRep; if (v && !seen[v]) { seen[v] = 1; out.push(v); } });
      (this.filters.salesRep || []).forEach(function (v) { if (v && !seen[v]) { seen[v] = 1; out.push(v); } });
      return out.sort(function (a, b) { return String(a).localeCompare(String(b)); });
    },
    // Un-selected territories / reps for the cards' "＋ Add" pickers. Sourced from the COMPLETE unscoped lists
    // (opts.*, the DISTINCT values from /customer-insights/filters) — NOT the scoped byTerritory/bySalesRep — so
    // you can add ANY territory/rep at any time, even ones with no data under the current Customer/Part/Code
    // filters. (Adding such a value just surfaces it as a struck-through "dead" filter with the why-tooltip.)
    // Exclude BOTH selected filters AND whatever's already rendered on the card (territoryCardRows / repCardRows)
    // — no point offering to "add" a row that's already shown. Leaves only rows NOT currently on the card.
    addableTerritoryOptions() {
      var shown = {};
      (this.territoryCardRows || []).forEach(function (t) { if (t && t.territory) shown[String(t.territory).trim().toUpperCase()] = 1; });
      (this.filters.territory || []).forEach(function (t) { shown[String(t).trim().toUpperCase()] = 1; });
      return ((this.opts && this.opts.territories) || []).filter(function (t) { return t && !shown[String(t).trim().toUpperCase()]; });
    },
    addableRepOptions() {
      var norm = function (s) { return String(s == null ? "" : s).toUpperCase().replace(/\s+/g, ""); };   // name-safe (opts.* is untrimmed)
      var shown = {};
      (this.repCardRows || []).forEach(function (r) { if (r && r.salesRep) shown[norm(r.salesRep)] = 1; });
      (this.filters.salesRep || []).forEach(function (r) { shown[norm(r)] = 1; });
      return ((this.opts && this.opts.salesReps) || []).filter(function (r) { return r && !shown[norm(r)]; });
    },
    // Selected territories/reps move to chips above and drop out of their card list (matches the
    // Customer ID(s) + Part(s) Ordered cards) so the remaining, still-addable values stay visible.
    visibleTerritories() {
      var sel = this.filters.territory || [];
      return (this.byTerritory || []).filter((t) => sel.indexOf(t.territory) < 0);
    },
    visibleReps() {
      var sel = this.filters.salesRep || [];
      return (this.bySalesRep || []).filter((r) => sel.indexOf(r.salesRep) < 0);
    },
    // The Sales-by-Territory card reflects the Territory FILTER: when territories are selected it shows EXACTLY
    // those (in selection order) — each one's data row, or a $0 placeholder if it has no sales under the current
    // filters — so every selected territory stays visible. No selection → every territory (byTerritory is
    // faceted so it always holds them all). Clicking a shown territory toggles it; add more via the dropdown.
    territoryCardRows() {
      var sel = this.filters.territory || [];
      var all = this.byTerritory || [];
      if (!sel.length) return all;
      var byName = {};
      all.forEach(function (t) { if (t && t.territory) byName[t.territory] = t; });
      return sel.map(function (name) { return byName[name] || { territory: name, sales: 0 }; });
    },
    // Bars scale to the largest DISPLAYED territory so a single selected territory still fills its bar.
    maxTerritoryCardSales() {
      return (this.territoryCardRows || []).reduce((m, t) => Math.max(m, Number(t.sales) || 0), 0);
    },
    // 💡 Sales-by-Territory insight: the sales reps that make up each territory's total (from the loaded rep
    // leaderboard, grouped by each rep's territory). Powers the per-territory 💡 shown when >1 rep is included.
    territoryContributors() {
      var m = {};
      (this.bySalesRep || []).forEach(function (r) {
        if (!r) return;
        var terr = (r.territory == null ? "" : String(r.territory)).trim();
        if (!terr) return;
        if (Math.round((Number(r.sales) || 0) * 100) === 0) return;   // drop $0 noise
        if (!m[terr]) m[terr] = [];
        m[terr].push({ rep: r.salesRep || "(Unassigned)", sales: Number(r.sales) || 0 });
      });
      Object.keys(m).forEach(function (k) { m[k].sort(function (a, b) { return b.sales - a.sales; }); });
      return m;
    },
    // 💡 Rep-history popup — show ONLY the VISUAL-original groups that genuinely differ from how the customer is
    // credited now: orders with no rep in VISUAL (auto-assigned by the prefix rule) or a rep that is no longer one
    // of the customer's current owners (an alias / reassignment). A group whose original rep is still a current
    // owner is NOT a change — listing it (with its old blank territory) is what made the popup look contradictory
    // ("attributed to X" up top AND "X (Unassigned)" below), so we drop it.
    codWhyHistoryRows() {
      var norm = function (s) { return String(s == null ? "" : s).toUpperCase().replace(/\s+/g, ""); };
      var curReps = (this.codWhyPop.attribution || []).map(function (a) { return norm(a.rep); });
      return (this.codWhyPop.history || []).filter(function (h) {
        var r = norm(h && h.rep);
        if (!r) return true;                        // no rep in VISUAL → auto-assigned; always worth showing
        return curReps.indexOf(r) === -1;           // a rep that is NOT a current owner → real alias / rep change
      });
    },
    // Sales Reps card — same rule as territoryCardRows: show EXACTLY the selected reps (a $0 placeholder for a
    // selected rep with no sales in the current scope, so every selected rep appears), else every rep.
    repCardRows() {
      var sel = this.filters.salesRep || [];
      var all = this.bySalesRep || [];
      if (!sel.length) {
        // Hide reps whose sales rounds to $0.00 (noise) — EXCEPT (Unassigned), which a Master must ALWAYS see as the
        // entry point to the 💡 diagnostic, even at $0.00. No other role ever sees (Unassigned). ORDER MATTERS: the
        // (Unassigned) exception is checked BEFORE the $0.00 drop, else a $0 (Unassigned) row gets filtered out first.
        var isMaster = this.isMaster;
        var rows = all.filter(function (r) {
          if (!r) return false;
          if (r.salesRep === "(Unassigned)") return isMaster;    // Master: keep even at $0.00; every other role: drop
          return Math.round((Number(r.sales) || 0) * 100) !== 0; // real reps: drop $0.00 noise
        });
        // The backend GROUP BY omits the (Unassigned) row ENTIRELY when the scope has zero unassigned orders (e.g. a
        // recent date range — every recent order has a rep). A Master still needs the diagnostic anchor, so synthesize
        // a $0.00 (Unassigned) row (rendered last, since it's the smallest) whenever none came back from the query.
        if (isMaster && !rows.some(function (r) { return r.salesRep === "(Unassigned)"; })) {
          rows.push({ salesRep: "(Unassigned)", sales: 0, orders: 0, territory: "" });
        }
        return rows;
      }
      // NAME-SAFE: the order data spells reps one way (e.g. "SCOTT APPLEGATE") while a rep added from the admin map /
      // picker can differ in case/space ("Scott Applegate") — an exact byName[name] lookup then MISSED the real row and
      // rendered a $0 fallback (the KPIs were right because SQL matches case-insensitively; only this card was wrong).
      var norm = function (s) { return String(s == null ? "" : s).toUpperCase().replace(/\s+/g, ""); };
      var byName = {};
      all.forEach(function (r) { if (r && r.salesRep) byName[norm(r.salesRep)] = r; });
      return sel.map(function (name) {
        return byName[norm(name)] || { salesRep: name, sales: 0, territory: (this.repTerritoriesFor(name)[0] || '') };
      }.bind(this));
    },
    maxRepCardSales() {
      return (this.repCardRows || []).reduce((m, r) => Math.max(m, Number(r.sales) || 0), 0);
    },
    // rep -> its territory color, so the Customer ID(s) bars match the rep's color on the Sales Reps
    // / Sales by Territory cards (a rep is the same color everywhere). Built from the rep leaderboard.
    repTerritoryColors() {
      var map = {};
      (this.bySalesRep || []).forEach((r) => { if (r && r.salesRep) map[r.salesRep] = this.terrColor(r.territory); });
      return map;
    },
    // customerId -> the color of the customer's OWN territory (their state's territory, via the admin
    // map — the SAME logic the map uses). Stable regardless of which reps are in bySalesRep (which
    // narrows when a customer is filtered), so the Customer bars always match the map + territory chips.
    customerColorMap() {
      var stateTerr = {};
      (this.territoryAssignments || []).forEach((a) => {
        if (!a || !a.state || (a.territory || "") === "International") return;
        var k = String(a.state).toUpperCase();
        if (!stateTerr[k]) stateTerr[k] = a.territory;
      });
      var m = {};
      (this.byCustomer || []).forEach((c) => {
        if (!c || !c.customerId) return;
        var terr = stateTerr[String(c.customerId).slice(0, 2).toUpperCase()];
        if (terr) m[c.customerId] = this.terrColor(terr);
      });
      return m;
    },
    // Admin-set territory colors (Account Setup → Sales Territories, Color column): territory -> hex.
    // First non-empty color per territory wins; terrColor() prefers these over the built-in palette.
    adminTerrColors() {
      var m = {};
      (this.territoryAssignments || []).forEach(function (a) {
        if (a && a.territory && a.color && !m[a.territory]) m[a.territory] = a.color;
      });
      return m;
    },
    // When the data is scoped to exactly one territory, tint the otherwise-neutral Part bars with it
    // (selecting a rep cross-adds its territory, so picking a rep tints the parts too).
    activeTerritoryColor() {
      var t = this.filters.territory || [];
      return t.length === 1 ? this.terrColor(t[0]) : null;
    },
    // The report's accent color, exposed as the --ci-accent CSS variable on .ci-wrap so every count /
    // value (KPIs, saved carts, code counts) and the Customer Order Details rows share ONE color:
    // the territory's admin color (rpt_SalesTerritory.Color via terrColor) when the view is scoped to a
    // single territory (a Sales user, or a one-territory filter), else the brand red.
    accentColor() {
      if (this.activeTerritoryColor) return this.activeTerritoryColor;
      var t = (this.byTerritory || []).filter(function (x) { return x && x.territory && x.territory !== "(Unassigned)"; });
      if (t.length === 1) return this.terrColor(t[0].territory);
      // NOT scoped to one territory (a Master / all-territories view) → return EMPTY so --ci-accent is left
      // UNSET and each theme's own CSS fallback decides: maroon in light, the chat blue in dark. Hard-coding
      // #9e2436 here made the fallback theme-blind and impossible to override per-theme (the inline var always
      // beat any stylesheet rule). The single-territory branches above still win, so the TERRITORY signal on
      // KPI values is preserved in every theme. (2026-07-26)
      return "";
    },
    // Full-US territory map: every assigned state colored by its territory
    // (admin-managed). International is non-geographic and never paints a state,
    // so a state like NC keeps its SouthEast color. Current-filter order sales
    // are merged in for the hover tooltip only — the coloring itself is static.
    mapStates() {
      const sales = {};
      (this.byState || []).forEach((s) => { if (s && s.state) sales[String(s.state).toUpperCase()] = s; });
      const geo = {};
      (this.territoryAssignments || []).forEach((a) => {
        if (!a || !a.state || (a.territory || "") === "International") return;
        const k = String(a.state).toUpperCase();
        if (!geo[k]) geo[k] = a.territory; // first non-International assignment wins
      });
      return Object.keys(geo).map((k) => {
        const s = sales[k];
        return { state: k, territory: geo[k], sales: s ? s.sales : 0, orderCount: s ? s.orderCount : 0 };
      });
    },
    // Legend = every distinct territory in the assignments (incl. International),
    // alphabetical — matches the Power BI legend order.
    mapLegend() {
      const names = Array.from(new Set((this.territoryAssignments || [])
        .map((a) => (a && a.territory ? a.territory : "")).filter(Boolean))).sort();
      return names.map((n) => ({ name: n, color: this.terrColor(n) }));
    },
    // Highlight the selected customer's state when one is chosen; else the clicked
    // state. So picking a customer shows where they are on the map.
    mapSelectedState() {
      // Highlight every selected state filter, plus every SELECTED CUSTOMER's home state (customer
      // IDs are state-prefixed, e.g. AL275 -> AL, NC195 -> NC), plus the single-customer story state.
      // These states STAND OUT (bold outline) while the territory coloring stays fully intact.
      var arr = (this.filters.state || []).slice();
      (this.filters.customer || []).forEach((id) => {
        var st = String(id || "").slice(0, 2).toUpperCase();
        if (STATE_NAMES[st] && arr.indexOf(st) < 0) arr.push(st);
      });
      var cs = this.story.customer && this.story.customer.state;
      if (cs && arr.indexOf(cs) < 0) arr.push(cs);
      return arr;
    },
    // Territories to highlight on the map. Explicit territory chips win; otherwise,
    // when the data is narrowed by other filters (customers/parts/etc.), light up
    // the territories actually present in the filtered results (e.g. picking
    // SouthEast customers highlights SouthEast and whites out the rest).
    mapSelectedTerritory() {
      if (this.filters.territory.length) return this.filters.territory.slice();
      if (!this.hasNarrowingFilter) return [];
      return Array.from(new Set((this.byTerritory || []).map((t) => t.territory).filter(Boolean)));
    },
    // Countries to bold-outline on the WORLD map: explicit country chips, PLUS — when customers are
    // filtered — the country each selected customer resolves to (from byCountry). So clicking the Brazil
    // account XS160 black-outlines Brazil, mirroring how clicking a US customer outlines their state.
    mapSelectedCountry() {
      var arr = (this.filters.country || []).slice();
      if ((this.filters.customer || []).length) {
        (this.byCountry || []).forEach(function (c) {
          if (c && c.country && arr.indexOf(c.country) < 0) arr.push(c.country);
        });
      }
      return arr;
    },
    // Favorites not in any group (rendered loose in the bar; grouped ones live inside their group popover).
    ungroupedFavorites() { return (this.favorites || []).filter(function (f) { return !f.groupId; }); },
    // The favorite OR group whose hover-popover is currently open.
    currentPopItem() {
      if (!this.favPopId) return null;
      var list = this.favPopKind === "group" ? this.favoriteGroups : this.favorites;
      return (list || []).find((x) => x.id === this.favPopId) || null;
    },
    // Title shown in the popover (the staged draft name while editing, else the live name).
    favPopName() { return this.favDraft ? this.favDraft.name : (this.currentPopItem ? this.currentPopItem.name : ""); },
    // The signed-in rep's email (the digest's primary recipient) — shown in the schedule modal. Defensive
    // about the currentUser shape; cosmetic only (the backend resolves the real recipient from the token).
    myEmail() {
      var u = (this.$store && this.$store.getters && this.$store.getters.currentUser) || null;
      return u ? (u.email || u.emailAddress || u.userName || u.username || "") : "";
    },
    // The email schedule (if any) for the favorite/group whose hover popover is open — shown at a glance.
    currentPopSchedule() {
      return this.currentPopItem ? this.notifyExistingFor(this.currentPopItem.id) : null;
    },
    // The filter chips shown in a loose favorite's popover (from the draft while editing, else the saved snapshot).
    favPopChips() {
      if (!this.currentPopItem || this.favPopKind === "group") return [];
      return this.chipsFromFilters(this.favDraft ? this.favDraft.filters : this.currentPopItem.filters);
    },
    favDirty() { return !!this.favDraft; },   // unsaved edits → show ✓ Save + pin the popover open
    // True when a non-territory, non-date filter is narrowing the data.
    hasNarrowingFilter() {
      var f = this.filters;
      return !!(f.customer.length || f.part.length || f.state.length || f.salesRep || f.status ||
        (f.invoiced && f.invoiced !== "all") || f.machineCodeCategory || f.machineCodePrefix || f.orderNo);
    },
    // Default (unfiltered) window = year-to-date, order-date basis.
    datesChanged() {
      const now = new Date();
      return this.filters.from !== (now.getFullYear() + "-01-01") ||
             this.filters.to !== now.toISOString().slice(0, 10) ||
             this.filters.dateBasis !== "order";
    },
    // Year-over-year only makes sense when the selected range spans ~a year or more; otherwise hidden.
    yoyEligible() {
      var f = this.filters;
      if (!f.from || !f.to) return false;
      var from = new Date(f.from + "T00:00:00"), to = new Date(f.to + "T00:00:00");
      if (isNaN(from.getTime()) || isNaN(to.getTime())) return false;
      return (to - from) / 86400000 >= 360;
    },
    // Active filter chips (one per narrowing filter; dates only if changed).
    activeChips() {
      var out = this.chipsFromFilters(this.filters);
      if (this.datesChanged) out.push({ key: "__dates", label: "Dates", text: this.fmtShort(this.filters.from) + " – " + this.fmtShort(this.filters.to) });
      return out;
    },
    // Plain-English synopsis of the CURRENT filters — the ✓ banner after the hero chat auto-applies a view.
    // Grouped by dimension with labels ("Sales Rep: Kevin Cornett · Status: CLOSED, HOLD") and ALWAYS ends
    // with the date range (unlike favorites, a synopsis must say what period the view covers). Live-computed,
    // so it stays truthful if the rep tweaks a filter while the banner is up.
    filterSynopsis() {
      var parts = [], byLabel = {}, order = [];
      (this.activeChips || []).forEach(function (c) {
        if (c.key === "__dates") return;   // dates get their own always-on trailing entry below
        var k = c.label || "Filter";
        if (!byLabel[k]) { byLabel[k] = []; order.push(k); }
        if (c.text && byLabel[k].indexOf(c.text) < 0) byLabel[k].push(c.text);
      });
      order.forEach(function (k) { parts.push(k + ": " + byLabel[k].join(", ")); });
      if (!parts.length) parts.push("All customers and reps");
      parts.push("Dates: " + this.fmtShort(this.filters.from) + " – " + this.fmtShort(this.filters.to));
      return parts.join("  ·  ");
    },
    // When EXACTLY ONE customer is selected, surface that customer's VMFG Notation + Specifications in the hero
    // (the same notes shown by the 📝/📋 popovers on the Customer ID(s) card). Sourced from the byCustomer facet
    // rows (notes lazily merged by loadCustomerNotes). Returns null unless a single customer is picked AND it has
    // at least one of notation/specs — so the chips appear only when there's something to show.
    heroCustomerNotes() {
      var arr = (this.filters && this.filters.customer) || [];
      if (arr.length !== 1) return null;
      var id = String(arr[0]);
      var c = (this.byCustomer || []).find(function (x) { return x && String(x.customerId) === id; });
      if (!c) return null;
      var notation = c.customerNotation ? String(c.customerNotation) : "";
      var specs = c.customerSpecs ? String(c.customerSpecs) : "";
      if (!notation && !specs) return null;
      return { id: id, name: c.customerName || "", notation: notation, specs: specs };
    },
    // activeChips grouped by dimension (key) so the rail renders ONE row per filter: a single value stays a plain
    // chip; multiple values (e.g. Status = CLOSED/HOLD/RELEASED) collapse into a dropdown-to-unselect. `summary` is
    // the comma-joined values for the pill's tooltip; `vals` keeps the original chip objects (removeChip/color/etc.).
    activeChipGroups() {
      var groups = [], byKey = {};
      (this.activeChips || []).forEach(function (c) {
        var g = byKey[c.key];
        if (!g) { g = byKey[c.key] = { key: c.key, label: c.label, vals: [] }; groups.push(g); }
        g.vals.push(c);
      });
      groups.forEach(function (g) { g.summary = g.vals.map(function (v) { return v.text; }).join(", "); });
      return groups;
    },
    // Live filters (still affecting results) show inline above favorites; the struck-through "dead" ones
    // (excluded by another filter) move into the More panel so the main row stays clean.
    relevantChips() { var self = this; return this.activeChips.filter(function (c) { return !self.chipIrrelevant(c); }); },
    // activeChips with the hovered card chip pulled to the front (rest keep their order) — drives the shared chip popover.
    chipHoverSorted() {
      var key = this.chipHoverKey, self = this;
      return (this.activeChips || []).slice().sort(function (a, b) {
        return (self._chipKey(a) === key ? 0 : 1) - (self._chipKey(b) === key ? 0 : 1);
      });
    },
    // Which date-preset dropdown is "active" (its button stays red-filled) — derived from activePreset.
    qtrActive() { return /^q[1-4]$/.test(this.activePreset); },       // QTR: a quarter is applied
    calYearActive() { return /^\d+$/.test(this.activePreset); },       // YEAR: a calendar-year count is applied (pure digits)
    // Split the unassigned accounts by which team fixes them: PARTS = needs the rep set in VISUAL (cause
    // prefix-ready / no-rep); IT = rep/prefix not in the territory map (cause rep-unmapped).
    unPartsCusts() {
      var cs = (this.unassignedPop.data && this.unassignedPop.data.customers) || [];
      return cs.filter(function (c) { return c && (c.cause === "prefix-ready" || c.cause === "no-rep"); });
    },
    unItCusts() {
      var cs = (this.unassignedPop.data && this.unassignedPop.data.customers) || [];
      return cs.filter(function (c) { return c && c.cause === "rep-unmapped"; });
    },
    // The accounts the email modal previews = the chosen target's subset (what actually gets emailed).
    unEmailCusts() { return this.unEmail.target === "parts" ? this.unPartsCusts : this.unItCusts; },
    rollActive() { return /^r\d+$/.test(this.activePreset); },         // TRAILING: a rolling-year count is applied (rN)
    // On mobile (< 992px) the grid renders the fixed single-column stack (mobileLayout); on desktop, the normal dashLayout.
    // (The old Master-only 'ask' card is gone from MOBILE_LAYOUT — the hero Ask serves every role now.)
    effectiveLayout() {
      var self = this;
      // Desktop: the normal dashLayout (which no longer contains 'filters' — that lives in the left rail; see
      // ensureBuiltinCards). Docked-by-default is applied IMPERATIVELY in applyLayout (strip + dashHidden) —
      // NEVER by filtering dashLayout here: vue-grid-layout writes x/y back into these items on every layout
      // pass, so a render-time filter of dashLayout recomputes → new array → re-layout → infinite render loop
      // (page freeze). Same trap as the old 'filters' card. Filtering mobileLayout below is safe — it's a
      // static constant the grid never mutates.
      if (!this.isMobile) return this.dashLayout;
      return this.mobileLayout.filter(function (it) {
        // MOBILE DOCK: ▶ on Email Insights hides Email AND Orders In Process together; the red
        // EMAIL edge tab (mirrors the TOOLS handle) restores both. Reactive on emailDocked.
        if (self.emailDocked && (it.i === "emailInsights" || it.i === "ordersInProcess")) return false;
        // Filters card docked → hidden until the FILTERS edge tab restores it. Safe here (mobileLayout is a
        // static constant the grid never mutates — unlike dashLayout on desktop; see the note above).
        if (self.filtersDocked && it.i === "filters") return false;
        return true;
      });
    },
    irrelevantChips() { var self = this; return this.activeChips.filter(function (c) { return self.chipIrrelevant(c); }); },
    // Active filters (other than the rep<->territory cross-pair) that NARROW the Sales by Territory
    // and Sales Reps facets — the chips a user would remove to reveal more rows in those cards.
    crossFacetLimiters() {
      return this.activeChips.filter((c) => c.key !== "territory" && c.key !== "salesRep");
    },
    // Filter values still present in the FULL-filtered result, grouped by dimension (from the backend
    // presentValues). A tracked dim absent here means NONE of its values survived (all its chips dead).
    presentByDim() {
      // Also index a NORMALIZED (upper, spaces stripped) alias of each present value so a filter chip that differs
      // only in case/space (e.g. admin "Scott Applegate" vs order-data "SCOTT APPLEGATE") isn't wrongly struck-through.
      var norm = function (s) { return String(s == null ? "" : s).toUpperCase().replace(/\s+/g, ""); };
      var m = {};
      (this.presentValues || []).forEach(function (pv) {
        if (!pv || !pv.dim) return;
        if (!m[pv.dim]) m[pv.dim] = {};
        m[pv.dim][String(pv.val)] = true;
        m[pv.dim][norm(pv.val)] = true;
      });
      return m;
    },
    // Unified Machine & Part Codes card: byCodes grouped by dimension, filtered by the search box,
    // with already-selected values dropped (they show as chips). Empty groups are hidden.
    codeGroups() {
      var q = (this.codeSearch || "").trim().toLowerCase();
      var dims = [
        { key: "machineCodeCategory", label: "Machine Code Category" },
        { key: "machineCodePrefix", label: "Machine Code Prefix" },
        { key: "machineCodeDescription", label: "Machine Code Description" },
        { key: "partDescription", label: "Part Description" },
      ];
      var byDim = {};
      (this.byCodes || []).forEach(function (c) {
        if (!c || !c.dim || c.val == null) return;
        (byDim[c.dim] = byDim[c.dim] || []).push(c);
      });
      var self = this;
      return dims.map(function (d) {
        var sel = self.filters[d.key] || [];
        var items = (byDim[d.key] || []).filter(function (c) {
          return sel.indexOf(c.val) < 0 && (!q || String(c.val).toLowerCase().indexOf(q) >= 0);
        });
        var max = items.reduce(function (mx, c) { return Math.max(mx, Number(c.orderCount) || 0); }, 0);
        return { key: d.key, label: d.label, items: items, max: max };
      }).filter(function (g) { return g.items.length; });
    },
    // "dim|val" -> [{rep, orderCount, pct, color}] (order-count desc) — the rep composition for each code row's bar,
    // colored by the rep's territory (name-safe via repTerritoriesFor). Empty for rows past the backend's per-dim slice.
    codeRepSegments() {
      var self = this;
      var m = {};
      (this.byCodeRep || []).forEach(function (r) {
        if (!r || !r.dim || r.val == null || !r.salesRep) return;
        var key = r.dim + "|" + r.val;
        (m[key] = m[key] || []).push({ rep: String(r.salesRep), orderCount: Number(r.orderCount) || 0 });
      });
      var colorCache = {};
      var colorFor = function (rep) {
        var k = String(rep || "");
        if (colorCache[k] == null) colorCache[k] = self.terrColor((self.repTerritoriesFor(rep)[0]) || "");
        return colorCache[k];
      };
      Object.keys(m).forEach(function (key) {
        var segs = m[key];
        segs.sort(function (a, b) { return b.orderCount - a.orderCount; });
        var total = segs.reduce(function (s, x) { return s + x.orderCount; }, 0);
        segs.forEach(function (x) { x.pct = total > 0 ? (x.orderCount / total * 100).toFixed(2) + "%" : "0%"; x.color = colorFor(x.rep); });
      });
      return m;
    },
    // Selected code values across all four code dims → chips shown at the top of the Machine & Part Codes card
    // (codeGroups already drops selected values from its lists). Each removed via toggleFilter(dim, val).
    codeChips() {
      var out = [];
      var f = this.filters;
      ["machineCodeCategory", "machineCodePrefix", "machineCodeDescription", "partDescription"].forEach(function (dim) {
        (f[dim] || []).forEach(function (v) { out.push({ dim: dim, val: v }); });
      });
      return out;
    },
    // Show the floating story only once something is actually filtered.
    hasActiveFilters() { return this.activeChips.length > 0; },
    // Filters are active but the combined result is empty (selections don't overlap).
    noResults() {
      return this.hasActiveFilters && !this.loading && !this.notReady && this.totalRows === 0;
    },
    // Plain-English narration of the current filter state.
    storySentence() {
      const f = this.filters;
      const parts = [];
      let lead = "Showing ";
      if (f.invoiced === "invoiced") lead += "invoiced ";
      else if (f.invoiced === "not") lead += "not-yet-invoiced ";
      lead += "orders from " + this.fmtShort(f.from) + " to " + this.fmtShort(f.to);
      if (f.dateBasis === "invoice") lead += " (by invoice date)";
      parts.push(lead);
      if (f.customer) parts.push("for customer “" + f.customer + "”");
      if (f.state) parts.push("in " + this.stateName(f.state));
      if (f.territory) parts.push("in the " + f.territory + " territory");
      if (f.salesRep) parts.push("handled by " + f.salesRep);
      if (f.part) parts.push("for part “" + f.part + "”");
      if (f.machineCodeCategory) parts.push("machine category " + f.machineCodeCategory);
      if (f.machineCodePrefix) parts.push("machine prefix " + f.machineCodePrefix);
      if (f.status) parts.push("status " + f.status);
      if (f.orderNo) parts.push("order # matching “" + f.orderNo + "”");
      return parts.join(", ") + ".";
    },
    storyStyle() {
      if (!this.storyPos) return null;
      return { left: this.storyPos.left + "px", top: this.storyPos.top + "px", right: "auto", bottom: "auto" };
    },
    // Top-2 parts (by qty) + how many others.
    storyParts() {
      const list = this.byPart || [];
      if (!list.length) return "";
      const top = list.slice(0, 2).map((p) => p.partNumber + " (" + this.fmtNum(p.qty) + ")");
      let s = "Top part" + (top.length > 1 ? "s" : "") + ": " + top.join(" and ");
      const others = list.length - top.length;
      if (others > 0) s += ", plus " + others + " other" + (others === 1 ? "" : "s");
      return s + ".";
    },
    // Top-2 parts across ALL customers (summative section).
    summaryTopParts() {
      const list = (this.story.summary && this.story.summary.topParts) || [];
      if (!list.length) return "";
      const top = list.slice(0, 2).map((p) => p.partNumber + " (" + this.fmtNum(p.qty) + ")");
      return "Top: " + top.join(" and ") + ".";
    },
    // The AI narrative split into renderable lines. Lines starting with -, •, * or "N." are
    // treated as bullets (the leading marker is stripped); blank lines are dropped.
    aiInsightsLines() {
      const raw = this.aiInsights || "";
      return raw.split(/\r?\n/)
        .map((l) => l.trim())
        .filter((l) => l.length > 0)
        .map((l) => {
          const m = l.match(/^([-•*]|\d+[.)])\s+(.*)$/);
          return m ? { bullet: true, text: m[2] } : { bullet: false, text: l };
        });
    },
    // The AI answer split into renderable lines (same bullet rules as aiInsightsLines). Markdown bold
    // markers are STRIPPED — the model occasionally emits **name** despite the prompt, and raw asterisks
    // were showing verbatim in the hero (and being read aloud by the talk-back).
    aiAnswerLines() {
      const raw = this.aiAnswer || "";
      return raw.split(/\r?\n/)
        .map((l) => l.replace(/\*\*/g, "").trim())
        .filter((l) => l.length > 0)
        .map((l) => {
          const m = l.match(/^([-•*]|\d+[.)])\s+(.*)$/);
          return m ? { bullet: true, text: m[2] } : { bullet: false, text: l };
        });
    },
    // The AI-proposed filter spec rendered as readable chips for the "Apply this view" preview.
    aiProposedChips() {
      const s = this.aiProposed;
      if (!s) return [];
      const out = [];
      const dimLabels = { customer: "Customer", salesRep: "Rep", territory: "Territory", country: "Country", state: "State", status: "Status", part: "Part",
        partDescription: "Part desc.", machineCodeCategory: "Machine category", machineCodePrefix: "Machine code", machineCodeDescription: "Machine desc." };
      Object.keys(dimLabels).forEach((k) => {
        if (Array.isArray(s[k])) s[k].forEach((v) => out.push({ label: dimLabels[k] + ": " + v }));
      });
      if (s.from || s.to) out.push({ label: "Dates: " + (s.from || "…") + " → " + (s.to || "…") });
      if (s.invoiced && s.invoiced !== "all") out.push({ label: s.invoiced === "invoiced" ? "Invoiced only" : "Open only" });
      return out;
    },
    // Processing-overlay: the compact date range being searched (month + year, e.g. "Jan 1990 – Jul 2026"),
    // shown ABOVE the spinner. Empty when neither bound is set.
    procDateLabel() {
      var monthYear = function (d) {
        if (!d) return "";
        var dt = new Date(String(d).length <= 10 ? d + "T00:00:00" : d);
        return isNaN(dt.getTime()) ? "" : dt.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      };
      var a = monthYear(this.filters.from), b = monthYear(this.filters.to);
      if (a && b) return a + " – " + b;
      return a || b || "";
    },
    // Processing-overlay: the "estimated time remaining" line BELOW "Asking…". Counts down from the per-run
    // estimate; once elapsed passes it, we stop pretending and say "almost there…".
    // ✕ tooltip matches whatever the overlay is actually waiting on.
    procStopLabel() {
      if (this.aiAskBusy) return "Stop the search";
      if (this.aiInsightsBusy) return "Stop waiting for the insights";
      return "Stop loading";
    },
    procRemainLabel() {
      var elapsed = Math.floor((this.procElapsedMs || 0) / 1000);
      var remain = (this.procEstimateSec || 0) - elapsed;
      return remain > 0 ? ("about " + remain + "s remaining") : "almost there…";
    },
    // The active window is the auto-applied ALL-TIME range (an un-dated question defaults to 1990→today).
    // Drives the processing-overlay note + quick-narrow presets (the 1990 scan is the slow case).
    procIsAllTime() {
      return String(this.filters.from || "").slice(0, 4) === "1990";
    },
    // The overlay is PAUSED awaiting confirmation (all-time ask, nothing loading yet) → show the ASK circle.
    procPaused() {
      return this.askConfirmAllTime && !this.loading && !this.aiAskBusy;
    },
    // How many groups are checked in the Excel export picker (drives the Export button label + disable).
    aiExcelCheckedCount() {
      return (this.aiExcelModal.groups || []).filter(function (g) { return g.checked; }).length;
    },
    aiPresentCheckedCount() {
      return (this.aiPresentModal.groups || []).filter(function (g) { return g.checked; }).length;
    },
    // Average ask time across the questions currently in view (those that carry a measured duration).
    // (Overall figure for the 🕘 history list header; the OVERLAY uses the per-range average below.)
    avgAskLabel() {
      var xs = (this.qaHistory || []).filter(function (h) { return typeof h.ms === "number" && h.ms > 0; });
      if (!xs.length) return "";
      var avg = xs.reduce(function (s, h) { return s + h.ms; }, 0) / xs.length;
      return this.fmtAskMs(avg);
    },
    // Average ask time for the DATE RANGE currently shown above the ASK button (the window being searched) —
    // bucketed by window width, so the all-time gate shows the all-time average, not a 3-yr search's time.
    // "" until at least one past ask covered a similarly-wide window.
    procRangeAvg() {
      return this.avgAskForRangeYears(this._rangeYears());
    },
    // 🎤 available only where the browser has the Web Speech API (Chrome/Edge; hidden elsewhere).
    voiceSupported() {
      return typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
    },
    // The curated Azure neural voices for the hero talk-back dropdown (module constant, template-friendly).
    ttsVoices() { return TTS_VOICES; },
    // True whenever the system is working on an Ask or a filter/data refresh — drives the "Loading…" overlay.
    // aiInsightsBusy joins loading/aiAskBusy so "Generate Insights" raises the same overlay. NOTE: anything
    // added here MUST also be cleared in cancelProcessing(), or the ✕ tears down the overlay while the flag
    // stays true and the watcher immediately re-raises it — a permanently stuck overlay.
    isProcessing() { return !!(this.loading || this.aiAskBusy || this.aiInsightsBusy); },
    // Result-table columns where ≥70% of the non-empty cells are REAL numbers (or money-formatted
    // strings — see aiColCellNum). These get summed on group rows and feed the measure pick below.
    aiTableNumCols() {
      var t = this.aiTable;
      if (!t || !Array.isArray(t.columns) || !Array.isArray(t.rows)) return [];
      var out = [];
      for (var i = 0; i < t.columns.length; i++) {
        var n = 0, hits = 0;
        for (var r = 0; r < t.rows.length; r++) {
          var v = (t.rows[r] || [])[i];
          if (v == null || String(v).trim() === "") continue;
          n++;
          if (this.aiColCellNum(v) !== null) hits++;
        }
        if (n > 0 && hits >= Math.max(1, Math.floor(n * 0.7))) out.push(i);
      }
      return out;
    },
    // Which column carries the MEASURE to chart: prefer a numeric column whose HEADER names a money
    // measure (Sales/Total/Amount/…) — otherwise a trailing count column like "Orders" would win and
    // the bars would chart counts instead of dollars. Column 0 is never the measure (it's the label /
    // grouping column; group rows deliberately don't sum it, so its bars would render empty).
    // Falls back to the right-most numeric; -1 = none.
    aiTableNumIdx() {
      var t = this.aiTable;
      var numeric = this.aiTableNumCols.filter(function (i) { return i !== 0; });
      if (!t || !numeric.length) return -1;
      for (var j = numeric.length - 1; j >= 0; j--) {
        if (/sales|total|amount|revenue|price|\$/i.test(String(t.columns[numeric[j]] || ""))) return numeric[j];
      }
      return numeric[numeric.length - 1];
    },
    // Which numeric columns are MONEY (get a $ + always 2 decimals): a numeric column whose header
    // names a money measure. Label columns like "Sales Rep" match the regex but aren't numeric, so
    // the aiTableNumCols filter keeps them out.
    aiMoneyCols() {
      var t = this.aiTable;
      if (!t || !Array.isArray(t.columns)) return [];
      return this.aiTableNumCols.filter(function (i) {
        return /sales|total|amount|revenue|price|\$/i.test(String(t.columns[i] || ""));
      });
    },
    // The flat (ungrouped) rows in the active sort order. No sort → original order.
    // Thin wrapper: the real work lives in _flatAiRows(t) so the presentation can run the SAME
    // logic over the group-FILTERED table instead of this.aiTable.
    aiFlatRows() { return this._flatAiRows(this.aiTable); },
    // GROUPED view of the result table: when the first column repeats (a "top N per X" breakdown —
    // per rep, per territory, per customer…) the card shows one collapsible row per group, numeric
    // columns summed, "+" expanding the detail rows. Null = plain ranking → the flat tbody renders.
    // Same repeat heuristic as exportAiTable's per-group sheets, so card and workbook always agree.
    // Thin wrapper for the same reason as aiFlatRows — see _groupAiTable(t).
    aiTableGroups() { return this._groupAiTable(this.aiTable); },
    // Largest group total in the measure column — the 100% group bar.
    aiGroupMax() {
      var gs = this.aiTableGroups, idx = this.aiTableNumIdx;
      if (!gs || idx < 0) return 0;
      var m = 0;
      gs.forEach(function (g) { var v = g.sums[idx]; if (v != null && v > m) m = v; });
      return m;
    },
    // Open-state is keyed by group INDEX (not the group's name) — index keys can never collide with
    // Object.prototype members, and group order is deterministic for a given table.
    allAiGroupsOpen() {
      var gs = this.aiTableGroups;
      if (!gs || !gs.length) return false;
      var open = this.aiTableOpen;
      return gs.every(function (g, i) { return !!open[i]; });
    },
    // Largest value in the numeric column — the 100% bar.
    aiTableMax() {
      var t = this.aiTable, idx = this.aiTableNumIdx;
      if (!t || idx < 0) return 0;
      var m = 0;
      for (var r = 0; r < t.rows.length; r++) {
        var v = this.aiCellNum((t.rows[r] || [])[idx]);
        if (v !== null && v > m) m = v;
      }
      return m;
    },
    // The current DOCKED scenario key: "" (nothing docked) | "email" | "filters" | "email+filters".
    // Keys the per-user dockLayouts presets and the autosave routing. Fixed member order — never reorder.
    dockKey() {
      var parts = [];
      if (this.emailDocked) parts.push("email");
      if (this.filtersDocked) parts.push("filters");
      return parts.join("+");
    },
    // Human name for the current scenario ("Docked layout — Filters + Email Insights").
    dockLabel() {
      return this.dockKey === "email" ? "Email Insights"
        : this.dockKey === "filters" ? "Filters"
        : this.dockKey === "email+filters" ? "Filters + Email Insights" : "";
    },
    // ===== SHARED-REPORT plumbing (2026-08-05). These replace the ~15 hardcoded
    // `activeLayout === "default"` tests that limited docked layouts to Sales Insights. =====
    // The shared report the ACTIVE layout is; null on a per-user custom layout ("L<epoch>").
    sharedReport() {
      var id = this.activeLayout;
      return SHARED_REPORTS.find(function (r) { return r.id === id; }) || null;
    },
    // Dock-preset namespace for the active layout. "" on a custom layout, which keeps docked presets
    // OFF there exactly as today (a personal layout has no shared row to publish them to).
    dockScope() { return this.sharedReport ? this.sharedReport.roleKey : ""; },
    // The saved preset for THIS report + THIS dock combination, or null when none exists — in which
    // case docking just hides the card, the pre-2026-08-05 behaviour.
    dockPresetHere() { return this.getDockPreset(this.dockScope, this.dockKey); },
    // Shared reports this viewer may actually see, in picker order. Drives the numbered rows so adding
    // a report to SHARED_REPORTS is the only edit needed — the numbers are positional, never stored.
    visibleSharedReports() {
      var self = this;
      return SHARED_REPORTS.filter(function (r) { return r.listed && (!r.access || !!self[r.access]); });
    },
    // Standalone reports shown as launchers. Gated by the SAME authorizedReports list the Reports
    // dashboard tiles use, with the same fail-open rule (show everything until access resolves, and treat
    // "all-insights" as the covering grant) so the drawer and the dashboard can never disagree.
    launcherReports() {
      var a = this.authorizedReports || [];
      if (!this.authorizedReportsOk || a.indexOf("all-insights") >= 0) return LAUNCHER_REPORTS;
      return LAUNCHER_REPORTS.filter(function (r) { return a.indexOf(r.key) >= 0; });
    },
    // Display-name resolver: a published LayoutJson.name wins over the built-in label. Returns a
    // FUNCTION so the template can call reportNameOf('shared3'); depends on roleDefaults so a rename
    // re-renders every row that shows the name.
    reportNameOf() {
      var rd = this.roleDefaults || {};
      return function (id) {
        var R = SHARED_REPORTS.find(function (r) { return r.id === id; });
        if (!R) return "";
        var n = rd[R.roleKey] && rd[R.roleKey].name;
        return (n && String(n).trim()) || R.name;
      };
    },
    // The active report's display name — used in the dock buttons and the editing notes.
    activeReportName() { return this.sharedReport ? this.reportNameOf(this.sharedReport.id) : ""; },
    // dashHidden entries that are REAL user hides — the Email dock marker doesn't count (it's there so
    // ensureBuiltinCards won't re-append the docked card, not because the user hid anything).
    userHiddenCount() {
      var self = this;
      return (this.dashHidden || []).filter(function (h) { return !(h === "emailInsights" && self.emailDocked); }).length;
    },
    // Ask history GROUPED BY SALES REP (own group first, others by most-recent activity). Sales sees
    // one group (their own); Sales All sees every Sales rep's; Master sees everyone's — the server
    // decides what rows come back, this just groups whatever arrived.
    qaHistoryGroups() {
      var groups = [], byName = {};
      (this.qaHistory || []).forEach(function (h) {
        var k = h.repName || "Unknown";
        if (!byName[k]) { byName[k] = { name: k, own: false, ownerId: "", items: [] }; groups.push(byName[k]); }
        if (h.own) byName[k].own = true;
        if (!byName[k].ownerId && h.ownerId) byName[k].ownerId = h.ownerId;   // Master-only (backend); powers group delete-all
        byName[k].items.push(h);
      });
      groups.sort(function (a, b) { return (b.own ? 1 : 0) - (a.own ? 1 : 0); });
      return groups;
    },
    // Insights history grouped the same way (for the drawer's 🕘 popup).
    aiInsightHistoryGroups() {
      var groups = [], byName = {};
      (this.aiInsightHistory || []).forEach(function (h) {
        var k = h.repName || "Unknown";
        if (!byName[k]) { byName[k] = { name: k, own: false, items: [] }; groups.push(byName[k]); }
        if (h.own !== false) byName[k].own = byName[k].own || h.own === true;
        byName[k].items.push(h);
      });
      groups.sort(function (a, b) { return (b.own ? 1 : 0) - (a.own ? 1 : 0); });
      return groups;
    },
    // Hero Ask typeahead pool — every value the loaded cards know (reps, territories, customers by name
    // AND id, countries, states, parts by number AND description), deduped. Field names are read
    // defensively (camelCase from the API, PascalCase fallback) so a facet shape change degrades quietly.
    heroSuggestPool() {
      var pool = [];
      var push = function (text, label) { var t = (text == null ? "" : String(text)).trim(); if (t) pool.push({ text: t, label: label }); };
      (this.bySalesRep || []).forEach(function (r) { push(r.salesRep || r.SalesRep, "Sales rep"); });
      (this.byTerritory || []).forEach(function (r) { push(r.territory || r.Territory, "Territory"); });
      (this.byCustomer || []).forEach(function (r) { push(r.customerName || r.CustomerName, "Customer"); push(r.customerId || r.CustomerId, "Customer ID"); });
      (this.byCountry || []).forEach(function (r) { push(r.country || r.Country, "Country"); });
      (this.byState || []).forEach(function (r) { push(r.state || r.State || r.customerState || r.CustomerState, "State"); });
      (this.byPart || []).forEach(function (r) { push(r.partNumber || r.PartNumber, "Part"); push(r.partDescription || r.PartDescription, "Part desc."); });
      var seen = {}, out = [];
      pool.forEach(function (s) { var k = s.text.toLowerCase(); if (!seen[k]) { seen[k] = 1; out.push(s); } });
      return out;
    },
    // Coaching/tips text split into renderable lines (same bullet rules as the insights narrative).
    aiCoachingLines() {
      const raw = this.aiCoaching || "";
      return raw.split(/\r?\n/)
        .map((l) => l.trim())
        .filter((l) => l.length > 0)
        .map((l) => {
          const m = l.match(/^([-•*]|\d+[.)])\s+(.*)$/);
          return m ? { bullet: true, text: m[2] } : { bullet: false, text: l };
        });
    },
    // Compact data lists for the print-style "Report data" section under the insights.
    aiDataCustomers() {
      return (this.byCustomer || []).slice().sort(function (a, b) { return (Number(b.sales) || 0) - (Number(a.sales) || 0); }).slice(0, 8);
    },
    aiDataParts() { return (this.byPart || []).slice(0, 8); },
    aiDataMaxCust() { return this.aiDataCustomers.reduce(function (m, c) { return Math.max(m, Number(c.sales) || 0); }, 0); },
    aiDataMaxPart() { return this.aiDataParts.reduce(function (m, p) { return Math.max(m, Number(p.qty) || 0); }, 0); },
    // partNumber -> [{ name(territory), val(qty), color }] desc — a part's quantity rolled up from its per-rep
    // rows (byPartRep) to territories (via the rep leaderboard). Drives the territory-segmented part bars on
    // BOTH the on-screen "Report data" section and the print presentation (same source = identical segments).
    partTerritorySegMap() {
      var repTerr = {};
      (this.bySalesRep || []).forEach(function (r) { if (r && r.salesRep) repTerr[r.salesRep] = r.territory || "(Unassigned)"; });
      var byPart = {};
      (this.byPartRep || []).forEach(function (pr) {
        if (!pr || !pr.partNumber) return;
        var t = repTerr[pr.salesRep] || "(Unassigned)";
        var m = byPart[pr.partNumber] || (byPart[pr.partNumber] = {});
        m[t] = (m[t] || 0) + (Number(pr.qty) || 0);
      });
      var out = {}; var self = this;
      Object.keys(byPart).forEach(function (pn) {
        out[pn] = Object.keys(byPart[pn]).map(function (t) { return { name: t, val: byPart[pn][t], color: self.terrColor(t) }; })
          .filter(function (s) { return s.val > 0; }).sort(function (a, b) { return b.val - a.val; });
      });
      return out;
    },
    // The state whose flag shows below the US map: the HOVERED state (rich payload from the map), else — when
    // exactly one state is highlighted by the current filters — that selected state (resolve its name + stats).
    mapFlagState() {
      var base = null;
      if (this.mapHoverState && this.mapHoverState.postal) base = this.mapHoverState;
      else {
        var sel = this.mapSelectedState || [];
        if (sel.length === 1) {
          var pSel = String(sel[0]).toUpperCase();
          var rSel = (this.mapStates || []).find(function (s) { return s && String(s.state).toUpperCase() === pSel; });
          base = { postal: pSel, name: US_STATE_NAMES[pSel] || pSel, territory: rSel ? rSel.territory : null, sales: rSel ? (Number(rSel.sales) || 0) : 0, hasData: !!rSel };
        }
      }
      if (!base || !base.postal) return null;
      // Enrich with the state's ORDER count (from the state aggregate) + CUSTOMER count (in-view customers whose
      // id-prefix maps to this state) so the flag panel shows sales · customers · orders — parity with the world map.
      var postal = String(base.postal).toUpperCase();
      var rec = (this.mapStates || []).find(function (s) { return s && String(s.state).toUpperCase() === postal; });
      var orders = rec ? (Number(rec.orderCount) || 0) : 0;
      var customers = (this.byCustomer || []).reduce(function (n, c) {
        return n + (String((c && c.customerId) || "").slice(0, 2).toUpperCase() === postal ? 1 : 0);
      }, 0);
      return Object.assign({}, base, { orders: orders, customers: customers });
    },
    // The country whose flag shows below the WORLD map, in priority: (1) the hovered country, (2) the
    // explicitly SELECTED country (filters.country), (3) — only when nothing is selected — the USA default.
    worldFlagCountry() {
      if (this.mapHoverCountry) return this.mapHoverCountry;
      var match = function (list, pred) { for (var i = 0; i < (list || []).length; i++) { if (pred(list[i])) return list[i]; } return null; };
      var sel = (this.filters.country || []);
      if (sel.length) {
        var name = sel[0];
        var rec = match(this.byCountry, function (c) { return c && String(c.country || "").toUpperCase() === String(name).toUpperCase(); });
        return { name: rec ? rec.country : name, present: !!rec, sales: rec ? (Number(rec.sales) || 0) : 0, customers: rec ? (Number(rec.customers) || 0) : 0, orders: rec ? (Number(rec.orderCount) || 0) : 0 };
      }
      return null;   // nothing hovered + no explicit country filter → no default flag; hover a country (e.g. the highlighted US) to see it
    },
  },
  watch: {
    // Only one card may hold Full view — entering it on one exits it on the other, or both would be
    // class-hidden and the page would look empty. Entering re-runs the scroll pin so the maximized card
    // sizes/pins under the chat band; exiting clears the pin variables (see _clearFullViewPin) so a stale
    // translate cannot poison the next entry's natural-top measurement.
    pcFull: function (v) { if (v) this.plFull = false; else this._clearFullViewPin(); this.$nextTick(this._onStickyScroll); },
    plFull: function (v) { if (v) this.pcFull = false; else this._clearFullViewPin(); this.$nextTick(this._onStickyScroll); },
    // SELF-HEAL the Costing for Parts card. ensureBuiltinCards places it only when canSeeCost is true,
    // and pruneUnauthorizedCards strips it whenever canSeeCost is false — but canSeeCost is FALSE for the
    // first moments of a page load, before the store user hydrates. On production that ordering left the
    // card permanently absent: a prune ran early, ensureBuiltinCards had already passed, and nothing put
    // it back. (It was masked on the dev stack, where the card was already in the saved layout.)
    // Verified live: canSeeCost true, isMaster true, activeLayout "default", partCosts NOT in dashHidden,
    // yet absent from dashLayout. Re-running placement the moment authorization resolves fixes the whole
    // class rather than this one symptom, and is idempotent — ensureBuiltinCards skips cards already
    // present, so a user who deliberately hid it keeps it hidden (dashHidden is honoured there).
    canSeeCost: function (allowed) {
      if (!allowed) return;
      // NEVER during a view-as preview. Impersonating an ALL INSIGHTS user froze the whole page: the
      // viewingAsUser watcher is replacing dashLayout with the previewed user's layout while the role
      // switch flips canSeeCost false->true, landing this watcher's ensureBuiltinCards() on the SAME
      // array mid-replace — two writers alternating on the array vue-grid-layout consumes, which is the
      // documented infinite-render freeze. It is All-Insights-specific because previewing a Sales user
      // leaves canSeeCost false, so this watcher never contends. The saveDashLayoutNow below was already
      // preview-guarded; ensureBuiltinCards was the hole. The preview still gets card DATA through the
      // normal path — viewingAsUser calls load(), whose .then fires loadPartCosts behind canSeeCost.
      if (this.viewingAsUser) return;
      var self = this;
      this.$nextTick(function () {
        var had = (self.dashLayout || []).some(function (it) { return it.i === "partCosts"; });
        self.ensureBuiltinCards();
        var has = (self.dashLayout || []).some(function (it) { return it.i === "partCosts"; });
        if (!had && has && self.saveDashLayoutNow) self.saveDashLayoutNow();
        // The card auto-loads now, and load() fires its part-cost fetch behind an `if (canSeeCost)`.
        // On a cold page load that check usually runs BEFORE the store hydrates, so the fetch is skipped
        // and — with the button gone — nothing would ever ask for the data. Kick it here, the moment
        // authorization actually resolves. Guarded on rows so a normal filter-driven load isn't doubled.
        if (!self.partCostBusy && !(self.partCostRows || []).length) self.loadPartCosts();
      });
    },
    // A new result table starts collapsed, unsorted, and not-yet-favorited — none carry across answers.
    aiTable: function () { this.aiTableOpen = {}; this.aiSort = null; },
    // Global processing overlay: show a moment AFTER an Ask/filter refresh starts (so an instant load never
    // flashes a modal), hide immediately when it finishes.
    isProcessing: function (on) {
      if (on) {
        this._procBegin(false);   // 140ms anti-flash delay — instant filter loads never flash the modal
      } else {
        clearTimeout(this._procTimer);
        if (this._procTick) { clearInterval(this._procTick); this._procTick = null; }
        this.showProcessing = false;
        this.procElapsedMs = 0;
      }
    },
    // Admin "View as user": when the previewed user changes (or clears), reload the dashboard as THAT user
    // (their personal Layouts 2+) — or back to my own when cleared.
    viewingAsUser: function () {
      // Load the previewed user's personal layout AND re-fetch the data scoped as that user (buildParams now
      // sends viewAsUserId). Clearing the preview re-runs as my real self. Mirrors the company-switch reload.
      this.loadDashLayout();
      this.page = 1;
      this.load();
      this.loadDetails();
      this.loadSavedCarts(); this.loadOrdersInProcess();
      this.loadAiHistory();   // re-scope the hero chat history to the previewed user (or back to me when cleared)
    },
    // Auto-collapse the variable-content cards whenever their data changes (a filter/company change repopulates
    // these). autoSizeCards is $nextTick-wrapped (measures the fresh DOM) + idempotent (only resizes on a real
    // change), so firing from several watchers is safe. Layout switches are covered by applyLayout's own call.
    byCustomer: function () { this.autoSizeCards(); },
    byTerritory: function () { this.autoSizeCards(); },
    bySalesRep: function () { this.autoSizeCards(); },
    // Re-fit the Sales Reps card to its VISIBLE rows — repCardRows can shrink without bySalesRep changing (the
    // $0.00 filter drops reps; a rep chip added/removed), which the bySalesRep watcher alone wouldn't catch.
    "repCardRows.length": function () { this.autoSizeCards(); },
    savedCarts: { deep: true, handler: function () { this.autoSizeCards(); } },
    "oip.orders": function () { this.autoSizeCards(); },
    // Any open report panel (Filters/Tokens drawer, Dashboard popout, Sales Insights drawer) should clear
    // BOTH edge rails so no rail shows alongside it. One body class drives the hide (see syncReportPanelOpen).
    filtersDrawerOpen: function () { this.syncReportPanelOpen(); },
    tokensDrawerOpen:  function () { this.syncReportPanelOpen(); },
    dashToolsOpen:     function () { this.syncReportPanelOpen(); },
    // Sales rep "view as customer": selecting a customer switches the global
    // company to that customer; clearing it returns home (NC999). Silent switch.
    "filters.customer": {
      deep: true,
      handler: function (val) {
        // Broadcast the (single) selected customer so the header's parts-request modal can prefill
        // the customer company — for ALL rep roles (Master/Sales/Sales All), not just the Sales-locked one.
        this.broadcastCustomerContext();
        var arr = val || [];
        if (!this.isSalesLocked) { this._prevCustomerChips = arr.slice(); return; }
        // Programmatic revert from the prompt's "Back out" — restore silently: no re-prompt, no sync.
        if (this._chipRevertBusy) { this._prevCustomerChips = arr.slice(); return; }
        // Switch the company ONLY while exactly ONE customer is selected (user 2026-08-01): a single
        // chip means "acting as that customer", so the header follows it; two or more chips is a
        // comparison view, so the company returns to the NC999 home; clear all -> NC999 too.
        // Crossing 1 → 2+ WHILE acting as a customer asks first (chipHomePrompt): the rep deliberately
        // switched into that company, so don't yank them home unannounced — Continue homes to NC999,
        // Back out removes the just-added customers and stays (user 2026-08-01). ⚠ Vue 2 deep array
        // watchers hand back the SAME reference as the old value, so _prevCustomerChips (snapshotted
        // each run) holds the before-image the back-out needs.
        var prev = this._prevCustomerChips || [];
        var cc = (typeof localStorage !== "undefined") ? String(localStorage.companyCode || "").trim() : "";
        if (arr.length >= 2 && prev.length < 2 && cc && !/^NC999$/i.test(cc)) {
          this.chipHomePrompt = true;   // decision deferred to chipHomeContinue / chipHomeBack
          return;
        }
        this._prevCustomerChips = arr.slice();
        this.syncCompany(arr.length === 1 ? String(arr[0]) : "");
      },
    },
    // Auto-switch the map: an International territory in the filter -> world (country) map; else US
    // states. The US/World buttons still let you override within the current view.
    "filters.territory": {
      deep: true,
      handler: function (val) {
        this.mapMode = (val || []).some(function (t) { return /international/i.test(t); }) ? "world" : "us";
      },
    },
    // Switching map view clears any lingering hover so the flag panel doesn't show a stale state/country.
    mapMode: function () { this.mapHoverState = null; this.mapHoverCountry = null; },
    // While the Sales Insights drawer is open, hide all the edge toggles (rail + util).
    storyHidden: function (val) {
      if (typeof document !== "undefined") document.body.classList.toggle("jcs-si-open", !val);
      this.syncReportPanelOpen();
    },
    // Keep the header's compact From/To pickers in sync with the report's active window
    // (whether it changed via a shortcut button, the drawer, a card, or the route query).
    "filters.from": function () { this.broadcastDateRange(); },
    "filters.to": function () { this.broadcastDateRange(); },
  },
  mounted() {
    // Seed filters from the route query (the header search/date can navigate here).
    var qp = (this.$route && this.$route.query) || {};
    var fkeys = ["customer", "part", "territory", "salesRep", "status", "machineCodeCategory", "machineCodePrefix", "orderNo", "invoiced", "from", "to", "dateBasis", "state"];
    fkeys.forEach((k) => { if (qp[k] != null && qp[k] !== "") this.filters[k] = String(qp[k]); });
    // Header (reporting-role) search + date shortcuts drive this report.
    this.$root.$on("report-filter-select", this.onSuggestionSelect);
    this.$root.$on("report-date-range", this.onHeaderDateRange);
    if (typeof window !== "undefined") window.addEventListener("resize", this._onResizeMobile);   // swap desktop↔mobile layout on breakpoint cross
    this._stickyBusy = false; this._stickyRaf = null;
    this._applyingHistory = false; this._histTimer = null;
    if (typeof window !== "undefined") window.addEventListener("scroll", this._onStickyScroll, { passive: true });   // Filters/Favorites follow the scroll
    if (typeof document !== "undefined") document.addEventListener("keydown", this._onLayoutKeydown);   // Ctrl+Z / Ctrl+Y layout undo/redo
    var selfCI = this;
    this.$nextTick(function () { selfCI.measureStickyTop(); selfCI.refreshStickyCards(); });   // pin sticky panels just below the fixed header (height varies by role)
    if (typeof window !== "undefined") setTimeout(function () { selfCI.measureStickyTop(); selfCI.refreshStickyCards(); }, 700);   // re-measure after the header/responsive layout settles
    // Re-scope when the active company changes WITHOUT a page reload (e.g. an in-report customer switch). A
    // header-dropdown pick reloads the page, so this mainly catches the silent switches.
    if (this.$cartBus) this.$cartBus.$on("company-changed", this.onCompanyChanged);
    this.loadFilterOptions();
    this._loadServerTimings();   // pooled load-time averages that seed the refresh countdown estimate
    this.loadSalesTerritories();
    this.loadLayout();
    // Load the SHARED role-default (Layout 1) BEFORE the per-user dashboard, and gate all layout SAVES until both
    // are in (`_layoutReady`). Otherwise a race clobbered the main layout: if the per-user blob returned first (or
    // role-defaults was slow — e.g. a cold backend right after a deploy), applyLayout("default") ran with an empty
    // roleDefaults, fell back to the baked DASH_DEFAULT, and vue-grid's initial "layout-updated" auto-saved THAT
    // over the real Layout 1. (The per-user "Email-hidden layout" survived because it lives in the blob — hence it
    // was reliable while the visible main layout wasn't.)
    this._layoutReady = false;
    var _selfLR = this;
    // Report Access first (governs which layouts this user may see), THEN the shared + per-user layouts, THEN
    // land them on the right report (honor a ?report= deep-link from the Reports dashboard / enforce access).
    this.loadMyReportAccess()
      .then(function () { return _selfLR.loadRoleDefaults(); })
      .then(function () { return _selfLR.loadDashLayout(); })
      .then(function () { _selfLR.applyReportEntry(); })
      .finally(function () { _selfLR._layoutReady = true; });
    this.loadAiHistory();
    this.loadNotifications();
    // Non-reactive instance props for the NC999-return prompt (never in data() — the _ prefix isn't
    // proxied by Vue 2): the chips before-image the Back out restores, and the revert-in-progress flag.
    this._prevCustomerChips = [];
    this._chipRevertBusy = false;
    this._seedCompanyChip();   // acting as a customer company → show it as a chip BEFORE the first fetch
    this.load();
    this.loadDetails();
    this.loadSavedCarts();
    this.loadOrdersInProcess();
    // Push the initial window to the header pickers (the static default won't trip the watchers).
    this.broadcastDateRange();
    // Seed the header parts-request modal with any query-seeded customer (re-broadcast again after load()).
    this.broadcastCustomerContext();
    // Refresh restored AI cards against the current filters once loadDashLayout() has repopulated aiCards.
    setTimeout(this.refreshAiCards, 1200);
    if (typeof document !== "undefined") document.body.classList.toggle("jcs-si-open", !this.storyHidden);
    this.syncReportPanelOpen();
    // Mobile: swipe from the left edge → open Filters; from the right edge → open Insights; swipe back to close.
    if (typeof window !== "undefined") {
      window.addEventListener("touchstart", this.onSwipeStart, { passive: true });
      window.addEventListener("touchend", this.onSwipeEnd, { passive: true });
      // The hero card is pinned by translateY, so it MOVES as the page scrolls — its available height
      // has to be re-measured on scroll, not just on resize.
      window.addEventListener("resize", this.sizeAnswerScroll, { passive: true });
      window.addEventListener("resize", this.sizeQuotePrompt, { passive: true });
      window.addEventListener("scroll", this.sizeAnswerScroll, { passive: true });
    }
    // Close the QTR / YEAR date-preset popups when the user clicks anywhere outside them.
    if (typeof document !== "undefined") document.addEventListener("mousedown", this.onPresetDocClick);
    // Isolate the order-detail modal for printing: while it's open, hide the rest of the app so Print Order in the
    // embedded OrderDetails prints ONLY the order (not the report behind it). beforeprint/afterprint fire for both
    // the component's window.print() and Ctrl+P.
    if (typeof window !== "undefined") { window.addEventListener("beforeprint", this.onBeforePrint); window.addEventListener("afterprint", this.onAfterPrint); }
    // Float the Sales Insights panel at <body> level so it sits above every
    // layer (escapes the report card's stacking context).
    this.$nextTick(function () {
      try { if (this.$refs.storyPanel && document.body) document.body.appendChild(this.$refs.storyPanel); } catch (e) { /* noop */ }
      try { if (this.$refs.reportDrawers && document.body) document.body.appendChild(this.$refs.reportDrawers); } catch (e) { /* noop */ }
      // The print sheet must be a DIRECT child of <body>: its print isolation is "hide every body child
      // except .codp-doc", which is what lets it ignore whatever drawers/popovers happen to be open.
      try { if (this.$refs.codPrintDoc && document.body) document.body.appendChild(this.$refs.codPrintDoc); } catch (e) { /* noop */ }
      try { if (this.$refs.qrPrintDoc && document.body) document.body.appendChild(this.$refs.qrPrintDoc); } catch (e) { /* noop */ }
      try { if (this.$refs.favPopPanel && document.body) document.body.appendChild(this.$refs.favPopPanel); } catch (e) { /* noop */ }
      try { if (this.$refs.repBasisHelpPanel && document.body) document.body.appendChild(this.$refs.repBasisHelpPanel); } catch (e) { /* noop */ }
      try { if (this.$refs.chipHoverPanel && document.body) document.body.appendChild(this.$refs.chipHoverPanel); } catch (e) { /* noop */ }
      try { if (this.$refs.kpiMathPanel && document.body) document.body.appendChild(this.$refs.kpiMathPanel); } catch (e) { /* noop */ }
      try { if (this.$refs.terrInsPanel && document.body) document.body.appendChild(this.$refs.terrInsPanel); } catch (e) { /* noop */ }
      try { if (this.$refs.notePopPanel && document.body) document.body.appendChild(this.$refs.notePopPanel); } catch (e) { /* noop */ }
      try { if (this.$refs.specPopPanel && document.body) document.body.appendChild(this.$refs.specPopPanel); } catch (e) { /* noop */ }
      try { if (this.$refs.heroNotePanel && document.body) document.body.appendChild(this.$refs.heroNotePanel); } catch (e) { /* noop */ }
      try { if (this.$refs.perspPopPanel && document.body) document.body.appendChild(this.$refs.perspPopPanel); } catch (e) { /* noop */ }
      try { if (this.$refs.codWhyPopPanel && document.body) document.body.appendChild(this.$refs.codWhyPopPanel); } catch (e) { /* noop */ }
      try { if (this.$refs.unassignedPopPanel && document.body) document.body.appendChild(this.$refs.unassignedPopPanel); } catch (e) { /* noop */ }
      try { if (this.$refs.unEmailPanel && document.body) document.body.appendChild(this.$refs.unEmailPanel); } catch (e) { /* noop */ }
    }.bind(this));
  },
  beforeDestroy() {
    if (this._dashSaveTimer) { clearTimeout(this._dashSaveTimer); this._dashSaveTimer = null; }
    if (this._loadTimer) { clearInterval(this._loadTimer); this._loadTimer = null; }
    if (this._chipPopT) { clearTimeout(this._chipPopT); this._chipPopT = null; }
    window.removeEventListener("pointermove", this.onDrag);
    window.removeEventListener("pointerup", this.endDrag);
    window.removeEventListener("touchstart", this.onSwipeStart);
    window.removeEventListener("touchend", this.onSwipeEnd);
    this._favTouchTeardown();   // the favorites long-press drag adds its own document listeners imperatively
    if (typeof document !== "undefined") document.removeEventListener("mousedown", this.onPresetDocClick);
    if (typeof window !== "undefined") { window.removeEventListener("beforeprint", this.onBeforePrint); window.removeEventListener("afterprint", this.onAfterPrint); }
    if (typeof window !== "undefined") { window.removeEventListener("resize", this.sizeAnswerScroll); window.removeEventListener("scroll", this.sizeAnswerScroll); window.removeEventListener("resize", this.sizeQuotePrompt); }
    if (typeof document !== "undefined") { document.body.classList.remove("oipd-printing"); document.body.classList.remove("codp-printing"); }
    if (this._codPrintReset) { clearTimeout(this._codPrintReset); this._codPrintReset = null; }
    this.$root.$off("report-filter-select", this.onSuggestionSelect);
    try { if (this._heroRec) this._heroRec.abort(); } catch (e) { /* mic released with the page */ }
    this.$root.$off("report-date-range", this.onHeaderDateRange);
    if (typeof window !== "undefined") window.removeEventListener("resize", this._onResizeMobile);
    if (typeof window !== "undefined") window.removeEventListener("scroll", this._onStickyScroll);
    if (typeof document !== "undefined") document.removeEventListener("keydown", this._onLayoutKeydown);
    if (this._histTimer) { clearTimeout(this._histTimer); this._histTimer = null; }
    if (this._stickyRaf) { try { cancelAnimationFrame(this._stickyRaf); } catch (e) { /* noop */ } this._stickyRaf = null; }
    if (this.$cartBus) this.$cartBus.$off("company-changed", this.onCompanyChanged);
    // Leaving the report clears the parts-request customer context (the header then falls back to the active company).
    this.$root.$emit("report-customer-context", { customerId: "", customerName: "" });
    if (typeof document !== "undefined") document.body.classList.remove("jcs-si-open");
    if (typeof document !== "undefined") document.body.classList.remove("jcs-report-panel-open");
    try { var el = this.$refs.storyPanel; if (el && el.parentNode) el.parentNode.removeChild(el); } catch (e) { /* noop */ }
    try { var dr = this.$refs.reportDrawers; if (dr && dr.parentNode) dr.parentNode.removeChild(dr); } catch (e) { /* noop */ }
    try { var cp = this.$refs.chipHoverPanel; if (cp && cp.parentNode) cp.parentNode.removeChild(cp); } catch (e) { /* noop */ }
    try { var km = this.$refs.kpiMathPanel; if (km && km.parentNode) km.parentNode.removeChild(km); } catch (e) { /* noop */ }
    try { if (typeof document !== "undefined") document.removeEventListener("mousedown", this._kpiMathDocClose); } catch (e) { /* noop */ }
    try { var tip = this.$refs.terrInsPanel; if (tip && tip.parentNode) tip.parentNode.removeChild(tip); } catch (e) { /* noop */ }
    try { if (typeof document !== "undefined") document.removeEventListener("mousedown", this._terrInsDocClose); } catch (e) { /* noop */ }
    try { var np = this.$refs.notePopPanel; if (np && np.parentNode) np.parentNode.removeChild(np); } catch (e) { /* noop */ }
    try { if (typeof document !== "undefined") document.removeEventListener("mousedown", this._noteDocClose); } catch (e) { /* noop */ }
    try { if (typeof document !== "undefined") document.removeEventListener("mousedown", this._codWhyDocClose); } catch (e) { /* noop */ }
    try { var sp = this.$refs.specPopPanel; if (sp && sp.parentNode) sp.parentNode.removeChild(sp); } catch (e) { /* noop */ }
    try { if (typeof document !== "undefined") document.removeEventListener("mousedown", this._specDocClose); } catch (e) { /* noop */ }
    try { var hn = this.$refs.heroNotePanel; if (hn && hn.parentNode) hn.parentNode.removeChild(hn); } catch (e) { /* noop */ }
    try { clearTimeout(this._heroNoteClose); } catch (e) { /* noop */ }
    try { this.stopTts(); } catch (e) { /* noop */ }
    try { var pp = this.$refs.perspPopPanel; if (pp && pp.parentNode) pp.parentNode.removeChild(pp); } catch (e) { /* noop */ }
    try { if (typeof document !== "undefined") document.removeEventListener("mousedown", this._perspDocClose); } catch (e) { /* noop */ }
    try { var up = this.$refs.unassignedPopPanel; if (up && up.parentNode) up.parentNode.removeChild(up); } catch (e) { /* noop */ }
    try { if (typeof document !== "undefined") document.removeEventListener("mousedown", this._unDocClose); } catch (e) { /* noop */ }
    try { var uep = this.$refs.unEmailPanel; if (uep && uep.parentNode) uep.parentNode.removeChild(uep); } catch (e) { /* noop */ }
  },
  methods: {
    // Hide BOTH report edge rails whenever any panel is open (Filters/Tokens drawer, Dashboard popout,
    // Sales Insights drawer), so no rail shows next to an open panel. App.vue hides the right pill rail;
    // the report's scoped CSS hides the left edge rail. (jcs-si-open additionally hides the util stack.)
    syncReportPanelOpen: function () {
      var open = this.filtersDrawerOpen || this.tokensDrawerOpen || this.dashToolsOpen || !this.storyHidden;
      if (typeof document !== "undefined") document.body.classList.toggle("jcs-report-panel-open", !!open);
    },
    buildParams(withPaging) {
      const f = this.filters;
      const p = {};
      Object.keys(f).forEach((k) => {
        const v = f[k];
        if (Array.isArray(v)) { if (v.length) p[k] = v.join(","); }   // multi-select -> comma list -> backend IN()
        else if (v !== "" && v != null) { p[k] = v; }
      });
      // Active company code → the backend's scope. Master / Sales All: a specific company narrows the whole
      // report to it (NC999 = all). Other roles: the server-enforced territory boundary always applies; this
      // only narrows within it. Read from the header's companyCode mirror (set in Head.selectCompany).
      // ⚠ SKIP the scope when the active company merely MIRRORS a selected customer chip (the one-chip
      // header-follow flow): the chip already filters the report, and sending it ALSO as companyScope
      // hard-scoped every FACET — collapsing the Customer ID(s) card to that single customer with nothing
      // else to click (user 2026-08-01: "select AL105 → no option to select another"). The facets exclude
      // their own dimension precisely so more customers can be added; the chip keeps them browsable.
      var cc = (typeof localStorage !== "undefined") ? (localStorage.companyCode || "") : "";
      var ccMirrored = cc && Array.isArray(f.customer) &&
        f.customer.some(function (x) { return String(x).toUpperCase() === String(cc).toUpperCase(); });
      if (cc && !ccMirrored) p.companyScope = cc;
      // Admin "view as user" preview → ask the backend to scope AS that user. The server honors this only for a
      // real Master / Sales All (ignored otherwise), so the preview matches exactly what that user would see.
      var asUser = this.viewingAsUser;
      if (asUser && asUser.id) p.viewAsUserId = asUser.id;
      // "Attribute by" rep selector — only send for privileged (Master / Sales All); the backend ignores it for
      // everyone else anyway (and honors it only for the privileged EFFECTIVE identity, preview-aware). Default
      // owned/current makes the backend run the base table (no re-base), so this is a no-op unless George changes it.
      if (this.isPrivileged) { p.repBasis = this.repBasis; p.repView = this.repView; }
      if (withPaging) {
        p.page = this.page; p.pageSize = this.pageSize; p.sort = this.sort; p.dir = this.dir;
      }
      return p;
    },
    load() {
      this.loading = true;
      this._startLoadTimer();
      // Stale-response guard: each load() bumps a sequence; a response only applies if it's still the latest. Without
      // this, a slow EARLIER fetch (e.g. Midwest-only) could land AFTER a newer one (Midwest+Mountain&WestCoast) and
      // clobber presentValues/aggregates — which made a just-added territory/rep go "dead" (struck-through, $0) ~30s later.
      const seq = (this._loadSeq = (this._loadSeq || 0) + 1);
      httpClient.get("/report/customer-insights", { params: this.buildParams(true) })
        .then((res) => {
          if (seq !== this._loadSeq) return;   // a newer load() superseded this one — drop the stale data
          const d = res.data || {};
          // The backend caught a query failure (typically a TIMEOUT on too-broad a selection) and returned a
          // 200 + queryError flag instead of a 500. Surface it honestly — never let it read as "no matching orders."
          if (d.queryError) {
            if (typeof console !== "undefined") console.error("[Sales Insights] backend queryError:", d.message || d);
            this.loadError = true;
            this.loadErrorMsg = d.message || "";
            this.notReady = false;
            this.kpis = {}; this.kpisPrior = {};
            this.byCustomer = []; this.byCustomerTerritory = []; this.byPart = []; this.byTerritory = []; this.byState = []; this.bySalesRep = []; this.byPartRep = []; this.byCountry = []; this.byCodes = []; this.byCodeRep = []; this.presentValues = [];
            this.rows = []; this.totalRows = 0;
            return;
          }
          this.loadError = false;
          this.loadErrorMsg = "";
          this.notReady = !!d.notReady;
          this.kpis = d.kpis || {};
          this.kpisPrior = d.kpisPrior || {};
          this.byCustomer = d.byCustomer || [];
          this.byCustomerTerritory = d.byCustomerTerritory || [];   // per-customer territory segments
          this.byPart = d.byPart || [];
          this.byTerritory = d.byTerritory || [];
          this.byState = d.byState || [];
          this.bySalesRep = d.bySalesRep || [];
          this.byPartRep = d.byPartRep || [];
          this.byCountry = d.byCountry || [];
          // Machine & Part Codes: the NEW backend returns these EMPTY (peeled into /customer-insights/codes) and
          // loadCodes() below fills them; an OLD backend (pre-deploy) still returns them populated here, so we read
          // them as a FALLBACK — that keeps the card working during the window before the new backend is deployed.
          this.byCodes = d.byCodes || [];
          this.byCodeRep = d.byCodeRep || [];
          this.presentValues = d.presentValues || [];
          this.rows = d.rows || [];
          this.totalRows = d.totalRows || 0;
          // A clean, real load finished — feed its actual duration back into the countdown estimator so the
          // next comparable selection's countdown is sharper. (Errors/timeouts don't record — they'd skew it.)
          this._recordLoadTiming();
          // Machine & Part Codes is the report's HEAVIEST query (~5x the whole rest of the batch, measured). For a
          // broad/date-only view, DEFER it behind a button — same treatment as Customer Order Details. Any narrowing
          // filter (customer/part/rep/…) OR an active code chip auto-loads it (a narrowed set makes it cheap).
          // Costing for Parts loads with the rest of the dashboard (user's call, 2026-07-29 — it was
          // previously held behind a button so confidential figures couldn't appear just because someone
          // added a filter). The button is gone; the protection that matters was never the button anyway:
          // canSeeCost hides the card and [Authorize] on the endpoint refuses the data. It is cheap enough
          // to be eager — one indexed roll-up joined to a 13k-row table, unlike the codes card below.
          if (this.canSeeCost) this.loadPartCosts();
          if (this.canSeeLoss) this.loadPartLoss();   // Accounting card follows the same From/To window (role-gated, territory-free)
          if (this.hasNarrowingFilter || (this.codeChips && this.codeChips.length)) { this.codesDeferred = false; this.loadCodes(); }
          else { this.codesDeferred = true; this.codesBusy = false; }
          // Per-customer notation/specs (📝/📋) also load LAZILY — peeled off the eager byCustomer facet because
          // their NVARCHAR(MAX) MAX() defeated the columnstore's batch mode → wide-range timeout. Fills the 📝/📋
          // icons a moment after the grid. Backward-compatible: an OLD backend still returns notes on byCustomer and
          // the notes route 404s → loadCustomerNotes's merge is a harmless no-op.
          this.loadCustomerNotes();
          // Auto-pick the map view from where the filtered data actually is (US states vs world countries).
          this.autoMapMode();
          // byCustomer is now populated → (re)broadcast the selected customer so the header's parts-request
          // modal can resolve its company name (the mount-time emit may have preceded the data arriving).
          this.broadcastCustomerContext();
          // After a state click that narrows to exactly ONE customer, auto-select
          // it — which (for the Sales role) switches the company via the watch.
          if (this._autoPickCustomer) {
            this._autoPickCustomer = false;
            var only = (this.byCustomer || []).length === 1 ? this.byCustomer[0] : null;
            if (only && only.customerId && this.filters.customer.indexOf(only.customerId) < 0) {
              this.filters.customer.push(only.customerId);
              this.loadDetails();
            }
          }
        })
        .catch((err) => {
          if (seq !== this._loadSeq) return;   // stale response — a newer load() is in charge
          // A failed request used to masquerade as "no matching orders" (totalRows=0) while the KPI cards
          // kept their STALE values — very confusing. Now: surface it as a real error, clear the KPIs too so
          // nothing stale shows, and log the actual error so the true cause is visible in the console.
          if (typeof console !== "undefined") console.error("[Sales Insights] /report/customer-insights failed:", (err && err.response) ? err.response : err);
          this.loadError = true;
          this.loadErrorMsg = "";
          this.kpis = {}; this.kpisPrior = {};
          this.rows = []; this.totalRows = 0; this.byCustomer = []; this.byCustomerTerritory = []; this.byPart = []; this.byTerritory = []; this.byState = []; this.bySalesRep = []; this.byPartRep = []; this.byCountry = []; this.byCodes = []; this.byCodeRep = []; this.presentValues = [];
        })
        .finally(() => { if (seq === this._loadSeq) { this.loading = false; this._stopLoadTimer(); } });
    },
    // Machine & Part Codes — loaded LAZILY, separately from the main aggregation batch. Its two aggregations
    // (byCodes + byCodeRep, nested TOP-N over COUNT(DISTINCT)) are the heaviest in the report, so the server
    // peeled them into /customer-insights/codes and this fetches them AFTER the core cards land. Own stale-guard
    // (_codesSeq) so a newer filter's codes win; old values stay visible (with the sweep) until the new set lands.
    loadCodes() {
      const seq = (this._codesSeq = (this._codesSeq || 0) + 1);
      this.codesBusy = true;
      httpClient.get("/report/customer-insights/codes", { params: this.buildParams(false) })
        .then((res) => {
          if (seq !== this._codesSeq) return;   // superseded by a newer loadCodes()
          const d = res.data || {};
          this.byCodes = d.byCodes || [];
          this.byCodeRep = d.byCodeRep || [];
        })
        .catch((err) => {
          if (seq !== this._codesSeq) return;
          // Do NOT clear byCodes/byCodeRep here: an OLD backend (no /codes route yet) 404s, and the main load
          // already populated them as a fallback — clearing would blank the card during the pre-deploy window.
          if (typeof console !== "undefined") console.error("[Sales Insights] /report/customer-insights/codes failed:", (err && err.response) ? err.response : err);
        })
        .finally(() => { if (seq === this._codesSeq) this.codesBusy = false; });
    },
    // ---- Costing for Parts → Excel -------------------------------------------------------
    // Reuses the hero result-card mechanism (XLSX + _xlsxSheet + _xlsxSheetName) rather than adding a
    // second workbook path. _xlsxSheet passes any non-null cell through untouched, so real JS numbers
    // land as REAL Excel numbers (sortable/summable) with numIdx unused; that is why costs below are
    // pushed as numbers, not formatted strings.
    // Sorting. Clicking the active column flips direction; a third state returns to the SERVER order,
    // because "highest cost first, no-cost last" is information the client cannot reconstruct from the rows.
    pcToggleRow(pn) {
      this.$set(this.pcOpen, pn, !this.pcOpen[pn]);
      if (this.pcOpen[pn]) this.pcHover = null;   // expanded beats hovered
    },
    // One-line rendering of the audit trail, shared by the hover preview so it cannot drift from the
    // expanded panel.
    pcLastChangeText(r) {
      var lc = this.partCostLastChange(r);
      if (!lc) return "";
      return String(lc.date).slice(0, 10) + " · " + lc.area + (lc.user ? " · " + lc.user : "")
        + (lc.oldV != null ? " · " + this.fmtMoney(lc.oldV) + " → " + this.fmtMoney(lc.newV) : "");
    },
    pcRowHover(r, e) {
      if (this.pcOpen[r.partNumber]) { this.pcHover = null; return; }
      // Keep the card inside the viewport: flip left/up when the cursor is near an edge. 280x110 is the
      // popover's max footprint from the CSS.
      var vw = (typeof window !== "undefined") ? window.innerWidth : 1200;
      var vh = (typeof window !== "undefined") ? window.innerHeight : 800;
      var x = e.clientX + 14, y = e.clientY + 14;
      if (x + 290 > vw) x = Math.max(8, e.clientX - 290);
      if (y + 120 > vh) y = Math.max(8, e.clientY - 120);
      this.pcHover = { pn: r.partNumber, desc: r.partDescription || "", lc: this.pcLastChangeText(r),
                       inv: r.lastInventoryTransDate ? String(r.lastInventoryTransDate).slice(0, 10) : "", x: x, y: y };
    },
    pcRowLeave() { this.pcHover = null; },
    pcSugBlur() { var self = this; setTimeout(function () { self.pcSugOpen = false; self.pcSugIdx = -1; }, 120); },
    pcSortBy(col) {
      if (this.pcSort.col !== col) { this.pcSort = { col: col, dir: col === "partNumber" || col === "partDescription" ? 1 : -1 }; return; }
      if ((this.pcSort.col === "partNumber" || this.pcSort.col === "partDescription") ? this.pcSort.dir === 1 : this.pcSort.dir === -1) {
        this.pcSort = { col: col, dir: -this.pcSort.dir };
      } else {
        this.pcSort = { col: "", dir: -1 };
      }
    },
    // Used by the table, the charts, the Excel export and the presentation — one comparator, so what you
    // sorted on screen is what lands in the workbook and on the printed page.
    pcApplySort(rows) {
      var col = this.pcSort.col, dir = this.pcSort.dir;
      if (!col) return rows;
      var self = this;
      var key = function (r) {
        if (col === "lastChange") { var lc = self.partCostLastChange(r); return lc ? String(lc.date) : null; }
        // Derived, so it is not a plain row field. Unpriced/uncosted parts return null and therefore sort
        // LAST in both directions, same as every other missing value here.
        if (col === "margin") { var m = self.pcMargin(r); return m ? m.amt : null; }
        return r[col];
      };
      // A stable sort with MISSING VALUES ALWAYS LAST, in both directions. Letting null sort as 0 would
      // put "no cost on file" among the cheapest parts, which is the same absent-vs-zero confusion that
      // produced the 100%-margin defect.
      return rows.map(function (r, i) { return { r: r, i: i }; }).sort(function (a, b) {
        var x = key(a.r), y = key(b.r);
        var xn = (x === null || x === undefined || x === ""), yn = (y === null || y === undefined || y === "");
        if (xn && yn) return a.i - b.i;
        if (xn) return 1;
        if (yn) return -1;
        var c = (typeof x === "number" && typeof y === "number") ? (x - y) : String(x).localeCompare(String(y));
        return c !== 0 ? c * dir : a.i - b.i;
      }).map(function (o) { return o.r; });
    },
    // ---- PART(S) ORDERED: search every part in the selection, not the 500 the card holds ----
    // A rep searched YEX and got "No parts match" for a business that has ordered 147 YEX parts across
    // 525 lines -- none of them in the top 500 by quantity, so the browser had never been sent one.
    // Debounced in the child, sequence-guarded here, same shape as pcSuggest below.
    poPartSearch(term) {
      var self = this;
      var q = String(term || "").trim();
      if (q.length < 2) { this.poSearch = { term: "", rows: [], busy: false, capped: false, failed: false, descriptionSearched: true }; return; }
      var seq = (this._poSeq = (this._poSeq || 0) + 1);
      this.poSearch = Object.assign({}, this.poSearch, { busy: true, failed: false });
      var params = Object.assign({}, this.buildParams(false), { term: q });
      httpClient.get("/report/customer-insights/part-search", { params: params })
        .then(function (res) {
          if (seq !== self._poSeq) return;   // a slow "ye" must never overwrite the rows for "yex"
          var d = res.data || {};
          self.poSearch = {
            term: d.term || q,
            rows: Array.isArray(d.parts) ? d.parts : [],
            busy: false,
            capped: !!d.capped,
            failed: !!d.failed,
            // Default TRUE only when the server did not say; an absent field must not silently become
            // "descriptions were searched", which is the claim that was wrong.
            descriptionSearched: d.descriptionSearched !== false,
          };
        })
        .catch(function () {
          if (seq !== self._poSeq) return;
          // A failed search must never read as an empty result -- the card says so explicitly.
          self.poSearch = { term: q, rows: [], busy: false, capped: false, failed: true, descriptionSearched: true };
        });
    },
    // ---- search typeahead ----
    // Debounced so a fast typist fires one query, not eight. Stale-guarded by sequence: a slow response
    // for "dl" must never overwrite the list for "dlm".
    pcSuggest() {
      var self = this;
      var q = String(this.partCostSearch || "").trim();
      clearTimeout(this._pcSugTimer);
      if (q.length < 2) { this.pcSug = []; this.pcSugOpen = false; this.pcSugIdx = -1; return; }
      this._pcSugTimer = setTimeout(function () {
        var seq = (self._pcSugSeq = (self._pcSugSeq || 0) + 1);
        self.pcSugBusy = true;
        var params = Object.assign({}, self.buildParams(false), { term: q });
        httpClient.get("/report/customer-insights/part-cost-suggest", { params: params })
          .then(function (res) {
            if (seq !== self._pcSugSeq) return;
            var list = (res.data && res.data.suggestions) || [];
            // Drop anything already committed — offering a chip you already have is noise.
            var have = self.partCostTerms.map(function (t) { return String(t).toLowerCase(); });
            self.pcSug = list.filter(function (x) { return have.indexOf(String(x.value || "").toLowerCase()) < 0; });
            self.pcSugOpen = self.pcSug.length > 0;
            self.pcSugIdx = -1;
          })
          .catch(function () { if (seq === self._pcSugSeq) { self.pcSug = []; self.pcSugOpen = false; } })
          .finally(function () { if (seq === self._pcSugSeq) self.pcSugBusy = false; });
      }, 220);
    },
    // Picking a suggestion COMMITS it straight to a chip: the value came from the database, so there is
    // nothing left to refine, and it leaves the box empty for the next term.
    pcPickSuggest(i) {
      var x = this.pcSug[i];
      if (!x) return;
      this.partCostSearch = String(x.value || "");
      this.addPartCostTerm();
      this.pcSug = []; this.pcSugOpen = false; this.pcSugIdx = -1;
    },
    pcSugKey(e) {
      // "+" from the keyboard commits the typed term, exactly like the ＋ button. preventDefault stops the
      // literal plus landing in the box.
      if (e.key === "+") { e.preventDefault(); this.addPartCostTerm(); this.pcSug = []; this.pcSugOpen = false; return; }
      if (!this.pcSugOpen || !this.pcSug.length) {
        if (e.key === "Enter") { e.preventDefault(); this.addPartCostTerm(); }
        return;
      }
      if (e.key === "ArrowDown") { e.preventDefault(); this.pcSugIdx = (this.pcSugIdx + 1) % this.pcSug.length; }
      else if (e.key === "ArrowUp") { e.preventDefault(); this.pcSugIdx = (this.pcSugIdx <= 0 ? this.pcSug.length : this.pcSugIdx) - 1; }
      else if (e.key === "Enter" || e.key === "Tab") {
        if (this.pcSugIdx >= 0) { e.preventDefault(); this.pcPickSuggest(this.pcSugIdx); }
        else if (e.key === "Enter") { e.preventDefault(); this.addPartCostTerm(); this.pcSugOpen = false; }
      } else if (e.key === "Escape") { this.pcSugOpen = false; this.pcSugIdx = -1; }
    },
    // ＋ / Enter: park the typed text as a chip and clear the box for the next term. Silently ignores a
    // blank or duplicate so hammering ＋ can't build a row of empty chips.
    addPartCostTerm() {
      var t = String(this.partCostSearch || "").trim();
      if (!t) return;
      recordSearch("part-cost", t);   // Costing for Parts chip committed
      var dup = (this.partCostTerms || []).some(function (x) {
        return String(x).toLowerCase() === t.toLowerCase();
      });
      if (!dup) this.partCostTerms.push(t);
      this.partCostSearch = "";
      this.pcSug = []; this.pcSugOpen = false; this.pcSugIdx = -1;
    },
    removePartCostTerm(i) { this.partCostTerms.splice(i, 1); },
    clearPartCostSearch() { this.partCostTerms = []; this.partCostSearch = ""; this.pcSug = []; this.pcSugOpen = false; },
    // Table → horizontal bars → vertical bars → pie → table.
    // An ARRAY, not a lookup-with-|| fallback: the table state is "" , which is falsy, so
    // `{... pie: ""}[k] || "hbar"` sent pie straight back to horizontal bars and the cycle could never
    // return to the table. Indexing can't misread an empty string as "missing".
    cyclePartCostChart() {
      var order = ["", "hbar", "vbar", "pie", "gap"];
      var i = order.indexOf(this.pcChart);
      this.pcChart = order[(i < 0 ? 0 : i + 1) % order.length];
    },
    // "✨ Generate Insights" on the cost card. The browser sends only the FILTERS — the server re-queries
    // the costs itself, so the narrative always describes the real data rather than whatever this page
    // happens to be holding. Note that this ships cost figures to OpenAI; the endpoint carries the same
    // Master + All Insights gate as the numbers themselves.
    generatePartCostInsights() {
      if (!this.canSeeCost || this.pcAiBusy) return;
      var self = this;
      this.pcAiBusy = true;
      this.pcAiError = "";
      this.pcAi = "";
      this.pcAiBasis = "";
      httpClient.post("/report/customer-insights/part-cost-insights", { filters: this.buildParams(false) })
        .then(function (res) {
          var d = res.data || {};
          if (!d.ok) { self.pcAiError = d.message || "Couldn't generate cost insights."; return; }
          self.pcAi = d.insights || "";
          self.pcAiTitle = d.title || "";
          // State what the summary was actually computed over. Without it a briefing built from the top 40
          // parts reads as though it covered all of them.
          var b = d.basis || {};
          if (b.parts) {
            self.pcAiBasis = "Based on " + self.fmtNum(b.analysed || 0) + " of " + self.fmtNum(b.parts)
              + " parts in view (" + self.fmtNum(b.partsCosted || 0) + " have a cost on file"
              + (b.partsNoCost ? ", " + self.fmtNum(b.partsNoCost) + " don't" : "") + ")"
              + (b.changes ? " and the " + self.fmtNum(b.changes) + " most recent cost changes." : ".");
          }
        })
        .catch(function (err) {
          var st = (err && err.response) ? err.response.status : 0;
          self.pcAiError = st === 403
            ? "Your account isn't authorized for part costing."
            : "Something went wrong generating cost insights. Please try again.";
        })
        .finally(function () { self.pcAiBusy = false; });
    },
    // ---- Costing for Parts → printable presentation ----------------------------------------------
    // One chart rendered as an SVG STRING for the print document. Built from the same computeds the card
    // uses, so the printed chart and the on-screen chart cannot drift — and as vector, not a bitmap, so
    // it stays sharp on paper (an html2canvas capture of the card would only ever show whichever single
    // mode happened to be selected).
    // ONE generator for BOTH the on-screen chart and the print/Excel copy. The template used to carry
    // its own hand-written SVG while this method produced a second version for printing — two copies of
    // the same markup, free to drift. Now the card renders this output via v-html.
    //
    // Class names are emitted in BOTH modes; only the inline <style> is print-only. On screen the
    // external .ci-pcc-* rules apply, which is what keeps dark mode working. For print and for the PNG
    // rasterizer the SVG must stand alone, so the light-mode colours are inlined.
    _pcChartSvg(kind, g, wpx, hpx, forPrint) {
      var self = this;
      g = g || { bars: this.pcChartBars, ticks: this.pcChartTicks, pie: this.pcPie, gap: this.pcChartGap };
      if (!g.gap) g.gap = { bars: [], skipped: 0 };
      var esc = function (t) { return String(t == null ? "" : t).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); };
      var money = function (n) { return esc(self.fmtMoney(n)); };
      // Bar modes need more height than the pie: 12 rows at a 24-unit pitch is 288.
      var vbH = (kind === "hbar" || kind === "gap") ? 300 : 250;
      var dim = wpx ? ('width="' + wpx + '" height="' + hpx + '"') : 'width="100%" height="100%"';
      var style = forPrint
        ? '<style>'
          + '.ci-pcc-bar{fill:#c0392b}.ci-pcc-barp{fill:#2f8f5b}'
          + '.ci-pcc-lbl{font-size:9px;fill:#374151;font-family:ui-monospace,Consolas,monospace}'
          + '.ci-pcc-val{font-size:8px;fill:#6b7280}.ci-pcc-tick{font-size:8px;fill:#6b7280;text-anchor:end}'
          + '.ci-pcc-grid{stroke:#eceef1;stroke-width:1}.ci-pcc-axis{stroke:#d2d6dc;stroke-width:1}'
          + '.ci-pcc-zero{stroke:#9aa0a6;stroke-width:1}'
          + '.ci-pcc-vlbl{font-size:8px;fill:#374151;text-anchor:end;font-family:ui-monospace,Consolas,monospace}'
          + '.ci-pcc-leg{font-size:10px;fill:#374151}.ci-pcc-slice{stroke:#fff;stroke-width:1.5}'
          + '.ci-pcc-gapneg{fill:#c0392b}.ci-pcc-gappos{fill:#2f8f5b}'
          + '.ci-pcc-gapv{font-size:8px;text-anchor:end;fill:#374151}'
          + '.ci-pcc-gapp{font-size:7.5px;text-anchor:start;fill:#6b7280}.ci-pcc-gapp-neg{text-anchor:end}'
          + '</style>'
        : "";
      var open = '<svg viewBox="0 0 400 ' + vbH + '" ' + dim + ' xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMinYMin meet">' + style;
      // Shared legend for the paired modes — two series in one chart is unreadable without it.
      var legend = '<rect class="ci-pcc-bar" x="0" y="0" width="9" height="9" rx="2"/>'
        + '<text class="ci-pcc-leg" x="13" y="8">Unit cost</text>'
        + '<rect class="ci-pcc-barp" x="78" y="0" width="9" height="9" rx="2"/>'
        + '<text class="ci-pcc-leg" x="91" y="8">Unit price</text>';

      if (kind === "hbar") {
        // Each bar sits in its OWN <g> so its <title> tooltips that bar — an SVG <title> describes its
        // PARENT, and as siblings in the shared group only the first would count (every bar would hover
        // as bar #1).
        return open + '<g transform="translate(0,2)">' + legend + '</g><g transform="translate(0,16)">'
          + g.bars.map(function (b) {
            return '<g><title>' + esc(b.label) + " — cost " + money(b.value)
                 + (b.hasPrice ? ", price " + money(b.price) + ", margin " + money(b.price - b.value) : ", no price on file") + '</title>'
              + '<text class="ci-pcc-lbl" x="0" y="' + b.hty + '">' + esc(b.label) + '</text>'
              + '<rect class="ci-pcc-bar" x="92" y="' + b.hy + '" width="' + b.hw.toFixed(1) + '" height="8" rx="2"/>'
              + '<text class="ci-pcc-val" x="' + (96 + b.hw).toFixed(1) + '" y="' + (b.hy + 7) + '">' + money(b.value) + '</text>'
              + (b.hasPrice
                  ? '<rect class="ci-pcc-barp" x="92" y="' + b.hpy + '" width="' + b.hpw.toFixed(1) + '" height="8" rx="2"/>'
                    + '<text class="ci-pcc-val" x="' + (96 + b.hpw).toFixed(1) + '" y="' + (b.hpy + 7) + '">' + money(b.price) + '</text>'
                  : '<text class="ci-pcc-val" x="96" y="' + (b.hpy + 7) + '">no price on file</text>') + '</g>';
          }).join("") + "</g></svg>";
      }

      if (kind === "vbar") {
        return open + '<g transform="translate(0,2)">' + legend + '</g><g transform="translate(0,14)">'
          + g.ticks.map(function (t) {
            return '<line class="' + (t.i ? "ci-pcc-grid" : "ci-pcc-axis") + '" x1="48" y1="' + t.y + '" x2="394" y2="' + t.y + '"/>'
              + '<text class="ci-pcc-tick" x="44" y="' + (t.y + 3) + '">' + esc(t.label) + '</text>';
          }).join("")
          + g.bars.map(function (b) {
            return '<g><title>' + esc(b.label) + " — cost " + money(b.value)
                 + (b.hasPrice ? ", price " + money(b.price) + ", margin " + money(b.price - b.value) : ", no price on file") + '</title>'
              + '<rect class="ci-pcc-bar" x="' + b.vx.toFixed(1) + '" y="' + b.vy.toFixed(1) + '" width="' + b.vw.toFixed(1) + '" height="' + b.vh.toFixed(1) + '" rx="1"/>'
              + (b.hasPrice ? '<rect class="ci-pcc-barp" x="' + b.vpx.toFixed(1) + '" y="' + b.vpy.toFixed(1) + '" width="' + b.vpw.toFixed(1) + '" height="' + b.vph.toFixed(1) + '" rx="1"/>' : "")
              + '<text class="ci-pcc-vlbl" x="' + b.vcx.toFixed(1) + '" y="176" transform="rotate(-55 ' + b.vcx.toFixed(1) + ' 176)">' + esc(b.label) + '</text></g>';
          }).join("") + "</g></svg>";
      }

      if (kind === "gap") {
        return open + '<g transform="translate(0,6)">'
          + '<line class="ci-pcc-zero" x1="263" y1="0" x2="263" y2="' + (g.gap.bars.length * 24 + 4) + '"/>'
          + g.gap.bars.map(function (b) {
            return '<g><title>' + esc(b.label) + " — margin " + money(b.gap) + " (" + b.pct.toFixed(1) + "% of price): price " + money(b.price) + " vs cost " + money(b.cost) + '</title>'
              + '<text class="ci-pcc-lbl" x="0" y="' + b.ty + '">' + esc(b.label) + '</text>'
              + '<text class="ci-pcc-gapv" x="126" y="' + b.ty + '">' + money(b.gap) + '</text>'
              + '<rect class="' + (b.neg ? "ci-pcc-gapneg" : "ci-pcc-gappos") + '" x="' + b.x.toFixed(1) + '" y="' + b.y + '" width="' + b.w.toFixed(1) + '" height="10" rx="2"/>'
              // The margin %, at the bar's FREE end (left of a negative bar, right of a positive one),
              // where it can never collide with the zero line or the dollar column.
              + '<text class="' + (b.neg ? "ci-pcc-gapp ci-pcc-gapp-neg" : "ci-pcc-gapp") + '" x="' + (b.neg ? (b.x - 3).toFixed(1) : (b.x + b.w + 3).toFixed(1)) + '" y="' + b.ty + '">' + b.pct.toFixed(1) + '%</text></g>';
          }).join("") + "</g></svg>";
      }

      return open + g.pie.map(function (p) {
        return '<path d="' + p.d + '" fill="' + p.color + '" class="ci-pcc-slice"/>';
      }).join("") + g.pie.map(function (p) {
        return '<rect x="228" y="' + (p.ly - 9) + '" width="11" height="11" rx="2" fill="' + p.color + '"/>'
          + '<text class="ci-pcc-leg" x="246" y="' + p.ly + '">' + esc(p.label) + " &#8212; " + p.pct.toFixed(1) + '%</text>';
      }).join("") + "</svg>";
    },
    // Screenshot of the CARD (not the dashboard): walk up from the card body to its grid tile so the title
    // bar comes too. Captured in LIGHT mode via the onclone trick, same as captureDashboardShot, so a
    // dark-theme user still gets a printable page. Best-effort — "" just drops that one page.
    _capturePartCostShot() {
      if (typeof document === "undefined" || typeof html2canvas !== "function") return Promise.resolve("");
      var body = document.querySelector(".ci-pcost");
      if (!body) return Promise.resolve("");
      var el = (body.closest && body.closest(".vue-grid-item")) || body;
      return html2canvas(el, {
        backgroundColor: "#ffffff", useCORS: true, logging: false,
        onclone: function (doc) { try { if (doc && doc.documentElement) doc.documentElement.classList.remove("dark-mode", "mid-mode"); } catch (e) { /* noop */ } },
        scale: Math.min(2, (window.devicePixelRatio || 1) * 1.2),
        windowWidth: el.scrollWidth, width: el.scrollWidth,
        height: el.scrollHeight, windowHeight: el.scrollHeight, scrollX: 0, scrollY: 0
      }).then(function (c) { try { return c.toDataURL("image/png"); } catch (e) { return ""; } })
        .catch(function () { return ""; });
    },
    partCostPresentation() {
      if (!this.canSeeCost || this.pcPrintBusy) return;
      var self = this;
      // Open the tab SYNCHRONOUSLY inside the click, or the pop-up blocker eats it — the capture below is
      // async and a window.open after an await never reaches the screen.
      var w = null;
      try { w = window.open("", "_blank"); } catch (e) { w = null; }
      if (!w) { window.alert("Please allow pop-ups for this site to open the presentation."); return; }
      try {
        w.document.open();
        w.document.write("<!DOCTYPE html><html><head><meta charset='utf-8'><title>Costing for Parts</title></head>"
          + "<body style=\"font-family:Arial,Helvetica,sans-serif;color:#666;padding:48px;font-size:15px;\">Preparing your presentation&hellip;"
          + "<br><span style=\"font-size:12px;color:#999;\">Capturing the card &mdash; this can take a few seconds.</span></body></html>");
        w.document.close();
      } catch (e) { /* noop */ }
      this.pcPrintBusy = true;
      this._capturePartCostShot()
        .then(function (shot) {
          var html = self._buildPartCostPresentation(shot);
          try { w.document.open(); w.document.write(html); w.document.close(); w.focus(); } catch (e) { return; }
          setTimeout(function () { try { w.print(); } catch (e) { /* noop */ } }, 500);
        })
        .catch(function () {
          try {
            w.document.open();
            w.document.write("<body style=\"font-family:Arial;padding:40px;color:#c0392b;\">Couldn't build the presentation. Please close this tab and try again.</body>");
            w.document.close();
          } catch (e2) { /* noop */ }
        })
        .finally(function () { self.pcPrintBusy = false; });
    },
    _buildPartCostPresentation(shotUrl) {
      var self = this;
      var esc = function (s) { return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); };
      var t = this.partCostTotals || {};
      var rows = this.partCostView || [];
      var dateRange = this.fmtShort(this.filters.from) + " &ndash; " + this.fmtShort(this.filters.to);
      var fparts = [];
      if ((this.filters.territory || []).length) fparts.push("Territory: " + esc(this.filters.territory.join(", ")));
      if ((this.filters.customer || []).length) fparts.push("Customer: " + esc(this.filters.customer.join(", ")));
      if ((this.filters.salesRep || []).length) fparts.push("Rep: " + esc(this.filters.salesRep.join(", ")));
      if ((this.filters.part || []).length) fparts.push("Part: " + esc(this.filters.part.join(", ")));
      if (this.partCostTermList.length) fparts.push("Card search: " + esc(this.partCostSearchLabel));
      var filterSummary = fparts.length ? fparts.join(" &middot; ") : "All customers";

      var H = function (txt) { return '<div class="h2">' + txt + "</div>"; };
      var kpi = function (label, val) {
        return '<div class="kpi"><div class="kpi-l">' + esc(label) + '</div><div class="kpi-v">' + esc(val) + "</div></div>";
      };

      // PAGE 1 — the card as the user sees it, plus the headline counts.
      var p1 = '<section class="pg">'
        + '<div class="ttl">Costing for Parts</div>'
        + '<div class="sub">' + dateRange + " &middot; " + filterSummary + "</div>"
        + '<div class="kpis">'
        + kpi("Parts in view", this.fmtNum(t.parts || 0))
        + kpi("With cost on file", this.fmtNum(t.partsCosted || 0))
        + kpi("No cost on file", this.fmtNum(t.partsNoCost || 0))
        + kpi("Newest cost change", t.lastChange ? String(t.lastChange).slice(0, 10) : "—")
        + "</div>"
        + (shotUrl ? H("The card") + '<img class="shot" src="' + shotUrl + '" alt="Costing for Parts card">' : "")
        + "</section>";

      // PAGE 2 — ALL THREE charts, regardless of which one is on screen.
      var bars = this.pcChartBars.length, pie = this.pcPie.length;
      var p2 = '<section class="pg">' + H("Charts")
        + (bars
            ? '<div class="cap">' + esc("The " + this.fmtNum(bars) + " highest-cost parts in view by current unit cost.") + "</div>"
              + '<div class="chart">' + this._pcChartSvg("hbar", null, 900, 560, true) + "</div>"
              + '<div class="chart">' + this._pcChartSvg("vbar", null, 900, 560, true) + "</div>"
            : '<div class="cap">No parts in this view have a cost on file, so there is nothing to chart.</div>')
        + (pie
            ? '<div class="cap">Cost mix across the ' + esc(this.fmtNum(this.pcCostedInView)) + " parts with a cost on file &mdash; the share of a typical part's unit cost, not money spent.</div>"
              + '<div class="chart">' + this._pcChartSvg("pie", null, 900, 560, true) + "</div>"
          + (this.pcChartGap.bars.length
              ? '<div class="cap">Current margin (price &minus; cost), worst first. Red is priced BELOW today&rsquo;s cost. Both figures are current, so this is not margin at the time of sale.</div>'
                + '<div class="chart">' + this._pcChartSvg("gap", null, 900, 560, true) + "</div>"
              : "")
            : "")
        + "</section>";

      // PAGE 3 — the AI briefing, only if one has been generated.
      var p3 = "";
      if (this.pcAi) {
        p3 = '<section class="pg">' + H(this.pcAiTitle || "Cost insights")
          + '<ul class="ins">' + this.pcAiLines.map(function (l) { return "<li>" + esc(l) + "</li>"; }).join("") + "</ul>"
          + (this.pcAiBasis ? '<div class="basis">' + esc(this.pcAiBasis) + "</div>" : "")
          + '<div class="basis">AI-generated from the current filters. Figures above are from the report; the wording is a summary and should be read as such.</div>'
          + "</section>";
      }

      // PAGE 4+ — the filtered data. Capped so a 25,000-row selection can't produce a print job nobody
      // wanted; the cap is STATED rather than silently applied.
      var CAP = 300;
      var shown = rows.slice(0, CAP);
      var body = shown.map(function (r) {
        var lc = self.partCostLastChange(r);
        var mg = self.pcMargin(r);
        var m = function (v) { return v == null ? "&mdash;" : esc(self.fmtMoney(v)); };
        return "<tr><td class='pn'>" + esc(r.partNumber) + "</td><td>" + esc(r.partDescription || "") + "</td>"
          + "<td class='n'>" + m(r.unitMaterialCost) + "</td><td class='n'>" + m(r.unitLaborCost) + "</td>"
          + "<td class='n'>" + m(r.unitBurdenCost) + "</td><td class='n'>" + m(r.unitServiceCost) + "</td>"
          + "<td class='n b'>" + (r.unitCostTotal > 0 ? esc(self.fmtMoney(r.unitCostTotal)) : "<span class='nc'>no cost on file</span>") + "</td>"
          + "<td class='n'>" + (r.unitPrice > 0 ? esc(self.fmtMoney(r.unitPrice)) : "<span class='nc'>no price</span>") + "</td>"
          + "<td class='n'>" + (mg ? (mg.amt < 0 ? "<span class='neg'>" : "<span>") + esc(self.fmtMoney(mg.amt)) + " (" + mg.pct.toFixed(1) + "%)</span>" : "&mdash;") + "</td>"
          + "<td class='n'>" + esc(self.fmtNum(r.qty)) + "</td>"
          + "<td>" + (lc ? esc(String(lc.date).slice(0, 10)) + " <span class='who'>" + esc(lc.area) + (lc.user ? " &middot; " + esc(lc.user) : "") + "</span>" : "&mdash;") + "</td></tr>";
      }).join("");
      var p4 = '<section class="pg">' + H("Part cost detail")
        + '<div class="cap">' + esc(this.fmtNum(shown.length)) + " of " + esc(this.fmtNum(rows.length)) + " rows in the current view"
        + (rows.length > CAP ? " &mdash; the remaining " + esc(this.fmtNum(rows.length - CAP)) + " are in the Excel export, not printed here." : "") + "</div>"
        + '<table class="dt"><thead><tr><th>Part</th><th>Description</th><th class="n">Material</th><th class="n">Labor</th>'
        + '<th class="n">Burden</th><th class="n">Service</th><th class="n">Unit cost</th><th class="n">Unit price</th><th class="n">Margin</th><th class="n">Qty</th><th>Last cost change</th></tr></thead>'
        + "<tbody>" + body + "</tbody></table></section>";

      var css = "@page{size:landscape;margin:12mm}"
        + "*{box-sizing:border-box}body{margin:0;font-family:Arial,Helvetica,sans-serif;color:#222}"
        + ".pg{page-break-after:always;padding:0 0 10px}.pg:last-child{page-break-after:auto}"
        + ".ttl{font-size:24px;font-weight:800;color:#9e2436}"
        + ".sub{font-size:12px;color:#666;margin:2px 0 14px}"
        + ".h2{font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;color:#9e2436;margin:0 0 6px}"
        + ".kpis{display:flex;gap:10px;margin:0 0 14px}"
        + ".kpi{flex:1;border:1px solid #e6e6ea;border-radius:8px;padding:10px 12px}"
        + ".kpi-l{font-size:10px;text-transform:uppercase;letter-spacing:.04em;color:#888}"
        + ".kpi-v{font-size:20px;font-weight:800;color:#9e2436;margin-top:2px}"
        + ".shot{width:100%;border:1px solid #e6e6ea;border-radius:6px}"
        + ".chart{border:1px solid #e6e6ea;border-radius:6px;padding:8px;margin:0 0 12px;page-break-inside:avoid}"
        + ".cap{font-size:11px;color:#666;margin:0 0 6px}"
        + ".ins{margin:0 0 10px;padding-left:18px}.ins li{font-size:12.5px;line-height:1.5;margin-bottom:4px}"
        + ".basis{font-size:10px;color:#777;border-top:1px dashed #ddd;padding-top:5px;margin-top:6px}"
        + ".dt{width:100%;border-collapse:collapse;font-size:9.5px;table-layout:fixed}"
        + ".dt th{background:#f4f5f7;text-align:left;padding:4px 5px;border-bottom:1px solid #ddd;font-size:9px;text-transform:uppercase;letter-spacing:.03em;color:#555}"
        + ".dt td{padding:3px 5px;border-bottom:1px solid #f0f1f3;vertical-align:top;overflow-wrap:anywhere}"
        + ".dt tr{page-break-inside:avoid}"
        + ".dt .n{text-align:right;white-space:nowrap}.dt .b{font-weight:700}"
        + ".dt .pn{font-family:ui-monospace,Consolas,monospace;white-space:nowrap}"
        + ".nc{color:#8a5300;font-style:italic}.who{color:#777}.neg{color:#c0392b;font-weight:700}"
        + ".foot{font-size:9px;color:#999;margin-top:10px}"
        + ".conf{font-size:10px;color:#9e2436;font-weight:700;margin-top:4px}";

      return "<!DOCTYPE html><html><head><meta charset='utf-8'><title>Costing for Parts</title><style>" + css + "</style></head><body>"
        + p1 + p2 + p3 + p4
        + '<div class="foot">Generated ' + esc(new Date().toLocaleString()) + " from the JC Steele Service Portal &middot; Sales Insights &rarr; Costing for Parts."
        + " Unit cost is the CURRENT standard cost from VMFG, not the cost at time of sale, so it cannot be used to derive historical margin.</div>"
        + '<div class="conf">Confidential &mdash; part cost is restricted to Master and All Insights roles.</div>'
        + "</body></html>";
    },
    openPartCostExcel() {
      if (!this.canSeeCost) return;
      this.pcExcelModal = { open: true, scope: this.partCostTermList.length ? "search" : "page", busy: false, error: "" };
    },
    confirmPartCostExcel() {
      var self = this;
      var scope = this.pcExcelModal.scope;
      if (scope !== "all") { this._writePartCostWorkbook(scope === "search" ? this.partCostView : this.partCostRows, scope); return; }
      // "All" re-queries with a big top rather than paging the card, so the workbook is one consistent
      // snapshot. The server bounds it at 25,000.
      this.pcExcelModal.busy = true;
      this.pcExcelModal.error = "";
      var params = Object.assign({}, this.buildParams(false), { top: 25000 });
      httpClient.get("/report/customer-insights/part-costs", { params: params })
        .then(function (res) {
          var d = res.data || {};
          if (d.available === false) { self.pcExcelModal.error = "Part costing is not available on this database."; return; }
          // The user sorted the card; the workbook must arrive in that order too.
          return self._writePartCostWorkbook(self.pcApplySort(d.rows || []), "all");
        })
        .catch(function (err) {
          var st = (err && err.response) ? err.response.status : 0;
          self.pcExcelModal.error = st === 403
            ? "Your account is not authorized for part costing."
            : "Couldn't fetch all parts — try This page, or narrow the dashboard filter.";
        })
        .finally(function () { self.pcExcelModal.busy = false; });
    },
    // Rasterize one of our SVG charts to a PNG data URL. A data: URI does NOT taint the canvas, so
    // toDataURL stays legal. Resolves to "" on any failure — a missing picture must never abort an export
    // whose actual payload is the numbers.
    _svgToPng(svg, w, h) {
      return new Promise(function (resolve) {
        try {
          var img = new Image();
          img.onload = function () {
            try {
              var c = document.createElement("canvas");
              c.width = w * 2; c.height = h * 2;          // 2x so the chart is not soft when printed
              var ctx = c.getContext("2d");
              ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height);   // Excel has no page white
              ctx.drawImage(img, 0, 0, c.width, c.height);
              resolve(c.toDataURL("image/png"));
            } catch (e) { resolve(""); }
          };
          img.onerror = function () { resolve(""); };
          img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
        } catch (e) { resolve(""); }
      });
    },
    // Excel export. ExcelJS rather than SheetJS because the user asked for the CHARTS in the workbook and
    // SheetJS CE can write neither a chart object nor an image (verified against the installed 0.18.5).
    // ExcelJS cannot emit a NATIVE Excel chart either, so each chart goes in as a PNG rasterized from the
    // very SVG the card draws, AND its series goes in as a data sheet: the picture to read, the numbers to
    // re-chart and to check the picture against.
    // DYNAMIC import so ~250KB gzipped of ExcelJS is code-split into its own chunk and only downloaded by
    // someone who actually exports — the main bundle is already 630KB gzipped.
    _writePartCostWorkbook(rows, scope) {
      var self = this;
      rows = rows || [];
      this.pcExcelModal.busy = true;
      this.pcExcelModal.error = "";

      // Geometry from the EXPORTED rows, not the on-screen view — an "all parts" workbook must chart all
      // parts. Same pure functions the card uses, so the two can never drift.
      var g = { bars: pcBarsFrom(rows), pie: pcPieFrom(rows), gap: pcGapFrom(rows), ticks: null };
      g.ticks = pcTicksFrom(g.bars);
      var CW = 900, CH = 560;
      var charts = [
        { key: "hbar", title: "Unit cost vs unit price by part (horizontal bars)" },
        { key: "vbar", title: "Unit cost vs unit price by part (vertical bars)" },
        { key: "pie",  title: "Cost mix — share of a typical part's unit cost" },
        { key: "gap",  title: "Current margin (price − cost), worst first (red = priced below today's cost)" }
      ].filter(function (c) {
        if (c.key === "pie") return g.pie.length > 0;
        if (c.key === "gap") return g.gap.bars.length > 0;
        return g.bars.length > 0;
      });
      // forPrint=true inlines the colours: a rasterized PNG and a separate print document both have to
      // stand alone, with none of this page's stylesheet available to them.
      charts.forEach(function (c) { c.svg = self._pcChartSvg(c.key, g, CW, CH, true); });

      var costed = rows.filter(function (r) { return r.unitCostTotal > 0; });
      var csum = function (k) { return costed.reduce(function (acc, r) { return acc + (Number(r[k]) || 0); }, 0); };
      var mix = [["Material", csum("unitMaterialCost")], ["Labor", csum("unitLaborCost")],
                 ["Burden", csum("unitBurdenCost")], ["Service", csum("unitServiceCost")]];
      var mixTot = mix.reduce(function (acc, m) { return acc + m[1]; }, 0);
      var t = this.partCostTotals || {};

      // RETURNED so a caller that pre-fetched rows (the "all" scope) can chain on it — otherwise its
      // .finally cleared the modal's busy flag while this was still importing ExcelJS and rasterizing
      // PNGs, re-enabling Export mid-build (double download).
      return Promise.all([
        import(/* webpackChunkName: "exceljs" */ "exceljs"),
        Promise.all(charts.map(function (c) { return self._svgToPng(c.svg, CW, CH); }))
      ]).then(function (res) {
        var ExcelJS = res[0] && res[0].default ? res[0].default : res[0];
        var pngs = res[1] || [];
        var wb = new ExcelJS.Workbook();
        wb.creator = "JC Steele Service Portal";
        wb.created = new Date();
        var MONEY = '"$"#,##0.00';

        var sheet = function (name, cols, aoa, widths) {
          var ws = wb.addWorksheet(name);
          ws.addRow(cols);
          ws.getRow(1).font = { bold: true };
          ws.getRow(1).alignment = { vertical: "middle" };
          aoa.forEach(function (r) { ws.addRow(r); });
          ws.views = [{ state: "frozen", ySplit: 1 }];
          (widths || []).forEach(function (w, i) { if (w) ws.getColumn(i + 1).width = w; });
          return ws;
        };

        // 1. The detail rows.
        var detail = rows.map(function (r) {
          var lc = self.partCostLastChange(r);
          var mg = self.pcMargin(r);
          return [
            r.partNumber, r.partDescription || "",
            // null stays EMPTY, never 0 — a blank cell reads "not on file", a 0 reads "free".
            r.unitMaterialCost, r.unitLaborCost, r.unitBurdenCost, r.unitServiceCost,
            (r.unitCostTotal > 0 ? r.unitCostTotal : null),
            // 0 exports as an EMPTY cell for the same reason it renders blank on screen.
            (r.unitPrice > 0 ? r.unitPrice : null),
            // Both EMPTY unless cost and price are both on file — never 0, which would read as
            // "breaks even" instead of "cannot be computed".
            (mg ? mg.amt : null),
            (mg ? Math.round(mg.pct * 10) / 10 : null),
            (r.unitCostTotal > 0 ? "Yes" : "No"),
            r.qty, r.orders,
            r.lastInventoryTransDate ? String(r.lastInventoryTransDate).slice(0, 10) : "",
            lc ? String(lc.date).slice(0, 10) : "", lc ? lc.area : "", lc ? (lc.user || "") : "",
            lc ? lc.oldV : null, lc ? lc.newV : null
          ];
        });
        var ws1 = sheet("Part Cost",
          ["Part", "Description", "Material", "Labor", "Burden", "Service", "Unit cost", "Unit price",
           "Margin", "Margin %", "Cost on file", "Qty in view", "Orders in view", "Last inv trans",
           "Last change", "Area", "Changed by", "Old", "New"],
          detail, [18, 42, 12, 12, 12, 12, 13, 13, 13, 10, 11, 12, 13, 13, 12, 10, 16, 12, 12]);
        // Money at 3-9, Old/New now at 18-19 (Last inv trans pushed them one right).
        [3, 4, 5, 6, 7, 8, 9, 18, 19].forEach(function (c) { ws1.getColumn(c).numFmt = MONEY; });
        ws1.getColumn(10).numFmt = '0.0"%"';

        // 2. The charts, as pictures.
        var cs = wb.addWorksheet("Charts");
        cs.getColumn(1).width = 26;
        var row = 1;
        charts.forEach(function (c, i) {
          cs.getCell("A" + row).value = c.title;
          cs.getCell("A" + row).font = { bold: true, size: 12 };
          row += 1;
          if (pngs[i]) {
            var id = wb.addImage({ base64: pngs[i], extension: "png" });
            cs.addImage(id, { tl: { col: 0, row: row }, ext: { width: 720, height: 448 } });
            row += 25;
          } else {
            cs.getCell("A" + row).value = "This chart could not be rendered as an image in your browser — its numbers are on the data sheet.";
            row += 3;
          }
        });
        if (!charts.length) cs.getCell("A1").value = "No parts in this selection have a cost on file, so there is nothing to chart.";

        // 3. The same series as data, so the charts can be rebuilt, sorted and checked.
        // The series behind the paired bars — now the FULL pair plus the margin, so the sheet can
        // rebuild exactly what the picture shows. Missing price stays EMPTY, never 0.
        var ws2 = sheet("Chart - Top Parts", ["Part", "Description", "Unit cost", "Unit price", "Margin", "Margin %"],
          g.bars.map(function (b) { return [b.label, b.desc, b.value,
            b.hasPrice ? b.price : null,
            b.hasPrice ? b.price - b.value : null,
            b.hasPrice ? Math.round((b.price - b.value) / b.price * 1000) / 10 : null]; }), [20, 40, 13, 13, 13, 10]);
        [3, 4, 5].forEach(function (c) { ws2.getColumn(c).numFmt = MONEY; });
        ws2.getColumn(6).numFmt = '0.0"%"';
        // The series behind the margin chart, worst first — same rows, same order.
        if (g.gap.bars.length) {
          var wsm = sheet("Chart - Margin", ["Part", "Description", "Unit cost", "Unit price", "Margin", "Margin %"],
            g.gap.bars.map(function (b) { return [b.label, b.desc, b.cost, b.price, b.gap, Math.round(b.pct * 10) / 10]; }),
            [20, 40, 13, 13, 13, 10]);
          [3, 4, 5].forEach(function (c) { wsm.getColumn(c).numFmt = MONEY; });
          wsm.getColumn(6).numFmt = '0.0"%"';
        }
        var ws3 = sheet("Chart - Cost Mix", ["Component", "Sum of unit costs", "Share %"],
          mix.map(function (m) { return [m[0], m[1], mixTot > 0 ? Math.round(m[1] / mixTot * 1000) / 10 : null]; }),
          [16, 20, 12]);
        ws3.getColumn(2).numFmt = MONEY;

        // 4. Provenance. A workbook of cost figures that escapes into an inbox should still say what it is.
        sheet("About", ["Field", "Value"], [
          ["Report", "Sales Insights — Costing for Parts"],
          ["Generated", new Date().toISOString().slice(0, 19).replace("T", " ")],
          ["Scope", scope === "all" ? "All parts in view" : (scope === "search" ? ("Search: " + self.partCostSearchLabel) : "This page (highest-cost parts loaded)")],
          ["Rows in this workbook", rows.length],
          ["Parts in view (server)", t.parts == null ? "" : t.parts],
          ["With cost on file", t.partsCosted == null ? "" : t.partsCosted],
          ["No cost on file", t.partsNoCost == null ? "" : t.partsNoCost],
          ["Date range", (self.fromDate || "") + " to " + (self.toDate || "")],
          ["Charts", "The Charts sheet holds pictures of the card's charts. The two 'Chart - …' sheets hold the same numbers, so you can re-chart or verify them."],
          ["Cost Mix meaning", "'Share %' is each component's share of the SUMMED UNIT COSTS across parts that have a cost on file — what a typical part's cost is made of. It is NOT total money spent."],
          ["Note", "Unit cost is the CURRENT cost from VMFG. It is not the cost at the time of sale, so it cannot be used to compute historical margin. No margin or revenue is included."],
          ["Confidential", "Part cost is restricted to Master and All Insights roles."]
        ], [24, 110]);

        return wb.xlsx.writeBuffer().then(function (buf) {
          var blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
          var url = URL.createObjectURL(blob);
          var link = document.createElement("a");
          link.href = url;
          link.download = "part-costs-" + new Date().toISOString().slice(0, 10) + ".xlsx";
          document.body.appendChild(link);
          link.click();
          setTimeout(function () { try { URL.revokeObjectURL(url); link.parentNode.removeChild(link); } catch (e) { /* noop */ } }, 1500);
          self.pcExcelModal.open = false;
        });
      }).catch(function (err) {
        if (typeof console !== "undefined") console.error("[Sales Insights] part-cost Excel export failed:", err);
        self.pcExcelModal.error = "Couldn't build the workbook. Please try again.";
      }).finally(function () { self.pcExcelModal.busy = false; });
    },
    // Parts Sold at a Loss — the Accounting report card. Follows the dashboard From/To window; everything
    // else about the selection (customer, rep, territory…) is deliberately ignored — rpt_PartLoss is
    // company-wide shipment data gated by ROLE, not territory. The server is the boundary ([Authorize]
    // Master/Accounting Insights/All Insights); a 403 here is the correct answer for an unauthorized call.
    loadPartLoss() {
      if (!this.canSeeLoss) return;
      var seq = (this._plSeq = (this._plSeq || 0) + 1);
      var self = this;
      this.plBusy = true;
      var params = { category: this.plCat, top: 500 };
      if ((this.plTerms || []).length) params.search = this.plTerms.join(",");
      var f = this.filters || {};
      if (f.from) params.from = f.from;
      if (f.to) params.to = f.to;
      httpClient.get("/report/accounting/part-loss", { params: params })
        .then(function (res) {
          if (seq !== self._plSeq) return;
          var d = res.data || {};
          self.plAvailable = d.available !== false;
          self.plQueryError = !!d.queryError;
          self.plDetail = d.detail || "";
          self.plRows = Array.isArray(d.rows) ? d.rows : [];
          self.plCats = Array.isArray(d.categories) ? d.categories : [];
          self.plTruncated = !!d.truncated;
        })
        .catch(function (err) {
          if (seq !== self._plSeq) return;
          self.plRows = []; self.plCats = []; self.plTruncated = false;
          if (err && err.response && err.response.status === 403) {
            self.plAvailable = false;
            self.plDetail = "Your sign-in token doesn't carry an accounting role yet — if you were just granted access, sign out and back in (role permissions live in the token for up to 24 hours).";
          } else {
            self.plQueryError = true;
            self.plDetail = "The request failed — " + ((err && err.message) || "network error");
          }
        })
        .finally(function () { if (seq === self._plSeq) self.plBusy = false; });
    },
    setPlCat(k) {
      if (this.plCat === k) return;
      this.plCat = k;
      this.loadPartLoss();   // rows are fetched per category; the tab counts ride along on every response
    },
    setPlSort(col) {
      if (this.plSort === col) { this.plDir = this.plDir === "asc" ? "desc" : "asc"; return; }
      this.plSort = col;
      // First click gives the reading you actually want: newest first for the date, the most NEGATIVE
      // line first for loss (ascending — LineLoss is price minus cost, so a big loss is a big negative).
      this.plDir = col === "transactionDate" ? "desc" : col === "lineLoss" ? "asc" : "asc";
    },
    plSortMark(col) { return this.plSort === col ? (this.plDir === "asc" ? " ▲" : " ▼") : ""; },
    // Cents matter on unit figures (fmtMoney rounds for dashboard KPIs), and toLocaleString puts the
    // minus INSIDE the $ — format the sign by hand so credits read "-$40,877.00".
    plMoney(v) {
      var n = Number(v || 0);
      var s = Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return (n < 0 ? "-$" : "$") + s;
    },
    // The one comparator behind the table, the presentation and every Excel scope — what you sorted on
    // screen is what lands in the workbook and on the printed page.
    plApplySort(rows) {
      var col = this.plSort, dir = this.plDir === "asc" ? 1 : -1;
      rows = (rows || []).slice();
      rows.sort(function (a, b) {
        var av = a[col], bv = b[col];
        if (typeof av === "string" || typeof bv === "string") {
          av = String(av == null ? "" : av).toLowerCase(); bv = String(bv == null ? "" : bv).toLowerCase();
          return av < bv ? -dir : av > bv ? dir : 0;
        }
        return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
      });
      return rows;
    },
    // ---- search typeahead (mirrors pcSuggest; server-side across the whole window) ----
    plSuggest() {
      var self = this;
      var q = String(this.plSearch || "").trim();
      clearTimeout(this._plSugTimer);
      if (q.length < 2) { this._plSugSeq = (this._plSugSeq || 0) + 1; this.plSug = []; this.plSugOpen = false; this.plSugIdx = -1; return; }
      this._plSugTimer = setTimeout(function () {
        var seq = (self._plSugSeq = (self._plSugSeq || 0) + 1);
        self.plSugBusy = true;
        var f = self.filters || {};
        var params = { term: q };
        if (f.from) params.from = f.from;
        if (f.to) params.to = f.to;
        httpClient.get("/report/accounting/part-loss-suggest", { params: params })
          .then(function (res) {
            if (seq !== self._plSugSeq) return;
            var list = (res.data && res.data.suggestions) || [];
            var have = self.plTerms.map(function (t) { return String(t).toLowerCase(); });
            self.plSug = list.filter(function (x) { return have.indexOf(String(x.value || "").toLowerCase()) < 0; });
            self.plSugOpen = self.plSug.length > 0;
            self.plSugIdx = -1;
          })
          .catch(function () { if (seq === self._plSugSeq) { self.plSug = []; self.plSugOpen = false; } })
          .finally(function () { if (seq === self._plSugSeq) self.plSugBusy = false; });
      }, 220);
    },
    plPickSuggest(i) {
      var x = this.plSug[i];
      if (!x) return;
      this.plSearch = String(x.value || "");
      this.addPlTerm();
      this.plSug = []; this.plSugOpen = false; this.plSugIdx = -1;
    },
    plSugKeydown(e) {
      if (e.key === "+") { e.preventDefault(); this.addPlTerm(); this.plSug = []; this.plSugOpen = false; return; }
      if (!this.plSugOpen || !this.plSug.length) {
        if (e.key === "Enter") { e.preventDefault(); this.addPlTerm(); }
        return;
      }
      if (e.key === "ArrowDown") { e.preventDefault(); this.plSugIdx = (this.plSugIdx + 1) % this.plSug.length; }
      else if (e.key === "ArrowUp") { e.preventDefault(); this.plSugIdx = (this.plSugIdx <= 0 ? this.plSug.length : this.plSugIdx) - 1; }
      else if (e.key === "Enter" || e.key === "Tab") {
        if (this.plSugIdx >= 0) { e.preventDefault(); this.plPickSuggest(this.plSugIdx); }
        else if (e.key === "Enter") { e.preventDefault(); this.addPlTerm(); this.plSugOpen = false; }
      } else if (e.key === "Escape") { this.plSugOpen = false; this.plSugIdx = -1; }
    },
    plSugBlur() { var self = this; setTimeout(function () { self.plSugOpen = false; self.plSugIdx = -1; }, 120); },
    // Chips travel to the SERVER comma-joined (house style), so a comma inside a chip would split it
    // into two terms over there — strip commas before committing.
    addPlTerm() {
      var t = String(this.plSearch || "").trim().replace(/,/g, " ").replace(/\s+/g, " ").trim();
      recordSearch("part-loss", t);   // Parts Sold at a Loss chip committed
      this.plSearch = "";
      this.plSug = []; this.plSugOpen = false; this.plSugIdx = -1;
      // Kill the debounce timer AND orphan any in-flight response — without this a suggest armed just
      // before Enter/＋ reopens the dropdown a beat after the chip lands.
      clearTimeout(this._plSugTimer);
      this._plSugSeq = (this._plSugSeq || 0) + 1;
      if (!t) return;
      // The server drops terms under 2 characters and caps at 8 (PartLossSearchWhere) — refuse them
      // HERE, keeping the text in the box, so a chip that would filter nothing never renders as active.
      if (t.length < 2) { this.plSearch = t; return; }
      if ((this.plTerms || []).length >= 8) { window.alert("Up to 8 search terms — remove one first."); this.plSearch = t; return; }
      var dup = (this.plTerms || []).some(function (x) { return String(x).toLowerCase() === t.toLowerCase(); });
      if (dup) return;
      this.plTerms.push(t);
      this.loadPartLoss();   // chips filter server-side — see the data() note
    },
    removePlTerm(i) { this.plTerms.splice(i, 1); this.loadPartLoss(); },
    clearPlSearch() {
      var had = (this.plTerms || []).length > 0;
      this.plTerms = []; this.plSearch = ""; this.plSug = []; this.plSugOpen = false;
      clearTimeout(this._plSugTimer);
      this._plSugSeq = (this._plSugSeq || 0) + 1;
      if (had) this.loadPartLoss();
    },
    // Table → biggest losses → by month → by customer → table. An ARRAY, not a lookup-with-||
    // fallback — the table state is "", which is falsy (the pc cycle's documented lesson).
    cyclePlChart() {
      var order = ["", "hbar", "vbar", "pie"];
      var i = order.indexOf(this.plChart);
      this.plChart = order[(i < 0 ? 0 : i + 1) % order.length];
    },
    togglePlFull() {
      this.plFull = !this.plFull;
      if (this.plFull) this.pcFull = false;   // only one card may hold Full view (see the pcFull watcher)
    },
    // ONE generator for the on-screen chart, the print doc and the Excel PNGs (the cost card's rule).
    // Class names are emitted in BOTH modes; the inline <style> is print-only so dark mode still works
    // on screen via the external .ci-pcc-* rules.
    _plChartSvg(kind, g, wpx, hpx, forPrint) {
      var self = this;
      g = g || { bars: this.plChartBars, months: this.plMonthBars, ticks: this.plMonthTicks, pie: this.plPie };
      var esc = function (t) { return String(t == null ? "" : t).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); };
      var money = function (n) { return esc(self.plMoney(n)); };
      var vbH = kind === "hbar" ? 300 : 250;
      var dim = wpx ? ('width="' + wpx + '" height="' + hpx + '"') : 'width="100%" height="100%"';
      var style = forPrint
        ? '<style>.ci-pcc-bar{fill:#c0392b}'
          + '.ci-pcc-lbl{font-size:9px;fill:#374151;font-family:ui-monospace,Consolas,monospace}'
          + '.ci-pcc-val{font-size:8px;fill:#6b7280}.ci-pcc-tick{font-size:8px;fill:#6b7280;text-anchor:end}'
          + '.ci-pcc-grid{stroke:#eceef1;stroke-width:1}.ci-pcc-axis{stroke:#d2d6dc;stroke-width:1}'
          + '.ci-pcc-vlbl{font-size:8px;fill:#374151;text-anchor:end;font-family:ui-monospace,Consolas,monospace}'
          + '.ci-pcc-leg{font-size:10px;fill:#374151}.ci-pcc-slice{stroke:#fff;stroke-width:1.5}</style>'
        : "";
      var open = '<svg viewBox="0 0 400 ' + vbH + '" ' + dim + ' xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMinYMin meet">' + style;
      if (kind === "hbar") {
        // Each bar sits in its OWN <g> so its <title> tooltips that bar — an SVG <title> describes its
        // PARENT, and as siblings in the shared group only the first would count (every bar would hover
        // as bar #1). The pc generator this was cloned from now does the same.
        return open + '<g transform="translate(0,4)">'
          + g.bars.map(function (b) {
            return '<g><title>' + esc(b.label) + " — total loss " + money(b.loss) + " over " + b.lines + " line(s)" + '</title>'
              + '<text class="ci-pcc-lbl" x="0" y="' + b.hty + '">' + esc(b.label) + '</text>'
              + '<rect class="ci-pcc-bar" x="92" y="' + b.hy + '" width="' + b.hw.toFixed(1) + '" height="10" rx="2"/>'
              + '<text class="ci-pcc-val" x="' + (96 + b.hw).toFixed(1) + '" y="' + b.hty + '">' + money(b.loss) + '</text></g>';   // lines count lives in the <title> — appended text clipped past the 400-unit viewBox on a full-width bar
          }).join("") + "</g></svg>";
      }
      if (kind === "vbar") {
        return open + '<g transform="translate(0,14)">'
          + g.ticks.map(function (t) {
            return '<line class="' + (t.i ? "ci-pcc-grid" : "ci-pcc-axis") + '" x1="48" y1="' + t.y + '" x2="394" y2="' + t.y + '"/>'
              + '<text class="ci-pcc-tick" x="44" y="' + (t.y + 3) + '">' + esc(t.label) + '</text>';
          }).join("")
          + g.months.bars.map(function (b) {
            return '<g><title>' + esc(b.label) + " — loss " + money(b.loss) + " over " + b.lines + " line(s)" + '</title>'
              + '<rect class="ci-pcc-bar" x="' + b.vx.toFixed(1) + '" y="' + b.vy.toFixed(1) + '" width="' + b.vw.toFixed(1) + '" height="' + b.vh.toFixed(1) + '" rx="1"/>'
              + '<text class="ci-pcc-vlbl" x="' + b.vcx.toFixed(1) + '" y="176" transform="rotate(-55 ' + b.vcx.toFixed(1) + ' 176)">' + esc(b.label) + '</text></g>';
          }).join("") + "</g></svg>";
      }
      return open + g.pie.map(function (p) {
        return '<path d="' + p.d + '" fill="' + p.color + '" class="ci-pcc-slice"><title>' + esc(p.label) + " — " + money(-p.v) + " (" + p.pct.toFixed(1) + '%)</title></path>';
      }).join("") + g.pie.map(function (p) {
        return '<rect x="228" y="' + (p.ly - 9) + '" width="11" height="11" rx="2" fill="' + p.color + '"/>'
          + '<text class="ci-pcc-leg" x="246" y="' + p.ly + '">' + esc(p.label) + " &#8212; " + p.pct.toFixed(1) + '%</text>';
      }).join("") + "</svg>";
    },
    // "✨ Generate Insights" — the browser sends only the window + chips; the server re-queries the
    // losses itself, so the narrative always describes the real data.
    generatePartLossInsights() {
      if (!this.canSeeLoss || this.plAiBusy) return;
      var self = this;
      this.plAiBusy = true;
      this.plAiError = "";
      this.plAi = "";
      this.plAiBasis = "";
      var f = this.filters || {};
      httpClient.post("/report/accounting/part-loss-insights", {
        from: f.from || null, to: f.to || null,
        category: this.plCat, search: (this.plTerms || []).join(",")
      })
        .then(function (res) {
          var d = res.data || {};
          if (!d.ok) { self.plAiError = d.message || "Couldn't generate loss insights."; return; }
          self.plAi = d.insights || "";
          self.plAiTitle = d.title || "";
          var b = d.basis || {};
          if (b.lines) {
            self.plAiBasis = "Based on " + self.fmtNum(b.lines) + " below-cost line(s) in the window — the "
              + self.fmtNum(b.parts || 0) + " biggest-loss parts, " + self.fmtNum(b.customers || 0)
              + " customer(s) and " + self.fmtNum(b.months || 0) + " month(s).";
          }
        })
        .catch(function (err) {
          var st = (err && err.response) ? err.response.status : 0;
          self.plAiError = st === 403
            ? "Your account isn't authorized for loss reporting."
            : "Something went wrong generating loss insights. Please try again.";
        })
        .finally(function () { self.plAiBusy = false; });
    },
    // Screenshot of the CARD (title bar included) in LIGHT mode — same onclone trick as the cost card.
    _capturePartLossShot() {
      if (typeof document === "undefined" || typeof html2canvas !== "function") return Promise.resolve("");
      var body = document.querySelector(".ci-ploss");
      if (!body) return Promise.resolve("");
      var el = (body.closest && body.closest(".vue-grid-item")) || body;
      return html2canvas(el, {
        backgroundColor: "#ffffff", useCORS: true, logging: false,
        onclone: function (doc) { try { if (doc && doc.documentElement) doc.documentElement.classList.remove("dark-mode", "mid-mode"); } catch (e) { /* noop */ } },
        scale: Math.min(2, (window.devicePixelRatio || 1) * 1.2),
        windowWidth: el.scrollWidth, width: el.scrollWidth,
        height: el.scrollHeight, windowHeight: el.scrollHeight, scrollX: 0, scrollY: 0
      }).then(function (c) { try { return c.toDataURL("image/png"); } catch (e) { return ""; } })
        .catch(function () { return ""; });
    },
    partLossPresentation() {
      if (!this.canSeeLoss || this.plPrintBusy) return;
      var self = this;
      // Open the tab SYNCHRONOUSLY inside the click, or the pop-up blocker eats it.
      var w = null;
      try { w = window.open("", "_blank"); } catch (e) { w = null; }
      if (!w) { window.alert("Please allow pop-ups for this site to open the presentation."); return; }
      try {
        w.document.open();
        w.document.write("<!DOCTYPE html><html><head><meta charset='utf-8'><title>Parts Sold at a Loss</title></head>"
          + "<body style=\"font-family:Arial,Helvetica,sans-serif;color:#666;padding:48px;font-size:15px;\">Preparing your presentation&hellip;"
          + "<br><span style=\"font-size:12px;color:#999;\">Capturing the card &mdash; this can take a few seconds.</span></body></html>");
        w.document.close();
      } catch (e) { /* noop */ }
      this.plPrintBusy = true;
      this._capturePartLossShot()
        .then(function (shot) {
          var html = self._buildPartLossPresentation(shot);
          try { w.document.open(); w.document.write(html); w.document.close(); w.focus(); } catch (e) { return; }
          setTimeout(function () { try { w.print(); } catch (e) { /* noop */ } }, 500);
        })
        .catch(function () {
          try {
            w.document.open();
            w.document.write("<body style=\"font-family:Arial;padding:40px;color:#c0392b;\">Couldn't build the presentation. Please close this tab and try again.</body>");
            w.document.close();
          } catch (e2) { /* noop */ }
        })
        .finally(function () { self.plPrintBusy = false; });
    },
    _buildPartLossPresentation(shotUrl) {
      var self = this;
      var esc = function (s) { return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); };
      var rows = this.plView || [];
      var dateRange = this.fmtShort(this.filters.from) + " &ndash; " + this.fmtShort(this.filters.to);
      var tab = (this.plTabs.find(function (t) { return t.key === self.plCat; }) || {}).label || this.plCat;
      var sub = dateRange + " &middot; " + esc(tab) + " lines"
        + (this.plSearchLabel ? " &middot; Card search: " + esc(this.plSearchLabel) : "");
      var H = function (txt) { return '<div class="h2">' + txt + "</div>"; };
      var kpi = function (label, val) {
        return '<div class="kpi"><div class="kpi-l">' + esc(label) + '</div><div class="kpi-v">' + esc(val) + "</div></div>";
      };
      // PAGE 1 — the card as the user sees it, plus the headline counts.
      var p1 = '<section class="pg">'
        + '<div class="ttl">Parts Sold at a Loss</div>'
        + '<div class="sub">' + sub + "</div>"
        + '<div class="kpis">'
        + kpi("Lines in view", this.fmtNum(rows.length))
        + kpi("Parts", this.fmtNum(this.plPartsInView))
        + kpi("Customers", this.fmtNum(this.plCustomersInView))
        + (this.plCat === "BelowCost" ? kpi("Loss in view", this.plMoney(this.plLossInView)) : "")
        + kpi("Newest shipment", this.plNewestShip || "—")
        + "</div>"
        + (shotUrl ? H("The card") + '<img class="shot" src="' + shotUrl + '" alt="Parts Sold at a Loss card">' : "")
        + "</section>";
      // PAGE 2 — ALL THREE charts, regardless of which one is on screen.
      var mb = this.plMonthBars;
      var p2 = '<section class="pg">' + H("Charts")
        + (this.plChartBars.length
            ? '<div class="cap">The ' + esc(this.fmtNum(this.plChartBars.length)) + " parts carrying the biggest total loss in view.</div>"
              + '<div class="chart">' + this._plChartSvg("hbar", null, 900, 560, true) + "</div>"
            : '<div class="cap">No loss dollars in this view, so there is nothing to chart by part.</div>')
        + (mb.bars.length
            ? '<div class="cap">Loss by month' + (mb.clipped ? " (clipped to the newest 24 months)" : "") + ".</div>"
              + '<div class="chart">' + this._plChartSvg("vbar", null, 900, 560, true) + "</div>"
            : "")
        + (this.plPie.length
            ? '<div class="cap">Share of the loss in view by customer &mdash; the top 7 plus Other.</div>'
              + '<div class="chart">' + this._plChartSvg("pie", null, 900, 560, true) + "</div>"
            : "")
        + "</section>";
      // PAGE 3 — the AI briefing, only if one has been generated.
      var p3 = "";
      if (this.plAi) {
        p3 = '<section class="pg">' + H(this.plAiTitle || "Loss insights")
          + '<ul class="ins">' + this.plAiLines.map(function (l) { return "<li>" + esc(l) + "</li>"; }).join("") + "</ul>"
          + (this.plAiBasis ? '<div class="basis">' + esc(this.plAiBasis) + "</div>" : "")
          + '<div class="basis">AI-generated from the current window. Figures above are from the report; the wording is a summary and should be read as such.</div>'
          + "</section>";
      }
      // PAGE 4+ — the lines. Capped, and the cap is STATED rather than silently applied.
      var CAP = 300;
      var shown = rows.slice(0, CAP);
      var body = shown.map(function (r) {
        return "<tr><td>" + esc(String(r.transactionDate || "").slice(0, 10)) + "</td><td class='pn'>" + esc(r.partNumber) + "</td>"
          + "<td>" + esc(r.partDescription || "") + "</td><td>" + esc(r.customerId) + "</td>"
          + "<td>" + esc(r.custOrderId) + (r.orderLineNo ? " &middot; " + esc(r.orderLineNo) : "") + "</td>"
          + "<td class='n'>" + esc(self.fmtNum(r.qty)) + "</td>"
          + "<td class='n'>" + esc(self.plMoney(r.netUnitPrice)) + "</td>"
          + "<td class='n'>" + esc(self.plMoney(r.actUnitCost)) + "</td>"
          + "<td class='n'>" + (r.lineLoss < 0 ? "<span class='neg'>" : "<span>") + esc(self.plMoney(r.lineLoss)) + "</span></td></tr>";
      }).join("");
      var p4 = '<section class="pg">' + H("Loss line detail")
        + '<div class="cap">' + esc(this.fmtNum(shown.length)) + " of " + esc(this.fmtNum(rows.length)) + " lines in the current view"
        + (rows.length > CAP ? " &mdash; the remaining " + esc(this.fmtNum(rows.length - CAP)) + " are in the Excel export, not printed here." : "") + "</div>"
        + '<table class="dt"><thead><tr><th>Date</th><th>Part</th><th>Description</th><th>Customer</th><th>Order</th>'
        + '<th class="n">Qty</th><th class="n">Net unit price</th><th class="n">Actual unit cost</th><th class="n">Loss</th></tr></thead>'
        + "<tbody>" + body + "</tbody></table></section>";
      var css = "@page{size:landscape;margin:12mm}"
        + "*{box-sizing:border-box}body{margin:0;font-family:Arial,Helvetica,sans-serif;color:#222}"
        + ".pg{page-break-after:always;padding:0 0 10px}.pg:last-child{page-break-after:auto}"
        + ".ttl{font-size:24px;font-weight:800;color:#9e2436}"
        + ".sub{font-size:12px;color:#666;margin:2px 0 14px}"
        + ".h2{font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;color:#9e2436;margin:0 0 6px}"
        + ".kpis{display:flex;gap:10px;margin:0 0 14px}"
        + ".kpi{flex:1;border:1px solid #e6e6ea;border-radius:8px;padding:10px 12px}"
        + ".kpi-l{font-size:10px;text-transform:uppercase;letter-spacing:.04em;color:#888}"
        + ".kpi-v{font-size:20px;font-weight:800;color:#9e2436;margin-top:2px}"
        + ".shot{width:100%;border:1px solid #e6e6ea;border-radius:6px}"
        + ".chart{border:1px solid #e6e6ea;border-radius:6px;padding:8px;margin:0 0 12px;page-break-inside:avoid}"
        + ".cap{font-size:11px;color:#666;margin:0 0 6px}"
        + ".ins{margin:0 0 10px;padding-left:18px}.ins li{font-size:12.5px;line-height:1.5;margin-bottom:4px}"
        + ".basis{font-size:10px;color:#777;border-top:1px dashed #ddd;padding-top:5px;margin-top:6px}"
        + ".dt{width:100%;border-collapse:collapse;font-size:9.5px;table-layout:fixed}"
        + ".dt th{background:#f4f5f7;text-align:left;padding:4px 5px;border-bottom:1px solid #ddd;font-size:9px;text-transform:uppercase;letter-spacing:.03em;color:#555}"
        + ".dt td{padding:3px 5px;border-bottom:1px solid #f0f1f3;vertical-align:top;overflow-wrap:anywhere}"
        + ".dt tr{page-break-inside:avoid}"
        + ".dt .n{text-align:right;white-space:nowrap}"
        + ".dt .pn{font-family:ui-monospace,Consolas,monospace;white-space:nowrap}"
        + ".neg{color:#c0392b;font-weight:700}"
        + ".foot{font-size:9px;color:#999;margin-top:10px}"
        + ".conf{font-size:10px;color:#9e2436;font-weight:700;margin-top:4px}";
      return "<!DOCTYPE html><html><head><meta charset='utf-8'><title>Parts Sold at a Loss</title><style>" + css + "</style></head><body>"
        + p1 + p2 + p3 + p4
        + '<div class="foot">Generated ' + esc(new Date().toLocaleString()) + " from the JC Steele Service Portal &middot; Accounting &rarr; Parts Sold at a Loss."
        + " Each line compares the order line's net price to the ACTUAL cost booked on the shipment transaction on its date &mdash; never today's cost projected backwards.</div>"
        + '<div class="conf">Confidential &mdash; loss figures are restricted to Master, Accounting Insights and All Insights roles.</div>'
        + "</body></html>";
    },
    // ---- Excel (scope modal + workbook with chart PNGs, mirroring the cost card's export) ----
    openPartLossExcel() {
      if (!this.canSeeLoss) return;
      this.plExcelModal = { open: true, scope: this.plSearchLabel ? "search" : "page", busy: false, error: "" };
    },
    confirmPartLossExcel() {
      var self = this;
      var scope = this.plExcelModal.scope;
      if (scope !== "all") { this._writePartLossWorkbook(scope === "search" ? this.plView : this.plApplySort(this.plRows), scope); return; }
      // "All" re-queries with a big top rather than paging the card — one consistent snapshot; the
      // server bounds it at 25,000.
      this.plExcelModal.busy = true;
      this.plExcelModal.error = "";
      var f = this.filters || {};
      var params = { category: this.plCat, top: 25000 };
      if (f.from) params.from = f.from;
      if (f.to) params.to = f.to;
      if ((this.plTerms || []).length) params.search = this.plTerms.join(",");
      httpClient.get("/report/accounting/part-loss", { params: params })
        .then(function (res) {
          var d = res.data || {};
          if (d.available === false) { self.plExcelModal.error = "Loss reporting is not available on this database."; return; }
          return self._writePartLossWorkbook(self.plApplySort(d.rows || []), "all", !!d.truncated);
        })
        .catch(function (err) {
          var st = (err && err.response) ? err.response.status : 0;
          self.plExcelModal.error = st === 403
            ? "Your account is not authorized for loss reporting."
            : "Couldn't fetch all lines — try This page, or narrow the date range.";
        })
        .finally(function () { self.plExcelModal.busy = false; });
    },
    _writePartLossWorkbook(rows, scope, clipped) {
      var self = this;
      rows = rows || [];
      this.plExcelModal.busy = true;
      this.plExcelModal.error = "";
      // Geometry from the EXPORTED rows, not the on-screen view — an "all lines" workbook must chart
      // all lines. Same pure functions the card uses, so the two can never drift.
      var bars = plLossBarsFrom(rows);
      var months = plMonthBarsFrom(rows);
      var g = { bars: bars, months: months, ticks: plTicksFrom(months.max), pie: plPieFrom(rows) };
      var svgs = [
        { name: "Biggest losses by part", svg: bars.length ? this._plChartSvg("hbar", g, 900, 560, true) : "" },
        { name: "Loss by month", svg: months.bars.length ? this._plChartSvg("vbar", g, 900, 560, true) : "" },
        { name: "Loss by customer", svg: g.pie.length ? this._plChartSvg("pie", g, 900, 560, true) : "" }
      ];
      // RETURNED so a caller that pre-fetched rows (the "all" scope) can chain on it — otherwise its
      // .finally cleared the modal's busy flag while this was still importing ExcelJS and rasterizing
      // PNGs, re-enabling Export mid-build (double download).
      return Promise.all([
        import(/* webpackChunkName: "exceljs" */ "exceljs"),
        Promise.all(svgs.map(function (s) { return s.svg ? self._svgToPng(s.svg, 900, 560) : Promise.resolve(""); }))
      ]).then(function (loaded) {
        var ExcelJS = loaded[0].default || loaded[0];
        var pngs = loaded[1];
        var wb = new ExcelJS.Workbook();
        var sheet = function (name, headers, rowsA, widths) {
          var ws = wb.addWorksheet(name);
          ws.addRow(headers);
          ws.getRow(1).font = { bold: true };
          rowsA.forEach(function (r) { ws.addRow(r); });
          (widths || []).forEach(function (w, i) { if (w) ws.getColumn(i + 1).width = w; });
          return ws;
        };
        var MONEY = '"$"#,##0.00;[Red]-"$"#,##0.00';
        // 1. The lines themselves, in the user's sort.
        var ws1 = sheet("Loss lines",
          ["Date", "Part", "Description", "Customer", "Order", "Line", "Qty", "Net unit price", "Actual unit cost", "Line loss", "Category"],
          rows.map(function (r) {
            return [String(r.transactionDate || "").slice(0, 10), r.partNumber, r.partDescription, r.customerId,
                    r.custOrderId, r.orderLineNo, Number(r.qty) || 0, Number(r.netUnitPrice) || 0,
                    Number(r.actUnitCost) || 0, Number(r.lineLoss) || 0, r.category];
          }),
          [12, 20, 40, 12, 12, 6, 10, 15, 16, 13, 11]);
        [8, 9, 10].forEach(function (c) { ws1.getColumn(c).numFmt = MONEY; });
        ws1.getColumn(7).numFmt = "#,##0.##";
        // 2. The charts as pictures — the same SVGs the card draws, rasterized.
        var cs = wb.addWorksheet("Charts");
        cs.getColumn(1).width = 26;
        var row = 0;
        svgs.forEach(function (s, i) {
          if (!pngs[i]) return;
          cs.getCell(row + 1, 1).value = s.name;
          cs.getRow(row + 1).font = { bold: true };
          var id = wb.addImage({ base64: pngs[i], extension: "png" });
          cs.addImage(id, { tl: { col: 0, row: row + 1 }, ext: { width: 720, height: 448 } });
          row += 25;
        });
        // 3. The same numbers as data — to re-chart and to check the pictures against.
        var partsAgg = plPartsAgg(rows).slice(0, 100);
        var ws2 = sheet("Chart - Loss by part", ["Part", "Description", "Lines", "Qty", "Total loss"],
          partsAgg.map(function (p) { return [p.label, p.desc, p.lines, p.qty, p.loss]; }), [20, 40, 8, 10, 14]);
        ws2.getColumn(5).numFmt = MONEY;
        var monthsAll = plMonthAgg(rows).months;
        var ws3 = sheet("Chart - Loss by month", ["Month", "Lines", "Loss"],
          monthsAll.map(function (m) { return [m.ym, m.lines, m.loss]; }), [10, 8, 14]);
        ws3.getColumn(3).numFmt = MONEY;
        // 4. Provenance — a workbook of loss figures that escapes into an inbox should say what it is.
        var f = self.filters || {};
        var byCat = {};
        (self.plCats || []).forEach(function (c) { byCat[c.category] = c; });
        sheet("About", ["Field", "Value"], [
          ["Report", "Accounting — Parts Sold at a Loss"],
          ["Generated", new Date().toISOString().slice(0, 19).replace("T", " ")],
          ["Tab", self.plCat],
          ["Scope", scope === "all" ? "All lines in the window" : (scope === "search" ? ("Search: " + self.plSearchLabel) : "This page (most recent lines loaded)")],
          ["Rows in this workbook", rows.length],
          ["Date window", (f.from || "earliest") + " to " + (f.to || "latest")],
          ["Search chips", (self.plTerms || []).join(", ") || "(none)"],
          ["Window counts", "Below cost " + ((byCat.BelowCost || {}).rows || 0) + " · No charge " + ((byCat.NoCharge || {}).rows || 0) + " · Credits " + ((byCat.Credit || {}).rows || 0)],
          ["Export ceiling", clipped ? "⚠ REACHED — the window holds more lines than the 25,000-row export ceiling; this workbook is the NEWEST 25,000. Narrow the dates for the rest." : "Not reached — every line in scope is present."],
          ["Method", "Each line compares the order line's net price to the ACTUAL cost booked on the shipment transaction on its date — never today's cost projected backwards."],
          ["Categories", "Below cost = invoiced under actual cost (the real losses). No charge = $0 lines (warranty/service/samples). Credits = negative-price credit/return lines. Only Below cost is summed as loss."],
          ["Charts", "The Charts sheet holds pictures of the card's charts. The two 'Chart - …' sheets hold the same numbers, so you can re-chart or verify them."],
          ["Confidential", "Loss figures are restricted to Master, Accounting Insights and All Insights roles."]
        ], [24, 110]);
        return wb.xlsx.writeBuffer().then(function (buf) {
          var blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
          var url = URL.createObjectURL(blob);
          var link = document.createElement("a");
          link.href = url;
          link.download = "parts-sold-at-a-loss-" + new Date().toISOString().slice(0, 10) + ".xlsx";
          document.body.appendChild(link);
          link.click();
          setTimeout(function () { try { URL.revokeObjectURL(url); link.parentNode.removeChild(link); } catch (e) { /* noop */ } }, 1500);
          self.plExcelModal.open = false;
        });
      }).catch(function (err) {
        if (typeof console !== "undefined") console.error("[Accounting] part-loss Excel export failed:", err);
        self.plExcelModal.error = "Couldn't build the workbook. Please try again.";
      }).finally(function () { self.plExcelModal.busy = false; });
    },
    // Costing for Parts — deferred load. Never called unless canSeeCost (the template gates the button and
    // the auto-load), but the SERVER is the boundary: a 403 here is the expected, correct answer for an
    // unauthorized caller, and is reported rather than swallowed so a genuine grant-delay is legible.
    loadPartCosts() {
      if (!this.canSeeCost) return;
      const seq = (this._partCostSeq = (this._partCostSeq || 0) + 1);
      this.partCostBusy = true;
      this.partCostDenied = false;
      this.partCostDetail = "";
      this.partCostQueryError = false;
      httpClient.get("/report/customer-insights/part-costs", { params: this.buildParams(false) })
        .then((res) => {
          if (seq !== this._partCostSeq) return;
          const d = res.data || {};
          this.partCostAvailable = d.available !== false;
          this.partCostDetail = d.detail || "";
          this.partCostQueryError = !!d.queryError;
          this.partCostRows = d.rows || [];
          this.partCostTotals = d.totals || null;
        })
        .catch((err) => {
          if (seq !== this._partCostSeq) return;
          const st = (err && err.response) ? err.response.status : 0;
          this.partCostRows = [];
          this.partCostTotals = null;
          // 403 = the endpoint exists and refused us. The most common innocent cause is a stale token:
          // role claims are baked into the JWT for 24h, so someone just added to All Insights keeps an old
          // token until they sign out and back in. Say that, instead of showing an empty table.
          if (st === 403) this.partCostDenied = true;
          else if (typeof console !== "undefined") console.error("[Sales Insights] /report/customer-insights/part-costs failed:", (err && err.response) ? err.response : err);
        })
        .finally(() => { if (seq === this._partCostSeq) this.partCostBusy = false; });
    },
    // CURRENT margin: current unit price minus current standard unit cost. Legitimate ONLY because both
    // sides are today's figures — this is NOT the margin the part actually sold at, and nothing here may
    // be multiplied by historical quantity (that is precisely how the -$95M figure was produced).
    // Returns null unless BOTH sides are present: a missing price is not a zero margin, and a missing cost
    // is not a 100% one.
    pcMargin(r) {
      var c = Number(r.unitCostTotal) || 0, p = Number(r.unitPrice) || 0;
      if (!(c > 0) || !(p > 0)) return null;
      // Percent of PRICE (gross margin), the sales convention — not markup on cost, which would read
      // higher for the same money and invite an apples-to-oranges comparison.
      return { amt: p - c, pct: (p - c) / p * 100 };
    },
    // Most recent cost change across the four areas, for the "last changed" column.
    partCostLastChange(r) {
      var best = null;
      [["Material", r.latestMaterialCostChangeDate, r.latestMaterialCostUser, r.latestMaterialCostOld, r.latestMaterialCostNew],
       ["Labor", r.latestLaborCostChangeDate, r.latestLaborCostUser, r.latestLaborCostOld, r.latestLaborCostNew],
       ["Burden", r.latestBurdenCostChangeDate, r.latestBurdenCostUser, r.latestBurdenCostOld, r.latestBurdenCostNew],
       ["Service", r.latestServiceCostChangeDate, r.latestServiceCostUser, r.latestServiceCostOld, r.latestServiceCostNew]]
        .forEach(function (t) {
          if (!t[1]) return;
          if (!best || String(t[1]) > String(best.date)) best = { area: t[0], date: t[1], user: t[2], oldV: t[3], newV: t[4] };
        });
      return best;
    },
    // Per-customer NOTATION + SPECS (📝/📋 on the Customer ID(s) card), loaded LAZILY and merged into byCustomer —
    // peeled off the eager byCustomer facet because the NVARCHAR(MAX) MAX() over notation/specs can't use the
    // columnstore's batch mode, which dragged the whole main batch into a slow row-scan → wide-range timeout.
    // Own stale-guard (_notesSeq). Best-effort: on error/timeout the icons just don't appear. Merges by CustomerId
    // via $set so the reactive byCustomer rows pick up the 📝/📋. Backward-compatible with an OLD backend (its
    // byCustomer already carries the notes and this route 404s → the merge finds nothing new and is a no-op).
    loadCustomerNotes() {
      const seq = (this._notesSeq = (this._notesSeq || 0) + 1);
      httpClient.get("/report/customer-insights/customer-notes", { params: this.buildParams(false) })
        .then((res) => {
          if (seq !== this._notesSeq) return;   // superseded by a newer load
          const notes = (res.data && res.data.notes) || [];
          if (!notes.length || !this.byCustomer || !this.byCustomer.length) return;
          const byId = {};
          notes.forEach((n) => { if (n && n.customerId) byId[String(n.customerId)] = n; });
          this.byCustomer.forEach((c) => {
            const n = byId[String(c.customerId)];
            if (n) { this.$set(c, "customerNotation", n.customerNotation || null); this.$set(c, "customerSpecs", n.customerSpecs || null); }
          });
        })
        .catch(() => { /* best-effort — the 📝/📋 icons simply don't appear (esp. on the very widest ranges) */ });
    },

    // ---- Refresh timer (the loading banner's elapsed count-up + estimate + progress bar) ---------------
    // A real ETA is impossible (a query swings from ~1s for a narrow range to 60s+ for "all years"), so we count
    // UP the TRUE elapsed (always honest) and show it against an ESTIMATE derived from the selected range width +
    // whether filters narrow it, self-calibrated: each successful load's actual time is blended into a per-bucket
    // EMA (localStorage), so the estimate sharpens with use. Past the estimate we drop the "N left" and say
    // "finishing up…" (elapsed keeps climbing) + creep the bar 95→99. Uses browser Date.now()/setInterval.
    _readLoadEma() {
      try { var raw = localStorage.getItem("jcsCiLoadEma"); var o = raw ? JSON.parse(raw) : null; return (o && typeof o === "object") ? o : {}; }
      catch (e) { return {}; }
    },
    _hasNarrowingFilter() {
      var f = this.filters || {};
      var arrs = [f.customer, f.part, f.salesRep, f.territory, f.state, f.country, f.status,
        f.machineCodeCategory, f.machineCodePrefix, f.machineCodeDescription, f.partDescription];
      for (var i = 0; i < arrs.length; i++) { if (arrs[i] && arrs[i].length) return true; }
      if (f.invoiced && f.invoiced !== "all") return true;
      if (f.orderNo && ("" + f.orderNo).trim()) return true;
      return false;
    },
    _loadBucketKey() {
      var f = this.filters || {};
      var from = f.from ? new Date(f.from) : null;
      var to = f.to ? new Date(f.to) : null;
      var days = (from && to && !isNaN(from) && !isNaN(to)) ? Math.max(0, (to - from) / 86400000) : 365;
      var w = days <= 31 ? "m" : days <= 93 ? "q" : days <= 186 ? "h" : days <= 372 ? "y" : days <= 1860 ? "5y" : "wide";
      return w + ":" + (this._hasNarrowingFilter() ? "f" : "a");
    },
    _estimateLoadMs() {
      var key = this._loadBucketKey();
      // Prefer the POOLED server average (shared across all users + persistent) when we have it.
      var srv = (this._serverTimings || {})[key];
      var srvMs = (srv && srv.ms) ? srv.ms : 0;
      if (srvMs > 0) return Math.min(178000, Math.max(1200, Math.round(srvMs * 1.1)));
      // Else fall back to THIS browser's own learned time, padded ~12% so the countdown finishes early.
      var ema = this._readLoadEma();
      if (ema[key] != null && ema[key] > 0) {
        return Math.min(178000, Math.max(1200, Math.round(ema[key] * 1.12)));
      }
      // First-time heuristic per width bucket (ms); a narrowing filter is much faster than the full table.
      var w = key.split(":")[0];
      var base = ({ m: 2200, q: 3000, h: 4000, y: 5500, "5y": 12000, wide: 26000 })[w] || 5000;
      if (key.indexOf(":f") >= 0) base = Math.max(1500, Math.round(base * 0.45));
      return base;
    },
    _startLoadTimer() {
      this._stopLoadTimer();                       // clear any prior tick (rapid re-filtering)
      this.loadEstMs = this._estimateLoadMs();
      this._loadStartTs = Date.now();
      this.loadElapsedMs = 0;
      this.loadPastEstimate = false;
      this.loadProgressPct = 4;
      var self = this;
      this._loadTimer = setInterval(function () { self._tickLoadTimer(); }, 250);
    },
    _tickLoadTimer() {
      var est = this.loadEstMs || 4000;
      var elapsed = Date.now() - (this._loadStartTs || Date.now());
      this.loadElapsedMs = elapsed;                // count UP — the honest number
      if (elapsed < est) {
        this.loadPastEstimate = false;
        this.loadProgressPct = Math.min(95, (elapsed / est) * 100);
      } else {
        // Past the estimate — drop "N left" (would go negative) and creep the bar 95→99 asymptotically.
        this.loadPastEstimate = true;
        var over = elapsed - est;
        this.loadProgressPct = Math.min(99, 95 + 4 * (1 - Math.exp(-over / Math.max(est, 3000))));
      }
    },
    _stopLoadTimer() {
      if (this._loadTimer) { clearInterval(this._loadTimer); this._loadTimer = null; }
      this.loadProgressPct = 100;                  // brief "done" (banner hides via v-if="loading" anyway)
      this.loadPastEstimate = false;
    },
    // Format a duration for the banner: "8s", "45s", "1m 05s", "3m". Used for elapsed / remaining / estimate.
    _fmtDur(ms) {
      var s = Math.max(0, Math.round((ms || 0) / 1000));
      if (s < 60) return s + "s";
      var m = Math.floor(s / 60), r = s % 60;
      return r ? (m + "m " + (r < 10 ? "0" + r : r) + "s") : (m + "m");
    },
    _recordLoadTiming() {
      var elapsed = Date.now() - (this._loadStartTs || Date.now());
      if (!(elapsed > 0) || elapsed > 178000) return;   // ignore zero/absurd (near-timeout) samples
      var ms = Math.round(elapsed);
      var key = this._loadBucketKey();
      // This browser's own learned time (immediate + offline fallback).
      var ema = this._readLoadEma();
      ema[key] = (ema[key] == null) ? ms : Math.round(ema[key] * 0.6 + ms * 0.4);
      try { localStorage.setItem("jcsCiLoadEma", JSON.stringify(ema)); } catch (e) { /* storage full/blocked */ }
      // Pool it server-side (shared across all users + persistent). Fire-and-forget; also fold it into the
      // in-memory map so THIS session's next estimate benefits immediately without re-fetching.
      if (!this._serverTimings) this._serverTimings = {};
      var cur = this._serverTimings[key]; var curMs = (cur && cur.ms) ? cur.ms : 0;
      this._serverTimings[key] = { ms: curMs > 0 ? Math.round(curMs * 0.7 + ms * 0.3) : ms, samples: (((cur && cur.samples) || 0) + 1) };
      httpClient.post("/report/timing", { bucket: key, ms: ms }).catch(function () { /* telemetry table not deployed yet — local estimate still works */ });
    },
    _loadServerTimings() {
      var self = this;
      httpClient.get("/report/timings")
        .then(function (res) { self._serverTimings = (res && res.data && typeof res.data === "object") ? res.data : {}; })
        .catch(function () { if (!self._serverTimings) self._serverTimings = {}; });
    },
    loadFilterOptions(attempt) {
      attempt = attempt || 0;
      httpClient.get("/report/customer-insights/filters")
        .then((res) => {
          if (res.data && !res.data.notReady) {
            // Reassign to a NEW object so the dropdowns re-render. Status depends on opts.statuses; Territory
            // and Sales Rep now derive from the loaded aggregates and union opts in as a superset.
            this.opts = Object.assign({}, this.opts, res.data);
            // Default the Status slicer to everything EXCEPT Cancelled/Void (that box stays unchecked). Applied
            // ONCE, and only on a fresh view where the user hasn't already picked statuses (so a favorite / restored
            // filter still wins). `_statusDefaultApplied` is a plain instance flag (NOT in data() — Vue 2 won't
            // proxy a leading-underscore data key onto `this`).
            if (!this._statusDefaultApplied && Array.isArray(this.opts.statuses) && this.opts.statuses.length && !this.filters.status.length) {
              this._statusDefaultApplied = true;
              var keep = this.opts.statuses.filter(function (s) { return String(s).toUpperCase() !== "CANCELLED/VOID"; });
              if (keep.length && keep.length < this.opts.statuses.length) this.filters.status = keep;
            }
          } else if (attempt < 4) {
            // notReady (sync table not populated yet) — back off and retry so the options fill in later.
            setTimeout(() => this.loadFilterOptions(attempt + 1), 2000);
          }
        })
        // A slow/failed call used to leave the dropdowns permanently empty (no retry). Retry a few times so
        // the choices appear reliably even when the bundled DISTINCT query is slow the first time.
        .catch(() => { if (attempt < 4) setTimeout(() => this.loadFilterOptions(attempt + 1), 2000); });
    },
    // Admin-managed State → Territory map (Account Setup → Sales Territories).
    // Loaded once; the map geography doesn't change with the report filters.
    loadSalesTerritories() {
      httpClient.get("/report/sales-territory")
        .then((res) => { this.territoryAssignments = Array.isArray(res.data) ? res.data : []; })
        .catch(() => { this.territoryAssignments = []; });
    },
    // Click-to-filter: set this filter to `value`, or clear it if it's already
    // the active value (toggle). Reload everything so the cards, map, table,
    // and story all move together.
    toggleFilter(key, value) {
      const v = value == null ? "" : String(value);
      if (!v) return;
      if (Array.isArray(this.filters[key])) {
        const arr = this.filters[key];
        const i = arr.indexOf(v);
        if (i >= 0) arr.splice(i, 1); else arr.push(v);   // multi-select: add if new, remove if already chosen
      } else {
        this.filters[key] = this.filters[key] === v ? "" : v;
      }
      this.page = 1;
      this.load();
      this.loadDetails();
    },
    // The KPI cards' "Invoiced XX" / "Not XX" segments act as the Invoiced filter: click to set it, click the
    // already-active one again to clear back to All. Mirrors the ⚙ More panel's Invoiced dropdown.
    toggleInvoiced(kind) {
      this.filters.invoiced = this.filters.invoiced === kind ? "all" : kind;
      this.page = 1;
      this.load();
      this.loadDetails();
    },
    // ---- Rep <-> Territory cross-filter --------------------------------------------------------
    // Reps and territories are linked: selecting one cross-adds the other so the two picker cards
    // mirror each other (pick a rep -> its territory leaves the Sales by Territory card; pick a
    // territory -> its rep(s) leave the Sales Reps card). Removing one removes the cross-added
    // counterpart unless another active selection still needs it. Links come from the rep leaderboard
    // (bySalesRep), so the rep names always match the order data (a cross-added rep can't filter to 0).
    // A rep maps to their primary territory here; true multi-territory ties would need the admin map.
    repTerritoriesFor(name) {
      // NAME-SAFE: bySalesRep.salesRep is TRIMMED (LTRIM/RTRIM) but a rep added from the "＋ Add sales rep" picker
      // (opts.salesReps = raw DISTINCT SalesRep, untrimmed) can differ in whitespace/case, so an exact === missed it —
      // e.g. adding an International rep (Terry Mendez) failed to cross-add its "International" territory. Match on the
      // normalized key (UPPER + strip spaces — same key the backend rep→territory join uses).
      var norm = function (s) { return String(s == null ? "" : s).toUpperCase().replace(/\s+/g, ""); };
      var key = norm(name);
      var self = this;
      var set = {};
      (this.bySalesRep || []).forEach(function (r) {
        if (r && r.salesRep != null && norm(r.salesRep) === key && r.territory && String(r.territory) !== "(Unassigned)") set[String(r.territory)] = 1;
      });
      // Fallback: straight from the admin map (covers a rep not in the current bySalesRep), resolved to the ORDER-DATA
      // territory spelling so the cross-added chip matches the Territory card / filter.
      if (!Object.keys(set).length) {
        (this.territoryAssignments || []).forEach(function (a) {
          if (a && a.salesRep != null && norm(a.salesRep) === key && a.territory && String(a.territory) !== "(Unassigned)") {
            var t = self._resolveTerritoryName(String(a.territory));
            if (t) set[t] = 1;
          }
        });
      }
      return Object.keys(set);
    },
    territoryRepsFor(terr) {
      // A territory's reps, for the cross-add. Two requirements:
      //  1) COMPLETE — the old source (scoped bySalesRep) is read BEFORE the reload and, after the first
      //     territory, repTerrScope has already narrowed it, so subsequent territories added no reps (that's why
      //     picking 3 territories selected only 1 rep). The admin map (territoryAssignments) is fully loaded +
      //     covers every territory, so it's complete.
      //  2) NAME-SAFE — the filter does a raw `SalesRep IN (...)`, so a name must match rpt_CustomerOrders.SalesRep
      //     exactly or it filters to empty. Admin names can differ in case/spacing, so we resolve each admin rep to
      //     the ORDER-DATA spelling via a normalized key (UPPER + spaces stripped — same as the backend join), and
      //     SKIP any admin rep that has no order-data match (adding it would zero the report).
      var t = String(terr);
      var norm = function (s) { return String(s == null ? "" : s).toUpperCase().replace(/\s+/g, ""); };
      // NAME-SAFE: t is the ORDER-DATA territory spelling (from _resolveTerritoryName), which can differ from the admin
      // map's spelling by case/space — so match on the normalized key, NOT ===. The exact === was why a MAP-CLICKED
      // territory added no reps → the existing rep filter then zeroed it out (only Sales by Territory reflected the click).
      var tKey = norm(t);
      var orderByKey = {};
      var addOrder = function (name) { if (name != null && String(name).trim()) { var k = norm(name); if (!orderByKey[k]) orderByKey[k] = String(name); } };
      (this.bySalesRep || []).forEach(function (r) { addOrder(r && r.salesRep); });
      ((this.opts && this.opts.salesReps) || []).forEach(addOrder);
      var set = {};
      (this.territoryAssignments || []).forEach(function (a) {
        if (a && a.territory != null && norm(a.territory) === tKey && a.salesRep != null && String(a.salesRep).trim()) {
          var orderName = orderByKey[norm(a.salesRep)];
          if (orderName) set[orderName] = 1;   // only reps that actually exist in the order data
        }
      });
      var out = Object.keys(set);
      // Fallback: admin map empty/unmatched for this territory -> the (scoped) leaderboard's reps for it.
      if (!out.length) {
        (this.bySalesRep || []).forEach(function (r) { if (r && r.territory != null && norm(r.territory) === tKey && r.salesRep && out.indexOf(String(r.salesRep)) < 0) out.push(String(r.salesRep)); });
      }
      return out;
    },
    crossAddRep(name) {
      if (this.filters.salesRep.indexOf(name) < 0) this.filters.salesRep.push(name);
      this.repTerritoriesFor(name).forEach((t) => { if (this.filters.territory.indexOf(t) < 0) this.filters.territory.push(t); });
    },
    crossRemoveRep(name) {
      var i = this.filters.salesRep.indexOf(name);
      if (i >= 0) this.filters.salesRep.splice(i, 1);
      this.repTerritoriesFor(name).forEach((t) => {
        var stillUsed = this.filters.salesRep.some((r) => this.repTerritoriesFor(r).indexOf(t) >= 0);
        var ti = this.filters.territory.indexOf(t);
        if (!stillUsed && ti >= 0) this.filters.territory.splice(ti, 1);
      });
    },
    crossAddTerritory(name) {
      if (this.filters.territory.indexOf(name) < 0) this.filters.territory.push(name);
      this.territoryRepsFor(name).forEach((r) => { if (this.filters.salesRep.indexOf(r) < 0) this.filters.salesRep.push(r); });
    },
    crossRemoveTerritory(name) {
      var i = this.filters.territory.indexOf(name);
      if (i >= 0) this.filters.territory.splice(i, 1);
      this.territoryRepsFor(name).forEach((r) => {
        var stillUsed = this.filters.territory.some((t) => this.territoryRepsFor(t).indexOf(r) >= 0);
        var ri = this.filters.salesRep.indexOf(r);
        if (!stillUsed && ri >= 0) this.filters.salesRep.splice(ri, 1);
      });
      // Removing a territory also clears the CUSTOMERS within it.
      var self = this;
      this.filters.customer = (this.filters.customer || []).filter(function (cid) {
        var c = (self.byCustomer || []).find(function (cc) { return cc && String(cc.customerId) === cid; });
        return !(c && String(c.territory || "") === name);
      });
    },
    // Removing a CUSTOMER chip: drop it, and if its rep is no longer claimed by any other selected customer,
    // cascade-remove that rep (crossRemoveRep also clears any territory no remaining rep supports).
    crossRemoveCustomer(id) {
      var i = this.filters.customer.indexOf(id);
      if (i >= 0) this.filters.customer.splice(i, 1);
      var self = this;
      var rec = (this.byCustomer || []).find(function (c) { return c && String(c.customerId) === id; });
      if (!rec) return;
      var rep = rec.salesRep ? String(rec.salesRep) : "";
      if (!rep || rep === "(Unassigned)") return;
      var repStillNeeded = (this.filters.customer || []).some(function (cid) {
        var c = (self.byCustomer || []).find(function (cc) { return cc && String(cc.customerId) === cid; });
        return c && String(c.salesRep || "") === rep;
      });
      if (!repStillNeeded && this.filters.salesRep.indexOf(rep) >= 0) this.crossRemoveRep(rep);
    },
    // Restore the Email Insights card from its rail pill (un-hide + re-place at its Layout-1 size, then flash it).
    // Collapse Email Insights to the rail pill — stash its exact grid spot so restore returns it there.
    collapseEmailInsights() {
      this.flushPendingDashSave();   // a debounced save armed pre-dock must land via the OLD routing, not after the swap
      // Capture edits made in the CURRENT scenario before leaving it (e.g. Filters was docked and the user
      // rearranged → those edits belong to the "filters" preset). The base (nothing-docked) arrangement is
      // already persisted by the normal autosave.
      var oldKey = this.dockKey;
      if (oldKey && this.dockScope && this.canPublishSharedLayout) { this.writeDockPreset(oldKey); }
      // Mark docked BEFORE saving anything else: while docked, every layout save (drag/hide/place) is routed
      // to the current scenario's DOCKED preset, NOT the shared main layout (roleDefaults.ALL). That was the
      // original bug — docking used to overwrite the shared Layout 1 with the collapsed arrangement.
      this.emailDocked = true;
      // Remember the current (Email-visible) arrangement so RESTORE can rebuild it. PERSISTED to the per-user
      // record (not just a session variable) — that was the bug: a hard refresh while docked wiped the snapshot,
      // so undocking dropped Email at the bottom.
      this.emailVisibleLayout = {
        layout: JSON.parse(JSON.stringify(this.dashLayout || [])),
        hidden: (this.dashHidden || []).slice(),
        titleStyles: JSON.parse(JSON.stringify(this.titleStyles || {})),
        mapZoom: this.mapZoom
      };
      // Stamp WHICH layout this snapshot belongs to, so restoreEmailInsights can refuse to replay a
      // Sales-Insights snapshot onto Accounting (and vice versa).
      this.emailSnapLayout = this.activeLayout;
      this._emailLayoutSnap = JSON.parse(JSON.stringify(this.dashLayout || []));   // same-session fast path
      var e = (this.dashLayout || []).find(function (it) { return it.i === "emailInsights"; });
      if (e) this._emailPos = { x: e.x, y: e.y, w: e.w, h: e.h };
      // Scenario preset first ("email" or "email+filters"); then the LEGACY single Email-hidden preset
      // (pre-scenario blobs); else just hide the card.
      var preset = this.emailHiddenLayout;
      if (this.dockScope && this.applyDockPreset(this.dockKey)) {
        this.saveDashLayoutNow();   // persist + record in undo history (routes into this scenario's slot)
      } else if (this.activeLayout === "default" && this.isMaster && this.dockKey === "email" && preset && Array.isArray(preset.layout)) {
        // LEGACY fallback — DEFAULT layout only: the preset is a default-layout arrangement, and applying
        // it on a custom/shared layout would replace (then persist over) that layout's own cards.
        this.dashLayout = JSON.parse(JSON.stringify(preset.layout)).filter(function (it) { return it.i !== "emailInsights"; });
        this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
        this.dashHidden = (preset.hidden || []).slice();
        if (this.dashHidden.indexOf("emailInsights") < 0) this.dashHidden.push("emailInsights");
        this.titleStyles = JSON.parse(JSON.stringify(preset.titleStyles || {}));
        if (typeof preset.mapZoom === "number") this.mapZoom = preset.mapZoom;
        this.normalizeDockState();   // Filters may also be docked — keep it stripped
      this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
        this.saveDashLayoutNow();
      } else {
        this.hideCard("emailInsights");
        this.normalizeDockState();
      this.pruneUnauthorizedCards();
      }
      this.persistDash();   // persist emailVisibleLayout + dockLayouts so undock survives a hard refresh
    },
    // A dock/undock is about to swap the layout: if a debounced save (drag/zoom, 800ms) is still pending,
    // run it NOW through the CURRENT routing — left armed, it would fire after the swap and publish the
    // docked arrangement to the wrong store (e.g. the shared Layout 1). saveDashLayoutNow clears the timer.
    flushPendingDashSave() {
      if (this._dashSaveTimer) this.saveDashLayoutNow();
    },
    // ── Docked-layout scenarios ───────────────────────────────────────────────────────────────────
    // Snapshot the CURRENT arrangement into the given scenario's preset (docked cards stripped from the
    // snapshot — they're not on screen in that scenario). The shared writer for the 📌 button, the
    // autosave routing, and the scenario-exit capture in the dock/undock methods.
    // ===== DOCK-PRESET ACCESSORS (2026-08-05) =====
    // Sales Insights ("ALL") reads/writes the ORIGINAL flat `dockLayouts`; every other shared report
    // uses `dockLayoutsByReport[roleKey]`. Keeping Layout 1 on the flat map is deliberate — it is the
    // shape the backend's helpdesk-blob dock bridge reads ($.dockLayouts on the ALL row), so Sales
    // Insights behaviour and storage are untouched by this whole feature.
    dockScopeFor(id) {
      var R = SHARED_REPORTS.find(function (r) { return r.id === id; });
      return R ? R.roleKey : "";
    },
    getDockPreset(scope, key) {
      if (!scope || !key) return null;
      if (scope === "ALL") return (this.dockLayouts || {})[key] || null;
      var m = (this.dockLayoutsByReport || {})[scope];
      return (m && m[key]) || null;
    },
    setDockPreset(scope, key, preset) {
      if (!scope || !key) return;
      if (scope === "ALL") { this.$set(this.dockLayouts, key, preset); return; }
      // $set on the nested map too — Vue 2 can't observe a key added to a plain child object.
      if (!this.dockLayoutsByReport[scope]) this.$set(this.dockLayoutsByReport, scope, {});
      this.$set(this.dockLayoutsByReport[scope], key, preset);
    },
    deleteDockPreset(scope, key) {
      if (!scope || !key) return;
      if (scope === "ALL") { this.$delete(this.dockLayouts, key); return; }
      var m = (this.dockLayoutsByReport || {})[scope];
      if (m) this.$delete(m, key);
    },
    writeDockPreset(key) {
      if (!key) return;
      if (!this.dockScope) return;   // custom layout: nothing to write a shared preset into
      var dockedIds = key.split("+").map(function (k) { return k === "email" ? "emailInsights" : "filters"; });
      this.setDockPreset(this.dockScope, key, {
        layout: JSON.parse(JSON.stringify((this.dashLayout || []).filter(function (it) { return dockedIds.indexOf(it.i) < 0; }))),
        hidden: (this.dashHidden || []).slice(),
        titleStyles: JSON.parse(JSON.stringify(this.titleStyles || {})),
        mapZoom: this.mapZoom
      });
      // Keep the LEGACY single Email-hidden field mirroring the "email" scenario so old bundles reading the
      // blob stay coherent AND the migration re-seed can never resurrect a stale arrangement over this one.
      // Sales Insights ONLY — emailHiddenLayout is a Layout-1 concept, and letting another report write it
      // would feed that report's arrangement back into Sales through the migration re-seed.
      if (this.dockScope === "ALL" && key === "email") this.emailHiddenLayout = JSON.parse(JSON.stringify(this.getDockPreset("ALL", key)));
      this.saveDockDefaults();   // publish docks to the shared rpt_RoleDefaultLayout table (authoritative; not just the per-user blob)
    },
    // 📌 "Set/Update Docked layout — {scenario}": save the current arrangement as THIS scenario's preset.
    saveDockPreset() {
      var key = this.dockKey;
      if (!key) return;
      this.writeDockPreset(key);
      this.persistDash();
      var self = this; this.dashSaved = true; setTimeout(function () { self.dashSaved = false; }, 1800);
    },
    clearDockPreset() {
      var key = this.dockKey, scope = this.dockScope;
      if (!key || !scope || !this.getDockPreset(scope, key)) return;
      this.deleteDockPreset(scope, key);
      // Clearing an email scenario must also drop the LEGACY field — otherwise the migration re-seed
      // (loadDashLayout) and the legacy fallback would resurrect the just-cleared arrangement forever.
      // Sales Insights only, for the same reason as writeDockPreset.
      if (scope === "ALL" && key.indexOf("email") >= 0) this.emailHiddenLayout = null;
      this.persistDash();
      this.saveDockDefaults();   // reflect the cleared scenario in the shared table too
    },
    // Apply the saved preset for scenario `key` into the live state. Returns false when none is saved.
    // Always normalizes afterwards so the docked cards can never leak in from a stale snapshot.
    // `scope` lets a caller apply a preset for a layout it is SWITCHING TO, before activeLayout (and so
    // dockScope) has caught up — applyLayout needs that. Omitted => the active report.
    applyDockPreset(key, scope) {
      var p = this.getDockPreset(scope || this.dockScope, key);
      if (!p || !Array.isArray(p.layout)) return false;
      this.dashLayout = JSON.parse(JSON.stringify(p.layout));
      this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
      this.dashHidden = Array.isArray(p.hidden) ? p.hidden.slice() : [];
      this.titleStyles = JSON.parse(JSON.stringify(p.titleStyles || {}));
      if (typeof p.mapZoom === "number") this.mapZoom = p.mapZoom;
      this.normalizeDockState();
      this.pruneUnauthorizedCards();
      return true;
    },
    // Make the live layout agree with the dock flags, whatever source it came from: docked cards are
    // stripped (+ Email marked in dashHidden so ensureBuiltinCards can't re-append it — Filters has its
    // own docked-skip there); an UNdocked Filters card is guaranteed present. Email insertion is NOT
    // done here — restoreEmailInsights owns that (it has richer position sources). NOTE: when Email is
    // UNdocked, an 'emailInsights' entry in dashHidden is a legitimate user ×-hide and must be KEPT —
    // restoreEmailInsights removes the marker explicitly on its own path.
    normalizeDockState() {
      if (this.emailDocked) {
        this.dashLayout = (this.dashLayout || []).filter(function (it) { return it.i !== "emailInsights"; });
        this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
        if ((this.dashHidden || []).indexOf("emailInsights") < 0) this.dashHidden.push("emailInsights");
      }
      if (this.filtersDocked) {
        this.dashLayout = (this.dashLayout || []).filter(function (it) { return it.i !== "filters"; });
        this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
      } else if (!(this.dashLayout || []).some(function (it) { return it.i === "filters"; })) {
        this.dashLayout.forEach(function (it) { if (it.i !== "favorites") it.y = (Number(it.y) || 0) + 9; });
        this.dashLayout.unshift({ i: "filters", x: 0, y: 0, w: 20, h: 9 });
        this.dashHidden = (this.dashHidden || []).filter(function (h) { return h !== "filters"; });
      }
    },
    // ── Named layout versions ─────────────────────────────────────────────────────────────────────
    // Explicit, user-named snapshots of the CURRENT arrangement, saved in the per-user blob (the reliable store —
    // same place the Email-hidden layout lives). A dependable restore point independent of the shared Layout-1
    // default, so a bad auto-save / deploy race can always be recovered with one click.
    saveLayoutVersion() {
      var name = (this.versionName || "").trim();
      if (!name) name = "Version " + (this.layoutVersions.length + 1);
      var snap = {
        id: "v" + Date.now() + "_" + Math.floor(Math.random() * 100000),
        name: name.slice(0, 60),
        savedAt: new Date().toISOString(),
        layout: JSON.parse(JSON.stringify(this.dashLayout || [])),
        hidden: (this.dashHidden || []).slice(),
        titleStyles: JSON.parse(JSON.stringify(this.titleStyles || {})),
        mapZoom: this.mapZoom,
        aiCards: JSON.parse(JSON.stringify(this.aiCards || {})),
        emailDocked: !!this.emailDocked
      };
      this.layoutVersions.unshift(snap);
      if (this.layoutVersions.length > 20) this.layoutVersions = this.layoutVersions.slice(0, 20);   // keep the last 20
      this.versionName = "";
      this.persistDash();
      var self = this; this.dashSaved = true; setTimeout(function () { self.dashSaved = false; }, 1800);
    },
    restoreLayoutVersion(v) {
      if (!v || !Array.isArray(v.layout)) return;
      this.dashLayout = JSON.parse(JSON.stringify(v.layout));
      this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
      this.dashHidden = (v.hidden || []).slice();
      this.titleStyles = JSON.parse(JSON.stringify(v.titleStyles || {}));
      if (typeof v.mapZoom === "number") this.mapZoom = v.mapZoom;
      if (v.aiCards) this.aiCards = JSON.parse(JSON.stringify(v.aiCards));
      this.emailDocked = !!v.emailDocked;
      this.ensureBuiltinCards();
      this.loadCustomCards();
      this.normalizeDockState();   // the version may predate the current dock flags — reconcile docked cards/markers
      this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
      this.saveDashLayoutNow();   // persist the restored arrangement as the current layout (routes by dock scenario)
      this.recordLayoutHistory();
    },
    deleteLayoutVersion(v) {
      if (!v) return;
      this.layoutVersions = this.layoutVersions.filter(function (x) { return x.id !== v.id; });
      this.persistDash();
    },
    fmtVerDate(iso) {
      try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch (e) { return ""; }
    },
    // While ANY card is docked, capture the live arrangement into the CURRENT scenario's DOCKED preset and
    // persist it to the per-user blob. This is where "docked-mode" layout edits go — the shared main layout
    // is left untouched, so it survives docking + a hard refresh.
    syncDockPresetFromLive() {
      var key = this.dockKey;
      if (!key) return;
      this.writeDockPreset(key);
      this.persistDash();
    },
    restoreEmailInsights() {
      this.flushPendingDashSave();   // a debounced save armed pre-undock must land via the OLD routing, not after the swap
      // Capture edits made while docked into THAT scenario's preset before leaving it.
      var oldKey = this.dockKey;   // "email" or "email+filters"
      if (oldKey && this.dockScope && this.canPublishSharedLayout) { this.writeDockPreset(oldKey); }
      this.emailDocked = false;   // back to an Email-visible view; saves route to the new scenario (or main)
      var hi = (this.dashHidden || []).indexOf("emailInsights");
      if (hi >= 0) this.dashHidden.splice(hi, 1);
      // Entering "filters"-only-docked with a saved preset (that preset INCLUDES the Email card) → apply it
      // and skip the legacy source chain below. A preset that somehow lacks the card falls through instead.
      var nk = this.dockKey;   // "" or "filters"
      if (nk && this.dockScope) {
        var np = this.getDockPreset(this.dockScope, nk);
        if (np && Array.isArray(np.layout) && np.layout.some(function (it) { return it.i === "emailInsights"; })) {
          this.applyDockPreset(nk);
          // Restoring Email IS an explicit un-hide — drop any stale marker the preset snapshot carried.
          this.dashHidden = (this.dashHidden || []).filter(function (h) { return h !== "emailInsights"; });
          this.saveDashLayoutNow();
          this.persistDash();
          this.flashCard("emailInsights");
          return;
        }
      }
      // Take the authoritative main layout from the ACTIVE report's own shared row. This used to be
      // hardwired to roleDefaults["ALL"], so un-docking Email on Accounting rebuilt the SALES dashboard
      // over it (live bug, 2026-08-05). "" scope (custom layout) => null, i.e. fall through as before.
      var _mrow = this.dockScope ? (this.roleDefaults || {})[this.dockScope] : null;
      var main = (_mrow && Array.isArray(_mrow.layout)) ? _mrow : null;
      var vis = this.emailVisibleLayout;
      var snap = this._emailLayoutSnap;
      if (main && main.layout.some(function (it) { return it.i === "emailInsights"; })) {
        // AUTHORITATIVE shared main layout (never clobbered by docking now) → the freshest source of truth.
        this.dashLayout = JSON.parse(JSON.stringify(main.layout));
        this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
        this.dashHidden = (main.hidden || []).filter(function (i) { return i !== "emailInsights"; });
        this.titleStyles = JSON.parse(JSON.stringify(main.titleStyles || {}));
        if (typeof main.mapZoom === "number") this.mapZoom = main.mapZoom;
      } else if ((this.emailSnapLayout || "default") === this.activeLayout && vis && Array.isArray(vis.layout) && vis.layout.some(function (it) { return it.i === "emailInsights"; })) {
        // PERSISTED Email-visible snapshot → rebuild it exactly (also HEALS a main layout an older build had clobbered).
        this.dashLayout = JSON.parse(JSON.stringify(vis.layout));
        this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
        this.dashHidden = (vis.hidden || []).filter(function (i) { return i !== "emailInsights"; });
        this.titleStyles = JSON.parse(JSON.stringify(vis.titleStyles || {}));
        if (typeof vis.mapZoom === "number") this.mapZoom = vis.mapZoom;
      } else if ((this.emailSnapLayout || "default") === this.activeLayout && snap && snap.some(function (it) { return it.i === "emailInsights"; })) {
        // Same-session snapshot: put every card back where it was; append any card added while hidden.
        var snapIds = {};
        snap.forEach(function (it) { snapIds[it.i] = true; });
        var maxY = snap.reduce(function (m, it) { return Math.max(m, (Number(it.y) || 0) + (Number(it.h) || 0)); }, 0);
        var extras = (this.dashLayout || []).filter(function (it) { return !snapIds[it.i]; })
          .map(function (it) { var c = JSON.parse(JSON.stringify(it)); c.y = maxY; maxY += (Number(it.h) || 3); return c; });
        this.dashLayout = JSON.parse(JSON.stringify(snap)).concat(extras);
        this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
      } else if (!(this.dashLayout || []).some(function (it) { return it.i === "emailInsights"; })) {
        // Fallback (e.g. hidden via the generic × instead of the collapse arrow): original pos if known, else bottom.
        var p = this._emailPos;
        if (p) {
          this.dashLayout.push({ i: "emailInsights", x: p.x, y: p.y, w: p.w, h: p.h });
        } else {
          var size = this.cardDefaultSize("emailInsights", { isBuiltin: true });
          var my = (this.dashLayout || []).reduce(function (m, it) { return Math.max(m, (Number(it.y) || 0) + (Number(it.h) || 0)); }, 0);
          this.dashLayout.push({ i: "emailInsights", x: 0, y: my, w: size.w, h: size.h });
        }
      }
      this.normalizeDockState();   // Filters may STILL be docked (no saved "filters" preset) — the main-layout
      this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
                                  // sources above include its card, so re-strip; also syncs the email marker.
      this.saveDashLayoutNow();   // routes to the new scenario's slot, or re-affirms/HEALS the shared main layout
      this.persistDash();         // persist emailDocked=false so a refresh comes back to the Email-visible view
      this.flashCard("emailInsights");
    },
    // Sales Reps card (object) / Sales Rep slicer (string) -> toggle rep + cross-sync territory.
    toggleRep(rep) {
      var name = rep && rep.salesRep != null ? String(rep.salesRep) : String(rep || "");
      this.toggleRepName(name);
    },
    toggleRepName(name) {
      if (!name) return;
      if (this.filters.salesRep.indexOf(name) >= 0) this.crossRemoveRep(name);
      else this.crossAddRep(name);
      this.page = 1;
      this.load();
      this.loadDetails();
    },
    // Sales by Territory card / Territory slicer -> toggle territory + cross-sync its rep(s).
    toggleTerritory(terr) {
      var name = terr == null ? "" : String(terr);
      if (!name) return;
      if (this.filters.territory.indexOf(name) >= 0) this.crossRemoveTerritory(name);
      else this.crossAddTerritory(name);
      this.page = 1;
      this.load();
      this.loadDetails();
    },
    // The cards' "＋ Add territory / sales rep" pickers (they replaced the old top-row dropdowns). Adds the picked
    // value through the same toggle a card-row click uses, then resets the <select> back to its placeholder.
    addFromPicker(dim, ev) {
      var val = (ev && ev.target) ? ev.target.value : "";
      if (ev && ev.target) ev.target.value = "";
      if (!val) return;
      if (dim === "territory") this.toggleTerritory(val);
      else if (dim === "salesRep") this.toggleRepName(val);
    },
    // Add a value to a multi-select (array) filter — used by the drawer dropdowns/inputs.
    addFilter(key, value) {
      const v = value == null ? "" : String(value).trim();
      if (!v || !Array.isArray(this.filters[key])) return;
      if (this.filters[key].indexOf(v) < 0) this.filters[key].push(v);
      this.page = 1;
      this.load();
      this.loadDetails();
    },
    // Clear an entire multi-select (array) filter (the slicer's clear button).
    clearFilter(key) {
      if (!Array.isArray(this.filters[key])) return;
      this.filters[key] = [];
      this.page = 1;
      this.load();
      this.loadDetails();
    },
    // Customer ID(s) card → toggle the customer filter, and ON ADD also cross-add the customer's own TERRITORY
    // and SALES REP (byCustomer carries both, in order-data names) so picking a customer pulls in its region +
    // rep like the map/cards do. Removal just drops the customer — its territory/rep may be shared, so leave them.
    toggleCustomerFilter(id) {
      var cid = id == null ? "" : String(id);
      if (!cid || !Array.isArray(this.filters.customer)) return;
      var i = this.filters.customer.indexOf(cid);
      if (i >= 0) {
        this.crossRemoveCustomer(cid);   // drop + cascade-remove the rep/territory this customer alone contributed
      } else {
        this.filters.customer.push(cid);
        // Auto-focus a rep + territory ONLY when the customer maps to a SINGLE (territory,rep) segment — taken from the
        // per-customer breakdown (customerSegments ⟵ byCustomerTerritory) so the pair ALWAYS corresponds. For a customer
        // split across reps/territories (e.g. NE110 = George Campbell/Northern & Canada + Kevin Cornett/Midwest) DON'T
        // force one — leave just the customer filter so the Sales Reps + Sales by Territory cards show the FULL split.
        // (Was: MAX(rep) + MAX(territory) from byCustomer — INDEPENDENT maxes that, for a split customer, picked a
        // MISMATCHED pair, e.g. Kevin Cornett + Northern & Canada → contradictory filter → $0, other rep/terr hidden.)
        var segs = (this.customerSegments && this.customerSegments[cid]) || [];
        if (segs.length === 1) {
          var terr = segs[0] && segs[0].territory ? String(segs[0].territory) : "";
          if (terr && terr !== "International" && terr !== "(Unassigned)" && this.filters.territory.indexOf(terr) < 0) this.filters.territory.push(terr);
          var rep = segs[0] && segs[0].salesRep ? String(segs[0].salesRep) : "";
          if (rep && rep !== "(Unassigned)" && this.filters.salesRep.indexOf(rep) < 0) this.filters.salesRep.push(rep);
        }
      }
      this.page = 1;
      this.load();
      this.loadDetails();
    },
    // Clicking a state on the (territory) map adds that state's TERRITORY. The name is resolved from the ORDER
    // DATA (byState) so it matches the Territory card / opts.territories / the backend filter exactly; the admin
    // map (_territoryForState) is only a fallback, and its result is name-normalized to the order data too —
    // admin territory names can differ in case/spacing, and an unmatched name would never show in the card and
    // could filter to zero (same name-safety issue as the reps cross-add). Mirrors onSelectCountry (byCountry).
    onSelectState(postal) {
      var p = postal == null ? "" : String(postal).toUpperCase();
      if (!p) return;
      // Resolve the territory the SAME WAY the map COLORS the state — via the admin map (_territoryForState,
      // first non-International assignment wins). Only fall back to the state's order-data territory if the admin
      // map has no assignment for it. (Previously this preferred byState.territory, which for a state whose orders
      // span reps in >1 territory — e.g. TX — filtered a DIFFERENT territory than the color the user clicked.)
      var terr = this._resolveTerritoryName(this._territoryForState(p));
      if (!terr) {
        var rec = (this.byState || []).find(function (s) { return s && String(s.state).toUpperCase() === p; });
        if (rec && rec.territory && String(rec.territory) !== "International") terr = String(rec.territory);
      }
      if (terr) { this.toggleTerritory(terr); return; }
      // No territory for this state (unassigned/International) → fall back to a state-level filter so the click still does something.
      var arr = this.filters.state;
      var i = arr.indexOf(p);
      var adding = i < 0;
      if (adding) arr.push(p); else arr.splice(i, 1);   // multi-select states
      // Auto-pick the sole customer only when adding the FIRST state with no customer yet.
      this._autoPickCustomer = adding && arr.length === 1 && !this.filters.customer.length;
      this.page = 1;
      this.load();
      this.loadDetails();
    },
    // A US state's territory, from the admin map — same rule the map uses to COLOR the state (first non-International
    // assignment wins), so clicking a state filters exactly the territory it's colored as.
    _territoryForState(postal) {
      var p = String(postal == null ? "" : postal).toUpperCase();
      var found = "";
      (this.territoryAssignments || []).forEach(function (a) {
        if (!found && a && a.state && String(a.state).toUpperCase() === p && a.territory && String(a.territory) !== "International") found = String(a.territory);
      });
      return found;
    },
    // Map a territory name (e.g. from the admin map) to the ORDER-DATA spelling the cards/filters use, matching on a
    // normalized key (UPPER + spaces stripped). Returns the input unchanged if there's no match. Keeps a map/admin-
    // sourced territory chip in sync with opts.territories so it shows in the card and filters correctly.
    _resolveTerritoryName(name) {
      var raw = name == null ? "" : String(name);
      if (!raw) return "";
      var norm = function (s) { return String(s == null ? "" : s).toUpperCase().replace(/\s+/g, ""); };
      var target = norm(raw);
      var pool = ((this.opts && this.opts.territories) || []).slice();
      (this.byTerritory || []).forEach(function (t) { if (t && t.territory) pool.push(t.territory); });
      var match = pool.find(function (t) { return norm(t) === target; });
      return match || raw;
    },
    // World-map country click → filter by that COUNTRY. The world map is a COUNTRY view: the hover tooltip
    // shows the country's OWN stats and says "Click to filter by <country>", so a click must set the Country
    // filter — NOT the country's MAX(Territory) (which sent Russia to its arbitrary top territory "Mountain
    // and West Coast" + that territory's reps, via toggleTerritory's rep cross-add). Resolve to the byCountry
    // card's CANONICAL label (CountryNorm output, e.g. "RUSSIA") so the chip, the world-map highlight
    // (mapSelectedCountry reads filters.country), and the backend `(CountryNorm) IN (...)` all agree.
    onSelectCountry(name) {
      var n = name == null ? "" : String(name);
      if (!n) return;
      var rec = (this.byCountry || []).find(function (c) { return c && String(c.country).toUpperCase() === n.toUpperCase(); });
      var country = (rec && rec.country) ? String(rec.country) : n;
      this.toggleFilter("country", country);
    },
    // ---- Active-filter chip/tooltip helpers (shared by the per-chip hover popover): hover a popover row to reveal
    // its custom HTML "comment" tooltip. (The old "See active filters" pill's open/close handlers were removed 2026-07-03.)
    // ---- Shared chip popover (opened by hovering ANY card's filter chip): shows the full active-filter list — same
    // content + dead/culprit analysis as the "See active filters" popup — with the hovered chip highlighted + pulled
    // to the top. Body-appended + fixed-positioned under the hovered chip so it escapes the cards' clipped overflow.
    openChipHoverAt(key, val, ev) {
      if (this._chipHoverT) { clearTimeout(this._chipHoverT); this._chipHoverT = null; }
      this.chipHoverKey = String(key) + "|" + (val == null ? "" : String(val));
      try {
        var r = ev && ev.currentTarget && ev.currentTarget.getBoundingClientRect ? ev.currentTarget.getBoundingClientRect() : null;
        if (r) {
          var w = 420;
          var left = Math.max(8, Math.min(Math.round(r.left), (window.innerWidth || 1200) - w - 8));
          this.chipHoverPos = { top: Math.round(r.bottom + 6), left: left };
        }
      } catch (e) { /* noop */ }
      this.chipHoverOpen = true;
    },
    openChipHoverPop() { if (this._chipHoverT) { clearTimeout(this._chipHoverT); this._chipHoverT = null; } this.chipHoverOpen = true; },
    closeChipHoverPop() { var self = this; if (this._chipHoverT) clearTimeout(this._chipHoverT); this._chipHoverT = setTimeout(function () { self.chipHoverOpen = false; self.chipHoverKey = ""; self.hoverChipKey = ""; self._chipHoverT = null; }, 220); },
    isChipHoverActive(c) { return !!c && this.chipHoverKey === this._chipKey(c); },
    // Sales-rep chip label → "First L" (first name + last-name initial) so the chips stay compact. The chip's DATA
    // (filter value, hover popover, removal) still uses the FULL rep name; only the visible label is shortened.
    shortRepName(name) {
      var s = String(name == null ? "" : name).trim();
      if (!s) return s;
      var parts = s.split(/\s+/);
      if (parts.length < 2) return s;
      return parts[0] + " " + parts[parts.length - 1].charAt(0).toUpperCase();
    },
    // ---- KPI "% vs prior period" show-your-work tooltip (body-appended, fixed). Opened by CLICKING the "?" help
    // button on a KPI card; clicking the "?" again toggles it, and a document mousedown listener closes it on any
    // outside click. (Was hover-triggered on the whole delta; the user asked for a click-a-question-mark affordance.)
    toggleKpiMath(kind, ev) {
      if (this.kpiMathOpen && this.kpiMathKind === kind) { this.closeKpiMath(); return; }
      this.kpiMathKind = kind;
      this.kpiMathBody = this._buildKpiMath(kind);
      try {
        var r = ev && ev.currentTarget && ev.currentTarget.getBoundingClientRect ? ev.currentTarget.getBoundingClientRect() : null;
        if (r) {
          var w = 380;
          var left = Math.max(8, Math.min(Math.round(r.left), (window.innerWidth || 1200) - w - 8));
          this.kpiMathPos = { top: Math.round(r.bottom + 8), left: left };
        }
      } catch (e) { /* noop */ }
      this.kpiMathOpen = true;
      var self = this;
      this.$nextTick(function () { if (typeof document !== "undefined") document.addEventListener("mousedown", self._kpiMathDocClose); });
    },
    closeKpiMath() {
      this.kpiMathOpen = false; this.kpiMathKind = "";
      if (typeof document !== "undefined") document.removeEventListener("mousedown", this._kpiMathDocClose);
    },
    _kpiMathDocClose(e) {
      var t = e && e.target;
      if (t && t.closest && (t.closest(".ci-kpi-math") || t.closest(".ci-kpi-help"))) return;   // click inside the popover or on a "?" button → keep open
      this.closeKpiMath();
    },
    // 📝 in the Customer ID(s) card → show that customer's notation in a body-appended popover near the icon.
    onCustomerNotation(payload) {
      var c = payload && payload.customer, ev = payload && payload.event;
      var text = c && c.customerNotation ? String(c.customerNotation) : "";
      if (!text) return;
      try {
        var r = ev && ev.currentTarget && ev.currentTarget.getBoundingClientRect ? ev.currentTarget.getBoundingClientRect() : null;
        if (r) {
          var w = 320;
          var left = Math.max(8, Math.min(Math.round(r.right - w), (window.innerWidth || 1200) - w - 8));
          var top = Math.round(r.bottom + 6);
          if (top > (window.innerHeight || 800) - 190) top = Math.max(8, Math.round(r.top - 6 - 180));   // flip up near the bottom
          this.notePop.top = top; this.notePop.left = left;
        }
      } catch (e) { /* noop */ }
      this.notePop.id = c.customerId || "";
      this.notePop.name = (c.customerId || "") + (c.customerName ? " — " + c.customerName : "");
      this.notePop.text = text;
      this.notePop.open = true;
      this._fitPopToViewport("notePopPanel", this.notePop);
      var self = this;
      this.$nextTick(function () { if (typeof document !== "undefined") document.addEventListener("mousedown", self._noteDocClose); });
    },
    closeNotePop() {
      this.notePop.open = false;
      if (typeof document !== "undefined") document.removeEventListener("mousedown", this._noteDocClose);
    },
    _noteDocClose(e) {
      var t = e && e.target;
      if (t && t.closest && (t.closest(".ci-notepop") || t.closest(".cb-note"))) return;   // inside the popover or on a 📝 icon → keep open
      this.closeNotePop();
    },
    // 💡 Customer Order Details "rep history" — the customer is currently attributed to its mapped rep, but some of its
    // orders were originally sold by a DIFFERENT rep in VISUAL. Shows current vs the OrigSalesRep breakdown.
    onCodWhyHere(payload) {
      var id = payload && payload.customerId, ev = payload && payload.event;
      var c = (((this.details || {}).customers) || []).find(function (x) { return x && x.customerId === id; });
      if (!c) return;
      this.closeNotePop(); this.closeSpecPop(); this.closePerspPop();
      var p = this._popAnchor(ev, 340);
      this.codWhyPop.top = p.top; this.codWhyPop.left = p.left;
      this.codWhyPop.name = id + (c.customerName ? " — " + c.customerName : "");
      this.codWhyPop.currentRep = c.currentRep || "(Unassigned)";
      this.codWhyPop.currentTerr = ((c.territories || [])[0] || {}).name || "";
      this.codWhyPop.attribution = (c.currentAttribution || []).slice();
      this.codWhyPop.history = (c.repHistory || []).slice();
      this.codWhyPop.open = true;
      this._fitPopToViewport("codWhyPopPanel", this.codWhyPop);
      var self = this;
      this.$nextTick(function () { if (typeof document !== "undefined") document.addEventListener("mousedown", self._codWhyDocClose); });
    },
    _codWhyIsCur(rep) {
      var norm = function (s) { return String(s == null ? "" : s).toUpperCase().replace(/\s+/g, ""); };
      return norm(rep) === norm(this.codWhyPop.currentRep);
    },
    closeCodWhyPop() {
      this.codWhyPop.open = false;
      if (typeof document !== "undefined") document.removeEventListener("mousedown", this._codWhyDocClose);
    },
    _codWhyDocClose(e) {
      var t = e && e.target;
      if (t && t.closest && (t.closest(".ci-whypop") || t.closest(".cod-why"))) return;
      this.closeCodWhyPop();
    },
    // Position a body-appended popover just under its trigger icon (mirrors onCustomerNotation's math).
    _popAnchor(ev, w) {
      var pos = { top: 120, left: 120 };
      try {
        var r = ev && ev.currentTarget && ev.currentTarget.getBoundingClientRect ? ev.currentTarget.getBoundingClientRect() : null;
        if (r) {
          pos.left = Math.max(8, Math.min(Math.round(r.right - w), (window.innerWidth || 1200) - w - 8));
          pos.top = Math.round(r.bottom + 6);
          if (pos.top > (window.innerHeight || 800) - 190) pos.top = Math.max(8, Math.round(r.top - 6 - 180));   // flip up near the bottom
        }
      } catch (e) { /* noop */ }
      return pos;
    },
    // Clamp a body-appended popover so it never spills off ANY screen edge. Measured AFTER render (nextTick), so it
    // uses the popover's ACTUAL height — re-callable once async content (the 💡 fetch) grows it.
    _fitPopToViewport(refName, popObj) {
      var self = this;
      this.$nextTick(function () {
        try {
          var el = self.$refs[refName];
          if (!el || !el.getBoundingClientRect) return;
          var w = el.offsetWidth || 320, h = el.offsetHeight || 160;
          var vw = window.innerWidth || 1200, vh = window.innerHeight || 800;
          var left = popObj.left, top = popObj.top;
          if (left + w > vw - 8) left = vw - w - 8;   // clamp right
          if (left < 8) left = 8;                       // clamp left
          if (top + h > vh - 8) top = vh - h - 8;       // clamp bottom
          if (top < 8) top = 8;                         // clamp top
          popObj.left = left; popObj.top = top;
        } catch (e) { /* noop */ }
      });
    },
    // 💡 Sales-by-Territory: WHO rolls up into a territory + WHY (shown only when >1 rep makes up the total).
    territoryInsightInfo(name) {
      var reps = this.territoryContributors[name] || [];
      var total = reps.reduce(function (s, r) { return s + (Number(r.sales) || 0); }, 0);
      return { reps: reps, total: total, show: reps.length > 1 };
    },
    onTerritoryInsight(t, ev) {
      if (ev && ev.stopPropagation) ev.stopPropagation();
      var name = t && t.territory;
      if (this.terrIns.open && this.terrIns.key === name) { this.closeTerrIns(); return; }
      var p = this._popAnchor(ev, 340);
      this.terrIns.key = name;
      this.terrIns.body = this._buildTerrInsBody(name);
      this.terrIns.top = p.top; this.terrIns.left = p.left;
      this.terrIns.open = true;
      var self = this;
      this._fitPopToViewport("terrInsPanel", this.terrIns);
      this.$nextTick(function () { if (typeof document !== "undefined") document.addEventListener("mousedown", self._terrInsDocClose); });
    },
    closeTerrIns() {
      this.terrIns.open = false; this.terrIns.key = "";
      if (typeof document !== "undefined") document.removeEventListener("mousedown", this._terrInsDocClose);
    },
    _terrInsDocClose(e) {
      var t = e && e.target;
      if (t && t.closest && (t.closest(".ci-terr-ins") || t.closest(".ci-terr-insbtn"))) return;
      this.closeTerrIns();
    },
    _buildTerrInsBody(name) {
      var esc = this._esc, self = this;
      var info = this.territoryInsightInfo(name);
      var reps = info.reps || [], total = info.total || 0;
      var isUn = name === "(Unassigned)";
      var h = '';
      h += '<div class="ci-terr-ins-h"><span class="ci-terr-ins-dot" style="background:' + esc(this.terrColor(name)) + '"></span>' + esc(name) + '</div>';
      h += '<div class="ci-terr-ins-sub">' + this.fmtMoney(total) + ' · ' + reps.length + ' sales rep' + (reps.length === 1 ? '' : 's') + ' in this total</div>';
      h += '<div class="ci-terr-ins-list">';
      reps.forEach(function (r) {
        var pct = total ? Math.round((r.sales / total) * 100) : 0;
        h += '<div class="ci-terr-ins-row"><span class="ci-terr-ins-rep">' + esc(r.rep) + '</span><span class="ci-terr-ins-amt">' + self.fmtMoney(r.sales) + ' <em>' + pct + '%</em></span></div>';
      });
      h += '</div>';
      if (isUn) {
        h += '<div class="ci-terr-ins-why"><b>Why here:</b> these reps have a name in VISUAL that isn\'t mapped to any territory, so their orders can\'t be classified. Map each one in <b>Account Setup &rarr; Sales Territories</b> (add a row with the rep\'s <em>exact</em> VISUAL name) and they\'ll move into the right territory on the next sync.</div>';
      } else {
        h += '<div class="ci-terr-ins-why"><b>Why together:</b> each of these reps is assigned to <b>' + esc(name) + '</b> in Sales Territories, so every order they own rolls up into this one territory total. Click the bar to filter the whole report to ' + esc(name) + '.</div>';
      }
      return h;
    },
    // ⚙ "Inferred attribution" flag: what share of a rep's total did NOT originate as their OWN VISUAL sales — split into
    // (a) orders originally credited to a DIFFERENT rep in VISUAL, rolled here via a rep-alias / reassignment, and
    // (b) orders with NO rep in VISUAL, assigned by the customer-ID prefix rule. Lets a Master (or the rep) see how much
    // of a total is reattributed vs the rep's own book — and WHY (alias vs prefix), not a blanket "auto-mapped from prefix".
    repAutoPct(r) {
      if (!r || r.salesRep === "(Unassigned)") return 0;   // (Unassigned) already has its own 💡
      var s = Number(r.sales) || 0;
      var inferred = (Number(r.autoMappedSales) || 0) + (Number(r.remappedSales) || 0);
      if (s <= 0 || inferred <= 0) return 0;
      return Math.min(100, Math.round((inferred / s) * 100));
    },
    repAutoTitle(r) {
      var self = this;
      var s = Number(r && r.sales) || 0;
      var prefix = Number(r && r.autoMappedSales) || 0;   // no rep in VISUAL → prefix-assigned
      var remap = Number(r && r.remappedSales) || 0;      // originally a DIFFERENT VISUAL rep → alias / reassignment
      var own = s - prefix - remap;                       // the rep's own VISUAL sales
      var nm = (r && r.salesRep) || "this rep";
      var pctOf = function (v) { return s > 0 ? Math.round((v / s) * 100) : 0; };
      var lines = [];
      if (remap > 0) {
        var from = (r && r.remappedFrom) ? (" from " + r.remappedFrom) : "";
        lines.push("• " + self.fmtMoney(remap) + " (" + pctOf(remap) + "%) inherited" + from + " via a rep-alias / reassignment — originally credited to a different rep in VISUAL.");
      }
      if (prefix > 0) {
        lines.push("• " + self.fmtMoney(prefix) + " (" + pctOf(prefix) + "%) from orders with NO rep in VISUAL, auto-assigned by the customer-ID prefix rule.");
      }
      if (own > 0) {
        lines.push("• " + self.fmtMoney(own) + " (" + pctOf(own) + "%) is " + nm + "'s own VISUAL sales.");
      }
      return "How " + nm + "'s " + self.fmtMoney(s) + " total breaks down by origin:\n" + lines.join("\n");
    },
    // 📋 specifications popover for a customer (customer-level CustomerSpecs).
    onCustomerSpecs(payload) {
      var c = payload && payload.customer, ev = payload && payload.event;
      var text = c && c.customerSpecs ? String(c.customerSpecs) : "";
      if (!text) return;
      this.closeNotePop(); this.closePerspPop();
      var p = this._popAnchor(ev, 320);
      this.specPop.top = p.top; this.specPop.left = p.left;
      this.specPop.id = c.customerId || "";
      this.specPop.name = (c.customerId || "") + (c.customerName ? " — " + c.customerName : "");
      this.specPop.text = text;
      this.specPop.open = true;
      this._fitPopToViewport("specPopPanel", this.specPop);
      var self = this;
      this.$nextTick(function () { if (typeof document !== "undefined") document.addEventListener("mousedown", self._specDocClose); });
    },
    closeSpecPop() {
      this.specPop.open = false;
      if (typeof document !== "undefined") document.removeEventListener("mousedown", this._specDocClose);
    },
    _specDocClose(e) {
      var t = e && e.target;
      if (t && t.closest && (t.closest(".ci-specpop") || t.closest(".cb-spec"))) return;
      this.closeSpecPop();
    },
    // Hero Notations/Specifications chip HOVER → open the body-appended popover under the chip. kind = "note"|"spec".
    // Reuses the .ci-notepop look; its own state (heroNote) so the click-toggled card popovers are untouched.
    heroNoteHover(kind, ev) {
      var n = this.heroCustomerNotes;
      if (!n) return;
      var text = kind === "spec" ? n.specs : n.notation;
      if (!text) return;
      clearTimeout(this._heroNoteClose);
      var p = this._popAnchor(ev, 320);
      this.heroNote.top = p.top; this.heroNote.left = p.left;
      this.heroNote.kind = kind;
      this.heroNote.name = n.id + (n.name ? " — " + n.name : "");
      this.heroNote.text = text;
      this.heroNote.open = true;
      this._fitPopToViewport("heroNotePanel", this.heroNote);
    },
    heroNoteKeep() { clearTimeout(this._heroNoteClose); },
    heroNoteLeave() {
      var self = this;
      clearTimeout(this._heroNoteClose);
      // Short grace so the pointer can travel from the chip INTO the popover (to read/scroll long notes).
      this._heroNoteClose = setTimeout(function () { self.heroNote.open = false; }, 160);
    },
    // ----- Hero Ask ("Chat") collapse to the right edge (mirrors the Email dock) -----
    collapseChat() {
      this.chatDocked = true;
      this.heroNote.open = false;   // don't leave a note popover orphaned when the hero disappears
      this._heroTy = 0;             // the pinned band is gone — let the cards re-pin to the nav line
      try { if (typeof localStorage !== "undefined") localStorage.ciChatDocked = "1"; } catch (e) { /* noop */ }
      this.$nextTick(this._onStickyScroll);
    },
    restoreChat() {
      this.chatDocked = false;
      try { if (typeof localStorage !== "undefined") localStorage.ciChatDocked = "0"; } catch (e) { /* noop */ }
      var self = this;
      // Fresh element (v-if remount): pin it immediately if the page is already scrolled.
      this.$nextTick(function () {
        try { if (self.$refs.heroAskInput) self.$refs.heroAskInput.focus(); } catch (e) { /* noop */ }
        self._onStickyScroll();
      });
    },
    // ✕ Clear everything in one click — wipe the search box + the AI answer/result card (invalidating any
    // in-flight ask so it can't repopulate), then resetFilters() clears every filter chip + the dates baseline
    // and reloads the dashboard to the clean scope. (The ▶ dock button HIDES chat+results together; this one
    // ERASES them.)
    clearHero() {
      this._procSeq = (this._procSeq || 0) + 1;   // any in-flight ask can no longer repopulate after the clear
      this.aiAskBusy = false;
      this.aiQuestion = "";
      this.aiAnswer = "";
      this.aiTable = null;
      this.aiProposed = null;
      this.aiProposedNote = "";
      this.aiApplied = false;
      this.aiInsights = "";
      this.aiAskError = "";
      this.aiFollow = { stage: "", name: "", favId: "" };
      this.aiAskHistOpen = false;
      if (this.heroSug) this.heroSug.open = false;
      try { this.stopTts(); } catch (e) { /* noop */ }
      this.ttsError = "";
      this.resetFilters();   // clears all filter chips + dates baseline and reloads the dashboard to the clean scope
    },
    // ----- Hero talk-back: speak the answer in an Azure NEURAL voice (mobile). Never the browser voice —
    // when /report/tts returns 204/an error (Azure Speech unconfigured or down) we show a notice instead. -----
    speakAnswer() {
      var self = this;
      if (this.ttsPlaying) { this.stopTts(); return; }
      var text = (this.aiAnswerLines || []).map(function (l) { return l.text; }).join(" ").trim();
      if (!text || this.ttsBusy) return;
      this.ttsBusy = true;
      this.ttsError = "";
      httpClient.post("/report/tts", { text: text, voice: this.ttsVoice }, { responseType: "blob" })
        .then(function (res) {
          if (!res || res.status === 204 || !res.data || !res.data.size) {
            self.ttsError = "Voice playback isn't available right now.";
            return;
          }
          var url = URL.createObjectURL(res.data);
          var audio = new Audio(url);
          self._ttsAudio = audio;
          audio.onended = function () { self.ttsPlaying = false; URL.revokeObjectURL(url); };
          audio.onerror = function () { self.ttsPlaying = false; self.ttsError = "Couldn't play the audio."; URL.revokeObjectURL(url); };
          return audio.play().then(function () { self.ttsPlaying = true; });
        })
        .catch(function () { self.ttsError = "Voice playback isn't available right now."; })
        .finally(function () { self.ttsBusy = false; });
    },
    stopTts() {
      try { if (this._ttsAudio) { this._ttsAudio.pause(); this._ttsAudio.currentTime = 0; } } catch (e) { /* noop */ }
      this.ttsPlaying = false;
    },
    onTtsVoiceChange() {
      try { if (typeof localStorage !== "undefined") localStorage.ciTtsVoice = this.ttsVoice; } catch (e) { /* noop */ }
      this.stopTts();   // a new voice applies to the NEXT Listen
    },
    // ----- Hero RESULT CARD helpers (the structured table an analytical answer carries) -----
    // Parse a cell as a number, tolerating "$790,400.53" / "(1,234)" money strings. A string with
    // any OTHER character (letters, "#", "/") is NOT numeric — the old strip-everything parser read
    // customer ids like "XA430" as 430, so the grouped card literally summed Customer IDs. Null = not numeric.
    aiCellNum(v) {
      if (v == null) return null;
      if (typeof v === "number") return isFinite(v) ? v : null;
      var s = String(v).trim();
      if (!s || /[^0-9$,.\-%()\s]/.test(s)) return null;
      s = s.replace(/[$,%\s]/g, "");
      if (/^\(.*\)$/.test(s)) s = "-" + s.slice(1, -1);
      if (!s || s === "-" || s === "." || s === "-.") return null;
      var n = Number(s);
      return isFinite(n) ? n : null;
    },
    // Column-DETECTION variant: only real JSON numbers or money-formatted strings ("$…" / "1,234.56")
    // qualify a column as numeric. Plain digit STRINGS stay out — an order-number column ("60685")
    // must never become a summed measure column.
    aiColCellNum(v) {
      if (typeof v === "number") return isFinite(v) ? v : null;
      if (typeof v === "string" && /[$,.]/.test(v)) return this.aiCellNum(v);
      return null;
    },
    // Display: every number-typed cell gets thousands separators (2 decimals only when fractional) —
    // server-materialized tables carry raw numbers in ALL numeric columns, not just the measure.
    // String cells render as sent (a model-built table may pre-format its own values).
    fmtAiCell(v, ci) {
      if (typeof v === "number" && isFinite(v)) {
        var money = ci != null && this.aiMoneyCols.indexOf(ci) !== -1;
        // A year must never render as "2,024" — plausible whole-dollar values in this band are rare
        // enough that skipping the separator there is the lesser evil (money columns keep the $ + commas).
        if (!money && Number.isInteger(v) && v >= 1900 && v <= 2100) return String(v);
        var s = v.toLocaleString(undefined, { minimumFractionDigits: money ? 2 : (v % 1 ? 2 : 0), maximumFractionDigits: 2 });
        return money ? "$" + s : s;
      }
      return v == null ? "" : String(v);
    },
    // Compare two result-table cells for sorting: numbers (incl. "$1,234") numerically, else natural
    // string compare (so "AL105" < "AL120" and numbers-in-text order sensibly).
    _aiCmp(a, b) {
      var na = this.aiCellNum(a), nb = this.aiCellNum(b);
      if (na !== null && nb !== null) return na - nb;
      if (na !== null) return -1;
      if (nb !== null) return 1;
      return String(a == null ? "" : a).localeCompare(String(b == null ? "" : b), undefined, { numeric: true, sensitivity: "base" });
    },
    // Flat (ungrouped) rows of ANY result table in the active sort order. Split out of the
    // aiFlatRows computed so the 📊 Presentation can render the group-FILTERED table through the
    // identical sort logic — reading the computed there silently re-introduced every excluded group.
    _flatAiRows(t) {
      if (!t || !Array.isArray(t.rows)) return [];
      if (!this.aiSort) return t.rows;
      var s = this.aiSort, self = this;
      return t.rows.slice().sort(function (a, b) { return self._aiCmp((a || [])[s.col], (b || [])[s.col]) * s.dir; });
    },
    // Grouped view of ANY result table. Split out of the aiTableGroups computed for the same reason:
    // one grouper, so the card, the workbook and the presentation can never drift apart.
    _groupAiTable(t) {
      if (!t || !Array.isArray(t.columns) || !Array.isArray(t.rows)) return null;
      if (t.columns.length <= 2 || t.rows.length <= 1) return null;
      // Object.create(null): a model-authored first-column value like "toString" would otherwise hit
      // an inherited Object.prototype member, skip the array init, and crash the whole render pass.
      var order = [], byKey = Object.create(null);
      for (var r = 0; r < t.rows.length; r++) {
        var row = t.rows[r] || [];
        var k = String(row[0] == null ? "" : row[0]);
        if (!byKey[k]) { byKey[k] = []; order.push(k); }
        byKey[k].push(row);
      }
      if (!(order.length > 1 && order.length < t.rows.length)) return null;
      var numCols = this.aiTableNumCols, self = this;
      var groups = order.map(function (k) {
        var rows = byKey[k], sums = {};
        numCols.forEach(function (ci) {
          if (ci === 0) return;
          var s = 0, any = false;
          rows.forEach(function (row) { var n = self.aiCellNum((row || [])[ci]); if (n !== null) { s += n; any = true; } });
          if (any) sums[ci] = s;
        });
        return { key: k, rows: rows, sums: sums };
      });
      // Sorting: sort the detail rows WITHIN each group by the clicked column; reorder the GROUPS by
      // their summed value of that column (numeric col) or by group key (column 0). A text detail
      // column leaves the group order at its default (biggest-first) and only sorts within groups.
      var sort = this.aiSort;
      if (sort) {
        var col = sort.col, dir = sort.dir;
        groups.forEach(function (g) { g.rows = g.rows.slice().sort(function (a, b) { return self._aiCmp((a || [])[col], (b || [])[col]) * dir; }); });
        var numericCol = numCols.indexOf(col) !== -1 && col !== 0;
        groups = groups.slice().sort(function (ga, gb) {
          if (col === 0) return self._aiCmp(ga.key, gb.key) * dir;
          if (numericCol) return ((ga.sums[col] || 0) - (gb.sums[col] || 0)) * dir;
          return 0;   // text detail column → keep the default group order
        });
      }
      return groups;
    },
    // Click a result-table header: ascending → descending → off (three-state).
    sortAiCol(i) {
      if (this.aiSort && this.aiSort.col === i) {
        this.aiSort = this.aiSort.dir === 1 ? { col: i, dir: -1 } : null;
      } else {
        this.aiSort = { col: i, dir: 1 };
      }
    },
    aiBarPct(row) {
      var idx = this.aiTableNumIdx, max = this.aiTableMax;
      if (idx < 0 || !max) return 0;
      var n = this.aiCellNum((row || [])[idx]);
      if (n === null || n <= 0) return 0;
      return Math.max(2, Math.round((n / max) * 100));
    },
    // Bar color: the row's first cell often names a rep — reuse their territory color when known.
    aiBarColor(row) {
      var label = String((row || [])[0] || "");
      var m = this.repTerritoryColors;
      return (m && m[label]) || "#9e2436";
    },
    toggleAiGroup(gi) {
      this.$set(this.aiTableOpen, gi, !this.aiTableOpen[gi]);
    },
    toggleAllAiGroups() {
      var open = !this.allAiGroupsOpen, next = {};
      (this.aiTableGroups || []).forEach(function (g, i) { next[i] = open; });
      this.aiTableOpen = next;
    },
    // Group bar: the group's measure-column SUM relative to the largest group (its own 100%).
    aiGroupBarPct(g) {
      var idx = this.aiTableNumIdx, max = this.aiGroupMax;
      if (idx < 0 || !max) return 0;
      var v = g && g.sums[idx];
      if (v == null || v <= 0) return 0;
      return Math.max(2, Math.round((v / max) * 100));
    },
    // Excel-safe worksheet name: ≤31 chars, no : \ / ? * [ ], never empty, deduped with a numeric suffix.
    _xlsxSheetName(name, used) {
      var s = String(name == null ? "" : name).replace(/[:\\/?*[\]]/g, " ").replace(/\s+/g, " ").trim().slice(0, 31) || "Sheet";
      var base = s, n = 2;
      while (used[s.toLowerCase()]) { var suffix = " (" + n + ")"; s = base.slice(0, 31 - suffix.length) + suffix; n++; }
      used[s.toLowerCase()] = true;
      return s;
    },
    // Rows → worksheet: header + data, the measure column as REAL numbers (so Excel can sum/sort),
    // with column widths sized to content.
    _xlsxSheet(columns, rows, numIdx) {
      var self = this;
      var aoa = [columns.slice()];
      rows.forEach(function (r) {
        aoa.push((r || []).map(function (c, ci) {
          if (ci === numIdx) { var n = self.aiCellNum(c); if (n !== null) return n; }
          return c == null ? "" : c;
        }));
      });
      var ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = columns.map(function (c, ci) {
        var w = String(c || "").length;
        rows.forEach(function (r) { var l = String((r || [])[ci] == null ? "" : (r || [])[ci]).length; if (l > w) w = l; });
        return { wch: Math.min(50, Math.max(10, w + 2)) };
      });
      return ws;
    },
    // The distinct first-column GROUP keys of the result table, in first-appearance order — but ONLY when
    // the table is genuinely grouped (col 0 repeats, e.g. one Sales Rep across 10 customer rows). A plain
    // ranking (all col-0 values unique) returns [] (nothing to pick).
    _aiTableGroups() {
      var t = this.aiTable;
      if (!t) return [];
      var cols = t.columns || [], rows = t.rows || [];
      if (!(cols.length > 2 && rows.length > 1)) return [];
      var order = [], seen = Object.create(null);
      rows.forEach(function (r) {
        var k = String((r || [])[0] == null ? "" : (r || [])[0]);
        if (!seen[k]) { seen[k] = true; order.push(k); }
      });
      return (order.length > 1 && order.length < rows.length) ? order : [];
    },
    // ⬇ Excel: on a GROUPED table, open a picker (which groups to include) first; a flat ranking exports
    // straight away (no groups to choose).
    exportAiTable() {
      if (!this.aiTable || typeof document === "undefined") return;
      var groups = this._aiTableGroups();
      if (!groups.length) { this._writeAiWorkbook(null); return; }
      this.aiExcelModal = { open: true, groups: groups.map(function (k) { return { key: k, checked: true }; }) };
    },
    // The modal's ⬇ Export: build the workbook from ONLY the checked groups.
    confirmExportAiTable() {
      var checked = (this.aiExcelModal.groups || []).filter(function (g) { return g.checked; }).map(function (g) { return g.key; });
      this.aiExcelModal.open = false;
      if (!checked.length) return;
      this._writeAiWorkbook(new Set(checked));
    },
    setAiExcelAll(v) { (this.aiExcelModal.groups || []).forEach(function (g) { g.checked = !!v; }); },
    // Download the result table as a real .xlsx WORKBOOK — sheet 1 = the (optionally group-filtered) full
    // card, then one sheet PER GROUP. includeKeys = null → every group; a Set → only those group keys.
    _writeAiWorkbook(includeKeys) {
      var t = this.aiTable;
      if (!t || typeof document === "undefined") return;
      var cols = (t.columns || []).map(function (c) { return String(c == null ? "" : c); });
      var key0 = function (r) { return String((r || [])[0] == null ? "" : (r || [])[0]); };
      var rows = (t.rows || []).filter(function (r) { return !includeKeys || includeKeys.has(key0(r)); });
      if (!rows.length) return;
      var numIdx = this.aiTableNumIdx;
      var used = Object.create(null);   // null-proto: sheet names / group keys must never hit Object.prototype
      var wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, this._xlsxSheet(cols, rows, numIdx), this._xlsxSheetName(t.title || "All Results", used));
      // Grouped table (first column repeats) → a sheet per (included) group, in first-appearance order.
      if (cols.length > 2 && rows.length > 1) {
        var order = [], byKey = Object.create(null);
        rows.forEach(function (r) {
          var k = key0(r);
          if (!byKey[k]) { byKey[k] = []; order.push(k); }
          byKey[k].push(r);
        });
        if (order.length > 1 && order.length < rows.length) {
          var self = this;
          order.forEach(function (k) {
            XLSX.utils.book_append_sheet(wb, self._xlsxSheet(cols, byKey[k], numIdx), self._xlsxSheetName(k, used));
          });
        }
      }
      var name = ((t.title || "ai-results").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "ai-results") + ".xlsx";
      XLSX.writeFile(wb, name);
    },
    // ----- Filters card collapse to the right edge (desktop pill + mobile edge tab) -----
    collapseFilters() {
      this.flushPendingDashSave();   // a debounced save armed pre-dock must land via the OLD routing, not after the swap
      // Capture edits made in the CURRENT scenario before leaving it (e.g. Email already docked → the edits
      // belong to the "email" preset). The base arrangement is persisted by the normal autosave.
      var oldKey = this.dockKey;
      if (oldKey && this.dockScope && this.canPublishSharedLayout) { this.writeDockPreset(oldKey); }
      this.filtersDocked = true;
      try { if (typeof localStorage !== "undefined") localStorage.ciFiltersDocked = "1"; } catch (e) { /* noop */ }
      // Scenario preset for the NEW state ("filters" or "email+filters") if saved; else just strip the card
      // from the live grid (mobile reacts via effectiveLayout; the stored layout keeps it).
      if (!(this.dockScope && this.applyDockPreset(this.dockKey))) {
        this.dashLayout = (this.dashLayout || []).filter(function (it) { return it.i !== "filters"; });
        this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
      }
      if (this.dockScope && this.canPublishSharedLayout) { this.syncDockPresetFromLive(); }   // seed/refresh this scenario's slot
      this.persistDash();
    },
    restoreFilters() {
      this.flushPendingDashSave();   // a debounced save armed pre-undock must land via the OLD routing, not after the swap
      // Capture edits made while docked into THAT scenario's preset before leaving it.
      var oldKey = this.dockKey;   // "filters" or "email+filters"
      if (oldKey && this.dockScope && this.canPublishSharedLayout) { this.writeDockPreset(oldKey); }
      this.filtersDocked = false;
      try { if (typeof localStorage !== "undefined") localStorage.ciFiltersDocked = "0"; } catch (e) { /* noop */ }
      // Entering "email"-only-docked with a saved preset → apply it directly. Otherwise re-apply the active
      // layout: its final dock-sync rebuilds the right state (filters card back at its saved spot, Email
      // still stripped while docked).
      var nk = this.dockKey;   // "" or "email"
      if (nk && this.dockScope && this.applyDockPreset(nk)) {
        this.saveDashLayoutNow();
        this.persistDash();
      } else {
        this.applyLayout(this.activeLayout);
        this.persistDash();
      }
      this.flashCard("filters");
    },
    // 💡 On-demand AI sales perspective on a customer's note + specs (customer-level), fetched + shown in a popover.
    onCustomerPerspective(payload) {
      var c = payload && payload.customer, ev = payload && payload.event;
      if (!c) return;
      this.closeNotePop(); this.closeSpecPop();
      var p = this._popAnchor(ev, 340);
      this.perspPop.top = p.top; this.perspPop.left = p.left;
      this.perspPop.id = c.customerId || "";
      this.perspPop.name = (c.customerId || "") + (c.customerName ? " — " + c.customerName : "");
      this.perspPop.headline = ""; this.perspPop.body = ""; this.perspPop.actions = [];
      this.perspPop.busy = true;
      this.perspPop.open = true;
      this._fitPopToViewport("perspPopPanel", this.perspPop);
      var self = this;
      this.$nextTick(function () { if (typeof document !== "undefined") document.addEventListener("mousedown", self._perspDocClose); });
      httpClient.post("/report/notation-perspective", { customerCode: c.customerId, level: "customer" })
        .then(function (res) {
          var d = res && res.data, pv = d && d.perspective;
          if (d && d.ok && pv) {
            self.perspPop.headline = pv.headline || "";
            self.perspPop.body = pv.body || "";
            self.perspPop.actions = Array.isArray(pv.actions) ? pv.actions : [];
          } else {
            self.perspPop.body = (d && d.message) || "No perspective available for this customer yet.";
          }
        })
        .catch(function () { self.perspPop.body = "Couldn't load a perspective right now — please try again."; })
        .finally(function () { self.perspPop.busy = false; self._fitPopToViewport("perspPopPanel", self.perspPop); });
    },
    closePerspPop() {
      this.perspPop.open = false;
      if (typeof document !== "undefined") document.removeEventListener("mousedown", this._perspDocClose);
    },
    _perspDocClose(e) {
      var t = e && e.target;
      if (t && t.closest && (t.closest(".ci-persppop") || t.closest(".cb-persp"))) return;
      this.closePerspPop();
    },
    // 💡 "Why unassigned" — fetch the diagnostic for the territory-unassigned orders in the current view.
    onUnassignedDiag(r, ev) {
      this.closeNotePop(); this.closeSpecPop(); this.closePerspPop();
      var p = this._popAnchor(ev, 440);
      this.unassignedPop.top = p.top; this.unassignedPop.left = p.left;
      this.unassignedPop.data = null; this.unassignedPop.busy = true; this.unassignedPop.showAll = false; this.unassignedPop.open = true;
      this._fitPopToViewport("unassignedPopPanel", this.unassignedPop);
      var self = this;
      this.$nextTick(function () { if (typeof document !== "undefined") document.addEventListener("mousedown", self._unDocClose); });
      httpClient.get("/report/customer-insights/unassigned-diagnostic", { params: this.buildParams(false) })
        .then(function (res) { self.unassignedPop.data = (res && res.data) ? res.data : { ok: false }; })
        .catch(function () { self.unassignedPop.data = { ok: false, message: "Couldn't load the diagnostic right now — please try again." }; })
        .finally(function () { self.unassignedPop.busy = false; self._fitPopToViewport("unassignedPopPanel", self.unassignedPop); });
    },
    closeUnassignedPop() {
      this.unassignedPop.open = false;
      if (typeof document !== "undefined") document.removeEventListener("mousedown", this._unDocClose);
    },
    _unDocClose(e) {
      var t = e && e.target;
      if (t && t.closest && (t.closest(".ci-unpop") || t.closest(".ci-unassigned-diag"))) return;
      this.closeUnassignedPop();
    },
    toggleUnassignedAll() {
      this.unassignedPop.showAll = !this.unassignedPop.showAll;
      this._fitPopToViewport("unassignedPopPanel", this.unassignedPop);
    },
    openUnassignedEmail(target) {
      this.closeUnassignedPop();
      this.unEmail = { open: true, busy: false, sent: false, note: "", error: "", target: (target === "parts" ? "parts" : "it") };
    },
    closeUnassignedEmail() { this.unEmail.open = false; },
    // Deep-link a chip → open Account Setup straight to Sales Territories with the rep pre-filled in the Add row.
    addRepToTerritoryMap(rep) {
      this.closeUnassignedPop();
      if (this.$root) this.$root.$emit("open-account-setup", { jump: "territories", addRep: rep });
    },
    sendUnassignedEmail() {
      var self = this;
      this.unEmail.busy = true; this.unEmail.error = "";
      httpClient.post("/report/unassigned-email", { filters: this.buildParams(false), note: this.unEmail.note, target: this.unEmail.target })
        .then(function (res) {
          var d = res && res.data;
          if (d && d.ok) { self.unEmail.sent = true; setTimeout(function () { self.unEmail.open = false; }, 1600); }
          else { self.unEmail.error = (d && d.message) || "Couldn't send the email."; }
        })
        .catch(function () { self.unEmail.error = "Couldn't send the email — please try again."; })
        .finally(function () { self.unEmail.busy = false; });
    },
    // Build the professor-style HTML that shows exactly how a KPI's % vs prior period was derived, step by step.
    // Prior window mirrors the backend: the equal-length period immediately before (from − span … from, span = to − from).
    _buildKpiMath(kind) {
      var k = this.kpis || {}, p = this.kpisPrior || {}, esc = this._esc, self = this;
      var MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      var fmtD = function (d) { return MON[d.getMonth()] + " " + d.getDate() + ", " + d.getFullYear(); };
      var curFromT = String(this.filters.from), curToT = String(this.filters.to), pf = "", pt = "", spanDays = 0;
      var basisLabel = this.filters.dateBasis === "invoice" ? "invoice date" : "order date";
      try {
        var fromD = new Date(curFromT + "T00:00:00"), toD = new Date(curToT + "T00:00:00");
        var spanMs = toD.getTime() - fromD.getTime();
        pf = fmtD(new Date(fromD.getTime() - spanMs)); pt = fmtD(fromD);
        curFromT = fmtD(fromD); curToT = fmtD(toD);
        spanDays = Math.round(spanMs / 86400000) + 1;
      } catch (e) { /* noop */ }
      var isMoney = kind !== "orders";
      var fmt = function (v) { return isMoney ? self.fmtMoney(v) : self.fmtNum(Math.round(v)); };
      var cur, prior, label, pre = "";
      if (kind === "sales") { cur = Number(k.totalSales) || 0; prior = Number(p.totalSales) || 0; label = "total sales"; }
      else if (kind === "orders") { cur = Number(k.orderCount) || 0; prior = Number(p.orderCount) || 0; label = "number of orders"; }
      else {
        var cs = Number(k.totalSales) || 0, co = Number(k.orderCount) || 0, ps = Number(p.totalSales) || 0, po = Number(p.orderCount) || 0;
        cur = co ? cs / co : 0; prior = po ? ps / po : 0; label = "average order value";
        pre = '<p style="margin:8px 0;"><b>First, the average for each period</b> (average = total sales ÷ orders):<br>'
            + 'This period: ' + esc(self.fmtMoney(cs)) + ' ÷ ' + esc(self.fmtNum(co)) + ' = <b>' + esc(self.fmtMoney(cur)) + '</b><br>'
            + 'Prior period: ' + esc(self.fmtMoney(ps)) + ' ÷ ' + esc(self.fmtNum(po)) + ' = <b>' + esc(self.fmtMoney(prior)) + '</b></p>';
      }
      // Inline styles throughout — this HTML is injected via v-html, which carries NO scoped-CSS attribute, so
      // class-based (scoped) rules would not reach it. The wrapper .ci-kpi-math sets the base color/font (inherited).
      var HH = 'style="font-weight:800;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#b9651a;margin-bottom:8px;"';
      var PP = 'style="margin:8px 0;"';
      if (!prior) {
        return '<div ' + HH + '>No prior period to compare</div><p ' + PP + '>The equal-length period immediately before your selected range has no ' + esc(label) + ', so a percent change can\'t be calculated (we\'d be dividing by zero).</p>';
      }
      var change = cur - prior;
      var pctExact = (change / Math.abs(prior)) * 100;
      var up = change >= 0, arrow = up ? "↑" : "↓", sign = up ? "+" : "−";
      var changeStr = sign + fmt(Math.abs(change));
      var resultColor = up ? "#2e7d5b" : "#c0392b";
      var row = function (lbl, dates, val) {
        return '<div style="display:flex;gap:8px;align-items:baseline;">'
          + '<span style="font-weight:700;min-width:74px;flex:0 0 auto;">' + esc(lbl) + '</span>'
          + '<span style="opacity:.7;font-size:11px;flex:1 1 auto;">' + esc(dates) + '</span>'
          + '<span style="opacity:.5;flex:0 0 auto;">=</span>'
          + '<span style="font-weight:800;flex:0 0 auto;">' + esc(val) + '</span></div>';
      };
      return '<div ' + HH + '>How the ' + arrow + ' ' + Math.abs(pctExact).toFixed(0) + '% “vs prior period” was calculated</div>'
        + '<p ' + PP + '><b>“Period” = the date range selected in your filters.</b> This period is <b>' + esc(curFromT) + ' – ' + esc(curToT) + '</b>' + (spanDays > 0 ? ' (' + spanDays + ' days, by ' + esc(basisLabel) + ')' : ' (by ' + esc(basisLabel) + ')') + '. We compare its <b>' + esc(label) + '</b> against the <b>equal-length period immediately before it</b>' + (spanDays > 0 ? ' — the previous ' + spanDays + ' days' : '') + '.</p>'
        + '<div style="display:flex;flex-direction:column;gap:5px;margin:8px 0;padding:8px 10px;background:rgba(230,126,34,.10);border-radius:8px;">'
        +   row("This period", curFromT + " – " + curToT, fmt(cur))
        +   row("Prior period", pf + " – " + pt, fmt(prior))
        + '</div>'
        + pre
        + '<p ' + PP + '><b>Step 1 — find the change.</b><br>change = this period − prior period<br>= ' + esc(fmt(cur)) + ' − ' + esc(fmt(prior)) + '<br>= <b>' + esc(changeStr) + '</b></p>'
        + '<p ' + PP + '><b>Step 2 — write that change as a percent of the prior period.</b><br>percent = ( change ÷ prior period ) × 100<br>= ( ' + esc(changeStr) + ' ÷ ' + esc(fmt(Math.abs(prior))) + ' ) × 100<br>= <b>' + sign + Math.abs(pctExact).toFixed(2) + '%</b></p>'
        + '<p ' + PP + '><b>Step 3 — round to a whole percent.</b> → <b style="color:' + resultColor + ';">' + arrow + ' ' + Math.abs(pctExact).toFixed(0) + '%</b><br><span style="opacity:.7;font-style:italic;font-size:11px;">Reading it: this period is ' + (up ? "higher" : "lower") + ' than the one before it.</span></p>';
    },
    // Smart tooltip for a struck-through (irrelevant) filter: explains WHY it's dead + names the likeliest cause.
    // A filter goes dead when, combined with the OTHER (cross-dimension) filters, it yields zero rows. Same-
    // dimension chips are OR'd together so they're never the cause; among the still-relevant other filters we
    // name the MOST SPECIFIC one (a Part/Customer usually drives the empty) as the likely culprit.
    _causeRank(key) { return ({ part: 1, partDescription: 2, machineCodeDescription: 3, machineCodePrefix: 4, machineCodeCategory: 5, customer: 6, country: 7, state: 8, territory: 9, salesRep: 10, status: 11, invoiced: 12 })[key] || 99; },
    // The still-relevant OTHER-dimension filters that constrain chip c, most-specific first (the top one is the
    // likeliest reason c went dead — a Part/Customer usually drives the empty; same-dimension chips are OR'd, excluded).
    _causesFor(c) {
      var self = this;
      return (this.activeChips || []).filter(function (x) { return x && x.key !== c.key && !self.chipIrrelevant(x); })
        .sort(function (a, b) { return self._causeRank(a.key) - self._causeRank(b.key); });
    },
    _esc(s) { return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;"); },
    _chipKey(c) { return (c && c.key) + "|" + ((c && c.val) || ""); },
    hoverChip(c) { this.hoverChipKey = this._chipKey(c); },
    unhoverChip() { this.hoverChipKey = ""; },
    isChipHovered(c) { return !!c && this.hoverChipKey === this._chipKey(c); },
    // Custom HTML "comment" tooltip for a dead (struck-through) or culprit filter — expands inline on row hover.
    chipTipHtml(c) {
      if (!c) return "";
      var self = this, esc = this._esc;
      if (this.chipIrrelevant(c)) {
        var causes = this._causesFor(c);
        if (!causes.length) return '<div class="ci-tip-h">Filter not affecting results</div><div class="ci-tip-b">No orders match <b>' + esc(c.label) + ': ' + esc(c.text) + '</b> in the selected date range.</div>';
        var top = causes[0];
        var items = causes.map(function (x) {
          var isTop = x.key === top.key && (x.val || "") === (top.val || "");
          return '<li' + (isTop ? ' class="ci-tip-cause"' : '') + '>' + esc(x.label) + ': ' + esc(x.text) + (isTop ? ' <span class="ci-tip-tag">likely cause</span>' : '') + '</li>';
        }).join("");
        return '<div class="ci-tip-h">Filter not affecting results</div>'
          + '<div class="ci-tip-b">No orders match <b>' + esc(c.label) + ': ' + esc(c.text) + '</b> combined with:</div>'
          + '<ul class="ci-tip-l">' + items + '</ul>'
          + '<div class="ci-tip-f">Remove this filter, or <b>' + esc(top.label) + '</b>, to bring back the unused filters.</div>';
      }
      if (this.chipIsCulprit(c)) {
        var affected = (this.activeChips || []).filter(function (x) {
          if (!self.chipIrrelevant(x)) return false;
          var t = self._causesFor(x)[0];
          return t && t.key === c.key && (t.val || "") === (c.val || "");
        });
        return '<div class="ci-tip-h">Likely cause</div>'
          + '<div class="ci-tip-b"><b>' + esc(c.label) + ': ' + esc(c.text) + '</b> is knocking these filters out:</div>'
          + '<ul class="ci-tip-l">' + affected.map(function (x) { return '<li>' + esc(x.label) + ': ' + esc(x.text) + '</li>'; }).join("") + '</ul>'
          + '<div class="ci-tip-f">Remove it to bring them back.</div>';
      }
      return "";
    },
    // A relevant filter is a "culprit" (highlighted) if it's the top cause of at least one struck-through filter.
    chipIsCulprit(c) {
      if (!c || this.chipIrrelevant(c)) return false;
      var self = this;
      return (this.activeChips || []).some(function (x) {
        if (!self.chipIrrelevant(x)) return false;
        var top = self._causesFor(x)[0];
        return top && top.key === c.key && (top.val || "") === (c.val || "");
      });
    },
    // Choose a customer from the in-view list (dropdown) — sets the filter, which
    // switches the company for the Sales role via the filters.customer watch.
    pickCustomer(id) {
      if (!id || this.filters.customer.indexOf(id) >= 0) return;
      this.filters.customer.push(id);
      this.page = 1;
      this.load();
      this.loadDetails();
    },
    // A grouped-search suggestion was chosen → set its structured filter (single
    // value per category for v1) and apply immediately. Chips reflect it via activeChips.
    onSuggestionSelect(item) {
      if (!item || !item.filterField) return;
      var key = item.filterField; var val = item.filterValue == null ? "" : String(item.filterValue);
      if (Array.isArray(this.filters[key])) {
        if (val && this.filters[key].indexOf(val) < 0) this.filters[key].push(val);
      } else {
        this.filters[key] = val;
      }
      // Activity trail: a filter committed from the grouped search or a card click.
      recordAction("report-filter-select", key + "=" + val);
      this.page = 1;
      this.load();
      this.loadDetails();
    },
    // Header date-shortcut → set the window and apply.
    onHeaderDateRange(payload) {
      if (!payload) return;
      if (payload.from) this.filters.from = payload.from;
      if (payload.to) this.filters.to = payload.to;
      this.activePreset = "";   // a header date-picker change is a custom range, not a named preset
      this.page = 1;
      this.load();
      this.loadDetails();
    },
    // Broadcast the report's current date window so the header's From/To pickers mirror it.
    broadcastDateRange() {
      this.$root.$emit("report-date-current", { from: this.filters.from, to: this.filters.to });
    },
    // Broadcast the report's currently-selected customer so the header's Sales-rep parts-request
    // modal can prefill the CUSTOMER company. Only a single, unambiguous selection prefills; 0 or
    // 2+ selected clears it. Name resolves from byCustomer (falls back to the dropdown label).
    broadcastCustomerContext() {
      var arr = this.filters.customer || [];
      // Machine/part text from the current filters → prefills the parts-request form's Machine field + request.
      // [].concat handles both the array shape and a query-seeded string; take the most descriptive non-empty group.
      var f = this.filters;
      var pick = function (groups) {
        for (var i = 0; i < groups.length; i++) {
          var a = [].concat(groups[i] || []).filter(Boolean);
          if (a.length) return a.join(", ");
        }
        return "";
      };
      var machineText = pick([f.machineCodeDescription, f.machineCodePrefix, f.machineCodeCategory]);
      var partText = pick([f.partDescription, f.part]);
      var partCode = String(([].concat(f.part || []).filter(Boolean))[0] || "");   // primary part code → Head resolves machine/assembly
      // Average past-order quantity for that part (SUM Qty / distinct orders). byPart's wherePart EXCLUDES the
      // Part filter, so this record reflects the customer's full history for the part, not just the filtered slice.
      var avgQty = "";
      if (partCode) {
        var prec = (this.byPart || []).find(function (p) { return p && String(p.partNumber) === partCode; });
        if (prec && Number(prec.orderCount) > 0) avgQty = String(Math.max(1, Math.round(Number(prec.qty) / Number(prec.orderCount))));
      }
      if (arr.length !== 1) {
        this.$root.$emit("report-customer-context", { customerId: "", customerName: "", machineText: "", partText: "", partCode: "", summary: "", avgQty: "" });
        return;
      }
      var id = String(arr[0]);
      var name = "";
      var rec = (this.byCustomer || []).find(function (c) { return c && String(c.customerId) === id; });
      if (rec && rec.customerName) {
        name = String(rec.customerName);
      } else {
        var opt = (this.customerOptions || []).find(function (o) { return String(o.value) === id; });
        if (opt && opt.label) {
          var m = String(opt.label).split(" — ");
          name = m.length > 1 ? m.slice(1).join(" — ") : "";
        }
      }
      // Compose a summative, parts-team-friendly description of what the rep narrowed to (per the sales email
      // chain: who the customer is, their territory/rep, the machine/codes + part they're after, and the window).
      var terr = pick([f.territory]) || (rec && rec.territory ? String(rec.territory) : "");
      var rep = pick([f.salesRep]) || (rec && rec.salesRep ? String(rec.salesRep) : "");
      var s = "Parts request for " + (name || id) + (name && id ? " (" + id + ")" : "") + ".";
      var who = [];
      if (terr) who.push(terr + " territory");
      if (rep) who.push("rep " + rep);
      if (who.length) s += " " + who.join(", ") + ".";
      if (machineText) s += " Machine / codes: " + machineText + ".";
      if (partText) s += " Part(s) of interest: " + partText + ".";
      if (f.from && f.to) s += " Based on " + (f.dateBasis === "invoice" ? "invoice" : "order") + " dates " + f.from + " to " + f.to + ".";
      if (f.invoiced && f.invoiced !== "all") s += (f.invoiced === "invoiced" ? " Invoiced only." : " Not-invoiced only.");
      s += " Please advise on availability, pricing, and lead time.";
      this.$root.$emit("report-customer-context", { customerId: id, customerName: name, machineText: machineText, partText: partText, partCode: partCode, summary: s, avgQty: avgQty });
    },
    // Email Insights opt-in changed → persist in the per-user dashboard blob.
    onEmailSetLinked(v) {
      this.emailLinked = !!v;
      this.persistDash();
    },
    // (Email Insights "Quote this part" now opens a NEW TAB directly from the EmailInsights component's click
    // gesture — see EmailInsights.openQuote — so no parent handler is needed.)
    // The active company selection IS a customer narrowing — make it VISIBLE (user 2026-08-01): when the
    // report runs while "acting as" a customer company (header selector pick, now possible for territory
    // reps), show that customer as a normal chip in the filter rail + the Customer ID(s) card instead of
    // narrowing invisibly (buildParams sends companyScope either way; the chip only surfaces it). Seeding
    // the chip also completes the existing symmetry for reps: the filters.customer watcher already maps
    // chips → company (syncCompany), so ×-ing the seeded chip returns them home to NC999 with no extra
    // code. NC999 = home = no chip. The watcher's syncCompany(cc) after a seed no-ops in Head
    // (onReportCompanySwitch bails when the company is already active), so this cannot loop.
    _seedCompanyChip() {
      var cc = (typeof localStorage !== "undefined") ? String(localStorage.companyCode || "").trim() : "";
      if (!cc || /^NC999$/i.test(cc)) return;
      var r = (this.$store && this.$store.getters) ? this.$store.getters.effectiveRoleName : "";
      if (!r || r === "Customer") return;   // Customer role: no selector, no chips
      if (Array.isArray(this.filters.customer) && this.filters.customer.indexOf(cc) < 0) {
        this.filters.customer.push(cc);
      }
    },
    // The active company changed (header selector or in-report customer switch) without a page reload →
    // refetch so the report re-scopes to the new company (buildParams now sends the new companyScope).
    onCompanyChanged() {
      this._seedCompanyChip();   // keep the chip in step with a silent company switch
      this.page = 1;
      this.load();
      this.loadDetails();
      this.loadSavedCarts(); this.loadOrdersInProcess();
    },
    // Pure date-range for a shortcut key (no side effects): YTD, qN calendar quarter, N calendar years,
    // rN = TRAILING N years ending TODAY (rolling window), today/yesterday/week/month.
    _shortcutRange(kind) {
      var now = new Date(); var y = now.getFullYear();
      var iso = function (d) { return d.toISOString().slice(0, 10); };
      var from, to = iso(now);
      if (kind === "today") { from = iso(now); }
      else if (kind === "yesterday") { var yd = new Date(now.getTime()); yd.setDate(now.getDate() - 1); from = iso(yd); to = from; }
      else if (kind === "week") { var w = new Date(now.getTime()); w.setDate(now.getDate() - 6); from = iso(w); }
      else if (kind === "month") { var mo = new Date(now.getTime()); mo.setMonth(now.getMonth() - 1); from = iso(mo); }
      else if (kind === "q1") { from = y + "-01-01"; to = y + "-03-31"; }
      else if (kind === "q2") { from = y + "-04-01"; to = y + "-06-30"; }
      else if (kind === "q3") { from = y + "-07-01"; to = y + "-09-30"; }
      else if (kind === "q4") { from = y + "-10-01"; to = y + "-12-31"; }
      else if (kind === "ytd") { from = y + "-01-01"; }
      else if (typeof kind === "string" && kind.charAt(0) === "r") { var rn = Number(kind.slice(1)) || 1; var rd = new Date(now.getTime()); rd.setFullYear(now.getFullYear() - rn); from = iso(rd); to = iso(now); }   // rN = TRAILING N years ending TODAY (rolling window)
      else { var n = Number(kind) || 1; from = (y - n + 1) + "-01-01"; to = y + "-12-31"; }   // N = N calendar years (Jan 1 – Dec 31)
      return { from: from, to: to };
    },
    // Date shortcut (drawer): apply the range + reload.
    setDateShortcut(kind) {
      var r = this._shortcutRange(kind);
      this.filters.from = r.from; this.filters.to = r.to;
      this.activePreset = String(kind);   // remember the applied preset so its button stays highlighted (cleared by a manual date edit / reset)
      this.page = 1; this.load(); this.loadDetails();
    },
    // Close the QTR / YEAR date-preset popups when a mousedown lands outside any .ci-preset-pop-wrap.
    onPresetDocClick(e) {
      var t = e && e.target;
      if (!t || !t.closest || !t.closest(".ci-preset-pop-wrap")) { this.qtrOpen = false; this.yearOpen = false; this.rollOpen = false; }
      // Close the active-filter unselect dropdown when clicking anywhere outside it.
      if (this.openChipGroupKey && (!t || !t.closest || !t.closest(".ci-frl-chipgrp"))) this.openChipGroupKey = null;
    },
    // Toggle an active-filter group's unselect dropdown (rail). Only one open at a time.
    toggleChipGroup(key) { this.openChipGroupKey = (this.openChipGroupKey === key) ? null : key; },
    // "Attribute by" ⓘ hover popup. Body-appended (see mounted) + position:fixed at the icon's viewport coords so
    // the rail's overflow:auto never clips it; flips to the left of the rail if there isn't room on the right.
    showRepBasisHelp(e) {
      clearTimeout(this._rbHelpT);
      // Ensure the popup is a direct child of <body> BEFORE we show it — the mount-time append can miss if the
      // filters card mounted later, and inside the rail (.ci-frl-body overflow:auto) it would be clipped. Idempotent.
      var panel = this.$refs.repBasisHelpPanel;
      if (panel && document.body && panel.parentNode !== document.body) {
        try { document.body.appendChild(panel); } catch (err) { /* noop */ }
      }
      var el = e && (e.currentTarget || e.target);
      var W = 330;
      var vw = (typeof window !== "undefined") ? window.innerWidth : 1200;
      var vh = (typeof window !== "undefined") ? window.innerHeight : 800;
      var left = 24, top = 120;
      if (el && el.getBoundingClientRect) {
        var r = el.getBoundingClientRect();
        left = r.right + 10;                                       // to the RIGHT of the ⓘ
        if (left + W > vw - 8) left = Math.max(8, r.left - W - 10); // flip to the left if no room
        top = Math.max(8, Math.min(r.top - 6, vh - 340));          // aligned to the ⓘ, kept on-screen
      }
      // Position the node IMPERATIVELY (viewport-anchored) — inline styles win over any stale/transform-affected
      // :style binding once the element has been moved to <body>, so it always lands beside the ⓘ.
      if (panel && panel.style) {
        panel.style.position = "fixed";
        panel.style.top = top + "px";
        panel.style.left = left + "px";
        panel.style.right = "auto";
        panel.style.bottom = "auto";
        panel.style.margin = "0";
      }
      this.repBasisHelp.top = top; this.repBasisHelp.left = left;
      this.repBasisHelp.open = true;
    },
    keepRepBasisHelp() { clearTimeout(this._rbHelpT); this.repBasisHelp.open = true; },
    hideRepBasisHelp() {
      var self = this;
      clearTimeout(this._rbHelpT);
      this._rbHelpT = setTimeout(function () { self.repBasisHelp.open = false; }, 160);   // grace period to move onto the popup
    },
    // Date inputs commit on CHANGE (a complete value), not per-keystroke. Binding
    // <input type=date> with v-model updates on every input event, which reverts
    // partial typing and makes the field feel "stuck" / unchangeable. :value +
    // @change avoids that; empty/invalid intermediate values are ignored.
    onDateInput(which, e) {
      var val = e && e.target ? e.target.value : "";
      if (!val) return;
      this.filters[which] = val;
      this.activePreset = "";   // a manual date edit means no preset is active → clear any highlighted preset
      this.applyFilters();
    },
    // ── Typeable From/To dates (MM/DD/YYYY masked text inputs, replacing the fiddly native date picker) ──
    // The native <input type=date> committed a reload mid-typing (e.g. year 2026→2022 flashed through 0002/0020),
    // and its segments couldn't be cleared/typed freely. These text inputs let you clear on focus, type digits with
    // auto-inserted slashes, and only apply when a COMPLETE valid date is entered (on blur / Enter).
    dateDisplay(iso) {
      if (!iso) return "";
      var m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
      return m ? (m[2] + "/" + m[3] + "/" + m[1]) : String(iso);   // YYYY-MM-DD → MM/DD/YYYY
    },
    onDateTypeMask(e) {
      var el = e && e.target; if (!el) return;
      var d = String(el.value || "").replace(/\D/g, "").slice(0, 8);   // keep up to MMDDYYYY digits
      var out = d.slice(0, 2);
      if (d.length >= 3) out += "/" + d.slice(2, 4);
      if (d.length >= 5) out += "/" + d.slice(4, 8);
      el.value = out;   // auto-insert the slashes as they type
    },
    commitTypedDate(which, e) {
      var el = e && e.target; if (!el) return;
      var m = String(el.value || "").match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      var revert = () => { el.value = this.dateDisplay(this.filters[which]); };
      if (!m) { revert(); return; }   // incomplete / not MM/DD/YYYY → snap back to the current value
      var mm = parseInt(m[1], 10), dd = parseInt(m[2], 10), yy = parseInt(m[3], 10);
      if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yy < 1900 || yy > 2100) { revert(); return; }
      var iso = yy + "-" + (mm < 10 ? "0" + mm : mm) + "-" + (dd < 10 ? "0" + dd : dd);
      var dt = new Date(iso + "T00:00:00");
      if (isNaN(dt.getTime()) || dt.getMonth() + 1 !== mm || dt.getDate() !== dd) { revert(); return; }   // e.g. 02/31
      if (iso === this.filters[which]) return;   // unchanged → no reload
      this.filters[which] = iso;   // (a future date is clamped to today by the maxDateStr watcher)
      this.activePreset = "";
      this.applyFilters();
    },
    removeChip(key, val) {
      if (key === "__dates") {
        const now = new Date();
        this.filters.from = now.getFullYear() + "-01-01";
        this.filters.to = now.toISOString().slice(0, 10);
        this.filters.dateBasis = "order";
        this.activePreset = "ytd";   // removing the date chip restores the default YTD range
      } else if (key === "invoiced") {
        this.filters.invoiced = "all";
      } else if (key === "salesRep") {
        this.crossRemoveRep(String(val));        // also drops the rep's now-unsupported territory chip(s)
      } else if (key === "territory") {
        this.crossRemoveTerritory(String(val));  // also drops the territory's now-unsupported rep chip(s) + its customers
      } else if (key === "customer") {
        this.crossRemoveCustomer(String(val));   // also drops the rep/territory this customer alone contributed
      } else if (Array.isArray(this.filters[key])) {
        var i = this.filters[key].indexOf(val);
        if (i >= 0) this.filters[key].splice(i, 1);
      } else {
        this.filters[key] = "";
      }
      this.page = 1;
      this.load();
      this.loadDetails();
    },
    cycleStoryCorner() {
      const order = ["br", "bl", "tl", "tr"];
      this.storyCorner = order[(order.indexOf(this.storyCorner) + 1) % order.length];
    },
    stateName(postal) {
      const k = (postal || "").toString().toUpperCase();
      return STATE_NAMES[k] || k;
    },
    // Format an ask duration (ms) compactly: "820ms" under a second, else "2.3s".
    fmtAskMs(ms) {
      var n = Number(ms);
      if (!isFinite(n) || n <= 0) return "";
      return n < 1000 ? (Math.round(n) + "ms") : ((n / 1000).toFixed(1) + "s");
    },
    fmtShort(d) {
      if (!d) return "";
      const dt = new Date(String(d).length <= 10 ? d + "T00:00:00" : d);
      if (isNaN(dt.getTime())) return String(d).slice(0, 10);
      return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    },
    fmtAgo(d) {
      if (!d) return "";
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return "";
      const days = Math.floor((new Date() - dt) / 86400000);
      if (days <= 0) return "today";
      if (days === 1) return "yesterday";
      if (days < 30) return days + "d ago";
      const mo = Math.floor(days / 30);
      return mo < 12 ? mo + "mo ago" : Math.floor(mo / 12) + "y ago";
    },
    yoyPct(v, yoy) {
      const m = (yoy || []).reduce((mx, y) => Math.max(mx, Number(y.sales) || 0), 0);
      if (m <= 0) return "0%";
      return Math.max(2, (Number(v) || 0) / m * 100).toFixed(1) + "%";
    },
    yoyDelta(yoy) {
      const y = yoy || [];
      if (y.length < 2) return "";
      const cur = Number(y[0].sales) || 0, prev = Number(y[1].sales) || 0;
      if (prev <= 0) return "";
      const pct = ((cur - prev) / prev) * 100;
      return " · " + (pct >= 0 ? "+" : "") + pct.toFixed(0) + "% vs " + y[1].year;
    },
    // Average sales of every OTHER year in the set (for year i). For the current year (i=0) that's the
    // average of all prior years; the bar is green when at/above it, red when below.
    // A calendar year is "partial" when the selected range doesn't fully cover Jan 1–Dec 31 of it
    // (the first/last year of a range usually are). Partial years aren't fairly comparable, so we
    // gray them out and exclude them from the average.
    yoyIsPartial(year) {
      if (!this.filters.from || !this.filters.to) return false;
      var from = new Date(this.filters.from + "T00:00:00"), to = new Date(this.filters.to + "T00:00:00");
      if (isNaN(from.getTime()) || isNaN(to.getTime())) return false;
      return from > new Date(year, 0, 1) || to < new Date(year, 11, 31);
    },
    avgOfOtherFull(arr, i) {
      var self = this;
      var others = (arr || []).filter(function (y, j) { return j !== i && !self.yoyIsPartial(y.year); });
      if (!others.length) return 0;
      return others.reduce(function (s, y) { return s + (Number(y.sales) || 0); }, 0) / others.length;
    },
    yoyBarColor(arr, i) {
      if (this.yoyIsPartial(arr[i].year)) return "#9aa0a6";   // gray — partial boundary year, not compared
      var avg = this.avgOfOtherFull(arr, i);
      var v = Number(arr[i].sales) || 0;
      return (avg > 0 && v < avg) ? "#c0392b" : "#2e9e4f";    // red if below the other full years' avg, else green
    },
    yoyTooltip(arr, i) {
      var y = arr[i]; var v = Number(y.sales) || 0;
      if (this.yoyIsPartial(y.year)) return y.year + " sales: " + this.fmtMoney(v) + " — partial year (the date range covers only part of " + y.year + "), so it isn't compared.";
      var avg = this.avgOfOtherFull(arr, i);
      if (avg <= 0) return y.year + " sales: " + this.fmtMoney(v) + " (the only full year in the range).";
      var pct = ((v - avg) / avg) * 100;
      var s = y.year + " sales: " + this.fmtMoney(v) + " — " + Math.abs(pct).toFixed(0) + "% " +
              (pct >= 0 ? "above " : "below ") + "the average of the other full years (" + this.fmtMoney(avg) + "). ";
      s += (pct >= 0) ? "On track / ahead of pace." : ("Behind pace — " + this.fmtMoney(avg - v) + " short of the average.");
      return s;
    },
    // Story "extras" (YoY, customer standing, saved carts) — refreshed with the filters.
    loadStory() {
      httpClient.get("/report/customer-insights/story", { params: this.buildParams(false) })
        .then((res) => {
          const d = res.data || {};
          this.story = { summary: d.summary || null, customer: d.customer || null, savedCarts: d.savedCarts || [] };
        })
        .catch(() => { this.story = { summary: null, customer: null, savedCarts: [] }; });
    },
    // AI insights narrative — MANUAL only (the "✨ Generate Insights" / "Regenerate" button).
    // Sends the CURRENT filters; the backend aggregates the figures and asks OpenAI for a
    // short plain-English summary. Never auto-called on filter change (cost + data control).
    generateInsights() {
      var self = this;
      this.aiInsightsBusy = true;
      this.aiInsightsError = "";
      var body = { filters: this.buildParams(false) };
      // When launched from the hero result card, fold the question + its table into the narrative
      // so the insights OPEN by answering exactly what the rep asked. Panel-generate clears aiAskCtx.
      var askQ = (this.aiAskCtx && this.aiAskCtx.question || "").trim();
      if (askQ) {
        body.askQuestion = askQ;
        var askTxt = this.aiAskTableText(this.aiAskCtx.table);
        // Group-filtered presentation: body.filters is still the WHOLE dashboard, so without this
        // instruction the model happily writes about every rep again. askData is free text the prompt
        // reads back as the asked-for results, so the scope line rides along with it.
        var askG = (this.aiAskCtx && this.aiAskCtx.groups) || null;
        if (askG && askG.length) askTxt = "SCOPE — discuss ONLY these groups: " + askG.join(", ") + ". Ignore every other group.\n" + askTxt;
        body.askData = askTxt;
      }
      return httpClient.post("/report/ai-insights", body)
        .then(function (res) {
          var d = res.data || {};
          if (d.ok) {
            self.aiInsights = d.insights || "";
            self.aiCoaching = d.coaching || "";
            self.aiInsightsStale = false;
            self.aiInsightsAskKey = askQ;   // remember what question (if any) these insights reflect
            // Instant local entry; the backend also logs it to rpt_AiHistory (powers the 🕘 popup + analytics).
            self.aiInsightHistory.unshift({ id: "i" + Date.now(), createdAt: new Date().toISOString(), insights: self.aiInsights, coaching: self.aiCoaching, title: d.title || "", snapshot: d.snapshot || "" });
          } else {
            self.aiInsightsError = d.message || "Couldn't generate insights.";
          }
        })
        .catch(function () { self.aiInsightsError = "Something went wrong generating insights. Please try again."; })
        .finally(function () { self.aiInsightsBusy = false; });
    },
    // Compact text rendering of a result table for the insights prompt (the model reads it as the
    // "askedResults" block). Row-capped so the payload stays bounded.
    aiAskTableText(t) {
      if (!t || !Array.isArray(t.columns) || !Array.isArray(t.rows)) return "";
      var lines = [];
      if (t.title) lines.push(t.title);
      lines.push(t.columns.join(" | "));
      t.rows.slice(0, 200).forEach(function (r) {
        lines.push((r || []).map(function (c) { return String(c == null ? "" : c); }).join(" | "));
      });
      return lines.join("\n");
    },
    // Panel "Generate Insights"/"Regenerate": filters-only (clears any hero-question context first).
    panelGenerateInsights() {
      this.aiAskCtx = null;
      return this.generateInsightsAndPresentation();
    },
    // Result-card "✨ Generate Insights": generate a narrative that OPENS by answering this question
    // (and archive the matching presentation), then reveal the Sales Insights panel so the rep sees it.
    cardGenerateInsights() {
      if (!this.aiTable || this.aiInsightsBusy || this.printBusy) return;
      this.aiAskCtx = { question: (this.aiQuestion || "").trim(), table: this.aiTable };
      this.aiInsights = "";   // force a fresh generation that folds in this question
      if (typeof this.openStory === "function") this.openStory();
      return this.generateInsightsAndPresentation();
    },
    // Distill a free-text question into a short keyword title (drop filler words, Title-case, cap length).
    distillQuestionName(q) {
      var stop = /\b(i|want|to|see|the|for|of|my|along|with|their|a|an|and|please|show|me|give|list|between|from|is|are|would|like|can|you|us|we)\b/gi;
      var kw = (q || "").replace(stop, " ").replace(/[^\w\s&.\-]/g, " ").replace(/\s+/g, " ").trim();
      kw = kw.split(" ").slice(0, 8).join(" ");
      return (kw ? kw.charAt(0).toUpperCase() + kw.slice(1) : "AI view").slice(0, 60);
    },
    // Normalized question key for dedupe / favorited-checks (case + whitespace insensitive).
    _qKey(q) { return String(q == null ? "" : q).trim().toLowerCase().replace(/\s+/g, " "); },
    // Is this past question already saved as a ★ favorite (matched on the stored ask.question)?
    isQuestionFavorited(h) {
      if (h && h._faved) return true;
      var k = this._qKey(h && h.q);
      if (!k) return false;
      var self = this;
      return (this.favorites || []).some(function (f) { return f.ask && self._qKey(f.ask.question) === k; });
    },
    // ★ on a past question → save it as a re-runnable chat favorite (question + its date window), keyword-titled.
    favoriteFromHistory(h) {
      if (!h || this.viewingAsUser) return;          // read-only while previewing another user
      var q = (h.q || "").trim();
      if (!q || this.isQuestionFavorited(h)) return;   // no duplicate favorite
      var applied = h.applied || {};
      this.favorites.push({
        id: "F" + Date.now(), groupId: "", name: this.distillQuestionName(q),
        filters: this.snapshotFilters(),
        ask: { question: q, from: applied.from || this.filters.from, to: applied.to || this.filters.to }
      });
      this.saveFavorites();
      this.$set(h, "_faved", true);   // instant feedback on the row (dims the star)
    },
    // Result-card "📊 Presentation": open the print-ready presentation with a Question & Results page.
    // Regenerate the narrative first only if the current insights don't already reflect THIS question.
    // 📊 Presentation: on a GROUPED result table, ask which groups to include FIRST (user 2026-08-02 —
    // same behaviour as the ⬇ Excel button). A flat ranking has no groups to choose, so it builds straight
    // away. Mirrors exportAiTable/confirmExportAiTable exactly, including the shared _aiTableGroups() source
    // so the two pickers can never disagree about what the groups are.
    cardPresentation() {
      if (!this.aiTable || this.printBusy || this.aiInsightsBusy) return;
      var groups = this._aiTableGroups();
      if (!groups.length) { this._runCardPresentation(null); return; }
      this.aiPresentModal = { open: true, groups: groups.map(function (k) { return { key: k, checked: true }; }) };
    },
    // The modal's Build button: keep only the checked groups, then present.
    confirmCardPresentation() {
      if (this.printBusy) return;
      var checked = (this.aiPresentModal.groups || []).filter(function (g) { return g.checked; }).map(function (g) { return g.key; });
      if (!checked.length) return;
      this.aiPresentModal.open = false;
      this._runCardPresentation(new Set(checked));
    },
    setAiPresentAll(v) { (this.aiPresentModal.groups || []).forEach(function (g) { g.checked = !!v; }); },
    // includeKeys = null → the whole table; a Set → only rows whose FIRST column (the group key) is in it.
    // The filtered table drives the Question & Results page and the AI narrative. The cover KPIs, the
    // Overview charts and the Saved Orders pages are dashboard-wide by construction (buildParams) and
    // are NOT narrowed to the picked groups — the picker copy says so.
    _runCardPresentation(includeKeys) {
      var t = this.aiTable;
      if (!t) return;
      var table = t, picked = null;
      if (includeKeys) {
        var rows = (t.rows || []).filter(function (r) {
          return includeKeys.has(String((r || [])[0] == null ? "" : (r || [])[0]));
        });
        if (!rows.length) return;
        // Shallow clone + swapped rows: never mutate this.aiTable — the on-screen card must keep showing
        // every group even when the presentation covers a subset.
        table = Object.assign({}, t, { rows: rows });
        if (rows.length !== (t.rows || []).length) picked = Array.from(includeKeys);
      }
      var q = (this.aiQuestion || "").trim();
      this.aiAskCtx = { question: q, table: table, groups: picked };
      // A SUBSET build must not reuse an all-groups narrative. Group filtering never changes the
      // question string, so the askKey check alone can never fire — without `|| picked` the previously
      // generated every-rep write-up printed verbatim on the "Sales Insights & Tips" page.
      this.aiPresentFiltered = !!picked;
      if (this.aiInsightsAskKey !== q || picked) this.aiInsights = "";
      this.printInsights();
    },
    // ===== Ask a question — Q&A over the SAME scoped dashboard data, plus an optional AI-proposed
    // filter selection the rep applies with one click (propose-then-apply). Scope is enforced server-side. =====
    // Width of the current date range in (fractional) years — feeds the refresh time estimate.
    _rangeYears() { return this._spanYears(this.filters.from, this.filters.to); },
    // Width (in years) of an arbitrary from→to window.
    _spanYears(from, to) {
      try {
        var f = new Date(String(from).length <= 10 ? from + "T00:00:00" : from);
        var t = new Date(String(to).length <= 10 ? to + "T00:00:00" : to);
        if (isNaN(f.getTime()) || isNaN(t.getTime())) return 1;
        return Math.max(0, (t - f) / (365.25 * 86400000));
      } catch (e) { return 1; }
    },
    // Bucket a window width into the shortcut families, so "average ask time" is compared like-for-like
    // (a YTD ask and an all-time ask take very different times). Keep in sync with the shortcut ranges.
    _rangeBucket(years) {
      if (years >= 15) return "all";   // all-time (1990→today ≈ 36y)
      if (years >= 4)  return "5y";    // ~5-year (and other multi-year customs)
      if (years >= 2)  return "3y";    // ~3-year
      if (years >= 0.9) return "1y";   // ~1-year
      return "ytd";                    // year-to-date / sub-year
    },
    // Average measured ask time over past questions whose applied window falls in the SAME width bucket as
    // `years`. "" when no comparable sample exists yet (better than showing an unrelated range's time).
    avgAskForRangeYears(years) {
      var self = this, b = this._rangeBucket(years);
      var xs = (this.qaHistory || []).filter(function (h) {
        if (!(typeof h.ms === "number" && h.ms > 0)) return false;
        var a = h.applied || {};
        if (!a.from || !a.to) return false;
        return self._rangeBucket(self._spanYears(a.from, a.to)) === b;
      });
      if (!xs.length) return "";
      return this.fmtAskMs(xs.reduce(function (s, h) { return s + h.ms; }, 0) / xs.length);
    },
    // Average ask time for a shortcut's window (YTD/1Yr/3Yr/5Yr) — used in the overlay button tooltips.
    avgAskForShortcut(kind) {
      try { var r = this._shortcutRange(kind); return this.avgAskForRangeYears(this._spanYears(r.from, r.to)); }
      catch (e) { return ""; }
    },
    // Tooltip for a narrow-shortcut button: its description + the average time for that window (when known).
    shortcutTip(kind, base) {
      var a = this.avgAskForShortcut(kind);
      return a ? (base + " · avg " + a) : base;
    },
    // Initialize + show the processing overlay for a new ask/refresh: per-run estimate, elapsed ticker,
    // show-flag. The isProcessing watcher calls this on its false→true TRANSITION (with the 140ms
    // anti-flash delay); askQuestion ALSO calls it directly with immediate=true, because a transition
    // watcher misses an ask that starts while a data load is already in flight (true→true never fires) —
    // an ask is never instant, so it always deserves the overlay.
    _procBegin(immediate) {
      var self = this;
      clearTimeout(this._procTimer);
      if (this._procTick) { clearInterval(this._procTick); this._procTick = null; }
      // Estimate for the "time remaining" line: an ask is roughly fixed; a data refresh scales with the
      // selected date-range width (a full-history pull genuinely takes longer than a YTD one).
      // Insights generation is a long LLM round-trip over the whole dashboard — much slower than an ask, and
      // it must be checked FIRST (a generate can overlap a refresh, and the 12s ask estimate would read as a
      // stalled bar). Ask ≈ fixed; a plain refresh scales with the selected range width.
      this.procEstimateSec = this.aiInsightsBusy ? 45 : (this.aiAskBusy ? 12 : Math.min(30, 6 + Math.round(this._rangeYears() * 0.5)));
      this.procElapsedMs = 0;
      this._procStart = Date.now();
      this._procTick = setInterval(function () { self.procElapsedMs = Date.now() - self._procStart; }, 250);
      if (immediate) this.showProcessing = true;
      else this._procTimer = setTimeout(function () { self.showProcessing = true; }, 140);
    },
    // The ✕ in the middle of the processing spinner: stop waiting on the in-flight Ask / data refresh.
    // Bumps the request token so a pending ask result is IGNORED (askQuestion checks it), then clears the
    // busy flags — the isProcessing watcher tears down the overlay + elapsed timer. Best-effort stops TTS too.
    cancelProcessing() {
      var askFlow = this.aiAskBusy || this.askConfirmAllTime;   // ✕ during a plain filter refresh must NOT touch the filters
      this._procSeq = (this._procSeq || 0) + 1;
      this.aiAskBusy = false;
      this.loading = false;
      this.aiInsightsBusy = false;   // paired with isProcessing() — without this the ✕ leaves the overlay stuck
      this.showProcessing = false;
      this.askConfirmAllTime = false;
      if (this._procTick) { clearInterval(this._procTick); this._procTick = null; }
      // Cancelling an ASK flow (mid-ask ✕, or the paused gate) leaves the dashboard un-reloaded — put the
      // filter chips back in sync with the data still on screen, or the page reads as broken ($0 cards
      // under all-time chips was a real report).
      if (askFlow) this._restorePreAskView();
      try { this.stopTts(); } catch (e) { /* noop */ }
    },
    // Write a filter-values snapshot back onto this.filters (values only, NO reload). Shared by the
    // pre-ask rollback below and askQuestion's phantom-gate rollback.
    _applyFilterSnapshot(snap, preset) {
      var self = this;
      if (!snap) return;
      Object.keys(snap).forEach(function (k) {
        var v = snap[k];
        self.$set(self.filters, k, Array.isArray(v) ? v.slice() : v);
      });
      this.activePreset = preset || "";
    },
    // Roll the filter VALUES back to the pre-ask snapshot (no reload — the dashboard still SHOWS that
    // view's data; restoring the values is what makes the page coherent again). Used when an ask flow
    // stops without running its reload: ✕ mid-ask, dismissing the paused all-time gate, or an ask that
    // ERRORED after the clean-slate reset. Consumes the snapshot and retires the ask-view UI (✓ banner +
    // favorite offer) that described the abandoned view.
    _restorePreAskView() {
      this._applyFilterSnapshot(this._aiPrevFilters, this._aiPrevPreset);
      this._aiPrevFilters = null;
      this._aiPrevPreset = "";
      this.aiApplied = false;
      clearTimeout(this._aiFollowTimer);
      this.aiFollow = { stage: "", name: "", favId: "" };
    },
    // The ASK circle in the PAUSED all-time overlay: the user consented to the expensive all-time run.
    // Re-ask LOCKED to the all-time window (filters are already pinned there) — the initial un-dated ask stays
    // deliberately cheap (no all-time query, no card) so the gate appears fast; only NOW do we run the heavy
    // all-time summary + drill, and because it's one locked ask the answer + card + dashboard all describe the
    // same window (a plain reload would leave the provisional current-window answer over an all-time board).
    confirmAllTimeAsk() {
      this.askConfirmAllTime = false;
      this.askQuestion({ lockDates: true });
    },
    // A date shortcut in the overlay narrows the window and RE-RUNS the question locked to it — so the answer,
    // the result card, AND the dashboard all re-scope to that range (a plain reload would leave the card at the
    // original all-time range). The re-ask sends lockDates=true so the backend honors this window instead of
    // re-applying the no-date → full-history default.
    procQuickRange(kind) {
      var r = this._shortcutRange(kind);
      // Clean slate first (the lockDates re-ask skips askQuestion's own reset): drop the paused all-time
      // view's dims/dates, THEN pin the shortcut window. The pre-ask undo snapshot from the ask that opened
      // this gate is kept — ✕ during the re-ask restores the view the user actually came from.
      this._resetFilterValues();
      this.filters.from = r.from; this.filters.to = r.to;
      this.activePreset = String(kind);
      this.askConfirmAllTime = false;
      this.askQuestion({ lockDates: true });
    },
    // Click on the overlay backdrop while PAUSED = dismiss without running. Restores the pre-ask filters so
    // the chips keep matching the (never-reloaded) dashboard data.
    onProcBackdrop() {
      if (this.procPaused) { this.askConfirmAllTime = false; this._restorePreAskView(); }
    },
    // ===== Quote attachments =================================================
    heroPickFiles() { try { this.$refs.heroFileInput.value = ""; this.$refs.heroFileInput.click(); } catch (e) { /* ignore */ } },
    heroFilesChosen(e) {
      var list = (e && e.target && e.target.files) ? Array.prototype.slice.call(e.target.files) : [];
      for (var i = 0; i < list.length; i++) this.heroAddFile(list[i]);
    },
    // Snipping Tool / Ctrl+Shift+S put the capture on the clipboard as an image item. Only
      // swallow the paste when an image is actually present, so pasting TEXT still works normally.
    heroPaste(e) {
      try {
        var items = (e.clipboardData && e.clipboardData.items) ? e.clipboardData.items : [];
        var handled = false;
        for (var i = 0; i < items.length; i++) {
          if (items[i].kind !== "file") continue;
          var f = items[i].getAsFile();
          if (!f || !/^image\//i.test(f.type || "")) continue;
          this.heroAddFile(f, true);
          handled = true;
        }
        if (handled) e.preventDefault();
      } catch (err) { /* a clipboard we cannot read must not break typing */ }
    },
    heroAddFile(file, pasted) {
      var self = this;
      if (!file) return;
      if (this.heroFiles.length >= 6) { this.aiAskError = "Up to 6 attachments per question."; return; }
      var name = file.name || (pasted ? "screenshot-" + (this.heroFiles.length + 1) + ".png" : "attachment");
      var ext = (name.split(".").pop() || "").toLowerCase();
      var okType = /^image\/(png|jpe?g)$/i.test(file.type || "") ||
                   ["pdf", "docx", "xlsx", "png", "jpg", "jpeg"].indexOf(ext) >= 0;
      if (!okType) { this.aiAskError = name + " is not a supported type (PDF, DOCX, XLSX, PNG, JPG)."; return; }
      if (file.size > 15 * 1024 * 1024) { this.aiAskError = name + " is over 15 MB."; return; }
      // Same name AND size = the same file picked twice. Size alone would reject two different
      // screenshots that happened to compress identically.
      if (this.heroFiles.some(function (x) { return x.name === name && x.size === file.size; })) return;
      var isImg = /^image\//i.test(file.type || "") || ["png", "jpg", "jpeg"].indexOf(ext) >= 0;
      // Does this look like a quote? Filename only — opening the file to sniff its contents before the
      // rep has asked for anything would be doing work on a document they may not want processed.
      var looksLikeQuote = ext === "pdf" && /\b(quote|quotation|qt)\b/i.test(name.replace(/[_\-]/g, " "));
      var r = new FileReader();
      r.onload = function () {
        self.aiAskError = "";
        self.heroFiles.push({
          name: name,
          size: file.size,
          sizeLabel: self.heroFileSize(file.size),
          icon: ext === "pdf" ? "📄" : (ext === "xlsx" ? "📊" : (ext === "docx" ? "📝" : "🖼")),
          thumb: isImg ? r.result : null,
          dataUri: r.result
        });
        // A NEW file gets a NEW window question. Carrying the last quote's narrow choice over to this
        // one would silently shrink a comparison the rep never asked to shrink.
        self.quoteHistory = { from: "", kind: "", label: "all history" };
        // Only offer once, and never while a question is already running or typed — an unprompted
        // dialog over someone mid-sentence is worse than no dialog.
        // "Box is empty" OR "the box only holds text we wrote ourselves" — a leftover composed question
        // is not the rep mid-sentence, and treating it as such is what silenced the dialog for a second
        // quote.
        var typed = (self.aiQuestion || "").trim();
        var boxIsOurs = !typed || typed === (self._composedQuestion || "").trim();
        if (looksLikeQuote && !self.aiAskBusy && boxIsOurs) {
          self.quotePrompt = { open: true, name: name, busy: true, peek: null, dataUri: r.result,
                               step: 1, pending: { mode: "", question: "" } };
          self.sizeQuotePrompt();
          self.quotePeek();
        }
      };
      r.onerror = function () { self.aiAskError = "Couldn't read " + name + "."; };
      r.readAsDataURL(file);
    },
    heroRemoveFile(i) {
      this.heroFiles.splice(i, 1);
      // No files left → no window and no composed question. Both were chosen FOR a file that is gone.
      if (!this.heroFiles.length) {
        this.quoteHistory = { from: "", kind: "", label: "all history" };
        if (this.aiQuestion === this._composedQuestion) { this.aiQuestion = ""; }
        this._composedQuestion = "";
      }
      this.$nextTick(this.sizeAnswerScroll);
    },
    // Where the dialog's top edge sits: below whatever fixed chrome the app is currently showing.
    // App.vue keeps body padding-top equal to the fixed header's height, so that IS the app's own
    // measurement — no second guess to drift out of sync when the header grows a role line.
    sizeQuotePrompt() {
      try {
        var pad = parseInt(window.getComputedStyle(document.body).paddingTop, 10) || 0;
        var nav = document.querySelector("#globalBreadcrumbs");
        var navH = nav ? Math.max(0, nav.getBoundingClientRect().bottom) : pad;
        var top = Math.max(pad, navH) + 14;
        // Cap it: on a short window a tall offset would leave no room for the dialog itself.
        top = Math.min(top, Math.max(60, Math.round(window.innerHeight * 0.35)));
        document.documentElement.style.setProperty("--qp-top", top + "px");
      } catch (e) { /* the CSS fallback covers it */ }
    },
    // Read the attachment far enough to name its customer and count its priced lines. Cheap on the
    // server (extraction + census + a lookup, no AI call), and it is what turns "compare this" from a
    // guess into something the rep confirmed.
    quotePeek() {
      var self = this;
      var uri = this.quotePrompt.dataUri;
      if (!uri) { this.quotePrompt.busy = false; return; }
      // STAMPED. Attach file A, dismiss it mid-peek, attach file B: A's reply would otherwise land in
      // B's dialog and print "This quote is for <A's customer>" over B's filename — and the rep would
      // then confirm a comparison of B's lines against A's customer. That is the exact substitution this
      // whole confirm step exists to prevent, so the reply is only accepted if it is still the one we
      // asked for: same token, same dialog, same file.
      var tok = this._peekTok = (this._peekTok || 0) + 1;
      var forName = this.quotePrompt.name;
      var mine = function () {
        return tok === self._peekTok && self.quotePrompt.open && self.quotePrompt.name === forName;
      };
      httpClient.post("/report/quote-peek", { dataUri: uri, fileName: forName })
        .then(function (res) { if (mine()) self.quotePrompt.peek = res.data || null; })
        .catch(function () { if (mine()) self.quotePrompt.peek = null; })
        .then(function () {
          if (!mine()) return;
          self.quotePrompt.busy = false;
          // The bytes were only needed for the peek. Dropping the reference here keeps one copy of the
          // file in memory (heroFiles) instead of two.
          self.quotePrompt.dataUri = null;
        });
    },
    // "Yes, compare it" — compose the question the rep would have typed, then ask HOW FAR BACK before
    // running it. The window changes every average in the answer, so it is part of the request, not a
    // default to be discovered afterwards.
    quotePromptYes() {
      // Pin the CONFIRMED customer into the question. The server would resolve it from the document
      // anyway, but naming it means the comparison the rep agreed to is the comparison that runs.
      var who = (this.quotePrompt.peek && this.quotePrompt.peek.customerId) || "";
      this.quotePrompt.pending = {
        mode: "ask",
        question: "Compare the attached quote against what " + (who ? who : "this customer") +
                  " has actually paid. For each line, tell me whether the price is in line with " +
                  "their history, and flag anything they have never bought before."
      };
      this.quotePrompt.step = 2;
      this.$nextTick(this.sizeQuotePrompt);
    },
    // "No" — the file stays attached and the rep asks their own question. Dismissing must never cost
    // them the upload they just did. Nor the wording: dismissed from the WINDOW step, the question they
    // already picked goes into the box unsent (they chose it; only the window went unanswered), and the
    // window stays at all-history — the dialog closing must never be what narrows a comparison.
    quotePromptNo() {
      var pend = this.quotePrompt.pending || {};
      var keep = this.quotePrompt.step === 2 ? (pend.question || "") : "";
      this.quoteDialogClose();
      if (keep) { this.aiQuestion = keep; this._composedQuestion = keep; }
      this.heroFocusAsk();
    },
    // ← Back from the window step: the action choice is not final until the window is picked.
    quotePromptBack() { this.quotePrompt.step = 1; this.quotePrompt.pending = { mode: "", question: "" }; this.$nextTick(this.sizeQuotePrompt); },
    // One place that clears the dialog, so a new field can never be left behind on one of the exits.
    quoteDialogClose() {
      this.quotePrompt = { open: false, name: "", busy: false, peek: null, dataUri: null, step: 1,
                           pending: { mode: "", question: "" } };
    },
    // The window is chosen: "" = all history, otherwise a shortcut kind (ytd / r1 / r3 / r5). Sends the
    // rep on to whichever action step 1 picked.
    quoteWindowPick(kind) {
      var from = "", label = "all history";
      if (kind) {
        try { from = this._shortcutRange(kind).from; } catch (e) { from = ""; }
        // A shortcut we could not resolve must fall back to ALL history — never to a window nobody chose.
        if (from) label = "since " + from;
      }
      this.quoteHistory = { from: from, kind: from ? kind : "", label: label };
      var pend = this.quotePrompt.pending || { mode: "", question: "" };
      this.quoteDialogClose();
      this.aiQuestion = pend.question || this.aiQuestion;
      // Remember that WE wrote this text, so the ask that follows can clear it again and the next
      // attachment still gets its dialog.
      this._composedQuestion = pend.question || "";
      if (pend.mode === "ask") this.askQuestion();
      else this.heroFocusAsk();
    },
    // A suggestion puts the words in the box rather than sending them, so the rep can edit first — but
    // it still gets the window question: every one of these answers is grounded in the same history.
    quotePromptUse(q) {
      // Carry the confirmed customer into a free-form question too, so a suggestion that never mentions
      // a customer still runs against the right one.
      var who = (this.quotePrompt.peek && this.quotePrompt.peek.customerId) || "";
      this.quotePrompt.pending = {
        mode: "use",
        question: who ? q.replace(/\bthey\b/i, who).replace(/\bthis quote\b/i, "this " + who + " quote") : q
      };
      this.quotePrompt.step = 2;
      this.$nextTick(this.sizeQuotePrompt);
    },
    heroFocusAsk() { try { this.$refs.heroAskInput.focus(); } catch (e) { /* not mounted */ } },
    heroFileSize(b) {
      if (!b) return "";
      if (b < 1024) return b + " B";
      if (b < 1024 * 1024) return Math.round(b / 1024) + " KB";
      return (b / (1024 * 1024)).toFixed(1) + " MB";
    },
    /**
     * A question WITH attachments takes its own path rather than threading through askQuestion.
     * askQuestion resets the filters, proposes a view and rewrites the dashboard — none of which a
     * quote review should do. It answers in prose about a document; it is not a view proposal.
     */
    askWithAttachments() {
      var self = this;
      if (this.aiAskBusy) return;
      // Same token the plain ask uses: ✕ (cancelProcessing) bumps _procSeq, and a reply that arrives
      // after that must be dropped rather than painted over whatever the rep did next.
      var seq = this._procSeq = (this._procSeq || 0) + 1;
      recordSearch("ask-quote", this.aiQuestion);
      this.heroVoiceStop();
      this.aiAskBusy = true;
      this.aiAskError = "";
      this.aiAnswer = "";
      this.aiProposed = null;
      this.aiProposedNote = "";
      this.aiTable = null;
      this.qrLines = [];
      this.qrOpen = {};
      this.qrRead = null;
      this.qrPrintPick = { open: false, mode: "print", rows: [] };
      this.qrFoldOpen = false;
      this.qrRemoved = [];
      this.heroFileNotes = [];
      this.aiAskHistOpen = false;
      this.stopTts(); this.ttsError = "";
      httpClient
        .post("/report/ai-ask-attach", {
          question: (this.aiQuestion || "").trim(),
          filters: this.buildParams(false),
          // How far back to compare. Empty = the customer's whole history (what this did before the
          // dialog asked), and the server validates it either way.
          historyFrom: this.quoteHistory.from || "",
          attachments: this.heroFiles.map(function (f) { return { dataUri: f.dataUri, fileName: f.name }; })
        })
        .then(function (res) {
          if (seq !== self._procSeq) return;   // cancelled, or superseded by a newer ask
          var d = res.data || {};
          // Show the per-file notes on success AND failure — which files were readable is the
          // first thing to check when an answer looks thin.
          self.heroFileNotes = Array.isArray(d.attachments) ? d.attachments : [];
          if (!d.ok) { self.aiAskError = d.message || "Couldn't review that. Please try again."; return; }
          self.aiAnswer = d.answer || "";
          self.aiAnswerCollapsed = false;   // a new answer always arrives open
          // The line-by-line comparison the server resolved. Rendered as a table BESIDE the prose: the
          // narrative explains, the table is checkable.
          self.qrLines = Array.isArray(d.lines) ? d.lines : [];
          self.qrOpen = {};
          self.qrCostIncluded = !!d.costIncluded;
          self.qrRead = d.quoteRead || null;
          self._warmPrintLogo();
          // THE QUOTE IS A VIEW, TOO (user 2026-08-12). The customer it named and the window the rep
          // chose become the dashboard filters — the same mechanism a chat answer uses, so the Customer
          // ID(s) card, the map and every other card describe the customer whose quote is on screen.
          // Reversible: the ✓ banner's ↩ UNDO restores the exact filters snapshotted here.
          self._applyQuoteView(d);
          // CLEAR THE BOX. The question this dialog composed names a customer ("…what XI199 has actually
          // paid…"). Left sitting there it (a) suppresses the dialog for the NEXT attachment, because the
          // dialog only offers itself over an empty box, so no peek and no window step run, and (b) hands
          // the backend that customer id, which wins over the one printed on the new document. The next
          // quote would then be priced against the previous customer's history with nothing on screen
          // saying so.
          if (self.aiQuestion === self._composedQuestion) { self.aiQuestion = ""; self._composedQuestion = ""; }
          // A window is chosen per attachment and is now spent.
          self.quoteHistory = { from: "", kind: "", label: "all history" };
          self.$nextTick(self.sizeAnswerScroll);   // the card just changed height
        })
        .catch(function () {
          if (seq !== self._procSeq) return;
          self.aiAskError = "Couldn't reach the review service. Please try again.";
        })
        .then(function () { if (seq === self._procSeq) self.aiAskBusy = false; });
    },
    askQuestion(opts) {
      // Attachments present → quote review, not a dashboard question. Routed here so the Ask button and
      // Enter both pick it up without two separate checks.
      //
      // NOT for a locked re-ask. lockDates means the caller is a favorite replay or the all-time gate's
      // ASK/narrow button — a saved dashboard view, not a question about a document. Those were being
      // silently rerouted into a quote comparison over whatever file was still in the tray, and because
      // the attachment path never reloads the dashboard, the filter chips ended up advertising a window
      // over data that was never fetched.
      if ((this.heroFiles || []).length > 0 && !lockDates) return this.askWithAttachments();
      // opts.lockDates = the user explicitly chose the current window (overlay shortcut) → send it so the
      // backend honors Filters.From/To instead of re-applying the no-date default. (When called from a button
      // /keydown, opts is a DOM event → lockDates is undefined → normal behavior.)
      var lockDates = !!(opts && opts.lockDates === true);
      var self = this;
      // The quote card belongs to the answer that produced it. Cleared here so a plain question never
      // renders beneath "Quote vs past orders · 22 line(s) · 📅 vs since …" from a file that is no longer
      // attached — a rep printing that card would be presenting last quote's arithmetic as part of this
      // answer.
      this.qrLines = [];
      this.qrOpen = {};
      this.qrRead = null;
      this.qrPrintPick = { open: false, mode: "print", rows: [] };
      this.qrFoldOpen = false;
      this.qrRemoved = [];
      this.heroFileNotes = [];
      // Activity trail: every Ask-bar path (button, Enter, paused-gate re-ask, favorites)
      // funnels through here, so one call covers all six call sites.
      recordSearch("ask", this.aiQuestion);
      // Asking means "I'm done talking" — kill any live dictation BEFORE reading the box. Placed here rather
      // than on the Ask button because askQuestion has six call sites (button, Enter, the paused-gate re-ask,
      // favorites); this is the only choke point all of them pass through.
      this.heroVoiceStop();
      var question = (this.aiQuestion || "").trim();
      if (!question || this.aiAskBusy) return;
      var wasPaused = this.askConfirmAllTime;   // re-asking from the paused all-time gate? (see the rollback below)
      var seq = this._procSeq = (this._procSeq || 0) + 1;   // this ask's token; cancelProcessing() bumps it to invalidate
      this.aiAskHistOpen = false;   // starting a new Ask closes the 🕘 past-questions list
      this.askConfirmAllTime = false;   // clear any prior paused all-time confirm
      // Retire the PREVIOUS ask's follow-up UI before anything else: the "Save this view as a favorite?"
      // bar and the ✓ view-updated banner describe the previous view — left up, they showed a stale
      // (often all-time "1990–…") title while a different window was being asked.
      clearTimeout(this._aiFollowTimer);
      this.aiFollow = { stage: "", name: "", favId: "" };
      this.aiApplied = false;
      // Every NEW ask starts from a clean slate — snapshot the CURRENT view FIRST (↩ Undo and a dismissed
      // all-time gate restore it: these are the filters the visible dashboard data was actually loaded
      // with), THEN clear the previous run's filters so a repeat ask never inherits a stale window/dims
      // (e.g. a prior "5 Yr" narrow leaving $0 on the next all-time run). A lockDates re-ask (overlay
      // shortcut / favorite re-run) is EXEMPT — it carries the explicit window its caller just staged,
      // and keeps the snapshot chain from the ask that opened the gate.
      if (!lockDates) {
        // If we're re-asking straight out of a PAUSED all-time gate, this.filters was already mutated to the
        // phantom all-time view whose data was NEVER loaded (applyProposedFilters set it, then paused). Roll
        // that back to the real pre-ask snapshot FIRST, so we snapshot the view the dashboard actually shows —
        // not the phantom (which would leave a later ✕/dismiss "restoring" an all-time view over stale data).
        if (wasPaused && this._aiPrevFilters) this._applyFilterSnapshot(this._aiPrevFilters, this._aiPrevPreset);
        this._aiPrevFilters = JSON.parse(JSON.stringify(this.filters));
        this._aiPrevPreset = this.activePreset;
        this._resetFilterValues();
      }
      this.aiAskBusy = true;
      this.procLockDates = lockDates;   // the overlay may show the date range only when the window is KNOWN pre-response
      this._procBegin(true);   // show the overlay deterministically — the isProcessing watcher alone misses a
                               // start while a load is already in flight (true→true never fires the watcher)
      this.aiAskError = "";
      this.aiAnswer = "";
      this.aiProposed = null;
      this.aiProposedNote = "";
      this.aiApplied = false;
      this.aiTable = null;
      this.stopTts(); this.ttsError = "";   // a new question retires the previous answer's audio
      httpClient.post("/report/ai-ask", { filters: this.buildParams(false), question: question, lockDates: lockDates })
        .then(function (res) {
          if (seq !== self._procSeq) return;   // the user cancelled (✕) or asked again → ignore this stale result
          var d = res.data || {};
          // A failed ask never reloads the dashboard, but askQuestion already RESET the filters at the top —
          // so put the pre-ask view back or the chips (reset baseline) contradict the still-displayed data.
          if (!d.ok) { self.aiAskError = d.message || "Couldn't answer that. Please try again."; self._restorePreAskView(); return; }
          self.aiAnswer = d.answer || "";
          self.aiAnswerCollapsed = false;   // a new answer always arrives open
          self.aiProposedNote = d.note || "";
          var f = (d.filters && typeof d.filters === "object") ? d.filters : {};
          self.aiProposed = Object.keys(f).length ? f : null;
          // Structured RESULT TABLE (rankings/breakdowns) → rendered as a mini card with bars + Excel export.
          self.aiTable = (d.table && Array.isArray(d.table.columns) && Array.isArray(d.table.rows) && d.table.rows.length) ? d.table : null;
          // Capture the proposed view + table NOW, before applyProposedFilters nulls aiProposed — the history
          // entry (built only if this ask actually loads results, below) needs them.
          var mine = (self.qaHistory || []).find(function (x) { return x.own && x.repName; });
          var histEntry = {
            id: d.historyId != null ? d.historyId : "q" + Date.now(),
            q: question,
            a: self.aiAnswer,
            applied: self.aiProposed ? JSON.parse(JSON.stringify(self.aiProposed)) : null,
            note: self.aiProposedNote,
            table: self.aiTable ? JSON.parse(JSON.stringify(self.aiTable)) : null,   // SESSION-only (not in rpt_AiHistory) — replay this session shows the card; a reload replays text only
            ts: new Date().toISOString(),
            ms: (typeof d.durationMs === "number" ? d.durationMs : null),   // server-measured ask time → the per-range "avg ask time"
            repName: mine ? mine.repName : "You",
            own: true
          };
          // AUTO-APPLY: the hero chat adjusts the filters itself — no "Apply this view" click. The ↩ Undo
          // snapshot was taken at ask START (before the clean-slate reset), so Undo / a dismissed gate restore
          // the view the user actually had. Forward lockDates: a shortcut/favorite re-ask carries an explicit
          // window → run, don't re-gate.
          var hadProposed = !!(self.aiProposed && self.aiProposedChips.length);
          self.applyProposedFilters(self.aiProposed || {}, { lockDates: lockDates });   // clears aiProposed, reloads every card, starts ⭐→📧
          self.aiApplied = hadProposed;
          // Record to history + clear the Ask box ONLY when RESULTS actually loaded. applyProposedFilters sets
          // askConfirmAllTime=true ONLY when it PAUSED at the all-time gate — in that case this ask is the
          // PROVISIONAL gate step, so DON'T log it (the confirm/narrow re-ask logs the real one → no duplicate
          // history, and the per-range avg isn't polluted by the cheap pre-gate time) and KEEP the question
          // (the gate's ASK/narrow buttons re-ask with it). On a real load: dedupe same-text (move-to-top,
          // keep most recent), unshift, cap 50, and clear the box so the answered question can't re-fire.
          if (!self.askConfirmAllTime) {
            var newQKey = self._qKey(question);
            self.qaHistory = (self.qaHistory || []).filter(function (x) { return !(x.own && self._qKey(x.q) === newQKey); });
            self.qaHistory.unshift(histEntry);
            if (self.qaHistory.length > 50) self.qaHistory = self.qaHistory.slice(0, 50);
            self.aiQuestion = "";
          }
        })
        .catch(function () { if (seq === self._procSeq) { self.aiAskError = "Something went wrong. Please try again."; self._restorePreAskView(); } })
        .finally(function () { if (seq === self._procSeq) self.aiAskBusy = false; });
    },
    // Re-apply a saved AI view (history ↻, the "✓ Apply this view" chip) WITHOUT re-running the question.
    // These entry points bypass askQuestion's ask-start snapshot, so stage one here first — otherwise a
    // later all-time-gate dismissal would restore null (or a stale earlier snapshot). Since the backend now
    // stamps a window onto every saved view, an all-time one correctly opens the paused gate, backed by this
    // snapshot. Guarded against reshaping the view out from under an in-flight ask.
    reapplyHistoryView(h) {
      if (this.aiAskBusy) return;
      if (!h || !h.applied) return;
      this._aiPrevFilters = JSON.parse(JSON.stringify(this.filters));
      this._aiPrevPreset = this.activePreset;
      this.applyProposedFilters(h.applied);
    },
    // Apply the AI's proposed view: clear the dimension filters first (for a clean, predictable result that
    // matches the AI's description), then set the proposed dimensions/dates. Reversible via Clear all.
    // opts.lockDates = the user EXPLICITLY chose this window (overlay shortcut / favorite re-run) → skip the
    // all-time confirm gate and run immediately (they already committed; the gate is only for the un-dated
    // default no one asked for).
    applyProposedFilters(spec, opts) {
      var self = this;
      var lockDates = !!(opts && opts.lockDates === true);
      spec = spec || {};   // {} = a pure "Clear all" (a new question that proposes NO filters still starts clean)
      // A locked re-ask (overlay shortcut / favorite) comes back with the exact window that was staged —
      // keep its preset highlight (e.g. TRAILING (5)) instead of flattening every dated view to "custom".
      var keepPreset = (spec.from && spec.to && spec.from === this.filters.from && spec.to === this.filters.to)
        ? this.activePreset : null;
      var dims = ["salesRep", "territory", "status", "machineCodeCategory", "machineCodePrefix", "machineCodeDescription", "partDescription", "customer", "part", "state", "country"];
      // FULL "Clear all" first (same baseline as resetFilters — dims AND date/invoiced/dateBasis), THEN overlay
      // whatever THIS view proposes. Previously date/invoiced were left as-is when unproposed, so the previous
      // question's date range / invoiced state lingered under the new one; now every ask starts truly clean.
      dims.forEach(function (k) { if (Array.isArray(self.filters[k])) self.$set(self.filters, k, []); });
      this.filters.orderNo = "";
      var now = new Date();
      this.filters.dateBasis = "order";
      this.filters.invoiced = spec.invoiced || "all";
      this.filters.from = spec.from || (now.getFullYear() + "-01-01");
      this.filters.to = spec.to || now.toISOString().slice(0, 10);
      // Apply EVERY proposed dimension the backend can validate (incl. machine codes + part descriptions).
      dims.forEach(function (k) {
        if (Array.isArray(spec[k]) && spec[k].length) self.$set(self.filters, k, spec[k].slice());
      });
      // The report's baseline EXCLUDES CANCELLED\VOID + blank-status rows (same invariant as resetFilters) — a
      // view that doesn't ask for a status keeps that default instead of the cleared [] silently widening every
      // number to cancelled orders. A view that DOES ask for a status set it in the loop above and is untouched.
      if (!(Array.isArray(spec.status) && spec.status.length))
        this.$set(this.filters, "status", ["CLOSED", "HOLD", "RELEASED"]);
      this.aiProposed = null;   // dismiss the chip once applied
      this.activePreset = keepPreset !== null ? keepPreset
        : ((spec.from || spec.to) ? "" : "ytd");   // a proposed date = custom range; else the YTD baseline
      // If the applied window is ALL-TIME (an un-dated question defaults to 1990→today), do NOT auto-run the
      // heavy dashboard reload — PAUSE and let the user confirm (click ASK in the overlay) or narrow first.
      // Any other (dated) view reloads immediately as before. Read spec.from DIRECTLY (not the procIsAllTime
      // computed) so the decision is deterministic right after mutating this.filters — this is what makes a
      // REPEAT all-time ask reliably re-show the overlay.
      var isAllTime = String(spec.from || "").slice(0, 4) === "1990";
      if (isAllTime && !lockDates) { this.askConfirmAllTime = true; }
      else { this.askConfirmAllTime = false; this.applyFilters(); }   // clamps dates + reloads data, details, story, saved carts, AI cards
      // Follow-up chain: offer to keep this view as a favorite (and then to schedule an email digest of it).
      // Only when the view set at least one DIMENSION — favorites snapshot chips, not the date range, so a
      // dates-only view would save an empty favorite.
      var hasDims = dims.some(function (k) { return Array.isArray(spec[k]) && spec[k].length; });
      // Prefill the favorite name with the applied FILTER VALUES + the date window (always) — e.g.
      // "RUSSIA · Jan 1, 1990 – Jul 25, 2026" — so the saved title self-documents the exact view.
      // Stash the ANSWERED question on the follow bar NOW — askQuestion clears this.aiQuestion right after this
      // (so the box can't re-fire), and the saver reads it. Without this stash the favorite saves an empty
      // question → it renders as a yellow filter-favorite instead of the BLUE chat-favorite and can't re-run.
      this.aiFollow = hasDims
        ? { stage: "fav", name: this.suggestFavoriteNameWithDates(), favId: "", q: (this.aiQuestion || "").trim() }
        : { stage: "", name: "", favId: "" };
    },
    // ↩ Undo for an auto-applied AI view: restore the exact filters + preset highlight snapshotted in
    // askQuestion before the apply, then reload. Also retires the ⭐→📧 follow-up (it was for the AI view).
    aiUndoApplied() {
      var self = this, prev = this._aiPrevFilters;
      if (prev) {
        Object.keys(prev).forEach(function (k) {
          var v = prev[k];
          self.$set(self.filters, k, Array.isArray(v) ? v.slice() : v);
        });
        this.activePreset = this._aiPrevPreset || "";
      }
      this._aiPrevFilters = null;
      this._aiPrevPreset = "";
      this.aiApplied = false;
      this.aiFollow = { stage: "", name: "", favId: "" };
      this.applyFilters();
    },
    // ⭐→📧 follow-up chain after applying an AI view: save it as a favorite, then offer the existing
    // scheduled-email digest (openNotify) for that favorite — the same settings as the favorites bar's 📧.
    // An existing favorite that MATCHES (same question, or same name) → ask to overwrite first (§ dupe stage).
    _findDupeFavorite(name, question) {
      var norm = function (s) { return String(s == null ? "" : s).trim().toLowerCase().replace(/\s+/g, " "); };
      var qn = norm(question), nn = norm(name);
      return (this.favorites || []).find(function (f) {
        if (f.ask && f.ask.question && qn && norm(f.ask.question) === qn) return true;   // same question (chat fav)
        return !!(nn && norm(f.name) === nn);                                             // same name
      }) || null;
    },
    aiFollowSaveFavorite() {
      var name = (this.aiFollow.name || "").trim() || this.suggestFavoriteName();
      var q = this.aiFollow.q || this.aiQuestion;   // the stashed answered question (aiQuestion is cleared by now)
      var dupe = this._findDupeFavorite(name, q);
      if (dupe) { this.aiFollow = { stage: "dupe", name: name, favId: "", dupeId: dupe.id, dupeName: dupe.name, q: q }; return; }
      this._commitFollowFavorite(name, null);
    },
    // Overwrite the matched favorite IN PLACE — the new settings transfer onto it while its identity (id,
    // groupId) + any linked email-digest schedule carry over, so the replacement keeps working.
    aiFollowOverwrite() { this._commitFollowFavorite((this.aiFollow.name || "").trim() || this.suggestFavoriteName(), this.aiFollow.dupeId); },
    aiFollowSaveNew() { this._commitFollowFavorite((this.aiFollow.name || "").trim() || this.suggestFavoriteName(), null); },
    _commitFollowFavorite(name, overwriteId) {
      // This came from an ASK, so it's a CHAT favorite (carries the question) → renders BLUE (.ci-fav.is-chat)
      // and re-runs the question on click. Read the STASHED question (this.aiFollow.q) — askQuestion already
      // cleared this.aiQuestion, so reading the live box here would save an empty question (→ yellow, no re-run).
      var ask = { question: (this.aiFollow.q || this.aiQuestion || "").trim(), from: this.filters.from, to: this.filters.to };
      var favId = "";
      if (overwriteId) {
        var ex = (this.favorites || []).find(function (f) { return f.id === overwriteId; });
        if (ex) { this.$set(ex, "name", name); this.$set(ex, "filters", this.snapshotFilters()); this.$set(ex, "ask", ask); favId = ex.id; }
      }
      if (!favId) { var fav = { id: "F" + Date.now(), name: name, filters: this.snapshotFilters(), groupId: "", ask: ask }; this.favorites.push(fav); favId = fav.id; }
      this.saveFavorites();
      this.aiFollow = { stage: "notify", name: name, favId: favId };
      // The "Saved! Want this emailed on a schedule?" prompt auto-dismisses after 10s (unless the user acts).
      var self = this;
      clearTimeout(this._aiFollowTimer);
      this._aiFollowTimer = setTimeout(function () {
        if (self.aiFollow && self.aiFollow.stage === "notify") self.aiFollow = { stage: "", name: "", favId: "" };
      }, 10000);
    },
    aiFollowOpenNotify() {
      var id = this.aiFollow.favId;
      var fav = (this.favorites || []).find(function (f) { return f.id === id; });
      this.aiFollow = { stage: "", name: "", favId: "" };
      if (fav) this.openNotify(fav, "favorite");
    },
    // ===== Hero Ask typeahead — complete real card values mid-sentence ("Mark" → "Mark Warren"). =====
    // The fragment = the trailing 1–3 words being typed; a value matches when it STARTS WITH the fragment
    // or (single-word fragment) any word inside it does ("warren" also finds "Mark Warren").
    heroSugRecalc() {
      var text = this.aiQuestion || "";
      var m = text.match(/([A-Za-z0-9][A-Za-z0-9 &.'\-]*)$/);
      var words = m ? m[1].trim().split(/\s+/).filter(Boolean) : [];
      if (!words.length) { this.heroSug.open = false; this.heroSug.items = []; return; }
      var pool = this.heroSuggestPool, matches = [], frag = "";
      for (var n = Math.min(3, words.length); n >= 1 && !matches.length; n--) {
        var cand = words.slice(-n).join(" ").toLowerCase();
        if (cand.length < 2) continue;
        matches = pool.filter(function (s) {
          var t = s.text.toLowerCase();
          if (t === cand) return false;                       // already fully typed
          if (t.indexOf(cand) === 0) return true;             // value starts with the fragment
          return n === 1 && t.split(/[\s/,\-]+/).some(function (w) { return w.indexOf(cand) === 0; });
        }).slice(0, 8);
        if (matches.length) frag = words.slice(-n).join(" ");
      }
      // Also surface PAST QUESTIONS (DB-backed rpt_AiHistory) that contain what's typed so far —
      // picking one replaces the whole input with that phrase.
      var typed = text.trim().toLowerCase();
      if (typed.length >= 3) {
        var phrases = [];
        (this.qaHistory || []).forEach(function (h) {
          var q2 = (h && h.q ? String(h.q) : "").trim();
          if (!q2) return;
          var lo = q2.toLowerCase();
          if (lo === typed) return;   // already fully typed
          if (lo.indexOf(typed) >= 0 && !phrases.some(function (p) { return p.text.toLowerCase() === lo; }))
            phrases.push({ text: q2, label: "Past question", phrase: true });
        });
        matches = matches.concat(phrases.slice(0, 3));
      }
      this.heroSug = { open: matches.length > 0, items: matches, idx: 0, frag: frag };
    },
    heroSugPick(s) {
      if (!s) return;
      // A past-question PHRASE replaces the whole input (reuse the sentence as-is).
      if (s.phrase) {
        this.aiQuestion = s.text;
        this.heroSug = { open: false, items: [], idx: 0, frag: "" };
        var el0 = this.$refs.heroAskInput; if (el0 && el0.focus) el0.focus();
        return;
      }
      var text = this.aiQuestion || "";
      var frag = this.heroSug.frag || "";
      // Strip the typed fragment tolerantly (case-insensitive, flexible internal whitespace, trailing
      // spaces allowed) — a plain exact-suffix check missed "Mark " (trailing space) and produced
      // "Mark Mark Warren" by appending instead of replacing.
      var re = null;
      if (frag) {
        var esc = frag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+");
        re = new RegExp(esc + "\\s*$", "i");
      }
      if (re && re.test(text)) this.aiQuestion = text.replace(re, "") + s.text;
      else this.aiQuestion = text + (text && !/\s$/.test(text) ? " " : "") + s.text;
      this.heroSug = { open: false, items: [], idx: 0, frag: "" };
      var el = this.$refs.heroAskInput; if (el && el.focus) el.focus();
    },
    // One keydown handler for the hero input: when suggestions are open, arrows navigate and Tab/Enter
    // inserts; otherwise Enter (without Shift) submits the question — Shift+Enter keeps the newline.
    heroSugKey(e) {
      if (this.heroSug.open && this.heroSug.items.length) {
        if (e.key === "ArrowDown") { e.preventDefault(); this.heroSug.idx = (this.heroSug.idx + 1) % this.heroSug.items.length; return; }
        if (e.key === "ArrowUp") { e.preventDefault(); this.heroSug.idx = (this.heroSug.idx - 1 + this.heroSug.items.length) % this.heroSug.items.length; return; }
        if (e.key === "Tab" || e.key === "Enter") { e.preventDefault(); this.heroSugPick(this.heroSug.items[this.heroSug.idx]); return; }
        if (e.key === "Escape") { this.heroSug.open = false; return; }
      }
      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); this.askQuestion(); }
    },
    heroSugBlur() { var self = this; setTimeout(function () { self.heroSug.open = false; }, 150); },
    // ===== 🎤 Voice input — dictate the question via the browser's Web Speech API. Click to start,
    // click again (or go silent) to stop. Interim words stream into the field live; existing text is
    // kept and the dictation appends after it. Nothing is sent anywhere except the browser's own
    // speech service; the question still only goes to the AI when the user hits Ask.
    heroVoiceToggle() {
      var self = this;
      if (this.heroVoice.on) { try { if (this._heroRec) this._heroRec.stop(); } catch (e) { /* */ } return; }
      // Chrome / Firefox / Edge / the Google app on iPhone-iPad are WebKit shells WITHOUT Apple's
      // speech service — webkitSpeechRecognition may EXIST there but start() always dies with
      // 'service-not-allowed', and no permission can fix it (user 2026-08-01: "Fix Microphone"
      // looped uselessly in Chrome-on-iPhone). EXPLOIT the keyboard's own 🎙 instead (user
      // 2026-08-01): every phone keyboard dictates into the focused field with NO site permission
      // and NO speech service. Focus the box right here — iOS only honors programmatic focus inside
      // a user gesture — so the keyboard (mic key included) is already up when the help appears.
      if (this._iosNonSafari()) {
        try { if (this.$refs.heroAskInput) this.$refs.heroAskInput.focus(); } catch (e) { /* */ }
        this._showSpeechServiceHelp();
        return;
      }
      var SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) { this.aiAskError = "Voice input isn't supported in this browser."; return; }
      var rec = new SR();
      this._heroRec = rec;
      rec.lang = "en-US"; rec.interimResults = true; rec.continuous = true;
      // Dictation appends AFTER whatever is already typed.
      this._heroVoiceBase = (this.aiQuestion || "").replace(/\s+$/, "");
      if (this._heroVoiceBase) this._heroVoiceBase += " ";
      rec.onresult = function (ev) {
        var t = "";
        for (var i = 0; i < ev.results.length; i++) t += ev.results[i][0].transcript;
        self.aiQuestion = (self._heroVoiceBase + t).replace(/\s{2,}/g, " ");
      };
      rec.onend = function () { self.heroVoice.on = false; self.heroSugRecalc(); };
      rec.onerror = function (e) {
        self.heroVoice.on = false;
        var err = e && e.error;
        // Two DIFFERENT failures that used to share one message (user 2026-08-01):
        //  'not-allowed'        = the MIC permission — fixable, offer the one-tap Fix button.
        //  'service-not-allowed' = the speech SERVICE itself is unavailable (iOS non-Safari
        //                          browsers, some webviews) — no permission fixes it; explain.
        if (err === "service-not-allowed") { self._showSpeechServiceHelp(); return; }
        if (err === "not-allowed") {
          self.aiAskError = "Microphone access was blocked — allow the mic for this site to dictate.";
          self.micFixAvail = true;   // offer the one-tap fix (see fixMicrophone)
        }
      };
      this.aiAskError = "";
      this.micFixAvail = false;
      this.micHelp = null;
      this.heroVoice.on = true;
      try { rec.start(); } catch (e) { this.heroVoice.on = false; }
    },
    // "🎙 Fix microphone" (user 2026-08-01, mobile): a web page can NEVER flip the mic permission
    // itself — but a getUserMedia call from an explicit tap is the strongest legal nudge: whenever the
    // browser is still willing to ask (permission state "prompt", which covers most mobile denials),
    // it re-raises its native Allow dialog right here. Allowed → the grant covers the Web Speech API
    // too, so dictation starts immediately. Refused without a prompt (hard-block) → show exact
    // per-device steps, since only the user can flip it from the browser/OS settings.
    fixMicrophone() {
      var self = this;
      if (this.micFixBusy) return;
      this.micFixBusy = true;
      var md = navigator.mediaDevices;
      if (!md || !md.getUserMedia) { this.micFixBusy = false; this.micHelp = this._micHelpSteps(); this._requestAiMicHelp("mic-blocked"); return; }
      md.getUserMedia({ audio: true })
        .then(function (stream) {
          try { stream.getTracks().forEach(function (t) { t.stop(); }); } catch (e) { /* */ }
          self.micFixBusy = false;
          self.micFixAvail = false;
          self.micHelp = null;
          self.aiAskError = "";
          self.heroVoiceToggle();   // permission granted — dictating in the same tap
        })
        .catch(function () {
          self.micFixBusy = false;
          self.micHelp = self._micHelpSteps();
          self._requestAiMicHelp("mic-blocked");   // upgrade the static steps in place when the AI answers
        });
    },
    // Ask the backend (OpenAI, cached per device) for steps tailored to the EXACT browser/OS — the
    // static _micHelpSteps panel is already showing, so this only ever UPGRADES it in place (user
    // 2026-08-01: pass the browser + mobile flag to OpenAI; include a settings deep link when one
    // truly exists for that platform). Sequence-guarded: a stale response never overwrites a newer
    // panel, and a dismissed panel stays dismissed.
    _requestAiMicHelp(context) {
      var self = this;
      var seq = (this._micAiSeq = (this._micAiSeq || 0) + 1);
      httpClient.post("/report/mic-fix-help", {
        context: context,
        platform: (navigator.platform || "") + ((navigator.maxTouchPoints || 0) > 0 ? " touch" : ""),
        isMobile: typeof window !== "undefined" && window.innerWidth < 992
      }).then(function (res) {
        var d = res.data || {};
        if (seq !== self._micAiSeq || !d.ok || !d.steps || !d.steps.length || !self.micHelp) return;
        self.micHelp = {
          title: d.title || self.micHelp.title,
          device: self.micHelp.device,
          steps: d.steps,
          copyLink: self.micHelp.copyLink,
          deepLink: d.deepLink || null,
          deepLinkLabel: d.deepLinkLabel || null,
          ai: true
        };
      }).catch(function () { /* the static steps stand */ });
    },
    // iOS browser that ISN'T Safari (Chrome=CriOS, Firefox=FxiOS, Edge=EdgiOS, the Google app=GSA,
    // DuckDuckGo, Opera=OPT). All are WebKit shells; Apple withholds the speech service from them,
    // so dictation cannot work there regardless of permissions. iPadOS 13+ masquerades as
    // "Macintosh" — the maxTouchPoints check catches it.
    _iosNonSafari() {
      var ua = navigator.userAgent || "";
      var ios = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1);
      return ios && /(CriOS|FxiOS|EdgiOS|GSA|DuckDuckGo|OPT\/)/i.test(ua);
    },
    // The speech SERVICE (not the mic) is unavailable. iOS non-Safari → the honest fix is Safari:
    // hand them the link to carry over. Everything else → name the browsers where dictation works.
    _showSpeechServiceHelp() {
      this.heroVoice.on = false;
      this.micFixAvail = false;   // no permission can fix a missing service — hide the mic-fix button
      if (this._iosNonSafari()) {
        var b = /CriOS/i.test(navigator.userAgent || "") ? "Chrome" : "this browser";
        // Keyboard mic FIRST (user 2026-08-01: "each phone has its own microphone… exploit that"):
        // it dictates into the field with no site permission at all. Safari stays the secondary
        // route for anyone who wants the site's own live-streaming dictation.
        this.aiAskError = b + " can't run the site's dictation on iPhone/iPad — but your keyboard's own 🎙 works right here:";
        this.micHelp = {
          title: "Use your keyboard's microphone — it works in every browser:",
          copyLink: true,
          steps: [
            "The question box is focused and your keyboard is up (we just did that for you).",
            "Tap the 🎙 key ON THE KEYBOARD and start talking — the words type straight into the box. No site permission needed.",
            "Prefer the site's built-in live dictation instead? That needs Safari — tap “Copy page link” below and open it there."
          ]
        };
        this._requestAiMicHelp("speech-service");   // upgrade in place with device-exact steps
      } else {
        this.aiAskError = "The speech service isn't available in this browser — use your keyboard's dictation (🎙 key on phones, Win+H on Windows) or Chrome / Safari.";
        this.micHelp = null;
      }
    },
    // "Copy page link" for the carry-to-Safari flow — clipboard API first, execCommand fallback.
    copyPageLink() {
      var self = this;
      var text = window.location.href;
      var done = function () {
        self.micLinkCopied = true;
        setTimeout(function () { self.micLinkCopied = false; }, 2500);
      };
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(done, function () { self._copyLinkFallback(text, done); });
          return;
        }
      } catch (e) { /* fall through */ }
      this._copyLinkFallback(text, done);
    },
    _copyLinkFallback(text, done) {
      try {
        var ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        done();
      } catch (e) { window.prompt("Copy this link:", text); }
    },
    // Device detection for the unblock steps. iOS is checked FIRST: every iOS browser (Safari, Chrome,
    // Edge) uses Safari's engine and the iOS permission model, so the steps are the same. iPadOS 13+
    // masquerades as "Macintosh" — the maxTouchPoints check catches it.
    _micHelpSteps() {
      var ua = navigator.userAgent || "";
      var isIOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1);
      // Phones lead with the KEYBOARD's own 🎙 (user 2026-08-01): it dictates into the field with no
      // site permission at all — the fastest fix even when the site's mic stays blocked.
      if (isIOS) {
        return { device: "iPhone / iPad", steps: [
          "Fastest fix: tap into the question box and use the 🎙 key on your keyboard — it types your words in with NO site permission needed.",
          "To unblock the site's own dictation instead: tap the “aA” (or ⋯) icon in the address bar, choose “Website Settings”, and set Microphone to Allow.",
          "If you don't see that option: open the iPhone Settings app, scroll to your browser (Safari or Chrome), and turn Microphone on.",
          "Come back to this page and tap “I've allowed it — try again.”"
        ] };
      }
      if (/Android/i.test(ua)) {
        return { device: "Android", steps: [
          "Fastest fix: tap into the question box and use the 🎙 on your keyboard (top right on most keyboards) — it types your words in with NO site permission needed.",
          "To unblock the site's own dictation instead: tap the lock (or ⚙) icon just left of the web address, then Permissions.",
          "Set Microphone to Allow. If Microphone isn't listed, tap “Reset permissions,” reload, and tap the 🎙 again to get a fresh prompt.",
          "Still blocked? The mic may be off for the Chrome APP itself: open the phone's Settings → Apps → Chrome → Permissions → Microphone → Allow.",
          "Come back to this page and tap “I've allowed it — try again.”"
        ] };
      }
      var browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : "your browser";
      var isMac = /Macintosh/.test(ua);
      return { device: "this computer (" + browser + ")", steps: [
        "Click the lock icon at the left end of the address bar.",
        (browser === "Firefox"
          ? "Under Permissions, clear the blocked Microphone entry (click the ✕ next to it)."
          : "Open Site permissions (or the Permissions list) and set Microphone to Allow."),
        "Reload the page if asked, then click “I've allowed it — try again.”",
        (isMac
          ? "Alternative: click into the question box and use macOS dictation (press the 🎤 key, or double-press Fn) — no site permission needed."
          : "Alternative: click into the question box and press Win + H — Windows dictation types right in, no site permission needed.")
      ] };
    },
    // HARD stop for dictation, used when the user moves on (Ask / clear / collapse) rather than deliberately
    // toggling the mic off. Two things matter here and neither is optional:
    //   1. Detach onresult FIRST. That handler does `aiQuestion = base + full transcript` — a wholesale
    //      overwrite. askQuestion clears aiQuestion right after it reads it, so a late result landing after
    //      the ask started would silently re-populate the box with the old dictation. Detaching makes the
    //      race impossible regardless of engine timing.
    //   2. abort() over stop(): stop() still delivers one final result, abort() discards it. Nothing is lost
    //      because interimResults=true has already streamed the words into the box.
    // The deliberate mic-off path (heroVoiceToggle) still uses stop() so the final, punctuated result lands.
    heroVoiceStop() {
      var rec = this._heroRec;
      if (!rec) return;
      try { rec.onresult = null; } catch (e) { /* */ }
      try { rec.abort(); } catch (e) { try { rec.stop(); } catch (e2) { /* */ } }
      this.heroVoice.on = false;
    },
    // Re-show a past question's answer + its proposed view WITHOUT another API call (instant + free).
    replayQuestion(h) {
      if (!h || this.aiAskBusy) return;   // don't reshape the view out from under an in-flight ask
      this.aiQuestion = h.q || "";
      this.aiAnswer = h.a || "";
      this.aiProposedNote = h.note || "";
      this.aiAskError = "";
      this.aiTable = (h.table && Array.isArray(h.table.columns)) ? h.table : null;   // same-session replays keep the result card
      // Selecting a past question RE-APPLIES its view like a fresh ask: CLEAR every filter first, then overlay
      // whatever that question proposed (empty {} = a pure clear-all). Without this, switching from one past
      // question to another left the FIRST question's filters (e.g. Customer XI116) applied under the second's
      // card. Snapshot first so ↩ Undo restores the prior view. (applyProposedFilters clears aiProposed + reloads.)
      var applied = (h.applied && Object.keys(h.applied).length) ? JSON.parse(JSON.stringify(h.applied)) : {};
      this._aiPrevFilters = JSON.parse(JSON.stringify(this.filters));
      this._aiPrevPreset = this.activePreset;
      this.applyProposedFilters(applied);   // clears all filters + applies this question's view + reloads
      this.aiApplied = Object.keys(applied).length > 0;
      // DB-backed entries archive the result table in rpt_AiHistory.Snapshot — fetch it lazily on the
      // FIRST replay after a reload so the card comes back (the answer is only 1-2 sentences when a
      // table exists). One attempt per entry; a miss (old row / text-only answer) stays a miss.
      if (!this.aiTable && Number(h.id) > 0 && !h._tableTried) {
        h._tableTried = true;
        var self = this, q = h.q || "";
        httpClient.get("/report/ai-history/table", { params: { id: Number(h.id) } })
          .then(function (res) {
            var raw = res.data && res.data.json;
            if (!raw) return;
            var t = null;
            try { t = JSON.parse(raw); } catch (e) { t = null; }
            if (t && Array.isArray(t.columns) && Array.isArray(t.rows) && t.rows.length) {
              h.table = t;
              if (self.aiQuestion === q) self.aiTable = t;   // only if this entry is still the one on screen
            }
          })
          .catch(function () { /* best-effort */ });
      }
    },
    // Remove a past question from the Ask history (rides in the per-user dashboard blob → persist). New ones are
    // added automatically when you ask; this lets you prune the list (handy on mobile / on the go).
    removeQuestion(h) {
      var i = this.qaHistory.indexOf(h);
      if (i >= 0) this.qaHistory.splice(i, 1);
      if (this.askHistHover === h) this.askHistHover = this.qaHistory[0] || null;
      // History is DB-backed (rpt_AiHistory): delete the caller's own row too, so the removal
      // SURVIVES a reload. Numeric id = a DB row; best-effort (a failure just re-lists on reload).
      var idNum = Number(h && h.id);
      if (idNum > 0) httpClient.post("/report/ai-history/delete", { id: idNum }).catch(function () { /* best-effort */ });
      this.persistDash();
    },
    // Master-only: wipe an ENTIRE user's hero-chat history (the group's 🗑 "Delete all"). Confirms, then
    // deletes server-side by the group's ownerId (exposed to Masters only) and prunes every matching row
    // locally. Backend re-checks the REAL Master role, so this can't be forced by a role-preview.
    removeUserHistory(g) {
      if (!this.isRealMaster || !g || !g.ownerId || g.own) return;
      var n = (g.items || []).length;
      if (!window.confirm('Delete ALL of ' + g.name + "'s hero chat history" + (n ? " (" + n + " shown)" : "") + "? This can't be undone.")) return;
      var owner = g.ownerId, self = this;
      httpClient.post("/report/ai-history/delete-user", { userId: owner })
        .then(function () {
          // Drop every row for that owner from the in-session list (the group disappears).
          self.qaHistory = (self.qaHistory || []).filter(function (x) { return x.ownerId !== owner; });
          if (self.askHistHover && self.askHistHover.ownerId === owner) self.askHistHover = self.qaHistory[0] || null;
          self.persistDash();
        })
        .catch(function () { /* best-effort — a failure just re-lists on reload */ });
    },
    // Load the per-user AI history (insights + questions) from rpt_AiHistory (DB-backed; the analytics store).
    loadAiHistory() {
      var self = this;
      // When a Master / Sales All is previewing another user (header "view as user"), ask the backend for
      // THAT user's chat history so the hero shows their questions (labeled "you" in the preview). Cleared
      // preview → my own scope. The server ignores viewAsUserId for non-privileged callers.
      var asUser = this.viewingAsUser;
      var params = (asUser && asUser.id) ? { viewAsUserId: asUser.id } : {};
      httpClient.get("/report/ai-history", { params: params })
        .then(function (res) {
          var d = res.data || {};
          self.aiInsightHistory = Array.isArray(d.insights) ? d.insights : [];
          if (Array.isArray(d.questions)) {
            var mapped = d.questions.map(function (q) {
              var applied = null;
              if (q.applied) { try { applied = JSON.parse(q.applied); } catch (e) { applied = null; } }
              // id stays NUMERIC (the rpt_AiHistory row id) so the × can DELETE the row; repName/own
              // drive the group-by-rep rendering (Sales All / Master see the whole team's history).
              return { id: q.id, q: q.question, a: q.answer, note: "", applied: applied, ts: q.createdAt,
                       ms: (typeof q.durationMs === "number" ? q.durationMs : null),
                       repName: q.repName || "", own: q.own !== false, ownerId: q.ownerId || "" };
            });
            // DEDUPE per rep by normalized question, keeping the MOST RECENT (rows arrive newest-first, so
            // the first occurrence of each question is the one to keep). A rep asking the same thing twice
            // shows once; the same question by a different rep is a distinct entry.
            var seenQ = Object.create(null);
            self.qaHistory = mapped.filter(function (x) {
              var key = (x.repName || "") + "||" + self._qKey(x.q);
              if (seenQ[key]) return false; seenQ[key] = true; return true;
            });
          }
        })
        .catch(function () { /* rpt_AiHistory not deployed yet — leave in-memory history as is */ });
    },
    // Load a past insight back into the panel as the current result.
    loadInsightFromHistory(h) {
      if (!h) return;
      this.aiInsights = h.insights || "";
      this.aiCoaching = h.coaching || "";
      this.aiInsightsStale = true;   // it reflects a past filter set, not necessarily the current view
      this.aiHistOpen = false;
      this.aiHistHover = null;
    },
    fmtHistDate(iso) {
      if (!iso) return "";
      var s = String(iso);
      if (!/[zZ]|[+-]\d\d:?\d\d$/.test(s)) s += "Z";   // backend datetime2 is UTC but tag-less
      var d = new Date(s);
      if (isNaN(d.getTime())) return s;
      return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    },
    // Bullet-parse arbitrary insight/coaching text for the history preview (same rules as aiInsightsLines).
    histLines(raw) {
      return String(raw || "").split(/\r?\n/).map(function (l) { return l.trim(); }).filter(function (l) { return l.length > 0; })
        .map(function (l) { var m = l.match(/^([-•*]|\d+[.)])\s+(.*)$/); return m ? { bullet: true, text: m[2] } : { bullet: false, text: l }; });
    },
    // Widen the Sales Insights drawer ~2" per click; once near the max width, the next click resets it to default.
    widenStory() {
      var step = 190;   // ~2 inches at 96dpi
      var maxW = Math.min((((typeof window !== "undefined" && window.innerWidth) || 1200) - 64), 1100);
      if (this.storyWidth >= maxW) { this.storyWidth = 340; return; }
      this.storyWidth = Math.min(maxW, this.storyWidth + step);
    },
    // Open the Sales Insights drawer at "3 clicks" wide (340 + 3×190 = 910px) so it's roomy the moment
    // the Insights toggle is clicked; capped to 92vw by CSS on narrow screens.
    openStory() {
      var maxW = Math.min((((typeof window !== "undefined" && window.innerWidth) || 1200) - 64), 1100);
      this.storyWidth = Math.min(maxW, 910);
      this.storyHidden = false;
    },
    // Open/close the insights 🕘 popup; auto-preview the most recent entry on open.
    toggleAiHist() {
      this.aiHistOpen = !this.aiHistOpen;
      if (this.aiHistOpen && this.aiInsightHistory.length) this.aiHistHover = this.aiInsightHistory[0];
    },
    // Open/close the chat (Ask) 🕘 history; auto-preview the most recent question on open.
    toggleAskHist() {
      this.aiAskHistOpen = !this.aiAskHistOpen;
      if (this.aiAskHistOpen && this.qaHistory.length) this.askHistHover = this.qaHistory[0];
    },
    // Defined KPI — saved (un-submitted) Orders vs Quotes for the selected customer(s).
    // Scoped only by the customer filter (carts are per-company); refreshes with the filters.
    loadSavedCarts() {
      httpClient.get("/report/customer-insights/saved-carts", { params: this.buildParams(false) })
        .then((res) => {
          const d = res.data || {};
          this.savedCarts = { savedOrders: Number(d.savedOrders) || 0, savedQuotes: Number(d.savedQuotes) || 0 };
        })
        .catch(() => { this.savedCarts = { savedOrders: 0, savedQuotes: 0 }; });
    },
    // ── Orders In Process card ──
    // Recently-placed submitted orders that aren't complete yet, scoped like the rest of the report (customer /
    // territory / rep filters + the mandatory server scope; the date range is intentionally ignored — it's a live
    // status view, like Saved Carts).
    loadOrdersInProcess() {
      var self = this;
      this.oip.busy = true;
      httpClient.get("/report/customer-insights/orders-in-process", { params: this.buildParams(false) })
        .then(function (res) { self.oip.orders = (res.data && res.data.orders) || []; })
        .catch(function () { self.oip.orders = []; })
        .finally(function () { self.oip.busy = false; self.autoSizeCards(); });
    },
    // Collapse the report's variable-content cards to fit their content (desktop only — mobile auto-heights via the
    // reflow CSS). Each is a fixed vue-grid-layout item, so "fit to content" = setting its grid `h`. We size on the
    // LIVE dashLayout (works whether a card was injected from DASH_DEFAULT or BAKED into the stored Layout 1 — the
    // reason the static DASH_DEFAULT h didn't take), then flag the resulting layout-updated so it is NOT persisted:
    // on the shared Default a Master save republishes Layout 1 to everyone, and the fit is per-viewer + recomputed on
    // load. New array ref → vue-grid-layout's ref-based `layout` watcher re-compacts (cards below pull UP, no gap).
    // Wired to the data watchers (byCustomer / byTerritory / bySalesRep / savedCarts / oip.orders) + applyLayout.
    autoSizeCards() {
      // AUTO-HEIGHT REMOVED (user request 2026-07-23). The programmatic re-sizing of the variable-content cards
      // (Sales by Territory, Sales Reps, Saved Carts, Orders In Process, Customer ID(s)) kept overriding the card
      // heights the Master set — so a saved MAIN or DOCKED layout could never hold a consistent height. Cards now
      // keep whatever height the saved layout gives them and scroll internally. Left as a no-op so every call site
      // (data watchers, applyLayout, the OIP-load .finally) stays harmless — no card height is ever changed
      // programmatically now; only the user's drag-resize + the saved layout set heights.
      return;
    },
    // Measure a card's NATURAL content height (px) → the grid `h` that fits it. Transiently removes the grid cell's
    // fixed height (and the .dash-card height:100%) so scrollHeight reports the header + body content extent, then
    // restores synchronously (before any paint → no flicker). 37px per grid row (row-height 25 + margin 12).
    measureCardH(id) {
      if (typeof document === "undefined") return null;
      var card = document.querySelector('.ci-wrap [data-card-id="' + id + '"]');
      if (!card) return null;
      var gi = card.closest(".vue-grid-item");
      if (!gi) return null;
      var giH = gi.style.height, cH = card.style.height;
      gi.style.height = "auto"; card.style.height = "auto";
      var natural = card.scrollHeight;
      gi.style.height = giH; card.style.height = cH;
      if (!natural) return null;
      return Math.max(3, Math.min(20, Math.ceil((natural + 16) / 37)));
    },
    // Status → chip class (amber Review / blue Preparing to Ship / teal Gathering Materials).
    oipStatusClass(s) {
      var t = (s || "").toLowerCase();
      if (t.indexOf("review") >= 0) return "is-review";
      if (t.indexOf("prepar") >= 0) return "is-prep";
      if (t.indexOf("gather") >= 0 || t.indexOf("firm") >= 0) return "is-gather";
      return "";
    },
    // 👁 → open the order-detail modal. Fetches the report-scoped detail payload (same shape as GET /order/{id})
    // and hands it to the embedded OrderDetails.vue. An empty array = not found / outside the rep's territory.
    openOrderDetail(orderId) {
      if (!orderId) return;
      var self = this;
      this.oipDetail = { open: true, busy: true, orderId: orderId, data: null };
      httpClient.get("/report/customer-insights/order-detail", { params: { id: orderId } })
        .then(function (res) {
          var arr = res.data;
          self.oipDetail.data = (Array.isArray(arr) && arr.length) ? arr : null;
        })
        .catch(function () { self.oipDetail.data = null; })
        .finally(function () { self.oipDetail.busy = false; });
    },
    closeOrderDetail() { this.oipDetail = { open: false, busy: false, orderId: null, data: null }; },
    // Measure how much room the answer body actually has and cap it there. Called whenever anything
    // that moves the card changes: a new answer, the window resizing or zooming, the card being pinned
    // as the page scrolls. rAF-throttled because it runs on scroll.
    sizeAnswerScroll() {
      if (this._sizeQueued) return;
      this._sizeQueued = true;
      var self = this;
      window.requestAnimationFrame(function () {
        self._sizeQueued = false;
        var el = self.$el && self.$el.querySelector(".ci-ask-answer-scroll");
        if (!el) return;
        var top = el.getBoundingClientRect().top;
        // 56px leaves room for the collapse handle (which sits OUTSIDE this box) plus a little air.
        var room = Math.round(window.innerHeight - top - 56);
        // Never collapse to nothing on a short window — below ~180px the answer is unusable anyway and
        // the page scroll is the better escape hatch.
        el.style.setProperty("--ci-answer-max", Math.max(180, room) + "px");
      });
    },
    qrToggle(n) { this.qrOpen = Object.assign({}, this.qrOpen, { [n]: !this.qrOpen[n] }); this.sizeAnswerScroll(); },
    // The two or three flags a rep must see WITHOUT expanding the row — the ones that change whether
    // the quoted price can be trusted at all. The rest live in the expanded detail.
    qrKeyFlags(l) {
      var show = {
        ambiguousSingleAmount: "unit price uncertain",
        suspectExtendedMisread: "may be a line total",
        arithmeticDidNotConfirm: "unverified",
        fractionalQuantity: "unverified",
        identicalAmountsAcrossLines: "rows may not have been read separately",
        matchNotCorroborated: "part match doubtful",
        duplicatePartMatch: "duplicate part match",
        foreignCurrency: "other currency",
        pricedPerPack: "per pack",
        pricedPerHundred: "per 100",
        pricedPerThousand: "per 1,000",
        notASaleLine: "not a priced line",
      };
      var out = [];
      (l.flags || []).forEach(function (f) { if (show[f] && out.indexOf(show[f]) < 0) out.push(show[f]); });
      return out.slice(0, 3);
    },
    /**
     * Turn the answered quote into the dashboard view: customer chip + the window that was compared.
     *
     * lockDates is deliberate — the rep picked this window in the dialog, so it must not be second-guessed
     * by the all-time confirm gate they already answered. The pre-apply snapshot is taken HERE because the
     * attachment path bypasses askQuestion, which is where the plain ask stages its undo.
     */
    _applyQuoteView(d) {
      var ids = (d && (d.comparedAgainst || d.customers)) || [];
      if (!Array.isArray(ids)) ids = [ids];
      ids = ids.filter(Boolean);
      if (!ids.length) return;   // no customer resolved → nothing honest to filter on
      this._aiPrevFilters = JSON.parse(JSON.stringify(this.filters));
      this._aiPrevPreset = this.activePreset;
      var from = (this.qrRead && this.qrRead.historyFrom) || "1990-01-01";   // all history = the dataset floor
      var spec = { customer: ids.slice(0, 10), from: from, to: new Date().toISOString().slice(0, 10) };
      this.applyProposedFilters(spec, { lockDates: true });
      this.aiApplied = true;
    },
    // Pull the letterhead into the browser cache while the rep reads the answer, so Print never has to
    // wait on it. Fire-and-forget: a failure here changes nothing (the print path waits and falls back).
    _warmPrintLogo() {
      if (this._logoWarmed || typeof Image === "undefined") return;
      this._logoWarmed = true;
      try {
        // require() of an asset yields the URL, or {default: url} depending on the loader's esModule flag.
        var mod = require("@/assets/img/steele-logo.svg");
        var img = new Image();
        img.src = (mod && mod.default) ? mod.default : mod;
      } catch (e) { /* the print path still waits for the real element */ }
    },
    // 📄 Open the document viewer, optionally jumping to a row's text.
    openQrDoc(find) {
      // Long raw rows make a poor search term — the first few tokens locate the row just as well.
      var f = String(find || "").trim().split(/\s+/).slice(0, 4).join(" ");
      // ROWS first (user 2026-08-12). The census reconstruction is the view that answers "why was this
      // line not compared" — the raw text is genuinely out of order (the page emits each line-number
      // AFTER its row), so it is the reference, not the starting point.
      this.qrDoc = { open: true, tab: "rows", find: f };
      this.$nextTick(this.sizeQuotePrompt);   // pushes the panel below the fixed header
    },
    // 🔍 Open View/Fix for one line, pre-filled with everything we DID read — so the rep is correcting a
    // reading, not typing a line from scratch.
    // ---- remove / restore a line by hand ----------------------------------------------------
    removeQrLine(l) {
      var i = (this.qrLines || []).indexOf(l);
      if (i < 0) return;
      this.qrLines.splice(i, 1);
      this.qrRemoved.push({ at: i, line: l });
      // The print / Excel pickers hold row ids from before the removal; drop the stale selection so a
      // removed line cannot come back through an export.
      this.qrPrintPick = { open: false, mode: "print", rows: [] };
      recordAction("report-quote-line-remove",
                   (l.partQuoted || l.partMatched || "line " + l.line) +
                   (l.quoteLine ? " (quote line " + l.quoteLine + ")" : ""));
    },
    undoQrRemove() {
      var last = this.qrRemoved.pop();
      if (!last) return;
      var at = Math.min(last.at, (this.qrLines || []).length);
      this.qrLines.splice(at, 0, last.line);
    },
    restoreAllQrRemoved() {
      // Oldest first, so each row lands back on the index it was taken from.
      var all = this.qrRemoved.slice().sort(function (a, b) { return a.at - b.at; });
      this.qrRemoved = [];
      for (var i = 0; i < all.length; i++)
        this.qrLines.splice(Math.min(all[i].at, this.qrLines.length), 0, all[i].line);
    },
    openQrFix(l) {
      if (!l) return;
      this.$nextTick(this.sizeQuotePrompt);   // same push-down as the quote dialog
      this.qrFix = {
        open: true,
        line: l.line,
        raw: l.raw || "",
        reason: l.notComparedReason || "",
        note: l.note || "",
        candidates: (l.unitCandidates || []).slice(0, 8),
        part: l.partQuoted || l.partMatched || "",
        qty: l.qty != null ? String(l.qty) : "",
        amount: l.quotedUnit != null ? String(l.quotedUnit) : "",
        isExt: false,
        busy: false,
        error: "",
      };
    },
    // Send the correction to the server, which re-runs the SAME resolver against the SAME history and
    // window. The row that comes back is marked as the rep's (source = "rep") and rendered with a chip:
    // a supplied number and a proven one must never look alike.
    submitQrFix() {
      var self = this;
      var f = this.qrFix;
      if (!f.open || f.busy) return;
      var cust = (this.qrLines || []).map(function (x) { return x.past && x.past.customerId; })
                     .filter(Boolean)[0]
                 || (this.qrRead && this.qrRead.customerId) || "";
      if (!cust) { f.error = "I could not tell which customer this quote is for, so there is nothing to compare against."; return; }
      f.busy = true; f.error = "";
      httpClient.post("/report/quote-line-fix", {
        customerId: cust,
        historyFrom: (this.qrRead && this.qrRead.historyFrom) || "",
        line: f.line,
        part: f.part,
        qty: f.qty,
        amount: f.amount,
        amountIsExtended: !!f.isExt,
        raw: f.raw,
      }).then(function (res) {
        var d = res.data || {};
        if (!d.ok || !d.line) { f.error = d.message || "That line could not be recomputed."; return; }
        // Replace the row in place so the table, the print sheet and the Excel export all see the fix.
        var i = (self.qrLines || []).findIndex(function (x) { return x.line === f.line; });
        if (i >= 0) self.qrLines.splice(i, 1, d.line);
        else self.qrLines.push(d.line);
        f.open = false;
        self.$nextTick(self.sizeAnswerScroll);
      }).catch(function () {
        f.error = "Couldn't reach the service to recompute that line.";
      }).then(function () { f.busy = false; });
    },
    // Is there a price on this line the arithmetic actually resolved? This is the ONE test behind the
    // picker's default: no readable price means no comparison, and a row of dashes is not something a rep
    // wants to hand a customer. It is a DEFAULT, not a rule — the rep can tick it back on.
    qrRowUnreadable(l) {
      return !l || l.quotedUnit == null || l.matchState === "priceNotRead";
    },
    // 🖨 Print / ⬇ Excel — both open the same row picker first (user 2026-08-12), mirroring the result
    // card's Excel picker. One picker, one selection, two outputs: whichever the rep asked for.
    openQrPrintPick(mode) {
      if (!this.qrLines.length) return;
      var self = this;
      this.qrPrintPick = {
        open: true,
        mode: mode === "excel" ? "excel" : "print",
        rows: this.qrLines.map(function (l) {
          var readable = !self.qrRowUnreadable(l);
          return {
            line: l.line,
            label: l.partQuoted || l.partMatched || ("line " + l.line),
            qty: l.qty != null ? self.fmtNum(l.qty) : "",
            price: l.quotedUnit != null ? self.fmtMoney(l.quotedUnit) : "",
            readable: readable,
            checked: readable,
          };
        })
      };
    },
    setQrPrintAll(v) { (this.qrPrintPick.rows || []).forEach(function (r) { r.checked = !!v; }); },
    // Close the picker, then do the thing. The selection is KEPT so a second print/export of the same
    // answer does not make the rep re-tick everything; a new answer rebuilds it (see askWithAttachments).
    confirmQrPrint() {
      if (!this.qrPrintPickCount) return;
      var mode = this.qrPrintPick.mode;
      this.qrPrintPick.open = false;
      if (mode === "excel") this.exportQuoteWorkbook();
      else this.quotePresentation();
    },
    /**
     * ⬇ Excel — the comparison as a real .xlsx workbook: a Read Me sheet naming the customer and the
     * history window, a Comparison sheet (one row per selected line), then ONE SHEET PER PART carrying
     * that part's past-order statistics (user 2026-08-12).
     *
     * Same mechanism as the result card's export — XLSX + _xlsxSheet + _xlsxSheetName — so there stays
     * one workbook path in this file rather than a second one to keep in step.
     *
     * EVERY NUMBER IS WRITTEN AS A REAL NUMBER, never a formatted string: this workbook exists so a rep
     * can sort and total it. Money keeps its own cells; percentages stay percentages as printed on screen.
     * Nothing is recomputed here — each value is the one the server resolved and the card displayed, so
     * the spreadsheet cannot disagree with the answer it came from.
     */
    exportQuoteWorkbook() {
      var lines = this.qrPrintLines;
      if (!lines.length || typeof document === "undefined") return;
      var self = this;
      var seesCost = !!this.qrCostIncluded;
      var win = (this.qrRead && this.qrRead.historyLabel) || "all history";
      var cust = lines.map(function (l) { return l.past && l.past.customerId; }).filter(Boolean)[0] || "";
      var files = (this.heroFileNotes || []).filter(function (n) { return n && n.ok; })
                      .map(function (n) { return n.name; });
      var used = Object.create(null);   // null-proto: a part number must never collide with Object.prototype
      var wb = XLSX.utils.book_new();
      var num = function (v) { return v == null ? "" : v; };   // null → blank cell, never 0

      // ---- Read Me: what this workbook is, and what it was measured against ----------------
      var meta = [
        ["Customer", cust || "(not identified)"],
        ["Compared against", win],
        ["Lines in this workbook", lines.length],
        ["Lines on the quote", (this.qrLines || []).length],
        ["Quote file(s)", files.join(", ")],
        ["Generated", new Date().toLocaleString()],
        ["", ""],
        ["Every price is PER UNIT", "Quoted prices and past-order prices are both per unit, so they compare directly."],
        ["A blank average", "means this part has no orders in the window above — not that it was never bought."],
        ["Margin", seesCost ? "Margin is at the QUOTED price, from current cost." : "Withheld: this export was made by a user without cost access."],
      ];
      XLSX.utils.book_append_sheet(wb, this._xlsxSheet(["Quote review", ""], meta, -1),
                                   this._xlsxSheetName("Read Me", used));

      // ---- Comparison: one row per selected line ------------------------------------------
      // "Quote Line" is the number printed on the document — the one to sort and cross-reference by.
      // "Read Seq" is only the order the rows arrived in, kept because it disambiguates two rows that the
      // census could not number.
      var cols = ["Quote Line", "Read Seq", "Quoted Part", "Matched Part", "Description", "Qty", "UOM",
                  "Quoted Unit Price", "Per Piece", "Avg of Past Orders", "% vs Avg", "% vs Last Paid",
                  "Margin %", "Verdict", "Reading", "Confidence", "Status", "Why Not Compared"];
      var rows = lines.map(function (l) {
        return [num(l.quoteLine), l.line, l.partQuoted || "", l.partMatched || "", l.description || "", num(l.qty),
                l.qtyUom || "", num(l.quotedUnit), num(l.quotedPerPiece), num(l.pastAvg), num(l.pctVsAvg),
                num(l.pctVsLastPaid), num(l.marginPct), l.verdict || "", l.how || "", l.confidence || "",
                self.qrStatusText(l), l.notComparedReason || ""];
      });
      XLSX.utils.book_append_sheet(wb, this._xlsxSheet(cols, rows, -1),
                                   this._xlsxSheetName("Comparison", used));

      // ---- One sheet per part -------------------------------------------------------------
      lines.forEach(function (l) {
        var p = l.past || null;
        var out = [
          ["QUOTE LINE", ""],
          ["Line number on the quote", num(l.quoteLine)],
          ["Quoted part", l.partQuoted || ""],
          ["Matched part", l.partMatched || "(no match in their order history)"],
          ["Description", l.description || ""],
          ["Quantity", num(l.qty)],
          ["Unit of measure", l.qtyUom || ""],
          ["Components listed under this line", num(l.descriptionRows)],
          ["Quoted unit price", num(l.quotedUnit)],
          ["Quoted price per piece", num(l.quotedPerPiece)],
          ["Currency", l.currency || ""],
          ["How the price was read", l.how || ""],
          ["Confidence in that reading", l.confidence || ""],
          ["Verdict", l.verdict || ""],
          ["% vs their average", num(l.pctVsAvg)],
          ["% vs what they last paid", num(l.pctVsLastPaid)],
          ["Margin at the quoted price %", num(l.marginPct)],
          ["", ""],
          ["PAST ORDERS" + (p && p.customerId ? " — " + p.customerId : "") + " (" + win + ")", ""],
        ];
        if (p) {
          out.push(["Orders", num(p.orders)]);
          out.push(["Total quantity", num(p.totalQty)]);
          out.push(["Last paid", num(p.lastPaid)]);
          out.push(["Weighted average paid", num(p.avgPaid)]);
          out.push(["Lowest paid", num(p.minPaid)]);
          out.push(["Highest paid", num(p.maxPaid)]);
          out.push(["Spread (highest / lowest)", num(p.spreadRatio)]);
          out.push(["First order", p.firstOrder || ""]);
          out.push(["Last order", p.lastOrder || ""]);
          out.push(["Everyone else average", num(p.everyoneElseAvg)]);
          out.push(["Everyone else customers", num(p.everyoneElseCustomers)]);
          if (p.listPrice != null) out.push(["List price", p.listPrice]);
          if (p.unitCost != null) out.push(["Unit cost", p.unitCost]);
          if (p.withinTheirRange != null)
            out.push(["Quoted price inside everything they have paid", p.withinTheirRange ? "Yes" : "No"]);
        } else {
          out.push(["No past orders for this part under this customer" + (win === "all history" ? "" : " " + win), ""]);
        }
        // The reading and its caveats travel WITH the numbers. A spreadsheet cell cannot show a chip, so a
        // price the quote never settled would otherwise look as solid as one it proved.
        var notes = [];
        if (l.note) notes.push(l.note);
        (l.flagsExplained || []).forEach(function (f) { notes.push(f); });
        (l.alternateReadings || []).forEach(function (a) { notes.push("Also reads as: " + a); });
        if (l.notComparedReason) notes.push(l.notComparedReason);
        if (notes.length) {
          out.push(["", ""]);
          out.push(["NOTES", ""]);
          notes.forEach(function (n, i) { out.push([i === 0 ? "Reading" : "", n]); });
        }
        // Prefixed with the quote's line number so the TAB ORDER matches the quote and two lines for the
        // same part cannot collide into "PART (2)".
        var base = l.partQuoted || l.partMatched || ("line " + l.line);
        var sheetName = l.quoteLine ? (l.quoteLine + " " + base) : base;
        XLSX.utils.book_append_sheet(wb, self._xlsxSheet(["Statistic", "Value"], out, -1),
                                     self._xlsxSheetName(sheetName, used));
      });

      recordAction("report-quote-excel", (cust || "quote") + " · " + lines.length + " line(s) · " + win);
      var stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, ("quote-review-" + (cust || "customer") + "-" + stamp)
                         .toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/-+/g, "-") + ".xlsx");
    },
    // The row's status in words — the chip text, for a cell that cannot show a chip.
    qrStatusText(l) {
      if (l && l.source === "rep") return "compared — your correction";
      if (!l || !l.matchState || l.matchState === "matched") return "compared";
      if (l.matchState === "notInHistory") return this.qrNewChipText;
      if (l.matchState === "partNotIdentified") return "part not identified — not checked";
      if (l.matchState === "priceNotRead") return "price not read";
      return "not a priced line";
    },
    // 🖨 the printable sheet itself — the same comparison on the Steele letterhead. Reuses the
    // print-isolation pattern the per-order sheet uses: while printing, every body child except the
    // sheet is hidden, so no drawer or popover can bleed onto the page.
    quotePresentation() {
      if (!this.qrLines.length) return;
      var self = this;
      if (this._qrPrintReset) { clearTimeout(this._qrPrintReset); this._qrPrintReset = null; }
      this.qrPrintOn = true;
      this.$nextTick(function () {
        // WAIT FOR THE LETTERHEAD. The sheet's contents only exist once qrPrintOn flips, so its logo
        // starts downloading milliseconds before window.print() — and window.print() snapshots the page
        // as it is RIGHT NOW. Nothing else on this report requests that file (the site header uses a
        // different one from /public), so on a real network the first print of a session raced the image
        // and printed an empty letterhead box. Capped, because a genuinely missing image must delay a
        // print by a moment, never block it.
        self._qrWhenLogoReady(function () { self._qrDoPrint(); });
      });
    },
    // Resolve as soon as the sheet's logo has loaded (or failed), and never later than 1.5s.
    _qrWhenLogoReady(done) {
      var img = this.$refs.qrPrintLogo;
      if (!img || img.complete) { done(); return; }
      var fired = false;
      var go = function () { if (!fired) { fired = true; done(); } };
      img.addEventListener("load", go, { once: true });
      img.addEventListener("error", go, { once: true });
      setTimeout(go, 1500);
    },
    _qrDoPrint() {
      var self = this;
      try { window.print(); }
      catch (e) { this.qrPrintOn = false; return; }   // print unavailable → don't strand the sheet
      // afterprint does the real cleanup; this is the backstop for a browser that never fires it.
      this._qrPrintReset = setTimeout(function () {
        self.qrPrintOn = false;
        if (typeof document !== "undefined") document.body.classList.remove("qrp-printing");
      }, 300000);
    },
    // 🖨 on an order row → render that one document into the print sheet, then print. No fetch: the row
    // already carries every line, notation and spec the sheet shows.
    printCodDoc(p) {
      if (!p || !p.doc) return;
      var self = this;
      // Drop any backstop still pending from a previous print — otherwise an old timer could fire mid-print
      // and blank THIS sheet.
      if (this._codPrintReset) { clearTimeout(this._codPrintReset); this._codPrintReset = null; }
      this.codPrint = { customer: p.customer || {}, doc: p.doc };
      // Wait for the sheet to exist in the DOM — window.print() captures the CURRENT document, so printing
      // before the patch lands would print an empty sheet.
      this.$nextTick(function () {
        try { window.print(); }
        catch (e) { self.codPrint = { customer: {}, doc: null }; return; }   // print unavailable → don't strand the sheet
        // afterprint does the real cleanup. This is the backstop for a browser that never fires it: a sheet
        // left armed would silently hijack the user's NEXT print, which is worse than dropping it late.
        self._codPrintReset = setTimeout(function () {
          if (self.codPrint.doc) { self.codPrint = { customer: {}, doc: null }; }
          if (typeof document !== "undefined") document.body.classList.remove("codp-printing");
        }, 300000);
      });
    },
    // Print isolation for the order-detail modal (see the mounted() listeners + the @media print block).
    onBeforePrint() {
      if (typeof document === "undefined") return;
      // Two isolations share these listeners. The order SHEET wins when both are live (it is the action the
      // user just took), and neither engages for an ordinary report print.
      var sheet = !!this.codPrint.doc;
      if (this.qrPrintOn) { document.body.classList.add("qrp-printing"); this.stripThemeForPrint(); return; }
      if (!sheet && !this.oipDetail.open) return;
      document.body.classList.add(sheet ? "codp-printing" : "oipd-printing");
      this.stripThemeForPrint();
    },
    // Print in LIGHT mode: dark mode paints a steel background IMAGE on <html>, which bleeds into the
    // printout. Dropped for the duration and restored in onAfterPrint.
    stripThemeForPrint() {
      var de = document.documentElement;
      this._printThemeRestore = { dark: de.classList.contains("dark-mode"), mid: de.classList.contains("mid-mode") };
      de.classList.remove("dark-mode");
      de.classList.remove("mid-mode");
    },
    onAfterPrint() {
      if (typeof document === "undefined") return;
      document.body.classList.remove("oipd-printing");
      document.body.classList.remove("codp-printing");
      document.body.classList.remove("qrp-printing");
      this.qrPrintOn = false;
      if (this._qrPrintReset) { clearTimeout(this._qrPrintReset); this._qrPrintReset = null; }
      if (this.codPrint.doc) this.codPrint = { customer: {}, doc: null };
      if (this._codPrintReset) { clearTimeout(this._codPrintReset); this._codPrintReset = null; }
      var de = document.documentElement, r = this._printThemeRestore;
      if (r) { if (r.dark) de.classList.add("dark-mode"); if (r.mid) de.classList.add("mid-mode"); this._printThemeRestore = null; }
    },
    // Switch the global company (header + cart context) to the selected customer's
    // company — or back to NC999 when cleared. Resolves Company.Code → id, then
    // asks Head.vue to switch silently (no reload) so this report keeps its state.
    syncCompany(code) {
      const lookup = code || "NC999";
      httpClient.get("/report/company-by-code", { params: { code: lookup } })
        .then((res) => {
          const c = res.data;
          if (c && c.id) {
            // Label carries the CODE ("Name - (CODE)", the Master shape) so Head's companyCode
            // extraction works — the old bare-name label stored companyCode='' on every report-driven
            // switch. carryCarts keeps cart pointers honest on this silent switch (banks the outgoing
            // company's pointers, restores the target's) without the reload the report can't afford.
            this.$root.$emit("report-company-switch", {
              value: c.id,
              text: (c.name || lookup) + " - (" + (c.code || lookup) + ")",
              carryCarts: true
            });
          }
        })
        .catch(() => {});
    },
    // "Returning to NC999" prompt (see the filters.customer watcher). Continue = accept the multi-
    // customer view: snapshot the chips and home the company selector. Back out = remove the customers
    // that were just added (cascade-removing any rep/territory they alone contributed, via
    // crossRemoveCustomer) and stay on the current company; _chipRevertBusy keeps the watcher from
    // re-prompting or re-syncing mid-revert (Vue flushes watchers before the $nextTick reset below).
    chipHomeContinue() {
      this.chipHomePrompt = false;
      this._prevCustomerChips = (this.filters.customer || []).slice();
      this.syncCompany("");
    },
    chipHomeBack() {
      this.chipHomePrompt = false;
      var self = this;
      var keep = {};
      (this._prevCustomerChips || []).forEach(function (c) { keep[String(c)] = 1; });
      var added = (this.filters.customer || []).filter(function (c) { return !keep[String(c)]; });
      this._chipRevertBusy = true;
      added.forEach(function (c) { self.crossRemoveCustomer(String(c)); });
      // A wholesale apply (favorite / hero "apply this view") can also DROP the pre-existing chip —
      // put it back so backing out really restores the before-image.
      (this._prevCustomerChips || []).forEach(function (c) {
        if (self.filters.customer.indexOf(c) < 0) self.filters.customer.push(c);
      });
      this.$nextTick(function () { self._chipRevertBusy = false; });
      this.page = 1;
      this.load();
      this.loadDetails();
    },
    l2pct(v, max) {
      var m = Number(max) || 0;
      if (m <= 0) return "0%";
      return Math.max(2, (Number(v) || 0) / m * 100).toFixed(1) + "%";
    },
    // Per-account report layout preference (Layout 1 default / Layout 2 redesign).
    loadLayout() {
      // The old Layout 1/2 page-design toggle is retired — the report is always the card dashboard now,
      // and the "Layouts" picker manages named per-user arrangements instead. No-op kept for the mounted() call.
    },
    setLayout(val) {
      var v = val === "2" ? "2" : "1";
      this.layout = v;
      httpClient.post("/user/report-layout", { layout: v }).catch(function () { /* non-fatal */ });
    },
    // ----- per-account Layout-1 dashboard pad arrangement (mirrors report-layout) -----
    isAiCard(i) { return typeof i === "string" && i.indexOf("ai-") === 0; },
    isCustomCard(i) { return typeof i === "string" && i.indexOf("cust-") === 0; },
    isBuiltinCard(i) { return !this.isAiCard(i) && !this.isCustomCard(i); },
    aiBarMax(items) { return (items || []).reduce(function (m, it) { return Math.max(m, Number(it.value) || 0); }, 0); },
    dashTitle(i) {
      if (this.isAiCard(i) && this.aiCards[i]) return this.aiCards[i].title || "AI card";
      if (this.isCustomCard(i) && this.customCards[i]) return this.customCards[i].title || "Card";
      return DASH_TITLES[i] || i;
    },
    // Build an AI card from the user's prompt + the current report filters.
    createAiCard() {
      var self = this;
      if (!this.aiPrompt.trim()) return;
      this.aiBusy = true;
      this.aiError = "";
      httpClient.post("/report/ai-card", { prompt: this.aiPrompt, filters: this.buildParams(false) })
        .then(function (res) {
          var d = res.data || {};
          if (d.ok && d.card) {
            var card = d.card;
            var id = "ai-" + Date.now();
            self.$set(self.aiCards, id, card);
            // Place the new card on a fresh row below everything currently on the grid.
            var maxY = (self.dashLayout || []).reduce(function (m, it) { return Math.max(m, (Number(it.y) || 0) + (Number(it.h) || 0)); }, 0);
            var isBar = card.viz === "barlist";
            self.dashLayout.push({ i: id, x: 0, y: maxY, w: isBar ? 2 : 1, h: isBar ? 6 : 2 });
            self.aiPrompt = "";
            self.aiOpen = false;
            self.saveDashLayout();
          } else {
            self.aiError = d.message || "Couldn't build that card.";
          }
        })
        .catch(function () { self.aiError = "Something went wrong building that card. Please try again."; })
        .finally(function () { self.aiBusy = false; });
    },
    // Remove an AI card from the grid + its stored definition, then persist.
    removeAiCard(id) {
      this.dashLayout = (this.dashLayout || []).filter(function (it) { return it.i !== id; });
      this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
      this.$delete(this.aiCards, id);
      this.saveDashLayout();
    },
    // Re-fetch each AI card's data deterministically when the filters change.
    refreshAiCards() {
      var self = this;
      Object.keys(this.aiCards || {}).forEach(function (id) {
        var c = self.aiCards[id];
        if (!c || !c.cardId) return;
        httpClient.get("/report/customer-insights/card", { params: Object.assign({ cardId: c.cardId }, self.buildParams(false)) })
          .then(function (r) { if (r.data && r.data.ok && r.data.card) self.$set(self.aiCards, id, r.data.card); })
          .catch(function () { /* leave the existing card data in place */ });
      });
    },
    // ----- Master card designer: shared custom cards (content + bound data) -----
    // Load the shared catalog, reconcile with this user's saved layout (append newly-shared
    // cards, drop ones an admin deleted), then fetch each data card's data.
    loadCustomCards() {
      var self = this;
      httpClient.get("/report/dashboard-cards")
        .then(function (res) {
          var d = res.data || {};
          if (!d.ok) return;
          var list = d.cards || [];
          var map = {};
          var validIds = {};
          list.forEach(function (c) {
            var gid = "cust-" + c.id;
            map[gid] = { id: c.id, type: c.type, title: c.title, viz: c.viz, contentHtml: c.contentHtml, spec: c.spec, scope: c.scope || null, data: null };
            validIds[gid] = true;
          });
          self.customCards = map;
          // Drop custom-card pads whose definition no longer exists.
          var layout = (self.dashLayout || []).filter(function (it) {
            return !(typeof it.i === "string" && it.i.indexOf("cust-") === 0) || validIds[it.i];
          });
          var changed = layout.length !== (self.dashLayout || []).length;
          // Append any shared card not yet placed — ONLY on the shared Default (Layout 1). Custom layouts
          // are CURATED: they show only the cards the user placed (via the template picker / Add card), so a
          // new layout stays blank instead of collecting every shared card.
          if (self.activeLayout === "default") {
            var present = {};
            layout.forEach(function (it) { present[it.i] = true; });
            var maxY = layout.reduce(function (m, it) { return Math.max(m, (Number(it.y) || 0) + (Number(it.h) || 0)); }, 0);
            list.forEach(function (c) {
              var gid = "cust-" + c.id;
              if (present[gid]) return;
              if (c.scope && c.scope !== "default") return;   // shared (null/"default") cards populate Layout 1; scoped cards belong only to their own layout
              var wide = c.type === "content" || c.viz === "table" || c.viz === "barlist";
              layout.push({ i: gid, x: 0, y: maxY, w: wide ? 2 : 1, h: wide ? 6 : 3 });
              maxY += wide ? 6 : 3;
              changed = true;
            });
          }
          self.dashLayout = layout;
          list.forEach(function (c) { if (c.type === "data") self.loadCustomCardData(c.id); });
          if (changed) self.saveDashLayout();
        })
        .catch(function () { /* no custom cards / table not present yet */ });
    },
    loadCustomCardData(id) {
      var self = this;
      var gid = "cust-" + id;
      httpClient.get("/report/dashboard-cards/" + id + "/data")
        .then(function (res) {
          var d = res.data || {};
          if (d.ok && self.customCards[gid]) self.$set(self.customCards[gid], "data", { viz: d.viz, data: d.data || {} });
        })
        .catch(function () { /* leave as Loading… */ });
    },
    addCustomCard() { this._replacingBuiltin = null; this.editingCard = null; this.editorOpen = true; },
    closeEditor() { this.editorOpen = false; this._replacingBuiltin = null; },
    // Scope a newly-created/duplicated card to the CURRENT layout: "default" → null (shared, populates Layout 1);
    // a custom layout → its id, so the card belongs only to that layout and never populates Layout 1.
    currentCardScope() { return this.activeLayout === "default" ? null : this.activeLayout; },
    // Cards on Layout 1 offered as starting templates in the Add-a-card picker: built-in KPIs (editable token
    // templates) + SHARED custom cards. Picking one drops an editable copy onto THIS layout (scoped to it).
    buildCardTemplates() {
      var self = this;
      var out = [];
      // Layout 1's ACTUAL cards (its published gids if a Master saved them, else the baked default), minus Filters.
      // Union the Master-published Layout 1 with the code baseline (DASH_DEFAULT) so NEWLY-shipped built-in cards
      // (e.g. Orders In Process, Email Insights) always appear in the picker even when the stored Layout 1 was
      // baked BEFORE those cards existed. `seen[gid]` below dedups; published order is preserved (listed first).
      var published = (this.roleDefaults && this.roleDefaults["ALL"] && Array.isArray(this.roleDefaults["ALL"].layout))
        ? this.roleDefaults["ALL"].layout
        : [];
      var l1 = (published || []).concat(DASH_DEFAULT);
      // Parts Sold at a Loss belongs to the Accounting report, not Layout 1, so it is NOT in DASH_DEFAULT —
      // append it here for anyone whose role can actually see it, or the picker could never offer it.
      if (this.canSeeLoss) l1 = l1.concat([{ i: "partLoss" }]);
      var onNow = {};
      (this.dashLayout || []).forEach(function (it) { onNow[it.i] = true; });
      var seen = {};
      (l1 || []).forEach(function (it) {
        var gid = it.i;
        if (gid === "filters" || seen[gid]) return;
        if (!DASH_TITLES[gid]) return;   // known built-ins only (custom cards handled below; AI cards are per-layout, excluded)
        seen[gid] = true;
        out.push({ key: "b:" + gid, gid: gid, title: self.dashTitle(gid), category: CATEGORY_MAP[gid] || "Other", isBuiltin: true, alreadyOn: !!onNow[gid] });
      });
      // Shared custom cards (scope null/"default") — the ones that live on Layout 1.
      var cc = this.customCards || {};
      Object.keys(cc).forEach(function (gid) {
        var c = cc[gid];
        if (c.scope && c.scope !== "default") return;
        out.push({ key: "c:" + gid, gid: gid, title: c.title || "Card", category: "Custom", isBuiltin: false, alreadyOn: !!onNow[gid] });
      });
      return out;
    },
    // Place the REAL Layout 1 card on the CURRENT layout: add its gid so the same render branch draws it
    // (identical formatting). Built-ins render on any layout by presence in dashLayout; customs render their own HTML.
    placeCardFromLayout1(desc) {
      if (!desc || !desc.gid) { this.editorOpen = false; this.editingCard = null; return; }
      var gid = desc.gid;
      if (!(this.dashLayout || []).some(function (it) { return it.i === gid; })) {
        var size = this.cardDefaultSize(gid, desc);
        var maxY = (this.dashLayout || []).reduce(function (m, it) { return Math.max(m, (Number(it.y) || 0) + (Number(it.h) || 0)); }, 0);
        this.dashLayout.push({ i: gid, x: 0, y: maxY, w: size.w, h: size.h });
        var hi = (this.dashHidden || []).indexOf(gid);
        if (hi >= 0) this.dashHidden.splice(hi, 1);   // un-hide if it was a hidden built-in
        this.saveDashLayoutNow();   // discrete action → persist immediately (SHARED2 / user blob)
      }
      this.editorOpen = false;
      this.editingCard = null;
      this.flashCard(gid);   // scroll to the new card + flash it red until it's on screen
    },
    // ── "Restore original card" — recover a built-in to the version I shipped ──────────────────────
    // A safety net for when a Master edits a card and it goes wrong. The built-in's content is the LIVE
    // component, so restoring = un-hide it, drop any edited copy converted from it, reset it to the Layout 1
    // factory size, and clear title styling. Reachable from the card's ↺ button AND the Add-card picker.
    restoreBuiltinCard(gid) {
      if (!gid || !this.isBuiltinCard(gid)) return;
      var self = this;
      // Remove any custom card that was converted FROM this built-in (tracked this session).
      Object.keys(this.cardOrigins || {}).forEach(function (cg) {
        if (self.cardOrigins[cg] === gid) self.removeConvertedCustomCard(cg);
      });
      var hi = (this.dashHidden || []).indexOf(gid);
      if (hi >= 0) this.dashHidden.splice(hi, 1);   // un-hide
      if (this.titleStyles && this.titleStyles[gid]) this.$delete(this.titleStyles, gid);   // drop title-style override
      var size = this.cardDefaultSize(gid, { isBuiltin: true });   // Layout 1 factory size
      var existing = (this.dashLayout || []).find(function (it) { return it.i === gid; });
      if (existing) {
        existing.w = size.w; existing.h = size.h;
      } else {
        var maxY = (this.dashLayout || []).reduce(function (m, it) { return Math.max(m, (Number(it.y) || 0) + (Number(it.h) || 0)); }, 0);
        this.dashLayout.push({ i: gid, x: 0, y: maxY, w: size.w, h: size.h });
      }
      this.saveDashLayoutNow();
      this.flashCard(gid);
    },
    // Delete a custom card that replaced a built-in (no confirm — the user asked to restore the original).
    removeConvertedCustomCard(custGid) {
      var c = this.customCards ? this.customCards[custGid] : null;
      this.dashLayout = (this.dashLayout || []).filter(function (it) { return it.i !== custGid; });
      this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
      if (this.cardOrigins && this.cardOrigins[custGid]) this.$delete(this.cardOrigins, custGid);
      if (c && c.id) {
        httpClient.delete("/report/dashboard-cards/" + c.id).catch(function () { /* non-fatal */ });
        this.$delete(this.customCards, custGid);
      }
    },
    // From an edited (converted) custom card's ↺ button: restore the built-in it came from (also removes this copy).
    restoreOriginalForCustom(custGid) {
      var b = this.cardOrigins ? this.cardOrigins[custGid] : null;
      if (b) this.restoreBuiltinCard(b);
    },
    // From the Add-card picker: a built-in tile → restore MY original of it (adds if missing, resets if present).
    restoreFromPicker(desc) {
      this.editorOpen = false;
      this.editingCard = null;
      if (desc && desc.gid && this.isBuiltinCard(desc.gid)) this.restoreBuiltinCard(desc.gid);
    },
    // Grid size for a placed card: match LAYOUT 1's size for that card (its published size if a Master saved one,
    // else the baked DASH_DEFAULT) so it lands the same dimensions as on Layout 1; else a sensible fallback.
    cardDefaultSize(gid, desc) {
      var l1 = (this.roleDefaults && this.roleDefaults["ALL"] && Array.isArray(this.roleDefaults["ALL"].layout))
        ? this.roleDefaults["ALL"].layout : DASH_DEFAULT;
      var src = (l1 || []).find(function (it) { return it.i === gid; }) || (DASH_DEFAULT || []).find(function (it) { return it.i === gid; });
      if (src) return { w: src.w, h: src.h };
      if (gid === "partLoss") return { w: 24, h: 14 };   // Accounting card — not in DASH_DEFAULT
      if (desc && !desc.isBuiltin) return { w: 8, h: 6 };   // custom card
      return { w: 24, h: 6 };
    },
    editCustomCard(gid) {
      var c = this.customCards[gid];
      if (!c) return;
      this._replacingBuiltin = null;
      this.editingCard = { id: c.id, type: c.type, title: c.title, viz: c.viz, contentHtml: c.contentHtml, spec: c.spec };
      this.editorOpen = true;
    },
    // Edit a BUILT-IN card → open the editor as a NEW custom card seeded with its title; on save the
    // built-in is hidden (restorable via Reset/Restore) and the new custom card takes its slot.
    // Edit a BUILT-IN or AI-generated card → convert it to a fully editable custom card. Built-ins seed a
    // TOKEN template (live {values}); AI cards have no token template so seed their current rendered HTML.
    editBuiltinCard(gid) {
      var pos = null;
      (this.dashLayout || []).forEach(function (it) { if (it.i === gid) pos = { x: it.x, y: it.y, w: it.w, h: it.h }; });
      var ai = this.isAiCard(gid);
      this._replacingBuiltin = { gid: gid, pos: pos, ai: ai };
      var html = ai ? this.captureCardHtml(gid) : (BUILTIN_TEMPLATES[gid] || ciGenericTemplate(this.dashTitle(gid)));
      this.editingCard = { type: "content", title: this.dashTitle(gid), contentHtml: html };
      this.editorOpen = true;
    },
    // Grab a card's currently-rendered body HTML from the live DOM (the seed for converting an AI card).
    captureCardHtml(gid) {
      try {
        var el = document.querySelector('[data-card-id="' + gid + '"] .dash-body');
        if (el) return el.innerHTML.trim();
      } catch (e) { /* noop */ }
      return "";
    },
    deleteCustomCard(gid) {
      var c = this.customCards[gid];
      if (!c) return;
      if (typeof window !== "undefined" && !window.confirm("Delete the card “" + (c.title || "") + "” for everyone?")) return;
      var self = this;
      httpClient.delete("/report/dashboard-cards/" + c.id)
        .then(function () {
          self.$delete(self.customCards, gid);
          self.dashLayout = (self.dashLayout || []).filter(function (it) { return it.i !== gid; });
          self.saveDashLayoutNow();
        })
        .catch(function () { /* non-fatal */ });
    },
    // Editor saved a card (create or edit): upsert its definition, ensure it's on the grid,
    // refresh its data, persist the layout.
    onCardSaved(card) {
      if (!card || !card.id) { this.editorOpen = false; this._replacingBuiltin = null; return; }
      var gid = "cust-" + card.id;
      this.$set(this.customCards, gid, { id: card.id, type: card.type, title: card.title, viz: card.viz, contentHtml: card.contentHtml, spec: card.spec, scope: card.scope || null, data: null });
      var repl = this._replacingBuiltin; this._replacingBuiltin = null;
      var wide = card.type === "content" || card.viz === "table" || card.viz === "barlist";
      if (repl && repl.gid) {
        // Converting a built-in or AI card: remove the original (built-in → hide/restorable; AI → delete its
        // def) and drop the new custom card into its slot.
        if (repl.ai) this.$delete(this.aiCards, repl.gid);
        else { if (this.dashHidden.indexOf(repl.gid) < 0) this.dashHidden.push(repl.gid); this.$set(this.cardOrigins, gid, repl.gid); }   // remember the origin → one-click "restore original"
        this.dashLayout = (this.dashLayout || []).filter(function (it) { return it.i !== repl.gid && it.i !== gid; });
        this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
        var p = repl.pos || { x: 0, y: 0, w: wide ? 2 : 1, h: wide ? 6 : 3 };
        this.dashLayout.push({ i: gid, x: p.x, y: p.y, w: p.w, h: p.h });
      } else if (!(this.dashLayout || []).some(function (it) { return it.i === gid; })) {
        var maxY = (this.dashLayout || []).reduce(function (m, it) { return Math.max(m, (Number(it.y) || 0) + (Number(it.h) || 0)); }, 0);
        this.dashLayout.push({ i: gid, x: 0, y: maxY, w: wide ? 2 : 1, h: wide ? 6 : 3 });
      }
      if (card.type === "data") this.loadCustomCardData(card.id);
      this.editorOpen = false;
      this.editingCard = null;
      this.saveDashLayoutNow();
      this.flashCard(gid);
    },
    ccFmt(v, format) {
      var n = Number(v) || 0;
      if (format === "money") return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
      if (format === "int") return Math.round(n).toLocaleString("en-US");
      return n.toLocaleString("en-US");
    },
    ccBarMax(items) { return (items || []).reduce(function (m, it) { return Math.max(m, Number(it.value) || 0); }, 0); },
    // Replace {token} placeholders in a content card with the live values (unknown tokens left as-is).
    renderTokens(html) {
      if (!html) return "";
      var t = this.reportTokens;
      return String(html).replace(/\{(\w+)\}/g, function (m, key) {
        if (!Object.prototype.hasOwnProperty.call(t, key)) return m;
        // The saved-cart counts render as a clickable token that opens the breakdown popup.
        if (key === "savedOrders" || key === "savedQuotes") {
          var type = key === "savedOrders" ? "order" : "quote";
          return '<span class="ci-sc-tok" data-sc-type="' + type + '" title="Click for the customer breakdown">' + t[key] + "</span>";
        }
        return t[key];
      });
    },
    // Delegated click handler for content cards — makes the saved-cart {tokens} above open the popup.
    onContentClick(e) {
      var el = (e && e.target && e.target.closest) ? e.target.closest(".ci-sc-tok") : null;
      if (el) this.openSavedCarts(el.getAttribute("data-sc-type"));
    },
    // Duplicate a card → POST a fresh custom copy (the original is untouched) and place it below.
    duplicateCard(gid) {
      var payload = null;
      if (this.isCustomCard(gid)) {
        var c = this.customCards[gid]; if (!c) return;
        payload = c.type === "data"
          ? { title: (c.title || "Card") + " (copy)", type: "data", viz: c.viz, spec: c.spec }
          : { title: (c.title || "Card") + " (copy)", type: "content", contentHtml: c.contentHtml };
      } else if (this.isBuiltinCard(gid)) {
        payload = { title: this.dashTitle(gid) + " (copy)", type: "content", contentHtml: BUILTIN_TEMPLATES[gid] || ciGenericTemplate(this.dashTitle(gid)) };
      }
      if (!payload) return;
      payload.scope = this.currentCardScope();   // copy belongs to the current layout (shared on Layout 1, scoped elsewhere)
      var self = this;
      httpClient.post("/report/dashboard-cards", payload)
        .then(function (res) {
          var d = res.data || {};
          if (!d.ok || !d.card) return;
          var card = d.card; var ngid = "cust-" + card.id;
          self.$set(self.customCards, ngid, { id: card.id, type: card.type, title: card.title, viz: card.viz, contentHtml: card.contentHtml, spec: card.spec, scope: card.scope || null, data: null });
          var maxY = (self.dashLayout || []).reduce(function (m, it) { return Math.max(m, (Number(it.y) || 0) + (Number(it.h) || 0)); }, 0);
          var wide = card.type === "content" || card.viz === "table" || card.viz === "barlist";
          self.dashLayout.push({ i: ngid, x: 0, y: maxY, w: wide ? 2 : 1, h: wide ? 6 : 3 });
          if (card.type === "data") self.loadCustomCardData(card.id);
          self.saveDashLayout();
        })
        .catch(function () { /* non-fatal */ });
    },
    // ----- per-card title styling (bold / color / size), persisted with the layout -----
    titleStyle(gid) {
      var s = this.titleStyles[gid]; if (!s) return {};
      var out = {};
      if (s.bold) out.fontWeight = "800";
      if (s.color) out.color = s.color;
      if (s.size) out.fontSize = s.size + "px";
      return out;
    },
    onTitleClick(gid) {
      if (!this.isMaster) return;
      this.titleEditGid = this.titleEditGid === gid ? null : gid;
    },
    setTitleStyle(gid, prop, val) {
      var s = Object.assign({}, this.titleStyles[gid] || {});
      s[prop] = val;
      this.$set(this.titleStyles, gid, s);
      this.saveDashLayout();
    },
    toggleTitleBold(gid) { this.setTitleStyle(gid, "bold", !(this.titleStyles[gid] || {}).bold); },
    clearTitleStyle(gid) { this.$delete(this.titleStyles, gid); this.saveDashLayout(); },
    // ----- Tokens reference drawer (left edge, below Filters) -----
    openTokens() { this.filtersDrawerOpen = false; this.tokensDrawerOpen = true; this.loadSchemaForTokens(); },
    loadSchemaForTokens() {
      if (this.schemaTables.length || this.schemaBusy || !this.isMaster) return;
      var self = this;
      this.schemaBusy = true;
      httpClient.get("/report/schema")
        .then(function (res) { if (res.data && res.data.ok) self.schemaTables = res.data.tables || []; })
        .catch(function () { /* not configured / no access */ })
        .finally(function () { self.schemaBusy = false; });
    },
    copyToken(tok) { try { if (navigator && navigator.clipboard) navigator.clipboard.writeText(tok); } catch (e) { /* noop */ } },
    // Customer notation is customer-level, so show it only on the FIRST cart of each customer in the breakdown list.
    scFirstNoteFor(c, ci) {
      var list = this.filteredScCarts || [];
      for (var k = 0; k < ci; k++) { if (list[k].customerId === c.customerId) return false; }
      return true;
    },
    // Customer Notation / Specifications are collapsed by default in the breakdown — click the header to expand.
    scNoteOpen(key) { return !!this.scNoteExpand[key]; },
    toggleScNote(key) { this.$set(this.scNoteExpand, key, !this.scNoteExpand[key]); },
    // Saved Carts click-through: open the breakdown popup for the chosen type, scoped to the customer filter.
    openSavedCarts(type) {
      var self = this;
      this.scDetail.type = (type === "quote") ? "quote" : "order";
      this.scDetail.open = true;
      this.scDetail.busy = true;
      this.scDetail.carts = [];
      this.scInsights = {};        // reset the 💡 cache for the new view
      this.scInsightCusts = {};    // reset the "insights already requested" set
      this.scExpanded = {};        // rep groups start COLLAPSED each time the breakdown opens
      this.scNoteExpand = {};      // notes/specs start collapsed each time the breakdown opens
      this.scPop.open = false;
      httpClient.get("/report/customer-insights/saved-carts-detail", { params: this.buildParams(false) })
        .then(function (res) {
          self.scDetail.carts = (res.data && res.data.carts) || [];
          // Only auto-load insights for a SINGLE-rep (flat) view — it shows every cart at once. A multi-rep view
          // opens collapsed, so its insights load per-rep on expand (toggleScRep), keeping the initial open light.
          var reps = {};
          (self.filteredScCarts || []).forEach(function (c) { reps[(c.salesRep || "").trim() || "(Unassigned)"] = 1; });
          if (Object.keys(reps).length <= 1) self.loadCartPartInsights();
        })
        .catch(function () { self.scDetail.carts = []; })
        .finally(function () { self.scDetail.busy = false; });
    },
    // Expand/collapse a rep group in the multi-rep breakdown. On expand, lazily load that rep's per-part AI
    // insights (only for customers not already requested) so the popup opens light and fills in on demand.
    toggleScRep(rep) {
      if (this.scExpanded[rep]) { this.$delete(this.scExpanded, rep); return; }
      this.$set(this.scExpanded, rep, true);
      var repCarts = (this.filteredScCarts || []).filter(function (c) {
        return ((c.salesRep || "").trim() || "(Unassigned)") === rep;
      });
      this.loadCartPartInsights(repCarts);
    },
    // Load per-part sales insights for the given carts (defaults to every cart in the open breakdown) — one
    // request per distinct customer (bounded), merged into scInsights keyed "customerId|partNumber". Customers
    // already requested (scInsightCusts) are skipped, so expanding a rep only fetches what's new. Best-effort.
    loadCartPartInsights(carts) {
      var self = this;
      var src = carts || this.scDetail.carts || [];
      var byCust = {};
      src.forEach(function (c) {
        var cust = c.customerId || "";
        if (!cust || self.scInsightCusts[cust]) return;   // skip customers already requested
        (c.items || []).forEach(function (it) {
          if (it && it.partNumber) (byCust[cust] = byCust[cust] || {})[it.partNumber] = true;
        });
      });
      var custIds = Object.keys(byCust).slice(0, 8);   // bound the fan-out
      if (!custIds.length) return;
      custIds.forEach(function (cust) { self.$set(self.scInsightCusts, cust, 1); });   // mark requested up front
      this.scInsightBusy = true;
      // Fetch customers SEQUENTIALLY (not in parallel): each request runs a couple of aggregate queries over the
      // large order-history table, and firing them all at once made the heavier customers time out → empty insights.
      custIds.reduce(function (chain, cust) {
        return chain.then(function () {
          return httpClient.post("/report/cart-part-insights", { customerCode: cust, partNumbers: Object.keys(byCust[cust]) })
            .then(function (res) {
              var ins = (res.data && res.data.insights) || {};
              Object.keys(ins).forEach(function (pn) { self.$set(self.scInsights, cust + "|" + pn, ins[pn]); });
            })
            .catch(function () { /* insights are best-effort */ });
        });
      }, Promise.resolve()).finally(function () { self.scInsightBusy = false; });
    },
    scInsightFor(c, it) {
      if (!c || !it || !it.partNumber) return null;
      return this.scInsights[(c.customerId || "") + "|" + it.partNumber] || null;
    },
    showPartInsight(c, it, ev) {
      var data = this.scInsightFor(c, it);
      if (!data) return;
      this.cancelHidePartInsight();
      var w = 300;
      var r = (ev && ev.currentTarget && ev.currentTarget.getBoundingClientRect)
        ? ev.currentTarget.getBoundingClientRect() : { left: 0, right: 0, top: 0, bottom: 0 };
      var x = Math.max(8, Math.min(r.right + 8, (window.innerWidth || 1200) - w - 8));
      var y = r.bottom + 8;
      if (y > (window.innerHeight || 800) - 230) y = Math.max(8, r.top - 8 - 210);   // flip up near the bottom
      this.scPop = { open: true, x: x, y: y, key: (c.customerId || "") + "|" + it.partNumber, data: data, cart: c };
    },
    scheduleHidePartInsight() {
      var self = this;
      this.cancelHidePartInsight();
      this.scPopHideTimer = setTimeout(function () { self.scPop.open = false; }, 180);
    },
    cancelHidePartInsight() {
      if (this.scPopHideTimer) { clearTimeout(this.scPopHideTimer); this.scPopHideTimer = null; }
    },
    // Click a cross-sell / companion part in the popover → open Search in a NEW TAB for that part, switched into
    // the customer's company, ready to quote (mirrors the Email Insights "quote this part" flow).
    openInsightPart(cart, part) {
      if (!cart || !part || !part.partNumber) return;
      var self = this;
      var q = { code: part.partNumber, allParts: "true", intent: "quote" };
      if (cart.companyId) q.switchCompanyId = cart.companyId;
      if (cart.customerId) q.switchCompanyCode = cart.customerId;
      if (cart.customerName) q.switchCompanyName = cart.customerName;
      // Open the tab NOW (synchronously, inside the click) so the pop-up blocker doesn't kill it, THEN navigate it once
      // we've resolved the part's machine/assembly. The Search header-search hydration needs machineName + assembly +
      // assemblyId to auto-select the machine/assembly/part — a bare ?code= lands on an empty Search page (the bug the
      // rep hit). Mirrors the Email Insights "quote this part" link, whose machine/assembly were resolved server-side.
      var w = null;
      try { w = window.open("", "_blank"); } catch (e) { w = null; }
      var go = function () {
        try {
          var href = window.location.origin + self.$router.resolve({ name: "Search", query: q }).href;
          if (w) { w.location.href = href; } else { window.open(href, "_blank"); }
        } catch (e) { /* noop */ }
      };
      if (!cart.customerId) { go(); return; }   // no customer → can't resolve; open code-only
      httpClient.get("/report/customer-insights/part-machine", { params: { customer: cart.customerId, code: part.partNumber } })
        .then(function (res) {
          var d = (res && res.data) || {};
          if (d.machineName) q.machineName = d.machineName;
          if (d.assembly) q.assembly = d.assembly;
          if (d.assemblyId) q.assemblyId = d.assemblyId;
        })
        .catch(function () { /* couldn't resolve → fall back to code-only */ })
        .finally(go);
    },
    // ----- printable Sales-Insights presentation (charts + KPIs + AI narrative) -----
    printInsights() {
      if (this.printBusy) return;
      var self = this;
      // Open the print window SYNCHRONOUSLY (inside the click) with a placeholder so the pop-up blocker
      // doesn't kill it while we do async work (screenshot + AI insights); we rewrite it when ready.
      var w = window.open("", "_blank");
      if (!w) { if (typeof window !== "undefined") window.alert("Please allow pop-ups to print the Sales Insights."); return; }
      // Reuse the Sales Insights already generated on the page; only auto-run if the rep hasn't generated one yet.
      var needGen = !this.aiInsights;
      try {
        w.document.open();
        w.document.write("<!DOCTYPE html><html><head><meta charset='utf-8'><title>Sales Insights</title></head><body style=\"font-family:Arial,Helvetica,sans-serif;color:#666;padding:48px;font-size:15px;\">Preparing your presentation&hellip;<br><span style=\"font-size:12px;color:#999;\">" + (needGen ? "Capturing the dashboard and running Sales Insights" : "Capturing the dashboard") + " &mdash; this can take a few seconds.</span></body></html>");
        w.document.close();
      } catch (e) { /* ignore */ }
      this.printBusy = true;
      var ensure = needGen ? this.generateInsights() : Promise.resolve();
      Promise.resolve(ensure)
        .then(function () { return self.loadSavedCartsForPrint(); })
        .then(function (carts) { return self.captureDashboardShot().then(function (shot) { return { carts: carts, shot: shot }; }); })
        .then(function (b) {
          var html = self.buildPresentationHtml(b.shot, b.carts);
          // Archive the EXACT rendered presentation (incl. screenshot) onto the most-recent insight entry, so
          // "Print a past report" replays it verbatim. Stash it in-memory too for an immediate reprint.
          httpClient.post("/report/ai-history/presentation", { html: html })
            .then(function () { if (self.aiInsightHistory[0]) self.$set(self.aiInsightHistory[0], "presentationHtml", html); })
            .catch(function () { /* best-effort archive */ });
          try { w.document.open(); w.document.write(html); w.document.close(); w.focus(); } catch (e) { return; }
          setTimeout(function () { try { w.print(); } catch (e) { /* noop */ } }, 600);
        })
        .catch(function () {
          try { w.document.open(); w.document.write("<body style=\"font-family:Arial;padding:40px;color:#c0392b;\">Couldn't build the presentation. Please close this tab and try again.</body>"); w.document.close(); } catch (e2) { /* ignore */ }
        })
        .finally(function () { self.printBusy = false; self.aiPresentFiltered = false; });
    },
    // Snapshot the live dashboard grid (.ci-wrap) to a PNG data URL for page 1. Best-effort: returns "" on
    // failure (page 1 is then skipped). The floating Sales Insights panel is body-appended outside .ci-wrap,
    // so it is not captured.
    captureDashboardShot() {
      // On mobile the dashboard is a narrow single-column stack, so an html2canvas capture is an unreadable tall
      // strip (and a wide/desktop capture isn't possible from a ~350px viewport). Skip it there — the presentation's
      // Overview / Saved-carts / Insights pages render FULL-WIDTH in the print doc, so the figures stay readable.
      if (this.isMobile) return Promise.resolve("");
      var el = (typeof document !== "undefined") ? document.querySelector(".ci-wrap") : null;
      if (!el || typeof html2canvas !== "function") return Promise.resolve("");
      return html2canvas(el, {
        backgroundColor: "#ffffff", useCORS: true, logging: false,
        // Capture in LIGHT mode regardless of the user's theme: strip dark-mode/mid-mode from the CLONED
        // <html> only (not the live page → no flicker), so the screenshot uses the light/steele-consistency
        // baseline.
        onclone: function (doc) { try { if (doc && doc.documentElement) doc.documentElement.classList.remove("dark-mode", "mid-mode"); } catch (e) { /* noop */ } },
        // Leave favorites + filter-bar controls OUT of the presentation screenshot (the filter VALUES stay).
        ignoreElements: function (node) { return node.classList && node.classList.contains("ci-noprint"); },
        scale: Math.min(1.5, (window.devicePixelRatio || 1) * 1.1),
        windowWidth: el.scrollWidth, width: el.scrollWidth,
        height: el.scrollHeight, windowHeight: el.scrollHeight, scrollX: 0, scrollY: 0
      }).then(function (canvas) { try { return canvas.toDataURL("image/png"); } catch (e) { return ""; } })
        .catch(function () { return ""; });
    },
    loadSavedCartsForPrint() {
      var self = this;
      return httpClient.get("/report/customer-insights/saved-carts-detail", { params: this.buildParams(false) })
        .then(function (res) { return (res.data && res.data.carts) || []; })
        .then(function (carts) {
          if (!carts.length) return carts;
          // Best-effort AI "why reach out" blurb per cart (buying-pattern analysis); merged in by cartId.
          return self.loadCartPatterns().then(function (pat) {
            carts.forEach(function (c) { if (pat && pat[c.cartId] != null) c.pattern = pat[c.cartId]; });
            return carts;
          });
        })
        .catch(function () { return []; });
    },
    // AI buying-pattern blurbs for the saved carts (POST the current filters; returns { cartId: text }).
    loadCartPatterns() {
      return httpClient.post("/report/ai-cart-patterns", this.buildParams(false))
        .then(function (res) { return (res.data && res.data.patterns) || {}; })
        .catch(function () { return {}; });
    },
    // Build the 5-page presentation: (1) dashboard screenshot, (2) the data, (3) saved Orders/Quotes detail,
    // (4) AI Insights, (5) Tips & Opportunities (coaching). Reuses the report's loaded aggregates + AI text.
    // Build the full presentation (screenshot + live data) and ARCHIVE it onto the most-recent insight entry,
    // so each generated insight carries a faithful presentation its "Presentation" button can reprint. No
    // window/print here — just capture + save. Returns the rendered HTML.
    buildAndArchivePresentation() {
      var self = this;
      return this.loadSavedCartsForPrint()
        .then(function (carts) { return self.captureDashboardShot().then(function (shot) { return self.buildPresentationHtml(shot, carts); }); })
        .then(function (html) {
          return httpClient.post("/report/ai-history/presentation", { html: html })
            .then(function () { if (self.aiInsightHistory[0]) self.$set(self.aiInsightHistory[0], "presentationHtml", html); return html; })
            .catch(function () { if (self.aiInsightHistory[0]) self.$set(self.aiInsightHistory[0], "presentationHtml", html); return html; });
        });
    },
    // "Generate Insights" button: generate the drawer narrative AND build + archive the matching presentation,
    // so the new history entry's "Presentation" button can reprint it. (printBusy shows the archive is working.)
    generateInsightsAndPresentation() {
      var self = this;
      this.printBusy = true;
      return this.generateInsights()
        .then(function () { if (self.aiInsights) return self.buildAndArchivePresentation(); })
        .finally(function () { self.printBusy = false; });
    },
    // Result-bar "Presentation" button: open / print the presentation archived alongside the current
    // insight (the exact copy generated at the time). Falls back to a fresh live print if nothing's archived.
    openCurrentPresentation() {
      if (this.printBusy) return;
      var h = this.aiInsightHistory && this.aiInsightHistory[0];
      if (h) this.printPastReport(h);
      else this.printInsights();
    },
    // "Print a past report" → re-print a stored insight from its saved data Snapshot (no live screenshot/data).
    // Window opened synchronously inside the click (no async first) so the pop-up blocker doesn't kill it.
    printPastReport(entry) {
      if (!entry) return;
      this.printChooserOpen = false;
      var self = this;
      var w = window.open("", "_blank");
      if (!w) { if (typeof window !== "undefined") window.alert("Please allow pop-ups to print the report."); return; }
      try { w.document.open(); w.document.write("<body style=\"font-family:Arial,Helvetica,sans-serif;padding:40px;color:#666;\">Loading the saved report&hellip;</body>"); w.document.close(); } catch (e) { /* ignore */ }
      var writeOut = function (html) {
        try { w.document.open(); w.document.write(html); w.document.close(); w.focus(); setTimeout(function () { try { w.print(); } catch (e) { /* noop */ } }, 400); }
        catch (e) { /* window closed */ }
      };
      // 1) a just-printed entry carries the exact HTML in memory; 2) a saved DB entry fetches it by id;
      // 3) entries with no saved presentation (older / generate-only) rebuild from the stored data snapshot.
      if (entry.presentationHtml) { writeOut(entry.presentationHtml); return; }
      if (typeof entry.id !== "number") { writeOut(this.buildPastPresentationHtml(entry)); return; }
      httpClient.get("/report/ai-history/presentation", { params: { id: entry.id } })
        .then(function (res) {
          var html = res.data && res.data.html;
          writeOut(html && html.length ? html : self.buildPastPresentationHtml(entry));
        })
        .catch(function () { writeOut(self.buildPastPresentationHtml(entry)); });
    },
    // Build a presentation from a PAST insight's stored Snapshot (the summary JSON) + its insights/coaching.
    // Self-contained (doesn't touch the live buildPresentationHtml); no dashboard screenshot for past reports.
    buildPastPresentationHtml(entry) {
      var self = this;
      var esc = function (s) { return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); };
      var money = function (n) { return self.fmtMoney(n); };
      var num = function (n) { return self.fmtNum(n); };
      var snap = {};
      try { snap = JSON.parse(entry.snapshot || "{}") || {}; } catch (e) { snap = {}; }
      var dateLabel = this.fmtHistDate(entry.createdAt);
      var titleLabel = entry.title || "Past report";
      var sectionH = function (t) { return '<div style="font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;color:#9e2436;margin:0 0 6px;">' + t + "</div>"; };
      var kpiBox = function (label, val) { return '<div style="flex:1;border:1px solid #e6e6ea;border-radius:8px;padding:10px 12px;"><div style="font-size:10px;text-transform:uppercase;letter-spacing:.04em;color:#888;">' + esc(label) + '</div><div style="font-size:20px;font-weight:800;color:#9e2436;margin-top:2px;">' + val + "</div></div>"; };
      var barRows = function (items, labelFn, valFn, fmtFn, color) {
        var max = items.reduce(function (m, it) { return Math.max(m, valFn(it) || 0); }, 0) || 1;
        return items.map(function (it) {
          var v = valFn(it) || 0; var pct = Math.max(2, v / max * 100);
          return '<div style="display:flex;align-items:center;gap:8px;margin:3px 0;font-size:12px;page-break-inside:avoid;"><span style="flex:0 0 38%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#333;">' + esc(labelFn(it)) + '</span><span style="flex:1;height:13px;background:#eef0f1;border-radius:3px;overflow:hidden;"><span style="display:block;height:100%;width:' + pct.toFixed(1) + "%;background:" + color + ';"></span></span><span style="flex:0 0 auto;color:#555;">' + fmtFn(v) + "</span></div>";
        }).join("");
      };
      var bulletList = function (lines) {
        return '<ul style="margin:0;padding-left:18px;font-size:13px;color:#333;line-height:1.55;">' + lines.map(function (ln) {
          return ln.bullet ? '<li style="margin:5px 0;">' + esc(ln.text) + "</li>" : '<li style="margin:9px 0 3px;list-style:none;font-weight:700;">' + esc(ln.text) + "</li>";
        }).join("") + "</ul>";
      };
      var cartCard = function (c) {
        var items = (c.items || []).map(function (it) { return '<tr><td style="padding:2px 6px;color:#333;">' + esc(it.part) + '</td><td style="padding:2px 6px;text-align:right;color:#333;">&times;' + num(it.qty) + "</td></tr>"; }).join("");
        return '<div style="border:1px solid #e6e6ea;border-radius:8px;padding:8px 10px;margin-bottom:8px;page-break-inside:avoid;"><div style="font-weight:700;color:#9e2436;font-size:12.5px;">' + esc(c.customer || c.customerId) + (c.customerId ? ' <span style="color:#999;font-weight:400;">(' + esc(c.customerId) + ")</span>" : "") + '</div><div style="font-size:11px;color:#777;margin:2px 0 4px;">' + num(c.lineCount) + " item" + (c.lineCount === 1 ? "" : "s") + " &middot; " + money(c.total) + "</div>" + (items ? '<table style="width:100%;border-collapse:collapse;font-size:11px;">' + items + "</table>" : "") + "</div>";
      };
      var k = { totalSales: snap.totalSales || 0, orderCount: snap.orderCount || 0, invoicedOrders: snap.invoicedOrders || 0 };
      var avg = k.orderCount ? k.totalSales / k.orderCount : 0;
      var topCust = (snap.topCustomers || []).slice(0, 15);
      var topParts = (snap.topParts || []).slice(0, 15);
      var terr = (snap.salesByTerritory || []);
      var allCarts = (snap.savedCartDetails || []);
      var orders = allCarts.filter(function (c) { return c.type !== "Quote"; });
      var quotes = allCarts.filter(function (c) { return c.type === "Quote"; });
      var page = function (title, inner, last) {
        return '<section style="' + (last ? "" : "page-break-after:always;") + 'padding:26px 30px;box-sizing:border-box;"><div style="border-bottom:3px solid #9e2436;padding-bottom:8px;margin-bottom:16px;"><div style="font-size:20px;font-weight:800;color:#9e2436;">JC Steele &mdash; Sales Insights</div><div style="font-size:12px;color:#666;margin-top:2px;">Generated ' + esc(dateLabel) + " &middot; " + esc(titleLabel) + (title ? " &middot; " + esc(title) : "") + "</div></div>" + inner + "</section>";
      };
      var dataPage = "<div style='display:flex;gap:10px;margin-bottom:18px;'>" + kpiBox("Total Sales", money(k.totalSales)) + kpiBox("Orders", num(k.orderCount)) + kpiBox("Avg / Order", money(avg)) + kpiBox("Invoiced", num(k.invoicedOrders) + " of " + num(k.orderCount)) + "</div>"
        + "<div style='margin-bottom:18px;'>" + sectionH("Top Customers") + barRows(topCust, function (c) { return c.name || c.id; }, function (c) { return Number(c.sales) || 0; }, money, "#8E44AD") + "</div>"
        + "<div style='margin-bottom:18px;'>" + sectionH("Top Parts (by quantity)") + barRows(topParts, function (p) { return p.number; }, function (p) { return Number(p.qty) || 0; }, num, "#16a085") + "</div>"
        + "<div style='margin-bottom:18px;'>" + sectionH("Sales by Territory") + barRows(terr, function (t) { return t.name; }, function (t) { return Number(t.sales) || 0; }, money, "#9e2436") + "</div>";
      var cartsPage = sectionH("Saved Orders (" + orders.length + ")") + (orders.length ? orders.map(cartCard).join("") : '<div style="font-size:12px;color:#999;margin-bottom:14px;">No saved orders.</div>') + '<div style="height:12px;"></div>' + sectionH("Saved Quotes (" + quotes.length + ")") + (quotes.length ? quotes.map(cartCard).join("") : '<div style="font-size:12px;color:#999;">No saved quotes.</div>');
      var insightsPage = entry.insights ? bulletList(this.histLines(entry.insights)) : '<div style="font-size:12px;color:#999;">No insights stored.</div>';
      var coachingPage = entry.coaching ? bulletList(this.histLines(entry.coaching)) : '<div style="font-size:12px;color:#999;">No tips stored.</div>';
      return "<!DOCTYPE html><html><head><meta charset='utf-8'><title>JC Steele &mdash; Sales Insights (past report)</title><style>@page{margin:0.4in;}body{margin:0;}*{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;color-adjust:exact !important;}</style></head><body style='font-family:Arial,Helvetica,sans-serif;color:#222;'>"
        + page("Overview", dataPage)
        + page("Saved Orders and Quotes", cartsPage)
        + page("Sales Insights & Tips", '<div style="border-top:1px solid #eee;padding-top:6px;">' + sectionH("&#10024; Sales Insights") + insightsPage + '</div><div style="border-top:1px solid #eee;padding-top:6px;margin-top:16px;">' + sectionH("&#128161; Tips, Best Practices and Industry Advice") + coachingPage + "</div>", true)
        + "</body></html>";
    },
    buildPresentationHtml(shotUrl, carts) {
      var self = this;
      var esc = function (s) { return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); };
      var money = function (n) { return self.fmtMoney(n); };
      var num = function (n) { return self.fmtNum(n); };
      var k = this.kpis || {};
      var avg = k.orderCount ? k.totalSales / k.orderCount : 0;
      var dateRange = this.fmtShort(this.filters.from) + " &ndash; " + this.fmtShort(this.filters.to);
      var fparts = [];
      if ((this.filters.territory || []).length) fparts.push("Territory: " + esc(this.filters.territory.join(", ")));
      if ((this.filters.customer || []).length) fparts.push("Customer: " + esc(this.filters.customer.join(", ")));
      if ((this.filters.salesRep || []).length) fparts.push("Rep: " + esc(this.filters.salesRep.join(", ")));
      if ((this.filters.part || []).length) fparts.push("Part: " + esc(this.filters.part.join(", ")));
      var filterSummary = fparts.length ? fparts.join(" &middot; ") : "All customers";
      var sectionH = function (t) { return '<div style="font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;color:#9e2436;margin:0 0 6px;">' + t + "</div>"; };
      // KPI box; the optional `breakdown` ([{name,val,color}]) is squeezed under the grand total as a
      // per-territory split (so each card shows how the total divides across the territories).
      var kpiBox = function (label, val, breakdown) {
        var bd = (breakdown && breakdown.length)
          ? '<div style="margin-top:6px;border-top:1px solid #eee;padding-top:4px;">' + breakdown.map(function (b) {
              return '<div style="display:flex;align-items:center;justify-content:space-between;gap:6px;font-size:9px;color:#777;line-height:1.65;">'
                + '<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0;"><span style="display:inline-block;width:7px;height:7px;border-radius:2px;background:' + b.color + ';margin-right:4px;vertical-align:middle;"></span>' + esc(b.name) + "</span>"
                + '<span style="flex:0 0 auto;font-weight:600;color:#555;">' + b.val + "</span></div>";
            }).join("") + "</div>"
          : "";
        // The count value font shrinks for longer numbers so it never spills outside the card.
        var vtxt = String(val).replace(/<[^>]*>/g, "");
        var vfont = vtxt.length > 15 ? 13 : (vtxt.length > 12 ? 15 : (vtxt.length > 9 ? 17 : (vtxt.length > 6 ? 19 : 22)));
        return '<div style="flex:1;min-width:0;border:1px solid #e6e6ea;border-radius:8px;padding:10px 12px;overflow:hidden;"><div style="font-size:10px;text-transform:uppercase;letter-spacing:.04em;color:#888;">' + esc(label) + '</div><div style="font-size:' + vfont + 'px;font-weight:800;color:#9e2436;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">' + val + "</div>" + bd + "</div>";
      };
      // Per-territory split for the KPI cards (sorted by sales, so all four cards list territories in the
      // same order). Sales/Orders/Avg come from byTerritory; Invoiced needs byTerritory.invoicedOrders
      // (added to the backend) — shown only once that field is present (older backend → omitted).
      var terrBd = (this.byTerritory || []).slice().sort(function (a, b) { return (Number(b.sales) || 0) - (Number(a.sales) || 0); });
      var bdSales = terrBd.map(function (t) { return { name: t.territory, val: money(t.sales), color: self.terrColor(t.territory) }; });
      var bdOrders = terrBd.map(function (t) { return { name: t.territory, val: num(t.orderCount), color: self.terrColor(t.territory) }; });
      var bdAvg = terrBd.map(function (t) { return { name: t.territory, val: money(t.orderCount ? (Number(t.sales) || 0) / t.orderCount : 0), color: self.terrColor(t.territory) }; });
      var hasInv = terrBd.some(function (t) { return t.invoicedOrders != null; });
      var bdInv = hasInv ? terrBd.map(function (t) { return { name: t.territory, val: num(t.invoicedOrders || 0), color: self.terrColor(t.territory) }; }) : null;
      // partNumber -> territory segments (qty rolled up rep->territory) — the SAME map the on-screen
      // "Report data" part bars use, so the presentation and the drawer show identical segments.
      var partSegs = this.partTerritorySegMap;
      // Single-color bar rows; `color` may be a constant string OR a fn(item)->color so each bar can carry its
      // own territory color (matching the dashboard cards).
      var barRows = function (items, labelFn, valFn, fmtFn, color) {
        var max = items.reduce(function (m, it) { return Math.max(m, valFn(it) || 0); }, 0) || 1;
        var colorOf = (typeof color === "function") ? color : function () { return color; };
        return items.map(function (it) {
          var v = valFn(it) || 0; var pct = Math.max(2, v / max * 100);
          return '<div style="display:flex;align-items:center;gap:8px;margin:3px 0;font-size:12px;page-break-inside:avoid;">'
            + '<span style="flex:0 0 38%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#333;">' + esc(labelFn(it)) + "</span>"
            + '<span style="flex:1;height:13px;background:#eef0f1;border-radius:3px;overflow:hidden;"><span style="display:block;height:100%;width:' + pct.toFixed(1) + "%;background:" + colorOf(it) + ';"></span></span>'
            + '<span style="flex:0 0 auto;color:#555;">' + fmtFn(v) + "</span></div>";
        }).join("");
      };
      // Segmented bar rows: the magnitude bar (value / max) is filled with territory-colored segments (each
      // territory's share of the row's value), with the per-territory breakdown listed below the bar.
      var segBarRows = function (items, labelFn, valFn, fmtFn, segsFn) {
        var max = items.reduce(function (m, it) { return Math.max(m, valFn(it) || 0); }, 0) || 1;
        return items.map(function (it) {
          var v = valFn(it) || 0; var pct = Math.max(2, v / max * 100);
          var segs = segsFn(it) || [];
          var segTotal = segs.reduce(function (s, x) { return s + (x.val || 0); }, 0);
          var fill = (segs.length && segTotal > 0)
            ? segs.map(function (s) { return '<span style="display:block;height:100%;float:left;width:' + (s.val / segTotal * 100).toFixed(2) + '%;background:' + s.color + ';"></span>'; }).join("")
            : '<span style="display:block;height:100%;width:100%;background:#16a085;"></span>';
          var bd = (segs.length > 1)
            ? '<div style="margin:1px 0 5px;padding-left:calc(38% + 8px);font-size:9.5px;color:#777;line-height:1.5;">'
              + segs.map(function (s) { return '<span style="white-space:nowrap;margin-right:11px;"><span style="display:inline-block;width:7px;height:7px;border-radius:2px;background:' + s.color + ';margin-right:3px;vertical-align:middle;"></span>' + esc(s.name) + " " + fmtFn(s.val) + "</span>"; }).join("")
              + "</div>"
            : "";
          return '<div style="margin:3px 0;page-break-inside:avoid;">'
            + '<div style="display:flex;align-items:center;gap:8px;font-size:12px;">'
            + '<span style="flex:0 0 38%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#333;">' + esc(labelFn(it)) + "</span>"
            + '<span style="flex:1;height:13px;background:#eef0f1;border-radius:3px;overflow:hidden;"><span style="display:block;height:100%;width:' + pct.toFixed(1) + '%;overflow:hidden;">' + fill + "</span></span>"
            + '<span style="flex:0 0 auto;color:#555;">' + fmtFn(v) + "</span></div>"
            + bd + "</div>";
        }).join("");
      };
      var yoy = (this.story.summary && this.story.summary.yoy) || [];
      var yoyHtml = "";
      if (this.yoyEligible && yoy.length >= 2) {
        var ymax = yoy.reduce(function (m, y) { return Math.max(m, Number(y.sales) || 0); }, 0) || 1;
        yoyHtml = "<div style='margin-bottom:18px;'>" + sectionH("Year over Year") + yoy.map(function (y, i) {
          var pct = Math.max(2, (Number(y.sales) || 0) / ymax * 100);
          var segs = (y.territories || []).filter(function (s) { return (Number(s.sales) || 0) > 0; });
          var segTotal = segs.reduce(function (s, x) { return s + (Number(x.sales) || 0); }, 0);
          var fill = (segs.length && segTotal > 0)
            ? segs.map(function (s) { return '<span style="display:block;height:100%;float:left;width:' + ((Number(s.sales) || 0) / segTotal * 100).toFixed(2) + '%;background:' + self.terrColor(s.name) + ';"></span>'; }).join("")
            : '<span style="display:block;height:100%;width:100%;background:' + self.yoyBarColor(yoy, i) + ';"></span>';
          return '<div style="display:flex;align-items:center;gap:8px;margin:3px 0;font-size:12px;"><span style="flex:0 0 54px;color:#777;">' + y.year + '</span><span style="flex:1;height:13px;background:#eef0f1;border-radius:3px;overflow:hidden;"><span style="display:block;height:100%;width:' + pct.toFixed(1) + '%;overflow:hidden;">' + fill + '</span></span><span style="flex:0 0 auto;color:#555;">' + money(y.sales) + "</span></div>";
        }).join("") + "</div>";
      }
      var topCust = (this.byCustomer || []).slice().sort(function (a, b) { return (Number(b.sales) || 0) - (Number(a.sales) || 0); }).slice(0, 15);
      var topPartsL = (this.byPart || []).slice(0, 15);
      var terr = (this.byTerritory || []).slice().sort(function (a, b) { return (Number(b.sales) || 0) - (Number(a.sales) || 0); });
      var bulletList = function (lines) {
        return '<ul style="margin:0;padding-left:18px;font-size:13px;color:#333;line-height:1.55;">' + lines.map(function (ln) {
          return ln.bullet ? '<li style="margin:5px 0;">' + esc(ln.text) + "</li>" : '<li style="margin:9px 0 3px;list-style:none;font-weight:700;">' + esc(ln.text) + "</li>";
        }).join("") + "</ul>";
      };
      var cartCard = function (c) {
        var items = (c.items || []).map(function (it) {
          return '<tr><td style="padding:2px 6px;color:#333;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">' + esc(it.partNumber) + '</td><td style="padding:2px 6px;color:#666;word-break:break-word;">' + esc(it.partName) + '</td><td style="padding:2px 6px;text-align:right;color:#333;white-space:nowrap;">&times;' + num(it.qty) + '</td><td style="padding:2px 6px;text-align:right;color:#333;white-space:nowrap;">' + money(it.unitPrice) + "</td></tr>";
        }).join("");
        return '<div style="border:1px solid #e6e6ea;border-radius:8px;padding:8px 10px;margin-bottom:8px;page-break-inside:avoid;">'
          + '<div style="font-weight:700;color:#9e2436;font-size:12.5px;">' + esc(c.customerName || c.customerId) + (c.customerId ? ' <span style="color:#999;font-weight:400;">(' + esc(c.customerId) + ")</span>" : "") + "</div>"
          + '<div style="font-size:11px;color:#777;margin:2px 0 4px;">' + (c.poNumbers ? (c.type === "Quote" ? "Quote ref: " : "PO #: ") + esc(c.poNumbers) + " &middot; " : "") + (c.createdAt ? "Created " + esc(self.fmtShort(c.createdAt)) + " &middot; " : "") + num(c.lineCount) + " item" + (c.lineCount === 1 ? "" : "s") + " &middot; " + money(c.total) + "</div>"
          + (items ? '<table style="width:100%;border-collapse:collapse;font-size:11px;table-layout:fixed;"><colgroup><col style="width:118px;"><col><col style="width:46px;"><col style="width:84px;"></colgroup>' + items + "</table>" : '<div style="font-size:11px;color:#999;">No line items.</div>')
          + (c.pattern ? '<div style="margin-top:6px;padding:5px 8px;background:#fbf4f5;border-left:3px solid #9e2436;border-radius:0 4px 4px 0;font-size:10.5px;color:#444;line-height:1.45;"><span style="font-weight:700;color:#9e2436;">&#128161; Opportunity:</span> ' + esc(c.pattern) + "</div>" : "")
          + "</div>";
      };
      var orders = (carts || []).filter(function (c) { return c.type !== "Quote"; });
      var quotes = (carts || []).filter(function (c) { return c.type === "Quote"; });
      var page = function (title, inner, last) {
        return '<section style="' + (last ? "" : "page-break-after:always;") + 'padding:26px 30px;box-sizing:border-box;">'
          + '<div style="border-bottom:3px solid #9e2436;padding-bottom:8px;margin-bottom:16px;"><div style="font-size:20px;font-weight:800;color:#9e2436;">JC Steele &mdash; Sales Insights</div><div style="font-size:12px;color:#666;margin-top:2px;">' + dateRange + " &middot; " + filterSummary + (title ? " &middot; " + esc(title) : "") + "</div></div>" + inner + "</section>";
      };
      // Dashboard screenshot is sized to fit ONE page — capped by BOTH page width and height so it never
      // spills onto a 2nd page (max-height leaves room for this page's header + padding).
      var shotPage = shotUrl
        ? '<div style="text-align:center;"><img src="' + shotUrl + '" style="max-width:100%;max-height:6.2in;height:auto;display:inline-block;border:1px solid #e0e0e0;border-radius:6px;" alt="Dashboard" /></div>'
        : '<div style="font-size:12px;color:#999;">Dashboard screenshot unavailable in this browser.</div>';
      // Page 1 (cover): the title header + the four KPI counts (with their per-territory split), centered.
      // min-height (not a fixed height) keeps the cover on ONE page in landscape too (shorter page);
      // the counts span the full content width (margin to margin), equal-width + evenly spaced.
      var coverPage = '<section style="page-break-after:always;padding:26px 30px;box-sizing:border-box;min-height:6.8in;display:flex;flex-direction:column;">'
        + '<div style="border-bottom:3px solid #9e2436;padding-bottom:10px;"><div style="font-size:26px;font-weight:800;color:#9e2436;">JC Steele &mdash; Sales Insights</div><div style="font-size:13px;color:#666;margin-top:3px;">' + dateRange + " &middot; " + filterSummary + "</div></div>"
        + '<div style="flex:1;display:flex;align-items:center;"><div style="width:100%;"><div style="display:flex;gap:14px;align-items:flex-start;">'
        + kpiBox("Total Sales", money(k.totalSales), bdSales) + kpiBox("Orders", num(k.orderCount), bdOrders) + kpiBox("Avg / Order", money(avg), bdAvg) + kpiBox("Invoiced", num(k.invoicedOrderCount) + " of " + num(k.orderCount), bdInv)
        + "</div></div></div></section>";
      // The data/overview page no longer repeats the KPI counts (they're the cover) — just the bar sections.
      var dataPage = yoyHtml
        + "<div style='margin-bottom:18px;'>" + sectionH("Top Customers") + barRows(topCust, function (c) { return c.customerName || c.customerId; }, function (c) { return Number(c.sales) || 0; }, money, function (c) { return self.customerColorMap[c.customerId] || "#8E44AD"; }) + "</div>"
        + "<div style='margin-bottom:18px;'>" + sectionH("Top Parts (by quantity)") + segBarRows(topPartsL, function (p) { return p.partNumber; }, function (p) { return Number(p.qty) || 0; }, num, function (p) { return partSegs[p.partNumber] || []; }) + "</div>"
        + "<div style='margin-bottom:18px;'>" + sectionH("Sales by Territory") + barRows(terr, function (t) { return t.territory; }, function (t) { return Number(t.sales) || 0; }, money, function (t) { return self.terrColor(t.territory); }) + "</div>";
      var cartsPage = sectionH("Saved Orders (" + orders.length + ")") + (orders.length ? orders.map(cartCard).join("") : '<div style="font-size:12px;color:#999;margin-bottom:14px;">No saved orders for the current filters.</div>')
        + '<div style="height:12px;"></div>' + sectionH("Saved Quotes (" + quotes.length + ")") + (quotes.length ? quotes.map(cartCard).join("") : '<div style="font-size:12px;color:#999;">No saved quotes for the current filters.</div>');
      var insightsPage = this.aiInsights ? bulletList(this.aiInsightsLines) : '<div style="font-size:12px;color:#999;">No AI insights were generated.</div>';
      var coachingPage = this.aiCoaching ? bulletList(this.aiCoachingLines) : '<div style="font-size:12px;color:#999;">No tips were generated.</div>';
      // Optional "Question & Results" page — present when launched from the hero result card. Renders the
      // question + its table (grouped with subtotals when the first column repeats), money columns as $.
      var askInner = "";
      var actx = this.aiAskCtx;
      if (actx && actx.table && Array.isArray(actx.table.columns) && Array.isArray(actx.table.rows) && actx.table.rows.length) {
        var at = actx.table, moneyCols = this.aiMoneyCols, numIdx = this.aiTableNumIdx;
        var fmtC = function (v, ci) {
          if (typeof v === "number" && isFinite(v)) {
            var m = moneyCols.indexOf(ci) !== -1;
            if (!m && Number.isInteger(v) && v >= 1900 && v <= 2100) return String(v);
            var s = v.toLocaleString(undefined, { minimumFractionDigits: m ? 2 : (v % 1 ? 2 : 0), maximumFractionDigits: 2 });
            return m ? "$" + s : s;
          }
          return esc(v == null ? "" : v);
        };
        var alignOf = function (ci) { return ci === numIdx ? "right" : "left"; };
        var head = "<tr>" + at.columns.map(function (c, ci) {
          return '<th style="text-align:' + alignOf(ci) + ';padding:5px 8px;border-bottom:2px solid #9e2436;font-size:10px;text-transform:uppercase;letter-spacing:.03em;color:#9e2436;white-space:nowrap;">' + esc(c) + "</th>";
        }).join("") + "</tr>";
        var bodyHtml = "";
        // Group the FILTERED table (`at` = actx.table). Reading the aiTableGroups COMPUTED here was
        // bound to this.aiTable, so it silently re-introduced every group the 📊 Presentation picker
        // excluded — the picker's filter was correct, its result was just discarded at this line.
        var grps = this._groupAiTable(at);
        if (grps) {
          grps.forEach(function (g) {
            bodyHtml += '<tr style="background:#f7f2f3;"><td style="padding:5px 8px;font-weight:700;color:#5d2830;">' + esc(g.key || "(blank)") + ' <span style="color:#999;font-weight:400;">&middot; ' + g.rows.length + "</span></td>"
              + at.columns.slice(1).map(function (c, i) { var ci = i + 1; return '<td style="padding:5px 8px;text-align:' + alignOf(ci) + ';font-weight:700;color:#5d2830;white-space:nowrap;">' + (g.sums[ci] == null ? "" : fmtC(g.sums[ci], ci)) + "</td>"; }).join("") + "</tr>";
            g.rows.forEach(function (r) {
              bodyHtml += "<tr>" + r.map(function (cell, ci) { return '<td style="padding:3px 8px 3px ' + (ci === 0 ? "26px" : "8px") + ';text-align:' + alignOf(ci) + ';color:#444;border-bottom:1px solid #f2f2f3;white-space:nowrap;">' + (ci === 0 ? "" : fmtC(cell, ci)) + "</td>"; }).join("") + "</tr>";
            });
          });
        } else {
          this._flatAiRows(at).forEach(function (r) {
            bodyHtml += "<tr>" + r.map(function (cell, ci) { return '<td style="padding:3px 8px;text-align:' + alignOf(ci) + ';color:#444;border-bottom:1px solid #f2f2f3;white-space:nowrap;">' + fmtC(cell, ci) + "</td>"; }).join("") + "</tr>";
          });
        }
        askInner = '<div style="font-size:14px;font-weight:700;color:#333;margin-bottom:4px;">' + esc(actx.question || at.title || "Results") + "</div>"
          + (at.title && actx.question ? '<div style="font-size:11px;color:#888;margin-bottom:2px;">' + esc(at.title) + "</div>" : "")
          + (at.caption ? '<div style="font-size:11px;color:#9e2436;font-weight:600;margin-bottom:10px;">' + esc(at.caption) + "</div>" : '<div style="height:8px;"></div>')
          + '<table style="width:100%;border-collapse:collapse;font-size:11px;">' + head + bodyHtml + "</table>";
      }
      return "<!DOCTYPE html><html><head><meta charset='utf-8'><title>JC Steele &mdash; Sales Insights</title>"
        + "<style>@page{margin:0.4in;}body{margin:0;}*{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;color-adjust:exact !important;}</style></head>"
        + "<body style='font-family:Arial,Helvetica,sans-serif;color:#222;'>"
        + coverPage
        + (askInner ? page("Question & Results", askInner) : "")
        + (shotUrl ? page("Dashboard snapshot", shotPage) : "")
        + page("Overview", dataPage)
        + page("Saved Orders and Quotes", cartsPage)
        + page("Sales Insights & Tips", '<div style="border-top:1px solid #eee;padding-top:6px;">' + sectionH("&#10024; Sales Insights") + insightsPage + '</div><div style="border-top:1px solid #eee;padding-top:6px;margin-top:16px;">' + sectionH("&#128161; Tips, Best Practices and Industry Advice") + coachingPage + "</div>", true)
        + "</body></html>";
    },
    // Load the shared, Master-set per-role default layouts (the locked Layout 1, per role). Graceful if the
    // endpoint/table isn't deployed yet — falls back to the baked DASH_DEFAULT. Response: { roles: { "<role>": "<json>" } }.
    // Which reports may THIS user see (server-authoritative, from the JWT). Master = all. Governs the layout
    // picker + the initial report. Fails soft: a missing/undeployed endpoint leaves authorizedReportsOk false,
    // so the canSee* computeds fall back to the prior role behavior (no lock-out before the backend deploys).
    loadMyReportAccess() {
      var self = this;
      return httpClient.get("/report/access/mine")
        .then(function (res) {
          var list = (res.data && res.data.authorizedReports) || [];
          self.authorizedReports = Array.isArray(list) ? list : [];
          self.authorizedReportsOk = true;
        })
        .catch(function () { self.authorizedReports = []; self.authorizedReportsOk = false; });
    },
    // Land the user on the right report: honor a ?report= / ?layout= deep-link from the Reports dashboard, then
    // make sure the active layout is one they're allowed to see (Master sees all). Called after layouts load.
    applyReportEntry() {
      var qp = (this.$route && this.$route.query) || {};
      // ?tools=1 — arrived from the Layouts pill on another /reports/ page; open the drawer on landing.
      if (String(qp.tools || "") === "1" && this.isLayoutsAdmin) this.dashToolsOpen = true;
      var want = String(qp.report || qp.layout || "").trim().toLowerCase();
      var target = "";
      if (want === "manufacturing-insights" || want === "shared2" || want === "manufacturing") target = "shared2";
      else if (want === "accounting-insights" || want === "shared3" || want === "accounting") target = "shared3";
      else if (want === "sales-insights" || want === "default" || want === "sales") target = "default";
      // A valid, allowed deep-link wins.
      if (target === "shared2" && this.canSeeManufacturing) { if (this.activeLayout !== "shared2") this.selectLayout("shared2"); return; }
      if (target === "shared3" && this.canSeeAccounting) { if (this.activeLayout !== "shared3") this.selectLayout("shared3"); return; }
      if (target === "default" && this.canSeeSalesInsights) { if (this.activeLayout !== "default") this.selectLayout("default"); return; }
      // No (allowed) deep-link: if the restored active layout is a standard report this user can't see, move them
      // to the first standard report they CAN see (prefer Sales Insights, else Manufacturing Insights).
      if (this.activeLayout === "default" && !this.canSeeSalesInsights) {
        if (this.canSeeManufacturing) this.selectLayout("shared2");
        else if (this.canSeeAccounting) this.selectLayout("shared3");
      } else if (this.activeLayout === "shared2" && !this.canSeeManufacturing) {
        if (this.canSeeSalesInsights) this.selectLayout("default");
        else if (this.canSeeAccounting) this.selectLayout("shared3");
      } else if (this.activeLayout === "shared3" && !this.canSeeAccounting) {
        if (this.canSeeSalesInsights) this.selectLayout("default");
        else if (this.canSeeManufacturing) this.selectLayout("shared2");
      }
    },
    loadRoleDefaults() {
      var self = this;
      return httpClient.get("/report/role-default-layout")
        .then(function (res) {
          var raw = (res.data && res.data.roles && typeof res.data.roles === "object") ? res.data.roles : {};
          var out = {};
          Object.keys(raw).forEach(function (role) {
            // Keep a row that carries ONLY docks or ONLY a name too — a report can be named or have its
            // docked arrangement set before any main layout is published, and dropping it here would make
            // the rename silently vanish on reload.
            try { var def = JSON.parse(raw[role]); if (def && (Array.isArray(def.layout) || (def.dockLayouts && typeof def.dockLayouts === "object") || (def.reportDocks && typeof def.reportDocks === "object") || typeof def.name === "string")) out[role] = def; } catch (e) { /* skip bad */ }
          });
          self.roleDefaults = out;
          if (self.activeLayout === "default" || self.activeLayout === "shared2" || self.activeLayout === "shared3") self.applyLayout(self.activeLayout);
        })
        .catch(function () { self.roleDefaults = {}; });
    },
    // Load the user's saved layouts. New format: { active, custom:[{id,name,layout,mapZoom,aiCards,hidden,titleStyles}] }.
    // A legacy single-arrangement save is migrated into one named custom layout so nobody loses their prior work.
    // The card grid resolution was raised to 24 columns + 25px rows (history: 3-col → 6-col at 50px rows →
    // 12-col at 25px → now 24-col at 25px) so admins can place/resize cards very finely. Older saved layouts are
    // normalized to 24-col on load: x & w scale by 24/sourceCols (×8 from 3-col, ×4 from 6-col, ×2 from 12-col);
    // y & h double ONLY for the 3/6-col grids (those used 50px rows) — 12/24-col already use 25px rows. The
    // `cols:24` marker makes this idempotent. Mutates in place.
    normalizeGridTo24(L) {
      if (!L) return L;
      if (!Array.isArray(L.layout) || !L.layout.length) { L.cols = 24; return L; }
      if (L.cols === 24) return L;
      // Infer the source grid width: the explicit marker wins; otherwise the widest card reveals it (a 24-col
      // layout reaches past column 12, a 12-col past 6, a 6-col past 3, a 3-col never exceeds 3). This keeps an
      // already-24 import that lacks the marker from being wrongly rescaled.
      var maxRight = L.layout.reduce(function (m, it) { return Math.max(m, (Number(it.x) || 0) + (Number(it.w) || 1)); }, 0);
      var fromCols = (L.cols === 12) ? 12 : (L.cols === 6) ? 6 : (maxRight > 12 ? 24 : (maxRight > 6 ? 12 : (maxRight > 3 ? 6 : 3)));
      if (fromCols < 24) {
        var fx = 24 / fromCols;                 // ×8 from 3-col, ×4 from 6-col, ×2 from 12-col
        var rowMul = (fromCols <= 6) ? 2 : 1;   // 3/6-col grids also used 50px rows (→25px = ×2); 12/24-col already 25px
        L.layout = L.layout.map(function (it) {
          return Object.assign({}, it, {
            x: (Number(it.x) || 0) * fx,
            w: Math.min(24, Math.max(1, (Number(it.w) || 1) * fx)),
            y: (Number(it.y) || 0) * rowMul,
            h: Math.max(1, (Number(it.h) || 1) * rowMul),
          });
        });
      }
      L.cols = 24;
      return L;
    },
    loadDashLayout() {
      var self = this;
      // When an admin is previewing a specific user, load THAT user's saved dashboard (their personal layouts).
      var asUser = (this.$store && this.$store.getters) ? this.$store.getters.viewingAsUser : null;
      var url = asUser ? ("/user/report-dashboard-for?userId=" + encodeURIComponent(asUser.id)) : "/user/report-dashboard";
      return httpClient.get(url)   // RETURN the promise so mounted()'s `_layoutReady` gate waits for this to finish
        .then(function (res) {
          var raw = res.data && res.data.dashboard;
          var parsed = null;
          if (raw) { try { parsed = JSON.parse(raw); } catch (e) { parsed = null; } }
          // RESET the per-user layout state before parsing — this runs again when the admin switches the
          // "view as user" preview, and leftovers from the previous user must never bleed into the next.
          self.emailHiddenLayout = null; self.emailVisibleLayout = null; self.dockLayouts = {}; self.dockLayoutsByReport = {}; self.emailSnapLayout = "";
          // Saved filter favorites + groups ride along in the same per-user blob. RESET them first — else a
          // Master's OWN favorites bleed into an impersonated view whose blob has none (only the loaded user's
          // favorites may ever show). The save side is already impersonation-guarded (persistDash/saveFavorites).
          self.favorites = []; self.favoriteGroups = [];
          if (parsed && Array.isArray(parsed.favorites)) self.favorites = parsed.favorites.filter(function (f) { return f && f.id && f.filters; });
          if (parsed && Array.isArray(parsed.favoriteGroups)) self.favoriteGroups = parsed.favoriteGroups.filter(function (g) { return g && g.id; });
          if (parsed && typeof parsed.emailLinked === "boolean") self.emailLinked = parsed.emailLinked;
          if (parsed && parsed.emailHiddenLayout && Array.isArray(parsed.emailHiddenLayout.layout)) self.emailHiddenLayout = parsed.emailHiddenLayout;
          if (parsed && parsed.emailVisibleLayout && Array.isArray(parsed.emailVisibleLayout.layout)) self.emailVisibleLayout = parsed.emailVisibleLayout;
          // Docked-layout scenarios (one arrangement per dock combination). MIGRATION: a pre-scenario blob
          // only has the single legacy emailHiddenLayout — seed the "email" scenario from it, and derive
          // "email+filters" by dropping the Filters card (the grid re-compacts the gap upward on apply).
          self.dockLayouts = (parsed && parsed.dockLayouts && typeof parsed.dockLayouts === "object" && !Array.isArray(parsed.dockLayouts)) ? parsed.dockLayouts : {};
          self.dockLayoutsByReport = (parsed && parsed.dockLayoutsByReport && typeof parsed.dockLayoutsByReport === "object" && !Array.isArray(parsed.dockLayoutsByReport)) ? parsed.dockLayoutsByReport : {};
          // An old blob has no stamp; its snapshot can only have come from Layout 1, so default to that.
          self.emailSnapLayout = (parsed && typeof parsed.emailSnapLayout === "string") ? parsed.emailSnapLayout : "default";
          if (!self.dockLayouts["email"] && self.emailHiddenLayout && Array.isArray(self.emailHiddenLayout.layout)) {
            self.$set(self.dockLayouts, "email", JSON.parse(JSON.stringify(self.emailHiddenLayout)));
            if (!self.dockLayouts["email+filters"]) {
              var bothSeed = JSON.parse(JSON.stringify(self.emailHiddenLayout));
              bothSeed.layout = (bothSeed.layout || []).filter(function (it) { return it.i !== "filters"; });
              self.$set(self.dockLayouts, "email+filters", bothSeed);
            }
          }
          // Docked layouts are stored in the shared rpt_RoleDefaultLayout "ALL" row (roleDefaults.ALL.dockLayouts)
          // — the SAME DB table as the main Layout 1, published by the Master. That table is the AUTHORITATIVE
          // source for EVERY user (incl. the Master who authors it), so it OVERRIDES the per-user blob per docked
          // scenario key — which is what stops a docked arrangement from drifting/being lost. Only keys the table
          // actually HAS override, so a table with no docks never wipes a blob-held arrangement. loadRoleDefaults()
          // has already resolved by the time this runs (mounted() chain).
          var _sharedDock = (self.roleDefaults && self.roleDefaults["ALL"] && self.roleDefaults["ALL"].dockLayouts
            && typeof self.roleDefaults["ALL"].dockLayouts === "object") ? self.roleDefaults["ALL"].dockLayouts : null;
          if (_sharedDock) {
            Object.keys(_sharedDock).forEach(function (k) {
              if (_sharedDock[k] && Array.isArray(_sharedDock[k].layout))
                self.$set(self.dockLayouts, k, JSON.parse(JSON.stringify(_sharedDock[k])));
            });
          }
          // Same rule for every OTHER shared report, from its OWN row's `reportDocks` — authoritative and
          // WHOLESALE, so a stale personal namespace can never outlive a helpdesk publish. We deliberately
          // do NOT read `dockLayouts` off a non-ALL row: saveRoleDefault used to stamp the flat SALES map
          // into every row it wrote, so SHARED2/SHARED3 both carry Sales-shaped presets there (confirmed in
          // the sandbox DB — SHARED2 has three dock scenarios for a one-card layout). Reading the new key
          // leaves that garbage inert, and it self-cleans as soon as each report is published again.
          SHARED_REPORTS.forEach(function (R) {
            if (R.roleKey === "ALL") return;
            var rd = self.roleDefaults && self.roleDefaults[R.roleKey];
            var rdk = (rd && rd.reportDocks && typeof rd.reportDocks === "object" && !Array.isArray(rd.reportDocks)) ? rd.reportDocks : null;
            if (rdk) self.$set(self.dockLayoutsByReport, R.roleKey, JSON.parse(JSON.stringify(rdk)));
          });
          // emailDocked is NO LONGER restored from the blob — Email Insights starts collapsed EVERY
          // session (docked-by-default, user decision 2026-07-21); opening it is a per-session action.
          // (The flag is still written for back-compat; older bundles reading it are unaffected.)
          if (parsed && Array.isArray(parsed.layoutVersions)) self.layoutVersions = parsed.layoutVersions.filter(function (v) { return v && v.id && Array.isArray(v.layout); });
          // (Q&A history now lives in rpt_AiHistory — loaded by loadAiHistory(), not this blob.)
          if (parsed && !Array.isArray(parsed) && Array.isArray(parsed.custom)) {
            self.customLayouts = parsed.custom
              .filter(function (L) { return L && L.id && Array.isArray(L.layout); })
              .map(function (L) { return self.normalizeGridTo24(L); });   // normalize 3-col / 6-col → 12-col
            self.activeLayout = parsed.active || "default";
            if (self.activeLayout !== "default" && !(self.activeLayout === "shared2" && self.canSeeSharedLayout2) && !(self.activeLayout === "shared3" && self.canSeeAccounting) && !self.customLayouts.some(function (L) { return L.id === self.activeLayout; })) self.activeLayout = "default";
          } else if (parsed && (Array.isArray(parsed) || Array.isArray(parsed.layout))) {
            var arr = Array.isArray(parsed) ? parsed : parsed.layout;
            self.customLayouts = [{
              id: "L" + Date.now(),
              name: "My Layout",
              layout: arr,
              mapZoom: (parsed && typeof parsed.mapZoom === "number") ? parsed.mapZoom : 0.64,
              aiCards: (parsed && parsed.aiCards && typeof parsed.aiCards === "object") ? parsed.aiCards : {},
              hidden: (parsed && Array.isArray(parsed.hidden)) ? parsed.hidden : [],
              titleStyles: (parsed && parsed.titleStyles && typeof parsed.titleStyles === "object") ? parsed.titleStyles : {}
            }];
            self.normalizeGridTo24(self.customLayouts[0]);   // normalize 3-col / 6-col → 12-col
            self.activeLayout = self.customLayouts[0].id;
          } else {
            self.customLayouts = []; self.activeLayout = "default";
          }
          self.applyLayout(self.activeLayout);
        })
        .catch(function () { self.favorites = []; self.favoriteGroups = []; self.customLayouts = []; self.activeLayout = "default"; self.applyLayout("default"); });
    },
    // Append any built-in card missing from an older saved layout (e.g. the newer
    // Customer Order Details + Order Details cards) so existing users still get them.
    ensureBuiltinCards() {
      // Filters + Favorites are grid cards again. If either is missing (older saved layouts predate them), inject
      // them at the top (Filters left, Favorites beside it) and push existing cards down — they're essential, so
      // this runs on EVERY layout. Then auto-populate the remaining built-ins on the shared Default only.
      var have0 = {};
      (this.dashLayout || []).forEach(function (it) { have0[it.i] = true; });
      // Filters is force-present EXCEPT while docked-to-the-right (desktop): then it's stripped and reopened from
      // the Filters rail pill. Done imperatively here (a method, not the grid-consumed effectiveLayout) so it
      // never triggers the vue-grid-layout render loop. Favorites is unaffected.
      var wantFilters = !this.filtersDocked;
      if (Array.isArray(this.dashLayout) && ((!have0["filters"] && wantFilters) || !have0["favorites"])) {
        if (!have0["filters"] && wantFilters) this.dashLayout.forEach(function (it) { if (it.i !== "filters" && it.i !== "favorites") it.y = (Number(it.y) || 0) + 9; });
        if (!have0["favorites"]) this.dashLayout.unshift({ i: "favorites", x: 20, y: 0, w: 4, h: 9 });
        if (!have0["filters"] && wantFilters) this.dashLayout.unshift({ i: "filters", x: 0, y: 0, w: 20, h: 9 });
      }
      // Docked → strip any 'filters' a saved/loaded layout still carries (keeps the live grid clean; the stored
      // blob is untouched, so reopening restores it at its saved spot on the next applyLayout).
      if (this.filtersDocked && have0["filters"]) this.dashLayout = this.dashLayout.filter(function (it) { return it.i !== "filters"; });
      // A SAVED layout can still carry partCosts after a role change or revocation. Strip it the same
      // imperative way the docked Filters card is stripped above, so the card frame and its title never
      // render for someone who has lost the role. NOT a dashLayout render-filter: that caused a
      // vue-grid-layout infinite render loop before (see the note by dashLayout in data()).
      if (!this.canSeeCost || !this.canSeeLoss) {
        var seeC = this.canSeeCost, seeL = this.canSeeLoss;
        var keepB = (this.dashLayout || []).filter(function (it) {
          if (it.i === "partCosts") return seeC;
          if (it.i === "partLoss") return seeL;
          return true;
        });
        // Length-guarded like pruneUnauthorizedCards: a no-op reassign is free fuel for the
        // vue-grid-layout render loop — only hand the grid a new array when a card actually left.
        if (keepB.length !== (this.dashLayout || []).length) this.dashLayout = keepB;
      }
      var have = {};
      this.dashLayout.forEach(function (it) { have[it.i] = true; });
      var hidden = this.dashHidden || [];
      // Auto-populate the rest of the built-in cards ONLY on the shared Default (Layout 1). Custom layouts are
      // curated — they start blank and show only the cards the user places via the picker / Add card.
      if (this.activeLayout !== "default") return;
      var maxY = this.dashLayout.reduce(function (m, it) { return Math.max(m, (Number(it.y) || 0) + (Number(it.h) || 0)); }, 0);
      var self = this;
      DASH_DEFAULT.forEach(function (def) {
        // A DOCKED Filters card is owned by the rail pill / edge tab — this auto-populate must never
        // re-append it (it would resurrect at the bottom on every reload while docked).
        if (def.i === "filters" && self.filtersDocked) return;
        // Costing for Parts is role-gated. Skipping it HERE (imperatively) rather than in a
        // dashLayout render-filter is deliberate: filtering the grid-consumed layout caused a
        // vue-grid-layout infinite render loop before (see the note by dashLayout in data()).
        if (def.i === "partCosts" && !self.canSeeCost) return;
        if (have[def.i] || hidden.indexOf(def.i) >= 0) return;   // skip placed + hidden
        self.dashLayout.push({ i: def.i, x: def.x, y: maxY, w: def.w, h: def.h });
        maxY += def.h;
      });
    },
    // REVIEW FINDING #5. ensureBuiltinCards strips an unauthorized partCosts, but three other paths
    // REPLACE this.dashLayout wholesale and never re-stripped it: applyDockPreset (the EMAIL/FILTERS
    // edge tabs and the Filters button) and restoreEmailInsights, both of which load the SHARED
    // roleDefaults blob verbatim. A stray partCosts entry in that blob therefore leaked the card FRAME
    // and title to anyone who opened the Filters card. Figures never leaked (loadPartCosts returns
    // early and the endpoint 403s), but the frame should not be there at all.
    // Call this after ANY assignment to dashLayout that does not go through ensureBuiltinCards.
    pruneUnauthorizedCards() {
      if (!Array.isArray(this.dashLayout)) return;
      var seeCost = this.canSeeCost, seeLoss = this.canSeeLoss;
      if (seeCost && seeLoss) return;
      var keep = this.dashLayout.filter(function (it) {
        if (it.i === "partCosts") return seeCost;
        if (it.i === "partLoss") return seeLoss;
        return true;
      });
      // ⚠ Reassign ONLY when a card was actually removed. This method runs inside onDashLayoutUpdated —
      // vue-grid-layout's OWN feedback event — so an unconditional `dashLayout = filter(...)` hands the
      // grid a NEW array identity on every event even when nothing changed: grid re-renders → emits
      // layout-updated again → prune again → the documented infinite-render FREEZE, for every viewer
      // whose role lacks the cost cards (any real Sales rep; any view-as preview of one). First hit:
      // view-as Mark Warren · Sales, 2026-07-31 — possible only after provision-on-preview granted the
      // role. The 07-30 watcher guard missed this path (its comment assumed Sales previews were safe).
      if (keep.length !== this.dashLayout.length) this.dashLayout = keep;
    },
    // Hide a built-in card — record it so ensureBuiltinCards won't re-add it on reload.
    hideCard(i) {
      if (this.dashHidden.indexOf(i) < 0) this.dashHidden.push(i);
      this.dashLayout = (this.dashLayout || []).filter(function (it) { return it.i !== i; });
      this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
      this.saveDashLayoutNow();
    },
    // Bring back every USER-hidden built-in card. Docked cards are NOT hidden cards — normalizeDockState
    // re-adds the Email dock marker (and keeps docked cards stripped) so this can't resurrect a docked card.
    restoreHiddenCards() {
      this.dashHidden = [];
      this.normalizeDockState();
      this.pruneUnauthorizedCards();
      this.ensureBuiltinCards();
      this.saveDashLayoutNow();
    },
    // Apply a layout into the live working state. "default" = the locked DASH_DEFAULT (read-only); else a saved custom layout.
    applyLayout(id) {
      var src = null;
      if (id === "shared2") {
        // Shared "Layout 2" — the Master-curated team layout ("SHARED2"). If not published yet, start BLANK
        // (just the Filters bar) like a new layout; a Master builds it up and it auto-publishes to Master + Sales All.
        var rd2 = this.roleDefaults ? this.roleDefaults["SHARED2"] : null;
        if (rd2 && Array.isArray(rd2.layout)) { this.normalizeGridTo24(rd2); src = rd2; }
        else src = { layout: [{ i: "filters", x: 0, y: 0, w: 24, h: 9 }], mapZoom: 0.64, aiCards: {}, hidden: [], titleStyles: {} };
      } else if (id === "shared3") {
        // Shared Accounting report ("SHARED3"). Until helpdesk publishes an arrangement, the SEED already IS
        // the report: Filters + Parts Sold at a Loss + Costing for Parts — so an accounting user gets a working
        // page on day one. ensureBuiltinCards/pruneUnauthorizedCards still strip either cost card from anyone
        // whose role can't see it (an Accounting-Insights-only user keeps partLoss but loses partCosts).
        var rd3 = this.roleDefaults ? this.roleDefaults["SHARED3"] : null;
        if (rd3 && Array.isArray(rd3.layout)) { this.normalizeGridTo24(rd3); src = rd3; }
        else src = { layout: [{ i: "filters", x: 0, y: 0, w: 24, h: 9 }, { i: "partLoss", x: 0, y: 9, w: 24, h: 14 }, { i: "partCosts", x: 0, y: 23, w: 24, h: 12 }], mapZoom: 0.64, aiCards: {}, hidden: [], titleStyles: {} };
      } else if (id !== "default") {
        src = this.customLayouts.find(function (L) { return L.id === id; });
      } else {
        // Layout 1 (Default) = the ONE shared default ("ALL", Master-set) if it exists, else the baked DASH_DEFAULT. Same for EVERY role.
        var rd = this.roleDefaults ? this.roleDefaults["ALL"] : null;
        if (rd && Array.isArray(rd.layout)) { this.normalizeGridTo24(rd); src = rd; }   // normalize 3-col / 6-col → 12-col
      }
      if (src) {
        this.dashLayout = JSON.parse(JSON.stringify(src.layout || []));
        this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
        this.mapZoom = (typeof src.mapZoom === "number") ? src.mapZoom : 0.64;
        this.aiCards = JSON.parse(JSON.stringify(src.aiCards || {}));
        this.dashHidden = (src.hidden || []).slice();
        this.titleStyles = JSON.parse(JSON.stringify(src.titleStyles || {}));
      } else {
        this.dashLayout = JSON.parse(JSON.stringify(DASH_DEFAULT));
        this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
        this.mapZoom = 0.64; this.aiCards = {}; this.dashHidden = []; this.titleStyles = {};
      }
      this.ensureBuiltinCards();
      this.loadCustomCards();
      // Anything DOCKED → re-apply the CURRENT SCENARIO's saved arrangement over the just-loaded main layout —
      // LIVE only (no persist), so a hard refresh returns to the same view without ever mutating the stored
      // main. Falls back to the LEGACY single Email-hidden preset for pre-scenario blobs.
      var dk = this.dockKey, dsc = this.dockScopeFor(id);   // scope from the id being APPLIED, not activeLayout
      if (dsc && dk && this.applyDockPreset(dk, dsc)) {
        // scenario preset applied (applyDockPreset normalized the docked cards + markers)
      } else if (id === "default" && this.isMaster && this.emailDocked && this.emailHiddenLayout && Array.isArray(this.emailHiddenLayout.layout)) {
        var pd = this.emailHiddenLayout;
        this.dashLayout = JSON.parse(JSON.stringify(pd.layout)).filter(function (it) { return it.i !== "emailInsights"; });
        this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
        this.dashHidden = (pd.hidden || []).slice();
        if (this.dashHidden.indexOf("emailInsights") < 0) this.dashHidden.push("emailInsights");
        this.titleStyles = JSON.parse(JSON.stringify(pd.titleStyles || {}));
        if (typeof pd.mapZoom === "number") this.mapZoom = pd.mapZoom;
      }
      // Session-default docking (every role, any layout): Email Insights starts collapsed each session, so if
      // the card is still in the just-applied layout, strip it LIVE-only here — same shape as the Master preset
      // above; the STORED layouts are never mutated by this. (Rendering must never filter dashLayout — see
      // effectiveLayout.) Snapshot first so restoreEmailInsights can put the card back exactly where it was;
      // marking it in dashHidden keeps ensureBuiltinCards from re-appending it on the next data reload.
      if (this.emailDocked && (this.dashLayout || []).some(function (it) { return it.i === "emailInsights"; })) {
        this._emailLayoutSnap = JSON.parse(JSON.stringify(this.dashLayout));
        // Refresh the Email-visible snapshot too (in memory, not persisted here) — on custom layouts
        // restoreEmailInsights rebuilds from this, and a stale one from an old dock on ANOTHER layout
        // would otherwise swap the user's arrangement on restore.
        this.emailVisibleLayout = {
          layout: JSON.parse(JSON.stringify(this.dashLayout)),
          hidden: (this.dashHidden || []).slice(),
          titleStyles: JSON.parse(JSON.stringify(this.titleStyles || {})),
          mapZoom: this.mapZoom
        };
        this.emailSnapLayout = this.activeLayout;   // see collapseEmailInsights
        var de = this.dashLayout.find(function (it) { return it.i === "emailInsights"; });
        if (de) this._emailPos = { x: de.x, y: de.y, w: de.w, h: de.h };
        this.dashLayout = this.dashLayout.filter(function (it) { return it.i !== "emailInsights"; });
        this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
        if (this.dashHidden.indexOf("emailInsights") < 0) this.dashHidden.push("emailInsights");
      }
      // Dock-state sync — must run LAST: the docked-preset overrides above can REPLACE dashLayout with a
      // snapshot captured in a DIFFERENT dock state (which once silently erased the just-injected Filters
      // card and made its pill look dead). normalizeDockState strips docked cards (+ syncs the Email hidden
      // marker) and guarantees an UNdocked Filters card exists — whatever source the layout came from.
      this.normalizeDockState();
      this.pruneUnauthorizedCards();
      this.autoSizeCards();   // collapse the variable-content cards to fit on the freshly applied layout (covers the
                              // race where the layout is applied AFTER the data already loaded — e.g. a layout switch)
      this.seedLayoutHistory();   // fresh undo history for the layout we just loaded/switched to
    },
    // Copy the live working state back into the active custom layout (no-op on the locked Default).
    syncActiveIntoCustom() {
      if (this.activeLayout === "default") return;
      var id = this.activeLayout;
      var L = this.customLayouts.find(function (x) { return x.id === id; });
      if (!L) return;
      L.layout = JSON.parse(JSON.stringify(this.dashLayout || []));
      L.mapZoom = this.mapZoom;
      L.aiCards = JSON.parse(JSON.stringify(this.aiCards || {}));
      L.hidden = (this.dashHidden || []).slice();
      L.titleStyles = JSON.parse(JSON.stringify(this.titleStyles || {}));
    },
    // Persist all custom layouts + which one is active.
    persistDash() {
      if (this.viewingAsUser) return;   // previewing another user → read-only; never write to my own dashboard
      this.syncActiveIntoCustom();
      (this.customLayouts || []).forEach(function (L) { L.cols = 12; });   // mark as 12-col so the migration stays idempotent
      httpClient.post("/user/report-dashboard", { Dashboard: JSON.stringify({ active: this.activeLayout, custom: this.customLayouts, favorites: this.favorites, favoriteGroups: this.favoriteGroups, qaHistory: this.qaHistory, emailLinked: this.emailLinked, emailHiddenLayout: this.emailHiddenLayout, emailVisibleLayout: this.emailVisibleLayout, dockLayouts: this.dockLayouts, dockLayoutsByReport: this.dockLayoutsByReport, emailSnapLayout: this.emailSnapLayout, emailDocked: this.emailDocked, layoutVersions: this.layoutVersions }) }).catch(function () { /* non-fatal */ });
    },
    // Switch the active layout (saving the one we're leaving first).
    selectLayout(id) {
      if (id === this.activeLayout) return;
      this.syncActiveIntoCustom();
      this.activeLayout = id;
      this.applyLayout(id);
      this.persistDash();
    },
    // Create a new named layout from the current view; it becomes editable + active.
    newLayout() {
      if (!this.canCreateLayout) return;   // Sales / Sales All can't create layouts (guard behind the hidden button)
      var name = window.prompt("Name this layout:", "Layout " + (this.customLayouts.length + 2));
      if (name === null) return;
      name = (name || "").trim() || ("Layout " + (this.customLayouts.length + 2));
      this.syncActiveIntoCustom();
      var id = "L" + Date.now();
      this.customLayouts.push({
        id: id, name: name,
        // Blank canvas — just the top Filters bar. The user builds it up via the template picker / Add card,
        // so new layouts start empty rather than cloning the current view.
        layout: [{ i: "filters", x: 0, y: 0, w: 24, h: 9 }],
        mapZoom: 0.64,
        aiCards: {},
        hidden: [],
        titleStyles: {}
      });
      this.activeLayout = id;
      this.applyLayout(id);
      this.persistDash();
    },
    renameLayout(id) {
      var L = this.customLayouts.find(function (x) { return x.id === id; });
      if (!L) return;
      var name = window.prompt("Rename layout:", L.name || "");
      if (name === null) return;
      L.name = (name || "").trim() || L.name;
      this.persistDash();
    },
    deleteLayout(id) {
      var L = this.customLayouts.find(function (x) { return x.id === id; });
      if (!L) return;
      if (!window.confirm('Delete the layout "' + (L.name || "") + '"?')) return;
      this.customLayouts = this.customLayouts.filter(function (x) { return x.id !== id; });
      if (this.activeLayout === id) { this.activeLayout = "default"; this.applyLayout("default"); }
      this.persistDash();
    },
    // Master: download a layout's JSON (the locked Default, or a custom one you built) so it can be baked
    // into the app's default (DASH_DEFAULT) and shipped to production. Pure client-side download — no backend.
    exportLayout(id) {
      var src;
      if (id === "default") {
        // Export the LIVE shared default (the Master-saved roleDefault) when one exists, else the baked default.
        var rd = this.roleDefaults ? this.roleDefaults["ALL"] : null;
        src = (rd && Array.isArray(rd.layout))
          ? { name: "Default", layout: rd.layout, mapZoom: rd.mapZoom, titleStyles: rd.titleStyles, hidden: rd.hidden, cols: rd.cols }
          : { name: "Default", layout: DASH_DEFAULT, mapZoom: 0.64, titleStyles: {}, hidden: [], cols: 24 };
      } else if (id === "shared2") {
        // Export the shared Manufacturing Insights report (roleDefault "SHARED2"), else a blank filters-only start.
        var rd2 = this.roleDefaults ? this.roleDefaults["SHARED2"] : null;
        src = (rd2 && Array.isArray(rd2.layout))
          ? { name: "Manufacturing Insights", layout: rd2.layout, mapZoom: rd2.mapZoom, titleStyles: rd2.titleStyles, hidden: rd2.hidden, cols: rd2.cols }
          : { name: "Manufacturing Insights", layout: [{ i: "filters", x: 0, y: 0, w: 24, h: 9 }], mapZoom: 0.64, titleStyles: {}, hidden: [], cols: 24 };
      } else if (id === "shared3") {
        // Export the shared Accounting report (roleDefault "SHARED3"), else its seed (Filters + the two cost cards).
        var rd3 = this.roleDefaults ? this.roleDefaults["SHARED3"] : null;
        src = (rd3 && Array.isArray(rd3.layout))
          ? { name: "Accounting", layout: rd3.layout, mapZoom: rd3.mapZoom, titleStyles: rd3.titleStyles, hidden: rd3.hidden, cols: rd3.cols }
          : { name: "Accounting", layout: [{ i: "filters", x: 0, y: 0, w: 24, h: 9 }, { i: "partLoss", x: 0, y: 9, w: 24, h: 14 }, { i: "partCosts", x: 0, y: 23, w: 24, h: 12 }], mapZoom: 0.64, titleStyles: {}, hidden: [], cols: 24 };
      } else {
        src = this.customLayouts.find(function (L) { return L.id === id; });
      }
      if (!src) return;
      var payload = {
        kind: "customer-insights-layout",
        name: src.name || "Layout",
        host: (window.location && window.location.hostname) || "",
        cols: (typeof src.cols === "number") ? src.cols : 12,   // grid system travels with the file (prevents re-scaling on import)
        layout: JSON.parse(JSON.stringify(src.layout || [])),
        mapZoom: (typeof src.mapZoom === "number") ? src.mapZoom : 0.64,
        titleStyles: src.titleStyles || {},
        hidden: src.hidden || []
      };
      var slug = (payload.name || "layout").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "layout";
      var blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url; a.download = "customer-insights-" + slug + ".json";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
    },
    // Sales / Sales All: import a Master's exported layout file → add it as a personal layout, made active.
    triggerImport() {
      var el = this.$refs.layoutImportInput;
      if (el) { el.value = ""; el.click(); }
    },
    // Registry-driven import: picks the right hidden <input> for a shared report's row so the drawer can
    // be a v-for instead of one hardcoded block per report.
    triggerImportFor(roleKey) {
      if (roleKey === "SHARED2") return this.triggerImportShared2();
      if (roleKey === "SHARED3") return this.triggerImportShared3();
      return this.triggerImport();
    },
    // Open a standalone report from the drawer (Manufacturing / Foundry / Work Order Insights). They have
    // no card grid, so there is no layout to control — the drawer just gets you there.
    openLauncherReport(path) {
      if (!path) return;
      this.dashToolsOpen = false;
      this.$router.push({ path: path }).catch(function () { /* same-route click */ });
    },
    // RENAME a shared standard report (helpdesk@jcsteele.com + Master; the server re-checks). Renaming is
    // purely cosmetic: the report KEY, the SHARED<N> role row, Report Access rows, the assigned role and
    // rpt_ReportNews.ReportKey all key off ids that never change — verified before building this.
    // It publishes the STORED row with only `name` swapped, so a rename can never push whatever the
    // publisher happens to be looking at over the live report.
    renameReport(id) {
      var R = SHARED_REPORTS.find(function (r) { return r.id === id; });
      if (!R || !this.canPublishSharedLayout) return;
      var current = this.reportNameOf(id);
      var next = window.prompt("Rename this report (blank restores “" + R.name + "”):", current);
      if (next === null) return;                       // cancelled
      next = String(next).trim().replace(/\s+/g, " ");
      if (next.length > 60) { window.alert("Please keep the name under 60 characters."); return; }
      if (next === current) return;                    // nothing to do
      var clearing = next.length === 0;
      // Start from what is PUBLISHED for this report so the layout, docks and hidden set survive untouched.
      var stored = (this.roleDefaults && this.roleDefaults[R.roleKey]) || null;
      var def = stored ? JSON.parse(JSON.stringify(stored)) : {};
      if (clearing) delete def.name; else def.name = next;
      var self = this;
      httpClient.post("/report/role-default-layout", {
        role: R.roleKey,
        layout: JSON.stringify(def),
        companyScope: this._activeCompanyCode(),
        clearName: clearing        // tells the server's preserve-merge NOT to resurrect the old name
      }).then(function (res) {
        if (res && res.data && res.data.ok === false) { window.alert(res.data.message || "Couldn't rename that report."); return; }
        self.$set(self.roleDefaults, R.roleKey, def);   // reflect immediately; reportNameOf is reactive on this
        self.dashSaved = true; setTimeout(function () { self.dashSaved = false; }, 1800);
      }).catch(function () { window.alert("Couldn't rename that report (network)."); });
    },
    // Master: import a layout file → set the shared Manufacturing Insights report (SHARED2) for everyone with access.
    triggerImportShared2() {
      var el = this.$refs.layoutImportInputShared2;
      if (el) { el.value = ""; el.click(); }
    },
    triggerImportShared3() {
      var el = this.$refs.layoutImportInputShared3;
      if (el) { el.value = ""; el.click(); }
    },
    // Master: import a layout file → set a shared standard report. roleKey "SHARED2" = Manufacturing Insights;
    // anything else = Layout 1 "ALL" (Sales Insights, seen by every report user).
    onImportFile(e, roleKey) {
      var self = this;
      var target = roleKey === "SHARED3" ? "SHARED3" : roleKey === "SHARED2" ? "SHARED2" : "ALL";
      var activeMatch = target === "SHARED3" ? "shared3" : target === "SHARED2" ? "shared2" : "default";
      var file = e && e.target && e.target.files && e.target.files[0];
      if (!file) return;
      if (!self.canPublishSharedLayout) { window.alert("Only helpdesk@jcsteele.com, signed in as Master with NC999 selected, can set a standard report's layout."); return; }
      var reader = new FileReader();
      reader.onload = function (ev) {
        var payload = null;
        try { payload = JSON.parse(ev.target.result); } catch (err) { payload = null; }
        if (!payload || !Array.isArray(payload.layout)) { window.alert("That file doesn't look like a Customer Insights layout export."); return; }
        var def = {
          // Honor the file's grid system; default to 12 (current grid) so a current export isn't re-scaled on load.
          cols: (typeof payload.cols === "number") ? payload.cols : 12,
          layout: payload.layout,
          mapZoom: (typeof payload.mapZoom === "number") ? payload.mapZoom : 0.64,
          hidden: Array.isArray(payload.hidden) ? payload.hidden : [],
          titleStyles: (payload.titleStyles && typeof payload.titleStyles === "object") ? payload.titleStyles : {}
        };
        httpClient.post("/report/role-default-layout", { role: target, layout: JSON.stringify(def), companyScope: self._activeCompanyCode() })
          .then(function (res) {
            if (res && res.data && res.data.ok === false) { window.alert("Not saved: " + (res.data.message || "the server refused the change.")); return; }
            self.$set(self.roleDefaults, target, def);
            if (self.activeLayout === activeMatch) self.applyLayout(activeMatch);
            window.alert(target === "SHARED3"
              ? "Accounting report updated — everyone with access now sees this arrangement."
              : target === "SHARED2"
                ? "Manufacturing Insights updated — everyone with access now sees this arrangement."
                : "Default Layout 1 updated — every report user now sees this arrangement.");
          })
          .catch(function () { window.alert("Couldn't save — the role-default backend may not be deployed yet (or rpt_RoleDefaultLayout isn't created)."); });
      };
      reader.readAsText(file);
    },
    // Persist the active layout (debounced ~800ms — vue-grid-layout fires freely while dragging/resizing). No-op on the locked Default.
    saveDashLayout() {
      if (!this._layoutReady) return;   // don't persist a half-loaded layout (race guard — see mounted())
      var self = this;
      if (this._dashSaveTimer) clearTimeout(this._dashSaveTimer);
      // Any card docked (Master, Layout 1): refine the CURRENT SCENARIO's docked preset — never the shared main layout.
      if (this.dockScope && this.canPublishSharedLayout && this.dockKey) {
        this._dashSaveTimer = setTimeout(function () { self.syncDockPresetFromLive(); }, 800);
        return;
      }
      if (this.activeLayout === "default" || this.activeLayout === "shared2" || this.activeLayout === "shared3") {
        if (!this.canPublishSharedLayout) return;   // shared layouts are read-only for everyone but the publisher
        var roleKey = this.activeLayout === "shared3" ? "SHARED3" : this.activeLayout === "shared2" ? "SHARED2" : "ALL";
        this._dashSaveTimer = setTimeout(function () { self.saveRoleDefault(roleKey); }, 800);  // Master arranging a shared layout → publish it
        return;
      }
      this._dashSaveTimer = setTimeout(function () { self.persistDash(); }, 800);
    },
    // Discrete change (place / delete / hide / restore / reset) → persist IMMEDIATELY (skip the 800ms debounce)
    // and show the brief "✓ Saved" cue. Continuous drag/resize still uses the debounced saveDashLayout().
    saveDashLayoutNow() {
      if (!this._layoutReady) return;   // don't persist a half-loaded layout (race guard — see mounted())
      if (this._dashSaveTimer) { clearTimeout(this._dashSaveTimer); this._dashSaveTimer = null; }
      if (this.viewingAsUser) return;   // read-only while previewing another user
      // Any card docked (Master, Layout 1): discrete edits refine the CURRENT SCENARIO's docked preset — never the shared main layout.
      if (this.dockScope && this.canPublishSharedLayout && this.dockKey) {
        this.syncDockPresetFromLive();
        this.recordLayoutHistory();
        var selfEd = this; this.dashSaved = true; setTimeout(function () { selfEd.dashSaved = false; }, 1800);
        return;
      }
      if (this.activeLayout === "default" || this.activeLayout === "shared2" || this.activeLayout === "shared3") {
        if (!this.canPublishSharedLayout) return;   // read-only for everyone but the publisher
        this.saveRoleDefault(this.activeLayout === "shared3" ? "SHARED3" : this.activeLayout === "shared2" ? "SHARED2" : "ALL");
      } else {
        this.persistDash();
      }
      this.recordLayoutHistory();   // discrete change (place/hide/restore/reset/…) → snapshot for undo
      var self = this; this.dashSaved = true; setTimeout(function () { self.dashSaved = false; }, 1800);
    },
    // Scroll a just-placed/created card into view and flash it red so the user notices where it landed.
    flashCard(gid) {
      this.$nextTick(function () {
        var el = null;
        try { el = document.querySelector('[data-card-id="' + gid + '"]'); } catch (e) { el = null; }
        if (!el) return;
        try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (e2) { /* older browsers */ }
        el.classList.remove("dash-card--flash");
        void el.offsetWidth;   // restart the animation if the class lingered
        el.classList.add("dash-card--flash");
        setTimeout(function () { try { el.classList.remove("dash-card--flash"); } catch (e3) { /* noop */ } }, 3600);
      });
    },
    // The browser's active company code, as the server sees it reported. One place, so the three publish
    // callers cannot disagree about where it lives.
    _activeCompanyCode() {
      return (typeof localStorage !== "undefined") ? String(localStorage.companyCode || "").trim() : "";
    },
    // Refuse to publish an arrangement that would leave every user staring at nothing. Nothing here is
    // hypothetical: hideCard() can hide every card, ensureBuiltinCards HONOURS `hidden` so it will not
    // self-heal, and non-publishers cannot reset — so a blank publish would strand everyone with no way out.
    publishBlocker() {
      var L = this.dashLayout || [];
      if (!L.length && !this.dockKey) return "There are no cards on this layout.";
      var hid = this.dashHidden || [];
      var allHidden = DASH_DEFAULT.every(function (d) { return hid.indexOf(d.i) >= 0; });
      if (allHidden) return "Every built-in card is hidden — users would see a blank dashboard.";
      var hasFilters = L.some(function (it) { return it.i === "filters"; });
      if (!hasFilters && !this.filtersDocked) return "The Filters card is missing and not docked — users would have no way to filter.";
      return "";
    },
    // Strip grid slots whose CONTENT cannot resolve for anybody else. aiCards is not part of the published
    // blob, so an ai-* slot would render for every user as an orphan titled "ai-1753…". partCosts is
    // role-restricted, and publishing it from an account that cannot see cost would be meaningless.
    sanitizeForPublish(def) {
      var self = this;
      var ccLoaded = Object.keys(this.customCards || {}).length > 0;
      def.layout = (def.layout || []).filter(function (it) {
        if (self.isAiCard(it.i)) return !!(self.aiCards && self.aiCards[it.i]);
        if (self.isCustomCard(it.i)) return !ccLoaded || !!self.customCards[it.i];
        return true;
      });
      if (!self.canSeeCost) def.layout = def.layout.filter(function (it) { return it.i !== "partCosts"; });
      if (!self.canSeeLoss) def.layout = def.layout.filter(function (it) { return it.i !== "partLoss"; });
      return def;
    },
    // The ONE deliberate publish. Dock-aware, and that matters: while anything is docked this.dashLayout IS
    // the DOCKED arrangement with the docked cards stripped out, so publishing it as the main layout would
    // permanently delete Filters / Email Insights for everyone. Docked -> update this scenario's preset and
    // leave the stored main layout alone; undocked -> publish the main layout (saveRoleDefault carries the
    // docks forward). Everyone's default dockKey is "email+filters", so the docked path is the common one.
    publishSharedLayout() {
      if (!this.canPublishSharedLayout) return;
      var isShared2 = this.activeLayout === "shared2";
      var isShared3 = this.activeLayout === "shared3";
      if (!isShared2 && !isShared3 && this.activeLayout !== "default") return;
      var blocker = this.publishBlocker();
      if (blocker) { window.alert("Not published.\n\n" + blocker); return; }
      var scope = isShared3 ? "Accounting" : isShared2 ? "Manufacturing Insights" : "Sales Insights (Layout 1)";
      if (!window.confirm("Publish this arrangement as the standard " + scope + "?\n\nEVERY user who can see this report will get it, read-only, on their next load. Your own named layouts are not affected.")) return;
      if (this._dashSaveTimer) { clearTimeout(this._dashSaveTimer); this._dashSaveTimer = null; }
      var dk = this.dockKey;
      if (!isShared2 && !isShared3 && dk) { this.syncDockPresetFromLive(); this.saveDockDefaults(); }
      else { this.saveRoleDefault(isShared3 ? "SHARED3" : isShared2 ? "SHARED2" : "ALL"); }
      var self = this;
      this.dashSaved = true;
      setTimeout(function () { self.dashSaved = false; }, 2000);
    },
    // Master: persist the current Layout-1 arrangement as the effective role's SHARED default (needs the role-default backend).
    saveRoleDefault(roleKey) {
      if (!this._layoutReady) return;   // don't overwrite the shared default with a half-loaded layout (race guard)
      if (!this.canPublishSharedLayout) return;   // already covers viewingAsUser
      var key = roleKey || "ALL";   // "ALL" = shared Layout 1 (every role); "SHARED<N>" = another shared report
      var prev = (this.roleDefaults && this.roleDefaults[key]) || {};
      var def = { cols: 24, layout: JSON.parse(JSON.stringify(this.dashLayout || [])), mapZoom: this.mapZoom, hidden: (this.dashHidden || []).slice(), titleStyles: JSON.parse(JSON.stringify(this.titleStyles || {})) };
      // Carry the docks so publishing a main layout never wipes them — but into the key that BELONGS to this
      // report. Publishing "ALL" used to stamp the flat Sales map into whatever row it wrote, which is how
      // SHARED2/SHARED3 ended up holding Sales-shaped presets. Layout 1 keeps the original flat `dockLayouts`
      // (the shape the backend's helpdesk dock-bridge reads); every other report uses `reportDocks`.
      if (key === "ALL") def.dockLayouts = JSON.parse(JSON.stringify(this.dockLayouts || {}));
      else def.reportDocks = JSON.parse(JSON.stringify((this.dockLayoutsByReport || {})[key] || {}));
      // Preserve this report's published display name across a layout publish. The backend preserve-merge
      // is the real backstop (it also covers older cached bundles), but sending it keeps roleDefaults, and
      // therefore the on-screen name, correct without waiting for a reload.
      if (typeof prev.name === "string" && prev.name) def.name = prev.name;
      // Refuse to push an arrangement that would brick everyone. Checked HERE, not only in the button, so
      // the debounced autosave cannot slip a broken layout past the guard.
      var blocker = this.publishBlocker();
      if (blocker) { if (typeof console !== "undefined") console.warn("[Sales Insights] publish blocked:", blocker); return; }
      this.sanitizeForPublish(def);
      this.$set(this.roleDefaults, key, def);
      var payload = { role: key, layout: JSON.stringify(def), companyScope: this._activeCompanyCode() };
      httpClient.post("/report/role-default-layout", payload)
        .then(function (res) { if (res && res.data && res.data.ok === false) window.alert("Not published: " + (res.data.message || "the server refused the change.")); })
        .catch(function () { /* default-layout backend not deployed yet — edit shows in-session; persistence lands with the deploy */ });
    },
    // Persist the DOCKED-scenario layouts to the shared rpt_RoleDefaultLayout "ALL" row — the SAME DB table as
    // the main Layout 1 — so they're authoritative for every user and can't be lost the way a per-user blob can.
    // While a card is docked, this.dashLayout IS the docked arrangement (NOT Layout 1), so the row's existing MAIN
    // layout is preserved from roleDefaults.ALL and only its dockLayouts are updated. Master-only; skipped if there
    // is no shared main layout to attach to (so we never clobber Layout 1 with an empty one).
    saveDockDefaults() {
      if (!this._layoutReady || !this.canPublishSharedLayout) return;
      var all = (this.roleDefaults && this.roleDefaults["ALL"]) ? this.roleDefaults["ALL"] : null;
      if (!all || !Array.isArray(all.layout) || !all.layout.length) return;   // no shared Layout 1 yet → leave docks in the blob (backend bridges from helpdesk)
      var base = JSON.parse(JSON.stringify(all));
      base.dockLayouts = JSON.parse(JSON.stringify(this.dockLayouts || {}));
      this.$set(this.roleDefaults, "ALL", base);
      httpClient.post("/report/role-default-layout", { role: "ALL", layout: JSON.stringify(base), companyScope: this._activeCompanyCode() })
        .then(function (res) { if (res && res.data && res.data.ok === false) window.alert("Not published: " + (res.data.message || "the server refused the change.")); })
        .catch(function () { /* role-default backend not deployed yet — in-session docks still apply */ });
    },
    onDashLayoutUpdated(newLayout) {
      if (this.isMobile) return;   // mobile uses the fixed stack (mobileLayout) — never persist it or clobber the desktop layout
      if (Array.isArray(newLayout)) this.dashLayout = newLayout;
      this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
      // Programmatic Orders-In-Process height-fit (autoSizeOip): don't persist or snapshot it — on the shared Default a
      // Master save would republish Layout 1 to everyone, and the fit is per-viewer + recomputed on load. Reset the flag
      // on the next tick so it covers any synchronous compaction fires but never suppresses a later real drag/resize.
      if (this._oipAutoResize) {
        var s = this;
        this.$nextTick(function () { s._oipAutoResize = false; s.refreshStickyCards(); });
        return;
      }
      this.saveDashLayout();
      var self = this;
      this.$nextTick(function () { self.refreshStickyCards(); });   // slot positions changed → recompute the pin
      this._queueHistory();   // debounced undo-history snapshot after a drag/resize settles
    },
    // ── Automatic layout version history (undo / redo) ──────────────────────────────────────────
    // Every layout change (drag/resize/hide/place/reset/title-style) records a snapshot so testing can be undone.
    // Session-scoped (resets per layout / on reload). Dedup by a geometry key; guarded so applying a snapshot
    // doesn't record itself.
    _layoutSnapKey(layout, hidden, titleStyles, mapZoom) {
      var l = (layout || []).map(function (it) { return it.i + ":" + it.x + ":" + it.y + ":" + it.w + ":" + it.h; }).sort().join("|");
      var h = (hidden || []).slice().sort().join(",");
      return l + "#" + h + "#" + JSON.stringify(titleStyles || {}) + "#" + (mapZoom || 0);
    },
    recordLayoutHistory() {
      if (this._applyingHistory || this.isMobile) return;
      var snap = {
        layout: JSON.parse(JSON.stringify(this.dashLayout || [])),
        hidden: (this.dashHidden || []).slice(),
        titleStyles: JSON.parse(JSON.stringify(this.titleStyles || {})),
        mapZoom: this.mapZoom
      };
      snap.key = this._layoutSnapKey(snap.layout, snap.hidden, snap.titleStyles, snap.mapZoom);
      var cur = this.layoutHistory[this.layoutHistoryIndex];
      if (cur && cur.key === snap.key) return;   // no real change → skip
      if (this.layoutHistoryIndex < this.layoutHistory.length - 1) {
        this.layoutHistory = this.layoutHistory.slice(0, this.layoutHistoryIndex + 1);   // drop the redo branch
      }
      this.layoutHistory.push(snap);
      if (this.layoutHistory.length > 60) this.layoutHistory.shift();   // cap memory
      this.layoutHistoryIndex = this.layoutHistory.length - 1;
    },
    _queueHistory() {
      var self = this;
      if (this._histTimer) clearTimeout(this._histTimer);
      this._histTimer = setTimeout(function () { self._histTimer = null; self.recordLayoutHistory(); }, 450);
    },
    // Reset the history and seed it with the current arrangement (called when a layout loads / is switched).
    seedLayoutHistory() {
      this.layoutHistory = [];
      this.layoutHistoryIndex = -1;
      var self = this;
      this.$nextTick(function () { self.recordLayoutHistory(); });
    },
    undoLayout() {
      if (!this.canUndo) return;
      this.layoutHistoryIndex--;
      this._applyLayoutSnapshot(this.layoutHistory[this.layoutHistoryIndex]);
    },
    redoLayout() {
      if (!this.canRedo) return;
      this.layoutHistoryIndex++;
      this._applyLayoutSnapshot(this.layoutHistory[this.layoutHistoryIndex]);
    },
    _applyLayoutSnapshot(snap) {
      if (!snap) return;
      this._applyingHistory = true;   // suppress recording while we apply (the geometry-key dedup catches echoes)
      this.dashLayout = JSON.parse(JSON.stringify(snap.layout || []));
      this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
      this.dashHidden = Array.isArray(snap.hidden) ? snap.hidden.slice() : [];
      this.titleStyles = JSON.parse(JSON.stringify(snap.titleStyles || {}));
      if (typeof snap.mapZoom === "number") this.mapZoom = snap.mapZoom;
      this.normalizeDockState();   // the snapshot may be from the OTHER dock state — never resurrect a docked card
      this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
      this.saveDashLayoutNow();   // persist the restored arrangement (recordLayoutHistory no-ops via _applyingHistory)
      var self = this;
      this.$nextTick(function () { self._applyingHistory = false; self.refreshStickyCards(); });
    },
    // Keyboard: Ctrl/Cmd+Z = undo, Ctrl/Cmd+Y or Ctrl/Cmd+Shift+Z = redo — but only when NOT typing in a field
    // (so it never clobbers text editing) and only on this report.
    _onLayoutKeydown(e) {
      if (!e || !(e.ctrlKey || e.metaKey) || this.isMobile || this.isLayoutLocked) return;
      var t = e.target || {}; var tag = (t.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.isContentEditable) return;
      var k = (e.key || "").toLowerCase();
      if (k === "z" && !e.shiftKey) { e.preventDefault(); this.undoLayout(); }
      else if ((k === "z" && e.shiftKey) || k === "y") { e.preventDefault(); this.redoLayout(); }
    },
    // Track the mobile breakpoint (< 992px) so the grid swaps between the desktop layout and the fixed mobile stack.
    _onResizeMobile() {
      var m = typeof window !== "undefined" && window.innerWidth < 992;
      if (m !== this.isMobile) this.isMobile = m;
      this.measureStickyTop();
      this.refreshStickyCards();   // the pinned Filters/Favorites left+width follow a window resize
    },
    // Measure the fixed site header's bottom edge so the sticky filter/favorites panels pin JUST below it
    // (with the red top border visible). The header height varies by role — Master shows an extra Role/User
    // preview row — so a hardcoded offset can't clear it reliably.
    measureStickyTop() {
      try {
        var el = document.querySelector(".stickyHeader") || document.getElementById("main-nav");
        if (el) { var b = el.getBoundingClientRect().bottom; if (b > 40 && b < 400) this.stickyTop = Math.round(b + 8); }
      } catch (e) { /* keep the default */ }
    },
    // ── Filters + Favorites "follow the scroll" ─────────────────────────────────────────────
    // They're real grid cards (draggable/resizable), so we can't use position:sticky (grid items are
    // absolutely positioned) NOR position:fixed (each grid-item carries a CSS transform, which would trap a
    // fixed child inside the item's own box). Instead, on every scroll frame we translateY the inner card down
    // by exactly how far its slot has scrolled above the header — the card visually stays pinned just below the
    // fixed header while its grid slot keeps reserving space so nothing below reflows.
    refreshStickyCards() {
      if (typeof document === "undefined") return;
      var ids = ["filters", "favorites"];
      var disable = this.isMobile || this._stickyBusy;
      var pinTop = 12;
      try {
        var h = document.querySelector(".stickyHeader") || document.getElementById("main-nav");
        if (h) { var hb = h.getBoundingClientRect().bottom; if (hb > 20 && hb < 600) pinTop = Math.round(hb + 6); }
      } catch (e) { /* use default */ }
      // HERO PIN (user 2026-07-31: "the hero chat should always be visible"). Same translateY pinning as
      // the cards below — position:sticky is DEAD everywhere on this site (#mainBody is a static
      // overflow-y:auto box while the WINDOW scrolls; the Search page documented the same discovery in
      // steele-consistency-fix.css), which is exactly why this handler exists at all. transform, not
      // position:fixed, so width/left/flow never need managing. The transform feeds back into
      // getBoundingClientRect, so the NATURAL top is rect.top minus the translation already applied
      // (tracked in _heroTy — parsing it back out of the style string would be worse).
      var hero = document.querySelector(".ci-wrap .ci-ask-hero");
      var heroBand = 0;
      if (hero && hero.style && !this.isMobile) {
        var hr = hero.getBoundingClientRect();
        var cur = this._heroTy || 0;
        var hty = Math.round(pinTop - (hr.top - cur));
        if (hty > 0) {
          this._heroTy = hty;
          hero.style.transform = "translateY(" + hty + "px)";
          if (hero.classList) hero.classList.add("is-pinned");
          heroBand = hero.offsetHeight + 8;
        } else if (cur) {
          this._heroTy = 0;
          hero.style.transform = "";
          if (hero.classList) hero.classList.remove("is-pinned");
        }
      } else {
        this._heroTy = 0;
        if (hero && hero.style && hero.style.transform) { hero.style.transform = ""; if (hero.classList) hero.classList.remove("is-pinned"); }
      }
      // FULL VIEW rides under the chat band (user 2026-07-31: a maximized card must fall just BELOW
      // the pinned chat, never behind it). Same translateY pinning, fed through CSS variables (see the
      // .gi-pcfull rule), and its height = the space under the pinned band — so the card plus footer are
      // all the page has left to scroll and the card top can never slide under the chat.
      var full = document.querySelector(".ci-pcfull-mode .vue-grid-item.gi-pcfull");
      if (full && full.style) {
        var heroH = (hero && !this.isMobile && hero.offsetHeight) ? hero.offsetHeight + 8 : 0;
        var fullTop = pinTop + heroH;
        var fr = full.getBoundingClientRect();
        var curF = this._fullTy || 0;
        var fty = Math.round(fullTop - (fr.top - curF));
        if (fty > 0) {
          this._fullTy = fty;
          full.style.setProperty("--jcs-full-ty", fty + "px");
        } else if (curF) {
          this._fullTy = 0;
          full.style.setProperty("--jcs-full-ty", "0px");
        }
        full.style.setProperty("--jcs-full-h", Math.max(420, Math.round((window.innerHeight || 800) - fullTop - 12)) + "px");
      } else if (this._fullTy) {
        this._fullTy = 0;
      }
      // Pinned cards tuck UNDER the pinned chat band, never beneath it.
      var cardPinTop = pinTop + heroBand;
      for (var k = 0; k < ids.length; k++) {
        var card = document.querySelector('.ci-wrap [data-card-id="' + ids[k] + '"]');
        if (!card || !card.style) continue;
        var slot = card.closest ? card.closest(".vue-grid-item") : null;
        if (disable || !slot) { this._unpinStickyCard(card); continue; }
        var r = slot.getBoundingClientRect();
        if (r.width < 10) { this._unpinStickyCard(card); continue; }
        var ty = Math.round(cardPinTop - r.top);
        if (ty > 0) {
          card.style.transform = "translateY(" + ty + "px)";
          if (card.classList) card.classList.add("is-pinned");
        } else {
          this._unpinStickyCard(card);
        }
      }
    },
    _unpinStickyCard(card) {
      if (!card || !card.style) return;
      if (card.style.transform) card.style.transform = "";
      if (card.classList) card.classList.remove("is-pinned");
    },
    // Exit of Full view: clear the pin variables everywhere. Left behind, a stale --jcs-full-ty would
    // shift the NEXT full-view measurement (the transform feeds getBoundingClientRect).
    _clearFullViewPin() {
      this._fullTy = 0;
      if (typeof document === "undefined") return;
      var els = document.querySelectorAll(".ci-wrap .vue-grid-item");
      for (var i = 0; i < els.length; i++) {
        if (els[i].style) { els[i].style.removeProperty("--jcs-full-ty"); els[i].style.removeProperty("--jcs-full-h"); }
      }
    },
    _onStickyScroll() {
      if (this._stickyRaf) return;
      var self = this;
      if (typeof requestAnimationFrame !== "undefined") {
        this._stickyRaf = requestAnimationFrame(function () { self._stickyRaf = null; self.refreshStickyCards(); });
      } else {
        this._stickyRaf = setTimeout(function () { self._stickyRaf = null; self.refreshStickyCards(); }, 16);
      }
    },
    // While a card is being dragged/resized, unpin so vue-grid-layout drives it natively; re-pin on drop.
    suppressSticky() {
      if (this._stickyBusy) return;
      this._stickyBusy = true;
      this.refreshStickyCards();
    },
    resumeSticky() {
      var self = this;
      this._stickyBusy = false;
      this.$nextTick(function () { self.refreshStickyCards(); });
    },
    // Collapse/expand the left filter rail; nudge vue-grid-layout to re-measure the (now wider/narrower) grid column.
    toggleFilterRail() {
      this.filterRailOpen = !this.filterRailOpen;
      this.$nextTick(function () { try { window.dispatchEvent(new Event("resize")); } catch (e) { /* noop */ } });
    },
    // Collapse/expand the left favorites rail (mirrors toggleFilterRail); nudge the grid to re-measure.
    toggleFavRail() {
      this.favRailOpen = !this.favRailOpen;
      this.$nextTick(function () { try { window.dispatchEvent(new Event("resize")); } catch (e) { /* noop */ } });
    },
    // KPI card icon shown in the TITLE bar (moved out of the body so the number/foot text isn't squeezed).
    kpiIcon(gid) {
      return { kpiSales: "📈", kpiOrders: "🛒", kpiAvg: "💲", savedCarts: "🛒", ordersInProcess: "⏳", partCosts: "🧮", partLoss: "🧾" }[gid] || "";
    },
    // Favorites card orientation: taller-than-wide => vertical (stack, top-centered), else horizontal (row, left-middle).
    // Grid units aren't square (row unit ~37px, col unit ~55px on desktop), so weight h vs w ~1.5:1.
    favIsVertical(it) {
      if (!it) return true;
      var w = Number(it.w) || 1;
      var h = Number(it.h) || 1;
      return (h * 2) > (w * 3);
    },
    // Data-heavy cards (long lists you can scroll INSIDE) would trap the page scroll when you pass over them.
    // DWELL gate: while the cursor has been over the card LESS than 0.75s, the wheel scrolls the PAGE (so you scroll
    // right past). Once the cursor has RESTED on the card 0.75s+, the wheel scrolls the card. Leaving the card resets
    // the timer, so re-entering starts a fresh 0.75s window.
    isHeavyScrollCard(gid) {
      return gid === "salesReps" || gid === "customers" || gid === "parts" || gid === "codes" || gid === "details";
    },
    onHeavyEnter(gid) {
      if (!this.isHeavyScrollCard(gid)) return;
      if (!this._heavyEnter) this._heavyEnter = {};
      this._heavyEnter[gid] = Date.now();   // start the dwell clock
    },
    onHeavyLeave(gid) {
      if (!this.isHeavyScrollCard(gid)) return;
      if (this._heavyEnter) this._heavyEnter[gid] = 0;   // reset → next approach re-arms the 0.75s delay
    },
    onHeavyWheel(e, gid) {
      if (!e || !this.isHeavyScrollCard(gid)) return;
      if (!this._heavyEnter) this._heavyEnter = {};
      var enter = this._heavyEnter[gid];
      if (!enter) { this._heavyEnter[gid] = Date.now(); enter = this._heavyEnter[gid]; }   // fallback if mouseenter was missed (scroll-under-cursor)
      if (Date.now() - enter < 750) {
        // Still inside the 0.75s dwell → redirect the wheel to the PAGE so the card doesn't trap the scroll.
        var dy = e.deltaY || 0;
        if (e.deltaMode === 1) dy *= 16; else if (e.deltaMode === 2) dy *= (window.innerHeight || 800);
        if (dy) { e.preventDefault(); if (window.scrollBy) window.scrollBy(0, dy); }
      }
      // else: rested 0.75s+ on this card → let it scroll its own list natively.
    },

    // "Reset this layout" — restore the active CUSTOM layout to the default arrangement. No-op on the locked Default.
    resetDashLayout() {
      var fromDefault = this.activeLayout === "default";
      if (fromDefault && !this.canPublishSharedLayout) return;   // only the publisher may reset the standard layout
      this.dashLayout = JSON.parse(JSON.stringify(DASH_DEFAULT));
      this.pruneUnauthorizedCards();   // role prune: this replaced dashLayout wholesale
      this.mapZoom = 0.64;
      this.dashHidden = [];
      this.titleStyles = {};
      this.ensureBuiltinCards();
      this.loadCustomCards();
      this.saveDashLayoutNow();
    },
    // Map zoom changed (from the map's +/- controls) -> store + persist with the layout.
    onMapZoom(z) {
      this.mapZoom = z;
      this.saveDashLayout();
    },
    onWorldZoom(z) { this.worldZoom = z; },
    // Clicking the US on the WORLD map drills into the US states map with a clean slate (all filters cleared),
    // so the user lands on the full domestic view — matches the "Clear all" reset.
    showUsFromWorld() {
      this.resetFilters();
      this.mapMode = "us";
      this.mapHoverState = null;
    },
    // US state flag (flagcdn serves ISO-3166-2 subdivisions, e.g. us-tx) for the panel below the US map.
    stateFlagUrl(postal) { return "https://flagcdn.com/w160/us-" + String(postal || "").toLowerCase() + ".png"; },
    // Country name -> ISO alpha-2 for the world-map hover flag (empty -> no flag image shown).
    countryFlagIso(name) { return COUNTRY_ISO[String(name || "").trim().toUpperCase()] || ""; },
    // World map: does this country have accounts in the (filtered) data? Used to suppress the flag for
    // filtered-out or zero-customer countries — no more "No accounts here" flag.
    countryHasAccounts(name) {
      var n = String(name || "").trim().toUpperCase();
      if (!n) return false;
      return (this.byCountry || []).some(function (c) { return c && String(c.country || "").trim().toUpperCase() === n && (Number(c.customers) || 0) > 0; });
    },
    // Auto-pick the map card's view (US states vs World) from where the FILTERED data actually is, so a
    // customer/territory selection lands on the right map. Explicit signals win; otherwise, when a narrowing
    // filter leaves PURELY international country data (e.g. clicking the Brazil account XS160), switch to the
    // world map. Purely-domestic -> US. Mixed or unfiltered -> leave as-is (don't fight a manual US/World toggle).
    autoMapMode() {
      var isIntl = function (c) { return c && !/^(UNITED STATES|USA|US)$/i.test(String(c)); };
      if ((this.filters.country || []).length) { this.mapMode = "world"; return; }
      if ((this.filters.territory || []).some(function (t) { return /international/i.test(t); })) { this.mapMode = "world"; return; }
      if (!this.hasNarrowingFilter) return;
      var countries = (this.byCountry || []).map(function (c) { return c && c.country; }).filter(Boolean);
      if (!countries.length) return;
      var anyIntl = countries.some(isIntl);
      var anyUS = countries.some(function (c) { return !isIntl(c); });
      if (anyIntl && !anyUS) this.mapMode = "world";
      else if (anyUS && !anyIntl) this.mapMode = "us";
    },
    // Explicit "Save layout" -> persist immediately (skip the debounce) + show "Saved".
    saveLayoutNow() {
      var self = this;
      if (this.viewingAsUser) return;   // read-only while previewing another user
      if (this._dashSaveTimer) { clearTimeout(this._dashSaveTimer); this._dashSaveTimer = null; }
      // Both shared layouts go through publishSharedLayout: it is dock-aware and it confirms. The old path
      // called saveRoleDefault() bare, which (a) published the DOCKED arrangement as Layout 1, silently
      // dropping Filters and Email Insights for everyone, and (b) had no shared2 branch at all, so 💾 could
      // never publish Manufacturing Insights.
      if (this.activeLayout === "default" || this.activeLayout === "shared2" || this.activeLayout === "shared3") { this.publishSharedLayout(); return; }
      this.syncActiveIntoCustom();
      httpClient.post("/user/report-dashboard", { Dashboard: JSON.stringify({ active: this.activeLayout, custom: this.customLayouts, favorites: this.favorites, favoriteGroups: this.favoriteGroups, qaHistory: this.qaHistory, emailLinked: this.emailLinked, emailHiddenLayout: this.emailHiddenLayout, emailVisibleLayout: this.emailVisibleLayout, dockLayouts: this.dockLayouts, dockLayoutsByReport: this.dockLayoutsByReport, emailSnapLayout: this.emailSnapLayout, emailDocked: this.emailDocked, layoutVersions: this.layoutVersions }) })
        .then(function () { self.dashSaved = true; setTimeout(function () { self.dashSaved = false; }, 2000); })
        .catch(function () { /* non-fatal */ });
    },
    // ----- draggable story panel (pointer events: mouse + touch) -----
    startDrag(e) {
      if (e.target && e.target.closest && e.target.closest("button")) return;
      const el = this.$refs.storyPanel;
      if (!el) return;
      const r = el.getBoundingClientRect();
      this._dragDX = e.clientX - r.left;
      this._dragDY = e.clientY - r.top;
      this.storyDragging = true;
      window.addEventListener("pointermove", this.onDrag);
      window.addEventListener("pointerup", this.endDrag);
      if (e.preventDefault) e.preventDefault();
    },
    onDrag(e) {
      const el = this.$refs.storyPanel;
      const bw = el ? el.offsetWidth : 320;
      const bh = el ? el.offsetHeight : 120;
      let left = e.clientX - (this._dragDX || 0);
      let top = e.clientY - (this._dragDY || 0);
      left = Math.max(4, Math.min(left, window.innerWidth - bw - 4));
      top = Math.max(4, Math.min(top, window.innerHeight - bh - 4));
      this.storyPos = { left: left, top: top };
    },
    endDrag() {
      this.storyDragging = false;
      window.removeEventListener("pointermove", this.onDrag);
      window.removeEventListener("pointerup", this.endDrag);
    },
    // ----- mobile swipe to open/close the Filters (left) + Insights (right) drawers -----
    onSwipeStart(e) {
      if (!e.touches || e.touches.length !== 1) { this._swipe = null; return; }
      var t = e.touches[0];
      this._swipe = { x: t.clientX, y: t.clientY };
    },
    onSwipeEnd(e) {
      var s = this._swipe; this._swipe = null;
      if (!s || typeof window === "undefined" || window.innerWidth >= 992) return;   // phones/tablets only
      // App.vue's right-edge rail tray shares this gesture space. If it just consumed a swipe,
      // stand down — otherwise one right-edge swipe both slides the rail out AND opens the story
      // panel. 400ms comfortably clears the rail's 280ms slide.
      if (window.__jcsTrayGestureAt && Date.now() - window.__jcsTrayGestureAt < 400) return;
      // Same handshake for the favorites long-press drag. The favorites strip runs to within ~16px of the
      // right edge, so a star that sits inside this edge zone would otherwise ALSO open the Insights panel
      // the moment the finger lifts off a drag.
      if (window.__jcsFavDragAt && Date.now() - window.__jcsFavDragAt < 400) return;
      var t = (e.changedTouches && e.changedTouches[0]) || null;
      if (!t) return;
      var dx = t.clientX - s.x, dy = t.clientY - s.y;
      if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;   // require a clear horizontal swipe
      var W = window.innerWidth;
      if (dx > 0) {                                                          // swipe RIGHT →
        if (s.x < 36 && !this.filtersDrawerOpen) this.filtersDrawerOpen = true;   // open Filters from the left edge
        else if (!this.storyHidden) this.storyHidden = true;                       // else close Insights
      } else {                                                              // swipe LEFT ←
        // The right edge belongs to App.vue's toggle-rail tray (.jcs-rail-peek). A LEFT swipe that
        // starts there must reveal the toggles — INSIGHTS pill included — and do NOTHING else, so
        // bail out before any drawer logic runs. This is unconditional rather than leaning on the
        // __jcsTrayGestureAt stamp: both are window "touchend" listeners, and CustomerInsights (a
        // child of App via <router-view>, statically imported) registers FIRST on a hard load of
        // /reports — so this handler runs BEFORE App.vue stamps the gesture and the guard silently
        // misses. This number must EQUAL App.vue's claim zone, not exceed it: at 56 vs App's 36
        // the band (W-56, W-36] became dead — this handler returned before its close-Filters
        // fallback and App.vue never entered its branch, so a finger landing 40-50px in did
        // nothing at all (it used to close Filters). 36 partitions the edge exactly: no overlap,
        // no gap. Keep the two in sync if either moves.
        if (s.x > W - 36) return;
        // Insights is TAP-only now: the 📊 INSIGHTS pill portalled into the rail the tray reveals
        // (and the report's own rail pill) call openStory(). Swiping never opens it.
        if (this.filtersDrawerOpen) this.filtersDrawerOpen = false;                // else close Filters
      }
    },
    // The nested Customer Order Details panel — its own (heavier) endpoint, refreshed
    // only on filter changes (not on flat-table paging/sort).
    loadDetails() {
      // Customer Order Details is a HEAVY full-table LOB scan. Auto-load it only when the view is NARROWED
      // (customer/part/rep/etc. → a small result); for a broad/date-only view, DEFER it behind a "Load order
      // details" button so it doesn't scan the 8GB clustered on every filter change. The lighter cards below
      // (story, saved carts, orders-in-process, AI cards) still load eagerly — they returned fast.
      if (this.hasNarrowingFilter) {
        this.fetchDetails();
      } else {
        this.detailsDeferred = true; this.detailsBusy = false;
        this.details = { customers: [], message: "" };
      }
      this.loadStory();
      this.loadSavedCarts(); this.loadOrdersInProcess();
      this.refreshAiCards();
      // Filters changed while an AI narrative is showing — mark it stale (MANUAL refresh only).
      if (this.aiInsights) this.aiInsightsStale = true;
    },
    // The actual details fetch — called automatically when narrowed, or from the "Load order details" button.
    fetchDetails() {
      this.detailsDeferred = false; this.detailsBusy = true;
      httpClient.get("/report/customer-insights/details", { params: this.buildParams(false) })
        .then((res) => {
          const d = res.data || {};
          this.details = { customers: d.customers || [], message: d.message || "" };
        })
        .catch(() => { this.details = { customers: [], message: "" }; })
        .finally(() => { this.detailsBusy = false; });
    },
    // Activity trail: a compact, readable summary of the filters CURRENTLY applied.
    // Report filters never reach the URL (this file has a single $router.push in ~14.6k
    // lines), so a page-view row records only "/reports/customer-insights" and a click row
    // records only a chip label. Without this the trail shows that someone opened a report
    // but never WHAT THEY WERE LOOKING AT, which is the actual question support asks.
    activityFilterSummary() {
      try {
        var f = this.filters || {};
        var parts = [];
        Object.keys(f).forEach(function (k) {
          var v = f[k];
          if (v === null || v === undefined || v === "") return;
          if (Array.isArray(v)) { if (v.length) parts.push(k + "=" + v.join("|")); return; }
          parts.push(k + "=" + String(v));
        });
        return parts.join("; ");
      } catch (e) { return ""; }
    },
    applyFilters() {
      // Never let the range run into the future (no data there) — clamp from/to to today.
      if (this.filters.to && this.filters.to > this.maxDateStr) this.filters.to = this.maxDateStr;
      if (this.filters.from && this.filters.from > this.maxDateStr) this.filters.from = this.maxDateStr;
      recordAction("report-filter-apply", this.activityFilterSummary());
      this.page = 1; this.load(); this.loadDetails();
    },
    // "Attribute by" rep-basis / view changed → re-scope the whole report. Reloads the main grid + detail rows
    // (both re-base server-side via buildParams' repBasis/repView) and, if the Machine & Part Codes card is
    // currently loaded (not deferred), its rep-colored segments too. Saved Carts / Orders In Process are keyed on
    // the portal's own cart scoping (not rpt_CustomerOrders rep attribution), so they don't re-base — left as-is.
    applyRepBasis() {
      this.page = 1;
      this.load();
      this.loadDetails();
      if (!this.codesDeferred) this.loadCodes();
    },
    setRepView(v) {
      if (this.repView === v) return;
      this.repView = v;
      this.applyRepBasis();
    },
    // Clicking a cart in the Saved Carts popup browses into that customer's company via the
    // portal's normal (reloading) company switch, with the cart widget pre-pointed at THIS
    // cart — so after the reload the standard cart widget shows it in its usual spot. The
    // switch saves/restores per-company cart pointers, so returning to NC999 stays clean.
    pickCartForDock(c) {
      if (!this.canActForCustomer || !c) return;
      var self = this;
      var code = String(c.customerId || "");
      var isQuote = (this.scDetail.type === "quote");
      this.scDetail.open = false;
      httpClient.get("/report/company-by-code", { params: { code: code || "NC999" } })
        .then(function (res) {
          var co = res.data;
          if (!co || !co.id) return;
          try {
            // Pre-seed the per-company cart pointer the (reloading) switch will restore.
            var st = { cartId: "", cartIdRFQ: "", poNumber: "", poNumberRFQ: "", machineId: "", machineIdRFQ: "", assemblyId: "", assemblyIdRFQ: "" };
            if (isQuote) st.cartIdRFQ = String(c.cartId); else st.cartId = String(c.cartId);
            localStorage.setItem("cart_company_" + co.id, JSON.stringify(st));
          } catch (e) { /* ignore storage errors */ }
          // reload:true → Head does a full (non-silent) switch: saves the rep's current
          // company cart, restores the pre-seeded pointer above, and reloads the page.
          self.$root.$emit("report-company-switch", { value: co.id, text: co.name || code, reload: true });
        })
        .catch(function () { /* non-fatal */ });
    },
    // Clean-slate the filter VALUES only (no reload). "Clear all" restores the DEFAULT status trio
    // (CLOSED/HOLD/RELEASED), NOT "all statuses" — empty would pull CANCELLED/VOID + blank rows into every
    // number. Matches the initial `filters` default in data() — keep the two in sync.
    _resetFilterValues() {
      const now = new Date();
      this.filters = {
        from: now.getFullYear() + "-01-01", to: now.toISOString().slice(0, 10),
        dateBasis: "order", invoiced: "all", salesRep: [], territory: [], status: ["CLOSED", "HOLD", "RELEASED"],
        machineCodeCategory: [], machineCodePrefix: [], machineCodeDescription: [], partDescription: [], customer: [], orderNo: "", part: [], state: [], country: [],
      };
      this.activePreset = "ytd";   // reset restores the default YTD range → keep YTD highlighted
    },
    resetFilters() {
      this._resetFilterValues();
      this.page = 1; this.load(); this.loadDetails();
      // Sales rep: clearing the filters returns the company selection to NC999 home.
      if (this.isSalesLocked) this.syncCompany("");
    },

    // Flatten a filters object into removable chips ({key,val,label,text}). Shared by activeChips and the
    // favorite hover-popover so the two always list the same filters. (Dates are handled separately.)
    chipsFromFilters(f) {
      f = f || {};
      var out = [], self = this;
      (f.customer || []).forEach(function (v) { out.push({ key: "customer", val: v, label: "Customer", text: v }); });
      (f.part || []).forEach(function (v) { out.push({ key: "part", val: v, label: "Part", text: v }); });
      (f.territory || []).forEach(function (v) { out.push({ key: "territory", val: v, label: "Territory", text: v }); });
      (f.state || []).forEach(function (v) { out.push({ key: "state", val: v, label: "State", text: self.stateName(v) }); });
      (f.country || []).forEach(function (v) { out.push({ key: "country", val: v, label: "Country", text: v }); });
      (f.salesRep || []).forEach(function (v) { out.push({ key: "salesRep", val: v, label: "Rep", text: v }); });
      (f.status || []).forEach(function (v) { out.push({ key: "status", val: v, label: "Status", text: v }); });
      if (f.invoiced && f.invoiced !== "all") out.push({ key: "invoiced", label: "Invoiced", text: f.invoiced === "invoiced" ? "Invoiced" : "Not invoiced" });
      (f.machineCodeCategory || []).forEach(function (v) { out.push({ key: "machineCodeCategory", val: v, label: "Machine cat.", text: v }); });
      (f.machineCodePrefix || []).forEach(function (v) { out.push({ key: "machineCodePrefix", val: v, label: "Machine pfx", text: v }); });
      (f.machineCodeDescription || []).forEach(function (v) { out.push({ key: "machineCodeDescription", val: v, label: "Machine desc.", text: v }); });
      (f.partDescription || []).forEach(function (v) { out.push({ key: "partDescription", val: v, label: "Part desc.", text: v }); });
      if (f.orderNo) out.push({ key: "orderNo", label: "Order #", text: f.orderNo });
      return out;
    },

    // ----- Filter favorites (personal, per-rep) ---------------------------------------------------------
    // Snapshot only the filter CHIPS (not the From/To range — the rep changes dates freely on a favorite).
    snapshotFilters() {
      var f = this.filters, snap = {};
      ["dateBasis", "invoiced", "salesRep", "territory", "status", "machineCodeCategory", "machineCodePrefix",
       "machineCodeDescription", "partDescription", "customer", "orderNo", "part", "state", "country"].forEach(function (k) {
        snap[k] = Array.isArray(f[k]) ? f[k].slice() : f[k];
      });
      return snap;
    },
    suggestFavoriteName() {
      // Name = ALL selected filters, grouped by dimension — e.g. "Midwest · Kevin Cornett · CLOSED, HOLD, RELEASED"
      // (was the first 3 chips only, which showed a 3-status pick as just "CLOSED"). Dates are excluded on
      // purpose: favorites don't snapshot the date range (reps change dates freely on a favorite).
      var groups = [], byLabel = {};
      (this.activeChips || []).forEach(function (c) {
        if (c.key === "__dates") return;   // activeChips carries a dates chip — excluded here per the rule
        var k = c.label || "";             // above (suggestFavoriteNameWithDates appends the window itself;
        if (!byLabel[k]) { byLabel[k] = []; groups.push(byLabel[k]); }   // including it here printed it TWICE)
        if (c.text && byLabel[k].indexOf(c.text) < 0) byLabel[k].push(c.text);
      });
      var name = groups.map(function (vals) { return vals.join(", "); }).join(" · ") || "Favorite";
      return name.length > 120 ? name.slice(0, 117) + "…" : name;
    },
    // Like suggestFavoriteName but ALWAYS appends the date window — used for chat/question favorites, where
    // the timeframe is part of the answer (an un-dated ask defaults to all-time). "RUSSIA · Jan 1, 1990 – …".
    suggestFavoriteNameWithDates() {
      var base = this.suggestFavoriteName();
      if (base === "Favorite") base = "";   // no dimension chips → lead with the dates
      var dates = this.fmtShort(this.filters.from) + " – " + this.fmtShort(this.filters.to);
      var full = (base ? base + " · " : "") + dates;
      return full.length > 120 ? full.slice(0, 117) + "…" : full;
    },
    saveCurrentAsFavorite() {
      if (!this.activeChips.length) return;
      var name = window.prompt("Name this favorite:", this.suggestFavoriteName());
      if (name === null) return;
      name = (name || "").trim() || this.suggestFavoriteName();
      this.favorites.push({ id: "F" + Date.now(), name: name, filters: this.snapshotFilters(), groupId: "" });
      this.saveFavorites();
    },
    applyFavorite(fav) {
      if (!fav || this.aiAskBusy) return;   // don't reshape the view (or clobber the pre-ask snapshot) mid-ask
      this.favPopId = ""; this.favPopKind = ""; this.favDraft = null; this.favRenaming = false;
      // A RESULT-CARD favorite (saved from the hero ☆): restore the question + its date window and RE-RUN
      // the ask, so the card rebuilds exactly (the ask also auto-applies the filters). The stored dates are
      // a belt-and-suspenders in case the question text alone doesn't re-parse the range.
      if (fav.ask && fav.ask.question) {
        if (this.chatDocked) this.restoreChat();
        this.aiQuestion = fav.ask.question;
        var self = this;
        var hasWin = !!(fav.ask.from || fav.ask.to);
        if (hasWin) {
          // Re-run pinned to the favorite's SAVED window (lockDates): askQuestion's clean-slate reset
          // would otherwise wipe these restored dates and the un-dated default would run it all-time.
          // A lockDates ask skips the internal snapshot+reset, so stage both here: snapshot the current
          // view (for ↩ Undo / gate dismissal), then a clean baseline + the saved window.
          this._aiPrevFilters = JSON.parse(JSON.stringify(this.filters));
          this._aiPrevPreset = this.activePreset;
          this._resetFilterValues();
          if (fav.ask.from) this.$set(this.filters, "from", fav.ask.from);
          if (fav.ask.to) this.$set(this.filters, "to", fav.ask.to);
          this.activePreset = "";
          this.$nextTick(function () { self.askQuestion({ lockDates: true }); });
        } else {
          // No stored window (an older favorite) — a plain ask; it snapshots + resets itself.
          this.$nextTick(function () { self.askQuestion(); });
        }
        return;
      }
      if (!fav.filters) return;
      var f = this.filters;
      Object.keys(fav.filters).forEach(function (k) { var v = fav.filters[k]; f[k] = Array.isArray(v) ? v.slice() : v; });
      this.page = 1; this.load(); this.loadDetails();
    },
    deleteFavorite(fav) {
      if (!fav) return;
      if (!window.confirm('Delete the favorite "' + (fav.name || "") + '"?')) return;
      this.favorites = this.favorites.filter(function (x) { return x.id !== fav.id; });
      this.pruneFavoriteGroups();
      if (this.favPopId === fav.id) { this.favPopId = ""; this.favPopKind = ""; }
      this.favDraft = null; this.favRenaming = false;
      this.saveFavorites();
    },
    groupFavorites(gid) { return (this.favorites || []).filter(function (f) { return f.groupId === gid; }); },
    // ---- hover popover: at-a-glance details + inline edit (rename / remove filters) ----
    openFavPop(id, kind, ev) {
      this.clearFavPopTimer();
      // Mid touch-drag the popover would cover the drop targets, and the compat mouseenter that fires
      // just AFTER a drag would pop it open over the bar you just dropped into.
      if (this.favTouchDrag || (this._favTouchDragAt && Date.now() - this._favTouchDragAt < 700)) return;
      if (this.favDraft) return;   // mid-edit on another popover — don't switch away (would lose the edit)
      this.favPopId = id; this.favPopKind = kind || "fav";
      // Position the body-appended (fixed) popover to the RIGHT of the hovered star (favorites are a vertical
      // column, so opening beside it keeps the OTHER favorite icons visible); flip to the left if it would
      // overflow the right edge. Aligned near the star's top, clamped to the viewport.
      try {
        var r = (ev && ev.currentTarget && ev.currentTarget.getBoundingClientRect) ? ev.currentTarget.getBoundingClientRect() : null;
        if (r) {
          var vw = window.innerWidth || 1200, vh = window.innerHeight || 800, popW = 300, gap = 8;
          var left = r.right + gap;
          if (left + popW > vw - 8) left = Math.max(8, r.left - gap - popW);
          var top = Math.max(8, Math.min(r.top, vh - 130));
          this.favPopPos = { top: Math.round(top), left: Math.round(left) };
        }
      } catch (e) { /* noop */ }
    },
    // Close on a short delay so moving the mouse from the star to the popover (crossing the gap) doesn't dismiss it;
    // re-entering the star OR the popover clears the timer. Pinned (never auto-closes) while there are unsaved edits.
    closeFavPop() {
      if (this.favDraft) return;
      var self = this;
      this.clearFavPopTimer();
      this._favPopTimer = setTimeout(function () { self.favPopId = ""; self.favPopKind = ""; self.favRenaming = false; }, 200);
    },
    clearFavPopTimer() { if (this._favPopTimer) { clearTimeout(this._favPopTimer); this._favPopTimer = null; } },
    ensureFavDraft() {
      if (this.favDraft || !this.currentPopItem) return;
      var it = this.currentPopItem;
      this.favDraft = this.favPopKind === "group"
        ? { name: it.name }
        : { name: it.name, filters: JSON.parse(JSON.stringify(it.filters || {})) };
    },
    startFavRename() {
      this.ensureFavDraft();
      this.favRenaming = true;
      var self = this;
      this.$nextTick(function () { try { var el = self.$refs.favRenameInput; el = (el && el.focus) ? el : (el && el[0]); if (el && el.focus) { el.focus(); el.select && el.select(); } } catch (e) { /* noop */ } });
    },
    removeFavFilter(chip) {
      this.ensureFavDraft();
      if (!this.favDraft || !this.favDraft.filters) return;
      var f = this.favDraft.filters;
      if (chip.key === "invoiced") f.invoiced = "all";
      else if (chip.key === "orderNo") f.orderNo = "";
      else if (Array.isArray(f[chip.key])) f[chip.key] = f[chip.key].filter(function (v) { return v !== chip.val; });
    },
    saveFavEdit() {
      var it = this.currentPopItem;
      if (this.favDraft && it) {
        it.name = (this.favDraft.name || "").trim() || it.name;
        if (this.favPopKind !== "group" && this.favDraft.filters) it.filters = this.favDraft.filters;
        this.saveFavorites();
      }
      this.favDraft = null; this.favRenaming = false;   // keep the popover open, now showing the saved result
    },
    cancelFavEdit() { this.favDraft = null; this.favRenaming = false; this.favPopId = ""; this.favPopKind = ""; },
    // Drag a favorite star onto another star (or onto a group) to group them.
    favDragStart(fav, e) {
      // iPad Safari can kick off a NATIVE HTML5 drag from the very same long press our touch path uses.
      // Refuse it — one finger, one drag. A real dragstart always carries dataTransfer; the synthetic
      // { currentTarget } object _favPromote passes never does, so this can't reject our own call.
      if (e && e.dataTransfer && (this.favTouchDrag || this.favPressId)) { try { e.preventDefault(); } catch (x) { /* */ } return; }
      this.favDragId = fav ? fav.id : "";
      this.favDropIdx = -1; this.favDropMode = ""; this.favDropGroupId = "";
      // Cache the rail's orientation once — it can't change mid-drag, and re-reading computed style
      // on every dragover would be wasteful. Column = vertical card, row = horizontal card.
      try {
        var bar = e && e.currentTarget && e.currentTarget.closest ? e.currentTarget.closest(".ci-fav-bar") : null;
        this._favVert = !!(bar && window.getComputedStyle(bar).flexDirection === "column");
      } catch (err) { this._favVert = false; }
      // setData/effectAllowed aren't needed by Chrome (every target does @dragover.prevent) but
      // Firefox refuses to start a drag without payload, and this gives a proper "move" cursor.
      try { if (e && e.dataTransfer) { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", fav ? fav.id : ""); } } catch (err) { /* noop */ }
    },
    favDragEnd() { this.favDragId = ""; this.favDropIdx = -1; this.favDropMode = ""; this.favDropGroupId = ""; },
    // Which THIRD of the hovered chip is the pointer in? Leading edge → insert before, trailing edge
    // → insert after, centre → group. Shift forces group (useful when the chips are tight). The bar's
    // flex-direction decides whether "leading" means top or left — cached at dragstart in _favVert
    // (imperative, NOT a data() prop: Vue 2 never proxies a leading underscore).
    _favModeFromEvent(e) {
      if (e && e.shiftKey) return "group";
      var el = e && e.currentTarget;
      if (!el || !el.getBoundingClientRect) return "group";
      var r = el.getBoundingClientRect();
      var pos = this._favVert ? (e.clientY - r.top) : (e.clientX - r.left);
      var size = this._favVert ? r.height : r.width;
      var frac = size > 0 ? pos / size : 0.5;
      // TOUCH splits the chip in HALF, not thirds. A star is ~26-34px, so its middle third is ~10px
      // (8.6px on the vertical rail) while a fingertip contact patch is 40-48px — a centre zone is
      // simply not aimable, and mis-hitting it pops a name-the-group prompt nobody asked for. On
      // touch, grouping is a DWELL instead (hold ~600ms over the target), which reaches this function
      // as shiftKey:true and short-circuits above. Mouse events carry no touchMode, so the desktop
      // thirds below are untouched.
      if (e && e.touchMode) return frac < 0.5 ? "before" : "after";
      if (frac < 0.33) return "before";
      if (frac > 0.67) return "after";
      return "group";
    },
    favChipOver(i, e) {
      if (!this.favDragId) return;
      this.favDropGroupId = "";
      this.favDropIdx = i;
      this.favDropMode = this._favModeFromEvent(e);
    },
    favChipLeave(i) { if (this.favDropIdx === i) { this.favDropIdx = -1; this.favDropMode = ""; } },
    favGroupOver(gid) { if (this.favDragId) { this.favDropGroupId = gid; this.favDropIdx = -1; this.favDropMode = ""; } },
    favGroupLeave(gid) { if (this.favDropGroupId === gid) this.favDropGroupId = ""; },
    // Keyboard equivalent — HTML5 drag isn't keyboard-reachable. Alt+Arrow moves a focused star one
    // slot. Same slot-mapped write-back as the drop handler (ungroupedFavorites is a FILTERED view,
    // so its indices are NOT this.favorites indices once anything is grouped).
    favNudge(fav, delta) {
      if (!fav || this.viewingAsUser) return;
      var vis = this.ungroupedFavorites;
      var from = vis.findIndex(function (f) { return f.id === fav.id; });
      if (from < 0) return;
      var to = from + delta;
      if (to < 0 || to >= vis.length) return;
      var ordered = vis.slice();
      ordered.splice(to, 0, ordered.splice(from, 1)[0]);
      var slots = [];
      this.favorites.forEach(function (f, idx) { if (!f.groupId) slots.push(idx); });
      var next = this.favorites.slice();
      slots.forEach(function (slotIdx, n) { next[slotIdx] = ordered[n]; });
      this.favorites = next;
      this.saveFavorites();
    },
    // Activate a favorite via mousedown/mouseup instead of @click: the panel star is draggable="true",
    // and the native HTML5 drag machinery routinely swallows the click (so clicking a favorite did
    // nothing — e.g. a chat favorite never populated the hero). A real drag fires dragstart→dragend
    // (no mouseup) so this only ever runs for an actual click; the ≤6px guard ignores a micro-drag.
    favDown(e) { this._favDownXY = e ? [e.clientX, e.clientY] : null; },
    favUp(fav, e) {
      var d = this._favDownXY; this._favDownXY = null;
      if (this.favDragId || !d || !e) return;   // a drag is in flight → not a click
      // A touch long-press drag can still emit a compat mousedown/mouseup pair after it ends; that is
      // a drag finishing, not a tap, and it must not also apply the favorite.
      if (this._favTouchDragAt && Date.now() - this._favTouchDragAt < 700) return;
      if (Math.abs(e.clientX - d[0]) > 6 || Math.abs(e.clientY - d[1]) > 6) return;   // moved → treat as a drag
      this.applyFavorite(fav);
    },
    // Drop a star ON another star. DEFAULT = REORDER (move the dragged one to the target's slot) —
    // that's the gesture people reach for and the frequent one. SHIFT = GROUP the two, which is rare
    // and deliberate (and still confirms via the name prompt). Dropping on a red GROUP chip to join
    // it is unchanged and lives in favDropOnGroup.
    favDropOnFavorite(target, e) {
      var self = this, src = (this.favorites || []).find(function (x) { return x.id === self.favDragId; });
      // Recompute from the drop event rather than trusting the last dragover — same result normally,
      // but immune to a drop that arrives without a preceding dragover on this chip.
      var mode = this._favModeFromEvent(e);
      this.favDragId = ""; this.favDropIdx = -1; this.favDropMode = ""; this.favDropGroupId = "";
      if (!src || !target || src.id === target.id) return;
      if (this.viewingAsUser) return;                       // read-only while previewing another user
      if (mode !== "group") {                               // ── REORDER (edge zones) ──
        var vis = this.ungroupedFavorites;
        var from = vis.findIndex(function (f) { return f.id === src.id; });
        var onto = vis.findIndex(function (f) { return f.id === target.id; });
        if (from < 0 || onto < 0) return;                   // one of them is inside a group → not a reorder
        var ordered = vis.slice();
        ordered.splice(from, 1);                            // pull the source OUT first…
        var at = ordered.findIndex(function (f) { return f.id === target.id; });
        if (at < 0) return;
        ordered.splice(mode === "after" ? at + 1 : at, 0, src);   // …then insert relative to the target
        var slots = [];
        this.favorites.forEach(function (f, idx) { if (!f.groupId) slots.push(idx); });
        var next = this.favorites.slice();
        slots.forEach(function (slotIdx, n) { next[slotIdx] = ordered[n]; });
        this.favorites = next;                              // whole-array assign keeps Vue 2 reactive
        this.saveFavorites();
        return;
      }
      if (target.groupId) { src.groupId = target.groupId; this.saveFavorites(); return; }   // target already in a group → join it
      var name = window.prompt("Group these favorites — name the group:", "Group " + (this.favoriteGroups.length + 1));
      if (name === null) return;
      name = (name || "").trim() || ("Group " + (this.favoriteGroups.length + 1));
      var gid = "G" + Date.now();
      this.favoriteGroups.push({ id: gid, name: name });
      src.groupId = gid; target.groupId = gid;
      this.saveFavorites();
    },
    favDropOnGroup(group) {
      var self = this, src = (this.favorites || []).find(function (x) { return x.id === self.favDragId; });
      this.favDragId = "";
      if (!src || !group) return;
      src.groupId = group.id;
      this.saveFavorites();
    },

    // ===== TOUCH: press-and-hold a ★ to pick it up, then slide it =====
    // HTML5 drag-and-drop emits NOTHING on a touch screen, which is why the entire reorder/group gesture
    // was dead on a phone. This adds a long-press "lift" and then routes every decision after it through
    // the SAME favDragStart / favChipOver / favGroupOver / favDropOnFavorite / favDropOnGroup the mouse
    // uses — there is exactly one drop implementation, so touch and mouse cannot drift apart.
    favTouchStart(fav, e) {
      if (this.viewingAsUser) return;                    // read-only while previewing another user
      if (this.favTouchDrag) this.favDragEnd();          // a previous drag never saw its touchend (rare)
      this._favTouchTeardown();
      var t = (e && e.touches && e.touches.length === 1) ? e.touches[0] : null;
      if (!t || !fav) return;                            // 2+ fingers = pinch/zoom, never a drag
      var self = this;
      this._favTouchXY = [t.clientX, t.clientY];
      this._favTouchFav = fav;
      this._favTouchEl = e.currentTarget;
      this.favPressId = fav.id;                          // .is-pressing swells over FAV_PRESS_MS — the swell IS the progress bar
      // The move/end listeners go on DOCUMENT: the finger leaves a 26px chip on the first millimetre, so
      // element-level tracking is useless. They MUST be { passive: false } — Chrome forces passive:true
      // for touchstart/touchmove registered on window/document/body, and on a passive listener
      // preventDefault() is a silent no-op, so the page AND the overflow:auto .ci-fav-cardbody would
      // keep scrolling underneath the drag.
      document.addEventListener("touchmove", this._favTouchMove, { passive: false });
      document.addEventListener("touchend", this._favTouchEnd, { passive: false });
      document.addEventListener("touchcancel", this._favTouchEnd, { passive: false });
      this._favPressT = setTimeout(function () { self._favPromote(); }, FAV_PRESS_MS);
    },
    // The press survived FAV_PRESS_MS without moving → LIFT the star.
    _favPromote() {
      this._favPressT = null;
      var fav = this._favTouchFav, el = this._favTouchEl;
      if (!fav || !el) { this._favTouchTeardown(); return; }
      this._favBarEl = el.closest ? el.closest(".ci-fav-bar") : null;
      this.favPressId = "";
      this._favTouchGroup = false; this._favDwellKey = ""; this._favHit = null;
      this._favTouchAt = this._favTouchXY ? this._favTouchXY.slice() : null;
      // Reuse the mouse path's dragstart verbatim: it sets favDragId and caches _favVert off the bar's
      // computed flex-direction. It guards `e.dataTransfer`, so a synthetic object is fine.
      this.favDragStart(fav, { currentTarget: el });
      this.favTouchDrag = true;
      this.closeFavPop();                                // a long press may have opened the hover popover
      this._favBuzz(15);                                 // "you may now move this"
      window.__jcsFavDragAt = Date.now();
    },
    _favTouchMove(e) {
      var t = (e && e.touches && e.touches[0]) || null;
      if (!t) return;
      if (!this.favTouchDrag) {
        // Still just a press. Any real movement means the user is SCROLLING, not picking up a star.
        var s = this._favTouchXY;
        if (s && (Math.abs(t.clientX - s[0]) > FAV_SLOP_PX || Math.abs(t.clientY - s[1]) > FAV_SLOP_PX)) this._favTouchTeardown();
        return;                                          // NOT prevented → the page scrolls normally
      }
      // Promoted. If the browser already committed this gesture to a scroll (cancelable:false) we can no
      // longer stop it — hand the gesture back rather than drag and scroll at the same time.
      if (!e.cancelable) { this.favDragEnd(); this._favTouchTeardown(); return; }
      e.preventDefault();
      // Keep App.vue's right-edge tray swipe AND this report's own drawer swipe suppressed for the whole
      // drag and 400ms past it — both decide at touchend, which fires after this handler.
      window.__jcsFavDragAt = Date.now();
      this._favTouchAt = [t.clientX, t.clientY];
      var hit = this._favWrapAt(t.clientX, t.clientY);
      // DWELL over one target flips the drop from REORDER to GROUP (see _favModeFromEvent for why
      // three-zone hit testing can't do grouping on a ~30px chip). Same gesture as making a folder on an
      // iOS home screen. Landing on a different target restarts the clock.
      if (!hit || hit.key !== this._favDwellKey) {
        var self = this;
        this._favDwellKey = hit ? hit.key : "";
        this._favTouchGroup = false;
        if (this._favDwellT) { clearTimeout(this._favDwellT); this._favDwellT = null; }
        if (hit && hit.kind === "fav" && hit.id !== this.favDragId) {
          this._favDwellT = setTimeout(function () {
            self._favTouchGroup = true;
            self._favBuzz(10);                           // a second, shorter tick = "this will GROUP now"
            self._favTouchPaint();                       // repaint: ring + ＋ replaces the red line
          }, FAV_DWELL_MS);
        }
      }
      this._favHit = hit;
      this._favTouchPaint();
    },
    _favTouchEnd(e) {
      var wasDrag = this.favTouchDrag, hit = this._favHit, at = this._favTouchAt;
      var abort = !e || e.type === "touchcancel";
      // Stand the other two touch consumers down. This handler is on DOCUMENT, which bubbles BEFORE
      // window, so App.onTrayTouchEnd and CI.onSwipeEnd always read a fresh flag.
      if (wasDrag) window.__jcsFavDragAt = Date.now();
      this._favTouchTeardown();                          // does NOT clear favDragId — the drop below still needs it
      if (!wasDrag) return;                              // never lifted → a plain tap; @click / favUp applies it
      this._favTouchDragAt = Date.now();                 // favTap / favUp / openFavPop suppress the compat click
      if (e && e.cancelable) e.preventDefault();
      if (abort || !hit || !at) { this.favDragEnd(); return; }   // released over nothing → cancel cleanly
      if (hit.kind === "group") {
        var g = (this.favoriteGroups || []).find(function (x) { return x.id === hit.id; });
        if (g) this.favDropOnGroup(g);
        this.favDragEnd();
        return;
      }
      var target = (this.favorites || []).find(function (f) { return f.id === hit.id; });
      if (!target || target.id === this.favDragId) { this.favDragEnd(); return; }
      this.favDropOnFavorite(target, this._favEvt(hit, at));     // ← the ONE shared drop path
      this.favDragEnd();
    },
    // Repaint the drop feedback for the current finger position — delegates to the SAME favChipOver /
    // favGroupOver the mouse uses, so the red line, the ring and the ＋ are pixel-identical.
    _favTouchPaint() {
      var hit = this._favHit, at = this._favTouchAt;
      if (!hit || !at || hit.id === this.favDragId) { this.favDropIdx = -1; this.favDropMode = ""; this.favDropGroupId = ""; return; }
      if (hit.kind === "group") { this.favGroupOver(hit.id); return; }
      this.favChipOver(hit.idx, this._favEvt(hit, at));
    },
    // A Touch carries no currentTarget, so hand _favModeFromEvent a plain object that quacks like the DOM
    // event it already reads: currentTarget (for getBoundingClientRect — the BUTTON, matching the mouse
    // path exactly), clientX/clientY, shiftKey (the dwell IS Shift on a phone), and touchMode so the chip
    // splits in half instead of thirds.
    _favEvt(hit, at) {
      return { currentTarget: hit.btn, clientX: at[0], clientY: at[1], shiftKey: !!this._favTouchGroup, touchMode: true };
    },
    // Touch has no dragover, so hit-test by hand. elementFromPoint would return the chip UNDER THE FINGER
    // — i.e. the one being dragged — except that .is-touchsrc puts pointer-events:none on its wrapper,
    // which removes it from hit testing entirely. Chips are ~26-34px with 5-8px gaps and a fingertip is
    // ~40px, so an exact hit is not realistic: a miss falls back to the nearest wrapper within 14px.
    // (Enlarging the hit areas in CSS instead would reflow the bar mid-drag and move the targets out from
    // under the finger.)
    _favWrapAt(x, y) {
      var bar = this._favBarEl;
      if (!bar) return null;
      var wrap = null, el = document.elementFromPoint(x, y);
      if (el && el.closest) wrap = el.closest(".ci-favwrap");
      if (!wrap || !bar.contains(wrap)) {
        var best = null, bestD = 14;
        Array.prototype.forEach.call(bar.querySelectorAll(".ci-favwrap"), function (w) {
          var r = w.getBoundingClientRect();
          var dx = x < r.left ? r.left - x : (x > r.right ? x - r.right : 0);
          var dy = y < r.top ? r.top - y : (y > r.bottom ? y - r.bottom : 0);
          var d = Math.max(dx, dy);
          if (d <= bestD) { bestD = d; best = w; }
        });
        wrap = best;
      }
      if (!wrap) return null;
      var gid = wrap.getAttribute("data-fav-group") || "";
      var btn = wrap.querySelector(".ci-fav");
      if (gid) return { kind: "group", id: gid, key: "g:" + gid, btn: btn };
      var fid = wrap.getAttribute("data-fav-id") || "";
      var idx = (this.ungroupedFavorites || []).findIndex(function (f) { return f.id === fid; });
      if (idx < 0) return null;
      return { kind: "fav", id: fid, idx: idx, key: "f:" + fid, btn: btn };
    },
    // Short haptic tick on lift (and again when a dwell arms GROUP). Both guards matter: iOS Safari has no
    // navigator.vibrate at all, and Chrome throws when the document isn't focused. Keep it <=20ms — a long
    // buzz reads as an error, not as a handle.
    _favBuzz(ms) {
      try { if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") navigator.vibrate(ms); } catch (e) { /* */ }
    },
    // Drop every listener and timer this gesture owns. Called on end, on cancel, on abort-by-scroll, and
    // from beforeDestroy. Deliberately does NOT touch favDragId — _favTouchEnd calls this BEFORE performing
    // the drop, and favDropOnFavorite/favDropOnGroup still need it.
    _favTouchTeardown() {
      if (this._favPressT) { clearTimeout(this._favPressT); this._favPressT = null; }
      if (this._favDwellT) { clearTimeout(this._favDwellT); this._favDwellT = null; }
      if (typeof document !== "undefined") {
        document.removeEventListener("touchmove", this._favTouchMove);
        document.removeEventListener("touchend", this._favTouchEnd);
        document.removeEventListener("touchcancel", this._favTouchEnd);
      }
      this.favPressId = "";
      this.favTouchDrag = false;
      this._favTouchXY = null; this._favTouchFav = null; this._favTouchEl = null;
      this._favBarEl = null; this._favHit = null; this._favTouchAt = null;
      this._favDwellKey = ""; this._favTouchGroup = false;
    },
    // Tap = apply. The mobile strip uses @click (the card uses favDown/favUp), and a long-press drag can
    // still emit a compat click when it ends — that is a drag finishing, not a tap.
    favTap(fav) {
      if (this.favTouchDrag) return;
      if (this._favTouchDragAt && Date.now() - this._favTouchDragAt < 700) return;
      this.applyFavorite(fav);
    },
    // Pull a favorite back OUT of its group (becomes a loose yellow star again) — distinct from deleting it.
    removeFromGroup(fav) {
      if (!fav) return;
      var self = this;
      fav.groupId = "";
      this.pruneFavoriteGroups();   // dissolve the group if that emptied it
      if (!this.favoriteGroups.some(function (g) { return g.id === self.favPopId; })) { self.favPopId = ""; self.favPopKind = ""; }
      this.saveFavorites();
    },
    deleteFavGroup(g) {
      if (!g) return;
      if (!window.confirm('Ungroup "' + (g.name || "") + '"? The favorites stay — they just spread back out.')) return;
      this.favorites.forEach(function (f) { if (f.groupId === g.id) f.groupId = ""; });
      this.favoriteGroups = this.favoriteGroups.filter(function (x) { return x.id !== g.id; });
      if (this.favPopId === g.id) { this.favPopId = ""; this.favPopKind = ""; }
      this.favDraft = null; this.favRenaming = false;
      this.saveFavorites();
    },

    // ===== Scheduled email digests (📧 Email on a favorite / group) =====
    loadNotifications() {
      var self = this;
      // Cache-bust: after a save/cancel the immediate reload was being served the stale (pre-change) list from
      // the HTTP cache, so the hover popover kept showing the old active/paused state until a full refresh.
      return httpClient.get("/report/notifications?t=" + Date.now())
        .then(function (res) { self.notifications = (res.data && res.data.items) || []; })
        .catch(function () { self.notifications = []; });
    },
    notifyExistingFor(refId) {
      return (this.notifications || []).find(function (n) { return n.refId === refId; }) || null;
    },
    openNotify(item, kind) {
      if (!item) return;
      this.notifyItem = item;
      this.notifyKind = kind === "group" ? "group" : "favorite";
      this.closeFavPop();
      var ex = this.notifyExistingFor(item.id);
      this.notifyExisting = ex;
      this.notifyMsg = ""; this.notifyBusy = "";
      this.notifyForm = ex
        ? {
            frequency: ex.frequency || "weekly",
            dayOfWeek: ex.dayOfWeek != null ? ex.dayOfWeek : 1,
            dayOfMonth: ex.dayOfMonth != null ? ex.dayOfMonth : 1,
            hourLocal: ex.hourLocal != null ? ex.hourLocal : 7,
            recipients: ex.recipients || "",
            active: ex.active !== false
          }
        : { frequency: "weekly", dayOfWeek: 1, dayOfMonth: 1, hourLocal: 7, recipients: "", active: false };   // NEW digest starts PAUSED — the user opts in by checking Active + Save (an existing schedule keeps its saved state above)
      this.notifyOpen = true;
    },
    closeNotify() { this.notifyOpen = false; },
    // The favorite's filters (or, for a group, each member favorite's) → the digest's sections.
    notifySections() {
      if (this.notifyKind === "group") {
        return this.groupFavorites(this.notifyItem.id).map(function (f) { return { name: f.name, filters: f.filters }; });
      }
      return [{ name: this.notifyItem.name, filters: this.notifyItem.filters }];
    },
    notifyPayload() {
      var f = this.notifyForm;
      return {
        RefId: this.notifyItem.id,
        Kind: this.notifyKind,
        Name: this.notifyItem.name,
        Sections: JSON.stringify(this.notifySections()),
        Frequency: f.frequency,
        DayOfWeek: f.frequency === "weekly" ? f.dayOfWeek : null,
        DayOfMonth: (f.frequency === "monthly" || f.frequency === "quarterly") ? f.dayOfMonth : null,
        HourLocal: f.hourLocal,
        Recipients: f.recipients,
        Active: f.active
      };
    },
    // Upsert the schedule + refresh the list; resolves { ok, next?, message? }.
    doSaveNotification() {
      var self = this;
      return httpClient.post("/report/notifications", this.notifyPayload())
        .then(function (res) {
          if (res.data && res.data.ok) {
            return self.loadNotifications().then(function () {
              // Reflect the just-saved values locally on top of the reloaded list, so the hover popover updates
              // WITHOUT a page refresh even if the reload raced/cached an older copy.
              var f = self.notifyForm, rid = self.notifyItem.id, patched = false;
              var patch = { frequency: f.frequency, dayOfWeek: f.dayOfWeek, dayOfMonth: f.dayOfMonth, hourLocal: f.hourLocal, recipients: f.recipients, active: f.active };
              if (res.data.nextRunAt) patch.nextRunAt = res.data.nextRunAt;
              self.notifications = (self.notifications || []).map(function (n) {
                if (n.refId === rid) { patched = true; return Object.assign({}, n, patch); }
                return n;
              });
              if (!patched) self.notifications = (self.notifications || []).concat([Object.assign({ id: res.data.id || null, refId: rid, kind: self.notifyKind, name: self.notifyItem.name, nextRunAt: res.data.nextRunAt || null }, patch)]);
              self.notifyExisting = self.notifyExistingFor(rid);
              return { ok: true, next: res.data.nextRunAt };
            });
          }
          return { ok: false, message: (res.data && res.data.message) || "Couldn't save." };
        })
        .catch(function () { return { ok: false, message: "Couldn't save the schedule." }; });
    },
    saveNotification() {
      var self = this;
      if (!this.notifyItem) return;
      this.notifyBusy = "save"; this.notifyMsg = "";
      this.doSaveNotification().then(function (r) {
        self.notifyMsg = r.ok ? ("Saved — next send " + self.fmtHistDate(r.next) + ".") : r.message;
      }).finally(function () { self.notifyBusy = ""; });
    },
    sendTestNotification() {
      var self = this;
      if (!this.notifyItem) return;
      this.notifyBusy = "test"; this.notifyMsg = "Saving + sending…";
      this.doSaveNotification().then(function (r) {
        var ex = self.notifyExistingFor(self.notifyItem.id);
        if (!r.ok || !ex) { self.notifyMsg = r.message || "Couldn't save the schedule."; return null; }
        return httpClient.post("/report/notifications/" + ex.id + "/send-now", {})
          .then(function (res) {
            self.notifyMsg = (res.data && res.data.message) || "Test sent.";
            // The send runs Generate Insights server-side + logs it — refresh the Insights History so the
            // new entry (with its attached presentation) shows immediately in the 🕘 list.
            if (res.data && res.data.ok && self.loadAiHistory) self.loadAiHistory();
          });
      }).catch(function () { self.notifyMsg = "Couldn't send the test email."; })
        .finally(function () { self.notifyBusy = ""; });
    },
    cancelNotification() {
      var self = this;
      if (!this.notifyExisting) return;
      var rid = this.notifyItem && this.notifyItem.id;
      this.notifyBusy = "del";
      httpClient.delete("/report/notifications/" + this.notifyExisting.id)
        .then(function () { return self.loadNotifications(); })
        .then(function () {
          if (rid) self.notifications = (self.notifications || []).filter(function (n) { return n.refId !== rid; });   // optimistic remove (guards a cached reload)
          self.notifyExisting = null; self.notifyMsg = "Schedule cancelled.";
        })
        .catch(function () { self.notifyMsg = "Couldn't cancel the schedule."; })
        .finally(function () { self.notifyBusy = ""; });
    },
    fmtHour(h) {
      var ap = h < 12 ? "AM" : "PM"; var hr = h % 12; if (hr === 0) hr = 12;
      return hr + ":00 " + ap;
    },
    // Compact one-line schedule label for the hover popover (e.g. "Weekly (Monday) · next Jul 7").
    schedLabel(n) {
      if (!n) return "";
      var freq = (n.frequency || "");
      freq = freq.charAt(0).toUpperCase() + freq.slice(1);
      var when = "";
      if (n.frequency === "weekly" && n.dayOfWeek != null) when = " (" + (this.weekDays[n.dayOfWeek] || "") + ")";
      else if ((n.frequency === "monthly" || n.frequency === "quarterly") && n.dayOfMonth != null) when = " (day " + n.dayOfMonth + ")";
      var status = n.active === false ? " · paused" : (n.nextRunAt ? " · next " + this.fmtShort(n.nextRunAt) : "");
      return freq + when + status;
    },
    // Remove any group left with no members (e.g. after deleting favorites out of it).
    pruneFavoriteGroups() {
      var self = this;
      this.favoriteGroups = (this.favoriteGroups || []).filter(function (g) {
        return self.favorites.some(function (f) { return f.groupId === g.id; });
      });
    },
    saveFavorites() {
      if (this.viewingAsUser) return;   // previewing another user → don't write to my own profile
      httpClient.post("/user/report-dashboard", { Dashboard: JSON.stringify({ active: this.activeLayout, custom: this.customLayouts, favorites: this.favorites, favoriteGroups: this.favoriteGroups, qaHistory: this.qaHistory, emailLinked: this.emailLinked, emailHiddenLayout: this.emailHiddenLayout, emailVisibleLayout: this.emailVisibleLayout, dockLayouts: this.dockLayouts, dockLayoutsByReport: this.dockLayoutsByReport, emailSnapLayout: this.emailSnapLayout, emailDocked: this.emailDocked, layoutVersions: this.layoutVersions }) }).catch(function () { /* non-fatal */ });
    },
    changeSort(key) {
      if (this.sort === key) this.dir = this.dir === "asc" ? "desc" : "asc";
      else { this.sort = key; this.dir = "asc"; }
      this.load();
    },
    changePageSize(v) { if (v) this.pageSize = Number(v); this.page = 1; this.load(); },
    goPage(n) { this.page = Math.min(Math.max(1, n), this.pageCount); this.load(); },
    exportCsv() {
      this.exporting = true;
      httpClient.get("/report/customer-insights/export", { params: this.buildParams(false), responseType: "blob" })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
          const a = document.createElement("a");
          a.href = url; a.download = "customer-insights.csv";
          document.body.appendChild(a); a.click(); a.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {})
        .finally(() => { this.exporting = false; });
    },
    fmtMoney(n, d) {
      const v = Number(n) || 0;
      const opt = { style: "currency", currency: "USD" };
      if (d != null && d !== "") { opt.minimumFractionDigits = Number(d); opt.maximumFractionDigits = Number(d); }
      return v.toLocaleString("en-US", opt);
    },
    // Per-card decimal places for the money KPI cards. Stored on the card's titleStyles entry so it persists
    // with the layout (no new persisted field); unset defaults to 2 (matches the mockup). Edit toolbar sets 0/1/2.
    kpiDec(gid) {
      const d = (this.titleStyles[gid] || {}).decimals;
      return d == null ? 2 : Number(d);
    },
    fmtNum(n) { return (Number(n) || 0).toLocaleString("en-US"); },
    fmtDate(d) { return d ? String(d).slice(0, 10) : ""; },
    // Long date for the print sheet, matching the card's own "May 27, 2026" rendering.
    fmtLongDate(d) {
      if (!d) return "";
      var dt = new Date(d);
      if (isNaN(dt.getTime())) return String(d).slice(0, 10);
      return dt.toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" });
    },
    // KPI card delta vs the prior equal-length window; null when there's no prior baseline (nothing to compare).
    kpiDelta(cur, prior) {
      cur = Number(cur) || 0;
      prior = Number(prior) || 0;
      if (!prior) return null;
      var pct = (cur - prior) / Math.abs(prior) * 100;
      var up = pct >= 0;
      return { up: up, txt: (up ? "↑ " : "↓ ") + Math.abs(pct).toFixed(0) + "%" };
    },
    barPct(v, max) {
      var m = Number(max) || 0;
      if (m <= 0) return "0%";
      return Math.max(2, (Number(v) || 0) / m * 100).toFixed(1) + "%";
    },
    // A segment's width as a share of its row's segment total (for the territory-segmented part bars).
    segShare(val, segs) {
      var total = (segs || []).reduce(function (s, x) { return s + (Number(x.val) || 0); }, 0);
      if (total <= 0) return "0%";
      return (Number(val) / total * 100).toFixed(2) + "%";
    },
    pctOfTotal(v) {
      var t = this.totalTerritorySales;
      if (t <= 0) return "";
      return " · " + ((Number(v) || 0) / t * 100).toFixed(0) + "%";
    },
    // Width % of one territory segment within a quarter bar (segment sales ÷ quarter total).
    segPct(v, total) {
      var t = Number(total) || 0;
      if (t <= 0) return "0%";
      return ((Number(v) || 0) / t * 100).toFixed(2) + "%";
    },
    terrColor(name) {
      // Admin-managed color (Account Setup → Sales Territories) wins; else the built-in palette.
      var admin = this.adminTerrColors[name];
      if (admin) return admin;
      // Palette matched to the Power BI map legend.
      var map = {
        "International": "#2E86DE",            // blue
        "Midwest": "#8E44AD",                  // purple
        "Mountain and West Coast": "#E67E22",  // orange
        "Northern & Canada": "#9B1B9E",        // magenta-purple
        "SouthEast": "#E84393",                // pink
      };
      return map[name] || "#c0392b";
    },
    // Color a filter chip's ✕ bubble by what it filters, so the chip matches its card's color:
    // territory -> territory color; rep -> the rep's territory color; customer -> its rep's territory
    // color; state -> its territory color; part -> the part green. Others keep the neutral bubble.
    chipColor(c) {
      if (!c) return null;
      if (c.key === "territory") return this.terrColor(c.val);
      if (c.key === "salesRep") return this.repTerritoryColors[c.val] || null;
      if (c.key === "part") return "#16a085";
      if (c.key === "customer") {
        var cust = (this.byCustomer || []).find((x) => x && x.customerId === c.val);
        return (cust && cust.salesRep && this.repTerritoryColors[cust.salesRep]) || null;
      }
      if (c.key === "state") {
        var a = (this.territoryAssignments || []).find((x) =>
          x && String(x.state || "").toUpperCase() === String(c.val || "").toUpperCase() && (x.territory || "") !== "International");
        return a ? this.terrColor(a.territory) : null;
      }
      return null;
    },
    // A chip is "dead" (struck through) when its value no longer appears in the full-filtered result —
    // another filter has excluded it. Only the tracked dims (those with a backend present-value list)
    // qualify; a tracked dim missing from presentByDim means NONE of its values survived.
    chipIrrelevant(c) {
      if (!c || c.val == null) return false;
      if (["customer", "territory", "salesRep", "part", "state", "country"].indexOf(c.key) < 0) return false;
      var dim = this.presentByDim[c.key];
      if (!dim) return true;
      var norm = function (s) { return String(s == null ? "" : s).toUpperCase().replace(/\s+/g, ""); };
      return !dim[String(c.val)] && !dim[norm(c.val)];   // name-safe: match on exact OR normalized value
    },
    // Hover text for an active-filter chip: the irrelevant-filter warning, else the Status explanation (for a
    // status chip), else none — so the Status tooltips appear on the chips too, not just in the dropdown.
    chipTitle(c) {
      if (this.chipIrrelevant(c)) return "This filter no longer affects the results — remove it";
      if (c && c.key === "status" && this.statusTips[c.val]) return this.statusTips[c.val];
      return null;
    },
  },
};
</script>

<style>
/* Touch-friendly, ALWAYS-VISIBLE scrollbars inside the report — cards & lists, the Insights drawer, the
   Filters/Tokens + Layout-2 drawers, and the dashboard-tools rail. Sales reps on Microsoft Surface tablets
   need a bar they can SEE (so they know a value-heavy card scrolls) and grab with a finger; the browser's
   default overlay scrollbars auto-hide and are too thin to touch. Styling ::-webkit-scrollbar also forces
   Edge/Chrome out of overlay mode. Bars appear ONLY when content actually overflows (overflow:auto), so a
   card that fits stays clean. Broad descendant selectors + !important beat the per-card defaults.
   (The page's own scrollbar is on <body>, untouched — the page still scrolls normally.) */
.ci-wrap, .ci-wrap *, .l2-drawer, .l2-drawer *, .dash-tools, .dash-tools * {
  scrollbar-width: thin !important;                    /* Firefox */
  scrollbar-color: #a3abb4 #e9edf1 !important;          /* Firefox: thumb / track */
}
.ci-wrap::-webkit-scrollbar, .ci-wrap *::-webkit-scrollbar,
.l2-drawer::-webkit-scrollbar, .l2-drawer *::-webkit-scrollbar,
.dash-tools::-webkit-scrollbar, .dash-tools *::-webkit-scrollbar {
  width: 11px !important;
  height: 11px !important;
  display: block !important;
}
.ci-wrap::-webkit-scrollbar-track, .ci-wrap *::-webkit-scrollbar-track,
.l2-drawer::-webkit-scrollbar-track, .l2-drawer *::-webkit-scrollbar-track,
.dash-tools::-webkit-scrollbar-track, .dash-tools *::-webkit-scrollbar-track {
  background: #e9edf1 !important;
  border-radius: 8px !important;
}
.ci-wrap::-webkit-scrollbar-thumb, .ci-wrap *::-webkit-scrollbar-thumb,
.l2-drawer::-webkit-scrollbar-thumb, .l2-drawer *::-webkit-scrollbar-thumb,
.dash-tools::-webkit-scrollbar-thumb, .dash-tools *::-webkit-scrollbar-thumb {
  background: #a3abb4 !important;
  border-radius: 8px !important;
  border: 2px solid #e9edf1 !important;                /* inset ring → thumb reads a touch slimmer than the track */
}
.ci-wrap::-webkit-scrollbar-thumb:hover, .ci-wrap *::-webkit-scrollbar-thumb:hover,
.l2-drawer::-webkit-scrollbar-thumb:hover, .l2-drawer *::-webkit-scrollbar-thumb:hover,
.dash-tools::-webkit-scrollbar-thumb:hover, .dash-tools *::-webkit-scrollbar-thumb:hover {
  background: #7c848d !important;
}
.ci-wrap *::-webkit-scrollbar-corner, .l2-drawer *::-webkit-scrollbar-corner, .dash-tools *::-webkit-scrollbar-corner {
  background: transparent !important;
}
/* Dark mode — match the report's deep surfaces */
html.dark-mode .ci-wrap, html.dark-mode .ci-wrap *,
html.dark-mode .l2-drawer, html.dark-mode .l2-drawer *,
html.dark-mode .dash-tools, html.dark-mode .dash-tools * {
  scrollbar-color: #566069 #262d34 !important;
}
html.dark-mode .ci-wrap::-webkit-scrollbar-track, html.dark-mode .ci-wrap *::-webkit-scrollbar-track,
html.dark-mode .l2-drawer *::-webkit-scrollbar-track, html.dark-mode .dash-tools *::-webkit-scrollbar-track {
  background: #262d34 !important;
}
html.dark-mode .ci-wrap::-webkit-scrollbar-thumb, html.dark-mode .ci-wrap *::-webkit-scrollbar-thumb,
html.dark-mode .l2-drawer *::-webkit-scrollbar-thumb, html.dark-mode .dash-tools *::-webkit-scrollbar-thumb {
  background: #566069 !important;
  border: 2px solid #262d34 !important;
}
html.dark-mode .ci-wrap *::-webkit-scrollbar-thumb:hover,
html.dark-mode .l2-drawer *::-webkit-scrollbar-thumb:hover,
html.dark-mode .dash-tools *::-webkit-scrollbar-thumb:hover {
  background: #6b757f !important;
}
</style>

<style scoped>
.ci-wrap { padding-bottom: 30px; }
.ci-head { margin-bottom: 14px; }
.ci-title { font-size: 24px; font-weight: 700; color: #c0392b; margin: 0; }
.ci-sub { color: #666; font-size: 13px; margin: 4px 0 0; }
.ci-notready { background: #fff4e0; border: 1px solid #f0c36d; color: #8a6d1a; border-radius: 8px; padding: 10px 14px; margin-bottom: 14px; font-size: 13px; }
.ci-noresults { background: #fdecee; border: 1px solid #e6a5af; color: #9e2436; border-radius: 8px; padding: 10px 14px; margin: 0 0 14px; font-size: 13px; font-weight: 600; }

.ci-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px; }
/* Top two-column layout: filters (left) | map + KPIs (right). */
.ci-top { margin-bottom: 16px; }
.ci-top-right { min-width: 0; }
.ci-top-right .ci-mappanel { margin: 4px auto 12px; }
.ci-top-right .ci-kpis { grid-template-columns: repeat(3, 1fr); margin-bottom: 0; }
/* Layout 1 top: inline filters removed (now in the shared search bar + drawer),
   so the map + KPIs span the width with the map centered. */
@media (min-width: 993px) {
  .ci-top-right .ci-mapbody { max-width: 100%; margin: 0 auto; }
}

/* ===== Shared search-first filter bar ===== */
.ci-fbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 0 0 12px; }
.ci-fbar-search { flex: 1 1 340px; min-width: 220px; }
.ci-fbar-date { display: flex; align-items: center; gap: 5px; font-size: 10.5px; color: #888; }
.ci-fbar-date span { font-weight: 600; text-transform: uppercase; letter-spacing: .03em; }
.ci-fbar-date input { height: 40px; border: 1px solid #d4d4d8; border-radius: 8px; padding: 0 8px; font-size: 13px; background: #fff; }
.ci-fbar-btn { height: 40px; border: 1px solid #d4d4d8; background: #fff; color: #333; border-radius: 8px; padding: 0 14px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
.ci-fbar-btn:hover { background: #f6f6f7; }
.ci-fbar-btn:disabled { opacity: .5; cursor: default; }
.ci-fbar-badge { background: #9e2436; color: #fff; border-radius: 10px; font-size: 11px; font-weight: 700; padding: 1px 7px; }
.ci-fbar-chips { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin: 0 0 16px; }
.ci-fbar-chip { display: inline-flex; align-items: center; gap: 6px; background: #fff; border: 1px solid #e0e0e4; border-radius: 16px; padding: 4px 6px 4px 12px; font-size: 13px; color: #333; }
.ci-fbar-chip-k { color: #9e2436; font-weight: 700; }
.ci-fbar-chip-x { border: 0; background: #ececef; color: #555; width: 18px; height: 18px; border-radius: 50%; cursor: pointer; font-size: 13px; line-height: 1; }
.ci-fbar-chip-x:hover { background: #9e2436; color: #fff; }
/* "Dead" chip: its value no longer affects the result (excluded by another filter) — struck through.
   The text is struck + muted, but the ✕ bubble keeps FULL color so it matches that filter's bar/map color. */
.ci-fbar-chip--dead { text-decoration: line-through; }
.ci-fbar-chip--dead .ci-fbar-chip-k { opacity: 0.5; }
.ci-fbar-chip--dead .ci-fbar-chip-x { text-decoration: none; }
.ci-fbar-clear { border: 0; background: none; color: #9e2436; font-size: 13px; font-weight: 600; cursor: pointer; text-decoration: underline; }
.ci-fbar-hint { font-size: 12px; color: #999; }
.ci-fbar-spacer { flex: 1; }
/* Filters pill bar — replaces the across-the-top chip strip; opens the drawer where the chips now live. */
.ci-fpill-bar { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; margin: 0 0 16px; }
.ci-fchips-row { display: flex; align-items: flex-start; gap: 8px; width: 100%; }
.ci-fchips-lbl { flex: 0 0 auto; padding-top: 6px; font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #9e2436; }
/* The chips wrap WITHIN this column, so a 2nd row aligns under the first chip — not under the label. */
.ci-fchips-list { flex: 1 1 auto; min-width: 0; display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.ci-fchips-none { font-size: 12px; color: #888; padding-top: 4px; }
.ci-fmore { flex: 0 0 auto; border: 1px solid #e0e0e4; background: #fff; border-radius: 14px; padding: 3px 10px; font-size: 12px; font-weight: 600; color: #444; cursor: pointer; }
.ci-fmore:hover, .ci-fmore.is-on { border-color: #9e2436; color: #9e2436; }
.ci-fmore-panel { padding: 10px 12px; border: 1px solid #ececec; border-radius: 8px; background: #faf7f8; }
.ci-fmore-controls { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 14px; }
.ci-fmore-f { display: flex; flex-direction: column; gap: 3px; font-size: 11px; font-weight: 600; color: #666; }
.ci-fmore-f > select { width: 150px; height: 30px; box-sizing: border-box; font-size: 12px; padding: 0 6px; border: 1px solid #d6d6d6; border-radius: 6px; background: #fff; }
/* Order Status (FilterSlicer) sized + labeled to match the two native selects above. */
.ci-fmore-status { width: 150px; }
/* The slicer is a CHILD component, so its .ms-box/.ms-val/.l2-f carry the CHILD's scope id — a plain
   `.ci-fmore-status .ms-box` rule gets THIS file's scope id appended and never matches. `>>>` pierces
   the boundary so these actually apply (that's why the box stayed 38px / 14px before). */
.ci-fmore-status >>> .l2-f { margin: 0; padding: 0; }
.ci-fmore-status >>> .l2-f > span { display: none; }   /* hide the slicer's own label — the outer .ci-fmore-f <span> matches the other two */
.ci-fmore-status >>> .ms-box { height: 30px; box-sizing: border-box; font-size: 12px; padding: 0 8px; }   /* same border-box height as the two selects */
.ci-fmore-status >>> .ms-val { font-size: 12px; }     /* match the selects' value text size (slicer's default is 14px) */
.ci-fmore-status >>> .ms-val.ms-ph { color: #444; }   /* darken the "All" placeholder toward the selects' value text */
/* Dead (struck-through) filters tucked under the controls. */
.ci-fmore-dead { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 10px; padding-top: 8px; border-top: 1px dashed #e6cfd4; }
.ci-fmore-dead-h { width: 100%; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: #a55; margin-bottom: 2px; }
/* Live chips painted with their territory color (matches the card bars). */
.ci-fbar-chip--color .ci-fbar-chip-k { color: rgba(255,255,255,.82) !important; }
.ci-fbar-chip--color .ci-fbar-chip-x { color: #fff; }
.ci-fav-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.ci-fpill { display: inline-flex; align-items: center; gap: 8px; background: #fff; border: 1px solid #e0e0e4; border-radius: 18px; padding: 5px 14px; font-size: 13px; font-weight: 600; color: #444; cursor: pointer; }
.ci-fpill:hover { border-color: #9e2436; color: #9e2436; }
.ci-fpill.has-active { border-color: #9e2436; }
.ci-fpill-icon { font-size: 14px; line-height: 1; }
.ci-fpill-count { background: #9e2436; color: #fff; font-size: 11px; font-weight: 700; min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center; }
.ci-fpill-clear { border: 0; background: none; color: #9e2436; font-size: 13px; font-weight: 600; cursor: pointer; text-decoration: underline; }
/* ---- Filter favorites (personal, per-rep) ---- */
.ci-fav-save { display: inline-flex; align-items: center; justify-content: center; height: 26px; padding: 0 9px; border: 1px solid #e0b400; background: #fffbe6; color: #b8860b; border-radius: 13px; cursor: pointer; line-height: 1; flex: 0 0 auto; }
.ci-fav-save:hover { background: #fff3c4; }
.ci-fav-save-star { font-size: 13px; }
.ci-fav-save-plus { font-size: 11px; font-weight: 700; margin-left: 1px; }
.ci-fav-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 5px; flex: 1 1 auto; min-width: 0; padding: 2px 0; }
.ci-favwrap { position: relative; flex: 0 0 auto; }
/* While its popover is open, the wrapper jumps into a high stacking layer so the popover paints OVER the
   grid cards (the report card sets isolation:isolate, so a modest z-index alone gets trapped under the grid). */
.ci-favwrap.is-pop-open { z-index: 10002; }
/* touch-action:manipulation only kills the double-tap-zoom delay — deliberately NOT `none`, which would
   forbid scrolling the favorites card by dragging from a star (and can't be applied conditionally anyway:
   the browser latches touch-action at pointer-down, before any handler runs). The user-select/callout
   resets stop iOS raising the selection callout on the long press. */
.ci-fav { display: inline-flex; align-items: center; gap: 4px; height: 26px; padding: 0 8px; border: 1px solid #e6c200; background: #fffdf0; color: #6b5800; border-radius: 13px; cursor: pointer; font-size: 12px; white-space: nowrap; flex: 0 0 auto; max-width: 160px; touch-action: manipulation; -webkit-touch-callout: none; -webkit-user-select: none; user-select: none; }
.ci-fav:hover { background: #fff6cc; box-shadow: 0 0 0 1px #e6c200; }
.ci-fav.is-active { box-shadow: 0 0 0 2px #e6c200; }
.ci-fav--group.is-active { box-shadow: 0 0 0 2px #c0392b; }
/* a GROUP is a red "folder" star (loose favorites stay yellow) */
.ci-fav--group { background: #fdeced; border-color: #d98a8a; font-weight: 600; }
.ci-fav--group:hover { background: #fbdcdd; box-shadow: 0 0 0 1px #d98a8a; }
.ci-fav--group .ci-fav-star { color: #c0392b; }
/* Chat favorites (saved from the hero result card — they carry an `ask`): BLUE CIRCLE + BLACK star,
   matching the .ci-ask-follow-star affordance on the save bar, so they stand apart from the yellow filter
   favorites. The rail chip holds only a star (its name is a hover popover), so a fixed 26px square +
   border-radius:50% renders a true circle. The !important on the star beats the more-specific
   `.ci-fav-panel .ci-fav-star` rule in the favorites panel. */
.ci-fav.is-chat { width: 26px; padding: 0; justify-content: center; border-radius: 50%; background: #a7d5f2; border-color: #7fbfe8; color: #123; }
.ci-fav.is-chat:hover { background: #93cbee; box-shadow: 0 0 0 1px #7fbfe8; }
.ci-fav.is-chat.is-active { box-shadow: 0 0 0 2px #7fbfe8; }
.ci-fav.is-chat .ci-fav-star { color: #111 !important; }
.ci-fav-star { color: #f1b500; font-size: 13px; line-height: 1; flex: 0 0 auto; }
/* ── DRAG FEEDBACK: you can SEE what releasing will do ──────────────────────────────────────────
   The whole chip is the target (a thin gap between chips proved unhittable). Which THIRD of it the
   pointer is over decides the action, and each shows a distinct cue:
     leading/trailing edge → REORDER → a red insertion LINE on that side
     centre                → GROUP   → the target rings red and shows a ＋ badge
   The line renders on .ci-favwrap (already position:relative) and is orientation-aware. */
.ci-fav-bar.is-favdrag .ci-fav { transition: box-shadow .1s ease, transform .1s ease; }
.ci-fav-bar.is-favdrag .ci-fav.is-dragging { opacity: .35; }
/* ── TOUCH: press-and-hold to pick a star up ─────────────────────────────────────────────────────
   No cursor and no HTML5 drag on a phone, so the chip has to narrate the gesture itself.
   ARMING: while the ~500ms press timer runs the star swells; the transition duration EQUALS
   FAV_PRESS_MS, so the swell IS the progress indicator (that, not a longer hold, is what answers
   "did it take?"). LIFTED: gold ring + shadow, and the desktop opacity:.35 is overridden back to 1 —
   on touch the finger already covers the chip, so fading it leaves no confirmation anything was picked
   up. Same specificity (0,3,1) as the rule above, so this MUST stay after it. */
.ci-fav.is-pressing { transform: scale(1.12); transition: transform .5s ease-out; }
.ci-fav-bar.is-favtouch .ci-fav.is-dragging { opacity: 1; box-shadow: 0 0 0 3px #e6c200, 0 3px 10px rgba(0,0,0,.28); transform: scale(1.18); }
/* The dragged star must not hit-test, or document.elementFromPoint returns IT instead of the target
   underneath the finger. On the WRAPPER, not the button, so closest('.ci-favwrap') skips it too. */
.ci-favwrap.is-touchsrc { pointer-events: none; }
/* the red insertion line */
.ci-favwrap.fav-drop-before::before,
.ci-favwrap.fav-drop-after::before {
  content: ""; position: absolute; background: #c0392b; border-radius: 2px;
  box-shadow: 0 0 5px rgba(192,57,43,.85); z-index: 3; pointer-events: none;
}
/* vertical rail (column) → the line lies across, above or below the chip. It overhangs the 26px chip
   by 6px each side (38px total) and is 5px thick so it reads clearly at a glance. Kept to -6px rather
   than a bigger overhang because .ci-fav-cardbody is overflow:auto — an absolutely-positioned child
   past its padding box would add a scrollbar on a narrow card. */
.ci-fav--vert .ci-favwrap.fav-drop-before::before { left: -6px; right: -6px; height: 5px; top: -6px; }
.ci-fav--vert .ci-favwrap.fav-drop-after::before  { left: -6px; right: -6px; height: 5px; bottom: -6px; }
/* horizontal rail (row) → the line stands upright, left or right of the chip */
.ci-fav--horz .ci-favwrap.fav-drop-before::before { top: -6px; bottom: -6px; width: 5px; left: -6px; }
.ci-fav--horz .ci-favwrap.fav-drop-after::before  { top: -6px; bottom: -6px; width: 5px; right: -6px; }
/* GROUP target — ring + grow, and a ＋ badge so it reads as "merge into this one". Applies to a
   loose star's centre zone AND to a red group chip (whole chip). */
.ci-favwrap.fav-drop-group .ci-fav { box-shadow: 0 0 0 3px #c0392b, 0 0 10px rgba(192,57,43,.6); transform: scale(1.18); }
.ci-favwrap.fav-drop-group::after {
  content: "+"; position: absolute; top: -7px; right: -7px; z-index: 4; pointer-events: none;
  width: 15px; height: 15px; border-radius: 50%; background: #c0392b; color: #fff;
  font-size: 12px; font-weight: 700; line-height: 15px; text-align: center;
}
/* names are HIDDEN by default (a clean row of stars) and only appear on hover */
.ci-fav-name { display: none; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 150px; }
.ci-fav:hover .ci-fav-name { display: inline; }
.ci-fav-badge { background: #c0392b; color: #fff; border-radius: 8px; font-size: 9px; padding: 0 5px; font-weight: 700; flex: 0 0 auto; }
/* the delete × on a loose star also only shows on hover (scoped to row chips, not the popover) */
.ci-fav-x { color: #b9a24a; font-size: 14px; line-height: 1; padding: 0 2px; border-radius: 50%; flex: 0 0 auto; }
.ci-fav-x:hover { color: #fff; background: #c0392b; }
.ci-fav > .ci-fav-x { display: none; }
.ci-fav:hover > .ci-fav-x { display: inline-flex; }
/* popover member: eject (remove from group) vs delete */
.ci-fav-eject { color: #2980b9; font-size: 13px; line-height: 1; padding: 0 3px; flex: 0 0 auto; }
.ci-fav-eject:hover { color: #1f5f8b; }
/* hover popover: at-a-glance details + inline edit (rename / remove filters). Body-appended + fixed, so it
   escapes the report card's isolated stacking context and paints above the grid (z-index alone gets trapped). */
.ci-favpop { position: fixed; z-index: 10050; background: #fff; border: 1px solid #e0c200; border-radius: 8px; box-shadow: 0 10px 28px rgba(0,0,0,.22); padding: 8px; min-width: 210px; max-width: 290px; max-height: 360px; overflow-y: auto; cursor: default; }
.ci-favpop-h { display: flex; align-items: center; gap: 6px; padding-bottom: 6px; border-bottom: 1px solid #f0e6b8; margin-bottom: 6px; }
.ci-favpop-title { flex: 1; font-weight: 700; font-size: 12.5px; color: #444; cursor: text; border-radius: 4px; padding: 2px 4px; }
.ci-favpop-title:hover { background: #fffae0; }
.ci-favpop-nameinput { flex: 1; font-size: 12.5px; font-weight: 700; padding: 2px 5px; border: 1px solid #e0c200; border-radius: 4px; outline: none; min-width: 0; }
.ci-favpop-save { width: 22px; height: 22px; border: 0; border-radius: 50%; background: #2e9e5b; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; line-height: 1; flex: 0 0 auto; }
.ci-favpop-save:hover { background: #25844c; }
.ci-favpop-sub { font-size: 10.5px; color: #999; margin: -2px 0 6px; }
.ci-favpop-filters { display: flex; flex-wrap: wrap; gap: 5px; }
.ci-favpop-chip { display: inline-flex; align-items: center; gap: 4px; background: #f6f6f7; border: 1px solid #e2e2e6; border-radius: 10px; padding: 2px 7px; font-size: 11px; color: #555; }
.ci-favpop-chip b { color: #333; font-weight: 700; }
.ci-favpop-chipx { color: #b0b0b6; font-size: 13px; line-height: 1; cursor: pointer; }
.ci-favpop-chipx:hover { color: #c0392b; }
.ci-favpop-empty { font-size: 11px; color: #999; }
.ci-favpop-ask { font-size: 12px; color: #333; line-height: 1.4; background: #faf5f6; border: 1px solid #eee2e4; border-left: 3px solid #d99411; border-radius: 0 4px 4px 0; padding: 6px 9px; }
.ci-favpop-ask-ic { margin-right: 5px; }
.ci-favpop-ask-hint { margin-top: 4px; font-size: 10.5px; color: #8a8a8a; }
html.dark-mode .ci-favpop-ask { background: #232830; border-color: #3a414a; border-left-color: #d99411; color: #cdd3da; }
html.dark-mode .ci-favpop-ask-hint { color: #8a929c; }
.ci-favpop-item { display: flex; align-items: center; gap: 6px; width: 100%; border: 0; background: none; padding: 5px 6px; border-radius: 5px; cursor: pointer; font-size: 12px; color: #444; text-align: left; }
.ci-favpop-item:hover { background: #fffae0; }
.ci-favpop-itemname { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-favpop-foot { display: flex; align-items: center; gap: 8px; margin-top: 8px; padding-top: 6px; border-top: 1px solid #f0e6b8; }
.ci-favpop-apply { border: 1px solid #9e2436; background: #9e2436; color: #fff; border-radius: 6px; font-size: 11px; font-weight: 600; padding: 3px 12px; cursor: pointer; }
.ci-favpop-apply:hover { background: #84202e; }
.ci-favpop-del { border: 0; background: none; color: #b0392b; font-size: 11px; cursor: pointer; margin-left: auto; }
.ci-favpop-del:hover { text-decoration: underline; }
.ci-favpop-email { border: 1px solid #c9a200; background: #fffbe6; color: #8a6d00; border-radius: 6px; font-size: 11px; font-weight: 700; padding: 3px 9px; cursor: pointer; }
.ci-favpop-email:hover { background: #fff3c4; }
.ci-favpop-sched { font-size: 10.5px; font-weight: 600; color: #2e7d46; background: #eef7f0; border: 1px solid #cfe8d6; border-radius: 6px; padding: 3px 8px; margin: 0 0 6px; }
.ci-favpop-sched.is-paused { color: #8a6d00; background: #fffbe6; border-color: #ecd98a; }
.ci-fav-popback { position: fixed; inset: 0; z-index: 10000; }

/* Schedule-email modal (📧 Email on a favorite/group) */
.ci-notify-back { position: fixed; inset: 0; background: rgba(0,0,0,.42); z-index: 10065; }
.ci-notify-modal { position: fixed; top: 300px; left: 50%; transform: translateX(-50%); width: 390px; max-width: 94vw; max-height: calc(100vh - 320px); overflow-y: auto; background: #fff; border-radius: 10px; box-shadow: 0 16px 48px rgba(0,0,0,.35); z-index: 10066; }
.ci-notify-head { position: sticky; top: 0; z-index: 1; display: flex; align-items: center; justify-content: space-between; background: #9e2436; color: #fff; padding: 11px 16px; border-radius: 10px 10px 0 0; font-weight: 700; font-size: 14px; }
.ci-notify-x { border: 0; background: none; color: #fff; font-size: 20px; line-height: 1; cursor: pointer; padding: 0 2px; }
.ci-notify-body { padding: 14px 16px; }
.ci-notify-name { font-size: 15px; font-weight: 800; color: #9e2436; }
.ci-notify-intro { font-size: 11.5px; color: #777; margin: 4px 0 8px; line-height: 1.45; }
.ci-notify-lbl { display: block; font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #888; margin: 12px 0 4px; }
.ci-notify-freq { display: flex; flex-wrap: wrap; gap: 12px; }
.ci-notify-freq label { font-size: 12.5px; color: #333; cursor: pointer; }
.ci-notify-row { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
.ci-notify-row .ci-notify-lbl { margin: 0; flex: 0 0 86px; }
.ci-notify-row select { font-size: 12.5px; padding: 4px 8px; border: 1px solid #d6d6d6; border-radius: 6px; background: #fff; }
.ci-notify-tz { font-size: 11px; color: #999; }
.ci-notify-to { font-size: 11.5px; color: #666; margin-bottom: 4px; }
.ci-notify-recip { width: 100%; box-sizing: border-box; font-size: 12.5px; padding: 6px 8px; border: 1px solid #d6d6d6; border-radius: 6px; }
.ci-notify-recip:focus { outline: none; border-color: #9e2436; }
.ci-notify-chk { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: #333; margin-top: 14px; cursor: pointer; }
.ci-notify-chk-note { color: #999; font-size: 11px; }
.ci-notify-status { font-size: 11.5px; color: #2e7d46; background: #eef7f0; border: 1px solid #cfe8d6; border-radius: 6px; padding: 6px 9px; margin-top: 12px; }
.ci-notify-msg { font-size: 12px; color: #9e2436; margin-top: 10px; }
.ci-notify-foot { display: flex; align-items: center; gap: 8px; padding: 10px 16px 14px; border-top: 1px solid #eee; }
.ci-notify-spacer { flex: 1; }
.ci-notify-closebtn { border: 1px solid #c7ccd2; background: #fff; color: #4a525c; border-radius: 6px; font-size: 12px; font-weight: 600; padding: 6px 12px; cursor: pointer; }
.ci-notify-del { border: 1px solid #d9b3b8; background: #fff; color: #9e2436; border-radius: 6px; font-size: 12px; font-weight: 600; padding: 6px 10px; cursor: pointer; }
.ci-notify-test { border: 1px solid #9e2436; background: #fff; color: #9e2436; border-radius: 6px; font-size: 12px; font-weight: 700; padding: 6px 12px; cursor: pointer; }
.ci-notify-save { border: 1px solid #9e2436; background: #9e2436; color: #fff; border-radius: 6px; font-size: 12px; font-weight: 700; padding: 6px 14px; cursor: pointer; }
.ci-notify-closebtn:disabled, .ci-notify-del:disabled, .ci-notify-test:disabled, .ci-notify-save:disabled { opacity: .6; cursor: default; }
/* Dark mode (the modal renders inside .ci-wrap; mid keeps it light) */
html.dark-mode .ci-notify-modal { background: #20262e; box-shadow: 0 16px 48px rgba(0,0,0,.6); }
html.dark-mode .ci-notify-body, html.dark-mode .ci-notify-foot { color: #e3e7eb; }
html.dark-mode .ci-notify-foot { border-top-color: #3a414a; }
html.dark-mode .ci-notify-closebtn { background: #2a313a; border-color: #4a525c; color: #cfd5db; }
html.dark-mode .ci-notify-name { color: #f0a8b1; }
html.dark-mode .ci-notify-intro, html.dark-mode .ci-notify-to { color: #aeb6bf; }
html.dark-mode .ci-notify-freq label, html.dark-mode .ci-notify-chk { color: #e3e7eb; }
html.dark-mode .ci-notify-row select, html.dark-mode .ci-notify-recip { background: #11151a; border-color: #3a414a; color: #e3e7eb; }
html.dark-mode .ci-notify-test, html.dark-mode .ci-notify-del { background: #2a313a; color: #f0a8b1; border-color: #5a3a40; }
html.dark-mode .ci-notify-status { background: #1f2b22; border-color: #2f5a3c; color: #9fd9b0; }
/* Drawer "Active filters" section (the chips moved here from the old top strip; drawer is white in both themes). */
.l2-activef .l2-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
.l2-activef-n { font-style: normal; background: #9e2436; color: #fff; font-size: 11px; font-weight: 700; padding: 0 7px; border-radius: 9px; margin-left: 6px; }
.l2-activef-empty { font-size: 12px; color: #888; background: #f6f6f7; border: 1px dashed #dcdce0; border-radius: 8px; padding: 8px 10px; margin-bottom: 4px; }
.ci-kpi { background: #fff; border: 1px solid #e6e6e6; border-top: 3px solid #e8b923; border-radius: 8px; padding: 12px 16px 14px; box-shadow: 0 1px 3px rgba(0,0,0,.06); }
.ci-kpi-label { font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #888; }
.ci-kpi-value { font-size: 22px; font-weight: 700; color: var(--ci-accent, #9e2436); margin: 4px 0 2px; overflow-wrap: anywhere; }
.ci-kpi-foot { font-size: 11px; color: #999; overflow-wrap: anywhere; }
/* The "Invoiced XX" / "Not XX" segments in the Total Sales + Orders feet are clickable Invoiced-filter
   toggles — brand-red + bold to read as actionable; the currently-applied one (.is-active) is underlined. */
.ci-kpi-seg { color: #9e2436; font-weight: 700; cursor: pointer; }
.ci-kpi-seg:hover, .ci-kpi-seg.is-active { text-decoration: underline; }
.ci-kpi-sep { color: #b8b8bf; }
html.dark-mode .ci-kpi-seg { color: #ef8a7e; }
/* Saved Carts KPI — two figures (Orders / Quotes) side by side in one tile. */
.ci-kpi-savedcarts { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 6px 22px; }
.ci-kpi-savedcarts .ci-savedcart-fig { min-width: 0; }
.ci-kpi-savedcarts .ci-kpi-foot { flex-basis: 100%; }

/* KPI cards v2 — leading icon + prior-period delta (↑/↓ % vs prior period). */
.ci-kpi--v2 { display: flex; align-items: flex-start; gap: 10px; }
.ci-kpi--v2 .ci-kpi-main { flex: 1 1 auto; min-width: 0; }
/* Keep the big value on one line (no mid-number wrap like "$13,966,936.0 / 3") and sized to fit the tile. */
.ci-kpi--v2 .ci-kpi-value { font-size: 18px; white-space: nowrap; overflow-wrap: normal; margin: 2px 0 1px; }
.ci-kpi--v2 .ci-kpi-foot { line-height: 1.3; }
/* Center the KPI text (label / value / invoiced breakdown / delta) on the three top KPI cards — keeps all
   colors, sizes, the clickable Invoiced·Not segments and the ? helper; text-align inherits to the block rows. */
.dash-card[data-card-id="kpiSales"] .ci-kpi-main,
.dash-card[data-card-id="kpiOrders"] .ci-kpi-main,
.dash-card[data-card-id="kpiAvg"] .ci-kpi-main { text-align: center; }
.ci-kpi-icon { flex: 0 0 40px; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 19px; line-height: 1; }
.ci-kpi-icon--sales  { background: #e5f5ec; }
.ci-kpi-icon--orders { background: #e6f0fb; }
.ci-kpi-icon--avg    { background: #f0e9fa; }
.ci-kpi-icon--carts  { background: #fdeede; }
.ci-kpi-delta { font-size: 11px; font-weight: 700; margin-top: 6px; white-space: nowrap; }
/* Small "?" help button on each KPI delta — click opens the show-your-work breakdown. Inherits the delta color. */
.ci-kpi-help { display: inline-flex; align-items: center; justify-content: center; width: 15px; height: 15px; margin-left: 5px; padding: 0; border: 1px solid currentColor; border-radius: 50%; background: transparent; color: inherit; font-size: 10px; font-weight: 800; line-height: 1; cursor: pointer; opacity: .75; vertical-align: middle; }
.ci-kpi-help:hover { opacity: 1; }
.ci-kpi-delta.is-up { color: #2e7d5b; }
.ci-kpi-delta.is-down { color: #c0392b; }
.ci-kpi-delta-lbl { color: #999; font-weight: 500; }
.ci-savedcart-figs { display: flex; flex-direction: column; gap: 3px; }
.ci-kpi--v2 .ci-savedcart-fig { display: flex; align-items: baseline; justify-content: space-between; gap: 14px; }
.ci-kpi--v2 .ci-savedcart-fig .ci-kpi-value { margin: 0; }
html.dark-mode .ci-kpi-icon--sales  { background: #17301f; }
html.dark-mode .ci-kpi-icon--orders { background: #142333; }
html.dark-mode .ci-kpi-icon--avg    { background: #241a33; }
html.dark-mode .ci-kpi-icon--carts  { background: #33260f; }
html.dark-mode .ci-kpi-delta.is-up { color: #6fd0a3; }
html.dark-mode .ci-kpi-delta.is-down { color: #ef8a7e; }
html.dark-mode .ci-kpi-delta-lbl { color: #8b929a; }

/* Phase B — top filter bar (grouped dropdowns + date range + presets + active-filter chips). */
.ci-fbar { background: #fff; border: 1px solid #e6e6ea; border-radius: 10px; padding: 12px 14px; margin: 2px 0 14px; position: relative; z-index: 30; }
.ci-fbar-controls { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
/* Share the row evenly so all four dropdowns stay on one line (Status sits right of Customer, not wrapped below). */
.ci-fbar-f { flex: 1 1 0; min-width: 130px; }
/* Invoiced native <select> — styled to match the FilterSlicer boxes (same height / border / focus ring). */
.ci-fbar-nativesel { width: 100%; height: 38px; box-sizing: border-box; border: 1px solid #d4d4d8; border-radius: 6px; padding: 0 10px; background: #fff; font-size: 14px; color: #222; cursor: pointer; }
.ci-fbar-nativesel:focus { outline: none; border-color: #9e2436; box-shadow: 0 0 0 2px rgba(158,36,54,.12); }
html.dark-mode .ci-fbar-nativesel { background-color: #11151a; border-color: #3a414a; color: #e3e7eb; }
/* Favorites — a full-width bordered row ABOVE the filter dropdowns so it stretches across the screen. Stars
   sit in a row and wrap to more rows only when there are many. "FAVORITES" is notched into the top-center
   border in brand red. */
.ci-fbar-fav { position: relative; display: flex; flex-wrap: wrap; align-items: center; gap: 8px 10px; width: 100%; padding: 9px 12px; margin: 0 0 12px; border: 1px solid #cdd0d5; border-radius: 8px; background: #fff; }
.ci-fbar-fav-legend { position: absolute; top: -7px; left: 50%; transform: translateX(-50%); padding: 0 6px; background: #fff; font-size: 9.5px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: #9e2436; white-space: nowrap; pointer-events: none; }
.ci-fbar-fav .ci-fav-bar { flex: 1 1 auto; display: flex; flex-wrap: wrap; align-items: center; gap: 6px; min-width: 0; padding: 0; }
/* ⚙ More — pushed to the far right of the date/presets row (right-aligned, after the YTD button). */
.ci-fbar-daterow .ci-fmore { margin-left: auto; }
/* Dark mode: box matches the presets/date treatment; the legend background must match the .ci-fbar card
   BEHIND the box's top border (#20262e) so the legend cleanly cuts the border. */
html.dark-mode .ci-fbar-fav { background: #2a313a; border-color: #4a525c; }
html.dark-mode .ci-fbar-fav-legend { background: #20262e; color: #ef8a7e; }
/* Open dropdowns FLOAT over the content below instead of expanding the bar and pushing the other fields down,
   and size to their content (full "ID — Name" on one line) up to a cap, rather than wrapping in a narrow box. */
.ci-fbar-controls >>> .ms-pop { position: absolute; left: 0; right: auto; top: calc(100% + 4px); z-index: 100; width: max-content; min-width: 100%; max-width: 340px; }
.ci-fbar-controls >>> .ms-opt span { white-space: nowrap; word-break: normal; }
.ci-fbar-lbl { display: block; font-size: 12px; color: #444; margin-bottom: 3px; }
.ci-fbar-daterow { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; margin-top: 10px; }
.ci-fbar-dates { display: flex; align-items: center; gap: 6px; }
.ci-fbar-dates input { width: 140px; height: 36px; border: 1px solid #d4d4d8; border-radius: 6px; padding: 0 8px; font-size: 13px; background: #fff; color: #222; }
.ci-fbar-dash { color: #888; }
.ci-fbar-presets { display: flex; flex-wrap: wrap; gap: 6px; }
.ci-fbar-presets button { border: 1px solid #d4d4d8; background: #fff; border-radius: 6px; font-size: 12px; padding: 4px 12px; cursor: pointer; color: #333; }
.ci-fbar-presets button:hover { border-color: #9e2436; color: #9e2436; }
/* (.ci-fbar-lastrow / .ci-fbar-lastrow-hint styles removed 2026-07-03 — the bottom filter row was dropped.) */
.ci-fbar-si { margin-left: auto; display: flex; align-items: center; gap: 8px; min-width: 0; }
/* Compact so Status + Invoiced sit to the RIGHT of the "See active filters" pill ON THE SAME ROW (they used to
   wrap to a second line because each field carried a 104px min-width). Fields are label-above-select columns —
   drop the min-width + shrink the label so the pair fits in the space beside the pill. Still wraps as a last resort. */
.ci-fbar-si .ci-fbar-f { min-width: 0; }
.ci-fbar-si >>> .l2-f { gap: 2px; }
.ci-fbar-si >>> .l2-f > span { font-size: 10.5px; white-space: nowrap; }
.ci-fbar-si >>> .ms-box { height: 30px; padding: 0 8px; min-width: 0; }
.ci-fbar-si >>> .ms-val { font-size: 12px; }
.ci-fbar-si .ci-fbar-nativesel { height: 30px; font-size: 12px; min-width: 0; }
/* "Clear all" — removes every filter + chip (resetFilters); sits right of Invoiced on the date row. */
.ci-fbar-clearall { flex: 0 0 auto; height: 30px; margin-left: 2px; border: 1px solid #d8b3ba; background: #fff; color: #9e2436; font-size: 11px; font-weight: 700; border-radius: 6px; padding: 0 10px; cursor: pointer; white-space: nowrap; }
.ci-fbar-clearall:hover { background: #9e2436; color: #fff; border-color: #9e2436; }
html.dark-mode .ci-fbar-clearall { background: #11151a; border-color: #5a3a41; color: #f0a8b1; }
html.dark-mode .ci-fbar-clearall:hover { background: #9e2436; color: #fff; }
.ci-chips-inset { padding: 0 14px; }
.ci-fbar-active { position: relative; display: flex; flex-wrap: nowrap; align-items: center; gap: 8px; }
.ci-card-addrow { margin-top: 8px; }
.ci-card-add { width: 100%; max-width: 240px; height: 30px; font-size: 12px; font-weight: 700; color: #9e2436; border: 1px dashed #d8b3ba; border-radius: 8px; background: #fff; padding: 0 8px; cursor: pointer; }
.ci-card-add:hover { background: #f7eef0; }
.ci-card-add:disabled { color: #b7b7bd; border-color: #e5e5e5; cursor: default; background: #fafafa; }
/* "Processing" sweep across the Sales-by-Territory / Sales-Reps bars while a filter reloads — an at-a-glance
   cue when the top timer is scrolled out of view. Current bars stay visible (dimmed) under the sweeping line. */
.ci-bars-loading .ci-bar-row, .ci-bars-loading .ci-codes-row { position: relative; opacity: .85; }
.ci-bars-loading .ci-bar-row::after, .ci-bars-loading .ci-codes-row::after { content: ""; position: absolute; left: 0; right: 0; top: 50%; height: 2px; margin-top: -1px; pointer-events: none; background: linear-gradient(90deg, rgba(158,36,54,0) 0%, rgba(158,36,54,0) 42%, #9e2436 50%, rgba(158,36,54,0) 58%, rgba(158,36,54,0) 100%); background-size: 230% 100%; animation: ci-barsweep 1.15s linear infinite; }
@keyframes ci-barsweep { from { background-position: 130% 0; } to { background-position: -130% 0; } }
html.dark-mode .ci-bars-loading .ci-bar-row::after, html.dark-mode .ci-bars-loading .ci-codes-row::after { background: linear-gradient(90deg, rgba(240,168,177,0) 0%, rgba(240,168,177,0) 42%, #f0a8b1 50%, rgba(240,168,177,0) 58%, rgba(240,168,177,0) 100%); background-size: 230% 100%; }
.ci-cust-chips { display: flex; flex-wrap: wrap; gap: 4px; margin: 0 0 6px; }
.ci-cust-chip { display: inline-flex; align-items: center; gap: 3px; background: #9e2436; color: #fff; border: 1px solid #000; border-radius: 11px; padding: 1px 3px 1px 8px; font-size: 10px; font-weight: 700; }
.ci-cust-chip button { border: 0; background: #fff; color: #9e2436; font-weight: 800; width: 13px; height: 13px; border-radius: 50%; font-size: 9px; line-height: 1; cursor: pointer; }
.ci-cust-chip button:hover { background: #ffd7dd; }
/* Chips collapse to ONE row; overflow is hidden and surfaced via the "See all ▾" popup. */
.ci-fbar-chips { flex: 1 1 auto; min-width: 0; display: flex; flex-wrap: wrap; align-items: center; gap: 6px; max-height: 30px; overflow: hidden; }
/* (.ci-fbar-summary "See active filters" pill styles removed 2026-07-03 — replaced by per-card chips + the shared chipHoverPanel popover.) */
.ci-fbar-seeall-wrap { position: relative; flex: 0 0 auto; }
.ci-fbar-seeall { border: 1px solid #d8b3ba; background: #fff; color: #9e2436; font-size: 12px; font-weight: 700; border-radius: 6px; padding: 4px 9px; cursor: pointer; white-space: nowrap; }
.ci-fbar-seeall:hover { background: #f7eef0; }
.ci-fbar-seeall-pop { position: absolute; top: calc(100% + 5px); left: 0; z-index: 70; width: 600px; max-width: 92vw; max-height: 320px; overflow-y: auto; background: #fff; border: 1px solid #e3d7da; border-radius: 10px; box-shadow: 0 10px 30px rgba(0,0,0,.18); padding: 8px; }
.ci-fbar-seeall-h { font-size: 11px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; color: #9e2436; padding: 2px 4px 6px; }
.ci-fbar-seeall-list { list-style: none; margin: 0; padding: 0; }
.ci-fbar-seeall-list li { font-size: 12.5px; color: #333; border-top: 1px solid #f2eef0; }
.ci-seeall-row { display: flex; align-items: center; gap: 6px; padding: 5px 4px; }
.ci-fbar-seeall-list li.is-dead .ci-seeall-row { opacity: .55; text-decoration: line-through; }
.ci-fbar-seeall-list li.is-culprit .ci-seeall-row { background: #fff5e9; border-radius: 6px; }
.ci-fbar-seeall-cause { flex: 0 0 auto; background: #e67e22; color: #fff; font-size: 9.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .03em; padding: 2px 6px; border-radius: 8px; white-space: nowrap; }
html.dark-mode .ci-fbar-seeall-list li.is-culprit .ci-seeall-row { background: #3a2a1a; }
/* Custom "comment"-style HTML tooltip shown inline under a hovered dead/culprit filter */
.ci-tip { margin: 2px 2px 6px; background: #fffdf5; border: 1px solid #ecd9b0; border-left: 3px solid #e67e22; border-radius: 8px; padding: 8px 10px; box-shadow: 0 2px 10px rgba(0,0,0,.10); }
.ci-tip-h { font-weight: 800; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; color: #b9651a; margin-bottom: 4px; }
.ci-tip-b { font-size: 11.5px; line-height: 1.45; color: #4a4a4a; }
.ci-tip-b b { color: #222; }
.ci-tip-l { list-style: none; margin: 5px 0; padding: 0; }
.ci-tip ul.ci-tip-l li { padding: 2px 0; border: 0; font-size: 11.5px; color: #555; }
.ci-tip ul.ci-tip-l li.ci-tip-cause { font-weight: 700; color: #b9651a; }
.ci-tip-tag { display: inline-block; background: #e67e22; color: #fff; font-size: 8.5px; font-weight: 800; text-transform: uppercase; padding: 1px 5px; border-radius: 7px; margin-left: 4px; }
.ci-tip-f { margin-top: 6px; font-size: 11px; color: #6a6a6a; font-style: italic; }
html.dark-mode .ci-tip { background: #2a2418; border-color: #5a4a2a; }
html.dark-mode .ci-tip-b { color: #cbbfa4; }
html.dark-mode .ci-tip-b b { color: #f0e6cf; }
html.dark-mode .ci-tip ul.ci-tip-l li { color: #b6ab90; }
html.dark-mode .ci-tip-f { color: #9a8f76; }
.ci-fbar-seeall-k { font-weight: 700; color: #6a6a72; flex: 0 0 auto; }
.ci-fbar-seeall-v { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-fbar-seeall-x { flex: 0 0 auto; border: 0; background: #9e2436; color: #fff; width: 18px; height: 18px; border-radius: 50%; font-size: 12px; line-height: 1; cursor: pointer; }
.ci-fbar-seeall-x:hover { background: #7d1c2b; }
.ci-fbar-seeall-clear { margin-top: 8px; width: 100%; border: 1px solid #d8b3ba; background: #fff; color: #9e2436; font-size: 11.5px; font-weight: 700; border-radius: 6px; padding: 5px; cursor: pointer; }
.ci-fbar-seeall-clear:hover { background: #f7eef0; }
html.dark-mode .ci-fbar-seeall { background: #11151a; border-color: #5a3a41; color: #f0a8b1; }
html.dark-mode .ci-fbar-seeall:hover { background: #1b2128; }
html.dark-mode .ci-fbar-seeall-pop { background: #1f242b; border-color: #3a414a; box-shadow: 0 10px 30px rgba(0,0,0,.5); }
/* Shared chip popover: reuses .ci-fbar-seeall-pop's box; this makes it FIXED (body-appended) + narrower, and adds
   the hovered-chip highlight. Placed AFTER .ci-fbar-seeall-pop so position:fixed wins over its position:absolute. */
.ci-chippop { position: fixed; z-index: 10050; width: 420px; max-width: 92vw; }
.ci-fbar-seeall-list li.is-hovered .ci-seeall-row { background: #fdecee; border-radius: 6px; box-shadow: inset 0 0 0 1px #e6c2ce; }
html.dark-mode .ci-fbar-seeall-list li.is-hovered .ci-seeall-row { background: #2a1a20; box-shadow: inset 0 0 0 1px #5a3a41; }
/* KPI "% vs prior period" show-your-work tooltip (body-appended, fixed) — orange "teaching note". The INNER content
   is injected via v-html (which carries NO scoped-CSS attribute), so its layout + specific colors are inline-styled
   in _buildKpiMath; here we style only the wrapper and set the base color/font that the inner content INHERITS
   (inheritance crosses the scoped-CSS boundary, unlike class selectors). */
.ci-kpi-math { position: fixed; z-index: 10050; width: 380px; max-width: 92vw; max-height: 72vh; overflow-y: auto; background: #fffdf5; border: 1px solid #ecd9b0; border-left: 3px solid #e67e22; border-radius: 10px; box-shadow: 0 12px 32px rgba(0,0,0,.20); padding: 12px 14px; cursor: default; color: #3a3a3a; font-size: 12px; line-height: 1.5; }
html.dark-mode .ci-kpi-math { background: #2a2418; border-color: #5a4a2a; box-shadow: 0 12px 32px rgba(0,0,0,.5); color: #cbbfa4; }
/* Customer notation popover (📝 in the Customer ID(s) card) — body-appended, near the icon. */
.ci-notepop { position: fixed; z-index: 100060; width: 320px; max-width: 92vw; max-height: 60vh; overflow-y: auto; background: #fff; border: 1px solid #e3e3e6; border-left: 3px solid #9e2436; border-radius: 10px; box-shadow: 0 12px 32px rgba(0,0,0,.22); padding: 10px 12px; cursor: default; color: #333; }
.ci-notepop-h { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 6px; }
.ci-notepop-title { font-size: 12px; font-weight: 700; color: #7a1f2b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-notepop-x { border: 0; background: none; color: #999; font-size: 16px; line-height: 1; cursor: pointer; padding: 0 2px; }
.ci-notepop-body { font-size: 12.5px; line-height: 1.5; white-space: pre-wrap; word-break: break-word; color: #3a3a3a; }
html.dark-mode .ci-notepop { background: #1f242b; border-color: #333a42; border-left-color: #9e2436; box-shadow: 0 12px 32px rgba(0,0,0,.5); color: #e3e7eb; }
html.dark-mode .ci-notepop-title { color: #f0a8b1; }
html.dark-mode .ci-notepop-body { color: #dfe3e8; }
html.dark-mode .ci-notepop-x { color: #8a929c; }
/* 📋 specs popover (blue accent) + 💡 perspective popover (amber accent) — reuse the .ci-notepop base. */
.ci-specpop { border-left-color: #2f6fb0; }
.ci-specpop .ci-notepop-title { color: #245a91; }
.ci-persppop { border-left-color: #d9a441; width: 340px; }
.ci-persppop .ci-notepop-title { color: #8a6d1a; }
.ci-whypop { border-left-color: #d9a441; width: 340px; }
.ci-whypop .ci-notepop-title { color: #8a6d1a; }
.ci-why-body p { margin: 0 0 7px; font-size: 12px; line-height: 1.45; }
.ci-why-body p:last-child { margin-bottom: 0; }
.ci-why-cust { color: #c0392b; }
.ci-why-zero { color: #8a6d1a; }
html.dark-mode .ci-whypop .ci-notepop-title { color: #e6c65f; }
html.dark-mode .ci-why-cust { color: #f0a8b1; }
html.dark-mode .ci-why-zero { color: #e6c65f; }
.ci-why-lbl { margin: 0 0 5px; font-size: 11.5px; color: #555; }
.ci-why-note { margin: 8px 0 0; font-size: 10.5px; line-height: 1.4; color: #8a919a; }
html.dark-mode .ci-why-lbl { color: #b7bec6; }
html.dark-mode .ci-why-note { color: #7f8790; }
.ci-why-hist { margin: 0 0 8px; display: flex; flex-direction: column; gap: 3px; }
.ci-why-hrow { display: grid; grid-template-columns: 1fr auto; grid-template-areas: "rep num" "terr num"; column-gap: 8px; padding: 4px 7px; border-radius: 6px; background: #f6f7f8; border-left: 3px solid #cbd2d8; }
.ci-why-hrow.is-cur { background: #eef6f0; border-left-color: #33a35e; }
.ci-why-hrep { grid-area: rep; font-weight: 700; font-size: 11.5px; color: #333; }
.ci-why-hterr { grid-area: terr; font-size: 10.5px; color: #777; }
.ci-why-hnum { grid-area: num; align-self: center; font-size: 11px; color: #555; font-variant-numeric: tabular-nums; white-space: nowrap; }
html.dark-mode .ci-why-hrow { background: #23282e; border-left-color: #3a424a; }
html.dark-mode .ci-why-hrow.is-cur { background: #16241b; border-left-color: #33a35e; }
html.dark-mode .ci-why-hrep { color: #e3e7eb; }
html.dark-mode .ci-why-hterr { color: #9aa3ab; }
html.dark-mode .ci-why-hnum { color: #aeb6bf; }
.ci-persp-headline { font-size: 12.5px; font-weight: 800; color: #7a1f2b; margin-bottom: 4px; }
.ci-persp-actions { margin: 8px 0 0; padding-left: 18px; }
.ci-persp-actions li { font-size: 12px; line-height: 1.45; color: #3a3a3a; margin-bottom: 3px; }
.ci-persp-loading { font-size: 12.5px; color: #777; display: flex; align-items: center; gap: 8px; padding: 4px 0; }
.ci-persp-spin { width: 13px; height: 13px; border: 2px solid #e0c48a; border-top-color: #d9a441; border-radius: 50%; display: inline-block; animation: ci-persp-spin 0.7s linear infinite; }
@keyframes ci-persp-spin { to { transform: rotate(360deg); } }
html.dark-mode .ci-specpop { border-left-color: #4a90d9; }
html.dark-mode .ci-specpop .ci-notepop-title { color: #8fc0ef; }
html.dark-mode .ci-persppop .ci-notepop-title { color: #e6c65f; }
html.dark-mode .ci-persp-headline { color: #f0a8b1; }
html.dark-mode .ci-persp-actions li { color: #dfe3e8; }
/* 💡 "Why unassigned" diagnostic (Sales Reps (Unassigned) row). */
.ci-unassigned-diag { border: 0; background: none; cursor: pointer; font-size: 12px; line-height: 1; padding: 0 0 0 5px; opacity: .85; }
/* ⚙ auto-mapped flag on a rep row: subtle amber chip, shows the % of the rep's sales assigned by the prefix rule. */
.ci-rep-auto { margin-left: 6px; font-size: 9.5px; font-weight: 700; color: #8a6d1a; background: #fbf3dc; border: 1px solid #ecd9a8; border-radius: 4px; padding: 0 4px; line-height: 1.5; cursor: help; white-space: nowrap; vertical-align: middle; }
.ci-rep-auto:hover { background: #f6e9c4; }
html.dark-mode .ci-rep-auto { color: #e6c65f; background: #2c2718; border-color: #4a3f1c; }
.ci-unassigned-diag:hover { opacity: 1; transform: scale(1.12); }
.ci-unpop { width: 440px; }
.ci-un-sum { font-weight: 800; color: #7a1f2b; font-size: 13px; margin-bottom: 6px; }
.ci-un-causes { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.ci-un-cause { font-size: 11px; font-weight: 700; border-radius: 10px; padding: 2px 8px; }
.ci-un-cause.is-no-rep { background: #fdecee; color: #9e2436; }
.ci-un-cause.is-rep-unmapped { background: #eef5fc; color: #245a91; }
.ci-un-cause.is-prefix-ready { background: #e7f6ec; color: #1f7a44; }
.ci-un-cause.is-mapped-pending-visual { background: #e3f5ea; color: #1c6b3d; }
.ci-un-offnote { margin: 6px 0 2px; padding: 6px 9px; font-size: 11.5px; line-height: 1.35; background: #fff7e6; border: 1px solid #f0d089; border-radius: 6px; color: #7a5200; }
html.dark-mode .ci-un-offnote { background: #2a2410; border-color: #5a4a1e; color: #e6c877; }
.ci-un-block { margin: 6px 0 8px; padding: 6px 8px; background: #f6f8fb; border: 1px solid #dbe6f2; border-radius: 6px; }
.ci-un-h { font-size: 10.5px; font-weight: 800; color: #666; margin: 6px 0 4px; text-transform: uppercase; letter-spacing: .02em; }
.ci-un-reps { display: flex; flex-wrap: wrap; gap: 5px; }
.ci-un-rep { font-size: 11px; background: #fff; border: 1px solid #cfdcea; border-radius: 8px; padding: 1px 7px; color: #245a91; }
.ci-un-rep--btn { cursor: pointer; font-family: inherit; }
.ci-un-rep--btn:hover { background: #eef5fc; border-color: #2f6fb0; }
html.dark-mode .ci-un-rep--btn:hover { background: #16222c; border-color: #4a90d9; }
.ci-un-list { list-style: none; margin: 0; padding: 0; }
.ci-un-item { padding: 6px 0; border-top: 1px solid #eee; }
.ci-un-item.is-mapped-pending-visual { border-left: 3px solid #33a35e; padding-left: 7px; }
.ci-un-item.is-mapped-pending-visual .ci-un-fix { color: #1f7a44; }
.ci-un-item-top { font-size: 12px; overflow: hidden; }
.ci-un-foot-ok { color: #1f7a44; }
.ci-un-st { font-size: 10px; background: #eef1f4; border-radius: 8px; padding: 0 6px; color: #667; margin-left: 4px; }
.ci-un-ctry { font-size: 10px; background: #e7f0e9; border-radius: 8px; padding: 0 6px; color: #3a6b4c; margin-left: 4px; }
html.dark-mode .ci-un-ctry { background: #1c2a22; color: #8fcfa3; }
html.dark-mode .ci-un-cause.is-prefix-ready { background: #16241b; color: #7bd88a; }
html.dark-mode .ci-un-cause.is-mapped-pending-visual { background: #16241b; color: #7bd88a; }
html.dark-mode .ci-un-item.is-mapped-pending-visual .ci-un-fix { color: #7bd88a; }
html.dark-mode .ci-un-foot-ok { color: #7bd88a; }
.ci-un-amt { float: right; font-weight: 800; color: #333; }
.ci-un-fix { font-size: 11.5px; color: #555; margin-top: 2px; line-height: 1.4; }
.ci-un-foot { margin-top: 8px; font-size: 11px; color: #777; border-top: 1px solid #eee; padding-top: 6px; }
html.dark-mode .ci-un-sum { color: #f0a8b1; }
html.dark-mode .ci-un-cause.is-no-rep { background: #34222a; color: #f0a8b1; }
html.dark-mode .ci-un-cause.is-rep-unmapped { background: #16222c; color: #8fc0ef; }
html.dark-mode .ci-un-block { background: #16222c; border-color: #2f4a63; }
html.dark-mode .ci-un-h { color: #aeb6bf; }
html.dark-mode .ci-un-rep { background: #1f242b; border-color: #2f4a63; color: #8fc0ef; }
html.dark-mode .ci-un-item { border-color: #333a42; }
html.dark-mode .ci-un-st { background: #2a2f36; color: #aeb6bf; }
html.dark-mode .ci-un-amt { color: #e3e7eb; }
html.dark-mode .ci-un-fix { color: #aeb6bf; }
html.dark-mode .ci-un-foot { color: #8a929c; border-color: #333a42; }
.ci-un-more { border: 0; background: none; color: #9e2436; font-weight: 700; font-size: 11.5px; cursor: pointer; padding: 6px 0 0; text-decoration: underline; display: block; }
.ci-un-email { margin-top: 8px; width: 100%; border: 1px solid #d9a441; background: #fff8e8; color: #8a6d1a; font-weight: 800; font-size: 12px; border-radius: 6px; padding: 7px 10px; cursor: pointer; }
.ci-un-email:hover { background: #fdefc8; }
html.dark-mode .ci-un-more { color: #f0a8b1; }
html.dark-mode .ci-un-email { background: #2c2617; border-color: #5a4a1e; color: #e6c65f; }
/* Email-the-parts-team modal (unassigned diagnostic) */
.ci-unemail-back { position: fixed; inset: 0; z-index: 100080; background: rgba(20,18,19,.5); display: flex; align-items: center; justify-content: center; padding: 24px 14px; }
.ci-unemail { width: 480px; max-width: 100%; background: #fff; border-radius: 12px; box-shadow: 0 18px 60px rgba(0,0,0,.35); overflow: hidden; }
.ci-unemail-h { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; font-weight: 800; color: #7a1f2b; border-bottom: 1px solid #eee; }
.ci-unemail-x { border: 0; background: none; font-size: 20px; line-height: 1; color: #999; cursor: pointer; }
.ci-unemail-body { padding: 14px 16px; }
.ci-unemail-lead { font-size: 12.5px; color: #555; line-height: 1.5; margin: 0 0 10px; }
.ci-unemail-lbl { font-size: 11px; font-weight: 800; color: #666; text-transform: uppercase; letter-spacing: .02em; }
.ci-unemail-note { width: 100%; margin-top: 4px; font-size: 13px; padding: 8px; border: 1px solid #d6d6d6; border-radius: 6px; resize: vertical; font-family: inherit; box-sizing: border-box; }
.ci-unemail-note:focus { outline: none; border-color: #9e2436; }
.ci-unemail-err { margin-top: 8px; color: #9e2436; font-size: 12px; }
.ci-unemail-foot { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 16px; border-top: 1px solid #eee; }
.ci-unemail-cancel { border: 1px solid #ccc; background: #fff; color: #555; font-weight: 700; font-size: 12.5px; border-radius: 6px; padding: 7px 14px; cursor: pointer; }
.ci-unemail-send { border: 0; background: #9e2436; color: #fff; font-weight: 800; font-size: 12.5px; border-radius: 6px; padding: 7px 16px; cursor: pointer; }
.ci-unemail-send:disabled, .ci-unemail-cancel:disabled { opacity: .6; cursor: default; }
.ci-unemail-sent { padding: 28px 16px; text-align: center; font-size: 15px; font-weight: 800; color: #2e7d32; }
html.dark-mode .ci-unemail { background: #1f242b; box-shadow: 0 18px 60px rgba(0,0,0,.6); }
html.dark-mode .ci-unemail-h { color: #f0a8b1; border-color: #333a42; }
html.dark-mode .ci-unemail-lead, html.dark-mode .ci-unemail-lbl { color: #aeb6bf; }
html.dark-mode .ci-unemail-note { background: #12161b; border-color: #333a42; color: #e3e7eb; }
html.dark-mode .ci-unemail-foot, html.dark-mode .ci-unemail-body { border-color: #333a42; }
html.dark-mode .ci-unemail-cancel { background: #2a2f36; border-color: #444; color: #cfd4da; }
html.dark-mode .ci-unemail-sent { color: #7bd88a; }
.ci-unemail-preview { margin: 0 0 12px; border: 1px solid #eee; border-radius: 6px; overflow: hidden; }
.ci-unemail-preview-h { font-size: 11px; font-weight: 800; color: #7a1f2b; background: #faf7f7; padding: 6px 10px; border-bottom: 1px solid #eee; }
.ci-unemail-preview-list { list-style: none; margin: 0; padding: 4px 0; max-height: 150px; overflow-y: auto; }
.ci-unemail-preview-list li { font-size: 12px; padding: 3px 10px; color: #333; }
.ci-unemail-preview-st { color: #888; }
.ci-unemail-preview-amt { float: right; font-weight: 700; color: #555; }
html.dark-mode .ci-unemail-preview { border-color: #333a42; }
html.dark-mode .ci-unemail-preview-h { background: #23282f; color: #f0a8b1; border-color: #333a42; }
html.dark-mode .ci-unemail-preview-list li { color: #cfd4da; }
html.dark-mode .ci-unemail-preview-amt { color: #aeb6bf; }
html.dark-mode .ci-fbar-seeall-h { color: #a7d5f2; }
html.dark-mode .ci-fbar-seeall-list li { color: #e3e7eb; border-top-color: #333a42; }
html.dark-mode .ci-fbar-seeall-k { color: #aeb4bd; }
html.dark-mode .ci-fbar-seeall-clear { background: #11151a; border-color: #5a3a41; color: #f0a8b1; }
.ci-fbar-active-n { font-size: 12px; font-weight: 800; color: #9e2436; }
.ci-fbar-clearall { border: 0; background: none; color: #9e2436; font-weight: 700; font-size: 12px; cursor: pointer; text-decoration: underline; }
html.dark-mode .ci-fbar { background: #20262e; border-color: #3a414a; }
html.dark-mode .ci-fbar-lbl { color: #c7ccd2; }
html.dark-mode .ci-fbar-dates input { background: #11151a; border-color: #3a414a; color: #e3e7eb; }
html.dark-mode .ci-fbar-presets button { background: #2a313a; border-color: #4a525c; color: #cfd5db; }
html.dark-mode .ci-card-add { background: #11151a; border-color: #5a3a41; color: #f0a8b1; }
html.dark-mode .ci-card-add:hover { background: #1b2128; }
html.dark-mode .ci-cust-chip { background: #9e2436; border-color: #000; color: #fff; }
/* ---- Tighter, consistent Filters card (smaller fields + "All"/label text, tighter grouping) ---- */
/* Stack the rows as full-width blocks so nothing — especially the date-preset buttons — ever overflows the
   card's right edge (the base `.ci-fbar` is a flex row, which sized the date row to its content and spilled). */
.dash-fbarcard .ci-fbar { display: block; }
.ci-fbar-controls, .ci-fbar-daterow, .ci-fbar-dates, .ci-fbar-presets, .ci-fbar-active, .ci-fbar-fav { min-width: 0; max-width: 100%; }
.ci-fbar-controls { gap: 8px 10px; }
.ci-fbar-f { min-width: 104px; }
.ci-fbar-daterow { gap: 6px 8px; margin-top: 8px; }
.ci-fbar-dates input { width: 116px; height: 32px; font-size: 12px; }
.ci-fbar-presets { gap: 5px; }
.ci-fbar-presets button { padding: 3px 8px; font-size: 11px; }
/* Match the FilterSlicer boxes: strip the native <select> chrome and draw the same ▾ chevron on the right,
   same 32px height / 12px text / padding, so Invoiced is indistinguishable from Territory/Sales Rep/etc. */
.ci-fbar-nativesel { height: 32px; font-size: 12px; padding: 0 24px 0 8px; appearance: none; -webkit-appearance: none; -moz-appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10'%3E%3Cpath fill='%23888' d='M1,3l4,4,4-4z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 9px center; }
.ci-fbar-active { margin-top: 8px; padding-top: 8px; }
/* FilterSlicer boxes inside the card: shorter box, smaller "All" value + field label, tighter label gap —
   so all five fields (incl. the Invoiced native select) match. */
.ci-fbar-controls >>> .l2-f { gap: 2px; }
.ci-fbar-controls >>> .l2-f > span { font-size: 11px; }
.ci-fbar-controls >>> .ms-box { height: 32px; padding: 0 8px; }
.ci-fbar-controls >>> .ms-val { font-size: 12px; }
.ci-fbar-controls >>> .ms-chev { font-size: 10px; }
/* Casing consistency: field labels + the "All" placeholder render as written (title case), so the Invoiced
   label matches Territory/Sales Rep/etc. and every field's "All" matches the Invoiced select's "All". */
.ci-fbar-controls >>> .l2-f > span { text-transform: none; }
.ci-fbar-controls >>> .ms-val { text-transform: none; }
/* QTR / YEAR date-preset popups — small pickers that drop below their button. They live inside the elevated
   Filters card (overflow visible + z-index), so they float above neighboring cards. */
.ci-preset-pop-wrap { position: relative; display: inline-flex; }
/* Active date preset (selected Today/Yest/1W/QTR/YEAR/TRAILING/YTD, or a chosen popup value) stays highlighted:
   red fill, white text, black border — per the user request. Applies to the bar buttons + popup option buttons. */
.ci-fbar-presets button.is-on, .ci-fbar-presets .ci-preset-pop-wrap > button.is-on { background: #9e2436; color: #fff; border-color: #000; }
.ci-fbar-presets button.is-on:hover, .ci-fbar-presets .ci-preset-pop-wrap > button.is-on:hover { background: #84202e; color: #fff; border-color: #000; }
html.dark-mode .ci-fbar-presets button.is-on, html.dark-mode .ci-fbar-presets .ci-preset-pop-wrap > button.is-on { background: #9e2436; color: #fff; border-color: #000; }
.ci-preset-pop { position: absolute; top: calc(100% + 4px); left: 0; z-index: 60; display: flex; flex-wrap: wrap; gap: 4px; width: max-content; max-width: 200px; background: #fff; border: 1px solid #d4d4d8; border-radius: 6px; box-shadow: 0 6px 18px rgba(0,0,0,.14); padding: 6px; }
.ci-preset-pop button { border: 1px solid #d4d4d8; background: #fff; border-radius: 5px; font-size: 11px; padding: 3px 9px; cursor: pointer; color: #333; }
.ci-preset-pop button:hover { border-color: #9e2436; color: #9e2436; }
.ci-preset-pop--year { width: 172px; }
.ci-preset-pop--year button { flex: 0 0 auto; min-width: 28px; text-align: center; padding: 3px 6px; }
html.dark-mode .ci-preset-pop { background: #2a313a; border-color: #4a525c; }
html.dark-mode .ci-preset-pop button { background: #20262e; border-color: #4a525c; color: #cfd5db; }
/* Territory Map card: lay the body out as a column and pin the flag/info footer to the bottom, so the map
   shrinks to fit and its footer is never pushed below the card edge (was scrolling out of view). */
/* Customer ID(s): the rows list FILLS the card height + scrolls internally (no ~4-row cap). Make the body a flex
   column so the .ci-chart → .cb → .cb-list chain grows to the card's height. (The mobile media query overrides
   the body back to auto-height, so mobile still shows every row.) */
.dash-card[data-card-id="customers"] .dash-body { display: flex; flex-direction: column; overflow: hidden; }
.dash-card[data-card-id="customers"] .ci-chart { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; }
.dash-card[data-card-id="map"] .dash-body { display: flex; flex-direction: column; overflow: hidden; }
.dash-card[data-card-id="map"] .ci-mapbody { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; }
.dash-card[data-card-id="map"] .ci-mapbody > :first-child { flex: 1 1 auto; min-height: 0; max-height: calc(100% - 58px); overflow: hidden; }
/* Scale the map SVG to fit the (capped) area instead of overflowing — keeps ~58px reserved for the footer. */
.dash-card[data-card-id="map"] .ci-mapbody > :first-child svg { max-height: 100%; max-width: 100%; height: auto; width: auto; margin: 0 auto; display: block; }
.dash-card[data-card-id="map"] .ci-mapflag { flex: 0 0 auto; margin-top: auto; }

/* Breakdown charts — CSS horizontal bars (no chart lib), fed by the endpoint's
   byCustomer / byPart / byTerritory aggregates; update with the applied filters. */
/* Map card mirrors the Search-page drawing: a small, centered, thin-bordered white box (no header). */
.ci-mappanel { background: #fff; border: 1px solid #e3e3e3; border-radius: 4px; overflow: hidden; max-width: 360px; margin: 4px auto 16px; }
.ci-mapbody { padding: 4px; }
.ci-detailpanel { background: #fff; border: 1px solid #e0d6d8; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,.06); overflow: hidden; margin: 0 0 16px; }
.ci-detailbody { padding: 0 12px 12px; }
/* Deferred Customer Order Details — "Load order details" prompt for broad views (avoids the full-table LOB scan). */
.ci-details-defer { padding: 22px 12px; text-align: center; }
.ci-details-loadbtn { border: 0; background: #b0122b; color: #fff; font-weight: 700; font-size: 13px; border-radius: 8px; padding: 10px 20px; cursor: pointer; }
.ci-details-loadbtn:hover { background: #8e0f22; }
.ci-details-defer-hint { margin: 10px auto 0; font-size: 11.5px; color: #888; max-width: 460px; line-height: 1.4; }
.ci-details-loading { padding: 22px 12px; text-align: center; color: #999; font-size: 12px; }
html.dark-mode .ci-details-defer-hint, html.dark-mode .ci-details-loading { color: #9aa3ab; }
.ci-charts { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 0 0 16px; }
.ci-chart { background: #fff; border: 1px solid #e0d6d8; border-radius: 8px; padding: 0 0 10px; box-shadow: 0 1px 3px rgba(0,0,0,.06); min-width: 0; overflow: hidden; }
/* Part(s) Ordered: make the chart FILL the card so its list scrolls internally (one styled scrollbar) instead of
   the card body ALSO scrolling — kills the double scrollbar. Pairs with .po/.po-list flex rules in PartsOrdered.vue. */
.ci-chart--fill { height: 100%; display: flex; flex-direction: column; }
/* Deep-maroon panel header with white uppercase title — matches the Power BI panels. */
.ci-chart-title { font-size: 12px; font-weight: 700; color: #fff; background: #9e2436; margin: 0 0 10px; padding: 9px 14px; text-transform: uppercase; letter-spacing: .03em; }
.ci-chart-title span { font-weight: 400; text-transform: none; color: #f0cdd3; font-size: 11px; }
.ci-bar-row { padding: 0 14px; }
.ci-bar-row { display: grid; grid-template-columns: 90px 1fr auto; align-items: center; gap: 8px; margin-bottom: 5px; font-size: 11px; }
.ci-bar-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #555; }
.ci-bar-track { background: #f0f0f0; border-radius: 4px; height: 14px; overflow: hidden; }
.ci-bar { height: 100%; border-radius: 4px; min-width: 2px; transition: width .3s ease; }
.ci-bar-cust { background: #8e44ad; }  /* purple — matches the PBI customer bars */
.ci-bar-part { background: #16a085; }  /* teal — matches the PBI parts bars */
.ci-bar-val { white-space: nowrap; color: #333; font-size: 10.5px; }
.ci-bar-pct { color: #999; }
.ci-terr-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 5px; vertical-align: middle; }

.ci-filters { background: #fafafa; border: 1px solid #ececec; border-radius: 10px; padding: 14px; margin-bottom: 14px; }
.ci-filter-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px 14px; }
.ci-f { display: flex; flex-direction: column; font-size: 11px; color: #666; gap: 3px; }
.ci-f span { font-weight: 600; }
.ci-f input, .ci-f select { font-size: 13px; padding: 5px 7px; border: 1px solid #d6d6d6; border-radius: 6px; background: #fff; }
.ci-filter-actions { display: flex; align-items: center; gap: 8px; margin-top: 12px; }
.ci-spacer { flex: 1 1 auto; }
.ci-btn { font-size: 13px; font-weight: 600; border-radius: 6px; padding: 7px 14px; cursor: pointer; border: 1px solid transparent; }
.ci-btn-primary { background: #c0392b; color: #fff; }
.ci-btn-primary:hover { background: #a93226; }
.ci-btn-ghost { background: #fff; border-color: #d0d0d0; color: #444; }
.ci-btn-ghost:hover:not(:disabled) { background: #f0f0f0; }
.ci-btn-export { background: #1f7a3d; color: #fff; }
.ci-btn-export:disabled { opacity: .5; cursor: default; }

.ci-resultmeta { display: flex; align-items: center; font-size: 12px; color: #777; margin-bottom: 6px; }
.ci-pagesize select { margin-left: 4px; font-size: 12px; padding: 2px 4px; border: 1px solid #d6d6d6; border-radius: 4px; }

.ci-tablewrap { overflow-x: auto; border: 1px solid #e6e6e6; border-radius: 8px; }
.ci-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.ci-table thead th { background: #9e2436; text-align: left; padding: 8px 10px; border-bottom: 1px solid #7e1c2b; white-space: nowrap; font-weight: 700; color: #fff; text-transform: uppercase; font-size: 11px; letter-spacing: .02em; }
.ci-table th.is-sortable { cursor: pointer; user-select: none; }
.ci-table th.is-sortable:hover { color: #ffd9df; }
.ci-table th.is-num, .ci-table td.is-num { text-align: right; }
.ci-sortarrow { margin-left: 4px; font-size: 10px; color: #ffd9df; }
.ci-table tbody td { padding: 7px 10px; border-bottom: 1px solid #f0f0f0; vertical-align: top; }
.ci-table tbody tr:hover { background: #fafafa; }
.ci-cust { display: block; font-weight: 600; color: #333; }
.ci-custid { display: block; font-size: 10.5px; color: #999; }
.ci-desc { color: #555; max-width: 240px; }
.ci-pill { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 10px; white-space: nowrap; }
.ci-pill.is-inv { background: #e7f5e8; color: #1f6e23; border: 1px solid #b6e0b9; }
.ci-pill.is-open { background: #fff4e0; color: #8a6d1a; border: 1px solid #f0c36d; }
.ci-empty { text-align: center; color: #999; padding: 28px; }

.ci-pager { display: flex; align-items: center; gap: 8px; justify-content: center; margin-top: 14px; }
.ci-pagenum { font-size: 12px; color: #666; }

@media (max-width: 992px) {
  .ci-kpis { grid-template-columns: repeat(2, 1fr); }
  .ci-filter-grid { grid-template-columns: repeat(2, 1fr); }
  .ci-charts { grid-template-columns: 1fr; }
}
@media (max-width: 576px) {
  .ci-kpis, .ci-filter-grid { grid-template-columns: 1fr; }
}
/* Clickable "Sales by Territory" bar rows */
.ci-bar-row--click { cursor: pointer; border-radius: 5px; transition: background .12s; }
.ci-bar-row--click:hover { background: #f3f4f6; }
.ci-bar-row--click.is-sel { background: #fdecee; box-shadow: inset 0 0 0 1px #9e2436; }
/* Sales Reps card: wider label so rep names fit; small muted territory suffix. */
.ci-reps .ci-bar-row { grid-template-columns: 180px 1fr auto; }
.ci-rep-terr { color: #9aa3ad; font-size: 10px; margin-left: 6px; font-weight: 400; }
html.dark-mode .ci-rep-terr { color: #aeb6bf; }
/* Map card US/World toggle */
.ci-maptoggle { display: flex; gap: 4px; justify-content: flex-end; padding: 0 6px 4px; }
.ci-maptoggle button { font-size: 10.5px; font-weight: 700; padding: 2px 11px; border: 1px solid #d4d4d8; background: #f3f4f6; color: #666; border-radius: 11px; cursor: pointer; line-height: 1.4; }
.ci-maptoggle button.is-on { background: #9e2436; color: #fff; border-color: #9e2436; }
.ci-mapempty { padding: 26px; text-align: center; color: #999; font-size: 12.5px; }
html.dark-mode .ci-maptoggle button { background: #11151a; border-color: #3a414a; color: #aeb6bf; }
html.dark-mode .ci-maptoggle button.is-on { background: #9e2436; color: #fff; border-color: #9e2436; }
/* State-flag panel below the US map (fills the otherwise-empty card space; hover a state or select one). */
.ci-mapflag { display: flex; align-items: center; gap: 10px; min-height: 34px; padding: 5px 10px 6px; margin: 2px 4px 2px; border-top: 1px solid #eee; }
.ci-mapflag-img { width: 40px; height: auto; border: 1px solid #e0e0e0; border-radius: 2px; flex: 0 0 auto; box-shadow: 0 1px 2px rgba(0,0,0,.12); }
.ci-mapflag-info { min-width: 0; line-height: 1.25; }
.ci-mapflag-name { font-size: 12.5px; font-weight: 700; color: #333; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-mapflag-sub { font-size: 11px; color: #777; }
.ci-mapflag-sub--none { color: #aaa; font-style: italic; }
.ci-mapflag-hint { font-size: 11.5px; color: #aaa; font-style: italic; padding: 4px 2px; }
.ci-mapflag-hintnote { font-weight: 400; font-size: 9.5px; color: #aaa; font-style: italic; }
html.dark-mode .ci-mapflag-hintnote { color: #7d858f; }
html.dark-mode .ci-mapflag { border-top-color: #3a414a; }
html.dark-mode .ci-mapflag-name { color: #e3e7eb; }
html.dark-mode .ci-mapflag-sub { color: #aeb6bf; }
html.dark-mode .ci-mapflag-hint, html.dark-mode .ci-mapflag-sub--none { color: #8a929c; }
/* Empty facet card: name the filters narrowing it + let the user remove them inline. */
.ci-empty-lead { font-size: 11px; color: #777; margin-bottom: 6px; line-height: 1.35; }
.ci-empty-chips { display: flex; flex-wrap: wrap; gap: 5px; justify-content: center; }
.ci-empty-chip { font-size: 11px; border: 1px solid #e0b3ba; background: #fdecee; color: #9e2436; border-radius: 12px; padding: 2px 9px; cursor: pointer; font-weight: 600; }
.ci-empty-chip:hover { background: #f8d7dc; }
html.dark-mode .ci-empty-lead { color: #aeb6bf; }
html.dark-mode .ci-empty-chip { background: #3a2227; border-color: #6e3038; color: #f0a8b1; }

/* ===== Draggable / resizable / snap-to-grid dashboard (Layout 1) ===== */
.dash-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 0 0 8px; }
.dash-toolbar-hint { font-size: 12px; color: #999; flex: 1 1 auto; }
.dash-save { border: 1px solid #9e2436; background: #9e2436; color: #fff; font-size: 12px; font-weight: 700; border-radius: 6px; padding: 6px 12px; cursor: pointer; }
.dash-save:hover { background: #861f30; }
.dash-reset { border: 1px solid #d4d4d8; background: #fff; color: #9e2436; border-radius: 8px; padding: 5px 12px; font-size: 12px; font-weight: 700; cursor: pointer; white-space: nowrap; }
.dash-reset:hover { background: #9e2436; color: #fff; border-color: #9e2436; }

/* The grid wrapper needs a little vertical breathing room before the detail panel below. */
.vue-grid-layout { margin: 0 0 16px; }
/* ===== Mobile (< 992px): the report reflows to a fixed single-column stack via MOBILE_LAYOUT/effectiveLayout. ===== */
/* "Ask about your data" mobile card (first in the stack) — reuses .ci-ask styling inside a dash-card. */
.ci-ask--mob { padding: 2px 2px 4px; }
.ci-ask-mob-h { display: flex; align-items: center; justify-content: space-between; gap: 8px; font-weight: 800; font-size: 13px; color: #9e2436; margin: 0 0 6px; }
.ci-ask-mob-hist { max-height: 190px; overflow-y: auto; border: 1px solid #eadfe2; border-radius: 8px; padding: 4px; margin-bottom: 6px; }
/* Remove-a-question "×" (shared by the desktop Recent-questions list + the mobile history). */
.ci-ask-hist-del { flex: 0 0 auto; border: 0; background: none; color: #b0392b; font-size: 14px; line-height: 1; cursor: pointer; padding: 0 4px; }
.ci-ask-hist-del:hover { color: #7d1c2b; }
/* ★ favorite this past question — blue button, yellow star (matches the chat-favorite affordance). */
.ci-ask-hist-fav { border: 0; background: #2f6fb0; border-radius: 5px; line-height: 1; cursor: pointer; }
.ci-ask-hist-fav:hover { background: #285f98; }
.ci-ask-hist-fav.is-faved { opacity: .5; cursor: default; }
.ci-ask-hist-fav-star { color: #ffd23f; font-size: 13px; }
/* Uniform, aligned action columns so ★ · ↻ · × line up on every row (hidden ones still hold their slot).
   Scoped to .ci-ask-hist-item (2 classes) so it beats the later single-class .ci-ask-hist-view rule. */
.ci-ask-hist-item .ci-ask-hist-act { flex: 0 0 26px; width: 26px; height: 24px; box-sizing: border-box; padding: 0; display: inline-flex; align-items: center; justify-content: center; font-size: 13px; }
.ci-ask-hist-item .ci-ask-hist-act.is-hidden { visibility: hidden; }
html.dark-mode .ci-ask-mob-h { color: #a7d5f2; }
html.dark-mode .ci-ask-mob-hist { border-color: #3a414a; }
/* Dark-mode text for the Sales Insights panel's Ask box + Generate-Insights note.
   The .ci-story panel is body-appended (teleported) so the global .ci-wrap dark
   rules miss it — but these scoped rules still match because Vue keeps the data-v
   scope id on teleported content (same as .ci-ask-mob-h above). Panel background
   is themed in global darkmode.css; here we only lighten the dim/white-on-white text. */
html.dark-mode .ci-ai-note { color: #cdd3da; }
html.dark-mode .ci-ask-h { color: #a7d5f2; }
html.dark-mode .ci-ask-input { background: #11151a; color: #e3e7eb; border-color: #3a414a; }
html.dark-mode .ci-ask-input::placeholder { color: #8b939c; }
html.dark-mode .ci-ask-foot { color: #cdd3da; }
html.dark-mode .ci-ask-hist-h { color: #aeb6bf; }
html.dark-mode .ci-ask-hist-q { background: #1f242b; border-color: #3a414a; color: #cdd3da; }
html.dark-mode .ci-ask-hist-q:hover { background: #343c45; }
/* Dark-mode labels for the YoY / Avg Quarterly Sales charts in the SI panel
   (Q1–Q4 left labels + the right-side dollar-value labels + the chart title). */
html.dark-mode .ci-yoy-year { color: #cdd3da; }
html.dark-mode .ci-yoy-year.is-cur { color: #f0a8b1; }
html.dark-mode .ci-yoy-val { color: #e3e7eb; }
html.dark-mode .ci-yoy-label { color: #cdd3da; }
html.dark-mode .ci-yoy-hint { color: #aeb6bf; }
/* Mobile (< 992px): reflow the grid to a flowing single column — every card FULL-WIDTH + AUTO-HEIGHT (sizes to its
   content → no white space when data is sparse, no clipping when dense; count cards get full width so $ values aren't
   squished). Overrides vue-grid-layout's absolute layout (its inline transform/width/height lose to !important).
   Card order = the effectiveLayout (MOBILE_LAYOUT) array order. */
@media (max-width: 991.98px) {
  .ci-wrap >>> .vue-grid-layout { height: auto !important; }
  .ci-wrap >>> .vue-grid-item { position: relative !important; left: auto !important; top: auto !important; transform: none !important; width: 100% !important; height: auto !important; margin: 0 0 12px !important; }
  .ci-wrap >>> .vue-grid-item > .dash-card,
  .ci-wrap >>> .vue-grid-item > .dash-fbarcard { height: auto !important; overflow: visible !important; }
  .ci-wrap >>> .vue-grid-item .dash-body { height: auto !important; overflow: visible !important; }
  /* Territory map: let the SVG size to its own aspect ratio (drop the fixed-card calc/flex height constraints). */
  .ci-wrap >>> .dash-card[data-card-id="map"] .ci-mapbody,
  .ci-wrap >>> .dash-card[data-card-id="map"] .ci-mapbody > :first-child { max-height: none !important; overflow: visible !important; flex: 0 0 auto !important; }
  /* Customer Order Details: the deep nested tables are inherently wide → fit-or-scroll handled in CustomerOrderDetails.vue. */
  .ci-wrap >>> .dash-card[data-card-id="details"] .dash-body { overflow-x: auto !important; }
  /* Machine & Part Codes: cap each column's row list (~20 rows) so the auto-height card isn't excessively tall; the rest scrolls. */
  .ci-wrap >>> .dash-card[data-card-id="codes"] .ci-codes-rows { max-height: 360px !important; }
  /* Filters card — pack fields into rows: From+To on one row, all presets on one row, Status+Invoiced two-up;
     Attribute By full width; active-filter chips collapse behind the "Filters" toggle (caret + pointer here). */
  .ci-wrap >>> .dash-card[data-card-id="filters"] .ci-frl-dates { flex: 1 1 100%; width: 100%; flex-direction: row; align-items: center; gap: 6px; }
  .ci-wrap >>> .dash-card[data-card-id="filters"] .ci-frl-dates .ci-datebox { flex: 1 1 0; min-width: 0; }
  .ci-wrap >>> .dash-card[data-card-id="filters"] .ci-frl-presetrow { display: flex; flex-wrap: wrap; gap: 6px; flex: 1 1 100%; width: 100%; }
  .ci-wrap >>> .dash-card[data-card-id="filters"] .ci-frl-field { flex: 1 1 45%; min-width: 0; width: auto; }
  .ci-wrap >>> .dash-card[data-card-id="filters"] .ci-frl-repbasis { flex: 1 1 100%; }
  .ci-wrap >>> .dash-card[data-card-id="filters"] .ci-frl-chips { flex: 1 1 100%; }
  .ci-wrap >>> .dash-card[data-card-id="filters"] .ci-frl-chips-toggle { cursor: pointer; }
  .ci-wrap >>> .dash-card[data-card-id="filters"] .ci-frl-chips-caret { display: inline; }
}
/* Each pad's card: full-height flex column, white, bordered, rounded, clipped. */
.dash-card { height: 100%; display: flex; flex-direction: column; background: #fff; border: 1px solid #e6e6ea; border-top: 3px solid #9e2436; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,.06); }
/* Newly-placed card: scroll-to + flash red until the user notices it (see flashCard). */
@keyframes ci-card-flash { 0%, 100% { background-color: #fff; box-shadow: 0 0 0 0 rgba(158,36,54,0); } 50% { background-color: #ffd6db; box-shadow: 0 0 0 3px rgba(158,36,54,.9); } }
.dash-card--flash { animation: ci-card-flash .8s ease-in-out 4; }
/* Autosave "✓ Saved" toast (fixed, bottom-center). */
.ci-saved-toast { position: fixed; bottom: 22px; left: 50%; transform: translateX(-50%); z-index: 10060; background: #1f8b4c; color: #fff; font-weight: 800; font-size: 13px; letter-spacing: .03em; padding: 8px 16px; border-radius: 999px; box-shadow: 0 4px 16px rgba(0,0,0,.25); animation: ci-saved-in .18s ease-out; }
@keyframes ci-saved-in { from { opacity: 0; transform: translate(-50%, 8px); } to { opacity: 1; transform: translate(-50%, 0); } }
/* ===== Left FILTER rail + FAVORITES strip (desktop) ===== */
.ci-body-flex { display: flex; align-items: flex-start; gap: 10px; }
.ci-grid-col { flex: 1 1 auto; min-width: 0; }
.ci-filter-rail { flex: 0 0 auto; width: 212px; position: sticky; top: 160px; align-self: flex-start; margin-top: 12px; background: #fff; border: 1px solid #e6e6ea; border-top: 3px solid #9e2436; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,.06); padding: 12px 10px; transition: width .16s ease, padding .16s ease; }
.ci-filter-rail.is-collapsed { width: 16px; padding: 0; }
.ci-filter-rail-handle { position: absolute; top: 8px; right: -12px; width: 24px; height: 24px; border-radius: 50%; border: 1px solid #d4d4d8; background: #fff; color: #9e2436; font-size: 11px; line-height: 1; cursor: pointer; box-shadow: 0 1px 4px rgba(0,0,0,.15); z-index: 3; }
/* Email Insights card collapse arrow — same circular ◀/▶ look as the Filters/Favorites rail handles, inline in the header. */
.dash-collapse-handle { width: 22px; height: 22px; border-radius: 50%; border: 1px solid #d4d4d8; background: #fff; color: #9e2436; font-size: 11px; line-height: 1; cursor: pointer; flex: 0 0 auto; margin-left: 4px; box-shadow: 0 1px 4px rgba(0,0,0,.15); }
.dash-collapse-handle:hover { background: #9e2436; color: #fff; border-color: #9e2436; }
.ci-filter-rail.is-collapsed .ci-filter-rail-handle { right: -3px; }
.ci-filter-rail-body { display: flex; flex-direction: column; gap: 12px; }
.ci-filter-rail-h { font-size: 12px; font-weight: 800; color: #9e2436; text-transform: uppercase; letter-spacing: .04em; }
.ci-frl-dates { display: flex; flex-direction: column; gap: 4px; }
.ci-frl-dates input[type=date] { width: 100%; height: 32px; border: 1px solid #d4d4d8; border-radius: 6px; padding: 0 8px; font-size: 13px; background: #fff; }
.ci-frl-lbl { font-size: 11px; font-weight: 700; color: #777; }
.ci-frl-presets { display: flex; flex-wrap: wrap; gap: 6px; }
.ci-frl-presets button, .ci-frl-presets .ci-preset-pop-wrap > button { height: 28px; padding: 0 10px; border: 1px solid #d4d4d8; background: #f7f7f8; border-radius: 6px; font-size: 12px; font-weight: 700; color: #555; cursor: pointer; }
.ci-frl-presets button.is-on, .ci-frl-presets .ci-preset-pop-wrap > button.is-on { background: #9e2436; color: #fff; border-color: #9e2436; }
/* Dark mode — the filter-rail presets + labels weren't darkened (they use .ci-frl-*, not the old .ci-fbar-*). */
html.dark-mode .ci-frl-presets button, html.dark-mode .ci-frl-presets .ci-preset-pop-wrap > button { background: #2a313a; border-color: #4a525c; color: #cfd5db; }
html.dark-mode .ci-frl-presets button:hover, html.dark-mode .ci-frl-presets .ci-preset-pop-wrap > button:hover { border-color: #9e2436; color: #f0a8b1; }
html.dark-mode .ci-frl-presets button.is-on, html.dark-mode .ci-frl-presets .ci-preset-pop-wrap > button.is-on { background: #9e2436; color: #fff; border-color: #9e2436; }
html.dark-mode .ci-frl-lbl { color: #aeb6bf; }
html.dark-mode .ci-frl-body .l2-f > span { color: #aeb6bf; }
.ci-frl-field { width: 100%; }
.ci-frl-clear { width: 100%; margin-top: 2px; }
/* "Attribute by" rep-basis selector + Original/Current segmented toggle (privileged users, in the rail). */
.ci-frl-repbasis { display: flex; flex-direction: column; gap: 6px; }
.ci-repview { display: flex; }
.ci-repview button { flex: 1 1 0; height: 26px; font-size: 11px; font-weight: 700; letter-spacing: .02em; border: 1px solid #d4d4d8; background: #fff; color: #6b7280; cursor: pointer; padding: 0; }
.ci-repview button:first-child { border-radius: 6px 0 0 6px; }
.ci-repview button:last-child { border-radius: 0 6px 6px 0; border-left: none; }
.ci-repview button.is-on { background: #9e2436; color: #fff; border-color: #9e2436; }
html.dark-mode .ci-repview button { background: #2a2f36; color: #aeb6bf; border-color: #3a3f47; }
html.dark-mode .ci-repview button.is-on { background: #9e2436; color: #fff; border-color: #9e2436; }
/* "Attribute by" ⓘ help — icon + body-appended hover popup written for a sales rep. */
.ci-rbhelp-lbl { display: inline-flex; align-items: center; gap: 4px; }
.ci-rbhelp-ic { display: inline-flex; align-items: center; justify-content: center; font-size: 12px; line-height: 1; color: #9e2436; cursor: help; opacity: .85; }
.ci-rbhelp-ic:hover, .ci-rbhelp-ic:focus { opacity: 1; outline: none; }
.ci-rbhelp-pop { position: fixed; z-index: 100050; width: 328px; max-width: calc(100vw - 16px); background: #fff; color: #333;
  border: 1px solid #e2e2e8; border-top: 3px solid #9e2436; border-radius: 8px; box-shadow: 0 8px 28px rgba(0,0,0,.22);
  padding: 10px 12px 11px; font-size: 12px; line-height: 1.4; }
.ci-rbhelp-h { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .03em; color: #9e2436; margin-bottom: 7px; }
.ci-rbhelp-row { padding: 6px 0; border-top: 1px solid #f0f0f2; }
.ci-rbhelp-row:first-of-type { border-top: none; padding-top: 0; }
.ci-rbhelp-row p { margin: 3px 0 0; color: #555; }
.ci-rbhelp-n { font-weight: 800; color: #222; }
.ci-rbhelp-sub { font-size: 10.5px; color: #888; font-style: italic; margin-left: 3px; }
.ci-rbhelp-def { display: inline-block; margin-left: 6px; font-size: 9.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .03em; color: #1f7a44; background: #e7f6ec; border-radius: 4px; padding: 1px 5px; vertical-align: 1px; }
.ci-rbhelp-toggle { border-top: 2px solid #efeff2; margin-top: 2px; }
html.dark-mode .ci-rbhelp-pop { background: #23272e; color: #d6dae0; border-color: #3a3f47; border-top-color: #9e2436; }
html.dark-mode .ci-rbhelp-row { border-top-color: #333842; }
html.dark-mode .ci-rbhelp-row p { color: #aeb6bf; }
html.dark-mode .ci-rbhelp-n { color: #f0f2f5; }
html.dark-mode .ci-rbhelp-def { color: #7bd88a; background: #16241b; }
html.dark-mode .ci-rbhelp-toggle { border-top-color: #333842; }
/* Favorites — an always-expanded panel, swapped to the far LEFT (order:-1) of the rail, sticky like it. */
.ci-fav-panel { flex: 0 0 auto; order: -1; width: 72px; position: sticky; top: 160px; align-self: flex-start; margin-top: 12px; background: #fff; border: 1px solid #e6e6ea; border-top: 3px solid #e0b400; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,.06); padding: 10px 8px; transition: width .16s ease, padding .16s ease; }
.ci-fav-panel.is-collapsed { width: 16px; padding: 0; overflow: visible; }
.ci-fav-panel.is-collapsed .ci-filter-rail-handle { right: -3px; }
/* KPI icon relocated to the card's title bar (frees the body width so the value/footer text fits). */
.dash-handle-ico { font-size: 14px; line-height: 1; margin-right: 3px; flex: 0 0 auto; }
.ci-kpi--v2 .ci-kpi-main { flex: 1 1 auto; min-width: 0; }
.ci-fav-bar--vert { display: flex; flex-direction: column; align-items: center; gap: 6px; max-height: calc(100vh - 200px); overflow-y: auto; }
/* Filters + Favorites are now resizable GRID CARDS — their bodies flow/wrap to the card's width. */
/* align-content:flex-start packs the wrapped rows to the TOP (no stretch-whitespace when the card is made taller);
   justify-content:flex-start keeps them LEFT. Tight gaps group the controls together. */
.ci-frl-body { display: flex; flex-wrap: wrap; align-items: flex-start; align-content: flex-start; justify-content: flex-start; gap: 6px 12px; padding: 4px 2px; overflow: auto; }
.ci-frl-body .ci-frl-dates { flex: 0 0 auto; width: 160px; }
.ci-frl-body .ci-frl-presets { flex: 0 0 auto; }
.ci-frl-body .ci-frl-field { width: auto; min-width: 150px; }
.ci-frl-body .ci-frl-chips { flex: 1 1 100%; flex-direction: row; flex-wrap: wrap; align-items: center; }
.ci-frl-body .ci-frl-chips-h { flex: 0 0 100%; }
.ci-frl-body .ci-frl-clear { width: auto; align-self: center; }
/* Compact the Filters card controls so the text matches the other cards — the Status slicer (.ms-box) and the
   Invoiced select (.l2-f select) were inheriting the base 38px/14px sizing, which read noticeably larger. */
.ci-frl-body .ci-frl-lbl { font-size: 10.5px; }
.ci-frl-body .ci-frl-dates input[type=date] { height: 30px; font-size: 12px; }
.ci-frl-body .ci-frl-presets button, .ci-frl-body .ci-frl-presets .ci-preset-pop-wrap > button { height: 26px; font-size: 11px; padding: 0 8px; }
.ci-frl-body >>> .l2-f > span { font-size: 10.5px; }
.ci-frl-body >>> .l2-f select, .ci-frl-body >>> .ci-fbar-nativesel { height: 30px; font-size: 12px; }
.ci-frl-body >>> .ms-box { height: 30px; font-size: 12px; padding: 0 8px; }
/* Match the Status slicer's "All" to the Invoiced select's "All": same dark text (not the gray placeholder) + size. */
.ci-frl-body >>> .ms-val, .ci-frl-body >>> .ms-val.ms-ph { font-size: 12px; color: #222; }
html.dark-mode .ci-frl-body >>> .ms-val, html.dark-mode .ci-frl-body >>> .ms-val.ms-ph { color: #e3e7eb; }
.ci-frl-body .ci-fbar-chip, .ci-frl-body .ci-frl-chips-h { font-size: 11px; }
/* Favorites card = icons only. Body is a flex box so the icon bar can fill it and align by orientation. */
.ci-fav-handle .ci-fav-col-btn { margin-left: auto; }   /* no title => push the ★+ save to the right */
.ci-fav-cardbody { padding: 6px 4px; overflow: auto; overscroll-behavior: contain; display: flex; }
.ci-fav-cardbody .ci-fav-bar { display: flex; gap: 8px; flex: 1 1 auto; }
/* Vertical (taller than wide): icons stack, centered across, packed to the TOP. */
.ci-fav--vert .ci-fav-bar { flex-direction: column; align-items: center; justify-content: flex-start; }
/* Horizontal (wider than tall): icons in a row, wrap, LEFT-aligned, vertically MIDDLE. */
.ci-fav--horz .ci-fav-bar { flex-direction: row; flex-wrap: wrap; align-items: center; align-content: center; justify-content: flex-start; }
.ci-fav-cardbody .ci-fav-panel-empty { flex: 1 1 auto; color: #888; font-size: 12px; padding: 6px 4px; line-height: 1.4; }
.ci-fav--vert .ci-fav-panel-empty { text-align: center; }
/* Empty Favorites in a vertical (tall/narrow) card: stack the ★+ save button on TOP and the hint text
   BELOW it, packed to the top — the card body is a flex row, which otherwise puts the text beside the star. */
.ci-fav-cardbody.ci-fav--vert { flex-direction: column; justify-content: flex-start; }
.ci-fav-cardbody.ci-fav--vert > .ci-fav-bar,
.ci-fav-cardbody.ci-fav--vert > .ci-fav-panel-empty { flex: 0 0 auto; width: 100%; }
.ci-fav-cardbody.ci-fav--vert > .ci-fav-panel-empty { margin-top: 8px; }
.ci-fav-panel-head { display: flex; align-items: center; justify-content: center; margin-bottom: 8px; }
.ci-fav-panel-h { font-size: 12px; font-weight: 800; color: #9a7b00; text-transform: uppercase; letter-spacing: .04em; }
.ci-fav-panel-empty-spacer { display: none; }
.ci-fav-panel-empty { color: #888; font-size: 12px; padding: 6px 4px; line-height: 1.4; }
.ci-fav-col-btn { display: inline-flex; align-items: center; gap: 2px; height: 26px; padding: 0 8px; border: 1px solid #e0b400; background: #fffbe6; border-radius: 8px; cursor: pointer; color: #9a7b00; font-weight: 800; }
.ci-fav-col-btn:hover:not(:disabled) { background: #fff3bf; }
.ci-fav-col-btn:disabled { opacity: .5; cursor: default; }
.ci-fav-panel .ci-fav-star { color: #e0b400; font-size: 15px; }
.ci-fav-col-plus { font-size: 13px; }
/* Active-filter chips inside the rail — stacked vertically below Invoiced. Reuses .ci-fbar-chip visuals. */
.ci-frl-chips { display: flex; flex-direction: column; align-items: stretch; gap: 6px; }
.ci-frl-chips-h { font-size: 11px; font-weight: 800; color: #9e2436; text-transform: uppercase; letter-spacing: .04em; }
.ci-frl-chips-h em { font-style: normal; color: #999; }
/* Dark mode: the red "ACTIVE FILTERS" label barely reads on the dark rail — use the chat-favorite blue
   (already bold via font-weight:800). Its count <em> gets a muted blue-gray to match. */
html.dark-mode .ci-frl-chips-h { color: #a7d5f2; }
html.dark-mode .ci-frl-chips-h em { color: #8fb8cf; }
/* The two preset groups wrap in .ci-frl-presetrow: transparent on desktop (display:contents keeps the existing
   card layout), a single flex row on mobile (see the reflow @media). */
.ci-frl-presetrow { display: contents; }
/* Active-filters header as a collapse toggle. Static-looking on desktop (caret hidden, chips always shown);
   the reflow @media makes it a real pointer toggle on mobile. */
.ci-frl-chips-toggle { display: inline-flex; align-items: center; gap: 6px; background: none; border: 0; padding: 0; cursor: default; font: inherit; }
.ci-frl-chips-caret { display: none; font-size: 10px; }
.ci-frl-chips-list { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.ci-frl-chip { align-self: flex-start; max-width: 100%; }
/* Active-filter GROUP (multi-value dimension → one compact pill that expands its values INLINE to unselect).
   Inline (not an absolute menu) so it can never be clipped by .ci-frl-body's overflow:auto. */
.ci-frl-chipgrp { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 4px; max-width: 100%; }
.ci-frl-chipgrp-btn { cursor: pointer; display: inline-flex; align-items: center; gap: 4px; }
.ci-frl-caret { font-size: 9px; line-height: 1; opacity: .8; transition: transform .12s ease; }
.ci-frl-chipgrp.is-open .ci-frl-caret { transform: rotate(180deg); }
/* The expanded value chips: a touch lighter than the pill so the group reads as one unit. */
.ci-frl-chipgrp-val { background: #f5f6f8; border-color: #e3e4e8; }
html.dark-mode .ci-frl-chipgrp-val { background: #2a2f36; border-color: #3a3f47; }
.ci-fav-menu-group { border-top: 1px solid #f0f0f2; margin-top: 4px; padding-top: 2px; }
.ci-fav-menu-grouph { font-size: 12px; font-weight: 800; color: #9e2436; padding: 4px 6px 2px; }
.ci-fav-menu-item { display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%; text-align: left; border: 0; background: none; border-radius: 6px; padding: 6px 8px; cursor: pointer; font-size: 13px; color: #333; }
.ci-fav-menu-item:hover { background: #faf3f4; }
.ci-fav-menu-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-fav-menu-x { flex: 0 0 auto; color: #bbb; font-size: 15px; line-height: 1; }
.ci-fav-menu-x:hover { color: #9e2436; }
/* Filters + Favorites grid card — like .dash-card but WITHOUT overflow:hidden, so the filter dropdown pop-outs
   can extend past the card box instead of being clipped. The inner .ci-fbar drops its own frame (the card
   provides it). */
.dash-fbarcard { height: 100%; display: flex; flex-direction: column; background: #fff; border: 1px solid #e6e6ea; border-top: 3px solid #9e2436; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,.06); overflow: visible; }
.dash-fbarcard .ci-fbar { flex: 1 1 auto; border: 0; border-radius: 0; box-shadow: none; margin: 0; background: transparent; overflow: visible; }
/* Grid items are transform-based stacking contexts, so an open dropdown's pop-out would render BEHIND the
   cards below. Raise the Filters card above its neighbors so its pop-outs float on top. */
/* Raise Filters/Favorites above neighbors (dropdown pop-outs + the follow-scroll pin) and let the pinned card
   translate DOWN past its slot without being clipped by the grid-item box. */
.ci-wrap >>> .vue-grid-item.gi-fbar { z-index: 15; overflow: visible; }
/* Follow-the-scroll pin: the card is translated down (see refreshStickyCards) — a stronger shadow reads as floating. */
.dash-card.is-pinned { box-shadow: 0 10px 26px rgba(0,0,0,.18); }
.dash-handle { display: flex; align-items: center; gap: 7px; cursor: move; background: #f7f7f9; border-bottom: 1px solid #ececef; padding: 6px 10px; user-select: none; flex: none; }
.dash-grip { color: #b3b3bb; font-size: 12px; line-height: 1; letter-spacing: -2px; }
/* Title flexes to fill the handle and WRAPS (never truncates) so it stays readable at any card width. */
.dash-handle-title { font-size: 11px; font-weight: 700; color: #6a6a72; text-transform: uppercase; letter-spacing: .04em; flex: 1 1 auto; min-width: 0; line-height: 1.2; white-space: normal; overflow-wrap: anywhere; }
.dash-body { flex: 1; overflow: auto; min-height: 0; }

/* Cards keep their own internals; strip the wrapper chrome so they fill the pad cleanly. */
.dash-body .ci-kpi { border: 0; border-radius: 0; box-shadow: none; height: 100%; }
.dash-body .ci-chart { border: 0; border-radius: 0; box-shadow: none; padding-bottom: 6px; }
.dash-body .ci-mappanel { border: 0; border-radius: 0; max-width: 100%; margin: 0; }
.dash-body .ci-mapbody { max-width: 100%; }

/* While dragging/resizing, give clear visual feedback. (>>> pierces the library-rendered nodes.) */
.ci-wrap >>> .vue-grid-item.vue-grid-placeholder { background: #9e2436; opacity: .18; border-radius: 8px; }
.ci-wrap >>> .vue-grid-item.vue-draggable-dragging .dash-card { box-shadow: 0 6px 20px rgba(0,0,0,.18); }
/* Reinforce the resize handle so the corner grab target sits above card content on white cards. */
.ci-wrap >>> .vue-grid-item > .vue-resizable-handle { z-index: 3; }

/* ===== AI card builder ===== */
.dash-ai-btn { border: 1px solid #9e2436; background: #fff; color: #9e2436; font-size: 12px; font-weight: 700; border-radius: 8px; padding: 5px 12px; cursor: pointer; white-space: nowrap; }
.dash-ai-btn:hover, .dash-ai-btn.active { background: #9e2436; color: #fff; }
.dash-aiform { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin: 0 0 12px; padding: 10px 12px; background: #fbf4f5; border: 1px solid #e6cfd4; border-radius: 8px; }
.dash-ai-input { flex: 1 1 260px; min-width: 180px; height: 36px; border: 1px solid #d4d4d8; border-radius: 6px; padding: 0 10px; font-size: 13px; background: #fff; }
.dash-ai-input:focus { outline: none; border-color: #9e2436; }
.dash-ai-create { border: 1px solid #9e2436; background: #9e2436; color: #fff; font-size: 12px; font-weight: 700; border-radius: 6px; padding: 8px 16px; cursor: pointer; white-space: nowrap; }
.dash-ai-create:hover:not(:disabled) { background: #861f30; }
.dash-ai-create:disabled { opacity: .5; cursor: default; }
.dash-ai-spinner { width: 16px; height: 16px; border: 2px solid #e6cfd4; border-top-color: #9e2436; border-radius: 50%; animation: dash-ai-spin .7s linear infinite; flex: none; }
@keyframes dash-ai-spin { to { transform: rotate(360deg); } }
.dash-ai-error { color: #c0392b; font-size: 12px; font-weight: 600; flex-basis: 100%; }
/* × remove button on AI pads — sits at the right edge of the drag handle. */
.dash-ai-remove { margin-left: auto; border: 0; background: #ececef; color: #6a2230; width: 18px; height: 18px; border-radius: 50%; font-size: 13px; line-height: 1; cursor: pointer; flex: none; }
.dash-ai-remove:hover { background: #9e2436; color: #fff; }
/* Generic AI card viz */
.ci-aicard { height: 100%; }
.ci-aibars { padding: 10px 14px; }
.ci-aibars .ci-bar-row { margin-bottom: 6px; }
.ci-bar-ai { background: #9e2436; }
.ci-aiempty { color: #999; font-size: 12px; padding: 6px 0; }

/* Machine & Part Codes — one searchable card, four facet columns (Category / Prefix / Description / Part-Desc) */
.ci-codes { display: flex; flex-direction: column; min-height: 0; height: 100%; }
.ci-codes-searchwrap { position: relative; padding: 0 14px 8px; flex: 0 0 auto; }
.ci-codes-search { width: 100%; font-size: 12px; padding: 5px 24px 5px 8px; border: 1px solid #d6d6d6; border-radius: 6px; }
.ci-codes-search:focus { outline: none; border-color: #9e2436; }
.ci-codes-clear { position: absolute; right: 20px; top: 3px; border: 0; background: none; color: #999; font-size: 16px; cursor: pointer; line-height: 1; }
.ci-codes-cols { display: flex; gap: 16px; align-items: flex-start; padding: 0 14px 4px; overflow: hidden; flex: 1 1 auto; min-height: 0; }
.ci-codes-group { flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; min-height: 0; max-height: 100%; }
.ci-codes-gh { font-size: 10.5px; font-weight: 700; color: #9e2436; text-transform: uppercase; letter-spacing: .03em; padding: 2px 0 4px; border-bottom: 1px solid #eee; margin-bottom: 3px; flex: 0 0 auto; }
.ci-codes-rows { overflow-y: auto; min-height: 0; }
.ci-codes-row { display: flex; flex-direction: column; align-items: stretch; gap: 3px; font-size: 11.5px; color: #444; padding: 3px 4px; border-radius: 4px; cursor: pointer; }
.ci-codes-row:hover { background: #fdecee; }
.ci-codes-rowtop { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.ci-codes-val { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-codes-cnt { color: var(--ci-accent, #9e2436); font-variant-numeric: tabular-nums; flex: 0 0 auto; }
.ci-codes-track { height: 8px; background: #eef0f1; border-radius: 3px; overflow: hidden; display: flex; }
.ci-codes-fill { height: 100%; display: flex; min-width: 2px; overflow: hidden; border-radius: 3px; transition: width .3s ease; }
.ci-codes-seg { height: 100%; flex: 0 0 auto; min-width: 1px; }
.ci-codes-seg:hover { filter: brightness(1.12); }
.ci-codes-solid { height: 100%; width: 100%; background: #aeb6bd; }
/* Short-code columns (prefix / category) hug their header width so the count sits right next to the
   code; the long description columns then reclaim the freed width and show more text. */
.ci-codes-group--narrow { flex: 0 0 auto; }
.ci-codes-group--narrow .ci-codes-gh { white-space: nowrap; }
.ci-codes-group--narrow .ci-codes-rowtop { justify-content: flex-start; gap: 10px; }
.ci-codes-group--narrow .ci-codes-val { flex: 0 1 auto; }
html.dark-mode .ci-codes-search { background: #11151a; border-color: #3a414a; color: #e3e7eb; }
html.dark-mode .ci-codes-gh { color: #a7d5f2; border-bottom-color: #3a414a; }
html.dark-mode .ci-codes-row { color: #cdd3da; }
html.dark-mode .ci-codes-track { background: #2a2f36; }
html.dark-mode .ci-codes-row:hover { background: #343c45; }
html.dark-mode .ci-codes-cnt { color: var(--ci-accent, #8a929c); }

/* Master-authored custom cards (content HTML + bound-data viz) */
.dash-cc-edit { margin-left: auto; border: 0; background: #ececef; color: #6a2230; width: 18px; height: 18px; border-radius: 50%; font-size: 11px; line-height: 1; cursor: pointer; flex: none; }
.dash-cc-edit:hover { background: #9e2436; color: #fff; }
.ci-cc-content { padding: 12px 14px; font-size: 13px; color: #333; line-height: 1.5; overflow: auto; height: 100%; word-break: break-word; }
.ci-cc-content :first-child { margin-top: 0; }
.ci-cc-content a { color: #9e2436; }
.ci-cc-content img, .ci-cc-content table { max-width: 100%; }
.ci-cc-tablewrap { height: 100%; overflow: auto; padding: 4px; }
.ci-cc-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.ci-cc-table th { text-align: left; background: #f4f4f5; padding: 5px 8px; position: sticky; top: 0; border-bottom: 1px solid #e4e4e7; white-space: nowrap; }
.ci-cc-table td { padding: 4px 8px; border-bottom: 1px solid #f0f0f2; }

/* ===== AI insights narrative (Sales Insights drawer) ===== */
.ci-ai { margin: 0 0 10px; padding-bottom: 10px; border-bottom: 1px solid #efe7e9; }
.ci-ai-gen {
  width: 100%; border: 1px solid #9e2436; background: #9e2436; color: #fff;
  font-size: 12px; font-weight: 700; border-radius: 8px; padding: 9px 12px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
}
.ci-ai-gen:hover:not(:disabled) { background: #861f30; }
.ci-ai-gen:disabled { opacity: .6; cursor: default; }
.ci-ai-result { background: #fbf4f5; border: 1px solid #e6cfd4; border-radius: 8px; padding: 8px 10px; }
.ci-ai-bar { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 4px; }
.ci-ai-bar-title { font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #9e2436; }
.ci-ai-regen {
  border: 1px solid #9e2436; background: #fff; color: #9e2436; font-size: 11px; font-weight: 700;
  border-radius: 6px; padding: 3px 9px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
}
.ci-ai-regen:hover:not(:disabled) { background: #9e2436; color: #fff; }
.ci-ai-regen:disabled { opacity: .6; cursor: default; }
.ci-ai-present {
  width: 100%; margin: 2px 0 6px; border: 1px solid #9e2436; background: #9e2436; color: #fff;
  font-size: 12px; font-weight: 700; border-radius: 7px; padding: 7px 12px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
}
.ci-ai-present:hover:not(:disabled) { background: #861f30; }
.ci-ai-present:disabled { opacity: .6; cursor: default; }
.ci-ai-stale { font-size: 11px; font-weight: 600; color: #b8860b; margin-bottom: 6px; }
.ci-ai-text { list-style: none; margin: 0; padding: 0; }
.ci-ai-text li { font-size: 12px; color: #333; line-height: 1.5; margin-bottom: 4px; white-space: pre-wrap; }
.ci-ai-text li.is-bullet { padding-left: 14px; position: relative; }
.ci-ai-text li.is-bullet::before { content: "•"; position: absolute; left: 2px; color: #9e2436; font-weight: 700; }
.ci-ai-error { color: #c0392b; font-size: 12px; font-weight: 600; margin-top: 6px; }
.ci-ai-note { font-size: 10.5px; color: #999; line-height: 1.4; margin: 6px 0 0; }
.ci-ai-coach { margin-top: 10px; border-top: 1px dashed #e6cfd4; padding-top: 8px; }
.ci-ai-coach-h { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #0e7c66; margin-bottom: 4px; }
/* Generate button row + 🕘 history toggle */
.ci-ai-genrow { display: flex; gap: 6px; align-items: stretch; }
.ci-ai-genrow .ci-ai-gen { flex: 1 1 auto; }
.ci-ai-histbtn { flex: 0 0 auto; border: 1px solid #9e2436; background: #fff; color: #9e2436; border-radius: 8px; padding: 0 10px; font-size: 13px; cursor: pointer; }
.ci-ai-histbtn:hover, .ci-ai-histbtn.is-on { background: #9e2436; color: #fff; }
.ci-ai-bar-tools { display: flex; align-items: center; gap: 6px; }
.ci-ai-bar-tools .ci-ai-histbtn { padding: 3px 8px; font-size: 12px; border-radius: 6px; }
/* Insights history popup */
.ci-ai-hist { margin: 8px 0; border: 1px solid #e6cfd4; border-radius: 8px; background: #fff; overflow: hidden; }
.ci-ai-hist-h { display: flex; justify-content: space-between; align-items: center; padding: 6px 8px; background: #fbf4f5; font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #9e2436; }
.ci-ai-hist-x { border: 0; background: none; color: #9e2436; font-size: 16px; line-height: 1; cursor: pointer; }
.ci-ai-hist-empty { padding: 10px; font-size: 11.5px; color: #999; }
.ci-ai-hist-wrap { display: flex; max-height: 240px; }
.ci-ai-hist-list { list-style: none; margin: 0; padding: 4px; flex: 0 0 42%; overflow-y: auto; border-right: 1px solid #eee; max-height: 240px; }
.ci-ai-hist-item { display: flex; align-items: center; gap: 6px; font-size: 11px; color: #444; padding: 4px 6px; border-radius: 5px; }
.ci-ai-hist-item:hover, .ci-ai-hist-item.is-hover { background: #fdecee; color: #9e2436; }
.ci-ai-hist-line { flex: 1 1 auto; min-width: 0; cursor: pointer; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ci-ai-hist-present { flex: 0 0 auto; border: 1px solid #9e2436; background: #fff; color: #9e2436; border-radius: 5px; padding: 2px 7px; font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; cursor: pointer; white-space: nowrap; }
.ci-ai-hist-present:hover { background: #9e2436; color: #fff; }
.ci-ai-hist-prev { flex: 1 1 auto; padding: 6px 8px; overflow-y: auto; max-height: 240px; }
.ci-ai-hist-prev-d { font-size: 10px; font-weight: 700; color: #9e2436; margin-bottom: 4px; }
.ci-ai-hist-hint { font-size: 11px; color: #aaa; padding: 8px 2px; }
.ci-ai-hist-tips { margin-top: 6px; border-top: 1px dashed #e6cfd4; padding-top: 5px; }
/* Print-style "Report data" section under the insights */
.ci-ai-data { margin-top: 10px; border-top: 1px dashed #e6cfd4; padding-top: 8px; }
.ci-ai-data-h { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #9e2436; margin-bottom: 6px; }
.ci-ai-data-sub { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: #888; margin: 8px 0 3px; }
.ci-ai-kpis { display: flex; flex-wrap: wrap; gap: 4px 12px; font-size: 11.5px; color: #555; }
.ci-ai-kpis b { color: #9e2436; }
.ci-ai-pwrap { margin: 2px 0; }
.ci-ai-brow { display: flex; align-items: center; gap: 6px; margin: 2px 0; font-size: 10.5px; }
.ci-ai-brow-l { flex: 0 0 38%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #444; }
.ci-ai-brow-t { flex: 1 1 auto; height: 11px; background: #eef0f1; border-radius: 3px; overflow: hidden; }
.ci-ai-brow-b { display: block; height: 100%; border-radius: 3px; min-width: 2px; overflow: hidden; }
.ci-ai-seg { display: block; height: 100%; float: left; }
.ci-ai-pbreak { padding-left: calc(38% + 6px); margin: 0 0 1px; font-size: 9px; color: #888; line-height: 1.55; }
.ci-ai-pchip { white-space: nowrap; margin-right: 9px; }
.ci-ai-pdot { display: inline-block; width: 6px; height: 6px; border-radius: 2px; margin-right: 3px; vertical-align: middle; }
.ci-ai-brow-v { flex: 0 0 auto; color: #666; font-variant-numeric: tabular-nums; }
.ci-ai-spin { width: 13px; height: 13px; border: 2px solid rgba(158, 36, 54, .25); border-top-color: currentColor; border-radius: 50%; animation: dash-ai-spin .7s linear infinite; flex: none; }

/* ===== Ask about your data (Q&A + propose-then-apply) ===== */
.ci-ask { margin: 0 0 10px; padding-bottom: 10px; border-bottom: 1px solid #efe7e9; }
.ci-ask-h { display: flex; align-items: center; justify-content: space-between; gap: 6px; font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #9e2436; margin-bottom: 6px; }
.ci-ask-h .ci-ai-histbtn { padding: 2px 7px; font-size: 12px; border-radius: 6px; }
.ci-ai-hist-ttl { font-weight: 400; opacity: .8; }
/* Print source chooser modal (new report vs a past one) */
.pc-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 10060; display: flex; align-items: center; justify-content: center; padding: 20px; }
.pc-modal { background: #fff; border-radius: 12px; width: 560px; max-width: 94vw; max-height: 88vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 12px 40px rgba(0,0,0,.3); }
.pc-head { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; background: #9e2436; color: #fff; font-weight: 700; font-size: 14px; }
.pc-x { border: 0; background: none; color: #fff; font-size: 20px; line-height: 1; cursor: pointer; }
.pc-body { padding: 16px; overflow-y: auto; }
.pc-new { display: block; width: 100%; text-align: left; border: 1px solid #9e2436; background: #fff; border-radius: 10px; padding: 12px 14px; cursor: pointer; }
.pc-new:hover { background: #fdecee; }
.pc-new-t { display: block; font-size: 14px; font-weight: 700; color: #9e2436; }
.pc-new-s { display: block; font-size: 11.5px; color: #777; margin-top: 2px; }
.pc-or { text-align: center; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: #999; margin: 12px 0 8px; }
.pc-empty { font-size: 12px; color: #999; padding: 8px; text-align: center; }
.pc-wrap { display: flex; border: 1px solid #ececec; border-radius: 8px; overflow: hidden; max-height: 320px; }
.pc-list { list-style: none; margin: 0; padding: 4px; flex: 0 0 44%; overflow-y: auto; border-right: 1px solid #ececec; max-height: 320px; }
.pc-item { font-size: 11.5px; color: #444; padding: 6px 8px; border-radius: 6px; cursor: pointer; }
.pc-item:hover, .pc-item.is-hover { background: #fdecee; color: #9e2436; }
.pc-ttl { font-weight: 400; opacity: .8; }
.pc-prev { flex: 1 1 auto; padding: 8px 10px; overflow-y: auto; max-height: 320px; }
.pc-prev-d { font-size: 11px; font-weight: 700; color: #9e2436; margin-bottom: 6px; }
.pc-prev-hint { font-size: 11px; color: #aaa; margin-top: 8px; }
.ci-ask-row { display: flex; gap: 6px; align-items: stretch; }
.ci-ask-input { flex: 1 1 auto; font-size: 12px; padding: 6px 8px; border: 1px solid #d6d6d6; border-radius: 6px; resize: vertical; font-family: inherit; min-height: 36px; }
.ci-ask-input:focus { outline: none; border-color: #9e2436; }
.ci-ask-go { flex: 0 0 auto; align-self: stretch; background: #9e2436; color: #fff; border: 0; border-radius: 6px; padding: 0 14px; font-size: 12px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 6px; }
.ci-ask-go:hover:not(:disabled) { background: #861f30; }
.ci-ask-go:disabled { opacity: .55; cursor: default; }
/* "That looks like a quote" prompt. Body-appended with the other floating chrome. */
/* Anchored to the TOP, not centred. Centring made the dialog grow in BOTH directions as content
   arrived — and the customer block arrives after the peek returns — so its head slid up under the fixed
   site header and the nav. Growing downward from a fixed top keeps everything that was already readable
   exactly where it was. --qp-top is measured in sizeQuotePrompt() from the app's own fixed-chrome
   height; the 150px fallback only applies before the first measurement. */
.qp-backdrop { position: fixed; inset: 0; background: rgba(20,18,19,.45); z-index: 100050; display: flex;
  align-items: flex-start; justify-content: center; padding: var(--qp-top, 150px) 20px 20px; overflow-y: auto; }
.qp-box { width: 520px; max-width: 100%; background: #fff; border-radius: 12px; border-top: 3px solid #9e2436;
  box-shadow: 0 18px 60px rgba(0,0,0,.35); padding: 16px 18px 14px;
  /* Never taller than what is left below the header — a long suggestion list scrolls inside the box
     rather than running off the bottom of the screen. */
  max-height: calc(100vh - var(--qp-top, 150px) - 32px); overflow-y: auto; }
.qp-h { font-size: 15px; font-weight: 800; color: #9e2436; }
.qp-file { font-size: 11.5px; color: #7a838d; margin: 2px 0 8px; word-break: break-all; }
.qp-p { font-size: 13px; color: #333; margin: 0 0 12px; line-height: 1.5; }
.qp-peek { border-radius: 8px; padding: 8px 11px; margin: 0 0 11px; font-size: 12.5px; line-height: 1.45; }
.qp-peek--busy { background: #f2f3f5; color: #7a838d; }
.qp-peek--ok { background: #f1f8f3; border: 1px solid #bfe0c9; color: #24503a; }
.qp-peek--warn { background: #fdf6e3; border: 1px solid #f0c36d; color: #6b5512; }
.qp-peek-l { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; opacity: .75; }
.qp-peek-cust { font-size: 14px; margin-top: 1px; }
.qp-peek-n { font-size: 11.5px; opacity: .85; margin-top: 2px; }
.qp-peek-hint { font-size: 11px; opacity: .75; margin-top: 4px; }
.qp-yes:disabled { opacity: .55; cursor: default; }
/* The window step: four narrow choices in a row, all-history full width beneath them. */
.qp-winrow { display: flex; gap: 6px; margin: 0 0 8px; }
.qp-win { flex: 1 1 0; padding: 9px 4px; border: 1px solid #cfd6dd; border-radius: 8px; background: #fff;
          font-size: 12.5px; font-weight: 700; color: #2c3e50; cursor: pointer; }
.qp-win:hover { border-color: #c0392b; color: #c0392b; }
.qp-win-all { width: 100%; }
.qp-winnote { font-size: 11px; color: #7a838d; line-height: 1.45; margin: 8px 0 0; }
.qp-btns--back { margin-top: 10px; }
html.dark-mode .qp-win { background: #232a31; border-color: #3a444e; color: #d7dde3; }
html.dark-mode .qp-winnote { color: #9aa3ad; }
html.dark-mode .qp-peek--ok { background: #16281d; border-color: #2f5a3f; color: #a8d8ba; }
html.dark-mode .qp-peek--warn { background: #2c2617; border-color: #5a4a1e; color: #e0cf9a; }
html.dark-mode .qp-peek--busy { background: #232a31; color: #9aa3ad; }
.qp-btns { display: flex; gap: 8px; flex-wrap: wrap; }
.qp-yes { flex: 1 1 240px; border: 0; background: #9e2436; color: #fff; font-weight: 700; font-size: 13px; border-radius: 8px; padding: 9px 14px; cursor: pointer; }
.qp-yes:hover { background: #7a1f2b; }
.qp-no { flex: 1 1 180px; border: 1px solid #d7dce1; background: #fff; color: #5b6470; font-weight: 700; font-size: 13px; border-radius: 8px; padding: 9px 14px; cursor: pointer; }
.qp-no:hover { border-color: #9aa3ad; background: #f7f8f9; }
.qp-sug-h { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #9aa3ad; margin: 14px 0 5px; }
.qp-sug { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
.qp-sug button { width: 100%; text-align: left; border: 1px solid #e6eaee; background: #fbfcfd; color: #333; font-size: 12.5px; border-radius: 7px; padding: 7px 10px; cursor: pointer; }
.qp-sug button:hover { border-color: #9e2436; background: #fdf3f2; color: #9e2436; }
html.dark-mode .qp-box { background: #1f242b; }
html.dark-mode .qp-p { color: #d6dde4; }
html.dark-mode .qp-no { background: #1f242b; border-color: #3a414a; color: #c7cdd4; }
html.dark-mode .qp-sug button { background: #1a1f26; border-color: #3a444e; color: #d6dde4; }

/* ===== Quote vs past orders ===== */
.qr-wrap { margin: 10px 0 4px; border: 1px solid #e2e6ea; border-radius: 8px; overflow: hidden; }
.qr-head { display: flex; align-items: center; gap: 10px; padding: 7px 10px; background: #f7f8f9; border-bottom: 1px solid #e2e6ea; flex-wrap: wrap; }
.qr-title { font-size: 12px; font-weight: 800; color: #9e2436; text-transform: uppercase; letter-spacing: .03em; }
.qr-sub { font-size: 11px; color: #7a838d; flex: 1 1 auto; }
.qr-present { flex: 0 0 auto; border: 1px solid #d7dce1; background: #fff; color: #5b6470; font-size: 11.5px; font-weight: 700; border-radius: 6px; padding: 4px 9px; cursor: pointer; }
.qr-present:hover { border-color: #c0392b; color: #c0392b; background: #fdf3f2; }
.qr-warn { padding: 6px 10px; font-size: 11.5px; background: #fdf6e3; color: #8a6d1a; border-bottom: 1px solid #f0d69a; }
.qr-tbl { width: 100%; border-collapse: collapse; font-size: 12px; }
.qr-tbl thead th { text-align: left; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #7a838d; padding: 5px 8px; border-bottom: 1px solid #e2e6ea; background: #fcfcfd; }
.qr-tbl th.qr-n, .qr-tbl td.qr-n { text-align: right; white-space: nowrap; }
.qr-tbl th.qr-x, .qr-tbl td.qr-x { width: 22px; text-align: center; color: #9aa3ad; }
.qr-row { cursor: pointer; border-bottom: 1px solid #f0f2f4; }
.qr-row:hover { background: #fdf3f2; }
.qr-row > td { padding: 6px 8px; vertical-align: top; }
/* A 3px left rail carries the verdict, so the shape of the quote reads at a glance. */
.qr-row.qr-above > td:first-child { box-shadow: inset 3px 0 0 #c0392b; }
.qr-row.qr-below > td:first-child { box-shadow: inset 3px 0 0 #2f8f5b; }
.qr-row.qr-in-line > td:first-child { box-shadow: inset 3px 0 0 #aeb6bd; }
.qr-part { font-weight: 700; color: #22303a; }
.qr-matched { font-size: 10.5px; color: #7a838d; margin-left: 5px; }
.qr-qty { font-size: 10.5px; color: #7a838d; margin-left: 6px; }
.qr-desc { font-size: 10.5px; color: #7a838d; margin-top: 1px; }
.qr-sub2 { font-size: 10.5px; color: #7a838d; margin-top: 1px; }
.qr-sub2.is-up { color: #c0392b; font-weight: 700; }
.qr-sub2.is-dn { color: #2f8f5b; font-weight: 700; }
.qr-unread { color: #9aa3ad; }
.qr-chip { display: inline-block; font-size: 9.5px; font-weight: 700; border-radius: 8px; padding: 0 6px; margin: 2px 3px 0 0; white-space: nowrap; }
.qr-chip--warn { background: #fdf6e3; color: #8a6d1a; border: 1px solid #f0c36d; }
.qr-chip--new { background: #eef5fc; color: #26527a; border: 1px solid #bcd6ef; }
/* A GAP, not a finding — deliberately a different colour from "never bought" so the two are never
   read as the same thing at a glance. */
.qr-chip--gap { background: #fdf6e3; color: #8a6d1a; border: 1px solid #f0c36d; }
.qr-chip--muted { background: #f2f3f5; color: #7a838d; border: 1px solid #dfe3e8; }
.qr-chip--comp { background: #f4f1fa; color: #4b3f7a; border: 1px solid #cfc6e6; }
.qr-note { padding: 7px 10px; background: #f6f7f9; border-bottom: 1px solid #e3e7ec; font-size: 11.5px; color: #55606b; line-height: 1.5; }
.qr-note-list { margin: 5px 0 0; padding-left: 16px; font-family: Consolas, monospace; font-size: 11px; }
.qr-note--rm { background: #fdf5f4; border-bottom-color: #f0d9d6; color: #7d4a44; }
.qr-fixbtn--rm { color: #a4453c; border-color: #e6c6c2; }
.qr-fixbtn--rm:hover { background: #fdeeec; border-color: #d99d96; }
html.dark-mode .qr-note--rm { background: #322322; border-color: #4d3733; color: #e3b3ad; }
html.dark-mode .qr-fixbtn--rm { color: #e39a92; border-color: #5c403c; }
.qr-note-list li { margin-bottom: 2px; }
html.dark-mode .qr-chip--comp { background: #241f33; color: #c7bce8; border-color: #453a66; }
html.dark-mode .qr-note { background: #232a31; border-color: #3a444e; color: #b9c2cc; }
.qr-gap { padding: 7px 10px; background: #fdf6e3; border-bottom: 1px solid #f0d69a; font-size: 11.5px; color: #6b5512; }
.qr-gap-h { font-weight: 800; margin-bottom: 3px; }
.qr-gap-win { margin-bottom: 4px; font-style: italic; }
/* The window this comparison used, beside its title. */
/* Row-picker rows: the part, its quantity and the price being printed — enough to know what a tick
   leaves out without going back to the table. */
.qr-xlsx { margin-left: 6px; }
.qr-doc-btn { margin-right: 6px; }
/* The quote's own line number: present but quiet — it is a cross-reference, not a finding. */
.qr-lineno { display: inline-block; min-width: 24px; margin-right: 6px; padding: 0 4px; border-radius: 4px;
             background: #eef2f6; color: #5a6b7d; font-size: 10.5px; font-weight: 700; }
html.dark-mode .qr-lineno { background: #2a313a; color: #9fb2c6; }
/* Push these two panels clear of the fixed header — same measured offset the quote dialog uses
   (--qp-top, set by sizeQuotePrompt from the app's OWN body padding, so it cannot drift). */
.ci-xlsx-overlay.qr-below-nav { align-items: flex-start; }
.ci-xlsx-overlay.qr-below-nav > .ci-xlsx-modal { margin-top: var(--qp-top, 96px); }
/* Column headers stay put while the rows scroll (user 2026-08-12). The scroll container is the answer
   card itself, so the sticky offset is 0 — and the header needs its own opaque background, or the rows
   show through it as they pass underneath. */
.qr-tbl thead th { position: sticky; top: 0; z-index: 3; background: #eef2f6;
                   box-shadow: inset 0 -1px 0 #d4dde5; }
html.dark-mode .qr-tbl thead th { background: #2a313a; box-shadow: inset 0 -1px 0 #3a444e; }
/* Printing has no scroll container and no sticky context — and a repeated header on paper comes from
   thead, not from this. */
@media print { .qr-tbl thead th { position: static; } }
.qr-fixbtn { margin-left: 8px; padding: 1px 8px; border: 1px solid #cfd6dd; border-radius: 9px; background: #fff;
             font-size: 10.5px; font-weight: 700; color: #37506b; cursor: pointer; white-space: nowrap; }
.qr-fixbtn:hover { border-color: #c0392b; color: #c0392b; }
/* A rep-supplied number is not a proven one — different colour, always labelled. */
.qr-chip--rep { background: #eef7f1; color: #24503a; border: 1px solid #a8ceb7; }
.qr-rawline { margin-top: 8px; padding: 7px 9px; background: #f7f8fa; border: 1px solid #e3e7ec; border-radius: 6px; }
.qr-rawline-h { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .05em; color: #7a838d; }
.qr-rawline code { display: block; margin-top: 3px; font-size: 11px; white-space: pre-wrap; word-break: break-word; color: #2c3e50; }
.qr-rawline-acts { margin-top: 6px; }
.qr-fix-modal, .qr-doc-modal { max-width: 780px; width: 92vw; }
.qr-fix-read { padding: 9px 11px; background: #f7f8fa; border: 1px solid #e3e7ec; border-radius: 8px; margin-bottom: 10px; }
.qr-fix-lbl { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .05em; color: #7a838d; }
.qr-fix-raw { display: block; margin: 4px 0 6px; font-size: 11.5px; white-space: pre-wrap; word-break: break-word; color: #2c3e50; }
.qr-fix-none { display: block; font-size: 12px; color: #8a6d1a; margin: 4px 0 6px; }
.qr-fix-why { font-size: 11.5px; color: #55606b; margin-top: 3px; line-height: 1.45; }
.qr-fix-form { display: flex; flex-wrap: wrap; gap: 8px 10px; align-items: flex-end; }
.qr-fix-f { display: flex; flex-direction: column; flex: 1 1 200px; font-size: 11px; font-weight: 700; color: #55606b; gap: 3px; }
.qr-fix-f--sm { flex: 0 0 120px; }
.qr-fix-f input { padding: 5px 7px; border: 1px solid #cfd6dd; border-radius: 5px; font-size: 12.5px; font-weight: 400; color: #2c3e50; }
.qr-fix-chk { flex: 1 1 100%; font-size: 12px; color: #2c3e50; display: flex; gap: 6px; align-items: center; }
.qr-fix-hint { flex: 1 1 100%; font-size: 11px; color: #7a838d; line-height: 1.45; }
.qr-fix-err { flex: 1 1 100%; font-size: 12px; color: #c0392b; font-weight: 700; }
.qr-doc-tools { display: flex; gap: 6px; margin-bottom: 8px; }
.qr-doc-tools input { flex: 1 1 auto; padding: 5px 8px; border: 1px solid #cfd6dd; border-radius: 5px; font-size: 12.5px; }
.qr-doc-tools button { padding: 4px 10px; border: 1px solid #cfd6dd; border-radius: 5px; background: #fff; font-size: 11.5px; font-weight: 700; color: #37506b; cursor: pointer; }
.qr-doc-tools button.is-on { background: #37506b; border-color: #37506b; color: #fff; }
.qr-doc-text { max-height: 58vh; overflow: auto; margin: 0; padding: 9px 11px; background: #fbfbfc; border: 1px solid #e3e7ec;
               border-radius: 6px; font-size: 11px; line-height: 1.5; white-space: pre-wrap; word-break: break-word; color: #2c3e50; }
.qr-doc-text mark { background: #ffe08a; color: #2c3e50; }
.qr-doc-rows { max-height: 58vh; overflow: auto; border: 1px solid #e3e7ec; border-radius: 6px; }
.qr-doc-rows table { width: 100%; border-collapse: collapse; font-size: 11px; }
.qr-doc-rows th { position: sticky; top: 0; background: #eef2f6; text-align: left; padding: 4px 6px; font-size: 10px; text-transform: uppercase; }
.qr-doc-rows td { border-top: 1px solid #eef0f3; padding: 4px 6px; vertical-align: top; }
.qr-doc-rows tr.is-item td { background: #f6fbf7; }
.qr-doc-rowtext { font-family: Consolas, monospace; white-space: pre-wrap; word-break: break-word; }
html.dark-mode .qr-fixbtn { background: #232a31; border-color: #3a444e; color: #a9bdd4; }
html.dark-mode .qr-rawline, html.dark-mode .qr-fix-read { background: #232a31; border-color: #3a444e; }
html.dark-mode .qr-rawline code, html.dark-mode .qr-fix-raw { color: #d7dde3; }
html.dark-mode .qr-doc-text { background: #1e242a; border-color: #3a444e; color: #d7dde3; }
html.dark-mode .qr-fix-f input, html.dark-mode .qr-doc-tools input { background: #1e242a; border-color: #3a444e; color: #d7dde3; }
html.dark-mode .qr-doc-rows th { background: #2a313a; color: #cfd6dd; }
html.dark-mode .qr-doc-rows tr.is-item td { background: #1d2a22; }
html.dark-mode .qr-chip--rep { background: #16281d; border-color: #2f5a3f; color: #a8d8ba; }
.qpp-qty { font-style: normal; opacity: .7; margin-left: 6px; font-size: 11.5px; }
.qpp-price { margin-left: 8px; }
.qpp-none { margin-left: 8px; color: #8a6d1a; font-size: 11.5px; }
.ci-xlsx-item.is-unreadable > span { opacity: .75; }
html.dark-mode .qpp-none { color: #e0cf9a; }
.qr-win { margin-left: 8px; padding: 1px 7px; border-radius: 9px; background: #eef2f6; border: 1px solid #d4dde5;
          font-size: 10.5px; font-weight: 700; color: #37506b; white-space: nowrap; }
html.dark-mode .qr-win { background: #232a31; border-color: #3a444e; color: #a9bdd4; }
.qr-gap ul { margin: 0; padding-left: 16px; }
.qr-gap li { margin-bottom: 2px; }
html.dark-mode .qr-gap { background: #2c2617; border-color: #5a4a1e; color: #e0cf9a; }
html.dark-mode .qr-chip--muted { background: #2a313a; color: #9aa3ad; border-color: #3a444e; }
.qr-detail > td { padding: 8px 10px 10px 30px; background: #fafbfc; border-bottom: 1px solid #e2e6ea; }
.qr-note { font-size: 11.5px; color: #4a4f56; line-height: 1.5; }
.qr-flags { margin: 5px 0 0; padding-left: 16px; font-size: 11px; color: #8a6d1a; }
.qr-both { margin-top: 7px; padding: 6px 9px; background: #fff8e8; border: 1px solid #f0c36d; border-radius: 6px; font-size: 11.5px; color: #4a4f56; }
.qr-both-h { font-weight: 700; margin-bottom: 2px; }
.qr-past { margin-top: 8px; }
.qr-past-h { font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #9e2436; margin-bottom: 4px; }
.qr-past-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 4px 12px; font-size: 11.5px; color: #22303a; }
.qr-past-grid > div { display: flex; flex-direction: column; }
.qr-past-grid span { font-size: 9.5px; text-transform: uppercase; letter-spacing: .04em; color: #9aa3ad; }
.qr-past-grid em { font-style: normal; color: #9aa3ad; font-size: 10px; }
.qr-past--none { font-size: 11.5px; color: #7a838d; }
.qr-outside { margin-top: 5px; font-size: 11.5px; color: #c0392b; font-weight: 600; }
html.dark-mode .qr-wrap { border-color: #3a444e; }
html.dark-mode .qr-head { background: #1d232a; border-color: #3a444e; }
html.dark-mode .qr-title { color: #f0a8b1; }
html.dark-mode .qr-present { background: #1f242b; border-color: #3a414a; color: #c7cdd4; }
html.dark-mode .qr-tbl thead th { background: #171b22; color: #9aa3ad; border-color: #3a444e; }
html.dark-mode .qr-row { border-color: #2a313a; }
html.dark-mode .qr-row:hover { background: #2b2126; }
html.dark-mode .qr-part { color: #e8edf2; }
html.dark-mode .qr-detail > td { background: #1a1f26; border-color: #3a444e; }
html.dark-mode .qr-note, html.dark-mode .qr-past-grid { color: #d6dde4; }
html.dark-mode .qr-both { background: #2c2617; border-color: #5a4a1e; color: #d6dde4; }

/* The answer body's own scroll region. Capped against the VIEWPORT, not the content: the hero card is
   pinned with translateY, so its height is what decides whether the bottom of a long answer can ever be
   reached. max() keeps a usable 220px on a short window instead of collapsing to nothing.
   overscroll-behavior: contain stops a wheel at the end of this list from scrolling the report behind it. */
/* --ci-answer-max is MEASURED in sizeAnswerScroll() from where this element actually sits in the
   viewport. A CSS constant cannot know that: the card starts below the site header and the nav, and
   grows as attachment chips are added, so any fixed subtraction is wrong at some zoom level. The
   calc() fallback only applies before the first measurement. */
.ci-ask-answer-scroll { max-height: var(--ci-answer-max, calc(100vh - 300px)); overflow-y: auto; overscroll-behavior: contain; }
.ci-ask-answer { margin-top: 8px; position: relative; padding-bottom: 10px; }
/* Collapsed keeps a slim band so the handle still has a border to sit on. */
.ci-ask-answer.is-collapsed { padding-bottom: 12px; border-bottom: 1px solid #e6eaee; }
.ci-ask-answer-stub { display: flex; align-items: baseline; gap: 8px; padding: 2px 0 6px; }
.ci-ask-answer-stub-t { font-size: 12px; font-weight: 600; color: #5b6470; }
.ci-ask-answer-stub-n { font-size: 11px; color: #9aa3ad; }

/* The handle: a circle straddling the bottom border, centred. translate(-50%, 50%) puts
   its midpoint ON the boundary so it reads as attached to the card rather than floating
   under it. */
.ci-ask-answer-toggle { position: absolute; left: 50%; bottom: 0; transform: translate(-50%, 50%);
  width: 26px; height: 26px; border-radius: 50%; border: 1px solid #d7dce1; background: #fff;
  color: #5b6470; font-size: 13px; line-height: 1; cursor: pointer; z-index: 3;
  display: inline-flex; align-items: center; justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,.10); transition: border-color .12s, color .12s, background .12s; }
.ci-ask-answer-toggle:hover { border-color: #c0392b; color: #c0392b; }
.ci-ask-answer-toggle:focus-visible { outline: 2px solid #c0392b; outline-offset: 2px; }
.ci-ask-answer-toggle.is-collapsed { background: #fdf3f2; border-color: #c0392b; color: #c0392b; }
.ci-ask-apply { margin-top: 8px; padding: 8px; border: 1px solid #f0c9cf; background: #fdf3f4; border-radius: 8px; }
.ci-ask-apply-note { font-size: 11.5px; color: #7a2731; margin-bottom: 6px; font-style: italic; }
.ci-ask-apply-chips { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 8px; }
.ci-ask-chip { font-size: 10.5px; font-weight: 600; background: #fff; color: #9e2436; border: 1px solid #e6b9c0; border-radius: 10px; padding: 2px 8px; }
.ci-ask-apply-actions { display: flex; gap: 6px; align-items: center; }
/* ✓ auto-applied banner: green confirmation + live filter synopsis + ↩ Undo */
.ci-ask-applied { margin-top: 8px; padding: 8px 10px; border: 1px solid #bfe0c6; background: #f2faf4; border-radius: 8px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.ci-ask-applied-check { font-size: 11.5px; font-weight: 700; color: #1e7a34; white-space: nowrap; }
.ci-ask-applied-syn { font-size: 11.5px; color: #33503b; flex: 1 1 auto; min-width: 220px; line-height: 1.45; }
.ci-ask-apply-btn { background: #9e2436; color: #fff; border: 0; border-radius: 6px; padding: 5px 12px; font-size: 11.5px; font-weight: 700; cursor: pointer; }
.ci-ask-apply-btn:hover { background: #861f30; }
.ci-ask-dismiss { background: transparent; color: #888; border: 0; padding: 5px 8px; font-size: 11.5px; cursor: pointer; }
.ci-ask-error { margin-top: 6px; font-size: 11.5px; color: #c0392b; font-weight: 600; }
/* "🎙 Fix microphone" + per-device unblock steps (mobile mic-permission recovery). The panel carries its
   own surface so it reads on the light AND dark hero bands. */
.ci-mic-fixbtn {
  display: inline-block; margin-left: 8px; border: 1px solid #c0392b; background: #c0392b; color: #fff;
  font-size: 11px; font-weight: 800; font-family: inherit; border-radius: 999px; padding: 3px 12px; cursor: pointer;
}
.ci-mic-fixbtn:hover { background: #9e2436; border-color: #9e2436; }
.ci-mic-fixbtn:disabled { opacity: .6; cursor: wait; }
.ci-mic-help {
  margin-top: 8px; background: #fff; border: 1px solid #e6c7c7; border-left: 3px solid #c0392b;
  border-radius: 8px; padding: 10px 12px; font-size: 12px; color: #3a3a3a; box-shadow: 0 2px 8px rgba(0,0,0,.10);
}
.ci-mic-help .ci-mic-fixbtn { margin-left: 0; margin-top: 4px; }
.ci-mic-help-h { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; font-weight: 700; }
.ci-mic-help-x { border: 0; background: none; color: #888; font-size: 14px; line-height: 1; cursor: pointer; padding: 0 2px; }
.ci-mic-help-x:hover { color: #333; }
.ci-mic-help-steps { margin: 6px 0 4px; padding-left: 20px; }
.ci-mic-help-steps li { margin: 3px 0; line-height: 1.45; }
.ci-mic-deeplink { text-decoration: none; margin-right: 6px; }
.ci-mic-ai-tag { display: block; margin-top: 6px; font-size: 10.5px; color: #8a929b; }
/* MOBILE: the ask field gets a row of its OWN; the 🎙/ASK/🕐/×/▶ strip wraps below it (user 2026-08-01
   — the input was squeezed into a sliver beside five buttons on phones). ⚠ The flex ITEM is
   .ci-ask-sugwrap (the typeahead wrapper AROUND the textarea) — the first cut targeted .ci-ask-input,
   which sits INSIDE the wrapper, so nothing wrapped. Applies to every ask row on phones (hero, docked
   chat, the mobile ask card) — they all share the same squeeze. */
@media (max-width: 991.98px) {
  .ci-ask-row { flex-wrap: wrap; }
  .ci-ask-row .ci-ask-sugwrap { flex: 1 1 100%; }
}
html.dark-mode .ci-mic-help { background: #232830; border-color: #4a3038; color: #c3cad2; }
html.dark-mode .ci-mic-help-x { color: #98a1aa; }
html.dark-mode .ci-mic-help-x:hover { color: #e7e9ec; }
/* MOBILE talk-back row: 🔊 Listen + Azure voice picker under the hero answer */
.ci-ask-tts { margin-top: 8px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ci-ask-tts-btn { border: 1px solid #e4c4ca; background: #fff; color: #9e2436; border-radius: 6px; font-size: 11.5px; font-weight: 700; padding: 5px 12px; cursor: pointer; }
.ci-ask-tts-btn:hover:not(:disabled) { background: #fdecee; }
.ci-ask-tts-btn:disabled { opacity: .55; cursor: default; }
.ci-ask-tts-voice { height: 28px; font-size: 11.5px; border: 1px solid #d6d6d6; border-radius: 6px; background: #fff; color: #444; padding: 0 6px; max-width: 150px; }
.ci-ask-tts-err { font-size: 11px; color: #c0392b; font-weight: 600; }
html.dark-mode .ci-ask-tts-btn { background: #2b313a; color: #f0a8b1; border-color: #5a3138; }
html.dark-mode .ci-ask-tts-voice { background: #11151a; color: #e3e7eb; border-color: #3a414a; }
/* Structured RESULT CARD under the hero answer: mini dash-card with sticky-header table + magnitude bars */
.ci-ask-tbl { margin-top: 10px; border: 1px solid #e4e9ee; border-top: 3px solid #9e2436; border-radius: 8px; background: #fff; overflow: hidden; }
.ci-ask-tbl-h { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 7px 10px; background: #faf7f8; border-bottom: 1px solid #eee; }
.ci-ask-tbl-title { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #9e2436; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-ask-tbl-hbtns { display: flex; gap: 6px; flex: 0 1 auto; flex-wrap: wrap; justify-content: flex-end; }
.ci-ask-tbl-export { flex: 0 0 auto; border: 1px solid #2f8f5b; background: #fff; color: #2f8f5b; border-radius: 6px; font-size: 11px; font-weight: 700; padding: 3px 10px; cursor: pointer; }
.ci-ask-tbl-export:hover { background: #ecf7f1; }
.ci-ask-tbl-groupall { flex: 0 0 auto; border: 1px solid #d4a7ad; background: #fff; color: #9e2436; border-radius: 6px; font-size: 11px; font-weight: 700; padding: 3px 10px; cursor: pointer; }
.ci-ask-tbl-groupall:hover { background: #fdecee; }
/* The two AI action buttons are SOLID-filled with white text so they stay legible on any theme
   (light / mid / dark) — the earlier outline style read as faint red-on-white. !important defeats
   any global button-color rule from the shared dark/mid stylesheets. */
.ci-ask-tbl-act { flex: 0 0 auto; border: 1px solid transparent; border-radius: 6px; font-size: 11px; font-weight: 700; padding: 3px 10px; cursor: pointer; white-space: nowrap; }
.ci-ask-tbl-act:disabled { opacity: .6; cursor: default; }
.ci-ask-tbl-gen { background: #9e2436 !important; color: #fff !important; border-color: #9e2436 !important; }
.ci-ask-tbl-gen:hover:not(:disabled) { background: #872030 !important; }
.ci-ask-tbl-present { background: #2f8f5b !important; color: #fff !important; border-color: #2f8f5b !important; }
.ci-ask-tbl-present:hover:not(:disabled) { background: #277a4d !important; }
.ci-ask-tbl th.ci-ask-sortable { cursor: pointer; user-select: none; }
.ci-ask-tbl th.ci-ask-sortable:hover { color: #9e2436; }
.ci-ask-tbl th.is-sorted { color: #9e2436; }
.ci-ask-sort { font-size: 9px; }
/* Grouped breakdown rows: clickable summary row per group + indented detail rows */
.ci-ask-tbl-grp td { background: #f7f2f3; border-top: 1px solid #eee2e4; font-weight: 700; color: #5d2830; cursor: pointer; white-space: nowrap; }
.ci-ask-tbl-grp:hover td { background: #f3eaec; }
.ci-ask-tbl-tgl { display: inline-block; width: 16px; height: 16px; line-height: 14px; text-align: center; border: 1px solid #d4a7ad; border-radius: 4px; margin-right: 7px; color: #9e2436; background: #fff; font-weight: 800; }
.ci-ask-tbl-grp-n { color: #9a8f91; font-weight: 600; font-size: 10.5px; }
.ci-ask-tbl-sub td:first-child { padding-left: 31px; }
.ci-ask-tbl-cap { padding: 5px 11px; font-size: 11px; font-weight: 600; color: #6a5a5c; background: #faf5f6; border-bottom: 1px solid #eee2e4; }
.ci-ask-tbl-scroll { max-height: 340px; overflow: auto; }
.ci-ask-tbl table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
.ci-ask-tbl th { text-align: left; position: sticky; top: 0; z-index: 1; background: #f4f4f5; padding: 5px 8px; border-bottom: 1px solid #e4e4e7; white-space: nowrap; font-size: 10.5px; text-transform: uppercase; letter-spacing: .03em; color: #666; }
.ci-ask-tbl th.is-num, .ci-ask-tbl td.is-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.ci-ask-tbl td { padding: 4px 8px; border-bottom: 1px solid #f2f2f3; color: #333; }
.ci-ask-tbl tr:last-child td { border-bottom: 0; }
.ci-ask-tbl-barth, .ci-ask-tbl-barcell { width: 28%; min-width: 90px; }
.ci-ask-tbl-bar { display: block; height: 10px; border-radius: 3px; min-width: 2px; }
html.dark-mode .ci-ask-tbl { background: #232830; border-color: #3a414a; border-top-color: #9e2436; }
html.dark-mode .ci-ask-tbl-h { background: #1f242b; border-bottom-color: #3a414a; }
html.dark-mode .ci-ask-tbl-cap { background: #1f242b; color: #c7b3b6; border-bottom-color: #3a414a; }
html.dark-mode .ci-ask-tbl-title { color: #a7d5f2; }
html.dark-mode .ci-ask-tbl-export { background: #1f2b22; color: #7fd694; border-color: #35543d; }
html.dark-mode .ci-ask-tbl th { background: #1f242b; color: #aeb6bf; border-bottom-color: #3a414a; }
html.dark-mode .ci-ask-tbl td { color: #cdd3da; border-bottom-color: #2c323b; }
html.dark-mode .ci-ask-tbl-groupall { background: #2b2225; color: #f0a8b1; border-color: #5a3a40; }
html.dark-mode .ci-ask-tbl th.is-sorted { color: #f0a8b1; }
/* The gen/present buttons are solid-filled (white text) in every theme — no per-theme override needed. */
html.dark-mode .ci-ask-tbl-grp td { background: #2a2f37; border-top-color: #3a414a; color: #e6bcc2; }
html.dark-mode .ci-ask-tbl-grp:hover td { background: #313743; }
html.dark-mode .ci-ask-tbl-tgl { background: #232830; border-color: #5a3a40; color: #f0a8b1; }
/* Selected-customer Notations / Specifications chips (hover → the .ci-notepop popover) */
.ci-ask-notes { margin-top: 9px; display: flex; flex-wrap: wrap; gap: 8px; }
.ci-ask-note-chip { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 700; background: #fff; color: #9e2436; border: 1px solid #e6b9c0; border-radius: 12px; padding: 3px 11px; cursor: help; }
.ci-ask-note-chip:hover, .ci-ask-note-chip:focus { background: #fdecee; outline: none; }
.ci-heronote { max-width: 340px; }
/* ===== HERO ASK — centered strip at the very top of the report ("Tell me what you want to see."). ===== */
/* Aligns EDGE-TO-EDGE with the dashboard cards below: the grid uses :margin="[12,12]", so its cards sit
   12px inside the container — the hero gets the same 12px side margins so both share the exact rails. */
.ci-ask-hero { margin: 4px 12px 14px; padding: 12px 14px 10px; border: 1px solid #e4e9ee; border-top: 3px solid #9e2436; border-radius: 10px; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.05); }
.ci-ask-hero .ci-ask-input { font-size: 14px; min-height: 44px; padding: 11px 12px; border-radius: 8px; }
.ci-ask-hero .ci-ask-go { padding: 0 20px; font-size: 13px; border-radius: 8px; }
.ci-ask-hero .ci-ai-histbtn { align-self: stretch; }
.ci-ask-hero .ci-ask-mob-hist { margin-top: 8px; }
/* PINNED CHAT (user 2026-07-31: "the hero chat should always be visible"). NOT position:sticky — that is
   dead on this site (#mainBody is a static overflow-y:auto box while the WINDOW scrolls; the Search page
   documented the same discovery in steele-consistency-fix.css). The bar rides the scroll via the SAME
   translateY pinning the Filters/Favorites cards use — see refreshStickyCards(), which also tucks those
   pinned cards UNDER the chat band. Desktop only: mobile keeps the CHAT edge-tab paradigm. */
.ci-ask-hero { position: relative; }
.ci-ask-hero.is-pinned { z-index: 400; box-shadow: 0 8px 22px rgba(0,0,0,.28); }
/* ORBITING BORDER (user 2026-07-31) — a slow red comet circling the chat. A ::before halo 3px OUTSIDE the
   bar: conic gradient whose start angle is a registered @property animated 0→1turn; the two-layer mask
   keeps only the rim, so the bar's own background, red top bar and theme overrides are untouched. Where
   @property is unsupported the ring just holds still — never a broken bar. */
@property --jcs-hero-orbit { syntax: "<angle>"; inherits: false; initial-value: 0deg; }
.ci-ask-hero::before {
  content: ""; position: absolute; inset: -3px; border-radius: 13px; padding: 3px;
  background: conic-gradient(from var(--jcs-hero-orbit),
    transparent 0turn, transparent .62turn,
    rgba(237,29,36,.35) .74turn, rgba(237,29,36,.9) .87turn, #ffb3a8 .945turn, #fff1ee .965turn,
    rgba(237,29,36,.9) .985turn, transparent 1turn);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask-composite: exclude;
  animation: jcsHeroOrbit 9s linear infinite;
  pointer-events: none;
}
@keyframes jcsHeroOrbit { to { --jcs-hero-orbit: 1turn; } }
/* Deliberately NO prefers-reduced-motion guard here: Windows boxes with "Animation effects" off (common
   on IT/perf-tuned machines — including the requester's) report reduced motion, and the guard silently
   froze the very effect that was asked for. A 9s decorative sweep is gentle, and the ✕ dock hides the
   whole bar for anyone who objects. */
/* ⭐→📧 follow-up chain (save favorite → schedule email digest) */
.ci-ask-follow { margin-top: 10px; display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 8px 10px; background: #fdf7ee; border: 1px solid #ecd9b8; border-radius: 8px; }
.ci-ask-follow-q { font-size: 12.5px; font-weight: 700; color: #6b4e1e; display: inline-flex; align-items: center; gap: 6px; }
/* duplicate-favorite confirm (Overwrite / Save as new / Cancel) */
.ci-ask-follow-dupe { background: #fdf3e0; border-color: #e6b34a; }
.ci-ask-follow-alt { background: #6b7280 !important; }
.ci-ask-follow-alt:hover { background: #565e6b !important; }
/* blue circle + black star — matches the chat-favorite affordance (.ci-fav.is-chat) on the favorites card */
.ci-ask-follow-star { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; width: 19px; height: 19px; border-radius: 50%; background: #a7d5f2; color: #111; font-size: 11px; line-height: 1; }
.ci-ask-follow-name { flex: 1 1 180px; min-width: 140px; font-size: 12px; padding: 5px 8px; border: 1px solid #d6cbb4; border-radius: 6px; font-family: inherit; }
.ci-ask-follow-name:focus { outline: none; border-color: #9e2436; }
/* pointer shown in the old 'ask' grid card (the field itself moved to the hero) */
.ci-ask-moved { font-size: 12px; color: #777; margin: 4px 2px 6px; }
/* MOBILE favorites strip above the hero Ask */
.ci-fav-bar--hero { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; max-width: 860px; margin: 2px auto 8px; padding: 6px 10px; background: #fff; border: 1px solid #e4e9ee; border-radius: 10px; }
.ci-fav-hero-empty { font-size: 11.5px; color: #97a0a9; }
html.dark-mode .ci-fav-bar--hero { background: #232830; border-color: #3a414a; }
/* MOBILE right-edge toggle STACK (CHAT · FILTERS · EMAIL). A fixed flex column pinned just above the global
   TOOLS handle (centered at 50%, 88px tall → top edge 50% - 44px). Container bottom sits 13px above TOOLS top
   (50% - 57px → bottom: 50% + 57px) and a 13px flex gap gives EQUAL spacing between every present tab AND to
   TOOLS — regardless of how many are docked. Mobile only (the whole stack is v-if=isMobile; desktop uses the
   .floating-pill-rail pills). */
/* Container bottom sits 13px above the TOOLS handle's top edge. TOOLS is pinned to 104px border-box (see the
   non-scoped !important rule below) → its top edge is 52px above viewport-center, so the stack bottom is at
   50% - 65px (bottom: 50% + 65px). The 13px flex gap then makes every gap — between tabs AND to TOOLS — equal. */
/* RE-ANCHORED 2026-07-26: the TOOLS handle this stack used to sit above is now hidden (the parked
   rail's own red strip is the affordance), so anchoring to its 104px box left a gap. Centre the
   stack on the viewport instead — it no longer depends on TOOLS existing. */
.ci-edge-stack { position: fixed; right: 0; top: 50%; transform: translateY(-50%); z-index: 9596; display: flex; flex-direction: column; gap: 13px; align-items: flex-end; }
/* RETIRED 2026-07-27 — hidden at EVERY width, not just desktop. These solid-red edge tabs were the
   mobile-only way to restore a docked Chat / Filters / Email panel, from before the right-edge tray
   existed. They are now pure duplicates: the Chat, Filters and Email .ci-rail-pill portals (~line
   1537-1556) carry NO isMobile guard, so they render on phones too and the swipe-out tray reveals
   them. Keeping both meant the old red tab sat on top of the new thin peek strip (and, at z 9596 vs
   the rail's 9590, covered part of the INSIGHTS pill that is now the only way into that drawer).
   The markup is left in place (v-if="isMobile") so the layout/portal wiring is undisturbed. */
.ci-edge-stack { display: none; }
/* Every tab is a FIXED 104px border-box (matches the pinned TOOLS handle) so the stack's gaps stay uniform no
   matter the caption length — 104px is tall enough for the longest label ("FILTERS", 7 chars). box-sizing is
   explicit (the app's global reset is border-box) and letter-spacing is 0 so FILTERS fits without clipping.
   position:static — the .ci-edge-stack lays them out. */
.ci-email-edge-tab { box-sizing: border-box; position: static; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 5px; width: 34px; height: 104px; padding: 10px 2px; margin: 0; border: none; border-top-left-radius: 11px; border-bottom-left-radius: 11px; background: #c0392b; color: #fff; cursor: pointer; box-shadow: -2px 2px 8px rgba(0,0,0,.25); }
.ci-email-edge-arrow { width: 0; height: 0; border-top: 6px solid transparent; border-bottom: 6px solid transparent; border-right: 7px solid #fff; }
.ci-email-edge-txt { writing-mode: vertical-rl; text-orientation: upright; font-size: 9px; font-weight: 700; letter-spacing: 0; line-height: 1; }
/* Collapse (▶) control in the hero row — docks the chat to the CHAT rail pill (desktop) / edge tab (mobile). */
.ci-ask-collapse { flex: 0 0 auto; align-self: stretch; width: 26px; border: 1px solid #e4c4ca; background: #fff; color: #9e2436; border-radius: 6px; font-size: 11px; line-height: 1; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.ci-ask-collapse:hover { background: #fdecee; }
/* ✕ Clear-all control in the hero row — wipes search + filters + results. Neutral gray so it reads as "clear",
   distinct from the red ▶ dock arrow beside it. */
.ci-ask-clear { flex: 0 0 auto; align-self: stretch; width: 26px; border: 1px solid #d9d2c4; background: #fff; color: #8a7f6a; border-radius: 6px; font-size: 12px; line-height: 1; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.ci-ask-clear:hover { background: #f2ede1; color: #6b4e1e; }
/* ▶ collapse control on the Filters card handle — docks it to the Filters rail pill (desktop) / edge tab (mobile). */
.ci-card-collapse { margin-left: auto; border: 1px solid #e4c4ca; background: #fff; color: #9e2436; border-radius: 6px; font-size: 11px; line-height: 1; cursor: pointer; padding: 2px 7px; }
.ci-card-collapse:hover { background: #fdecee; }
html.dark-mode .ci-card-collapse { background: #2b313a; color: #f0a8b1; border-color: #5a3138; }
/* ── DARK-MODE HERO CHAT SKIN (2026-07-26) ────────────────────────────────────────────────────────────
   Near-black bar wearing a soft BLUE halo, a deep-navy input ringed in light blue, and a vivid ASK button.
   The blue is the existing chat-favorite family (#a7d5f2 / #7fbfe8 / #8fd0f0) so the hero matches the blue
   circles + labels. The ASK red is #ed1d24 — the REAL logo red (public/steele-consistency-fix.css
   --steele-red), deliberately NOT a newly-invented red: the maroon #9e2436 goes muddy on near-black, and
   the logo red is both brighter and on-brand. The base red top-stripe becomes part of the blue ring.
   COLOURS / BORDERS / SHADOWS ONLY — no layout, sizing, padding or z-index changes (the hero's height feeds
   the sticky-header math). Dark mode only; light + mid untouched. */
/* KPI card totals in dark mode: the chat blue, matching the hero + favourite circles + section labels.
   Written as a FALLBACK so the territory signal still wins — when the view IS scoped to a single territory,
   accentColor() sets --ci-accent to that territory's admin colour and this blue never applies. It only
   governs the all-territories / Master case, where accentColor() now returns "" (see its comment). */
html.dark-mode .ci-kpi-value { color: var(--ci-accent, #a7d5f2); }

html.dark-mode .ci-ask-hero {
  background: #0b0e13;
  border-color: #7fbfe8;
  border-top-color: #7fbfe8;
  box-shadow: 0 0 0 1px rgba(167,213,242,.35), 0 0 22px rgba(127,191,232,.40);
}
html.dark-mode .ci-ask-hero .ci-ask-input {
  background: #101d33; border-color: #8fd0f0; color: #eaf3fb;
  box-shadow: 0 0 12px rgba(143,208,240,.22);
}
html.dark-mode .ci-ask-hero .ci-ask-input::placeholder { color: #9fb3c9; }
html.dark-mode .ci-ask-hero .ci-ask-input:focus {
  border-color: #a7d5f2;
  box-shadow: 0 0 0 2px rgba(167,213,242,.30), 0 0 16px rgba(143,208,240,.35);
}
html.dark-mode .ci-ask-hero .ci-ask-go { background: #ed1d24; color: #fff; }
html.dark-mode .ci-ask-hero .ci-ask-go:hover:not(:disabled) { background: #ff3b41; }
/* mic · 🕘 · ✕ · ▶ read as ONE dark control cluster (the 🕘 has no dark rule of its own otherwise, so in
   dark mode it was inheriting the LIGHT white/maroon styling). */
html.dark-mode .ci-ask-hero .ci-ask-mic,
html.dark-mode .ci-ask-hero .ci-ai-histbtn,
html.dark-mode .ci-ask-hero .ci-ask-clear,
html.dark-mode .ci-ask-hero .ci-ask-collapse { background: #151b24; border-color: #2f3947; }
html.dark-mode .ci-ask-hero .ci-ask-mic { color: #cfd6de; }
/* :not(.is-on) is load-bearing — this selector is (0,4,1) and the `.ci-ask-mic.is-on` white-glyph rule is
   only (0,3,1), so without the guard, hovering WHILE LISTENING repaints the mic blue on the red fill. That
   is the common path: you click the mic and the pointer stays on it for the whole dictation. */
html.dark-mode .ci-ask-hero .ci-ask-mic:not(.is-on):hover { border-color: #7fbfe8; color: #a7d5f2; }
html.dark-mode .ci-ask-hero .ci-ai-histbtn { color: #cfd6de; }
html.dark-mode .ci-ask-hero .ci-ai-histbtn:hover,
html.dark-mode .ci-ask-hero .ci-ai-histbtn.is-on { background: #ed1d24; border-color: #ed1d24; color: #fff; }
html.dark-mode .ci-ask-hero .ci-ask-clear { color: #b9c1cc; }
html.dark-mode .ci-ask-hero .ci-ask-clear:hover { background: #1d2531; color: #e6ebf1; }
html.dark-mode .ci-ask-hero .ci-ask-collapse { color: #ff6b70; }
html.dark-mode .ci-ask-hero .ci-ask-collapse:hover { background: #1d2531; }
html.dark-mode .ci-ask-follow { background: #2c2a22; border-color: #4a4636; }
html.dark-mode .ci-ask-follow-q { color: #e0c58a; }
html.dark-mode .ci-ask-applied { background: #1f2b22; border-color: #35543d; }
html.dark-mode .ci-ask-applied-check { color: #7fd694; }
html.dark-mode .ci-ask-applied-syn { color: #c3d8c9; }
html.dark-mode .ci-ask-note-chip { background: #2b313a; color: #f0a8b1; border-color: #5a3138; }
html.dark-mode .ci-ask-note-chip:hover, html.dark-mode .ci-ask-note-chip:focus { background: #3a2a2e; }
html.dark-mode .ci-ask-collapse { background: #2b313a; color: #f0a8b1; border-color: #5a3138; }
html.dark-mode .ci-ask-collapse:hover { background: #3a2a2e; }
html.dark-mode .ci-ask-clear { background: #2b313a; color: #b9c1cc; border-color: #47505c; }
html.dark-mode .ci-ask-clear:hover { background: #333c46; color: #e6ebf1; }
html.dark-mode .ci-ask-follow-name { background: #1f242b; border-color: #3a414a; color: #e8ebee; }
html.dark-mode .ci-ask-moved { color: #aab3bd; }
/* Hero Ask typeahead dropdown — anchored under the input; real card values with a dim label. */
.ci-ask-hero { position: relative; z-index: 20; }
.ci-ask-sugwrap { position: relative; flex: 1 1 auto; display: flex; }
.ci-ask-sugwrap .ci-ask-input { width: 100%; }
.ci-ask-sug { position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 1000; background: #fff; border: 1px solid #e4e9ee; border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,.14); max-height: 264px; overflow-y: auto; padding: 4px; }
.ci-ask-sug-item { display: flex; justify-content: space-between; gap: 10px; width: 100%; border: 0; background: none; text-align: left; padding: 7px 9px; border-radius: 6px; cursor: pointer; font-size: 12.5px; color: #333; }
.ci-ask-sug-item:hover, .ci-ask-sug-item.is-sel { background: #fdf3f4; }
.ci-ask-sug-txt { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-ask-sug-lbl { font-size: 10.5px; color: #9e2436; text-transform: uppercase; letter-spacing: .03em; font-weight: 700; flex: 0 0 auto; align-self: center; }
html.dark-mode .ci-ask-sug { background: #232830; border-color: #3a414a; }
html.dark-mode .ci-ask-sug-item { color: #e8ebee; }
html.dark-mode .ci-ask-sug-item:hover, html.dark-mode .ci-ask-sug-item.is-sel { background: #2b313a; }
/* Hero history rows: 🕘 icon left of each ask + a red left line separating entries */
.ci-ask-hero .ci-ask-hist-item { border-left: 3px solid #9e2436; padding: 3px 0 3px 8px; margin-bottom: 6px; border-radius: 2px; }
.ci-ask-hist-ic { flex: 0 0 auto; font-size: 12px; line-height: 1; }
.ci-ask-hist-avg { font-size: 11px; font-weight: 600; color: #6b5b5e; background: #f7eef0; border-radius: 6px; padding: 4px 9px; margin-bottom: 8px; }
.ci-ask-hist-avg strong { color: #9e2436; }
.ci-ask-hist-ms { flex: 0 0 auto; font-size: 10px; color: #9a8a8d; margin: 0 4px; white-space: nowrap; font-variant-numeric: tabular-nums; }
html.dark-mode .ci-ask-hist-avg { background: #2a2130; color: #c9bdc0; }
html.dark-mode .ci-ask-hist-avg strong { color: #f0a8b1; }
.ci-ask-hero .ci-ask-mob-hist { max-height: 320px; }
/* Group-by-sales-rep headers (shared by the Ask history + the Insights history popup) */
.ci-ask-hist-grp { margin-bottom: 10px; }
.ci-ask-hist-grp-h { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #9e2436; margin: 2px 0 5px; padding-bottom: 3px; border-bottom: 1px solid #f0e2e5; }
.ci-ask-hist-you { font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #2f7a4f; background: #e4f3ea; border-radius: 999px; padding: 1px 7px; }
/* Master-only "delete all this user's chat history" — pushed to the far right of the group header */
.ci-ask-hist-grpdel { margin-left: auto; flex: 0 0 auto; border: 1px solid #e3b8bf; background: #fbeef0; color: #9e2436; font-size: 9.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .03em; border-radius: 999px; padding: 2px 9px; cursor: pointer; }
.ci-ask-hist-grpdel:hover { background: #9e2436; border-color: #9e2436; color: #fff; }
html.dark-mode .ci-ask-hist-grpdel { background: rgba(158,36,54,.22); border-color: #7d1c2b; color: #f3c9cf; }
html.dark-mode .ci-ask-hist-grpdel:hover { background: #9e2436; color: #fff; }
.ci-ai-hist-groups { flex: 1 1 auto; min-width: 0; overflow-y: auto; }
html.dark-mode .ci-ask-hist-grp-h { border-bottom-color: #3a414a; color: #a7d5f2; }
html.dark-mode .ci-ask-hist-you { background: rgba(47,122,79,.28); }
/* Dark mode: the red per-question × is nearly invisible on the dark rows — use the chat-favorite blue. */
html.dark-mode .ci-ask-hist-del { color: #a7d5f2; }
html.dark-mode .ci-ask-hist-del:hover { color: #cfe8fa; }

/* ── DARK-MODE RED HIERARCHY (2026-07-26) ─────────────────────────────────────────────────────────────
   Red was marking DECORATION (~20 section headings / micro-labels) far more than ACTION (~6 buttons), so
   it stopped meaning "act here" — and the raw maroon #9e2436 reads poorly on a dark card besides. Every
   static decorative label is demoted to the chat-favorite BLUE (#a7d5f2) so red is freed up for buttons +
   genuine alerts. ONE treatment on purpose: #a7d5f2 is the same blue as .ci-fav.is-chat / .ci-frl-chips-h
   / .ci-ask-hist-grp-h, so all dark-mode section labels read as one consistent tier (no blue-vs-gray mix).
   DELIBERATELY NOT TOUCHED (leave red / leave alone):
     • var(--ci-accent) rules — .ci-kpi-value, .ci-codes-cnt. That variable is set INLINE on .ci-wrap from
       accentColor(), i.e. the TERRITORY's admin color (rpt_SalesTerritory.Color). Neutralizing them would
       destroy a territory-driven signal.
     • Every chart/bar/map color — those are inline styles + SVG fill attributes fed by terrColor(), i.e.
       territory assignments. CSS cannot and must not override them.
     • Primary actions (.ci-favpop-apply, .ci-notify-save, badges/counts) — red is CORRECT there.
     • .ci-chart-title (filled red band) and .ci-edge-label (interactive tab) — structural/affordance.
     • Light + mid mode untouched (mid has light-gray cards where the maroon still reads fine). */
html.dark-mode .ci-fchips-lbl,
html.dark-mode .ci-filter-rail-h,
html.dark-mode .ci-story-block-h,
html.dark-mode .ci-ai-bar-title,
html.dark-mode .ci-ai-data-h,
html.dark-mode .ci-rbhelp-h,
html.dark-mode .ci-tok-sec-h,
html.dark-mode .dash-tools-h,
html.dark-mode .dash-tb-lbl,
html.dark-mode .ci-ask-sug-lbl { color: #a7d5f2; }
/* Voice-input button — pulses brand-red while listening. The glyph is an INLINE SVG vintage microphone,
   not an emoji: 🎤 rendered as an ambiguous dark blob in Segoe UI Emoji (read as a "speaker" at 15px) and
   varied per OS. The SVG is currentColor-only, so it simply inherits `color` in each of the three states —
   maroon on white (light/mid), light gray on the dark control cluster, white on the red listening fill. */
/* Attach (📎) sits with the mic/Ask cluster; the count badge mirrors the mic's is-on treatment. */
/* Matched to .ci-ask-mic below, property for property: the two sit side by side in the same row, and a
   fixed 34px box next to a stretched one reads as a mistake. align-self:stretch is what makes it take the
   row's height instead of guessing at it. position:relative stays — the count badge hangs off it. */
.ci-ask-clip { position: relative; flex: 0 0 auto; align-self: stretch; border: 1px solid #d6d6d6;
  background: #fff; color: #9e2436; border-radius: 8px; padding: 0 12px; font-size: 15px; line-height: 1;
  cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
/* Same 18px box as .ci-ask-mic svg — the two icons must read as a matched pair. */
.ci-ask-clip svg { display: block; width: 18px; height: 18px; }
.ci-ask-clip:hover { border-color: #9e2436; color: #9e2436; }
/* Attached = tinted, not filled: the mic fills solid red while RECORDING, and these two states should
   not look identical when they mean different things. Same red as the mic, though. */
.ci-ask-clip.is-on { border-color: #9e2436; color: #9e2436; background: #fdf3f2; }
.ci-ask-clip-n { position: absolute; top: -5px; right: -5px; min-width: 15px; height: 15px; padding: 0 3px;
  border-radius: 8px; background: #c0392b; color: #fff; font-size: 9px; font-weight: 700; line-height: 15px; }
.ci-ask-file { display: none; }

.ci-ask-files { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; margin: 6px 0 0; }
.ci-ask-file-chip { display: inline-flex; align-items: center; gap: 6px; max-width: 260px; padding: 3px 6px;
  border: 1px solid #e2e6ea; border-radius: 14px; background: #fbfcfd; font-size: 11px; color: #2c3038; }
.ci-ask-file-thumb { width: 22px; height: 22px; object-fit: cover; border-radius: 4px; flex: 0 0 auto; }
.ci-ask-file-ico { font-size: 13px; flex: 0 0 auto; }
.ci-ask-file-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-ask-file-size { color: #9aa3ad; flex: 0 0 auto; }
.ci-ask-file-x { border: 0; background: none; color: #9aa3ad; cursor: pointer; font-size: 11px; padding: 0 1px; }
.ci-ask-file-x:hover { color: #c0392b; }
.ci-ask-file-hint { font-size: 10px; color: #9aa3ad; }
.ci-ask-files-read { display: flex; flex-wrap: wrap; gap: 8px; margin: 4px 0 0; font-size: 10px; color: #6b7b6b; }
.ci-ask-file-note.is-bad { color: #9e2436; }

.ci-ask-mic { flex: 0 0 auto; align-self: stretch; border: 1px solid #d6d6d6; background: #fff; color: #9e2436; border-radius: 8px; padding: 0 12px; font-size: 15px; cursor: pointer; line-height: 1; display: inline-flex; align-items: center; justify-content: center; }
.ci-ask-mic svg { display: block; width: 18px; height: 18px; }
.ci-ask-mic:hover { border-color: #9e2436; }
.ci-ask-mic.is-on { background: #9e2436; border-color: #9e2436; color: #fff; animation: ciMicPulse 1.2s ease-in-out infinite; }
@keyframes ciMicPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(158,36,54,.45); } 50% { box-shadow: 0 0 0 7px rgba(158,36,54,0); } }
html.dark-mode .ci-ask-mic { background: #232830; border-color: #3a414a; color: #cfd6de; }
html.dark-mode .ci-ask-mic.is-on { background: #9e2436; border-color: #9e2436; color: #fff; }
.ci-ask-hist { margin-top: 10px; }
.ci-ask-hist-h { display: flex; justify-content: space-between; align-items: center; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; color: #999; margin-bottom: 4px; }
.ci-ask-hist-clear { background: none; border: 0; color: #aaa; font-size: 10px; cursor: pointer; text-decoration: underline; }
.ci-ask-hist-list { list-style: none; margin: 0; padding: 0; }
.ci-ask-hist-item { display: flex; align-items: center; gap: 4px; margin-bottom: 3px; }
.ci-ask-hist-q { flex: 1 1 auto; text-align: left; background: #f6f7f8; border: 1px solid #ececec; border-radius: 6px; padding: 4px 8px; font-size: 11px; color: #444; cursor: pointer; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-ask-hist-q:hover { background: #eef0f2; }
.ci-ask-hist-view { flex: 0 0 auto; background: #fff; border: 1px solid #e6b9c0; color: #9e2436; border-radius: 6px; padding: 4px 8px; font-size: 11px; cursor: pointer; line-height: 1; }
.ci-ask-foot { margin-top: 8px; font-size: 10px; color: #aaa; line-height: 1.3; }

/* ===== Floating filter "story" panel ===== */
/* Sales Insights — right-side full-height drawer (mirrors the left Filters drawer). */
.ci-story {
  position: fixed; top: 0; right: 0; height: 100%;
  width: 340px; max-width: 92vw;
  z-index: 10001; /* ABOVE the site side-rails (body::before/::after @ z9999 in steele-consistency-fix.css) so the open drawer covers them instead of being cut by them */
  background: #fff;
  border-left: 3px solid #9e2436;
  box-shadow: none; /* was -6px 0 24px rgba(0,0,0,.18): that soft gray band over the page read as a "rail" */
  font-size: 12px;
  display: flex; flex-direction: column;
  transform: translateX(100%);
  transition: transform .22s ease;
}
.ci-story.is-open { transform: translateX(0); }
.ci-story-head {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 12px 14px; background: #9e2436; color: #fff; flex: none;
}
.ci-story-title { font-weight: 700; font-size: 12px; white-space: nowrap; }
.ci-story-tools { display: inline-flex; gap: 4px; }
.ci-story-tool {
  border: 0; background: rgba(255, 255, 255, .18); color: #fff; cursor: pointer;
  width: 22px; height: 22px; border-radius: 5px; font-size: 13px; line-height: 1;
}
.ci-story-tool:hover { background: rgba(255, 255, 255, .32); }
.ci-story-body { padding: 10px 12px 12px; }
.ci-story-sentence { margin: 0 0 8px; color: #333; line-height: 1.5; }
.ci-story-metrics { display: flex; flex-wrap: wrap; gap: 4px 14px; margin-bottom: 8px; color: #555; }
.ci-story-metric b { color: #9e2436; font-size: 13px; }
.ci-story-chips { display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 10px; }
.ci-story-chip {
  display: inline-flex; align-items: center; gap: 4px;
  background: #f3eef0; border: 1px solid #e2d2d6; border-radius: 12px;
  padding: 2px 4px 2px 8px; font-size: 11px; color: #4a3a3d;
}
.ci-story-chip-k { color: #9e2436; font-weight: 700; margin-right: 2px; }
.ci-story-chip-x {
  border: 0; background: #d9c4c8; color: #6a2230; cursor: pointer;
  width: 16px; height: 16px; border-radius: 50%; font-size: 12px; line-height: 1;
}
.ci-story-chip-x:hover { background: #9e2436; color: #fff; }
.ci-story-clear {
  width: 100%; border: 1px solid #9e2436; background: #fff; color: #9e2436;
  border-radius: 6px; padding: 6px; font-size: 11px; font-weight: 700; cursor: pointer;
}
.ci-story-clear:hover { background: #9e2436; color: #fff; }

/* draggable header + scrollable body */
.ci-story-body { flex: 1; overflow-y: auto; }

/* story lines + section blocks */
.ci-story-line { font-size: 11.5px; color: #444; margin-bottom: 6px; display: flex; align-items: center; flex-wrap: wrap; }
.ci-story-line b { color: #333; }
.ci-story-sep { margin: 0 7px; color: #c9c9c9; }
.ci-story-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right: 5px; }
.ci-story-dot.is-inv { background: #2e7d32; }
.ci-story-dot.is-open { background: #c79a00; }
.ci-story-block { border-top: 1px solid #efe7e9; margin-top: 8px; padding-top: 8px; }
.ci-story-block-h { font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #9e2436; margin-bottom: 5px; }

/* YoY mini-bars */
.ci-yoy-row { display: grid; grid-template-columns: 42px 1fr auto; align-items: center; gap: 6px; margin-bottom: 3px; }
.ci-yoy-year { font-size: 11px; color: #777; }
.ci-yoy-year.is-cur { color: #9e2436; font-weight: 700; }
.ci-yoy-track { background: #eef0f1; border-radius: 3px; height: 12px; overflow: hidden; }
.ci-yoy-bar { display: block; height: 100%; background: #c9a3aa; border-radius: 3px; min-width: 2px; }
/* Report-refresh loading banner (spinner + countdown row, then a full-width progress track) */
/* (removed 2026-07-26) .ci-loadbar / -row / -txt / -track / -fill / -spin — the red loading banner. The
   keyframes below MUST STAY: .ci-proc-spin (the processing overlay's spinner) animates with them, so
   deleting them with the rest of the block would silently freeze the overlay spinner. */
@keyframes ci-loadbar-spin { to { transform: rotate(360deg); } }
/* Global PROCESSING overlay — centered circular spinner + "Loading…" during an Ask or a filter refresh. */
/* ⬇ Excel export group-picker modal */
.ci-xlsx-overlay { position: fixed; inset: 0; z-index: 100095; display: flex; align-items: center; justify-content: center; background: rgba(20,18,19,.34); padding: 16px; }
.ci-xlsx-modal { width: 380px; max-width: 94vw; max-height: 86vh; display: flex; flex-direction: column; background: #fff; border-radius: 12px; border-top: 3px solid #1d6f42; box-shadow: 0 16px 48px rgba(0,0,0,.4); overflow: hidden; }
.ci-xlsx-head { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; font-weight: 800; font-size: 15px; color: #1d6f42; border-bottom: 1px solid #eee; }
.ci-xlsx-x { border: 0; background: none; font-size: 16px; line-height: 1; color: #888; cursor: pointer; padding: 2px 4px; }
.ci-xlsx-x:hover { color: #333; }
.ci-xlsx-sub { padding: 10px 16px 4px; font-size: 12px; color: #555; line-height: 1.4; }
.ci-xlsx-tools { display: flex; align-items: center; gap: 8px; padding: 6px 16px 8px; }
.ci-xlsx-tools button { border: 1px solid #cdd6ce; background: #f2f7f3; color: #1d6f42; font-size: 11px; font-weight: 700; border-radius: 999px; padding: 3px 10px; cursor: pointer; }
.ci-xlsx-tools button:hover { background: #1d6f42; border-color: #1d6f42; color: #fff; }
.ci-xlsx-count { margin-left: auto; font-size: 11px; color: #777; }
.ci-xlsx-list { flex: 1 1 auto; overflow-y: auto; list-style: none; margin: 0; padding: 4px 8px 8px; }
.ci-xlsx-item { display: flex; align-items: center; gap: 9px; padding: 7px 10px; border-radius: 7px; font-size: 13px; color: #222; cursor: pointer; }
.ci-xlsx-item:hover { background: #f4f6f4; }
.ci-xlsx-item input { width: 15px; height: 15px; accent-color: #1d6f42; cursor: pointer; }
.ci-xlsx-actions { display: flex; align-items: center; justify-content: flex-end; gap: 10px; padding: 12px 16px; border-top: 1px solid #eee; }
.ci-xlsx-cancel { border: 1px solid #ccc; background: #fff; color: #555; font-weight: 700; border-radius: 8px; padding: 7px 14px; cursor: pointer; }
.ci-xlsx-cancel:hover { background: #f2f2f2; }
.ci-xlsx-go { border: 0; background: #1d6f42; color: #fff; font-weight: 800; border-radius: 8px; padding: 7px 16px; cursor: pointer; }
.ci-xlsx-go:hover { background: #175835; }
.ci-xlsx-go:disabled { background: #b9c7bd; cursor: not-allowed; }
/* "Returning to NC999" confirm — the xlsx modal kit re-skinned to the report red (a company-switch
   warning, not an export). Dark-mode surfaces come from the base .ci-xlsx rules. */
/* 📊 Presentation picker — same kit as the Excel picker, in the presentation button's green. */
.ci-present-modal { border-top-color: #1d6f42; }
.ci-present-head { color: #1d6f42; }
.ci-present-go { background: #1d6f42; }
.ci-present-go:hover { background: #175835; }
.ci-homeprompt { width: 430px; border-top-color: #9e2436; }
.ci-homeprompt-head { color: #9e2436; }
.ci-homeprompt-go { background: #9e2436; }
.ci-homeprompt-go:hover { background: #7f1c2b; }
html.dark-mode .ci-xlsx-modal { background: #232830; color: #e7e9ec; }
html.dark-mode .ci-xlsx-head { color: #6bbf8e; border-bottom-color: #3a414a; }
html.dark-mode .ci-xlsx-sub { color: #b6bcc5; }
html.dark-mode .ci-xlsx-item { color: #e7e9ec; }
html.dark-mode .ci-xlsx-item:hover { background: #2b313a; }
html.dark-mode .ci-xlsx-tools button { background: rgba(29,111,66,.22); border-color: #2f6b47; color: #9ed9b5; }
html.dark-mode .ci-xlsx-actions { border-top-color: #3a414a; }
html.dark-mode .ci-xlsx-cancel { background: #2b313a; border-color: #4a515b; color: #cfd3d9; }
/* Backdrop dims + blurs the busy dashboard so the box reads as a distinct layer (was a faint .28 tint that
   let the dark card blend into the dark grid behind it). */
.ci-proc-overlay { position: fixed; inset: 0; z-index: 100090; display: flex; align-items: center; justify-content: center; background: rgba(15,13,14,.55); backdrop-filter: blur(2.5px); -webkit-backdrop-filter: blur(2.5px); }
/* Full border + red top accent + a soft brand-red glow so the panel's edges are unmistakable in BOTH themes
   (a bare top-stripe + black shadow disappeared on the dark dashboard). */
.ci-proc-box { display: flex; flex-direction: column; align-items: center; gap: 7px; background: #fff; border-radius: 12px; border: 1px solid #e4ccd0; border-top: 4px solid #9e2436; box-shadow: 0 24px 60px rgba(0,0,0,.30), 0 0 0 1px rgba(158,36,54,.12), 0 0 34px rgba(158,36,54,.18); padding: 18px 30px; }
.ci-proc-spin { width: 40px; height: 40px; border: 4px solid #eadfe1; border-top-color: #9e2436; border-radius: 50%; animation: ci-loadbar-spin .8s linear infinite; }
.ci-proc-txt { font-size: 13px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: #9e2436; }
/* date range being searched — compact, above the spinner */
.ci-proc-range { font-size: 11px; font-weight: 600; letter-spacing: .02em; color: #7a6a6d; }
/* spinner is a button: the ✕ sits in its center and stops the in-flight ask/refresh */
.ci-proc-spinbtn { position: relative; width: 44px; height: 44px; padding: 0; border: 0; background: transparent; cursor: pointer; }
.ci-proc-spinbtn .ci-proc-spin { position: absolute; top: 2px; left: 2px; }
.ci-proc-x { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; line-height: 1; color: #9e2436; background: transparent; transition: background .12s ease, color .12s ease, transform .12s ease; }
.ci-proc-spinbtn:hover .ci-proc-x { background: #9e2436; color: #fff; transform: translate(-50%,-50%) scale(1.12); }
.ci-proc-spinbtn:focus-visible .ci-proc-x { background: #9e2436; color: #fff; outline: 2px solid #fff; }
/* estimated time remaining — below "Asking…" */
.ci-proc-eta { font-size: 11px; color: #8a7a7d; letter-spacing: .01em; }
/* PAUSED all-time confirm: the circle is a GREEN ASK button with white "ASK" text (darkens on hover). */
.ci-proc-askbtn { width: 60px; height: 60px; border-radius: 50%; border: 0; background: #2f9e57; color: #fff; font-weight: 800; font-size: 14px; letter-spacing: .06em; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 10px rgba(47,158,87,.4); transition: background .12s ease, transform .12s ease, box-shadow .12s ease; }
.ci-proc-askbtn:hover, .ci-proc-askbtn:focus-visible { background: #278a4b; transform: scale(1.05); box-shadow: 0 3px 14px rgba(47,158,87,.5); outline: none; }
.ci-proc-ask-label { pointer-events: none; }
/* all-time (1990) note + quick-narrow presets — kept to ONE row (nowrap). */
.ci-proc-allnote { margin-top: 6px; max-width: 380px; text-align: center; }
.ci-proc-allnote-txt { font-size: 11px; color: #8a7a7d; line-height: 1.45; }
.ci-proc-allnote-txt strong { color: #9e2436; }
.ci-proc-presets { display: flex; flex-wrap: nowrap; align-items: center; justify-content: center; gap: 6px; margin-top: 8px; }
.ci-proc-presets-lbl { flex: 0 0 auto; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .02em; color: #9a8a8d; }
.ci-proc-presets button { flex: 0 0 auto; height: 26px; padding: 0 9px; border: 1px solid #d4d4d8; background: #f7f7f8; border-radius: 6px; font-size: 12px; font-weight: 700; color: #555; cursor: pointer; white-space: nowrap; }
.ci-proc-presets button:hover { border-color: #9e2436; color: #9e2436; }
/* DARK MODE: the Asking overlay wears the hero Ask bar's blue-glow skin — near-black surface, #7fbfe8 ring,
   soft blue halo — so the loading state reads as the same object the question was typed into. The maroon
   accent family (#9e2436 / #f0a8b1) this used to carry is kept for light + mid mode only. The GREEN ask
   button is deliberately untouched (it's a distinct affordance, not decoration). */
html.dark-mode .ci-proc-overlay { background: rgba(6,10,16,.62); }
html.dark-mode .ci-proc-box { background: #0b0e13; border-color: #7fbfe8; border-top-color: #7fbfe8; box-shadow: 0 24px 60px rgba(0,0,0,.6), 0 0 0 1px rgba(167,213,242,.35), 0 0 28px rgba(127,191,232,.45); }
html.dark-mode .ci-proc-spin { border-color: #24303f; border-top-color: #a7d5f2; }
html.dark-mode .ci-proc-txt { color: #a7d5f2; }
html.dark-mode .ci-proc-range { color: #9fb0c2; }
html.dark-mode .ci-proc-x { color: #a7d5f2; }
/* ✕ = cancel the in-flight ask, so the HOVER/FOCUS state stays red (destructive) even though the resting
   glyph now sits in the blue family. */
html.dark-mode .ci-proc-spinbtn:hover .ci-proc-x, html.dark-mode .ci-proc-spinbtn:focus-visible .ci-proc-x { background: #c0392b; color: #fff; }
html.dark-mode .ci-proc-eta { color: #93a3b4; }
html.dark-mode .ci-proc-presets button { background: #101d33; border-color: #3a5a78; color: #dce9f4; }
html.dark-mode .ci-proc-presets button:hover { border-color: #7fbfe8; color: #a7d5f2; }
html.dark-mode .ci-proc-askbtn { background: #2fa862; color: #fff; box-shadow: 0 2px 12px rgba(47,168,98,.45); }
html.dark-mode .ci-proc-askbtn:hover, html.dark-mode .ci-proc-askbtn:focus-visible { background: #38ba6f; color: #fff; }
html.dark-mode .ci-proc-allnote-txt { color: #c3ced9; }
html.dark-mode .ci-proc-allnote-txt strong { color: #a7d5f2; }
html.dark-mode .ci-proc-presets-lbl { color: #9fb0c2; }
/* Saved-carts click-through popup */
.ci-sc-click { cursor: pointer; border-radius: 6px; transition: background .12s; }
.ci-sc-click:hover { background: #f3f4f6; }
.ci-cc-content >>> .ci-sc-tok { cursor: pointer; color: #9e2436; font-weight: 700; text-decoration: underline dotted; }
.ci-cc-content >>> .ci-sc-tok:hover { text-decoration: underline; }
.sc-backdrop { position: fixed; inset: 0; background: rgba(20,18,19,.5); z-index: 100000; display: flex; align-items: center; justify-content: center; padding: 28px 14px; }
.sc-modal { width: 640px; max-width: 100%; max-height: calc(100vh - 56px); background: #fff; border-radius: 12px; box-shadow: 0 18px 60px rgba(0,0,0,.35); display: flex; flex-direction: column; }
.sc-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 14px 18px; border-bottom: 1px solid #eee; font-size: 16px; font-weight: 800; color: #9e2436; }
.sc-x { border: 0; background: none; font-size: 22px; line-height: 1; color: #999; cursor: pointer; padding: 0 4px; }
.sc-x:hover { color: #333; }
.sc-body { padding: 14px 18px; overflow-y: auto; }
.sc-empty { color: #999; font-size: 13px; padding: 16px; text-align: center; }
.sc-repgroup { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; margin: 12px 0 6px; padding: 6px 10px; border-left: 4px solid var(--sc-rep-rail, #c0392b); background: #faf7f8; border-radius: 0 6px 6px 0; cursor: pointer; user-select: none; transition: background .12s ease; }
.sc-repgroup:hover { background: #f3e8ea; }
.sc-repgroup:focus-visible { outline: 2px solid var(--sc-rep-rail, #c0392b); outline-offset: 1px; }
.sc-repgroup--open { background: #f3e8ea; }
.sc-repgroup:first-child { margin-top: 2px; }
.sc-repgroup-caret { color: var(--sc-rep-rail, #c0392b); font-size: 11px; line-height: 1; width: 10px; flex: 0 0 auto; }
.sc-repgroup-rep { font-weight: 800; font-size: 13px; color: #7a1f2b; }
.sc-repgroup-terr { font-size: 11px; color: #888; }
.sc-repgroup-meta { margin-left: auto; font-size: 11.5px; color: #666; font-variant-numeric: tabular-nums; white-space: nowrap; }
html.dark-mode .sc-repgroup { background: #262b31; }
html.dark-mode .sc-repgroup:hover, html.dark-mode .sc-repgroup--open { background: #30363d; }
html.dark-mode .sc-repgroup-rep { color: #f0a8b1; }
html.dark-mode .sc-repgroup-terr { color: #9aa3ab; }
html.dark-mode .sc-repgroup-meta { color: #aeb6bf; }
.sc-cart { border: 1px solid #ece3e5; border-radius: 8px; padding: 8px 10px; margin-bottom: 8px; }
.sc-cart-h { display: flex; align-items: center; gap: 8px; margin-bottom: 5px; flex-wrap: wrap; }
.sc-cust { font-weight: 700; color: #333; font-size: 13px; }
.sc-cust-id { font-size: 11px; color: #999; }
.sc-note { margin: 2px 0 8px; padding: 7px 9px; border: 1px solid #f0d69a; border-left: 3px solid #d9a441; background: #fdf6e3; border-radius: 6px; display: flex; flex-direction: column; gap: 4px; }
.sc-note-lbl { font-weight: 800; color: #8a6d1a; font-size: 10.5px; }
.sc-note-toggle { display: flex; align-items: center; gap: 5px; width: 100%; border: 0; background: none; cursor: pointer; text-align: left; padding: 0; font: inherit; }
.sc-note-caret { font-size: 9px; opacity: .7; }
.sc-note-txt { white-space: pre-wrap; word-break: break-word; color: #5a4a1e; font-size: 12px; line-height: 1.4; }
html.dark-mode .sc-note { background: #2c2617; border-color: #5a4a1e; border-left-color: #d9a441; }
html.dark-mode .sc-note-lbl { color: #e6c65f; }
html.dark-mode .sc-note-txt { color: #cbbfa4; }
.sc-notewrap { display: flex; flex-direction: column; }
.sc-spec { border-color: #bcd6ef; border-left-color: #2f6fb0; background: #eef5fc; }
.sc-spec .sc-note-lbl { color: #245a91; }
.sc-spec .sc-note-txt { color: #274a63; }
.sc-persp-btn { align-self: flex-start; border: 1px solid #e0c48a; background: #fff8e8; color: #8a6d1a; font-weight: 700; font-size: 11px; border-radius: 6px; padding: 3px 9px; cursor: pointer; margin: 0 0 8px; }
.sc-persp-btn:hover { background: #fdefc8; }
html.dark-mode .sc-spec { background: #16222c; border-color: #2f4a63; border-left-color: #4a90d9; }
html.dark-mode .sc-spec .sc-note-lbl { color: #8fc0ef; }
html.dark-mode .sc-spec .sc-note-txt { color: #b9d4ea; }
html.dark-mode .sc-persp-btn { background: #2c2617; border-color: #5a4a1e; color: #e6c65f; }
.sc-meta { margin-left: auto; font-size: 11.5px; color: #555; font-weight: 700; }
/* Item rows share ONE grid (part code | description | 💡 | qty | price) so the columns line up down the whole
   list. Each row was previously its own grid, which is why the 💡 / ×qty / price looked ragged. `display:contents`
   lets each <li>'s cells join the parent grid; max-content sizes the code/qty/price columns to their widest value
   in the cart so they align, and minmax(0,1fr) lets the description fill + ellipsize. */
.sc-items { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: max-content minmax(0,1fr) 18px max-content max-content; column-gap: 10px; row-gap: 3px; align-items: center; font-size: 12px; color: #555; }
.sc-items > li { display: contents; }
.sc-items > li.sc-noitems { display: block; grid-column: 1 / -1; }
.sc-pn { font-weight: 600; color: #333; }
/* 💡 per-part insight trigger (its own grid cell so rows with/without it stay aligned) */
.sc-insight-btn { border: 0; background: none; padding: 0 2px; font-size: 13px; line-height: 1; cursor: pointer; opacity: .8; filter: grayscale(.15); transition: transform .08s ease, opacity .08s ease; }
.sc-insight-btn:hover { opacity: 1; transform: scale(1.18); filter: none; }
.sc-ins-slot { display: inline-block; width: 0; }
.sc-pname { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #777; }
.sc-qty { color: #999; justify-self: end; text-align: right; }
.sc-price { font-variant-numeric: tabular-nums; justify-self: end; text-align: right; }
.sc-noitems { color: #999; font-style: italic; }
.sc-po { font-size: 11px; color: #9e2436; font-weight: 600; }

/* Dark mode for the Saved Carts breakdown popup. */
html.dark-mode .sc-modal { background: #1f242b; box-shadow: 0 18px 60px rgba(0,0,0,.6); }
html.dark-mode .sc-head { border-bottom-color: #3a414a; color: #f0a8b1; }
html.dark-mode .sc-x { color: #8a929c; }
html.dark-mode .sc-x:hover { color: #e3e7eb; }
html.dark-mode .sc-empty { color: #8a929c; }
html.dark-mode .sc-cart { background: #232a31; border-color: #3a414a; }
html.dark-mode .sc-cust { color: #e3e7eb; }
html.dark-mode .sc-cust-id { color: #8a929c; }
html.dark-mode .sc-meta { color: #aeb6bf; }
html.dark-mode .sc-po { color: #f0a8b1; }
html.dark-mode .sc-items li { color: #cdd3da; border-top-color: #3a414a; }
html.dark-mode .sc-pn { color: #e3e7eb; }
html.dark-mode .sc-pname { color: #aeb6bf; }
html.dark-mode .sc-qty { color: #8a929c; }
html.dark-mode .sc-noitems { color: #8a929c; }
.sc-ins-loading { font-size: 11.5px; color: #9e2436; background: #faf3f4; border: 1px solid #f0e0e3; border-radius: 6px; padding: 5px 9px; margin-bottom: 8px; display: inline-block; }
html.dark-mode .sc-ins-loading { color: #f0a8b1; background: #2a1f22; border-color: #4a3a3e; }
/* ── Per-part AI sales-insight popover (💡 hover) ── */
.sc-ins-pop { position: fixed; z-index: 100010; width: 300px; max-width: 92vw; background: #fff; color: #222; border: 1px solid #e6dcde; border-radius: 10px; box-shadow: 0 14px 44px rgba(0,0,0,.30); padding: 11px 13px 9px; font-size: 12.5px; }
.sc-ins-pop::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: #9e2436; border-radius: 10px 10px 0 0; }
.sc-ins-head { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
.sc-ins-ico { font-size: 14px; }
.sc-ins-title { font-weight: 800; color: #333; font-size: 13px; }
.sc-ins-headline { font-weight: 800; color: #9e2436; font-size: 12.5px; margin-bottom: 3px; }
.sc-ins-pitch { color: #555; line-height: 1.42; margin-bottom: 7px; }
.sc-ins-sec { margin-top: 7px; }
.sc-ins-sec-h { font-size: 10.5px; text-transform: uppercase; letter-spacing: .03em; color: #9a9aa2; font-weight: 700; margin-bottom: 4px; }
.sc-ins-chip { display: inline-flex; align-items: center; gap: 5px; max-width: 100%; border: 1px solid #e2c9ce; background: #fff; color: #6a2b34; border-radius: 999px; padding: 2px 9px 2px 3px; margin: 0 4px 4px 0; font-size: 11px; cursor: pointer; text-align: left; }
.sc-ins-chip:hover { background: #faf3f4; border-color: #9e2436; }
.sc-ins-pct { background: #9e2436; color: #fff; border-radius: 999px; padding: 1px 6px; font-size: 10.5px; font-weight: 700; }
.sc-ins-chip-pn { font-weight: 700; }
.sc-ins-chip-nm { color: #999; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 150px; }
.sc-ins-stats { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 8px; }
.sc-ins-stat { display: inline-flex; align-items: center; background: #f5f0f1; color: #555; border-radius: 6px; padding: 2px 7px; font-size: 11px; font-weight: 600; }
.sc-ins-stat.is-due { background: #fff2e0; color: #a15a12; }
.sc-ins-stat.is-up { background: #e7f4ec; color: #1e7d34; }
.sc-ins-stat.is-down { background: #fbeaec; color: #b12a3a; }
.sc-ins-foot { margin-top: 8px; padding-top: 6px; border-top: 1px solid #f0eaeb; font-size: 10px; color: #b0a8aa; }
/* Dark mode */
html.dark-mode .sc-ins-pop { background: #1f242b; color: #e3e7eb; border-color: #3a414a; box-shadow: 0 14px 44px rgba(0,0,0,.6); }
html.dark-mode .sc-ins-title { color: #e3e7eb; }
html.dark-mode .sc-ins-headline { color: #f0a8b1; }
html.dark-mode .sc-ins-pitch { color: #b9c0c8; }
html.dark-mode .sc-ins-sec-h { color: #8a929c; }
html.dark-mode .sc-ins-chip { background: #232a31; border-color: #4a3a3e; color: #e7c3c9; }
html.dark-mode .sc-ins-chip:hover { background: #2a313a; border-color: #9e2436; }
html.dark-mode .sc-ins-chip-nm { color: #8a929c; }
html.dark-mode .sc-ins-stat { background: #2a313a; color: #c7cdd4; }
html.dark-mode .sc-ins-stat.is-due { background: #3a2c17; color: #e0b070; }
html.dark-mode .sc-ins-stat.is-up { background: #1e3226; color: #7fd39b; }
html.dark-mode .sc-ins-stat.is-down { background: #3a2226; color: #f0a8b1; }
html.dark-mode .sc-ins-foot { border-top-color: #3a414a; color: #7d858e; }
/* ── Orders In Process card ── */
.ci-oip { height: 100%; overflow: auto; padding: 2px 2px 0; }
.ci-oip-empty { color: #999; font-size: 12px; padding: 12px 4px; }
.ci-oip-list { list-style: none; margin: 0; padding: 0; }
/* Fixed-width columns so every row lines up (code | name | total | status | 👁); total + status left-aligned. */
.ci-oip-row { display: grid; grid-template-columns: 62px minmax(120px,1fr) 96px 78px 150px 30px; column-gap: 14px; align-items: center; padding: 5px 4px; border-top: 1px solid #f1eef0; font-size: 12.5px; cursor: default; }
.ci-oip-head { position: sticky; top: 0; z-index: 1; background: #fff; border-top: 0;
  font-size: 9.5px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: #8a8f96;
  padding: 2px 4px 4px; cursor: default; }
.ci-oip-head:hover { background: #fff; }
.ci-oip-asof { font-size: 11px; color: #6b7280; white-space: nowrap; font-variant-numeric: tabular-nums; }
.ci-oip-row:first-child { border-top: 0; }
.ci-oip-row:hover { background: #faf3f4; }
.ci-oip-code { font-weight: 700; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ci-oip-name { color: #555; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-oip-ago { font-style: normal; font-weight: 400; color: #a0a0a6; font-size: 11px; white-space: nowrap; }
.ci-oip-total { font-variant-numeric: tabular-nums; color: #333; white-space: nowrap; text-align: left; }
.ci-oip-status { justify-self: start; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .02em; border-radius: 999px; padding: 2px 8px; white-space: nowrap; background: #eee; color: #555; }
.ci-oip-status.is-review { background: #fff2e0; color: #a15a12; }
.ci-oip-status.is-prep { background: #e6f0fb; color: #1f5fa8; }
.ci-oip-status.is-gather { background: #e3f5f1; color: #157a68; }
.ci-oip-eye { border: 0; background: none; font-size: 15px; line-height: 1; cursor: pointer; padding: 2px; border-radius: 5px; opacity: .8; }
.ci-oip-eye:hover { opacity: 1; background: #f0e0e3; transform: scale(1.12); }
html.dark-mode .ci-oip-empty { color: #8a929c; }
html.dark-mode .ci-oip-row { border-top-color: #333a42; }
html.dark-mode .ci-oip-row:hover { background: #2a313a; }
html.dark-mode .ci-oip-code, html.dark-mode .ci-oip-total { color: #e3e7eb; }
html.dark-mode .ci-oip-name { color: #aeb6bf; }
html.dark-mode .ci-oip-ago { color: #8a929c; }
html.dark-mode .ci-oip-head { background: #171b22; color: #9aa3ad; }
html.dark-mode .ci-oip-head:hover { background: #171b22; }
html.dark-mode .ci-oip-asof { color: #9aa3ad; }
html.dark-mode .ci-oip-status { background: #2a313a; color: #c7cdd4; }
html.dark-mode .ci-oip-status.is-review { background: #3a2c17; color: #e0b070; }
html.dark-mode .ci-oip-status.is-prep { background: #1c2b3d; color: #8fb8e6; }
html.dark-mode .ci-oip-status.is-gather { background: #163029; color: #7fd0be; }
html.dark-mode .ci-oip-eye:hover { background: #3a2226; }
/* Orders In Process → order-detail modal (embeds OrderDetails.vue in embedded mode) */
.oipd-backdrop { position: fixed; inset: 0; background: rgba(20,18,19,.5); z-index: 100000; display: flex; align-items: flex-start; justify-content: center; padding: 28px 14px; overflow: auto; }
.oipd-modal { position: relative; width: 900px; max-width: 100%; background: #fff; border-radius: 12px; box-shadow: 0 18px 60px rgba(0,0,0,.35); margin: auto; }
.oipd-x { position: absolute; top: 8px; right: 10px; z-index: 2; border: 0; background: rgba(255,255,255,.9); font-size: 24px; line-height: 1; color: #777; cursor: pointer; padding: 2px 8px; border-radius: 8px; }
.oipd-x:hover { color: #9e2436; }
.oipd-body { max-height: calc(100vh - 56px); overflow: auto; border-radius: 12px; }
.oipd-msg { padding: 40px 20px; text-align: center; color: #888; font-size: 14px; }
html.dark-mode .oipd-modal { background: #1f242b; box-shadow: 0 18px 60px rgba(0,0,0,.6); }
html.dark-mode .oipd-x { background: rgba(31,36,43,.9); color: #aeb6bf; }
html.dark-mode .oipd-msg { color: #8a929c; }
/* Right-rail "Cards" pill + dashboard-tools panel */
.ci-rail-pill { display: flex; flex-direction: column; align-items: center; gap: 6px; width: 56px; background: #fff; color: #9e2436; border: 1px solid #ececec; border-left: 3px solid #9e2436; border-radius: 0 8px 8px 0; box-shadow: 2px 2px 8px rgba(0,0,0,.15); padding: 10px 8px; cursor: pointer; user-select: none; }
.ci-rail-pill.is-open, .ci-rail-pill.is-open .ci-rail-pill-icon, .ci-rail-pill.is-open .ci-rail-pill-label { background: #9e2436; color: #fff; border-color: #9e2436; }
.ci-rail-pill:hover { background: #faf3f4; }
.ci-rail-pill-icon { font-size: 18px; line-height: 1; }
.ci-rail-pill-label { font-size: 10px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; text-align: center; }
.dash-tools { position: fixed; right: 0; top: 50%; transform: translateY(-50%); width: max-content; min-width: 232px; max-width: 400px; max-height: calc(100vh - 40px); overflow: visible; background: #fff; border: 1px solid #e6e6ea; border-radius: 12px; box-shadow: 0 12px 40px rgba(0,0,0,.28); display: flex; flex-direction: column; z-index: 10001; }
.dash-tools-h { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px; border-bottom: 1px solid #eee; font-size: 14px; font-weight: 800; color: #9e2436; }
.dash-tools-x { border: 0; background: none; font-size: 20px; line-height: 1; color: #999; cursor: pointer; }
.dash-tools-body { padding: 12px 14px; display: flex; flex-direction: column; gap: 8px; flex: 1 1 auto; min-height: 0; overflow-y: auto; }
.dash-tools-layout { display: flex; align-items: center; gap: 6px; }
.dash-tools-layout .dash-tools-lbl { font-size: 11px; font-weight: 700; color: #888; text-transform: uppercase; margin-right: auto; }
.dash-tools-layout button { width: 30px; height: 28px; border: 1px solid #d4d4d8; background: #fff; border-radius: 6px; cursor: pointer; font-weight: 700; color: #555; }
.dash-tools-layout button.active { background: #9e2436; color: #fff; border-color: #9e2436; }
.dash-tools-btn { text-align: left; border: 1px solid #d4d4d8; background: #f7f7f8; color: #333; font-size: 13px; font-weight: 700; border-radius: 8px; padding: 8px 12px; cursor: pointer; }
.dash-tools-btn:hover { background: #9e2436; color: #fff; border-color: #9e2436; }
/* Layout undo/redo row */
.dash-tools-undo { display: flex; gap: 6px; }
.dash-tools-undobtn { flex: 1 1 0; text-align: center; }
.dash-tools-undobtn:disabled { opacity: .45; cursor: default; }
.dash-tools-undobtn:disabled:hover { background: #f7f7f8; color: #333; border-color: #d4d4d8; }
.dash-tools-histnote { font-size: 11px; color: #999; text-align: center; margin: -2px 0 2px; }
.dash-tools-ai { display: flex; flex-direction: column; gap: 6px; padding: 8px; background: #fbf4f5; border: 1px solid #e6cfd4; border-radius: 8px; }
.dash-tools-ai input { height: 34px; border: 1px solid #d4d4d8; border-radius: 6px; padding: 0 8px; font-size: 13px; }
.dash-tools-ai button { height: 32px; border: 0; background: #9e2436; color: #fff; font-weight: 700; border-radius: 6px; cursor: pointer; }
.dash-tools-lock { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: #444; margin-top: 4px; padding-top: 8px; border-top: 1px solid #eee; cursor: pointer; }
.dash-tools-lock input { width: 16px; height: 16px; flex: none; }
/* Named layout versions (reliable save/restore) */
.dash-tools-versions { display: flex; flex-direction: column; gap: 6px; padding: 8px; background: #f4f7fb; border: 1px solid #cfdbe6; border-radius: 8px; }
.dash-ver-save { display: flex; flex-direction: column; gap: 6px; }
.dash-ver-save input { height: 32px; border: 1px solid #d4d4d8; border-radius: 6px; padding: 0 8px; font-size: 13px; }
.dash-ver-list { display: flex; flex-direction: column; gap: 4px; max-height: 190px; overflow: auto; }
.dash-ver-item { display: flex; align-items: center; gap: 4px; }
.dash-ver-restore { flex: 1 1 auto; text-align: left; border: 1px solid #cfdbe6; background: #fff; color: #26527a; font-size: 12.5px; font-weight: 600; border-radius: 6px; padding: 6px 9px; cursor: pointer; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dash-ver-restore:hover { background: #26527a; color: #fff; border-color: #26527a; }
.dash-ver-del { flex: none; width: 26px; height: 30px; border: 1px solid #e2e2e6; background: #fff; color: #b12a3a; font-size: 16px; line-height: 1; border-radius: 6px; cursor: pointer; }
.dash-ver-del:hover { background: #fbeaec; }
html.dark-mode .dash-tools-versions { background: #1a2027; border-color: #33455a; }
html.dark-mode .dash-ver-save input { background: #11151a; border-color: #3a414a; color: #e3e7eb; }
html.dark-mode .dash-ver-restore { background: #1f242b; border-color: #33455a; color: #9ec3e6; }
html.dark-mode .dash-ver-restore:hover { background: #26527a; color: #fff; }
html.dark-mode .dash-ver-del { background: #1f242b; border-color: #3a414a; }
/* Layouts picker — Layout 1 = locked default; 2+ = named per-user layouts */
.dash-tools-layouts { display: flex; flex-direction: column; gap: 4px; padding-bottom: 8px; margin-bottom: 4px; border-bottom: 1px solid #eee; }
.dash-lt-row { display: flex; align-items: center; justify-content: space-between; gap: 6px; border: 1px solid #e2e2e6; background: #fff; color: #333; font-size: 13px; font-weight: 600; border-radius: 7px; padding: 7px 10px; cursor: pointer; text-align: left; }
.dash-lt-row:hover { background: #faf3f4; }
.dash-lt-row.active { background: #9e2436; color: #fff; border-color: #9e2436; }
.dash-lt-name { flex: 1 1 auto; white-space: nowrap; }
.dash-lt-lock { font-size: 11px; opacity: .7; flex: none; }
.dash-lt-actions { display: flex; gap: 4px; flex: none; }
.dash-lt-ic { display: inline-block; width: 18px; height: 18px; line-height: 18px; text-align: center; border-radius: 4px; font-size: 12px; opacity: .65; }
.dash-lt-ic:hover { opacity: 1; background: rgba(0,0,0,.12); }
.dash-lt-row.active .dash-lt-ic:hover { background: rgba(255,255,255,.25); }
.dash-lt-new { border: 1px dashed #c9a3ab; background: #fff; color: #9e2436; font-size: 12.5px; font-weight: 700; border-radius: 7px; padding: 7px 10px; cursor: pointer; text-align: left; }
.dash-lt-new:hover { background: #faf3f4; }
.dash-tools-note { margin: 2px 0 0; font-size: 12px; color: #888; line-height: 1.45; }
.ci-yoy-bar.is-cur { background: #9e2436; }
.ci-q-bar { background: #2E86DE; display: flex; overflow: hidden; }
.ci-q-seg { height: 100%; min-width: 0; }
.ci-yoy-val { font-size: 10.5px; color: #555; font-variant-numeric: tabular-nums; }

/* saved carts */
.ci-cart { border: 1px solid #ece3e5; border-radius: 6px; padding: 6px 8px; margin-bottom: 6px; background: #fff; }
.ci-cart-h { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
.ci-cart-type { font-size: 9.5px; font-weight: 800; text-transform: uppercase; border-radius: 9px; padding: 1px 7px; color: #fff; }
.ci-cart-type.is-order { background: #2e7d32; }
.ci-cart-type.is-quote { background: #1565c0; }
.ci-cart-meta { font-size: 11px; color: #444; font-weight: 600; }
.ci-cart-when { margin-left: auto; font-size: 10px; color: #999; }
.ci-cart-items { list-style: none; margin: 0; padding: 0; }
.ci-cart-items li { display: grid; grid-template-columns: auto 1fr auto auto; gap: 6px; align-items: center; font-size: 10.5px; color: #555; padding: 1px 0; }
.ci-cart-pn { font-weight: 600; color: #333; }
.ci-cart-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #777; }
.ci-cart-qty { color: #999; }
.ci-cart-price { font-variant-numeric: tabular-nums; color: #444; }
.ci-cart-more { color: #999; font-style: italic; display: block !important; }
.ci-story-yoy { margin-top: 6px; }
.ci-yoy-label { font-size: 10px; font-weight: 700; color: #777; margin-bottom: 3px; }
.ci-yoy-hint { font-weight: 400; font-size: 9px; color: #aaa; text-transform: none; letter-spacing: 0; }
.ci-yoy-row { cursor: default; }
.ci-story-cust .ci-story-block-h { color: #1565c0; }
.ci-story-rep { margin-bottom: 4px !important; }
.ci-story-muted { color: #999; font-style: italic; }

/* ============================ LAYOUT 2 (redesigned) ============================ */
.ci-head { display: flex; align-items: flex-start; justify-content: flex-end; gap: 16px; flex-wrap: wrap; }
.ci-layout-toggle { display: inline-flex; align-items: center; gap: 4px; background: #f2f2f4; border: 1px solid #e0e0e4; border-radius: 8px; padding: 3px; flex: none; }
.ci-layout-lbl { font-size: 11px; color: #888; padding: 0 6px; text-transform: uppercase; letter-spacing: .04em; }
.ci-layout-toggle button { border: 0; background: transparent; width: 28px; height: 26px; border-radius: 6px; font-size: 13px; font-weight: 700; color: #666; cursor: pointer; }
.ci-layout-toggle button.active { background: #9e2436; color: #fff; }

.l2-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin: 0 0 16px; }
.l2-daterange { font-size: 14px; color: #444; font-weight: 600; }
.l2-spacer { flex: 1; }
.l2-btn { border: 1px solid #d4d4d8; background: #fff; color: #333; border-radius: 8px; padding: 8px 14px; font-size: 14px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
.l2-btn:hover { background: #f6f6f7; }
.l2-btn:disabled { opacity: .5; cursor: default; }
.l2-btn-primary { background: #9e2436; border-color: #9e2436; color: #fff; }
.l2-btn-primary:hover { background: #861d2d; }
.l2-badge { background: #9e2436; color: #fff; border-radius: 10px; font-size: 11px; font-weight: 700; padding: 1px 7px; }

.l2-chips { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin: 0 0 20px; }
.l2-chip { display: inline-flex; align-items: center; gap: 6px; background: #fff; border: 1px solid #e0e0e4; border-radius: 16px; padding: 4px 6px 4px 12px; font-size: 13px; color: #333; }
.l2-chip-k { color: #9e2436; font-weight: 700; }
.l2-chip-x { border: 0; background: #ececef; color: #555; width: 18px; height: 18px; border-radius: 50%; cursor: pointer; font-size: 13px; line-height: 1; }
.l2-chip-x:hover { background: #9e2436; color: #fff; }
.l2-chip-clear { border: 0; background: none; color: #9e2436; font-size: 13px; font-weight: 600; cursor: pointer; text-decoration: underline; }

.l2-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 0 0 24px; }
.l2-kpi { background: #fff; border: 1px solid #e6e6ea; border-radius: 8px; padding: 18px; }
.l2-kpi-val { font-size: 30px; font-weight: 700; color: #1f2430; line-height: 1.1; }
.l2-kpi-sub { font-size: 16px; font-weight: 600; color: #9aa0aa; }
.l2-kpi-lbl { font-size: 13px; color: #8b909a; margin-top: 6px; }

.l2-card { background: #fff; border: 1px solid #e6e6ea; border-radius: 8px; padding: 16px; margin: 0 0 24px; }
.l2-card-h { font-size: 20px; font-weight: 700; color: #1f2430; margin: 0 0 12px; }
.l2-mapbody { max-width: 900px; margin: 0 auto; }

.l2-ranks { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 0 0 24px; }
.l2-ranks .l2-card { margin: 0; }
.l2-rankrow { display: grid; grid-template-columns: minmax(70px, 38%) 1fr auto; align-items: center; gap: 10px; padding: 7px 6px; border-radius: 6px; cursor: pointer; }
.l2-rankrow:hover { background: #f6f6f7; }
.l2-rankrow.is-sel { background: #fdecee; box-shadow: inset 0 0 0 1px #9e2436; }
.l2-rank-lbl { font-size: 13px; color: #333; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: flex; align-items: center; }
.l2-rank-track { background: #eef0f1; border-radius: 4px; height: 16px; overflow: hidden; }
.l2-rank-bar { display: block; height: 100%; background: #9e2436; border-radius: 4px; min-width: 3px; }
.l2-bar-teal { background: #16a085; }
.l2-rank-val { font-size: 12.5px; color: #555; font-variant-numeric: tabular-nums; text-align: right; white-space: nowrap; }
.l2-dot { width: 9px; height: 9px; border-radius: 50%; margin-right: 6px; flex: none; }
.l2-rank-empty { color: #999; font-size: 13px; padding: 8px 6px; }
.l2-viewall { border: 0; background: none; color: #9e2436; font-size: 13px; font-weight: 600; cursor: pointer; padding: 8px 6px 2px; }

.l2-drawer-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.28); z-index: 9989; }
.l2-drawer { position: fixed; top: 0; left: 0; right: auto; height: 100%; width: 340px; max-width: 92vw; background: #fff; box-shadow: none; z-index: 10001; transform: translateX(-100%); /* z 10001 = above the site side-rails (body::before/::after @ z9999) so the drawer covers them */ transition: transform .22s ease; display: flex; flex-direction: column; }
.l2-drawer.open { transform: translateX(0); }

/* Drawer / panel CLOSE handle — a circle with a direction arrow straddling the inner border, vertically centered.
   Replaces the old top-right ×. Hidden while the panel is parked off-screen so no stray tab shows. */
.l2-drawer-tab { position: absolute; top: 50%; transform: translateY(-50%); width: 34px; height: 34px; border-radius: 50%; border: 1px solid #d9d9de; background: #fff; color: #9e2436; font-size: 15px; line-height: 1; cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,.2); display: flex; align-items: center; justify-content: center; z-index: 3; padding: 0; }
.l2-drawer-tab:hover { background: #9e2436; color: #fff; border-color: #9e2436; }
.l2-drawer-tab--right { right: -17px; }                          /* Filters drawer: right border, arrow ◀ */
.l2-drawer:not(.open) .l2-drawer-tab--right { display: none; }
.l2-drawer-tab--story { left: -17px; }                           /* Sales Insights panel: left border, arrow ▶ */
.ci-story:not(.is-open) .l2-drawer-tab--story { display: none; }
.ci-story-widen { left: -17px; top: calc(50% - 44px); font-size: 16px; }   /* widen handle, stacked above the close handle */
.ci-story:not(.is-open) .ci-story-widen { display: none; }

/* Report edge toggles — Filters (left) · Sales Insights (right) */
/* Left edge rail (Filters + Tokens) — boxed icon+label pills sized to match the right-side rail. */
.ci-edge-rail { position: fixed; left: 0; top: 50%; transform: translateY(-50%); z-index: 5000; display: flex; flex-direction: column; gap: 12px; }
/* When any report panel is open, hide the LEFT edge rail too (App.vue hides the right pill rail). */
body.jcs-report-panel-open .ci-edge-rail { display: none !important; }
.ci-edge { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; width: 56px; border: 1px solid #ececec; background: #fff; color: #9e2436; cursor: pointer; padding: 10px 6px; box-shadow: 0 2px 10px rgba(0,0,0,.15); }
.ci-edge:hover { background: #faf3f4; }
.ci-edge-left { border-left: 0; border-radius: 0 8px 8px 0; }
.ci-edge-icon { font-size: 18px; line-height: 1; }
.ci-edge-label { font-size: 10px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; line-height: 1.1; text-align: center; color: #9e2436; }
.ci-edge.is-open { background: #9e2436; color: #fff; border-color: #9e2436; }
.l2-drawer-h { display: flex; align-items: center; justify-content: space-between; padding: 16px; border-bottom: 1px solid #ececef; font-size: 18px; font-weight: 700; color: #1f2430; }
.l2-drawer-x { border: 0; background: none; font-size: 22px; color: #888; cursor: pointer; line-height: 1; }
.l2-drawer-body { padding: 16px; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 12px; }
.ci-tokens-drawer { width: 380px; }
.ci-tok-search { width: 100%; height: 34px; border: 1px solid #d4d4d8; border-radius: 6px; padding: 0 10px; font-size: 13px; }
.ci-tok-sec { border: 1px solid #ececef; border-radius: 8px; padding: 10px; }
.ci-tok-sec-h { font-size: 12px; font-weight: 800; color: #9e2436; text-transform: uppercase; letter-spacing: .03em; margin-bottom: 4px; }
.ci-tok-note { font-size: 11.5px; color: #888; margin: 0 0 8px; line-height: 1.4; }
.ci-tok-row { display: flex; align-items: baseline; gap: 8px; padding: 4px 6px; border-radius: 6px; cursor: pointer; }
.ci-tok-row:hover { background: #faf3f4; }
.ci-tok-name { color: #9e2436; font-weight: 700; font-family: monospace; font-size: 12px; flex: 0 0 auto; }
.ci-tok-label { font-size: 12px; color: #444; flex: 1; min-width: 0; }
.ci-tok-val { font-size: 11.5px; color: #888; max-width: 42%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-tok-table { border-top: 1px solid #f0f0f2; padding: 7px 0; }
.ci-tok-table:first-of-type { border-top: 0; }
.ci-tok-table-h { font-size: 12.5px; font-weight: 700; color: #333; margin-bottom: 4px; }
.ci-tok-count { font-size: 10.5px; font-weight: 700; color: #fff; background: #9e2436; border-radius: 9px; padding: 0 6px; margin-left: 4px; }
.ci-tok-fields { display: flex; flex-wrap: wrap; gap: 4px; }
.ci-tok-field { font-size: 11px; font-family: monospace; background: #f4f4f5; border: 1px solid #e4e4e7; border-radius: 4px; padding: 1px 5px; color: #555; }
.ci-tok-field.is-num { color: #1f6e23; border-color: #cde8cf; background: #f1f9f2; }
.ci-tok-empty { font-size: 12px; color: #999; padding: 4px 2px; }
/* Per-card title-style toolbar (between handle and body) */
.dash-titlebar { display: flex; align-items: center; gap: 6px; padding: 5px 8px; background: #fbf4f5; border-bottom: 1px solid #ececef; flex: none; }
.dash-tb-lbl { font-size: 10px; font-weight: 800; text-transform: uppercase; color: #9e2436; }
.dash-tb-btn { width: 24px; height: 24px; border: 1px solid #d4d4d8; background: #fff; border-radius: 5px; cursor: pointer; font-size: 12px; color: #333; padding: 0; }
.dash-tb-btn.active { background: #9e2436; color: #fff; border-color: #9e2436; }
.dash-tb-color { width: 28px; height: 24px; border: 1px solid #d4d4d8; border-radius: 5px; padding: 0; cursor: pointer; background: #fff; }
.dash-tb-size { height: 24px; border: 1px solid #d4d4d8; border-radius: 5px; font-size: 11px; }
.dash-tb-sp { flex: 1; }
.dash-tb-txt { border: 0; background: none; color: #9e2436; font-size: 11px; font-weight: 700; cursor: pointer; padding: 2px 4px; }
.dash-tb-txt:hover { text-decoration: underline; }
.dash-handle-title.is-clickable { cursor: pointer; }
.dash-handle-title.is-clickable:hover { text-decoration: underline dotted; }
.l2-f { display: flex; flex-direction: column; gap: 4px; }
.l2-f span { font-weight: 600; text-transform: uppercase; letter-spacing: .03em; font-size: 11px; color: #888; }
.l2-f input, .l2-f select { height: 38px; border: 1px solid #d4d4d8; border-radius: 6px; padding: 0 10px; font-size: 14px; background: #fff; }
.l2-f input[type="date"] { cursor: pointer; }
.l2-f input[type="date"]::-webkit-calendar-picker-indicator { cursor: pointer; opacity: .9; }
/* Typeable MM/DD/YYYY From/To date inputs (replaced the native date picker) */
.ci-datebox { width: 100%; box-sizing: border-box; font-size: 12px; padding: 4px 7px; border: 1px solid #d6d6d6; border-radius: 6px; color: #333; background: #fff; letter-spacing: .3px; }
.ci-datebox:focus { outline: none; border-color: #9e2436; box-shadow: 0 0 0 2px rgba(158,36,54,.12); }
html.dark-mode .ci-datebox { background: #1f242b; border-color: #3a414a; color: #e3e7eb; }
.l2-quickwrap { display: flex; flex-direction: column; gap: 6px; }
.l2-qr-row { display: flex; flex-wrap: wrap; gap: 5px; }
.l2-qr-row button { border: 1px solid #d4d4d8; background: #fff; color: #555; font-size: 12px; font-weight: 700; padding: 5px 9px; border-radius: 6px; cursor: pointer; }
.l2-qr-row button:hover { background: #9e2436; color: #fff; border-color: #9e2436; }
.l2-quickrange { display: inline-flex; border: 1px solid #d4d4d8; border-radius: 8px; overflow: hidden; align-self: flex-start; }
.l2-quickrange button { border: 0; border-right: 1px solid #e4e4e8; background: #fff; color: #555; font-size: 12px; font-weight: 700; padding: 7px 10px; cursor: pointer; }
.l2-quickrange button:last-child { border-right: 0; }
.l2-quickrange button:hover { background: #9e2436; color: #fff; }
.l2-drawer-foot { padding: 16px; border-top: 1px solid #ececef; display: flex; gap: 10px; }
.l2-drawer-foot .l2-btn { flex: 1; justify-content: center; }

.l2-tableheading { font-size: 20px; font-weight: 700; color: #1f2430; margin: 8px 0 12px; }
.l2-tablewrap { border: 1px solid #e6e6ea; border-radius: 8px; max-height: 70vh; overflow: auto; }
.l2-tablewrap .ci-table thead th { position: sticky; top: 0; z-index: 2; background: #f7f7f9; color: #555; border-bottom: 2px solid #e0e0e4; }
.l2-tablewrap .ci-table td { padding-top: 12px; padding-bottom: 12px; }
.l2-tablewrap .ci-table tbody tr:hover { background: #f9f9fa; }

@media (max-width: 992px) {
  .l2-kpis { grid-template-columns: repeat(2, 1fr); }
  .l2-ranks { grid-template-columns: 1fr; }
}
@media (max-width: 560px) {
  .l2-kpis { grid-template-columns: 1fr; }
}

/* Saved Carts popup → clickable carts that switch into that customer's company, plus a hint line. */
.sc-cart--click { cursor: pointer; transition: background .12s, box-shadow .12s; }
.sc-cart--click:hover { background: #faf7f8; box-shadow: inset 0 0 0 1px #e0d6d8; }
.sc-hint { font-size: 11.5px; color: #9e2436; margin: 0 0 8px; font-weight: 600; }
html.dark-mode .sc-cart--click:hover { background: #313842; box-shadow: inset 0 0 0 1px #4a525c; }
html.dark-mode .sc-hint { color: #f0a8b1; }
</style>

<!-- Global (unscoped) print isolation for the Orders-In-Process → order-detail modal. While that modal is open,
     `body.oipd-printing` (toggled by the beforeprint/afterprint listeners) hides the whole app (#app) so Print Order
     from the embedded OrderDetails prints ONLY the order — the modal is body-appended (sibling of #app), so it
     survives. Applies solely during a print while the modal is open; normal printing is untouched. -->
<style>
@media print {
  body.oipd-printing { background: #fff !important; background-image: none !important; }
  body.oipd-printing #app { display: none !important; }
  /* The report's toggle drawers/panels are body-appended (siblings of #app, so they escape the
     .ci-wrap isolation) — hiding #app alone doesn't remove them, so an OPEN drawer (dash-tools rail,
     Filters/Tokens, SI panel, favorites popover) would bleed onto the order printout. Hide them all
     while printing the order; the .oipd-backdrop order modal below is deliberately NOT in this list. */
  body.oipd-printing .dash-tools,
  body.oipd-printing .l2-drawer,
  body.oipd-printing .l2-drawer-backdrop,
  body.oipd-printing .ci-story,
  body.oipd-printing .ci-favpop { display: none !important; }
  body.oipd-printing .oipd-backdrop { position: static !important; background: #fff !important; overflow: visible !important; padding: 0 !important; display: block !important; }
  body.oipd-printing .oipd-modal { box-shadow: none !important; width: auto !important; max-width: 100% !important; margin: 0 !important; border-radius: 0 !important; }
  body.oipd-printing .oipd-body { max-height: none !important; overflow: visible !important; }
  body.oipd-printing .oipd-x { display: none !important; }
}
/* On paper there is no viewport to cap against, and a clipped scroll box would silently print a
   fraction of the answer. Unscoped so it applies to the report's own print as well. */
@media print {
  .ci-ask-answer-scroll { max-height: none !important; overflow: visible !important; }
}
/* ===== Quote review PRESENTATION sheet =====
   Same isolation as the per-order sheet: while printing, every direct child of <body> except this one
   is hidden, so an open drawer or popover cannot bleed onto the page. Screen-invisible always. */
.qrp-doc { display: none; }
@media print {
  body.qrp-printing { background: #fff !important; background-image: none !important; }
  body.qrp-printing > *:not(.qrp-doc) { display: none !important; }
  body.qrp-printing > .qrp-doc { display: block !important; }
  .qrp-doc { font-family: Arial, Helvetica, sans-serif; font-size: 11px; line-height: 1.4; color: #222;
    -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
  .qrp-head { display: flex; align-items: flex-start; justify-content: space-between; padding: 0 5px 14px; margin: 0 0 12px; border-bottom: 2px solid #c0392b; }
  /* flex: 0 0 auto matches the order sheet — the cell must never be squeezed by a long subtitle. An
     EXPLICIT height too: this SVG carries a viewBox and no width/height, so height:auto left the box to
     intrinsic sizing at the one moment (print snapshot) when it may not be resolved yet. 170x66 is the
     file's own 181:70 ratio. */
  .qrp-head-logo { flex: 0 0 auto; }
  .qrp-logo-img { width: 170px; max-width: 170px; height: 66px; max-height: 76px; display: block; }
  .qrp-logo-text { font-size: 30px; font-weight: 900; letter-spacing: 1px; color: #c0392b; line-height: 1; }
  .qrp-head-mid { flex: 1 1 auto; align-self: center; text-align: center; padding: 0 14px; min-width: 0; }
  .qrp-head-t { font-size: 17px; font-weight: 700; }
  .qrp-head-s { font-size: 11.5px; color: #555; margin-top: 2px; }
  .qrp-head-addr { flex: 0 0 auto; text-align: right; font-size: 11px; line-height: 1.35; }
  .qrp-head-addr-name { font-weight: 700; margin-bottom: 2px; }
  .qrp-tbl { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 11px; }
  .qrp-tbl th { text-align: left; background: #eee; border: 1px solid #999; padding: 4px 6px; font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; }
  .qrp-tbl td { border: 1px solid #ccc; padding: 4px 6px; vertical-align: top; color: #000; word-break: break-word; }
  .qrp-tbl .qrp-n { text-align: right; white-space: nowrap; }
  .qrp-ln { width: 5%; text-align: right; color: #555; }
  .qrp-tbl th:nth-child(2) { width: 24%; }
  .qrp-tbl th:nth-child(8) { width: 13%; }
  .qrp-tbl tr { page-break-inside: avoid; break-inside: avoid; }
  .qrp-desc { font-size: 9.5px; color: #555; }
  .qrp-read { text-transform: capitalize; }
  .qrp-sub td { font-size: 9.5px; color: #444; background: #fafafa; border-top: 0; }
  .qrp-narr { margin-top: 14px; page-break-inside: avoid; }
  .qrp-narr-h { font-size: 12px; font-weight: 700; color: #c0392b; border-bottom: 1px solid #ddd; padding-bottom: 3px; margin-bottom: 6px; }
  .qrp-narr ul { margin: 0; padding-left: 16px; }
  .qrp-narr li { margin-bottom: 3px; }
  .qrp-foot { margin-top: 14px; padding-top: 6px; border-top: 1px solid #ddd; font-size: 9.5px; color: #666; }
}
/* ===== Customer Order Details → per-order PRINT SHEET =====
   Isolation is ONE rule: while body.codp-printing is set, every direct child of <body> except the sheet is
   hidden. That covers #app and all of the report's body-appended drawers/popovers at once, so whatever the
   user had open can't bleed onto the printed order (the oipd block above has to name them by hand).
   The sheet is display:none at all other times — it exists purely to be printed. */
.codp-doc { display: none; }
@media print {
  body.codp-printing { background: #fff !important; background-image: none !important; }
  body.codp-printing > *:not(.codp-doc) { display: none !important; }
  body.codp-printing > .codp-doc { display: block !important; }
  /* Colors are load-bearing here (the red letterhead rule + red total), so opt out of the browsers'
     default print color-strip — same as the Order Details print stylesheet. */
  .codp-doc { font-family: Arial, Helvetica, sans-serif; font-size: 12px; line-height: 1.4; color: #222;
    -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
  /* Letterhead — logo | customer | doc # + Parts Department, over a red rule. */
  .codp-head { display: flex; align-items: flex-start; justify-content: space-between; padding: 0 5px 16px; margin: 0 0 14px; border-bottom: 2px solid #c0392b; }
  .codp-head-logo { flex: 0 0 auto; }
  .codp-logo-img { width: 180px; max-width: 180px; max-height: 80px; height: auto; display: block; }
  .codp-logo-text { font-size: 32px; font-weight: 900; letter-spacing: 1px; color: #c0392b; line-height: 1; }
  .codp-head-cust { flex: 1 1 auto; align-self: center; text-align: center; padding: 0 16px; min-width: 0; }
  .codp-head-cust-name { font-weight: 700; font-size: 16px; line-height: 1.2; }
  .codp-head-cust-id { font-size: 13px; color: #555; margin-top: 2px; }
  .codp-head-addr { flex: 0 0 auto; text-align: right; font-size: 12px; line-height: 1.4; }
  .codp-head-addr-name { font-weight: 700; margin-bottom: 2px; }
  .codp-head-docid { font-weight: 700; font-size: 14px; color: #c0392b; margin-bottom: 6px; }
  /* Date / Territory / Ship-To strip. Labelled inline so a printed sheet is self-explaining. */
  .codp-meta { display: flex; flex-wrap: wrap; gap: 6px 28px; font-size: 12px; margin: 0 2px 12px; }
  .codp-meta-l { display: block; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #777; }
  .codp-note { border: 1px solid #bbb; border-left: 3px solid #666; padding: 5px 8px; margin: 0 2px 8px; font-size: 11px; page-break-inside: avoid; break-inside: avoid; }
  .codp-note--spec { border-left-color: #2f6fb0; }
  .codp-note-l { display: block; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #555; margin-bottom: 2px; }
  /* Fixed layout so the header cells stay over their values (auto layout drifts in print), with the
     description column taking the slack. */
  .codp-parts { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 12px; }
  .codp-parts th { text-align: left; background: #eee; border: 1px solid #999; padding: 5px 8px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: #222; }
  .codp-parts td { border: 1px solid #ccc; padding: 5px 8px; vertical-align: top; color: #000; word-break: break-word; }
  .codp-parts th:nth-child(2), .codp-parts td:nth-child(2) { width: 38%; }
  .codp-parts th:nth-child(3), .codp-parts td:nth-child(3) { width: 13%; }
  .codp-parts .codp-n { text-align: right; white-space: nowrap; }
  .codp-parts tr { page-break-inside: avoid; break-inside: avoid; }
  .codp-pnote { font-size: 10px; color: #555; margin-top: 2px; }
  /* Bottom line, mirroring the Order Details printout: heavy black rule, black label, red amount. */
  .codp-total { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; border-top: 3px solid #000; margin-top: 10px; padding: 8px 4px 0; page-break-inside: avoid; break-inside: avoid; }
  .codp-total-l { font-size: 14px; font-weight: 700; color: #000; }
  .codp-total-v { font-size: 14px; font-weight: 700; color: #c0392b; }
}
/* Dark mode: the shared #orderDetails theme paints the Sold-To/Ship-To cards a lighter slate (#333A42) that reads
   as "mid-dark" against the report's deeper surfaces; deepen them to match the rest of the modal. Scoped to the
   report modal (.oipd-modal) so the standalone Order Details page keeps its own look. Background only — the red top
   accent + text colors from the base #orderDetails rules are preserved. */
@media screen {
  html.dark-mode .oipd-modal #orderDetails .info-card { background: #232a31 !important; }
}
/* 💡 Sales-by-Territory "who rolls up + why" popover (body-appended; unscoped so the v-html children style too). */
.ci-terr-insbtn { border: 0; background: none; cursor: pointer; font-size: 12px; line-height: 1; padding: 0 0 0 5px; opacity: .8; }
.ci-terr-insbtn:hover { opacity: 1; }
.ci-terr-ins { position: fixed; z-index: 10050; width: 320px; max-width: 92vw; max-height: 72vh; overflow-y: auto; background: #fff; border: 1px solid #e2e6ea; border-left: 3px solid #2f8f5b; border-radius: 10px; box-shadow: 0 12px 32px rgba(0,0,0,.20); padding: 12px 14px; cursor: default; }
.ci-terr-ins-h { display: flex; align-items: center; gap: 7px; font-weight: 800; font-size: 13.5px; color: #22303a; margin-bottom: 2px; }
.ci-terr-ins-dot { width: 11px; height: 11px; border-radius: 3px; flex: 0 0 auto; }
.ci-terr-ins-sub { font-size: 11.5px; color: #66707a; margin-bottom: 8px; }
.ci-terr-ins-list { display: flex; flex-direction: column; gap: 3px; margin-bottom: 9px; }
.ci-terr-ins-row { display: flex; justify-content: space-between; gap: 10px; font-size: 12.5px; padding: 3px 7px; border-radius: 5px; background: rgba(0,0,0,.03); }
.ci-terr-ins-rep { color: #333; font-weight: 600; }
.ci-terr-ins-amt { color: #22303a; white-space: nowrap; font-variant-numeric: tabular-nums; }
.ci-terr-ins-amt em { color: #8a94a0; font-style: normal; font-size: 11px; margin-left: 3px; }
.ci-terr-ins-why { font-size: 11.5px; line-height: 1.55; color: #4a4f56; border-top: 1px dashed #e2e6ea; padding-top: 7px; }
html.dark-mode .ci-terr-ins { background: #232a31; border-color: #3a444e; border-left-color: #3fae74; }
html.dark-mode .ci-terr-ins-h { color: #e8edf2; }
html.dark-mode .ci-terr-ins-sub { color: #9aa6b2; }
html.dark-mode .ci-terr-ins-row { background: rgba(255,255,255,.05); }
html.dark-mode .ci-terr-ins-rep { color: #d6dde4; }
html.dark-mode .ci-terr-ins-amt { color: #eef2f6; }
html.dark-mode .ci-terr-ins-amt em { color: #8a94a0; }
html.dark-mode .ci-terr-ins-why { color: #aeb8c2; border-top-color: #3a444e; }
/* The mobile TOOLS handle should keep rounded LEFT corners like the CHAT/FILTERS/EMAIL edge tabs. This rule is
   in a NON-scoped block so it can reach .jcs-edge-tray-handle (rendered in App.vue, outside this component's
   scope); !important wins over any square-cornered override. */
.jcs-edge-tray-handle { border-top-left-radius: 11px !important; border-bottom-left-radius: 11px !important; }

/* ---- Costing for Parts (SENSITIVE card) ------------------------------------------------ */
.ci-pcost { display: flex; flex-direction: column; height: 100%; min-height: 0; }
.ci-pcost-sum { display: flex; flex-wrap: wrap; gap: 4px 16px; align-items: baseline;
  padding: 4px 6px 6px; font-size: 11px; }
.ci-pcost-sum-fig em { display: block; font-style: normal; font-size: 10px; letter-spacing: .04em;
  text-transform: uppercase; color: #6b7280; }
.ci-pcost-sum-fig { font-weight: 600; color: #1f2937; }
.ci-pcost-sum-fig.is-margin { color: #c0392b; }
.ci-pcost-sum-match { color: #6b3fa0; }
html.dark-mode .ci-pcost-sum-match { color: #c4a8ea; }
/* The coverage note is the honesty control: margin covers only the costed share. Styled as a warning,
   not a footnote, because a reader who misses it will read the margin as complete. */
.ci-pcost-cover { margin-left: 6px; padding: 2px 8px; border-radius: 10px; font-size: 11px;
  background: #fdf3e7; color: #8a5300; border: 1px solid #f0d3a8; cursor: help; }
.ci-pcost-scroll { flex: 1 1 auto; min-height: 0; overflow-y: auto; overflow-x: hidden; }
.ci-pcost-xls { margin-left: 6px; flex: 0 0 auto; padding: 3px 10px; font-size: 11px; font-weight: 700;
  color: #fff; background: #1d6f42; border: 1px solid #1d6f42; border-radius: 6px; cursor: pointer; white-space: nowrap; }
.ci-pcost-xls:hover { background: #175734; border-color: #175734; color: #fff; }
.ci-pcost-xls:disabled { opacity: .5; cursor: default; }
html.dark-mode .ci-pcost-xls { background: transparent; color: #6fce9a; border-color: #2f7d52; }
html.dark-mode .ci-pcost-xls:hover { background: #1d6f42; color: #fff; }
.ci-pcost-tbl { width: 100%; border-collapse: collapse; font-size: 10.5px; line-height: 1.25;
  table-layout: fixed; }
/* Fixed widths, no sideways scrolling. Sum = 100%. Dropping Description and Last cost change into the
   expanded panel is what buys the room for Unit price. Order: ▸ / Part / 4 components / cost / price / qty. */
/* TEN columns now: ▸ / Part / 4 components / cost / price / margin / qty. Sum = 100%. */
.ci-pcost-tbl th:nth-child(1), .ci-pcost-tbl td:nth-child(1) { width: 4%; }
.ci-pcost-tbl th:nth-child(2), .ci-pcost-tbl td:nth-child(2) { width: 15%; }
.ci-pcost-tbl th:nth-child(3), .ci-pcost-tbl td:nth-child(3) { width: 10%; }
.ci-pcost-tbl th:nth-child(4), .ci-pcost-tbl td:nth-child(4),
.ci-pcost-tbl th:nth-child(5), .ci-pcost-tbl td:nth-child(5),
.ci-pcost-tbl th:nth-child(6), .ci-pcost-tbl td:nth-child(6) { width: 9%; }
.ci-pcost-tbl th:nth-child(7), .ci-pcost-tbl td:nth-child(7),
.ci-pcost-tbl th:nth-child(8), .ci-pcost-tbl td:nth-child(8) { width: 11%; }
.ci-pcost-tbl th:nth-child(9), .ci-pcost-tbl td:nth-child(9) { width: 12%; }
.ci-pcost-tbl th:nth-child(10), .ci-pcost-tbl td:nth-child(10) { width: 10%; }

/* Expander + expanded panel. */
.ci-pcost-th-x { width: 4%; }
.ci-pcost-row { cursor: pointer; }
.ci-pcost-row.open > td { background: #fdf6f5; }
.ci-pcost-x { text-align: center; padding-right: 0 !important; }
.ci-pcost-caret { color: #9aa0a6; font-size: 9px; }
.ci-pcost-row.open .ci-pcost-caret { color: #c0392b; }
.ci-pcost-price { font-weight: 600; }
/* Margin: the money on one line, the percentage under it, so one column carries both without widening. */
.ci-pcost-marg { font-weight: 600; color: #1d6f42; }
.ci-pcost-marg em { display: block; font-style: normal; font-weight: 400; font-size: 9px; color: #6b7280; }
.ci-pcost-marg.neg { color: #c0392b; }
.ci-pcost-marg.neg em { color: #c0392b; }
.ci-pcost-sum-below { color: #c0392b; }
/* The below-cost figure is a BUTTON now. Bare button chrome stripped; ACTIVE = solid red pill so the
   filtered state is unmissable. .ci-pcost prefix beats the .fe button color:#fff global (0,2,0 vs 0,1,1). */
.ci-pcost .ci-pcost-belowbtn { border: 1px solid transparent; background: none; padding: 1px 7px;
  border-radius: 9px; cursor: pointer; text-align: left; font: inherit; color: #c0392b; }
.ci-pcost .ci-pcost-belowbtn:hover { border-color: #c0392b; }
.ci-pcost .ci-pcost-belowbtn.on { background: #c0392b; color: #fff; }
.ci-pcost .ci-pcost-belowbtn.on em { color: #f0c4bf; }
html.dark-mode .ci-pcost .ci-pcost-belowbtn { color: #ef8a7e; }
html.dark-mode .ci-pcost .ci-pcost-belowbtn.on { background: #c0392b; color: #fff; }
html.dark-mode .ci-pcost-marg { color: #6fce9a; }
html.dark-mode .ci-pcost-marg em { color: #9aa3ad; }
html.dark-mode .ci-pcost-marg.neg,
html.dark-mode .ci-pcost-marg.neg em { color: #ef8a7e; }
html.dark-mode .ci-pcost-sum-below { color: #ef8a7e; }
.ci-pcost-exp > td { background: #fdf6f5; padding: 4px 8px 6px 22px !important; }
.ci-pcost-exp-row { display: flex; gap: 8px; font-size: 10px; line-height: 1.45; }
.ci-pcost-exp-row em { flex: 0 0 96px; font-style: normal; text-transform: uppercase;
  letter-spacing: .04em; font-size: 9px; color: #8a8f96; }
.ci-pcost-exp-row span { flex: 1 1 auto; min-width: 0; color: #1f2937; overflow-wrap: anywhere; }
html.dark-mode .ci-pcost-row.open > td,
html.dark-mode .ci-pcost-exp > td { background: #1e1a1a; }
html.dark-mode .ci-pcost-exp-row span { color: #e6e8ea; }
html.dark-mode .ci-pcost-exp-row em { color: #9aa3ad; }

/* Hover preview. position:fixed so .ci-pcost-scroll cannot clip it; pointer-events:none so it never
   eats the click that would expand the row. */
.ci-pcost-peek { position: fixed; z-index: 100085; pointer-events: none; max-width: 280px;
  padding: 7px 9px; border-radius: 8px; background: #fff; border: 1px solid #d2d6dc;
  box-shadow: 0 8px 22px rgba(0,0,0,.16); font-size: 10.5px; line-height: 1.4; }
.ci-pcost-peek-pn { font-family: ui-monospace, Menlo, Consolas, monospace; font-weight: 700; color: #c0392b; }
.ci-pcost-peek-d { color: #1f2937; margin-top: 2px; }
.ci-pcost-peek-l { color: #6b7280; margin-top: 3px; font-size: 9.5px; }
.ci-pcost-peek-h { color: #9aa0a6; margin-top: 4px; font-size: 9px; font-style: italic; }
html.dark-mode .ci-pcost-peek { background: #171b22; border-color: #3a414a; }
html.dark-mode .ci-pcost-peek-d { color: #e6e8ea; }
html.dark-mode .ci-pcost-peek-l,
html.dark-mode .ci-pcost-peek-h { color: #9aa3ad; }
/* Long part numbers / descriptions break rather than widen the table. */
.ci-pcost-tbl td { overflow-wrap: anywhere; }
.ci-pcost-tbl th { position: sticky; top: 0; z-index: 1; background: #f7f7f8; text-align: left;
  padding: 3px 6px; font-size: 9px; letter-spacing: .04em; text-transform: uppercase;
  color: #000; border-bottom: 1px solid #e2e4e8; white-space: nowrap; }
.ci-pcost-tbl th.num, .ci-pcost-tbl td.num { text-align: right; white-space: nowrap; }
.ci-pcost-tbl td { padding: 2px 6px; border-bottom: 1px solid #f0f1f3; vertical-align: top; }
.ci-pcost-tbl tbody tr:hover td { background: #fafbfc; }
.ci-pcost-pn { font-family: ui-monospace, Menlo, Consolas, monospace; white-space: nowrap; }
.ci-pcost-brk { margin-top: 2px; display: flex; flex-wrap: wrap; gap: 2px 10px; font-size: 10px; color: #6b7280; }
.ci-pcost-nocost { font-size: 9.5px; color: #8a5300; cursor: help; white-space: nowrap; }
.ci-pcost-detail { margin-top: 6px; font-family: ui-monospace, Menlo, Consolas, monospace;
  font-size: 11px; color: #8a5300; word-break: break-word; }
html.dark-mode .ci-pcost-detail { color: #e0b578; }
.ci-pcost-tbl td.is-neg { color: #c0392b; font-weight: 600; }
.ci-pcost-who { font-style: normal; font-size: 9.5px; color: #6b7280; margin-left: 5px; }
/* Dark + mid mode. The global darkmode.css cannot reach a scoped class, so the hooks live here. */
html.dark-mode .ci-pcost-sum-fig { color: #e6e8ea; }
html.dark-mode .ci-pcost-sum-fig em,
html.dark-mode .ci-pcost-brk,
html.dark-mode .ci-pcost-who { color: #9aa3ad; }
html.dark-mode .ci-pcost-tbl th { background: #171b22; color: #e6e8ea; border-bottom-color: #2b323c; }
html.dark-mode .ci-pcost-tbl td { border-bottom-color: #232932; }

/* Chart toggle. It carries the margin-left:auto that used to sit on the Excel button, so the pair still
   groups against the right edge with the "showing top N" pill. */
.ci-pcost-chartbtn { margin-left: auto; flex: 0 0 auto; padding: 2px 9px; font-size: 12px; line-height: 1.3;
  background: #fff; border: 1px solid #d2d6dc; border-radius: 6px; cursor: pointer; }
.ci-pcost-chartbtn:hover { border-color: #c0392b; }
.ci-pcost-chartbtn.on { background: #fdecea; border-color: #c0392b; }
html.dark-mode .ci-pcost-chartbtn { background: transparent; border-color: #3a414a; }
html.dark-mode .ci-pcost-chartbtn:hover,
html.dark-mode .ci-pcost-chartbtn.on { border-color: #c0392b; background: #2a1a1a; }

/* Search + Generate Insights share a row — the search box gives up the width it was wasting. */
.ci-pcost-toolrow { display: flex; align-items: center; gap: 8px; padding: 0 14px 8px; flex: 0 0 auto; }
/* DESCENDANT selector (specificity 0,2,0) so it beats `.ci-codes-searchwrap { flex: 0 0 auto }` outright
   rather than relying on source order — the box was staying at its intrinsic ~200px instead of filling
   the row. flex-basis 0 (not auto) makes the input's intrinsic size irrelevant: it takes ALL the free
   space, which is what "wider" means here. */
.ci-pcost-toolrow .ci-codes-searchwrap { flex: 1 1 0; min-width: 120px; padding: 0; }
/* ＋ add-another-term. SOLID fill in light mode: an outline-only button next to a white input read as
   blank. Slate rather than the AI purple — it belongs to the search, not to Generate Insights. */
.ci-pcost-add { flex: 0 0 auto; width: 26px; height: 24px; padding: 0; font-size: 15px; font-weight: 700;
  line-height: 1; color: #fff; background: #374151; border: 1px solid #374151; border-radius: 6px;
  cursor: pointer; }
.ci-pcost-add:hover:not(:disabled) { background: #1f2937; border-color: #1f2937; }
/* Disabled still has to be SEEN — an empty box is the normal resting state, so a near-invisible
   control here would look like the feature is missing rather than waiting for input. */
.ci-pcost-add:disabled { background: #e8eaed; border-color: #d2d6dc; color: #9aa0a6; cursor: default; }
html.dark-mode .ci-pcost-add { background: #3a414a; border-color: #4a525c; color: #e6e8ea; }
html.dark-mode .ci-pcost-add:hover:not(:disabled) { background: #4a525c; }
html.dark-mode .ci-pcost-add:disabled { background: #20252c; border-color: #2b323c; color: #6b727c; }

/* The one deliberate publish action — green like the other "this goes out to people" controls. */
.dash-tools-publish { background: #2f8f5b !important; color: #fff !important; border-color: #2f8f5b !important; }
.dash-tools-publish:hover { background: #277a4d !important; }

/* FULL VIEW. Sibling cards are hidden and this one is taken out of the grid's absolute positioning so it
   fills the width; the grid container's inline height is overridden or it would leave a tall blank gap.
   The hero chat bar is NOT inside .vue-grid-layout, so it survives without being special-cased. */
.ci-pcfull-mode .vue-grid-layout { height: auto !important; }
.ci-pcfull-mode .vue-grid-item.gi-pchide { display: none !important; }
.ci-pcfull-mode .vue-grid-item.gi-pcfull { position: relative !important;
  /* Pin + size are fed through CSS VARIABLES from refreshStickyCards: these declarations need
     !important to beat vue-grid-layout's inline transform/height — which would ALSO beat any inline
     pin, so a custom property on the inline style is the one channel that gets through.
     translateY(0) ≡ the old `none` as the grid-transform cancel. */
  transform: translateY(var(--jcs-full-ty, 0px)) !important;
  left: auto !important; top: auto !important; width: 100% !important;
  height: var(--jcs-full-h, calc(100vh - 230px)) !important; min-height: 420px; }
/* No resize grabber or drag affordance while maximized — the size is not the user's to set here. */
.ci-pcfull-mode .vue-grid-item.gi-pcfull .vue-resizable-handle { display: none !important; }
.ci-pcost-fullbtn { margin-left: auto; flex: 0 0 auto; padding: 3px 9px; font-size: 11px; font-weight: 600;
  white-space: nowrap; color: #000; background: #fff; border: 1px solid #d2d6dc; border-radius: 6px;
  cursor: pointer; }
/* Solid portal red on hover. Deliberately NO dark-mode override on this button: the resting state is a
   white pill with BLACK text in both themes, which is what was asked for, and keeping the light-mode fill
   is what makes black legible on a dark card. The dark-mode rules that used to sit here set the resting
   text to #e6e8ea, which is why it read as "not black" against the dark theme. */
.ci-pcost-fullbtn:hover { background: #c0392b; border-color: #c0392b; color: #fff; }

/* ---- SPECIFICITY FIX: a global rule steals `color` from every plain .ci-pcost-* button ----
   The app stylesheet carries `.fe .btn, .fe button { color: #fff; … }`. `.fe button` is (0,1,1) — one
   class plus one element — so it BEATS a bare `.ci-pcost-*` class at (0,1,0), and it sits later in the
   cascade. Result: any button here whose colour is set by a single class silently renders WHITE.
   That is exactly why "Full view" was white at rest while its :hover looked right — a pseudo-class
   lifts the hover rule to (0,2,0), which wins. Same reason the :disabled rules behave.
   Prefixing with the card root gets these to (0,2,0), so they win on class count without !important.
   Buttons that legitimately want white text (Excel, Generate Insights, Presentation, ＋) are untouched.
   Dark mode was never affected: `html.dark-mode .x` is already (0,2,0). */
.ci-pcost .ci-pcost-fullbtn, .ci-ploss .ci-pcost-fullbtn { color: #000; }   /* .ci-ploss reuses these classes under its own root */
/* The hover MUST be lifted alongside it. `.ci-pcost .ci-pcost-fullbtn` is (0,2,0) and so is the plain
   `.ci-pcost-fullbtn:hover` (one class + one pseudo-class) — a TIE, decided by source order, and this
   block sits later, so the resting black was overriding the hover white. (0,2,1) settles it properly.
   Rule of thumb for this file: if you raise a base rule's specificity, raise its state rules too. */
.ci-pcost .ci-pcost-fullbtn:hover, .ci-ploss .ci-pcost-fullbtn:hover { color: #fff; }
/* Same trap, same feature, caught while here: both of these would render white — one on a white row,
   the other on a pale lilac panel — i.e. invisible. */
.ci-pcost .ci-pcost-terms-clr, .ci-ploss .ci-pcost-terms-clr { color: #6b7280; }
.ci-pcost .ci-pcost-ai-x, .ci-ploss .ci-pcost-ai-x { color: #7b6a92; }

/* The chart host: the generated SVG is injected here and scales to the box. */
.ci-pcc-host { flex: 1 1 auto; min-height: 0; display: flex; }
.ci-pcc-host svg { flex: 1 1 auto; width: 100%; height: 100%; min-height: 0; }
/* Second series + the price-minus-cost chart. Price is GREEN so it never collides with the four
   cost-component colours used by the pie. */
.ci-pcc-barp { fill: #2f8f5b; }
.ci-pcc-zero { stroke: #9aa0a6; stroke-width: 1; }
.ci-pcc-gapneg { fill: #c0392b; }
.ci-pcc-gappos { fill: #2f8f5b; }
.ci-pcc-gapv { font-size: 8px; text-anchor: end; fill: #374151; }
.ci-pcc-gapp { font-size: 7.5px; text-anchor: start; fill: #6b7280; }
.ci-pcc-gapp-neg { text-anchor: end; }
html.dark-mode .ci-pcc-gapp { fill: #9aa3ad; }
html.dark-mode .ci-pcc-gapv { fill: #d7dbe0; }
html.dark-mode .ci-pcc-zero { stroke: #6b727c; }

/* Sortable headers. */
.ci-pcost-th { cursor: pointer; user-select: none; }
.ci-pcost-th:hover { color: #c0392b; }
.ci-pcost-th.is-sorted { color: #c0392b; }
.ci-pcost-sortarrow { font-size: 8px; margin-left: 2px; }
html.dark-mode .ci-pcost-th:hover,
html.dark-mode .ci-pcost-th.is-sorted { color: #ef8a7e; }

/* Typeahead list. Anchored to the search wrap (position:relative) and above the table. */
.ci-pcost-sug { position: absolute; z-index: 40; left: 0; right: 0; top: 100%; margin: 2px 0 0; padding: 0;
  list-style: none; max-height: 240px; overflow-y: auto; background: #fff; border: 1px solid #d2d6dc;
  border-radius: 6px; box-shadow: 0 6px 18px rgba(0,0,0,.12); }
.ci-pcost-sug li { display: flex; align-items: baseline; gap: 6px; padding: 4px 8px; font-size: 11px; cursor: pointer; }
.ci-pcost-sug li.on { background: #fdecea; }
.ci-pcost-sug-v { font-family: ui-monospace, Menlo, Consolas, monospace; font-weight: 600; color: #1f2937; white-space: nowrap; }
.ci-pcost-sug-l { color: #6b7280; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-pcost-sug-k { margin-left: auto; flex: 0 0 auto; font-size: 9px; color: #8a5300; }
html.dark-mode .ci-pcost-sug { background: #171b22; border-color: #3a414a; }
html.dark-mode .ci-pcost-sug li.on { background: #2a1a1a; }
html.dark-mode .ci-pcost-sug-v { color: #e6e8ea; }
html.dark-mode .ci-pcost-sug-l { color: #9aa3ad; }

/* Committed term chips. */
.ci-pcost-terms { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 6px;
  padding: 0 14px 8px; flex: 0 0 auto; }
.ci-pcost-terms-lbl { font-size: 9.5px; letter-spacing: .04em; text-transform: uppercase; color: #6b7280; }
/* LIGHT: the portal red, white text. DARK: the chat-favourite blue lifted verbatim from
   `.ci-fav.is-chat` (#a7d5f2 / #7fbfe8 / #123) with black text — same token, so a term chip reads as the
   same class of object as a chat favourite instead of inventing a fifth blue.
   UPPERCASE is display-only: partCostTermList lowercases before matching, so the chip can shout without
   changing what is actually searched, and the user's original casing stays intact in the workbook. */
.ci-pcost-term { display: inline-flex; align-items: center; gap: 4px; padding: 1px 4px 1px 8px;
  font-size: 11px; font-weight: 600; letter-spacing: .02em; text-transform: uppercase;
  border-radius: 10px; background: #c0392b; border: 1px solid #c0392b; color: #fff; }
.ci-pcost-term button { border: 0; background: none; cursor: pointer; font-size: 13px; line-height: 1;
  color: #f0c4bf; padding: 0 2px; }
.ci-pcost-term button:hover { color: #fff; }
.ci-pcost-terms-clr { border: 0; background: none; cursor: pointer; font-size: 10px; color: #6b7280;
  text-decoration: underline; padding: 0 2px; }
html.dark-mode .ci-pcost-terms-lbl,
html.dark-mode .ci-pcost-terms-clr { color: #9aa3ad; }
html.dark-mode .ci-pcost-term { background: #a7d5f2; border-color: #7fbfe8; color: #112233; }
html.dark-mode .ci-pcost-term button { color: #3a5a70; }
html.dark-mode .ci-pcost-term button:hover { color: #000; }

/* margin-left:auto pins the button to the right edge even if the search box ever stops growing. */
.ci-pcost-gen { margin-left: auto; flex: 0 0 auto; padding: 4px 10px; font-size: 11px; font-weight: 700; white-space: nowrap;
  color: #fff; background: #6b3fa0; border: 1px solid #6b3fa0; border-radius: 6px; cursor: pointer; }
.ci-pcost-gen:hover { background: #573182; border-color: #573182; }
.ci-pcost-gen:disabled { opacity: .5; cursor: default; }
/* Same green as the panel's "📊 Presentation" (.ci-ask-tbl-present) — the two do the same kind of thing,
   so they should look like the same control rather than two unrelated buttons. */
.ci-pcost-pres { flex: 0 0 auto; margin-left: 6px; padding: 4px 10px; font-size: 11px; font-weight: 700;
  white-space: nowrap; color: #fff; background: #2f8f5b; border: 1px solid #2f8f5b; border-radius: 6px;
  cursor: pointer; }
.ci-pcost-pres:hover:not(:disabled) { background: #277a4d; border-color: #277a4d; }
.ci-pcost-pres:disabled { opacity: .5; cursor: default; }

/* The AI briefing sits above the data it describes, capped so it can never crowd out the table. */
.ci-pcost-ai { margin: 0 14px 8px; padding: 8px 10px; border-radius: 8px; flex: 0 0 auto;
  background: #f6f2fb; border: 1px solid #e0d5f0; max-height: 42%; overflow-y: auto; }
.ci-pcost-ai-head { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.ci-pcost-ai-title { font-size: 11px; font-weight: 700; color: #4a2d73; letter-spacing: .02em; }
.ci-pcost-ai-x { margin-left: auto; border: 0; background: transparent; cursor: pointer;
  font-size: 15px; line-height: 1; color: #7b6a92; }
.ci-pcost-ai-body { font-size: 11px; line-height: 1.45; color: #2f2740; }
.ci-pcost-ai-ln { position: relative; padding-left: 11px; margin-bottom: 3px; }
.ci-pcost-ai-ln::before { content: "•"; position: absolute; left: 0; color: #6b3fa0; }
.ci-pcost-ai-err { color: #a3382c; }
.ci-pcost-ai-basis { margin-top: 6px; padding-top: 5px; border-top: 1px dashed #ddd2ee;
  font-size: 10px; color: #6d6480; }
html.dark-mode .ci-pcost-ai { background: #1b1726; border-color: #3a2f4d; }
html.dark-mode .ci-pcost-ai-title { color: #c4a8ea; }
html.dark-mode .ci-pcost-ai-body { color: #ded9e8; }
html.dark-mode .ci-pcost-ai-basis { color: #9a92ab; border-top-color: #3a2f4d; }
html.dark-mode .ci-pcost-ai-err { color: #ef9c92; }

/* Charts. Plain SVG — no charting library in this project, and three small static charts don't justify one. */
.ci-pcost-chart { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; padding: 0 14px 8px; }
.ci-pcost-chart-cap { font-size: 10px; color: #6b7280; margin-bottom: 4px; flex: 0 0 auto; }
.ci-pcc-svg { flex: 1 1 auto; width: 100%; min-height: 0; }
.ci-pcc-bar { fill: #c0392b; }
.ci-pcc-lbl { font-size: 9px; fill: #374151; font-family: ui-monospace, Menlo, Consolas, monospace; }
.ci-pcc-val { font-size: 9px; fill: #6b7280; }
.ci-pcc-tick { font-size: 8px; fill: #6b7280; text-anchor: end; }
.ci-pcc-grid { stroke: #eceef1; stroke-width: 1; }
.ci-pcc-vlbl { font-size: 8px; fill: #374151; text-anchor: end;
  font-family: ui-monospace, Menlo, Consolas, monospace; }
.ci-pcc-axis { stroke: #d2d6dc; stroke-width: 1; }
.ci-pcc-leg { font-size: 10px; fill: #374151; }
.ci-pcc-slice { stroke: #fff; stroke-width: 1.5; }
html.dark-mode .ci-pcost-chart-cap { color: #9aa3ad; }
html.dark-mode .ci-pcc-lbl,
html.dark-mode .ci-pcc-vlbl,
html.dark-mode .ci-pcc-leg { fill: #d7dbe0; }
html.dark-mode .ci-pcc-val,
html.dark-mode .ci-pcc-tick { fill: #9aa3ad; }
html.dark-mode .ci-pcc-axis { stroke: #3a414a; }
html.dark-mode .ci-pcc-grid { stroke: #232932; }
html.dark-mode .ci-pcc-slice { stroke: #12161c; }
html.dark-mode .ci-pcost-tbl tbody tr:hover td { background: #1b2028; }
html.dark-mode .ci-pcost-cover { background: #2a2116; color: #e0b578; border-color: #4a3a22; }
html.dark-mode .ci-pcost-nocost { color: #e0b578; }
html.mid-mode .ci-pcost-tbl th { background: #e9ebee; color: #000; }

/* Parts Sold at a Loss (Accounting report card). Table chrome comes from .ci-pcost-tbl; these are the
   card's own tabs/notes/loss accents. */
.ci-ploss { display: flex; flex-direction: column; height: 100%; min-height: 0; }
.ci-ploss-tabs { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; padding: 8px 14px 4px; flex: 0 0 auto; }
/* Category tabs are BUTTONS — prefixed with the card root or the global `.fe button { color:#fff }`
   (0,1,1) beats the bare class (0,1,0) and light-mode tab text goes white-on-white (the file's
   documented specificity trap). The .on state is raised alongside, per the rule of thumb above. */
.ci-ploss .ci-ploss-tab { border: 1px solid #d2d6dc; background: #fff; color: #374151; border-radius: 14px;
  font-size: 11px; padding: 3px 10px; cursor: pointer; line-height: 1.4; }
.ci-ploss-tab em { font-style: normal; font-weight: 700; margin-left: 4px; }
.ci-ploss-tab .ci-ploss-tab-loss { color: #c0392b; margin-left: 6px; }
.ci-ploss .ci-ploss-tab.on { background: #c0392b; border-color: #c0392b; color: #fff; }
.ci-ploss-tab.on .ci-ploss-tab-loss { color: #fff; }
.ci-ploss-xls { margin-left: auto; }
.ci-ploss-note { font-size: 10px; color: #6b7280; padding: 0 14px 6px; flex: 0 0 auto; }
.ci-ploss-scroll { flex: 1 1 auto; min-height: 0; overflow: auto; padding: 0 14px 10px; }
.ci-ploss-tbl th { cursor: pointer; user-select: none; white-space: nowrap; }
.ci-ploss-tbl th.num, .ci-ploss-tbl td.num { text-align: right; font-variant-numeric: tabular-nums; }
.ci-ploss-part { font-family: ui-monospace, Menlo, Consolas, monospace; white-space: nowrap; }
.ci-ploss-desc { max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci-ploss-line { color: #6b7280; }
.ci-ploss-loss.neg { color: #c0392b; font-weight: 700; }
.ci-ploss-trunc { font-size: 10px; color: #6b7280; padding: 6px 0 0; }
html.dark-mode .ci-ploss-tab { background: transparent; border-color: #3a414a; color: #d7dbe0; }
html.dark-mode .ci-ploss-tab .ci-ploss-tab-loss { color: #ef8a7e; }
html.dark-mode .ci-ploss-tab.on { background: #c0392b; border-color: #c0392b; color: #fff; }
html.dark-mode .ci-ploss-tab.on .ci-ploss-tab-loss { color: #fff; }
html.dark-mode .ci-ploss-note, html.dark-mode .ci-ploss-trunc, html.dark-mode .ci-ploss-line { color: #9aa3ad; }
html.dark-mode .ci-ploss-loss.neg { color: #ef8a7e; }
html.mid-mode .ci-ploss-tab { background: #fff; }
.ci-ploss-sum-loss { color: #c0392b; font-weight: 700; }
html.dark-mode .ci-ploss-sum-loss { color: #ef8a7e; }
</style>
